import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import './App.css';

// ── Auth helpers ──────────────────────────────────────────────────────────────
function getToken() {
  return sessionStorage.getItem('aurelius_token');
}

function setToken(token) {
  sessionStorage.setItem('aurelius_token', token);
}

function clearToken() {
  sessionStorage.removeItem('aurelius_token');
  sessionStorage.removeItem('aurelius_role');
  sessionStorage.removeItem('aurelius_clientId');
  sessionStorage.removeItem('aurelius_email');
  sessionStorage.removeItem('aurelius_displayName');
  // Restricted-user claims (S1.14)
  sessionStorage.removeItem('aurelius_allowed_tabs');
  sessionStorage.removeItem('aurelius_read_only');
  sessionStorage.removeItem('aurelius_role_label');
}

function authFetch(url, options) {
  var token = getToken();
  var headers = Object.assign({}, (options && options.headers) || {});
  if (token) headers['Authorization'] = 'Bearer ' + token;
  return fetch(url, Object.assign({}, options || {}, { headers: headers })).then(function(r) {
    if (r.status === 401) {
      clearToken();
      window.location.reload();
    }
    return r;
  });
}

const API = window.location.hostname === 'localhost' ? 'http://localhost:4000/api' : '/api';

// Unified GET helper — returns { ok, data, error } shape. Replaces the
// 33+ ad-hoc .then(r => r.json()).catch(() => {}) patterns that were
// silently eating errors and leaving tabs stuck on empty skeletons.
async function apiGet(path) {
  try {
    var r = await authFetch(API + path);
    if (!r.ok) {
      var msg;
      try { var body = await r.json(); msg = body.error || ('HTTP ' + r.status); } catch (e) { msg = 'HTTP ' + r.status; }
      return { ok: false, data: null, error: msg, status: r.status };
    }
    var data = await r.json();
    return { ok: true, data: data, error: null, status: r.status };
  } catch (e) {
    return { ok: false, data: null, error: e && e.message ? e.message : 'Network error', status: 0 };
  }
}

// Unified write helper (POST/PATCH/DELETE) — same { ok, data, error } shape as
// apiGet. Sends JSON, parses JSON back. Used by the Work/PM workspace.
async function apiSend(method, path, body) {
  try {
    var opts = { method: method, headers: { 'Content-Type': 'application/json' } };
    if (body !== undefined) opts.body = JSON.stringify(body);
    var r = await authFetch(API + path, opts);
    var data = null;
    try { data = await r.json(); } catch (e) { data = null; }
    if (!r.ok) return { ok: false, data: null, error: (data && data.error) || ('HTTP ' + r.status), status: r.status };
    return { ok: true, data: data, error: null, status: r.status };
  } catch (e) {
    return { ok: false, data: null, error: e && e.message ? e.message : 'Network error', status: 0 };
  }
}

// Compact error banner for tabs when a fetch fails — gives users feedback
// and a retry path instead of an empty screen.
function ErrorBanner({ message, onRetry }) {
  return (
    <div className="api-error-banner">
      <span className="api-error-banner-icon">⚠</span>
      <div className="api-error-banner-body">
        <div className="api-error-banner-title">We couldn't load this view</div>
        <div className="api-error-banner-detail">{message || 'Unknown error. Try again in a moment.'}</div>
      </div>
      {onRetry && <button className="api-error-banner-retry" onClick={onRetry}>Retry</button>}
    </div>
  );
}

// ── Login Page ────────────────────────────────────────────────────────────────
function LoginPage({ clientId, clientName, onLogin }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [shake, setShake] = useState(false);
  const [success, setSuccess] = useState(false);
  const [appVersion, setAppVersion] = useState('');
  const [theme, setLoginTheme] = useState(sessionStorage.getItem('aurelius_theme') || 'dark');
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [ssoProcessing, setSsoProcessing] = useState(false);
  // MSP Process Tech Verification widget — pre-auth access for techs
  // who don't need to sign in to verify a caller.
  const [mspVerifyOpen, setMspVerifyOpen] = useState(false);
  // Per-client URL flow defaults to password-only (legacy shared-account
  // pattern still works). User clicks "Sign in with a different account"
  // to reveal an email field for per-user logins (pre-SSO clients with
  // multiple named accounts).
  //
  // Deep-link variant: /client/<id>?email=alice@example.com auto-flips into
  // individual-account mode with the email prefilled — this is the URL we
  // hand to restricted users so they don't have to discover the toggle.
  var initialEmail = '';
  var initialUseEmail = false;
  try {
    var qs = typeof window !== 'undefined' && window.location && window.location.search;
    if (qs) {
      var params = new URLSearchParams(qs);
      var qEmail = params.get('email');
      if (qEmail && /.+@.+\..+/.test(qEmail)) {
        initialEmail = qEmail.trim();
        initialUseEmail = true;
      }
    }
  } catch (e) { /* ignore */ }
  const [useEmail, setUseEmail] = useState(initialUseEmail);
  const [email, setEmail] = useState(initialEmail);
  const canvasRef = useRef(null);

  useEffect(function() {
    document.documentElement.setAttribute('data-theme', theme);
    sessionStorage.setItem('aurelius_theme', theme);
  }, [theme]);

  useEffect(function() {
    fetch(API + '/version').then(function(r) { return r.json(); }).then(function(data) { setAppVersion(data.version); }).catch(function() {});
  }, []);

  // Is SSO enabled? Admin login (no clientId) checks the global flag; per-
  // client URLs check whether THAT client has sso_enabled wired (commercial
  // tier opt-in, CMMC tier pending Atomus approval). Demo client never
  // shows SSO — it's a hardcoded password bypass.
  useEffect(function() {
    if (clientId === 'demo') return;
    var url = API + '/auth/microsoft/config' + (clientId ? '?clientId=' + encodeURIComponent(clientId) : '');
    fetch(url)
      .then(function(r) { return r.ok ? r.json() : { enabled: false }; })
      .then(function(d) { setSsoEnabled(!!d.enabled); })
      .catch(function() {});
  }, [clientId]);

  // Handle SSO callback — backend has set aurelius_sso_token cookie + redirected with ?sso=success|error
  useEffect(function() {
    var params = new URLSearchParams(window.location.search);
    var ssoParam = params.get('sso');
    if (!ssoParam) return;

    // Strip query params from the URL so refresh doesn't re-trigger
    window.history.replaceState({}, '', window.location.pathname);

    if (ssoParam === 'error') {
      var reasonMap = {
        wrong_tenant: 'Your account is from a different Microsoft tenant.',
        no_matching_user: 'Your Microsoft account is not authorized for Aurelius.',
        state_mismatch: 'Session expired during sign-in — try again.',
        missing_state: 'Session expired during sign-in — try again.',
        exchange_failed: 'Microsoft sign-in failed. Try again or use a password.',
        no_oid: 'Microsoft did not return a stable identifier — contact support.'
      };
      setError(reasonMap[params.get('reason')] || 'Microsoft sign-in failed.');
      setShake(true);
      setTimeout(function() { setShake(false); }, 500);
      return;
    }

    if (ssoParam === 'success') {
      // Token lives in a short-lived cookie — read, extract, clear.
      setSsoProcessing(true);
      var cookieMatch = document.cookie.match(/aurelius_sso_token=([^;]+)/);
      if (!cookieMatch) { setError('Sign-in handoff failed — please try again.'); setSsoProcessing(false); return; }
      try {
        var parsed = JSON.parse(decodeURIComponent(cookieMatch[1]));
        // Clear the handoff cookie aggressively
        document.cookie = 'aurelius_sso_token=; Max-Age=0; path=/';
        setToken(parsed.token);
        sessionStorage.setItem('aurelius_role', parsed.role);
        if (parsed.email)       sessionStorage.setItem('aurelius_email', parsed.email);
        if (parsed.displayName) sessionStorage.setItem('aurelius_displayName', parsed.displayName);
        // Per-client SSO carries clientId in the handoff; admin SSO doesn't.
        if (parsed.clientId)    sessionStorage.setItem('aurelius_clientId', parsed.clientId);
        else                    sessionStorage.removeItem('aurelius_clientId');
        // Restricted-user claims (S1.14) — only set when present in handoff
        if (parsed.allowedTabs)  sessionStorage.setItem('aurelius_allowed_tabs', JSON.stringify(parsed.allowedTabs));
        else                     sessionStorage.removeItem('aurelius_allowed_tabs');
        if (parsed.readOnly)     sessionStorage.setItem('aurelius_read_only', '1');
        else                     sessionStorage.removeItem('aurelius_read_only');
        if (parsed.roleLabel)    sessionStorage.setItem('aurelius_role_label', parsed.roleLabel);
        else                     sessionStorage.removeItem('aurelius_role_label');
        setSuccess(true);
        setTimeout(function() { onLogin(parsed); }, 400);
      } catch (e) {
        setError('Sign-in handoff failed — please try again.');
        setSsoProcessing(false);
      }
    }
  }, [onLogin]);

  var [demoLaunching, setDemoLaunching] = useState(false);
  var launchDemo = function() {
    setDemoLaunching(true);
    setError('');
    sessionStorage.removeItem('aurelius_demo_role');
    fetch(API + '/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clientId: 'demo', password: 'aurelius-demo-2026' })
    }).then(function(r) { return r.json().then(function(data) { return { ok: r.ok, data: data }; }); })
      .then(function(resp) {
        if (resp.ok) {
          setToken(resp.data.token);
          sessionStorage.setItem('aurelius_role', resp.data.role);
          if (resp.data.clientId)     sessionStorage.setItem('aurelius_clientId', resp.data.clientId);
          if (resp.data.email)        sessionStorage.setItem('aurelius_email', resp.data.email);
          if (resp.data.displayName)  sessionStorage.setItem('aurelius_displayName', resp.data.displayName);
          setSuccess(true);
          setTimeout(function() { onLogin(resp.data); }, 500);
        } else {
          setError('Demo temporarily unavailable.');
          setDemoLaunching(false);
        }
      }).catch(function() {
        setError('Demo temporarily unavailable.');
        setDemoLaunching(false);
      });
  };

  useEffect(function() {
    var canvas = canvasRef.current;
    if (!canvas) return;
    var ctx = canvas.getContext('2d');
    var particles = [];
    var count = 40;
    var animId;
    function resize() {
      canvas.width = canvas.offsetWidth * (window.devicePixelRatio || 1);
      canvas.height = canvas.offsetHeight * (window.devicePixelRatio || 1);
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(window.devicePixelRatio || 1, window.devicePixelRatio || 1);
    }
    resize();
    window.addEventListener('resize', resize);
    for (var i = 0; i < count; i++) {
      particles.push({
        x: Math.random() * canvas.offsetWidth,
        y: Math.random() * canvas.offsetHeight,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        r: Math.random() * 1.5 + 0.5,
        hue: Math.random() > 0.5 ? 250 : 190,
      });
    }
    function draw() {
      var w = canvas.offsetWidth;
      var h = canvas.offsetHeight;
      ctx.clearRect(0, 0, w, h);
      for (var i = 0; i < particles.length; i++) {
        var p = particles[i];
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0 || p.x > w) p.vx *= -1;
        if (p.y < 0 || p.y > h) p.vy *= -1;
        for (var j = i + 1; j < particles.length; j++) {
          var q = particles[j];
          var dx = p.x - q.x, dy = p.y - q.y;
          var dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(q.x, q.y);
            ctx.strokeStyle = 'rgba(120,80,255,' + ((1 - dist / 120) * 0.12) + ')';
            ctx.lineWidth = 0.5; ctx.stroke();
          }
        }
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.hue === 250
          ? 'rgba(120,80,255,' + (0.3 + p.r * 0.15) + ')'
          : 'rgba(0,200,255,' + (0.25 + p.r * 0.12) + ')';
        ctx.fill();
      }
      animId = requestAnimationFrame(draw);
    }
    draw();
    return function() { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
  }, []);

  var handleSubmit = async function(e) {
    e.preventDefault();
    setError('');
    setShake(false);
    setLoading(true);
    try {
      var body = { password: password };
      if (clientId) body.clientId = clientId;
      // Include email when the operator chose "different account" mode (per-
      // user login on a non-SSO client). Backend prefers email + verifies
      // it matches the URL's clientId.
      if (useEmail && email) body.email = email.trim();
      var resp = await fetch(API + '/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      var data = await resp.json();
      if (resp.ok) {
        setToken(data.token);
        sessionStorage.setItem('aurelius_role', data.role);
        if (data.clientId)    sessionStorage.setItem('aurelius_clientId', data.clientId);
        if (data.email)       sessionStorage.setItem('aurelius_email', data.email);
        if (data.displayName) sessionStorage.setItem('aurelius_displayName', data.displayName);
        // Restricted-user claims (S1.14)
        if (data.allowedTabs)  sessionStorage.setItem('aurelius_allowed_tabs', JSON.stringify(data.allowedTabs));
        else                   sessionStorage.removeItem('aurelius_allowed_tabs');
        if (data.readOnly)     sessionStorage.setItem('aurelius_read_only', '1');
        else                   sessionStorage.removeItem('aurelius_read_only');
        if (data.roleLabel)    sessionStorage.setItem('aurelius_role_label', data.roleLabel);
        else                   sessionStorage.removeItem('aurelius_role_label');
        setSuccess(true);
        setTimeout(function() { onLogin(data); }, 600);
      } else {
        setError(data.error || 'Invalid password');
        setShake(true);
        setTimeout(function() { setShake(false); }, 500);
      }
    } catch (err) {
      setError('Connection failed. Please try again.');
      setShake(true);
      setTimeout(function() { setShake(false); }, 500);
    }
    setLoading(false);
  };

  // Polished loading screen while Launch Demo is in flight
  if (demoLaunching && !error) {
    return (
      <div className="login-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <style>{`
          @keyframes demoSpin { to { transform: rotate(360deg); } }
          @keyframes demoFade { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
          .demo-loading-wrap { text-align: center; animation: demoFade 0.4s ease-out both; }
          .demo-loading-brand { font-size: 11px; font-weight: 700; letter-spacing: 6px; color: var(--accent-blue); text-transform: uppercase; margin-bottom: 18px; }
          .demo-loading-title { font-size: 26px; font-weight: 800; color: var(--text-primary); margin-bottom: 10px; letter-spacing: -0.5px; }
          .demo-loading-sub { font-size: 13px; color: var(--text-muted); margin-bottom: 36px; max-width: 380px; line-height: 1.6; }
          .demo-loading-spinner { width: 40px; height: 40px; border: 3px solid rgba(91,158,244,0.15); border-top-color: var(--accent-blue); border-radius: 50%; animation: demoSpin 0.8s linear infinite; margin: 0 auto; }
        `}</style>
        <div className="demo-loading-wrap">
          <div className="demo-loading-brand">Aurelius</div>
          <div className="demo-loading-title">Loading live demo</div>
          <div className="demo-loading-sub">Signing you in as Helios Industries — a 52-person technology services firm using the full Aurelius platform.</div>
          <div className="demo-loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page" style={{ display: 'flex', flexDirection: 'row' }}>
      <style>{`
        @keyframes loginShake { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-8px)} 40%{transform:translateX(8px)} 60%{transform:translateX(-5px)} 80%{transform:translateX(5px)} }
        @keyframes loginMesh1 { 0%{transform:translate(0,0)} 50%{transform:translate(30px,-20px)} 100%{transform:translate(0,0)} }
        @keyframes loginMesh2 { 0%{transform:translate(0,0)} 50%{transform:translate(-20px,30px)} 100%{transform:translate(0,0)} }
        @keyframes loginSuccess { 0%{transform:scale(1)} 50%{transform:scale(1.03)} 100%{transform:scale(1)} }
        @keyframes loginStatFade { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
        @keyframes loginGridPulse { 0%,100%{opacity:0.03} 50%{opacity:0.06} }
        .login-split-left {
          flex: 0 0 58%; display: flex; flex-direction: column; justify-content: center; align-items: center;
          position: relative; overflow: hidden; padding: 48px;
        }
        .login-split-right {
          flex: 1; display: flex; flex-direction: column; justify-content: center; align-items: center;
          position: relative; padding: 48px; z-index: 2;
        }
        .login-mesh-orb {
          position: absolute; border-radius: 50%; filter: blur(80px); pointer-events: none; opacity: 0.12;
        }
        .login-grid-overlay {
          position: absolute; inset: 0; pointer-events: none;
          background-image: repeating-linear-gradient(0deg, transparent, transparent 59px, rgba(120,80,255,0.04) 60px),
                            repeating-linear-gradient(90deg, transparent, transparent 59px, rgba(120,80,255,0.04) 60px);
          background-size: 60px 60px; animation: loginGridPulse 8s ease-in-out infinite;
        }
        .login-left-divider {
          position: absolute; right: 0; top: 10%; bottom: 10%; width: 1px;
          background: linear-gradient(180deg, transparent, var(--border-default, rgba(56,97,168,0.15)) 30%, var(--border-default, rgba(56,97,168,0.15)) 70%, transparent);
        }
        .login-card-v2 {
          width: 100%; max-width: 380px;
          background: var(--bg-card, rgba(15,27,54,0.6)); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
          border: 1px solid var(--border-default, rgba(56,97,168,0.15)); border-radius: 16px;
          padding: 44px 36px; box-shadow: 0 24px 80px rgba(0,0,0,0.4);
        }
        .login-card-v2.shake { animation: loginShake 0.4s ease; }
        .login-card-v2.success { animation: loginSuccess 0.5s ease; }
        .login-pw-wrap {
          position: relative; display: flex; align-items: center;
        }
        .login-pw-wrap input { flex: 1; padding-right: 44px; }
        .login-pw-toggle {
          position: absolute; right: 14px; background: none; border: none; cursor: pointer; padding: 4px;
          color: var(--text-muted, #546889); font-size: 16px; line-height: 1; transition: color 0.2s;
        }
        .login-pw-toggle:hover { color: var(--text-secondary, #8b9dc3); }
        .login-btn-v2 {
          width: 100%; padding: 14px; border: none; border-radius: 10px; font-size: 13px; font-weight: 700;
          font-family: var(--font-body, 'DM Sans', sans-serif); cursor: pointer; letter-spacing: 0.8px;
          text-transform: uppercase; transition: all 0.3s; position: relative; overflow: hidden;
          background: linear-gradient(135deg, #1e3a7a, var(--accent-blue, #5b9ef4)); color: #fff;
        }
        .login-btn-v2:hover:not(:disabled) { box-shadow: 0 4px 24px rgba(91,158,244,0.3); transform: translateY(-1px); }
        .login-btn-v2:disabled { opacity: 0.35; cursor: not-allowed; transform: none; box-shadow: none; }
        .login-btn-v2.loading { background: linear-gradient(135deg, #1e3a7a, #3b6fc4); }
        .login-btn-v2.success-state { background: linear-gradient(135deg, #0f5132, #22c55e); }
        .login-demo-section { margin-top: 28px; }
        .login-demo-divider {
          display: flex; align-items: center; gap: 12px;
          font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 2px;
          color: var(--text-muted, #546889); margin-bottom: 14px;
        }
        .login-demo-divider::before, .login-demo-divider::after {
          content: ''; flex: 1; height: 1px; background: rgba(120, 80, 255, 0.12);
        }
        .login-demo-landing { display: flex; flex-direction: column; gap: 18px; }
        .login-demo-persona-card {
          padding: 20px 22px;
          background: linear-gradient(135deg, rgba(120, 80, 255, 0.06), rgba(91, 158, 244, 0.04));
          border: 1px solid rgba(120, 80, 255, 0.18);
          border-radius: 12px;
          text-align: center;
        }
        .login-demo-persona-eyebrow {
          font-size: 9px; font-weight: 700; letter-spacing: 2.5px; text-transform: uppercase;
          background: linear-gradient(135deg, #a78bfa, #5b9ef4);
          -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
          margin-bottom: 10px;
        }
        .login-demo-persona-name {
          font-size: 20px; font-weight: 700; color: var(--text-primary);
          letter-spacing: -0.3px; margin-bottom: 6px;
        }
        .login-demo-persona-meta {
          font-size: 12px; color: var(--text-muted, #546889);
          letter-spacing: 0.2px; line-height: 1.5;
        }
        .login-demo-note {
          text-align: center; margin-top: 2px;
          font-size: 10px; color: var(--text-muted, #546889); opacity: 0.7; letter-spacing: 0.4px;
        }
        .login-theme-toggle {
          position: absolute; top: 20px; right: 20px; z-index: 10;
          background: var(--bg-card, rgba(15,27,54,0.6)); backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px);
          border: 1px solid var(--border-default, rgba(56,97,168,0.15)); border-radius: 10px;
          width: 36px; height: 36px; cursor: pointer; font-size: 16px; line-height: 1;
          color: var(--text-muted, #546889); transition: all 0.3s;
          display: flex; align-items: center; justify-content: center;
        }
        .login-theme-toggle:hover { color: var(--text-primary, #e8edf5); border-color: var(--accent-blue, #5b9ef4); box-shadow: 0 0 12px rgba(91,158,244,0.15); }
        @media (max-width: 900px) {
          .login-split-left { display: none !important; }
          .login-split-right { flex: 1; }
        }
      `}</style>

      {/* Left panel — brand + animated particle network */}
      <div className="login-split-left">
        <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
        <div className="login-grid-overlay" />
        <div className="login-left-divider" />

        <div style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <img src="/logo.png" alt="CloudByte" style={{ width: 72, height: 72, marginBottom: 20, filter: 'drop-shadow(0 0 30px rgba(120,80,255,0.35))' }} />
          <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: 6, color: 'var(--text-primary, #e8edf5)', marginBottom: 4, textAlign: 'center' }}>AURELIUS</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted, #546889)', letterSpacing: 2, textAlign: 'center' }}>Security & operations dashboard</div>
        </div>
      </div>

      {/* Right panel — login form */}
      <div className="login-split-right">
        <button className="login-theme-toggle" onClick={function() { setLoginTheme(theme === 'dark' ? 'light' : 'dark'); }} title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
          {theme === 'dark' ? '☀' : '☾'}
        </button>

        {/* Support call verification — visually distinct, customer-facing
            card placed ABOVE the login form. Audience is the END USER
            who got a phone call from a CBG technician and wants to
            confirm the caller is legit before sharing access. Not every
            CBG client is signed up for Aurelius, so the framing avoids
            assuming the visitor knows what Aurelius is. */}
        {clientId !== 'demo' && (
          <div style={{
            width: '100%', maxWidth: 420, marginBottom: 24,
            padding: '18px 20px',
            background: 'linear-gradient(135deg, rgba(91,158,244,0.12), rgba(120,80,255,0.08))',
            border: '1px solid rgba(91,158,244,0.4)',
            borderRadius: 12,
            boxShadow: '0 4px 16px rgba(91,158,244,0.15)',
            display: 'flex', flexDirection: 'column', gap: 10
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 36, height: 36, borderRadius: '50%',
                background: 'rgba(91,158,244,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 18, color: '#5b9ef4', flex: 'none'
              }}>⛨</div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.2, color: '#5b9ef4' }}>SUPPORT CALL VERIFICATION</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary, #e8edf5)' }}>Did a technician call you?</div>
              </div>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary, #94a3b8)', lineHeight: 1.55 }}>
              Confirm the caller is a real CloudByte Group technician before sharing access, credentials, or sensitive information.
            </div>
            <button
              type="button"
              onClick={function() { setMspVerifyOpen(true); }}
              style={{
                background: '#5b9ef4', color: '#fff',
                border: 'none', borderRadius: 6,
                padding: '10px 18px', fontSize: 12, fontWeight: 700,
                letterSpacing: 0.5, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={function(ev) { ev.currentTarget.style.background = '#3b82f6'; }}
              onMouseLeave={function(ev) { ev.currentTarget.style.background = '#5b9ef4'; }}
            >
              <span>VERIFY A TECHNICIAN</span>
              <span style={{ fontSize: 14 }}>→</span>
            </button>
          </div>
        )}

        <div className={'login-card-v2' + (shake ? ' shake' : '') + (success ? ' success' : '')}>
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <div className="login-split-mobile-logo" style={{ display: 'none' }}><img src="/logo.png" alt="CloudByte" style={{ width: 48, height: 48 }} /></div>
            <style>{`.login-split-right .login-split-mobile-logo { display: none !important; } @media (max-width: 900px) { .login-split-right .login-split-mobile-logo { display: flex !important; justify-content: center; margin-bottom: 8px; } }`}</style>
            <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: 3, color: 'var(--text-primary, #e8edf5)', marginBottom: 4 }}>
              {clientId === 'demo' ? 'Take a tour' : clientId ? 'Welcome back' : 'Sign in'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted, #546889)', letterSpacing: 1.5 }}>
              {clientId === 'demo' ? 'A live look at the platform' : clientId ? 'Client dashboard access' : 'Administrator access'}
            </div>
            {clientName && clientId !== 'demo' && (
              <div style={{ marginTop: 16, padding: '10px 16px', background: 'var(--accent-blue-dim, rgba(91,158,244,0.12))', border: '1px solid rgba(91,158,244,0.2)', borderRadius: 8, fontSize: 14, fontWeight: 600, color: 'var(--accent-blue, #5b9ef4)' }}>
                {clientName}
              </div>
            )}
          </div>

          {clientId === 'demo' ? (
            <div className="login-demo-landing">
              <div className="login-demo-persona-card">
                <div className="login-demo-persona-body">
                  <div className="login-demo-persona-eyebrow">Sample Environment</div>
                  <div className="login-demo-persona-name">Helios Industries</div>
                  <div className="login-demo-persona-meta">52 employees · 3 offices · Full stack deployed</div>
                </div>
              </div>
              <button type="button" className={'login-btn-v2' + (demoLaunching ? ' loading' : '') + (success ? ' success-state' : '')} onClick={function() { launchDemo(); }} disabled={demoLaunching || success}>
                {success ? '✓ Entering' : demoLaunching ? 'Launching...' : 'Enter Demo'}
              </button>
              <div className="login-demo-note">No signup · No credit card · No strings attached</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Microsoft SSO — admin login (CBG tenant) OR per-client SSO when
                  the client has sso_enabled flipped on (commercial tier opt-in). */}
              {ssoEnabled && (
                <>
                  <button
                    type="button"
                    className={'login-sso-btn' + (ssoProcessing ? ' loading' : '') + (success ? ' success-state' : '')}
                    onClick={function() {
                      window.location.href = API + '/auth/microsoft/login' + (clientId ? '?clientId=' + encodeURIComponent(clientId) : '');
                    }}
                    disabled={ssoProcessing || success || loading}
                    title={clientId
                      ? ('Sign in with your ' + (clientName || 'company') + ' Microsoft account')
                      : 'Sign in with your CloudByte Group Microsoft account'}
                  >
                    <svg className="login-sso-logo" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                    </svg>
                    <span>{ssoProcessing ? 'Signing in…' : success ? '✓ Signed in' : 'Sign in with Microsoft'}</span>
                  </button>
                  <div className="login-sso-divider"><span>or use password</span></div>
                </>
              )}

              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {clientId && clientId !== 'demo' && (
                  <div style={{
                    display: 'flex',
                    background: 'var(--surface-2, rgba(255,255,255,0.04))',
                    border: '1px solid var(--border, rgba(255,255,255,0.08))',
                    borderRadius: 8,
                    padding: 3,
                    fontSize: 12,
                    fontWeight: 600
                  }}>
                    <button
                      type="button"
                      onClick={function() { setUseEmail(false); setEmail(''); setError(''); }}
                      style={{
                        flex: 1, padding: '8px 12px', borderRadius: 6, border: 0, cursor: 'pointer',
                        background: !useEmail ? 'var(--accent-blue, #5b9ef4)' : 'transparent',
                        color: !useEmail ? '#fff' : 'var(--text-muted, #546889)',
                        transition: 'all 0.15s ease', fontFamily: 'inherit', fontSize: 12, fontWeight: 600
                      }}
                    >
                      Shared account
                    </button>
                    <button
                      type="button"
                      onClick={function() { setUseEmail(true); setError(''); }}
                      style={{
                        flex: 1, padding: '8px 12px', borderRadius: 6, border: 0, cursor: 'pointer',
                        background: useEmail ? 'var(--accent-blue, #5b9ef4)' : 'transparent',
                        color: useEmail ? '#fff' : 'var(--text-muted, #546889)',
                        transition: 'all 0.15s ease', fontFamily: 'inherit', fontSize: 12, fontWeight: 600
                      }}
                    >
                      My individual login
                    </button>
                  </div>
                )}
                {useEmail && (
                  <input
                    type="email"
                    className="login-input"
                    placeholder="you@company.com"
                    value={email}
                    onChange={function(e) { setEmail(e.target.value); }}
                    autoFocus
                    autoComplete="username"
                    style={{ borderColor: error ? 'var(--accent-red, #f06565)' : undefined, boxShadow: error ? '0 0 0 3px var(--accent-red-dim, rgba(240,101,101,0.1))' : undefined }}
                  />
                )}
                <div className="login-pw-wrap">
                  <input
                    type={showPw ? 'text' : 'password'}
                    className="login-input"
                    placeholder="Enter password"
                    value={password}
                    onChange={function(e) { setPassword(e.target.value); }}
                    autoFocus={!ssoEnabled && !useEmail}
                    autoComplete="current-password"
                    style={{ borderColor: error ? 'var(--accent-red, #f06565)' : undefined, boxShadow: error ? '0 0 0 3px var(--accent-red-dim, rgba(240,101,101,0.1))' : undefined }}
                  />
                  <button type="button" className="login-pw-toggle" onClick={function() { setShowPw(!showPw); }} tabIndex={-1} title={showPw ? 'Hide password' : 'Show password'}>
                    {showPw ? '◉' : '○'}
                  </button>
                </div>
                <button type="submit" className={'login-btn-v2' + (loading ? ' loading' : '') + (success ? ' success-state' : '')} disabled={loading || !password || (useEmail && !email) || success || ssoProcessing}>
                  {success ? '✓ Authenticated' : loading ? 'Verifying...' : 'Enter Aurelius'}
                </button>
              </form>
            </div>
          )}

          {error && (
            <div style={{ marginTop: 14, padding: '10px 14px', background: 'var(--accent-red-dim, rgba(240,101,101,0.1))', border: '1px solid rgba(240,101,101,0.15)', borderRadius: 8, color: 'var(--accent-red, #f06565)', fontSize: 13, fontWeight: 500, animation: 'loginStatFade 0.3s ease' }}>
              {error}
            </div>
          )}

          <div style={{ marginTop: 24, textAlign: 'center', fontSize: 10, color: 'var(--text-muted, #546889)', letterSpacing: 0.5 }}>
            {clientId === 'demo' ? 'Poke around · Break nothing · Leave impressed' : 'Encrypted connection · Secure authentication'}
          </div>
        </div>

        <MspVerifyDrawer isOpen={mspVerifyOpen} onClose={function() { setMspVerifyOpen(false); }} />

        <div style={{ marginTop: 32, maxWidth: 380, textAlign: 'center' }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted, #546889)', opacity: 0.45, fontStyle: 'italic', lineHeight: 1.7, letterSpacing: 0.2 }}>
            "The first rule is to keep an untroubled spirit. The second is to look things in the face and know them for what they are."
          </div>
          <div style={{ fontSize: 10, color: 'var(--text-muted, #546889)', opacity: 0.3, marginTop: 6, letterSpacing: 1 }}>
            MARCUS AURELIUS · MEDITATIONS IV.3
          </div>
        </div>
        <div style={{ marginTop: 16, fontSize: 10, color: 'var(--text-muted, #546889)', opacity: 0.3, letterSpacing: 0.3 }}>
          Aurelius {appVersion ? 'v' + appVersion : ''} · CloudByte Group LLC
        </div>
      </div>
    </div>
  );
}
// ── Detect client URL mode ────────────────────────────────────────
function getClientFromURL() {
  const path = window.location.pathname;
  const match = path.match(/^\/client\/([a-z0-9-]+)/i);
  return match ? match[1].toLowerCase() : null;
}

const URL_CLIENT = getClientFromURL();
const IS_CLIENT_VIEW = !!URL_CLIENT;
const IS_DEMO_VIEW = URL_CLIENT === 'demo';

// Prospect-facing tenant assessment route: /assess/<token>
function getAssessTokenFromURL() {
  var m = window.location.pathname.match(/^\/assess\/([A-Za-z0-9_-]+)/);
  if (!m) return null;
  // Reserved sub-paths (error page, etc.) should not be treated as tokens
  if (m[1] === 'error') return null;
  return m[1];
}
const URL_ASSESS_TOKEN = getAssessTokenFromURL();
const IS_ASSESS_VIEW = !!URL_ASSESS_TOKEN || window.location.pathname === '/assess/error';
// Admin-only features (AI insights, QBR generation) are available to admins
// AND to demo visitors — the demo is a sales tool, prospects need to see these.
const CAN_USE_ADMIN_FEATURES = !IS_CLIENT_VIEW || IS_DEMO_VIEW;

// Runtime role check — reads sessionStorage at call time. Use this for
// PERMISSION gating (admin-only features, hidden tabs, gated buttons).
// IS_CLIENT_VIEW is a URL-based layout check ("is this the /client/<id>
// portal URL?") and IS NOT a permission check — a client-role user
// who logs in at the main / URL has IS_CLIENT_VIEW=false but is still
// not an admin. Mistaking one for the other is how we surfaced
// "Admin access required" to a client-role user on the Overview tab.
function isAdminRole() {
  try { return sessionStorage.getItem('aurelius_role') === 'admin'; }
  catch (e) { return false; }
}

// ── Restricted-user JWT claims (S1.14) ─────────────────────────────
// Decoded once per call from sessionStorage (set on login). Returns:
//   { allowedTabs: string[] | null, readOnly: bool, roleLabel: string | null }
// allowedTabs=null means "no per-tab restriction" — full access per the
// user's base role. readOnly=true forces every mutating affordance off.
function getRestrictedClaims() {
  try {
    var raw = sessionStorage.getItem('aurelius_allowed_tabs');
    var allowed = raw ? JSON.parse(raw) : null;
    return {
      allowedTabs: Array.isArray(allowed) && allowed.length > 0 ? allowed : null,
      readOnly: sessionStorage.getItem('aurelius_read_only') === '1',
      roleLabel: sessionStorage.getItem('aurelius_role_label') || null
    };
  } catch (e) {
    return { allowedTabs: null, readOnly: false, roleLabel: null };
  }
}

function isReadOnlyUser() { return getRestrictedClaims().readOnly; }

const placeholders = {
  patches: { compliant: 0, pending: 0, failed: 0 },
  credentials: { total: 0, weak: 0, reused: 0, breached: 0, mfaEnabled: 0 },
  siem: { totalEvents: 0, findings: 0, critical: 0, high: 0, medium: 0 },
  tickets: { open: 0, resolved: 0, avgResponse: '—', sla: 0, csat: 0 },
  projects: [],
  cmmc_domains: {
    'Access Control (AC)': 0, 'Awareness & Training (AT)': 0,
    'Audit & Accountability (AU)': 0, 'Config Management (CM)': 0,
    'Identification & Auth (IA)': 0, 'Incident Response (IR)': 0,
    'Maintenance (MA)': 0, 'Media Protection (MP)': 0,
    'Personnel Security (PS)': 0, 'Physical Protection (PE)': 0,
    'Risk Assessment (RA)': 0, 'Security Assessment (CA)': 0,
    'System & Comm Protection (SC)': 0, 'System & Info Integrity (SI)': 0,
  },
};

function scoreVerdict(score) {
  if (score === 0) return { text: 'No data', color: 'var(--text-muted)' };
  if (score >= 90) return { text: 'Strong', color: '#22c55e' };
  if (score >= 70) return { text: 'Good', color: '#f59e0b' };
  if (score >= 50) return { text: 'Needs attention', color: '#f97316' };
  return { text: 'Critical', color: '#ef4444' };
}

function ScoreRing({ score, label, size = 100, hero }) {
  // 240-degree gradient arc (not a full ring)
  var gaugeSize = hero ? 120 : 96;
  var strokeW = hero ? 10 : 8;
  var radius = (gaugeSize - strokeW) / 2;
  var arcDeg = 240;
  var arcRad = (arcDeg * Math.PI) / 180;
  var circumference = arcRad * radius;
  var startAngle = 150; // starts at bottom-left

  // score === null/undefined → render a neutral "Not configured" gauge instead
  // of a red 0% ring. The integration just isn't wired for this client yet.
  var unconfigured = score == null;
  var fillLen = unconfigured ? 0 : (score / 100) * circumference;

  // Gradient color stops based on score
  var colorStart, colorEnd, glowColor;
  if (unconfigured)        { colorStart = '#475569'; colorEnd = '#64748b'; glowColor = 'rgba(100,116,139,0.10)'; }
  else if (score >= 85)    { colorStart = '#22c55e'; colorEnd = '#4ade80'; glowColor = 'rgba(34,197,94,0.25)'; }
  else if (score >= 70)    { colorStart = '#f59e0b'; colorEnd = '#fbbf24'; glowColor = 'rgba(245,158,11,0.25)'; }
  else if (score >= 50)    { colorStart = '#f97316'; colorEnd = '#fb923c'; glowColor = 'rgba(249,115,22,0.25)'; }
  else                     { colorStart = '#ef4444'; colorEnd = '#f87171'; glowColor = 'rgba(239,68,68,0.25)'; }

  var verdict = unconfigured ? { text: 'Not configured', color: '#64748b' } : scoreVerdict(score);
  var gradId = 'sg-' + label.replace(/\s/g, '').toLowerCase();
  var cx = gaugeSize / 2;
  var cy = gaugeSize / 2;

  // Build arc path
  function polarToCart(cx, cy, r, deg) {
    var rad = ((deg - 90) * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  }
  var arcStart = polarToCart(cx, cy, radius, startAngle);
  var arcEnd = polarToCart(cx, cy, radius, startAngle + arcDeg);
  var largeArc = arcDeg > 180 ? 1 : 0;
  var bgPath = 'M ' + arcStart.x + ' ' + arcStart.y + ' A ' + radius + ' ' + radius + ' 0 ' + largeArc + ' 1 ' + arcEnd.x + ' ' + arcEnd.y;

  return (
    <div className={'score-gauge' + (hero ? ' score-gauge-hero' : '')} style={{ '--score-glow': glowColor }}>
      <svg width={gaugeSize} height={gaugeSize} viewBox={'0 0 ' + gaugeSize + ' ' + gaugeSize}>
        <defs>
          <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={colorStart} />
            <stop offset="100%" stopColor={colorEnd} />
          </linearGradient>
        </defs>
        {/* Background track */}
        <path d={bgPath} fill="none" stroke="var(--bg-elevated, #1e293b)" strokeWidth={strokeW} strokeLinecap="round" />
        {/* Filled arc — skipped when unconfigured or score=0 */}
        {!unconfigured && score > 0 && <path d={bgPath} fill="none" stroke={'url(#' + gradId + ')'} strokeWidth={strokeW}
          strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={circumference - fillLen}
          style={{ transition: 'stroke-dashoffset 1s cubic-bezier(0.4, 0, 0.2, 1)' }} />}
        {/* Center label — number for scored, dash for unconfigured */}
        {unconfigured
          ? <text x={cx} y={cy - 2} textAnchor="middle" dominantBaseline="central" className="score-gauge-number" fill="var(--text-muted, #94a3b8)">—</text>
          : <text x={cx} y={cy - 2} textAnchor="middle" dominantBaseline="central" className="score-gauge-number" fill="var(--text-primary, #f1f5f9)">{score}<tspan className="score-gauge-suffix">/100</tspan></text>
        }
      </svg>
      <span className="score-gauge-label">{label}</span>
      <span className="score-gauge-verdict" style={{ background: verdict.color + '15', color: verdict.color }}>{verdict.text}</span>
    </div>
  );
}

function StatCard({ title, value, subtitle, accent, live, onClick }) {
  return (
    <div className={`stat-card ${live ? 'stat-card-live' : ''} ${onClick ? 'stat-card-clickable' : ''}`} onClick={onClick || undefined}>
      <div className="stat-title">
        {title}
        {live && <span className="live-badge">LIVE</span>}
        {onClick && <span className="stat-drill">Details ›</span>}
      </div>
      <div className="stat-value" style={{ color: accent || '#e8edf5' }}>{value}</div>
      {subtitle && <div className="stat-subtitle">{subtitle}</div>}
    </div>
  );
}

function ProgressBar({ label, value, status }) {
  const color = value >= 80 ? '#22c55e' : value >= 50 ? '#f59e0b' : '#ef4444';
  return (
    <div className="progress-item">
      <div className="progress-header">
        <span>{label}</span>
        <span className={`status-badge ${status.toLowerCase().replace(' ', '-')}`}>{status}</span>
      </div>
      <div className="progress-track">
        <div className="progress-fill" style={{ width: `${value}%`, backgroundColor: color }} />
      </div>
      <div className="progress-pct">{value}%</div>
    </div>
  );
}

function CMMCBar({ domain, score }) {
  const color = score >= 80 ? '#22c55e' : score >= 60 ? '#f59e0b' : '#ef4444';
  return (
    <div className="cmmc-bar-row">
      <div className="cmmc-domain-name">{domain}</div>
      <div className="cmmc-bar-track">
        <div className="cmmc-bar-fill" style={{ width: `${score}%`, backgroundColor: color }} />
      </div>
      <div className="cmmc-bar-score">{score}%</div>
    </div>
  );
}

function TrendSparkline({ data, dataKey, color, label, height = 80, domain, suffix, invertDelta }) {
  if (!data || data.length === 0) return null;
  var latest = data[data.length - 1] ? data[data.length - 1][dataKey] : null;
  var first = data[0] ? data[0][dataKey] : null;
  var delta = latest != null && first != null ? latest - first : null;
  var yDomain = domain || ['dataMin - 2', 'dataMax + 2'];
  var deltaIsGood = invertDelta ? delta < 0 : delta > 0;

  return (
    <div className="sparkline-container">
      <div className="sparkline-header">
        <div className="sparkline-label">{label}</div>
        <div className="sparkline-current">
          <span className="sparkline-value" style={{ color: color }}>{latest != null ? latest : '—'}{suffix || ''}</span>
          {delta != null && delta !== 0 && data.length > 1 && (
            <span className={'sparkline-delta ' + (deltaIsGood ? 'sparkline-delta-up' : 'sparkline-delta-down')}>
              {delta > 0 ? '▲' : '▼'} {Math.abs(delta)}{suffix || ''}
            </span>
          )}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 4, right: 8, bottom: 4, left: -12 }}>
          <XAxis dataKey="date" hide={true} />
          <YAxis
            domain={yDomain}
            width={40}
            tick={{ fontSize: 10, fill: '#546889', fontFamily: 'var(--font-mono, monospace)' }}
            tickLine={false}
            axisLine={false}
            tickCount={3}
          />
          <Tooltip
            contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 6, fontSize: 12 }}
            labelStyle={{ color: '#94a3b8' }}
            itemStyle={{ color: color }}
            formatter={function(value) { return [value + (suffix || ''), label]; }}
            labelFormatter={function(d) { return d; }}
          />
          <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} dot={data.length < 10} activeDot={{ r: 4, fill: color }} />
        </LineChart>
      </ResponsiveContainer>
      {data.length === 1 && <div className="sparkline-hint">Trends appear as more daily snapshots are recorded</div>}
    </div>
  );
}

// ── Capability Coverage ──────────────────────────────────────────────
// Replaces the legacy vendor-name stack with a capability-first view.
// Clients think in capabilities ("do I have endpoint protection?") not
// in vendor names ("do I have NinjaOne?"). The map below resolves each
// capability against whatever vendor(s) actually back it for the client
// — works seamlessly for mixed stacks (UniFi + Meraki), CMMC clients
// where Atomus Aegis substitutes for Huntress + Blumira, and clients
// who don't deploy every capability.
//
// Each capability returns:
//   state:  'active' | 'inactive'
//   vendors: [{ name, live, role }] — what's backing it (or nothing if inactive)
//   essential: true if a CBG-tier client should always have this; controls
//              whether 'inactive' shows as a gap or a soft skip
var CAPABILITIES = [
  {
    id: 'endpoint',
    label: 'Endpoint Protection',
    description: 'RMM-managed patching, configuration baseline, and AV coverage on managed endpoints.',
    essential: true,
    evaluate: function(c, live) {
      var vendors = [];
      if (live.ninjaone)     vendors.push({ name: 'NinjaOne (Commercial)', live: true, role: 'RMM' });
      if (live.ninjaone_gov) vendors.push({ name: 'NinjaOne Gov',          live: true, role: 'RMM (FedRAMP)' });
      // Defender is the AV baseline on every Microsoft tenant we manage.
      // Surfaced separately rather than implicit so clients see it explicitly.
      if (live.defender)     vendors.push({ name: 'Microsoft Defender',    live: true, role: 'Built-in AV' });
      return { state: vendors.length > 0 ? 'active' : 'inactive', vendors: vendors };
    }
  },
  {
    id: 'threat',
    label: 'Threat Detection (EDR)',
    description: 'Active monitoring for malicious behavior on endpoints, with response capability.',
    essential: true,
    evaluate: function(c, live) {
      // Defender for Endpoint is the EDR floor on every Microsoft-tenant
      // client (CMMC + commercial). Huntress is the layered 24/7 SOC for
      // commercial tier. Atomus Aegis is NOT EDR — it's compliance
      // evidence collection (lives under its own capability below).
      var vendors = [];
      if (live.defender) vendors.push({ name: 'Microsoft Defender for Endpoint', live: true, role: 'Baseline EDR' });
      if (live.huntress) vendors.push({ name: 'Huntress',                         live: true, role: '24/7 managed SOC' });
      return { state: vendors.length > 0 ? 'active' : 'inactive', vendors: vendors };
    }
  },
  {
    id: 'identity',
    label: 'Identity & Access',
    description: 'User accounts, MFA, conditional access, and lifecycle governance.',
    essential: true,
    evaluate: function(c, live) {
      var vendors = [];
      if (live.entra) vendors.push({ name: 'Microsoft Entra', live: true, role: 'Primary IdP' });
      // Future: live.google_workspace for clients with Google IdP
      return { state: vendors.length > 0 ? 'active' : 'inactive', vendors: vendors };
    }
  },
  {
    id: 'network',
    label: 'Network Monitoring',
    description: 'Visibility into gateways, switches, access points, and site-level health.',
    essential: false,
    evaluate: function(c, live) {
      var vendors = [];
      if (live.unifi)  vendors.push({ name: 'Ubiquiti UniFi', live: true, role: 'Network controller' });
      if (live.meraki) vendors.push({ name: 'Cisco Meraki',   live: true, role: 'Cloud-managed network' });
      return { state: vendors.length > 0 ? 'active' : 'inactive', vendors: vendors };
    }
  },
  {
    id: 'siem',
    label: 'SIEM / Audit Logging',
    description: 'Centralized log aggregation and security event correlation.',
    essential: true,
    evaluate: function(c, live) {
      // Tier split: Blumira for commercial, Microsoft Sentinel for CMMC
      // (wired through Atomus approval). Atomus Aegis is NOT a SIEM —
      // it's compliance evidence collection. Don't conflate.
      var vendors = [];
      if (live.blumira)  vendors.push({ name: 'Blumira',            live: true, role: 'Commercial SIEM' });
      if (live.sentinel) vendors.push({ name: 'Microsoft Sentinel', live: true, role: 'CMMC SIEM (via Atomus)' });
      return { state: vendors.length > 0 ? 'active' : 'inactive', vendors: vendors };
    }
  },
  {
    id: 'vault',
    label: 'Password Vault',
    description: 'Encrypted credential storage and secure sharing across teams.',
    essential: false,
    evaluate: function(c, live) {
      var vendors = [];
      if (live.keeper) vendors.push({ name: c.cmmc ? 'KeeperGov' : 'Keeper', live: true, role: 'FIPS-validated vault' });
      return { state: vendors.length > 0 ? 'active' : 'inactive', vendors: vendors };
    }
  },
  {
    id: 'compliance_evidence',
    label: 'Compliance Evidence Collection',
    description: 'Endpoint-level audit log collection and CMMC control evidence — separate from threat detection.',
    essential: false,
    onlyIf: function(c) { return !!c.cmmc; },           // CMMC-only — Aegis is the compliance agent, not an EDR
    evaluate: function(c, live) {
      return { state: live.atomus ? 'active' : 'inactive', vendors: live.atomus ? [{ name: 'Atomus Aegis', live: true, role: 'Endpoint compliance agent' }] : [] };
    }
  },
  {
    id: 'compliance',
    label: 'Compliance Posture',
    description: 'CMMC L1/L2 control evidence, SSP support, and continuous control monitoring.',
    essential: false,
    onlyIf: function(c) { return !!c.cmmc; },           // hidden for non-CMMC clients
    evaluate: function(c, live) {
      return { state: 'active', vendors: [{ name: 'Atomus + Aurelius', live: true, role: 'CMMC partnership' }] };
    }
  },
  {
    id: 'projects',
    label: 'Project Tracking',
    description: 'Active engagement tracking, milestones, and task progress.',
    essential: false,
    evaluate: function(c, live) {
      return { state: live.asana ? 'active' : 'inactive', vendors: live.asana ? [{ name: 'Asana', live: true, role: 'PM platform' }] : [] };
    }
  },
  {
    id: 'devops',
    label: 'DevOps Tracking',
    description: 'Engineering issue tracking and release coordination.',
    essential: false,
    evaluate: function(c, live) {
      return { state: live.jira ? 'active' : 'inactive', vendors: live.jira ? [{ name: 'Jira', live: true, role: 'Issue tracker' }] : [] };
    }
  },
  {
    id: 'spend',
    label: 'Cloud Spend Mgmt',
    description: 'Subscription cost tracking and license utilization across the SaaS stack.',
    essential: false,
    evaluate: function(c, live) {
      return { state: live.pax8 ? 'active' : 'inactive', vendors: live.pax8 ? [{ name: 'Pax8', live: true, role: 'CSP marketplace' }] : [] };
    }
  }
];

// Compute the rolled-up capability state for the current client.
function computeCoverage(c, live) {
  live = live || {};
  return CAPABILITIES.filter(function(cap) {
    return !cap.onlyIf || cap.onlyIf(c);
  }).map(function(cap) {
    var r = cap.evaluate(c, live);
    return Object.assign({}, cap, r);
  });
}

// Compact pill in the client banner. Click → drawer.
function CoverageBadge({ c, live, onClick }) {
  var caps = computeCoverage(c, live);
  var active = caps.filter(function(x) { return x.state === 'active'; }).length;
  var total = caps.length;
  var essentialGaps = caps.filter(function(x) { return x.essential && x.state === 'inactive'; }).length;
  var pct = total > 0 ? Math.round((active / total) * 100) : 0;
  var tone = essentialGaps > 0 ? 'gap' : pct === 100 ? 'full' : 'partial';
  return (
    <button type="button" className={'coverage-badge coverage-badge-' + tone} onClick={onClick} title="Click to see capability coverage">
      <span className="coverage-badge-grid" aria-hidden="true">
        {/* mini 3×3 dot grid that fills proportionally */}
        {[0,1,2,3,4,5,6,7,8].map(function(i) {
          var on = i < Math.round((active / Math.max(total, 1)) * 9);
          return <span key={i} className={'coverage-grid-dot' + (on ? ' on' : '')} />;
        })}
      </span>
      <span className="coverage-badge-text">
        <strong>{active}</strong>/{total} <span className="coverage-badge-sub">capabilities</span>
        {essentialGaps > 0 && <span className="coverage-badge-gap-flag">{essentialGaps} gap{essentialGaps === 1 ? '' : 's'}</span>}
      </span>
    </button>
  );
}

// Drawer overlay — shows the full capability matrix with vendor attribution.
function CoverageDrawer({ c, live, onClose }) {
  var caps = computeCoverage(c, live);
  var active = caps.filter(function(x) { return x.state === 'active'; }).length;
  return (
    <>
      <div className="coverage-drawer-overlay" onClick={onClose} />
      <aside className="coverage-drawer" role="dialog" aria-label="Service Coverage">
        <div className="coverage-drawer-head">
          <div>
            <div className="coverage-drawer-title">Service Coverage</div>
            <div className="coverage-drawer-sub">
              <strong>{active}</strong> of <strong>{caps.length}</strong> capabilities active for {c.name}
            </div>
          </div>
          <button type="button" className="coverage-drawer-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="coverage-drawer-body">
          {caps.map(function(cap) {
            var stateCls = 'coverage-cap-' + cap.state + (cap.essential ? ' coverage-cap-essential' : '');
            return (
              <div key={cap.id} className={'coverage-cap ' + stateCls}>
                <div className="coverage-cap-marker">
                  {cap.state === 'active'
                    ? <span className="coverage-cap-check">✓</span>
                    : cap.essential
                      ? <span className="coverage-cap-warn">⊘</span>
                      : <span className="coverage-cap-dash">—</span>}
                </div>
                <div className="coverage-cap-body">
                  <div className="coverage-cap-row">
                    <div className="coverage-cap-label">{cap.label}</div>
                    {cap.essential && cap.state === 'inactive' && <span className="coverage-cap-tag coverage-cap-tag-gap">Recommended — not deployed</span>}
                    {!cap.essential && cap.state === 'inactive' && <span className="coverage-cap-tag coverage-cap-tag-opt">Optional</span>}
                  </div>
                  <div className="coverage-cap-desc">{cap.description}</div>
                  {cap.vendors.length > 0 && (
                    <div className="coverage-cap-vendors">
                      {cap.vendors.map(function(v, i) {
                        return (
                          <span key={i} className="coverage-cap-vendor">
                            <span className="coverage-cap-vendor-dot" />
                            <span className="coverage-cap-vendor-name">{v.name}</span>
                            {v.role && <span className="coverage-cap-vendor-role">{v.role}</span>}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="coverage-drawer-foot">
          Vendor attribution shown for transparency. Talk to CloudByte Group about expanding coverage.
        </div>
      </aside>
    </>
  );
}

function computeTrend(history, dataKey) {
  if (!history || history.length < 2) return null;
  var latest = null, prev = null;
  for (var i = history.length - 1; i >= 0; i--) {
    if (history[i][dataKey] != null) {
      if (latest === null) latest = history[i][dataKey];
      else if (prev === null) { prev = history[i][dataKey]; break; }
    }
  }
  if (latest === null || prev === null) return null;
  if (latest > prev) return 'up';
  if (latest < prev) return 'down';
  return 'flat';
}

function exportTableCSV(headers, rows, filename) {
  var csv = headers.join(',') + '\n';
  rows.forEach(function(row) {
    csv += row.map(function(cell) {
      var s = String(cell == null ? '' : cell);
      if (s.indexOf(',') !== -1 || s.indexOf('"') !== -1 || s.indexOf('\n') !== -1) s = '"' + s.replace(/"/g, '""') + '"';
      return s;
    }).join(',') + '\n';
  });
  var blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  var url = URL.createObjectURL(blob);
  var a = document.createElement('a');
  a.href = url; a.download = (filename || 'export') + '.csv';
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function KPIStrip({ items }) {
  return (
    <div className="kpi-strip">
      {items.map(function(item, i) {
        var classes = 'kpi-strip-item';
        if (item.onClick) classes += ' kpi-strip-clickable';
        if (item.live) classes += ' kpi-strip-live';
        return (
          <div key={i} className={classes} onClick={item.onClick || undefined}>
            <div className="kpi-strip-label">{item.label}</div>
            <div className="kpi-strip-value" style={{ color: item.accent || 'var(--text-primary)' }}>
              {item.value}
              {item.trend && <span className={'trend-arrow ' + item.trend}>{item.trend === 'up' ? '↑' : item.trend === 'down' ? '↓' : '→'}</span>}
            </div>
            {item.subtitle && <div className="kpi-strip-subtitle">{item.subtitle}</div>}
          </div>
        );
      })}
    </div>
  );
}

function StatCardCompact({ title, value, subtitle, accent, live, onClick }) {
  return (
    <div className={'stat-card-compact' + (live ? ' stat-card-live' : '') + (onClick ? ' stat-card-clickable' : '')} onClick={onClick || undefined}>
      <div className="stat-title">
        {title}
        {live && <span className="live-badge">LIVE</span>}
        {onClick && <span className="stat-drill">Details ›</span>}
      </div>
      <div className="stat-value" style={{ color: accent || '#e8edf5' }}>{value}</div>
      {subtitle && <div className="stat-subtitle">{subtitle}</div>}
    </div>
  );
}

function CollapsibleSection({ title, defaultOpen, children, rightContent }) {
  var _state = useState(defaultOpen !== false);
  var isOpen = _state[0];
  var setIsOpen = _state[1];
  return (
    <>
      <div className="section-title-collapsible" onClick={function() { setIsOpen(!isOpen); }}>
        <span className={'section-chevron' + (isOpen ? '' : ' collapsed')}>&#9662;</span>
        {title}
        {rightContent && <span className="section-title-right">{rightContent}</span>}
      </div>
      <div className={'section-content' + (isOpen ? '' : ' collapsed')}>
        {children}
      </div>
    </>
  );
}

// ── v8 Components — Content Bands, Segment Bars, Metric Lists ───────────────

function SegmentBar({ segments, size, showLegend }) {
  var total = segments.reduce(function(s, seg) { return s + (seg.value || 0); }, 0);
  if (total === 0) return null;
  return (
    <div className="segment-bar-wrap">
      <div className={'segment-bar' + (size === 'lg' ? ' segment-bar-lg' : '')}>
        {segments.map(function(seg, i) {
          if (!seg.value) return null;
          var pct = (seg.value / total) * 100;
          return <div key={i} className="segment-bar-segment" style={{ width: pct + '%', background: seg.color }} title={seg.label + ': ' + seg.value + ' (' + Math.round(pct) + '%)'} />;
        })}
      </div>
      {showLegend !== false && (
        <div className="segment-bar-legend">
          {segments.map(function(seg, i) {
            if (!seg.value && !seg.showZero) return null;
            return (
              <div key={i} className={'segment-bar-legend-item' + (seg.onClick ? ' clickable' : '')} onClick={seg.onClick || undefined}>
                <span className="segment-bar-legend-dot" style={{ background: seg.color }} />
                <span className="segment-bar-legend-value">{seg.value}</span>
                <span className="segment-bar-legend-label">{seg.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ContentBand({ title, subtitle, accent, children, actions, rightContent }) {
  return (
    <div className={'content-band' + (accent ? ' content-band-accent' : '')}>
      {(title || actions || rightContent) && (
        <div className="content-band-header">
          <div>
            {title && <div className="content-band-title">{title}</div>}
            {subtitle && <div className="content-band-subtitle">{subtitle}</div>}
          </div>
          <div className="content-band-actions">
            {rightContent}
            {actions}
          </div>
        </div>
      )}
      {children}
    </div>
  );
}

function MetricList({ children }) {
  return <div className="metric-list">{children}</div>;
}

function MetricListItem({ icon, iconColor, label, sublabel, value, valueColor, badge, badgeColor, trail, onClick }) {
  return (
    <div className={'metric-list-item' + (onClick ? ' clickable' : '')} onClick={onClick || undefined}>
      {icon && <div className="metric-list-icon" style={{ background: (iconColor || 'var(--accent-blue)') + '15', color: iconColor || 'var(--accent-blue)' }}>{icon}</div>}
      <div className="metric-list-body">
        <div className="metric-list-label">{label}</div>
        {sublabel && <div className="metric-list-sublabel">{sublabel}</div>}
      </div>
      {badge && <span className="metric-list-badge" style={{ background: (badgeColor || 'var(--accent-blue)') + '18', color: badgeColor || 'var(--accent-blue)' }}>{badge}</span>}
      {value != null && <div className="metric-list-value" style={{ color: valueColor || 'var(--text-primary)' }}>{value}</div>}
      {trail && <div className="metric-list-trail">{trail}</div>}
      {onClick && <span className="metric-list-arrow">›</span>}
    </div>
  );
}

function MicroBar({ value, max, color, label }) {
  var pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="micro-bar">
      <div className="micro-bar-track">
        <div className="micro-bar-fill" style={{ width: pct + '%', background: color || 'var(--accent-blue)' }} />
      </div>
      <span className="micro-bar-label" style={{ color: color || 'var(--text-primary)' }}>{label || pct + '%'}</span>
    </div>
  );
}

function QuickStats({ items }) {
  return (
    <div className="quick-stats">
      {items.map(function(item, i) {
        return (
          <div key={i} className="quick-stat">
            <span className="quick-stat-value" style={{ color: item.color || 'var(--text-primary)' }}>{item.value}</span>
            <span className="quick-stat-label">{item.label}</span>
            {item.sub && <span className="quick-stat-sub">{item.sub}</span>}
          </div>
        );
      })}
    </div>
  );
}

// ── ProposalDetailPanel + helpers ─────────────────────────────────
// Reusable rich-detail view for an AI proposal. Same component is
// rendered inside the ProposalsDrawer (slide-out) and the ChangesTab
// detail view (full-width), so the review affordances stay consistent
// regardless of where the operator approves from.
//
// Sections:
//   • Eyebrow row — severity, control, automation, trigger, requested_at
//   • Title + rationale + description
//   • Targets (collapsible) — table for user-only sets, list for mixed
//   • Operations (collapsible) — graph_call / ninjaone_call / manual
//   • Blast radius (collapsible) — reversible, rollback, user impact, comms
//   • References — links to NIST / Microsoft docs
//   • Actions — Approve / Reject (admin only)

function ExpandableSection({ title, badge, defaultOpen, children }) {
  var [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="expandable-section">
      <button type="button" className={'expandable-header' + (open ? ' open' : '')} onClick={function() { setOpen(!open); }}>
        <span className="expandable-chevron">{open ? '▾' : '▸'}</span>
        <span className="expandable-title">{title}</span>
        {badge != null && <span className="expandable-badge">{badge}</span>}
      </button>
      {open && <div className="expandable-body">{children}</div>}
    </div>
  );
}

function ProposalTargetsTable({ targets, context }) {
  if (!targets || targets.length === 0) return <div className="muted small">No targets resolved at propose time.</div>;
  var allUsers = targets.every(function(t) { return t && t.kind === 'user'; });
  function fmtDate(d) {
    if (!d) return 'Never';
    try {
      var dt = new Date(d);
      var now = Date.now();
      var diffDays = Math.floor((now - dt.getTime()) / 86400000);
      return dt.toLocaleDateString() + ' (' + (diffDays < 1 ? 'today' : diffDays + 'd ago') + ')';
    } catch (e) { return d; }
  }
  return (
    <div className="proposal-targets-wrap">
      {context && <div className="proposal-targets-context">{context}</div>}
      {allUsers ? (
        <div className="proposal-targets-table-wrap">
          <table className="proposal-targets-table">
            <thead>
              <tr>
                <th>UPN</th>
                <th>Display Name</th>
                <th>Last Sign-In</th>
                <th>Licenses</th>
                <th>Status</th>
                <th>Type</th>
              </tr>
            </thead>
            <tbody>
              {targets.map(function(t, i) {
                return (
                  <tr key={i}>
                    <td><code className="proposal-target-upn">{t.id}</code></td>
                    <td className="proposal-target-name">{t.display_name || <span className="muted small">—</span>}</td>
                    <td>{fmtDate(t.last_sign_in)}</td>
                    <td className="num">{t.license_count != null ? t.license_count : (t.licensed === true ? 'Yes' : t.licensed === false ? 'No' : '—')}</td>
                    <td><span className={'pill-tiny ' + (t.account_enabled === false ? 'pill-muted' : 'pill-good')}>{t.account_enabled === false ? 'Disabled' : 'Enabled'}</span></td>
                    <td className="muted small">{t.user_type || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <ul className="proposal-targets-list">
          {targets.map(function(t, i) {
            return (
              <li key={i} className={'proposal-target proposal-target-' + (t.kind || 'unknown')}>
                <span className="proposal-target-kind">{(t.kind || 'item').toUpperCase()}</span>
                <span className="proposal-target-label">{t.label || t.id}</span>
                {t.action && <span className="proposal-target-action">→ {t.action}</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function ProposalOperationsList({ operations }) {
  if (!operations || operations.length === 0) return <div className="muted small">No operations registered.</div>;
  return (
    <ol className="proposal-operations-list">
      {operations.map(function(op, i) {
        var kindLabel = op.kind === 'graph_call' ? 'GRAPH' : op.kind === 'ninjaone_call' ? 'NINJAONE' : op.kind === 'manual' ? 'MANUAL' : (op.kind || '').toUpperCase();
        return (
          <li key={i} className={'proposal-op proposal-op-' + (op.kind || 'manual')}>
            <div className="proposal-op-row1">
              <span className={'proposal-op-pill proposal-op-pill-' + (op.kind || 'manual')}>{kindLabel}</span>
              <span className="proposal-op-num">step {i + 1}</span>
              <span className="proposal-op-desc">{op.description}</span>
            </div>
            {op.graph && (
              <div className="proposal-op-detail">
                <div className="proposal-op-detail-label">Microsoft Graph call:</div>
                <code className="proposal-op-graph">
                  <strong>{op.graph.method}</strong> {op.graph.path}
                </code>
                {op.graph.body && <pre className="proposal-op-graph-body">{JSON.stringify(op.graph.body, null, 2)}</pre>}
              </div>
            )}
            {op.ps_script && (
              <div className="proposal-op-detail">
                <div className="proposal-op-detail-label">PowerShell:</div>
                <pre className="proposal-op-script">{op.ps_script}</pre>
              </div>
            )}
            {op.ticket_template && (
              <div className="proposal-op-detail">
                <div className="proposal-op-detail-label">NinjaOne ticket template:</div>
                <div className="proposal-op-ticket">
                  <div><strong>Subject:</strong> {op.ticket_template.subject}</div>
                  <div><strong>Priority:</strong> {op.ticket_template.priority}</div>
                  <div className="proposal-op-ticket-body">{op.ticket_template.body}</div>
                </div>
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

function ProposalBlastRadiusPanel({ blast }) {
  if (!blast) return null;
  return (
    <div className="proposal-blast">
      <div className="proposal-blast-row">
        <span className={'proposal-blast-pill ' + (blast.reversible ? 'blast-reversible' : 'blast-irreversible')}>
          {blast.reversible ? 'REVERSIBLE' : 'IRREVERSIBLE'}
        </span>
        <span className={'proposal-blast-pill ' + (blast.comms_required ? 'blast-comms-required' : 'blast-comms-optional')}>
          {blast.comms_required ? 'COMMS REQUIRED' : 'COMMS OPTIONAL'}
        </span>
      </div>
      {Array.isArray(blast.affected_systems) && blast.affected_systems.length > 0 && (
        <div className="proposal-blast-section">
          <div className="proposal-blast-label">Affected systems</div>
          <div className="proposal-blast-chips">
            {blast.affected_systems.map(function(s, i) { return <span key={i} className="proposal-blast-chip">{s}</span>; })}
          </div>
        </div>
      )}
      {blast.user_impact && (
        <div className="proposal-blast-section">
          <div className="proposal-blast-label">User impact</div>
          <div className="proposal-blast-text">{blast.user_impact}</div>
        </div>
      )}
      {blast.rollback && (
        <div className="proposal-blast-section">
          <div className="proposal-blast-label">Rollback path</div>
          <div className="proposal-blast-text">{blast.rollback}</div>
        </div>
      )}
      {blast.comms_template && (
        <div className="proposal-blast-section">
          <div className="proposal-blast-label">Comms template</div>
          <div className="proposal-blast-text proposal-blast-template">{blast.comms_template}</div>
        </div>
      )}
    </div>
  );
}

function ProposalDetailPanel({ proposal, onApprove, onReject, busy, admin, dense }) {
  if (!proposal) return null;
  var preview = proposal.targets_preview;
  var targets = preview && preview.objects;
  var hasTargets = Array.isArray(targets) && targets.length > 0;
  var hasOps = Array.isArray(proposal.operations) && proposal.operations.length > 0;
  var hasBlast = !!proposal.blast_radius;
  var hasRefs = Array.isArray(proposal.references) && proposal.references.length > 0;
  // If the API returned a status (only on the include_resolved path),
  // gate Approve/Reject — only awaiting_approval rows should be
  // actionable. Older payloads without status default to actionable.
  var actionable = !proposal.status || proposal.status === 'awaiting_approval';
  var resolutionStatusPill = null;
  if (proposal.status && proposal.status !== 'awaiting_approval') {
    var labelMap = { running: 'EXECUTING', completed: 'EXECUTED', failed: 'FAILED', cancelled: 'REJECTED' };
    var classMap = { running: 'res-running', completed: 'res-completed', failed: 'res-failed', cancelled: 'res-cancelled' };
    resolutionStatusPill = <span className={'proposal-resolution-pill ' + (classMap[proposal.status] || 'res-other')}>{labelMap[proposal.status] || proposal.status.toUpperCase()}</span>;
  }
  var hasResolution = !!proposal.status && proposal.status !== 'awaiting_approval' && (proposal.completed_at || proposal.rejected_reason || proposal.error_message || proposal.execution_result || proposal.approved_by_email);
  return (
    <div className={'proposal-detail' + (dense ? ' proposal-detail-dense' : '')}>
      <div className="proposal-detail-eyebrow">
        <span className={'proposal-sev-pill proposal-sev-' + (proposal.severity || 'medium')}>{(proposal.severity || 'medium').toUpperCase()}</span>
        {proposal.control_id && <span className="proposal-control-pill">{proposal.control_id}</span>}
        <span className="proposal-auto-pill">{proposal.automation || 'manual'}</span>
        {proposal.trigger && <span className="proposal-trigger-pill" title="What surfaced this proposal">{proposal.trigger}</span>}
        {resolutionStatusPill}
        <span className="proposal-when-pill">{new Date(proposal.requested_at).toLocaleString()}</span>
      </div>
      <div className="proposal-detail-title">{proposal.title}</div>
      {proposal.rationale && <div className="proposal-detail-rationale">"{proposal.rationale}"</div>}
      {proposal.description && <div className="proposal-detail-description">{proposal.description}</div>}

      {hasTargets && (
        <ExpandableSection title="Targets" badge={preview && preview.total != null ? preview.total : targets.length} defaultOpen={true}>
          <ProposalTargetsTable targets={targets} context={preview && preview.context} />
        </ExpandableSection>
      )}
      {!hasTargets && proposal.target_count > 0 && (
        <div className="proposal-detail-no-preview">
          {proposal.target_count} target(s) — preview not available (proposal predates target enrichment, or bundle could not be built at propose time). Approval will resolve targets at execution time.
        </div>
      )}

      {hasOps && (
        <ExpandableSection title="Operations" badge={proposal.operations.length} defaultOpen={false}>
          <ProposalOperationsList operations={proposal.operations} />
        </ExpandableSection>
      )}

      {hasBlast && (
        <ExpandableSection title="Blast radius & rollback" defaultOpen={false}>
          <ProposalBlastRadiusPanel blast={proposal.blast_radius} />
        </ExpandableSection>
      )}

      {hasResolution && (
        <ExpandableSection title="Resolution" defaultOpen={false}>
          <div className="proposal-resolution">
            {proposal.approved_at && (
              <div className="proposal-resolution-row">
                <span className="proposal-resolution-label">Approved</span>
                <span className="proposal-resolution-value">{new Date(proposal.approved_at).toLocaleString()}{proposal.approved_by_email ? ' · by ' + proposal.approved_by_email : ''}</span>
              </div>
            )}
            {proposal.completed_at && (
              <div className="proposal-resolution-row">
                <span className="proposal-resolution-label">Completed</span>
                <span className="proposal-resolution-value">{new Date(proposal.completed_at).toLocaleString()}</span>
              </div>
            )}
            {proposal.rejected_reason && (
              <div className="proposal-resolution-row">
                <span className="proposal-resolution-label">Rejection reason</span>
                <span className="proposal-resolution-value">"{proposal.rejected_reason}"</span>
              </div>
            )}
            {proposal.error_message && (
              <div className="proposal-resolution-row proposal-resolution-error">
                <span className="proposal-resolution-label">Error</span>
                <span className="proposal-resolution-value">{proposal.error_message}</span>
              </div>
            )}
            {proposal.execution_result && (
              <div className="proposal-resolution-row">
                <span className="proposal-resolution-label">Execution result</span>
                <span className="proposal-resolution-value">
                  {proposal.execution_result.executed && (
                    <span>{proposal.execution_result.executed.length || 0} executed</span>
                  )}
                  {proposal.execution_result.failed && proposal.execution_result.failed.length > 0 && (
                    <span style={{ color: '#ef4444', marginLeft: 8 }}>{proposal.execution_result.failed.length} failed</span>
                  )}
                  {proposal.execution_result.message && <span style={{ marginLeft: 8 }}>· {proposal.execution_result.message}</span>}
                </span>
              </div>
            )}
          </div>
        </ExpandableSection>
      )}

      {hasRefs && (
        <div className="proposal-detail-refs">
          <div className="proposal-detail-refs-label">References</div>
          {proposal.references.map(function(ref, i) {
            return (
              <a key={i} href={ref.url} target="_blank" rel="noopener noreferrer" className="proposal-detail-ref">
                {ref.label} ↗
              </a>
            );
          })}
        </div>
      )}

      {actionable && admin && (onApprove || onReject) && (
        <div className="proposal-detail-actions">
          {onApprove && <button type="button" className="admin-btn primary" disabled={busy} onClick={function() { onApprove(proposal); }}>{busy ? '…' : 'Approve & execute'}</button>}
          {onReject && <button type="button" className="admin-btn" disabled={busy} onClick={function() { onReject(proposal); }}>Reject</button>}
        </div>
      )}
    </div>
  );
}

// ── ProposalsDrawer ────────────────────────────────────────────────
// Slide-out from the right (same vocabulary as ChangelogPanel) listing
// AI proposals for the active client. Triggered by the TopBar badge
// when there are pending proposals. Approve/Reject inline — same
// handler logic as CMMCProposalsCard, just rehoused so the operator
// doesn't tab-hop after triggering a propose action elsewhere.
//
// When the drawer is open + visible, refetches every 30s to catch
// auto-drift proposals that landed since open.
function ProposalsDrawer({ isOpen, onClose, clientId, clientName, onChange }) {
  var [proposals, setProposals] = useState([]);
  var [loading, setLoading] = useState(true);
  var [err, setErr] = useState(null);
  var [busyId, setBusyId] = useState(null);
  var [toast, setToast] = useState(null);
  var admin = isAdminRole();

  function fetchProposals() {
    if (!clientId) return;
    setLoading(true); setErr(null);
    authFetch(API + '/clients/' + clientId + '/cmmc/proposals')
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setLoading(false);
        if (x.ok) setProposals(x.d.proposals || []);
        else setErr(x.d.error || 'Failed to load proposals');
      })
      .catch(function() { setLoading(false); setErr('Failed to load proposals'); });
  }

  useEffect(function() {
    if (!isOpen) return;
    fetchProposals();
    var iv = setInterval(fetchProposals, 30000);
    var handler = function(e) { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return function() { clearInterval(iv); window.removeEventListener('keydown', handler); };
  }, [isOpen, clientId]);  // eslint-disable-line react-hooks/exhaustive-deps

  function approve(proposal) {
    if (!window.confirm('Approve "' + proposal.title + '"? This will execute the playbook against ' + (proposal.target_count || 'auto-resolved') + ' target(s) immediately.')) return;
    setBusyId(proposal.id);
    authFetch(API + '/cmmc/proposals/' + proposal.id + '/approve', { method: 'POST' })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusyId(null);
        if (x.ok) {
          var execMode = x.d.mode || 'completed';
          var summary = execMode === 'executed'
            ? ('Executed: ' + (x.d.executed ? x.d.executed.length : 0) + ' affected, ' + (x.d.failed ? x.d.failed.length : 0) + ' failed')
            : (x.d.message || 'Approved');
          setToast({ tone: x.d.failed && x.d.failed.length > 0 ? 'warn' : 'success', text: summary });
        } else {
          setToast({ tone: 'error', text: x.d.error || 'Approval failed' });
        }
        fetchProposals();
        if (onChange) onChange();
        setTimeout(function() { setToast(null); }, 8000);
      })
      .catch(function() { setBusyId(null); setToast({ tone: 'error', text: 'Approval request failed' }); });
  }
  function reject(proposal) {
    var reason = window.prompt('Reject reason (optional):');
    if (reason === null) return;
    setBusyId(proposal.id);
    authFetch(API + '/cmmc/proposals/' + proposal.id + '/reject', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: reason })
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusyId(null);
        setToast({ tone: x.ok ? 'success' : 'error', text: x.ok ? 'Rejected.' : (x.d.error || 'Reject failed') });
        fetchProposals();
        if (onChange) onChange();
        setTimeout(function() { setToast(null); }, 4000);
      })
      .catch(function() { setBusyId(null); setToast({ tone: 'error', text: 'Reject request failed' }); });
  }

  if (!isOpen) return null;
  return (
    <>
      <div className="changelog-overlay" onClick={onClose} />
      <div className="proposals-drawer">
        <div className="proposals-drawer-header">
          <div>
            <div className="proposals-drawer-eyebrow">AI Proposals · awaiting approval</div>
            <div className="proposals-drawer-title">{clientName || clientId}</div>
            <div className="proposals-drawer-subtitle">{loading ? 'Loading…' : (proposals.length + ' pending')}</div>
          </div>
          <button className="changelog-close" onClick={onClose} title="Close (Esc)">✕</button>
        </div>
        <div className="proposals-drawer-body">
          {err && <div className="admin-banner error" style={{ fontSize: 12 }}>{err}</div>}
          {toast && <div className={'admin-banner ' + (toast.tone === 'success' ? 'success' : toast.tone === 'warn' ? 'warn' : 'error')} style={{ fontSize: 12 }}>{toast.text}</div>}
          {!loading && !err && proposals.length === 0 && (
            <div className="proposals-drawer-empty">
              <div className="proposals-drawer-empty-glyph">◇</div>
              <div className="proposals-drawer-empty-title">No proposals pending</div>
              <div className="proposals-drawer-empty-sub">When the continuous evaluator detects drift into a failing state — or you click "Propose remediation" on the Identity / Compliance tab — pending proposals show up here.</div>
            </div>
          )}
          <div className="proposals-drawer-list">
            {proposals.map(function(p) {
              return (
                <div key={p.id} className={'proposal-card proposal-card-sev-' + (p.severity || 'medium')}>
                  <ProposalDetailPanel
                    proposal={p}
                    admin={admin}
                    busy={busyId === p.id}
                    onApprove={function() { approve(p); }}
                    onReject={function() { reject(p); }}
                    dense={true}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}

// ── MspVerifyDrawer (S1.13) ─────────────────────────────────────────
// Global slide-out drawer that embeds the MSP Process Technician
// Verification widget. Accessible from the top-header shield icon on
// every Aurelius page regardless of which client/engagement is active.
// The widget is operator-tenant-level (one CBG token covers every end
// user CBG has configured in MSP Process), so the drawer needs no
// per-client context and works for pre-onboarded clients.
function MspVerifyDrawer({ isOpen, onClose }) {
  var [config, setConfig] = useState(null);
  var [loadError, setLoadError] = useState(null);
  var openedAuditRef = useRef(false);

  useEffect(function() {
    if (!isOpen) { openedAuditRef.current = false; return; }
    // Load config (cached after first open)
    if (!config) {
      authFetch(API + '/config/msp-verify')
        .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
        .then(function(x) {
          if (x.ok) setConfig(x.d);
          else setLoadError(x.d.error || 'Failed to load configuration');
        })
        .catch(function(err) { setLoadError('Request failed: ' + (err.message || 'unknown')); });
    }
    // Audit the open once per drawer-open lifecycle
    if (!openedAuditRef.current) {
      openedAuditRef.current = true;
      authFetch(API + '/audit/msp-verify-opened', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ context: 'header_drawer' })
      }).catch(function() { /* audit is best-effort */ });
    }
  }, [isOpen]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  return (
    <div onClick={onClose} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9998, display: 'flex', justifyContent: 'flex-end' }}>
      <div onClick={function(ev) { ev.stopPropagation(); }} style={{ width: '100%', maxWidth: 560, background: '#0b1220', borderLeft: '1px solid rgba(91,158,244,0.3)', height: '100vh', display: 'flex', flexDirection: 'column', boxShadow: '-8px 0 32px rgba(0,0,0,0.4)' }}>
        <div style={{ background: '#0b1220', padding: '18px 22px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: 10, flex: 'none' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.6px', color: '#5b9ef4', marginBottom: 2 }}>SUPPORT CALL VERIFICATION</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>Confirm the technician's identity</div>
          </div>
          <button type="button" onClick={onClose} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#cbd5e1', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '4px 10px', borderRadius: 4 }}>×</button>
        </div>

        {!config && !loadError && (
          <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Loading widget configuration…</div>
        )}

        {loadError && (
          <div style={{ padding: 24, textAlign: 'center', color: '#f87171', fontSize: 13 }}>
            <div style={{ marginBottom: 8 }}>{loadError}</div>
            <div style={{ color: '#94a3b8', fontSize: 11 }}>Admin: confirm your session is still active and try again.</div>
          </div>
        )}

        {config && !config.configured && (
          <div style={{ padding: 24, fontSize: 13, color: '#cbd5e1', lineHeight: 1.6 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#fbbf24', marginBottom: 6 }}>NOT CONFIGURED</div>
            <p>The MSP Process widget URL is not set on this Aurelius instance. To enable User Verification, an admin must set the <code style={{ color: '#a78bfa' }}>MSP_VERIFY_WIDGET_URL</code> environment variable in Azure App Service config — paste the iframe URL from your MSP Process portal (Settings → Widgets → Tech Verification).</p>
            <p style={{ marginTop: 12 }}>The token in the URL is operator-tenant-level (one token per MSP using Aurelius). It works for any end user that operator has configured in their MSP Process portal, regardless of whether the user's organization is onboarded to Aurelius.</p>
            <p style={{ marginTop: 12 }}>
              <a href={config.docs_url} target="_blank" rel="noopener noreferrer" style={{ color: '#5b9ef4', textDecoration: 'none' }}>MSP Process deployment guide ↗</a>
            </p>
          </div>
        )}

        {config && config.configured && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <iframe
              title="Technician identity verification"
              src={config.widget_url}
              sandbox="allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-same-origin"
              allow="clipboard-write"
              style={{ flex: 1, width: '100%', border: 0, background: '#fff' }}
            />
            <div style={{ padding: '8px 14px', background: 'rgba(255,255,255,0.02)', borderTop: '1px solid rgba(255,255,255,0.06)', fontSize: 10, color: '#64748b', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Identity verification widget · Every open is logged to the audit trail</span>
              <a href={config.docs_url} target="_blank" rel="noopener noreferrer" style={{ color: '#5b9ef4', textDecoration: 'none' }}>Docs ↗</a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ChangelogPanel({ isOpen, onClose }) {
  var [releases, setReleases] = useState([]);
  var [loading, setLoading] = useState(true);
  useEffect(function() {
    if (!isOpen) return;
    var handler = function(e) { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return function() { window.removeEventListener('keydown', handler); };
  }, [isOpen, onClose]);
  useEffect(function() {
    if (!isOpen) return;
    authFetch(API + '/changelog').then(function(r) { return r.json(); }).then(function(data) {
      setReleases(data); setLoading(false);
      sessionStorage.setItem('aurelius_changelog_seen', data[0] ? data[0].version : '');
    }).catch(function() { setLoading(false); });
  }, [isOpen]);
  if (!isOpen) return null;
  var typeIcons = { feature: '✦', improvement: '↑', security: '◆', fix: '●' };
  var typeColors = { feature: 'var(--accent-blue, #5b9ef4)', improvement: '#22c55e', security: '#a78bfa', fix: '#f59e0b' };
  var typeLabels = { feature: 'New', improvement: 'Improved', security: 'Security', fix: 'Fix' };
  return (
    <>
      <div className="changelog-overlay" onClick={onClose} />
      <div className="changelog-panel">
        <div className="changelog-header">
          <div>
            <div className="changelog-title">What's New</div>
            <div className="changelog-subtitle">Release notes & updates</div>
          </div>
          <button className="changelog-close" onClick={onClose}>✕</button>
        </div>
        <div className="changelog-body">
          {loading ? <div className="changelog-loading">Loading...</div> :
            releases.map(function(release, ri) {
              return (
                <div key={ri} className="changelog-release">
                  <div className="changelog-release-meta">
                    <span className="changelog-version">v{release.version}</span>
                    <span className="changelog-date">{release.date}</span>
                  </div>
                  <div className="changelog-release-title">{release.title}</div>
                  <div className="changelog-entries">
                    {release.entries.map(function(entry, ei) {
                      return (
                        <div key={ei} className="changelog-entry">
                          <span className="changelog-entry-type" style={{ color: typeColors[entry.type] || '#8b9dc3', background: (typeColors[entry.type] || '#8b9dc3') + '18' }}>{typeLabels[entry.type] || entry.type}</span>
                          <span className="changelog-entry-text">{entry.text}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          }
        </div>
      </div>
    </>
  );
}

// ── Shared Table Utilities ───────────────────────────────────────────────────

function timeAgo(dateStr) {
  if (!dateStr) return '—';
  var diff = Date.now() - new Date(dateStr).getTime();
  var mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return mins + 'm ago';
  var hrs = Math.floor(mins / 60);
  if (hrs < 24) return hrs + 'h ago';
  var days = Math.floor(hrs / 24);
  if (days < 30) return days + 'd ago';
  var months = Math.floor(days / 30);
  return months + 'mo ago';
}

function sortRows(rows, field, dir, accessors) {
  if (!field || !accessors[field]) return rows;
  return rows.slice().sort(function(a, b) {
    var va = accessors[field](a);
    var vb = accessors[field](b);
    if (va == null || va === '') return 1;
    if (vb == null || vb === '') return -1;
    if (typeof va === 'number' && typeof vb === 'number') return dir === 'asc' ? va - vb : vb - va;
    if (typeof va === 'boolean') { va = va ? 1 : 0; vb = vb ? 1 : 0; return dir === 'asc' ? va - vb : vb - va; }
    va = String(va).toLowerCase(); vb = String(vb).toLowerCase();
    return dir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
  });
}

function SortTh({ field, current, dir, onSort, children }) {
  var active = current === field;
  return (
    <th onClick={function() { onSort(field); }}>
      {children}
      <span className={'sort-arrow' + (active ? ' active' : '')}>{active ? (dir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}</span>
    </th>
  );
}

function useTableSort(defaultField, defaultDir) {
  var _s = useState(defaultField);
  var _d = useState(defaultDir || 'asc');
  function toggle(field) {
    if (_s[0] === field) _d[1](_d[0] === 'asc' ? 'desc' : 'asc');
    else { _s[1](field); _d[1]('asc'); }
  }
  return { sortField: _s[0], sortDir: _d[0], toggleSort: toggle };
}

function TableSearch({ value, onChange, count, total, noun, onExport }) {
  return (
    <div className="table-toolbar">
      <div className="table-search-wrap">
        <span className="table-search-icon">⌕</span>
        <input className="table-search" placeholder={'Search ' + (noun || 'rows') + '... (Esc to clear)'} value={value} onChange={function(e) { onChange(e.target.value); }} onKeyDown={function(e) { if (e.key === 'Escape') { onChange(''); e.target.blur(); } }} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {total > 0 && <span className="table-row-count">{count === total ? total + ' ' + (noun || 'rows') : count + ' of ' + total}</span>}
        {onExport && <button className="export-csv-btn" onClick={onExport} title="Export to CSV">⬇ CSV</button>}
      </div>
    </div>
  );
}

function filterRows(rows, query, fields) {
  if (!query || !query.trim()) return rows;
  var q = query.toLowerCase().trim();
  return rows.filter(function(row) {
    return fields.some(function(fn) {
      var val = fn(row);
      return val != null && String(val).toLowerCase().indexOf(q) !== -1;
    });
  });
}

function applyFilters(rows, active, accessors) {
  var keys = Object.keys(active);
  return rows.filter(function(row) {
    return keys.every(function(k) {
      if (active[k] == null) return true;
      var fn = accessors[k];
      if (!fn) return true;
      return String(fn(row)).toLowerCase() === String(active[k]).toLowerCase();
    });
  });
}

function TableFilters({ filters, active, onChange }) {
  var hasActive = Object.keys(active).some(function(k) { return active[k] != null; });
  return (
    <div className="table-filters">
      {filters.map(function(f) {
        return (
          <div key={f.key} className="table-filter-group">
            <span className="table-filter-label">{f.label}</span>
            {f.options.map(function(opt) {
              var isActive = active[f.key] === opt.value;
              return (
                <button key={opt.value} className={'table-filter-pill' + (isActive ? ' table-filter-active' : '')}
                  style={isActive && opt.color ? { borderColor: opt.color, color: opt.color, background: opt.color + '18' } : undefined}
                  onClick={function() { var next = Object.assign({}, active); next[f.key] = isActive ? null : opt.value; onChange(next); }}>
                  {opt.color && <span className="table-filter-dot" style={{ background: opt.color }} />}
                  {opt.label}
                  {opt.count != null && <span className="table-filter-count">{opt.count}</span>}
                </button>
              );
            })}
          </div>
        );
      })}
      {hasActive && <button className="table-filter-clear" onClick={function() { onChange({}); }}>✕ Clear</button>}
    </div>
  );
}

function Pagination({ page, totalPages, onPageChange, totalItems, startIndex, endIndex }) {
  if (totalPages <= 1) return null;
  return (
    <div className="table-pagination">
      <span className="table-pagination-info">Showing {startIndex + 1}–{endIndex} of {totalItems}</span>
      <div className="table-pagination-controls">
        <button className="table-pagination-btn" disabled={page === 0} onClick={function() { onPageChange(0); }}>«</button>
        <button className="table-pagination-btn" disabled={page === 0} onClick={function() { onPageChange(page - 1); }}>‹</button>
        <span className="table-pagination-current">{page + 1} / {totalPages}</span>
        <button className="table-pagination-btn" disabled={page >= totalPages - 1} onClick={function() { onPageChange(page + 1); }}>›</button>
        <button className="table-pagination-btn" disabled={page >= totalPages - 1} onClick={function() { onPageChange(totalPages - 1); }}>»</button>
      </div>
    </div>
  );
}

function TabDescription({ text }) {
  return <div className="tab-description">{text}</div>;
}

function HealthVerdict({ score, context }) {
  // score === null/undefined means "no data to score against" (e.g. no
  // integrations configured for this domain). Render a neutral
  // "informational" banner instead of a red Critical — alarmism on absence
  // of data is worse than helpful.
  var level;
  if (score == null) level = 'unconfigured';
  else if (score >= 90) level = 'excellent';
  else if (score >= 75) level = 'good';
  else if (score >= 50) level = 'attention';
  else level = 'critical';
  var labels = { excellent: 'Excellent', good: 'Good', attention: 'Needs Attention', critical: 'Critical', unconfigured: 'Not Yet Configured' };
  return (
    <div className={'health-verdict health-verdict-' + level}>
      <span className="health-verdict-dot" />
      <span className="health-verdict-label">{labels[level]}</span>
      <span className="health-verdict-context">{context}</span>
    </div>
  );
}

function DataFreshness({ live }) {
  return (
    <div className="data-freshness">
      {live && <span className="data-freshness-dot" />}
      {live ? 'Live data · Auto-refreshing every 60s' : 'Historical data · Updated daily at 11:00 AM ET'}
    </div>
  );
}

function SkeletonLoader() {
  return (
    <div className="skeleton-wrap">
      <div className="skeleton-line w60 h24" />
      <div className="skeleton-line w40" />
      <div className="skeleton-kpi-row"><div className="skeleton-kpi" /><div className="skeleton-kpi" /><div className="skeleton-kpi" /><div className="skeleton-kpi" /></div>
      <div className="skeleton-grid"><div className="skeleton-card" /><div className="skeleton-card" /><div className="skeleton-card" /><div className="skeleton-card" /><div className="skeleton-card" /><div className="skeleton-card" /></div>
      <div className="skeleton-line w80" style={{ marginTop: 20 }} />
      <div className="skeleton-line w100 h40" />
    </div>
  );
}

function Breadcrumb({ items }) {
  return (
    <div className="breadcrumb">
      {items.map(function(item, i) {
        var isLast = i === items.length - 1;
        return isLast
          ? <span key={i} className="breadcrumb-current">{item.label}</span>
          : <><button key={i} className="breadcrumb-link" onClick={item.onClick}>{item.label}</button><span className="breadcrumb-sep">›</span></>;
      })}
    </div>
  );
}

function EmptyStateAction({ icon, title, text, cta }) {
  return (
    <div className="empty-state-action">
      {icon && <div className="empty-state-icon">{icon}</div>}
      <div className="empty-state-title">{title}</div>
      <div className="empty-state-text">{text}</div>
      {cta && <div className="empty-state-cta">{cta}</div>}
    </div>
  );
}

// ── Integration registry (Phase B.6 e part 2) ─────────────────────
// One entry per integration Aurelius surfaces. Drives:
//   - per-tab empty states ("X is not connected — connect it via …")
//   - the integrations health summary at the top of Edit Client modal
//   - the "what does Aurelius read for this client" disclosure
//
// The required_field is the property on clientConfig that flips this
// integration from "not_started" to "configured." The vendor field is
// the human-readable label; description explains what data unlocks.
//
// editAnchor names the section in the Edit Client modal so future deep-
// linking from an empty state can jump directly to it. Today the CTA
// just routes admins to /admin → Integrations → Edit; deep-linking is
// reserved for a follow-on commit.
// applies_to controls per-tier visibility:
//   'all'        — shown on every client (most integrations)
//   'commercial' — only commercial-tier clients (Huntress, Blumira)
//   'cmmc'       — only CMMC-tier clients (Sentinel)
//
// CMMC clients don't see commercial-only integrations on the Setup
// tab or in the Edit Client integration health summary. They use
// different vendors handled by Atomus (EDR) or wired through Atomus-
// approved Microsoft tooling (Sentinel for SIEM).
//
// NinjaOne is "all" because BOTH tiers are migrating onto NinjaOne
// FedRAMP — the commercial fallback is legacy. The endpoints status
// detection treats fedramp_org_name as primary; ninjaone_org_id is
// treated as a "still on legacy commercial" signal that surfaces a
// migration prompt in the Setup tab description.
var INTEGRATION_DEFINITIONS = [
  { kind: 'endpoints',         vendor: 'NinjaOne (FedRAMP/Gov)', icon: '🖥', required_field: 'fedramp_org_name',
    fallback_field: 'ninjaone_org_id', // legacy commercial — flagged as a migration target
    description: 'RMM telemetry — device health, antivirus status, patch level, ticket activity. NinjaOne FedRAMP/Gov is the migration target for ALL clients regardless of CMMC status; commercial NinjaOne is being deprecated. Powers the Posture and Tickets tabs.',
    editAnchor: 'ninjaone',
    applies_to: 'all' },
  { kind: 'edr',               vendor: 'Huntress',           icon: '🛡', required_field: 'huntress_org_id',
    description: 'Managed EDR — endpoint threat detection and response, agent count, active incidents. Powers the Threat score. Commercial-tier only — CMMC clients use Atomus-managed EDR instead.',
    editAnchor: 'huntress',
    applies_to: 'commercial' },
  { kind: 'identity',          vendor: 'Microsoft Entra',    icon: '🔐', required_field: 'tenant_id',
    description: 'Identity posture — users, devices, conditional access, MFA, sign-in risk. Read-only; remediation flows through NinjaOne.',
    editAnchor: 'entra',
    applies_to: 'all',
    setup_path: 'connect-entra' /* a hint that admins should use the Connect-Entra panel inside the modal */ },
  { kind: 'siem-commercial',   vendor: 'Blumira',            icon: '📡', required_field: 'blumira_account_id',
    description: 'Commercial-tier SIEM — security event findings, threat-type breakdown, response cadence.',
    cmmc_alternative: 'siem-cmmc',
    editAnchor: 'blumira',
    applies_to: 'commercial' },
  { kind: 'siem-cmmc',         vendor: 'Microsoft Sentinel', icon: '📡', required_field: 'sentinel_workspace_id',
    description: 'CMMC-tier SIEM (Log Analytics workspace). Wired through Atomus approval. Replaces Blumira for CMMC clients.',
    editAnchor: 'sentinel',
    applies_to: 'cmmc' },
  { kind: 'network',           vendor: 'UniFi',              icon: '📶', required_field: 'unifi_host_ids',
    is_array: true,
    description: 'Network device monitoring — gateways, switches, access points, cameras across all sites. Health, firmware, uptime.',
    editAnchor: 'unifi',
    applies_to: 'all' },
  { kind: 'projects',          vendor: 'Asana',              icon: '📋', required_field: 'asana_project_gids',
    is_array: true,
    description: 'Active engagement tracking — task progress, milestones, overdue items by assignee.',
    editAnchor: 'asana',
    applies_to: 'all' },
  { kind: 'devops',            vendor: 'Jira',               icon: '⚙', required_field: 'jira_domain',
    description: 'DevOps issue tracking — sprint health, priority distribution, team workload, overdue items.',
    editAnchor: 'jira',
    applies_to: 'all' },
  { kind: 'cloud-cost',        vendor: 'Azure subscription', icon: '☁', required_field: 'azure_subscription_id',
    description: 'Azure resource cost + VM monitoring. Subscription-scoped RBAC; separate from Entra.',
    editAnchor: 'azure',
    applies_to: 'all' },
  { kind: 'billing',           vendor: 'Pax8',               icon: '💰', required_field: 'pax8_company_id',
    description: 'Vendor billing — Microsoft 365 + Azure + Atlassian spend, per-user cost, MoM trends.',
    editAnchor: 'pax8',
    applies_to: 'all' }
];

// Centralized "open admin Integrations + edit this client" navigation.
// Used by every Connect button across the SetupTab + IntegrationEmptyState.
//
// The ealier hash-routing approach (window.location.hash = '#admin/...')
// was a no-op because admin mode is in-app React state, not URL-routed.
// This dispatches a CustomEvent that App's listener picks up and flips
// adminMode + the integrations sub-tab. Falls back to a direct URL
// change if the listener isn't installed for some reason.
function gotoAdminIntegrationsForClient(clientId, integrationDef) {
  if (!clientId) return;
  // Stash intent so AdminIntegrationsTab auto-opens the Edit modal on
  // mount (existing pattern) — works regardless of how we get there.
  try {
    sessionStorage.setItem('aurelius_admin_integrations_intent', JSON.stringify({
      clientId: clientId,
      editAnchor: integrationDef ? integrationDef.editAnchor : null,
      vendor: integrationDef ? integrationDef.vendor : null,
      ts: Date.now()
    }));
  } catch (e) { /* sessionStorage may be disabled */ }
  // Dispatch the event App listens for. If nobody listens (e.g. the
  // user is on a client portal URL that doesn't host AdminPanel),
  // fall back to a hard navigate.
  var evt;
  try {
    evt = new CustomEvent('aurelius:goto-admin-integrations', { detail: { clientId: clientId } });
    window.dispatchEvent(evt);
  } catch (e) { /* IE etc. — degrade to URL */ }
  // Belt-and-suspenders: if the App listener didn't react within a
  // tick (e.g. on a client-only route), navigate to the admin URL.
  setTimeout(function() {
    if (window.__aurelius_admin_handled_intent === true) {
      window.__aurelius_admin_handled_intent = false;
      return;
    }
    // We're not in a context that can flip adminMode — hard-navigate
    // to / (the dashboard root) where adminMode lives.
    if (window.location.pathname !== '/') window.location.href = '/';
  }, 50);
}

// Returns true if this integration applies to the given client based
// on tier. Default ('all' or undefined) means yes.
function isIntegrationApplicable(def, c) {
  if (!def || !def.applies_to || def.applies_to === 'all') return true;
  var isCmmc = !!(c && c.cmmc);
  if (def.applies_to === 'cmmc') return isCmmc;
  if (def.applies_to === 'commercial') return !isCmmc;
  if (typeof def.applies_to === 'function') return def.applies_to(c);
  return true;
}

// Returns the subset of INTEGRATION_DEFINITIONS applicable to this
// client. CMMC clients see no Huntress/Blumira; commercial clients
// see no Sentinel.
function getApplicableIntegrations(c) {
  return INTEGRATION_DEFINITIONS.filter(function(def) { return isIntegrationApplicable(def, c); });
}

// Detects the "still on commercial NinjaOne" migration state.
// True when the legacy field is set but the FedRAMP target isn't.
// Drives the migration prompt in the Setup tab.
function isOnLegacyNinjaOne(c) {
  if (!c) return false;
  var hasFed = !!c.fedramp_org_name;
  var hasCommercial = c.ninjaone_org_id != null && c.ninjaone_org_id !== '';
  return hasCommercial && !hasFed;
}

// Helper — given a client's config object, return the connection state
// for one integration kind. Considers fallback fields (FedRAMP for
// NinjaOne) and array fields (UniFi host IDs).
function getIntegrationStatus(c, kind) {
  var def = INTEGRATION_DEFINITIONS.find(function(d) { return d.kind === kind; });
  if (!def || !c) return { kind: kind, status: 'unknown' };
  function hasValue(field) {
    var v = c[field];
    if (v == null || v === '') return false;
    if (def.is_array) return Array.isArray(v) && v.length > 0;
    return true;
  }
  var configured = hasValue(def.required_field) || (def.fallback_field && hasValue(def.fallback_field));
  // Identity has richer SSO-flow states from the new consent flow,
  // but those only apply when SSO is enabled. Legacy clients with
  // tenant_id + graph_client_id wired (Graph data reads working) but
  // no SSO toggle should report 'configured', not 'pending_consent' —
  // the consent flow is for portal sign-in, not Graph reads.
  if (kind === 'identity' && configured && c.sso_enabled) {
    if (c.entra_last_verified_at) return { kind: kind, status: 'connected', def: def, last_verified_at: c.entra_last_verified_at, tenant_name: c.entra_tenant_name };
    if (c.entra_consent_granted_at) return { kind: kind, status: 'consent_granted', def: def };
    return { kind: kind, status: 'pending_consent', def: def };
  }
  return { kind: kind, status: configured ? 'configured' : 'not_started', def: def };
}

// SetupTab (Phase B.6 e part 3) ─────────────────────────────────────
// Dedicated workspace setup view for admins. Walks through the
// canonical onboarding sequence in priority order. Lives at activeTab
// === 'setup' (filtered out of NavRail for client viewers + demo).
//
// Order is opinionated: Identity → Endpoints → EDR/SIEM → Network →
// Projects/DevOps/Billing. CMMC-tier swaps are handled inline via the
// integration definitions' cmmc_alternative metadata.
//
// One CTA per row. Clicking deep-links into admin Integrations with the
// same intent payload IntegrationEmptyState uses.
//
// Auto-hide is gone (tab itself is the affordance — admins navigate to
// it when they want it; nothing pulls them in unless we explicitly
// route brand-new clients to it on first load).
// ══════════════════════════════════════════════════════════════════
// ── TenantOnboardingWizard ─────────────────────────────────────────
// Full-screen guided flow for new tenants. Sequences the steps that
// were previously scattered across admin Integrations → Setup tab →
// Proposals bell → external PowerShell shell. End-to-end:
//
//   1. Welcome           — tenant snapshot + what the wizard will do
//   2. Connectivity      — verify Graph cert + licensing probe
//   3. Baseline          — evaluate the 12-item canonical baseline
//   4. Deploy            — one fix per screen with copy-ready PS1
//                          + Mark complete / Skip / Defer
//   5. Review            — re-evaluate, show before/after
//   6. Summary           — what was done, downloadable + printable
//
// State persists in localStorage keyed by clientId so a partial
// run survives a tab close + come-back-later. Server-side session
// persistence is a future-work item — for v1, single-operator
// continuity is enough.
function TenantOnboardingWizard({ clientId, clientName, onClose, onProposalCreated }) {
  var STORE_KEY = 'aurelius_onboarding_' + clientId;
  var STEPS = ['welcome', 'connectivity', 'baseline', 'deploy', 'review', 'summary'];

  function loadInitial() {
    try {
      var raw = localStorage.getItem(STORE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      // 24-hour TTL — stale wizards from a previous session shouldn't reopen mid-flow
      if (Date.now() - (parsed.ts || 0) > 24 * 60 * 60 * 1000) return null;
      return parsed;
    } catch (e) { return null; }
  }
  var initial = loadInitial();

  var [step, setStep] = useState(initial && initial.step ? initial.step : 'welcome');
  var [completedItems, setCompletedItems] = useState((initial && initial.completedItems) || {});
  var [skippedItems, setSkippedItems]   = useState((initial && initial.skippedItems) || {});
  var [verifyResult, setVerifyResult]   = useState((initial && initial.verifyResult) || null);
  var [baselineData, setBaselineData]   = useState((initial && initial.baselineData) || null);
  var [baselineLoading, setBaselineLoading] = useState(false);
  var [verifyLoading, setVerifyLoading] = useState(false);
  var [deployIndex, setDeployIndex]     = useState((initial && initial.deployIndex) || 0);
  var [initialBaselineSnapshot, setInitialBaselineSnapshot] = useState((initial && initial.initialBaselineSnapshot) || null);
  // Session start timestamp — set when operator first leaves the welcome
  // screen. Drives the onboarding-report endpoint's automation_executions
  // + audit_log filtering so multiple sessions per client don't bleed.
  var [sessionStartedAt, setSessionStartedAt] = useState((initial && initial.sessionStartedAt) || null);
  var [toast, setToast] = useState(null);

  // Persist state
  useEffect(function() {
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify({
        step: step, completedItems: completedItems, skippedItems: skippedItems,
        verifyResult: verifyResult, baselineData: baselineData,
        deployIndex: deployIndex, initialBaselineSnapshot: initialBaselineSnapshot,
        sessionStartedAt: sessionStartedAt,
        ts: Date.now()
      }));
    } catch (e) { /* localStorage may be full or unavailable; non-fatal */ }
  }, [step, completedItems, skippedItems, verifyResult, baselineData, deployIndex, initialBaselineSnapshot, sessionStartedAt, STORE_KEY]);

  // Stamp the session start when the operator first leaves welcome
  useEffect(function() {
    if (step !== 'welcome' && !sessionStartedAt) setSessionStartedAt(new Date().toISOString());
  }, [step, sessionStartedAt]);

  function showToast(tone, text) { setToast({ tone: tone, text: text }); setTimeout(function() { setToast(null); }, 5000); }

  function reset() {
    localStorage.removeItem(STORE_KEY);
    setStep('welcome'); setCompletedItems({}); setSkippedItems({});
    setVerifyResult(null); setBaselineData(null); setDeployIndex(0);
    setInitialBaselineSnapshot(null); setSessionStartedAt(null);
  }

  // ── Connectivity step actions ────────────────────────────────────
  function runVerify() {
    setVerifyLoading(true);
    authFetch(API + '/admin/clients/' + clientId + '/graph-app/verify', { method: 'POST' })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setVerifyLoading(false);
        setVerifyResult(x.d);
        if (!x.ok) showToast('error', (x.d && x.d.error) || 'Verify failed');
      })
      .catch(function() { setVerifyLoading(false); showToast('error', 'Verify request failed'); });
  }

  // ── Baseline step actions ────────────────────────────────────────
  function runBaseline(isInitial) {
    setBaselineLoading(true);
    authFetch(API + '/clients/' + clientId + '/baseline')
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBaselineLoading(false);
        if (x.ok) {
          setBaselineData(x.d);
          if (isInitial && !initialBaselineSnapshot) setInitialBaselineSnapshot(x.d.summary);
        } else {
          showToast('error', (x.d && x.d.error) || 'Baseline evaluation failed');
        }
      })
      .catch(function() { setBaselineLoading(false); showToast('error', 'Baseline request failed'); });
  }

  // Auto-fetch baseline when entering baseline step (if not already loaded)
  useEffect(function() {
    if (step === 'baseline' && !baselineData && !baselineLoading) runBaseline(true);
  }, [step]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch on review entry (so before/after delta is fresh)
  useEffect(function() {
    if (step === 'review') runBaseline(false);
  }, [step]);  // eslint-disable-line react-hooks/exhaustive-deps

  // ── Step navigation ──────────────────────────────────────────────
  var stepIndex = STEPS.indexOf(step);
  function next() {
    if (stepIndex < STEPS.length - 1) setStep(STEPS[stepIndex + 1]);
  }
  function back() {
    if (stepIndex > 0) setStep(STEPS[stepIndex - 1]);
  }

  // ESC closes wizard (with confirm if mid-flow)
  useEffect(function() {
    function onKey(e) {
      if (e.key === 'Escape') {
        if (step === 'summary' || step === 'welcome') onClose();
        else if (window.confirm('Close onboarding wizard? Your progress is saved and you can resume from this client\'s Setup tab.')) onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return function() { window.removeEventListener('keydown', onKey); };
  }, [step, onClose]);

  return (
    <div className="ob-overlay" role="dialog" aria-modal="true" aria-label="Tenant onboarding wizard">
      <div className="ob-shell">
        {/* Header — sticky, always visible */}
        <div className="ob-header no-print">
          <div className="ob-header-left">
            <span className="ob-eyebrow">Tenant Onboarding</span>
            <span className="ob-title">{clientName || clientId}</span>
          </div>
          <ObStepProgress steps={STEPS} current={step} completedCount={Object.keys(completedItems).length + Object.keys(skippedItems).length} />
          <div className="ob-header-right">
            <button className="admin-btn sm" onClick={function() { if (window.confirm('Reset wizard progress? This clears any completed-mark records in your browser. (Audit log entries on the server are preserved.)')) reset(); }}>Reset</button>
            <button className="admin-btn sm" onClick={onClose} title="Close (Esc) — progress is saved">✕ Close</button>
          </div>
        </div>

        {toast && <div className={'admin-banner ' + toast.tone + ' no-print'} style={{ margin: '8px 24px', fontSize: 12 }}>{toast.text}</div>}

        <div className="ob-body">
          {step === 'welcome' && (
            <ObWelcomeStep
              clientName={clientName}
              onStart={function() { setStep('connectivity'); }}
            />
          )}
          {step === 'connectivity' && (
            <ObConnectivityStep
              clientId={clientId}
              verifyResult={verifyResult}
              verifyLoading={verifyLoading}
              onVerify={runVerify}
              onNext={next}
              onBack={back}
            />
          )}
          {step === 'baseline' && (
            <ObBaselineStep
              data={baselineData}
              loading={baselineLoading}
              onRefresh={function() { runBaseline(false); }}
              onNext={next}
              onBack={back}
            />
          )}
          {step === 'deploy' && (
            <ObDeployStep
              clientId={clientId}
              data={baselineData}
              deployIndex={deployIndex}
              setDeployIndex={setDeployIndex}
              completedItems={completedItems}
              setCompletedItems={setCompletedItems}
              skippedItems={skippedItems}
              setSkippedItems={setSkippedItems}
              onProposalCreated={onProposalCreated}
              onNext={next}
              onBack={back}
              showToast={showToast}
            />
          )}
          {step === 'review' && (
            <ObReviewStep
              data={baselineData}
              initialSnapshot={initialBaselineSnapshot}
              completedItems={completedItems}
              skippedItems={skippedItems}
              loading={baselineLoading}
              onRefresh={function() { runBaseline(false); }}
              onNext={next}
              onBack={back}
            />
          )}
          {step === 'summary' && (
            <ObSummaryStep
              clientName={clientName}
              clientId={clientId}
              data={baselineData}
              initialSnapshot={initialBaselineSnapshot}
              completedItems={completedItems}
              skippedItems={skippedItems}
              sessionStartedAt={sessionStartedAt}
              onClose={function() { reset(); onClose(); }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Onboarding step progress bar ──────────────────────────────────
function ObStepProgress({ steps, current, completedCount }) {
  var STEP_LABELS = { welcome: 'Welcome', connectivity: 'Connectivity', baseline: 'Baseline', deploy: 'Deploy', review: 'Review', summary: 'Summary' };
  var currentIdx = steps.indexOf(current);
  return (
    <div className="ob-progress">
      {steps.map(function(s, i) {
        var cls = 'ob-progress-step';
        if (i < currentIdx) cls += ' ob-progress-step-done';
        else if (i === currentIdx) cls += ' ob-progress-step-active';
        return (
          <div key={s} className={cls}>
            <div className="ob-progress-dot">{i < currentIdx ? '✓' : (i + 1)}</div>
            <div className="ob-progress-label">{STEP_LABELS[s] || s}</div>
          </div>
        );
      })}
    </div>
  );
}

// ── Step 1: Welcome ───────────────────────────────────────────────
function ObWelcomeStep({ clientName, onStart }) {
  return (
    <div className="ob-step ob-step-centered">
      <div className="ob-welcome-glyph">⚙</div>
      <div className="ob-welcome-title">Tenant onboarding for {clientName}</div>
      <div className="ob-welcome-sub">
        This wizard guides you through the canonical M365 best-practices baseline
        in one continuous flow. It assumes the per-tenant Graph app + cert are
        already wired (see Admin → Integrations → Edit if not).
      </div>
      <div className="ob-welcome-list">
        <div className="ob-welcome-item">
          <span className="ob-welcome-num">1</span>
          <div><strong>Connectivity</strong><div className="muted">Verify Graph cert + probe Entra licensing tier</div></div>
        </div>
        <div className="ob-welcome-item">
          <span className="ob-welcome-num">2</span>
          <div><strong>Baseline assessment</strong><div className="muted">Evaluate 12 canonical controls; identify gaps</div></div>
        </div>
        <div className="ob-welcome-item">
          <span className="ob-welcome-num">3</span>
          <div><strong>Deploy</strong><div className="muted">One fix per screen with copy-ready PowerShell. Mark complete, skip, or defer per item.</div></div>
        </div>
        <div className="ob-welcome-item">
          <span className="ob-welcome-num">4</span>
          <div><strong>Review + Summary</strong><div className="muted">Re-evaluate, before/after delta, printable / downloadable report.</div></div>
        </div>
      </div>
      <button className="qbr-generate-btn" style={{ padding: '12px 32px', fontSize: 14 }} onClick={onStart}>Start →</button>
    </div>
  );
}

// ── Step 2: Connectivity ──────────────────────────────────────────
function ObConnectivityStep({ clientId, verifyResult, verifyLoading, onVerify, onNext, onBack }) {
  var ok = verifyResult && verifyResult.ok;
  var licensing = verifyResult && verifyResult.licensing;
  return (
    <div className="ob-step">
      <h2 className="ob-step-title">Connectivity</h2>
      <p className="ob-step-sub">Verifies the per-tenant Graph cert auth works against this client's Entra tenant and probes the Microsoft 365 licensing tier so the wizard can grey out controls that aren't deployable on the current SKU.</p>

      <div className="ob-action-card">
        <div className="ob-action-card-body">
          <div className="ob-action-card-title">Verify Graph connection</div>
          <div className="ob-action-card-sub">Hits Microsoft Graph /organization with the per-tenant cert, resolves the tenant name + primary domain, and reads /subscribedSkus for the licensing tier.</div>
        </div>
        <button className="admin-btn primary" disabled={verifyLoading} onClick={onVerify}>
          {verifyLoading ? 'Verifying…' : (ok ? 'Re-verify' : 'Verify connection')}
        </button>
      </div>

      {verifyResult && (
        <div className={'ob-result ' + (ok ? 'ob-result-ok' : 'ob-result-err')}>
          {ok ? (
            <>
              <div className="ob-result-row"><strong>✓ Connected.</strong> {verifyResult.user_count_seen} user record(s) returned on test query.</div>
              {verifyResult.tenant_name && <div className="ob-result-row">Tenant: <strong>{verifyResult.tenant_name}</strong> · {verifyResult.tenant_domain}</div>}
              {licensing && (
                <div className="ob-result-row">
                  Licensing tier: <strong className={'ob-tier-' + (licensing.tier || 'unknown')}>{(licensing.tier || 'unknown').toUpperCase()}</strong>
                  {' · '}
                  Intune: <strong>{licensing.has_intune ? 'yes' : 'no'}</strong>
                  {' · '}
                  Defender for Endpoint: <strong>{licensing.has_defender_endpoint ? 'yes' : 'no'}</strong>
                </div>
              )}
              {licensing && licensing.tier === 'free' && (
                <div className="ob-result-warn">
                  <strong>Free tier detected.</strong> Custom Conditional Access policies, sign-in-risk policies, and Intune-managed controls require Entra ID P1+ (M365 Business Premium minimum). Those baseline controls will appear as <em>licensing-blocked</em> in the next step.
                </div>
              )}
            </>
          ) : (
            <div className="ob-result-row"><strong>✗ Verify failed:</strong> {verifyResult.error}</div>
          )}
        </div>
      )}

      <div className="ob-nav">
        <button className="admin-btn" onClick={onBack}>← Back</button>
        <button className="qbr-generate-btn" style={{ padding: '10px 24px', fontSize: 13 }} disabled={!ok} onClick={onNext}>Next: Baseline →</button>
      </div>
    </div>
  );
}

// ── Step 3: Baseline assessment ───────────────────────────────────
function ObBaselineStep({ data, loading, onRefresh, onNext, onBack }) {
  if (loading || !data) return (
    <div className="ob-step ob-step-centered">
      <div className="ob-spinner">Evaluating baseline…</div>
      <div className="muted">Running 12 controls against the live Entra bundle. Typically 10-30 seconds.</div>
    </div>
  );
  var s = data.summary;
  return (
    <div className="ob-step">
      <h2 className="ob-step-title">Baseline assessment</h2>
      <p className="ob-step-sub">12 canonical M365 best-practices controls evaluated against this tenant's current state.</p>

      <div className="metric-tiles">
        <div className="metric-tile metric-tile-good">
          <div className="metric-tile-label">Passing</div>
          <div className="metric-tile-value" style={{ color: '#22c55e' }}>{s.pass}</div>
          <div className="metric-tile-context">{s.coverage_pct}% of applicable controls</div>
        </div>
        <div className={'metric-tile' + (s.fail > 0 ? ' metric-tile-alert' : '')}>
          <div className="metric-tile-label">Need fixing</div>
          <div className="metric-tile-value" style={{ color: s.fail > 0 ? '#ef4444' : undefined }}>{s.fail}</div>
          <div className="metric-tile-context">Walk-through in the next step</div>
        </div>
        <div className="metric-tile">
          <div className="metric-tile-label">Licensing-blocked</div>
          <div className="metric-tile-value">{s.blocked_licensing}</div>
          <div className="metric-tile-context">Requires P1 / Intune / etc.</div>
        </div>
        <div className="metric-tile">
          <div className="metric-tile-label">Pending / Unknown</div>
          <div className="metric-tile-value">{s.pending_proposal + s.unknown + (s.manual_check || 0)}</div>
          <div className="metric-tile-context">Already-queued or manual review</div>
        </div>
      </div>

      <div className="ob-summary-list">
        {data.items.map(function(item) {
          var statusLabel = item.status === 'pass' ? '✓ Pass' : item.status === 'fail' ? '✗ Fail' : item.status === 'blocked_licensing' ? '🔒 Blocked' : item.status === 'pending_proposal' ? '◇ Pending' : item.status === 'manual_check' ? '✎ Manual' : '? Unknown';
          return (
            <div key={item.id} className={'ob-summary-row ob-summary-row-' + item.status}>
              <span className="ob-summary-prio">#{item.priority}</span>
              <span className="ob-summary-title">{item.title}</span>
              <span className={'ob-summary-status ob-summary-status-' + item.status}>{statusLabel}</span>
            </div>
          );
        })}
      </div>

      <div className="ob-nav">
        <button className="admin-btn" onClick={onBack}>← Back</button>
        <button className="admin-btn" onClick={onRefresh}>↻ Re-evaluate</button>
        <button className="qbr-generate-btn" style={{ padding: '10px 24px', fontSize: 13 }} disabled={s.fail === 0} onClick={onNext}>
          {s.fail === 0 ? 'No fixes needed →' : 'Next: Deploy ' + s.fail + ' fix' + (s.fail === 1 ? '' : 'es') + ' →'}
        </button>
      </div>
    </div>
  );
}

// ── Step 4: Deploy (per-item) ─────────────────────────────────────
function ObDeployStep({ clientId, data, deployIndex, setDeployIndex, completedItems, setCompletedItems, skippedItems, setSkippedItems, onProposalCreated, onNext, onBack, showToast }) {
  // Build the actionable queue — only items in 'fail' state that
  // haven't been marked complete or skipped in this session.
  var allFailing = (data && data.items) ? data.items.filter(function(i) { return i.status === 'fail'; }) : [];
  var queue = allFailing.filter(function(i) { return !completedItems[i.id] && !skippedItems[i.id]; });
  var totalForProgress = allFailing.length;
  var doneInSession = Object.keys(completedItems).length + Object.keys(skippedItems).length;

  var [runbook, setRunbook] = useState(null);
  var [runbookLoading, setRunbookLoading] = useState(false);
  var [actionBusy, setActionBusy] = useState(false);
  var [copyState, setCopyState] = useState({});

  var current = queue.length > 0 ? queue[Math.min(deployIndex, queue.length - 1)] : null;

  // Auto-fetch runbook for current item
  useEffect(function() {
    if (!current) return;
    setRunbookLoading(true); setRunbook(null);
    authFetch(API + '/clients/' + clientId + '/baseline/' + current.id + '/runbook')
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setRunbookLoading(false);
        if (x.ok) setRunbook(x.d);
        else showToast('error', (x.d && x.d.error) || 'Runbook load failed');
      })
      .catch(function() { setRunbookLoading(false); showToast('error', 'Runbook request failed'); });
  }, [current && current.id, clientId]);  // eslint-disable-line react-hooks/exhaustive-deps

  function advance() {
    if (deployIndex < queue.length - 1) setDeployIndex(deployIndex + 1);
    else if (queue.length === 1) {
      // We just acted on the last item — bump index so render shows the "all done" view
      setDeployIndex(deployIndex + 1);
    }
  }

  function markComplete(method) {
    if (!current) return;
    setActionBusy(true);
    authFetch(API + '/clients/' + clientId + '/baseline/' + current.id + '/mark-complete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ method: method })
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setActionBusy(false);
        if (x.ok) {
          var newCompleted = Object.assign({}, completedItems);
          newCompleted[current.id] = { method: method, ts: Date.now() };
          setCompletedItems(newCompleted);
          advance();
        } else {
          showToast('error', (x.d && x.d.error) || 'Mark-complete failed');
        }
      })
      .catch(function() { setActionBusy(false); showToast('error', 'Request failed'); });
  }

  function skipItem(reason) {
    if (!current) return;
    setActionBusy(true);
    authFetch(API + '/clients/' + clientId + '/baseline/' + current.id + '/mark-complete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ method: 'skipped', notes: reason })
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setActionBusy(false);
        if (x.ok) {
          var newSkipped = Object.assign({}, skippedItems);
          newSkipped[current.id] = { reason: reason, ts: Date.now() };
          setSkippedItems(newSkipped);
          advance();
        }
      })
      .catch(function() { setActionBusy(false); });
  }

  // Approval-required playbooks: create the proposal AND let admin
  // approve immediately (Aurelius executes via Graph). For simplicity
  // here, we create the proposal then deep-link to the bell.
  function proposeAndApprove() {
    if (!current) return;
    setActionBusy(true);
    authFetch(API + '/clients/' + clientId + '/baseline/propose-all', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ only: [current.id] })
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setActionBusy(false);
        if (x.ok && x.d.created && x.d.created.length > 0) {
          showToast('success', 'Proposal #' + x.d.created[0].proposal_id + ' created. Approve from the ◇ bell to execute against the tenant.');
          if (onProposalCreated) onProposalCreated();
          var newCompleted = Object.assign({}, completedItems);
          newCompleted[current.id] = { method: 'proposal_created', ts: Date.now(), proposal_id: x.d.created[0].proposal_id };
          setCompletedItems(newCompleted);
          advance();
        } else {
          showToast('error', (x.d && x.d.error) || 'Propose failed');
        }
      })
      .catch(function() { setActionBusy(false); showToast('error', 'Request failed'); });
  }

  function copyScript(stepIdx, text) {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(function() {
          var c = Object.assign({}, copyState); c[stepIdx] = Date.now();
          setCopyState(c);
          setTimeout(function() { var c2 = Object.assign({}, copyState); delete c2[stepIdx]; setCopyState(c2); }, 2000);
        });
      }
    } catch (e) { /* clipboard unavailable */ }
  }

  // ── All fixes done in this session ───────────────────────────────
  if (!current && allFailing.length > 0 && doneInSession >= allFailing.length) {
    return (
      <div className="ob-step ob-step-centered">
        <div className="ob-done-glyph">✓</div>
        <div className="ob-done-title">All {allFailing.length} fix{allFailing.length === 1 ? '' : 'es'} marked in this session</div>
        <div className="ob-done-sub">{Object.keys(completedItems).length} completed · {Object.keys(skippedItems).length} skipped/deferred</div>
        <div className="ob-nav" style={{ justifyContent: 'center' }}>
          <button className="admin-btn" onClick={onBack}>← Back to Baseline</button>
          <button className="qbr-generate-btn" style={{ padding: '10px 24px', fontSize: 13 }} onClick={onNext}>Next: Review →</button>
        </div>
      </div>
    );
  }

  if (!current) {
    return (
      <div className="ob-step ob-step-centered">
        <div className="ob-done-title">Nothing to deploy</div>
        <div className="muted">All applicable baseline controls are passing or licensing-blocked.</div>
        <div className="ob-nav" style={{ justifyContent: 'center' }}>
          <button className="qbr-generate-btn" style={{ padding: '10px 24px', fontSize: 13 }} onClick={onNext}>Next: Review →</button>
        </div>
      </div>
    );
  }

  var SEV_COLORS = { critical: '#ef4444', high: '#f59e0b', medium: '#60a5fa', low: '#a78bfa' };
  var pb = runbook && runbook.playbook;
  var isApprovalRequired = pb && pb.automation === 'approval_required';
  var isManualScript = pb && (pb.automation === 'manual_with_script' || pb.automation === 'manual');

  return (
    <div className="ob-step ob-step-deploy">
      <div className="ob-deploy-header">
        <h2 className="ob-step-title" style={{ marginBottom: 0 }}>Deploy {doneInSession + 1} of {totalForProgress}: {current.title}</h2>
        <div className="ob-deploy-progress">
          <div className="ob-deploy-progress-bar"><div className="ob-deploy-progress-fill" style={{ width: ((doneInSession / Math.max(1, totalForProgress)) * 100) + '%' }} /></div>
          <span className="muted small">{doneInSession}/{totalForProgress}</span>
        </div>
      </div>

      <div className="ob-deploy-meta">
        <span className="ob-deploy-sev" style={{ color: SEV_COLORS[current.severity], background: SEV_COLORS[current.severity] + '20', border: '1px solid ' + SEV_COLORS[current.severity] + '40' }}>{current.severity}</span>
        <span className="ob-deploy-pill">Priority #{current.priority}</span>
        <span className="ob-deploy-pill">{current.category}</span>
        {pb && <span className="ob-deploy-pill">{pb.automation}</span>}
        {pb && pb.control_id && <span className="ob-deploy-pill">NIST {pb.control_id}</span>}
      </div>

      <div className="ob-deploy-section">
        <div className="ob-deploy-section-title">Why this matters</div>
        <div className="ob-deploy-text">{current.why}</div>
      </div>

      {pb && pb.description && (
        <div className="ob-deploy-section">
          <div className="ob-deploy-section-title">What it does</div>
          <div className="ob-deploy-text">{pb.description}</div>
        </div>
      )}

      {pb && pb.blast_radius && (
        <div className="ob-deploy-section">
          <div className="ob-deploy-section-title">Blast radius</div>
          <div className="ob-deploy-blast">
            <span className={'ob-pill ' + (pb.blast_radius.reversible ? 'ob-pill-good' : 'ob-pill-warn')}>{pb.blast_radius.reversible ? '↩ Reversible' : '⚠ Not reversible'}</span>
            {pb.blast_radius.comms_required && <span className="ob-pill ob-pill-warn">📢 Comms required</span>}
            {pb.blast_radius.user_impact && <div className="ob-deploy-text" style={{ marginTop: 6 }}>{pb.blast_radius.user_impact}</div>}
            {pb.blast_radius.rollback && <div className="muted small" style={{ marginTop: 4 }}><strong>Rollback:</strong> {pb.blast_radius.rollback}</div>}
          </div>
        </div>
      )}

      {runbookLoading && <div className="muted" style={{ padding: 12 }}>Loading playbook…</div>}

      {pb && pb.steps && pb.steps.length > 0 && (
        <div className="ob-deploy-section">
          <div className="ob-deploy-section-title">Steps</div>
          <ol className="ob-deploy-steps">
            {pb.steps.map(function(s, i) {
              return (
                <li key={i} className="ob-deploy-step">
                  <div className="ob-deploy-step-row">
                    <span className={'ob-deploy-step-kind ob-deploy-step-kind-' + (s.kind || 'manual')}>{(s.kind || 'manual').replace('_', ' ')}</span>
                    <span>{s.description}</span>
                  </div>
                  {s.ps_script && (
                    <div className="ob-deploy-script-wrap">
                      <button className="ob-deploy-copy" onClick={function() { copyScript(i, s.ps_script); }}>
                        {copyState[i] ? '✓ Copied' : '📋 Copy'}
                      </button>
                      <pre className="ob-deploy-script">{s.ps_script}</pre>
                    </div>
                  )}
                  {s.graph && (
                    <div className="ob-deploy-graph">
                      <code><strong>{s.graph.method}</strong> {s.graph.path}</code>
                      {s.graph.body && <pre>{JSON.stringify(s.graph.body, null, 2)}</pre>}
                    </div>
                  )}
                  {s.ticket_template && (
                    <div className="ob-deploy-ticket">
                      <div><strong>Subject:</strong> {s.ticket_template.subject} <em>({s.ticket_template.priority})</em></div>
                      <div className="muted small">{s.ticket_template.body}</div>
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      )}

      {isManualScript && (
        <div className="ob-deploy-hint">
          <strong>Tip:</strong> Open <a href="https://shell.azure.com/" target="_blank" rel="noopener noreferrer" className="ob-link">Azure Cloud Shell</a> in a new tab, sign in as the GA of the target tenant, paste the script, then come back and click "I ran the script" below.
        </div>
      )}

      <div className="ob-deploy-actions">
        {isApprovalRequired ? (
          <button className="qbr-generate-btn" style={{ padding: '10px 24px', fontSize: 13 }} disabled={actionBusy} onClick={proposeAndApprove}>
            {actionBusy ? 'Working…' : '◇ Propose for approval'}
          </button>
        ) : isManualScript ? (
          <button className="qbr-generate-btn" style={{ padding: '10px 24px', fontSize: 13 }} disabled={actionBusy} onClick={function() { markComplete('manual_run'); }}>
            {actionBusy ? 'Working…' : '✓ I ran the script'}
          </button>
        ) : (
          <button className="qbr-generate-btn" style={{ padding: '10px 24px', fontSize: 13 }} disabled={actionBusy} onClick={function() { markComplete('verified_existing'); }}>
            {actionBusy ? 'Working…' : '✓ Mark complete'}
          </button>
        )}
        <button className="admin-btn" disabled={actionBusy} onClick={function() { var r = window.prompt('Skip reason (optional):'); skipItem(r); }}>Skip</button>
        <button className="admin-btn" disabled={actionBusy} onClick={function() { var r = window.prompt('Defer note (optional):'); skipItem('deferred: ' + (r || 'no note')); }}>Defer</button>
        {queue.length > 1 && (
          <button className="admin-btn" disabled={actionBusy} onClick={function() { if (deployIndex < queue.length - 1) setDeployIndex(deployIndex + 1); }} title="Skip to the next item without marking this one">→ Next item</button>
        )}
      </div>

      <div className="ob-nav">
        <button className="admin-btn" onClick={onBack}>← Back to Baseline</button>
        <button className="admin-btn" onClick={onNext}>Skip to Review →</button>
      </div>
    </div>
  );
}

// ── Step 5: Review (before/after) ─────────────────────────────────
function ObReviewStep({ data, initialSnapshot, completedItems, skippedItems, loading, onRefresh, onNext, onBack }) {
  if (loading || !data) return <div className="ob-step ob-step-centered"><div className="ob-spinner">Re-evaluating baseline…</div></div>;
  var s = data.summary;
  var initial = initialSnapshot || s;
  var delta = {
    pass: s.pass - initial.pass,
    fail: s.fail - initial.fail
  };
  return (
    <div className="ob-step">
      <h2 className="ob-step-title">Review</h2>
      <p className="ob-step-sub">Baseline re-evaluated after deploy actions. Note: some controls (CA policies in report-only mode, Intune profile rollouts) take time to propagate — re-run later for the full picture.</p>

      <div className="ob-review-delta">
        <div className="ob-review-delta-card">
          <div className="ob-review-delta-label">Passing</div>
          <div className="ob-review-delta-numbers">
            <span className="muted">{initial.pass}</span>
            <span className="ob-review-arrow">→</span>
            <span className="ob-review-now" style={{ color: '#22c55e' }}>{s.pass}</span>
            {delta.pass > 0 && <span className="ob-review-delta-positive">+{delta.pass}</span>}
          </div>
        </div>
        <div className="ob-review-delta-card">
          <div className="ob-review-delta-label">Failing</div>
          <div className="ob-review-delta-numbers">
            <span className="muted">{initial.fail}</span>
            <span className="ob-review-arrow">→</span>
            <span className="ob-review-now" style={{ color: s.fail > 0 ? '#ef4444' : '#22c55e' }}>{s.fail}</span>
            {delta.fail < 0 && <span className="ob-review-delta-positive">{delta.fail}</span>}
          </div>
        </div>
        <div className="ob-review-delta-card">
          <div className="ob-review-delta-label">Marked complete this session</div>
          <div className="ob-review-delta-numbers">
            <span className="ob-review-now">{Object.keys(completedItems).length}</span>
          </div>
        </div>
        <div className="ob-review-delta-card">
          <div className="ob-review-delta-label">Skipped / deferred</div>
          <div className="ob-review-delta-numbers">
            <span className="ob-review-now">{Object.keys(skippedItems).length}</span>
          </div>
        </div>
      </div>

      <div className="ob-summary-list">
        {data.items.map(function(item) {
          var statusLabel = item.status === 'pass' ? '✓ Pass' : item.status === 'fail' ? '✗ Fail' : item.status === 'blocked_licensing' ? '🔒 Blocked' : item.status === 'pending_proposal' ? '◇ Pending' : item.status === 'manual_check' ? '✎ Manual' : '? Unknown';
          var sessionTag = completedItems[item.id] ? ' (marked in session)' : (skippedItems[item.id] ? ' (skipped in session)' : '');
          return (
            <div key={item.id} className={'ob-summary-row ob-summary-row-' + item.status}>
              <span className="ob-summary-prio">#{item.priority}</span>
              <span className="ob-summary-title">{item.title}<span className="muted small">{sessionTag}</span></span>
              <span className={'ob-summary-status ob-summary-status-' + item.status}>{statusLabel}</span>
            </div>
          );
        })}
      </div>

      <div className="ob-nav">
        <button className="admin-btn" onClick={onBack}>← Back to Deploy</button>
        <button className="admin-btn" onClick={onRefresh}>↻ Re-evaluate</button>
        <button className="qbr-generate-btn" style={{ padding: '10px 24px', fontSize: 13 }} onClick={onNext}>Next: Summary →</button>
      </div>
    </div>
  );
}

// ── Step 6: Summary (printable / closeable) ───────────────────────
function ObSummaryStep({ clientName, clientId, data, initialSnapshot, completedItems, skippedItems, sessionStartedAt, onClose }) {
  var [reportOpen, setReportOpen] = useState(false);
  if (!data) return null;
  var s = data.summary;
  var initial = initialSnapshot || s;
  var completed = Object.keys(completedItems).map(function(id) { return data.items.find(function(i) { return i.id === id; }); }).filter(Boolean);
  var skipped = Object.keys(skippedItems).map(function(id) { return data.items.find(function(i) { return i.id === id; }); }).filter(Boolean);
  return (
    <div className="ob-step ob-summary-print">
      <div className="ob-summary-header">
        <div className="ob-summary-eyebrow">Tenant Onboarding · Summary</div>
        <div className="ob-summary-title-name">{clientName || clientId}</div>
        <div className="muted small">Generated {new Date().toLocaleString()}</div>
      </div>

      <div className="ob-review-delta">
        <div className="ob-review-delta-card"><div className="ob-review-delta-label">Started</div><div className="ob-review-delta-numbers"><span className="muted">{initial.pass}/{initial.applicable || initial.total}</span></div></div>
        <div className="ob-review-delta-card"><div className="ob-review-delta-label">Finished</div><div className="ob-review-delta-numbers"><span style={{ color: '#22c55e' }}>{s.pass}/{s.applicable || s.total}</span></div></div>
        <div className="ob-review-delta-card"><div className="ob-review-delta-label">Coverage</div><div className="ob-review-delta-numbers"><span>{s.coverage_pct}%</span></div></div>
        <div className="ob-review-delta-card"><div className="ob-review-delta-label">Items addressed</div><div className="ob-review-delta-numbers"><span>{completed.length + skipped.length}</span></div></div>
      </div>

      {completed.length > 0 && (
        <div className="ob-deploy-section">
          <div className="ob-deploy-section-title">Completed</div>
          <ul className="ob-summary-bullets">
            {completed.map(function(i) { return <li key={i.id}><strong>{i.title}</strong> — {completedItems[i.id].method.replace('_', ' ')}</li>; })}
          </ul>
        </div>
      )}
      {skipped.length > 0 && (
        <div className="ob-deploy-section">
          <div className="ob-deploy-section-title">Skipped or deferred</div>
          <ul className="ob-summary-bullets">
            {skipped.map(function(i) { return <li key={i.id}><strong>{i.title}</strong> — {skippedItems[i.id].reason || 'no reason given'}</li>; })}
          </ul>
        </div>
      )}
      {s.blocked_licensing > 0 && (
        <div className="ob-deploy-hint" style={{ marginTop: 12 }}>
          <strong>Licensing reminder:</strong> {s.blocked_licensing} baseline control(s) remain blocked by the tenant's Entra licensing tier. Upgrade to Microsoft 365 Business Premium / E3 / E5 to enable.
        </div>
      )}

      {/* Audit-quality export — the Confluence-uploadable deliverable.
          Full PowerShell scripts, blast radius, rollback paths, audit
          log trail, signed/timestamped operator actions. The summary
          screen above is the at-a-glance; this is the full record. */}
      <div className="ob-deploy-hint" style={{ borderLeft: '3px solid var(--accent-blue)' }}>
        <strong>Export full onboarding report</strong> — documentation-grade artifact for Confluence / audit / rollback. Includes every PowerShell script that was generated, every blast-radius warning the operator saw, full audit-log trail (who did what when), and the post-deployment baseline state.
        <div style={{ marginTop: 8 }}>
          <button className="qbr-generate-btn" style={{ padding: '8px 20px', fontSize: 12 }} onClick={function() { setReportOpen(true); }}>Open full report →</button>
        </div>
      </div>

      <div className="ob-nav no-print">
        <button className="qbr-generate-btn" style={{ padding: '10px 24px', fontSize: 13 }} onClick={function() { window.print(); }}>Print summary</button>
        <button className="admin-btn" onClick={onClose}>Done — close wizard</button>
      </div>

      {reportOpen && (
        <BaselineOnboardingReport
          clientId={clientId}
          clientName={clientName}
          sessionStartedAt={sessionStartedAt || initial.session_started_at || new Date(Date.now() - 4 * 3600000).toISOString()}
          onClose={function() { setReportOpen(false); }}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// ── BaselineOnboardingReport ──────────────────────────────────────
// Documentation-grade artifact rendered from /baseline/onboarding-
// report. Designed for the Confluence-upload / audit-evidence /
// rollback-reference use case. Three export paths:
//
//   • Print / PDF        — browser-native, @media print stylesheet
//   • Download Markdown  — flattens everything to .md (Confluence
//                          paste-friendly)
//   • Download JSON      — raw context for machine processing /
//                          future diff against next onboarding
//
// Includes per-item: NIST control, blast radius, full PowerShell
// in copy-ready blocks, session-execution metadata (who/when/how),
// playbook references, plus the audit-log trail.
function BaselineOnboardingReport({ clientId, clientName, sessionStartedAt, onClose }) {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [err, setErr] = useState(null);

  useEffect(function() {
    setLoading(true); setErr(null);
    var qs = '?session_start=' + encodeURIComponent(sessionStartedAt || new Date(Date.now() - 24 * 3600000).toISOString());
    authFetch(API + '/clients/' + clientId + '/baseline/onboarding-report' + qs)
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, status: r.status, d: d }; }); })
      .then(function(x) {
        setLoading(false);
        if (x.ok) setData(x.d);
        else setErr((x.d && x.d.error) || ('Report load failed (HTTP ' + x.status + ')'));
      })
      .catch(function() { setLoading(false); setErr('Report request failed'); });
  }, [clientId, sessionStartedAt]);

  // Esc closes
  useEffect(function() {
    function k(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', k);
    return function() { window.removeEventListener('keydown', k); };
  }, [onClose]);

  function fmtDate(iso) {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch (e) { return iso; }
  }
  function statusLabel(s) {
    switch (s) {
      case 'pass': return '✓ Pass';
      case 'fail': return '✗ Fail';
      case 'pending_proposal': return '◇ Pending approval';
      case 'blocked_licensing': return '🔒 Licensing-blocked';
      case 'manual_check': return '✎ Manual check';
      default: return '? ' + (s || 'Unknown');
    }
  }
  function downloadBlob(content, filename, mime) {
    var blob = new Blob([content], { type: mime + ';charset=utf-8' });
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
  function exportJson() {
    if (!data) return;
    downloadBlob(JSON.stringify(data, null, 2),
      'aurelius-onboarding-' + clientId + '-' + new Date().toISOString().slice(0, 10) + '.json',
      'application/json');
  }
  function exportMarkdown() {
    if (!data) return;
    var lines = [];
    lines.push('# Tenant Onboarding Report — ' + (data.client.name || clientId));
    lines.push('');
    lines.push('**Generated:** ' + fmtDate(data.generated_at));
    lines.push('**Session:** ' + fmtDate(data.session.start) + ' → ' + fmtDate(data.session.end));
    lines.push('**Operator:** ' + (data.operator.email || '(unknown)'));
    lines.push('');
    lines.push('## Tenant Context');
    lines.push('');
    lines.push('| Field | Value |');
    lines.push('|---|---|');
    lines.push('| Client | ' + data.client.name + ' (' + data.client.id + ') · ' + data.client.type + ' · ' + data.client.industry + (data.client.cmmc ? ' · CMMC' : '') + ' |');
    lines.push('| Tenant | ' + (data.tenant.tenant_name || '—') + ' (' + (data.tenant.tenant_domain || '—') + ') |');
    lines.push('| Tenant ID | `' + data.tenant.tenant_id + '` |');
    lines.push('| Graph App ID | `' + data.tenant.graph_client_id + '` |');
    lines.push('| Licensing Tier | ' + ((data.tenant.licensing_tier || 'unknown').toUpperCase()) + ' |');
    lines.push('| Intune | ' + (data.tenant.has_intune ? 'yes' : 'no') + ' |');
    lines.push('| Defender for Endpoint | ' + (data.tenant.has_defender_endpoint ? 'yes' : 'no') + ' |');
    if (data.tenant.user_count != null) lines.push('| Users | ' + data.tenant.user_count + ' |');
    if (data.tenant.device_count != null) lines.push('| Devices (Entra) | ' + data.tenant.device_count + ' |');
    if (data.tenant.managed_device_count != null) lines.push('| Managed devices (Intune) | ' + data.tenant.managed_device_count + ' |');
    if (data.tenant.ca_policy_count != null) lines.push('| Conditional Access policies | ' + data.tenant.ca_policy_count + ' |');
    lines.push('');
    var s = data.post_deployment_state.summary;
    lines.push('## Post-deployment Baseline State');
    lines.push('');
    lines.push('**Coverage:** ' + s.pass + ' of ' + s.applicable + ' applicable controls passing (' + s.coverage_pct + '%)');
    lines.push('');
    lines.push('| State | Count |');
    lines.push('|---|---|');
    lines.push('| Pass | ' + s.pass + ' |');
    lines.push('| Fail | ' + s.fail + ' |');
    lines.push('| Pending approval | ' + s.pending_proposal + ' |');
    lines.push('| Licensing-blocked | ' + s.blocked_licensing + ' |');
    lines.push('| Manual check / unknown | ' + (s.manual_check + s.unknown) + ' |');
    lines.push('');
    lines.push('## Item-by-item Detail');
    lines.push('');
    data.session_items.forEach(function(it) {
      var c = it.catalog; var pb = it.playbook; var ex = it.session_execution;
      lines.push('### #' + c.priority + ': ' + c.title);
      lines.push('');
      lines.push('- **Current status:** ' + statusLabel(it.current_status));
      lines.push('- **Severity:** ' + c.severity);
      lines.push('- **NIST 800-171 control:** ' + (pb && pb.control_id ? pb.control_id : '—'));
      lines.push('- **Playbook:** `' + c.playbook_id + '` (' + (pb ? pb.automation : '—') + ')');
      lines.push('- **Licensing requirement:** ' + (c.requires_licensing || 'free'));
      if (it.current_reason) lines.push('- **Reason / Evidence:** ' + it.current_reason);
      lines.push('');
      lines.push('**Why this control matters:** ' + c.why);
      lines.push('');
      if (pb && pb.description) { lines.push('**What it does:** ' + pb.description); lines.push(''); }
      if (pb && pb.blast_radius) {
        lines.push('**Blast radius:**');
        lines.push('');
        lines.push('- Reversible: ' + (pb.blast_radius.reversible ? 'yes' : 'no'));
        lines.push('- Comms required: ' + (pb.blast_radius.comms_required ? 'yes' : 'no'));
        if (pb.blast_radius.user_impact) lines.push('- User impact: ' + pb.blast_radius.user_impact);
        if (pb.blast_radius.rollback) lines.push('- **Rollback path:** ' + pb.blast_radius.rollback);
        if (pb.blast_radius.affected_systems) lines.push('- Affected systems: ' + (pb.blast_radius.affected_systems || []).join(', '));
        lines.push('');
      }
      if (ex) {
        lines.push('**Session action taken:**');
        lines.push('');
        lines.push('- Method: `' + (ex.params.method || ex.params.trigger || ex.mode) + '`');
        lines.push('- Status: ' + ex.status);
        if (ex.requested_at)  lines.push('- Requested at: ' + fmtDate(ex.requested_at));
        if (ex.approved_at)   lines.push('- Approved at: ' + fmtDate(ex.approved_at) + (ex.approved_by_email ? ' by ' + ex.approved_by_email : ''));
        if (ex.completed_at)  lines.push('- Completed at: ' + fmtDate(ex.completed_at));
        if (ex.requested_by_email) lines.push('- Operator: ' + ex.requested_by_email);
        if (ex.operator_notes) lines.push('- Notes: _' + ex.operator_notes + '_');
        if (ex.error_message) lines.push('- **Error:** ' + ex.error_message);
        lines.push('');
      }
      if (pb && pb.steps && pb.steps.length > 0) {
        lines.push('**Steps executed (verbatim — rollback reference):**');
        lines.push('');
        pb.steps.forEach(function(st, i) {
          lines.push((i + 1) + '. **' + (st.kind || 'manual').replace('_', ' ') + ':** ' + st.description);
          if (st.ps_script) {
            lines.push('');
            lines.push('   ```powershell');
            st.ps_script.split('\n').forEach(function(l) { lines.push('   ' + l); });
            lines.push('   ```');
          }
          if (st.graph) {
            lines.push('');
            lines.push('   ```');
            lines.push('   ' + st.graph.method + ' ' + st.graph.path);
            if (st.graph.body) {
              JSON.stringify(st.graph.body, null, 2).split('\n').forEach(function(l) { lines.push('   ' + l); });
            }
            lines.push('   ```');
          }
        });
        lines.push('');
      }
      if (pb && pb.references && pb.references.length > 0) {
        lines.push('**References:**');
        lines.push('');
        pb.references.forEach(function(r) { lines.push('- [' + r.label + '](' + r.url + ')'); });
        lines.push('');
      }
      lines.push('---');
      lines.push('');
    });
    if (data.audit_trail && data.audit_trail.length > 0) {
      lines.push('## Audit Trail');
      lines.push('');
      lines.push('| Timestamp | Event | Operator | Severity | Metadata |');
      lines.push('|---|---|---|---|---|');
      data.audit_trail.forEach(function(a) {
        lines.push('| ' + fmtDate(a.timestamp) + ' | `' + a.event_type + '` | ' + (a.user_email || '—') + ' | ' + a.severity + ' | <code>' + JSON.stringify(a.metadata || {}).replace(/\|/g, '\\|').slice(0, 200) + '</code> |');
      });
      lines.push('');
    }
    lines.push('---');
    lines.push('*Generated by Aurelius at ' + fmtDate(data.generated_at) + '. Retain for audit + rollback reference.*');
    downloadBlob(lines.join('\n'),
      'aurelius-onboarding-' + clientId + '-' + new Date().toISOString().slice(0, 10) + '.md',
      'text/markdown');
  }

  if (loading) return (
    <div className="ob-overlay" onClick={onClose}>
      <div className="ob-shell" onClick={function(e) { e.stopPropagation(); }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="ob-spinner">Assembling report…</div>
      </div>
    </div>
  );
  if (err) return (
    <div className="ob-overlay" onClick={onClose}>
      <div className="ob-shell" onClick={function(e) { e.stopPropagation(); }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="admin-banner error" style={{ fontSize: 13 }}>{err}</div>
        <button className="admin-btn" onClick={onClose} style={{ marginTop: 12 }}>Close</button>
      </div>
    </div>
  );
  if (!data) return null;

  var s = data.post_deployment_state.summary;

  return (
    <div className="ob-overlay">
      <div className="bor-shell">
        <div className="bor-toolbar no-print">
          <button className="admin-btn sm" onClick={onClose}>← Close</button>
          <div style={{ flex: 1 }} />
          <button className="admin-btn sm" onClick={exportJson}>Download JSON</button>
          <button className="admin-btn sm" onClick={exportMarkdown}>Download Markdown</button>
          <button className="qbr-generate-btn" style={{ padding: '8px 18px', fontSize: 12 }} onClick={function() { window.print(); }}>Print / Save PDF</button>
        </div>

        <div className="bor-report">
          {/* ── Header ───────────────────────────────────────────────── */}
          <div className="bor-header">
            <div className="bor-eyebrow">Aurelius · Tenant Onboarding Report</div>
            <div className="bor-title">{data.client.name}</div>
            <div className="bor-subtitle">Documentation-grade evidence + rollback reference</div>
            <div className="bor-meta-grid">
              <div><span className="bor-meta-label">Client</span>{data.client.name} ({data.client.id})</div>
              <div><span className="bor-meta-label">Type / Industry</span>{data.client.type} · {data.client.industry}{data.client.cmmc ? ' · CMMC' : ''}</div>
              <div><span className="bor-meta-label">Tenant</span>{data.tenant.tenant_name || '—'}</div>
              <div><span className="bor-meta-label">Primary domain</span>{data.tenant.tenant_domain || '—'}</div>
              <div><span className="bor-meta-label">Tenant ID</span><code>{data.tenant.tenant_id}</code></div>
              <div><span className="bor-meta-label">Graph App ID</span><code>{data.tenant.graph_client_id}</code></div>
              <div><span className="bor-meta-label">Licensing</span><strong>{(data.tenant.licensing_tier || 'unknown').toUpperCase()}</strong> · Intune: {data.tenant.has_intune ? 'yes' : 'no'} · Defender: {data.tenant.has_defender_endpoint ? 'yes' : 'no'}</div>
              <div><span className="bor-meta-label">Operator</span>{data.operator.email || '—'}</div>
              <div><span className="bor-meta-label">Session</span>{fmtDate(data.session.start)} → {fmtDate(data.session.end)}</div>
              <div><span className="bor-meta-label">Generated</span>{fmtDate(data.generated_at)}</div>
            </div>
          </div>

          {/* ── Snapshot ─────────────────────────────────────────────── */}
          <div className="bor-section">
            <div className="bor-section-title">Tenant snapshot at report time</div>
            <div className="bor-snapshot-grid">
              {data.tenant.user_count != null && <div><span className="bor-snapshot-num">{data.tenant.user_count}</span><span className="bor-snapshot-label">Users</span></div>}
              {data.tenant.device_count != null && <div><span className="bor-snapshot-num">{data.tenant.device_count}</span><span className="bor-snapshot-label">Devices (Entra)</span></div>}
              {data.tenant.managed_device_count != null && <div><span className="bor-snapshot-num">{data.tenant.managed_device_count}</span><span className="bor-snapshot-label">Managed (Intune)</span></div>}
              {data.tenant.ca_policy_count != null && <div><span className="bor-snapshot-num">{data.tenant.ca_policy_count}</span><span className="bor-snapshot-label">CA policies</span></div>}
            </div>
          </div>

          {/* ── Post-deployment summary ──────────────────────────────── */}
          <div className="bor-section">
            <div className="bor-section-title">Post-deployment baseline state</div>
            <div className="metric-tiles">
              <div className="metric-tile metric-tile-good"><div className="metric-tile-label">Pass</div><div className="metric-tile-value" style={{ color: '#22c55e' }}>{s.pass}</div></div>
              <div className={'metric-tile' + (s.fail > 0 ? ' metric-tile-alert' : '')}><div className="metric-tile-label">Fail</div><div className="metric-tile-value" style={{ color: s.fail > 0 ? '#ef4444' : undefined }}>{s.fail}</div></div>
              <div className="metric-tile"><div className="metric-tile-label">Pending</div><div className="metric-tile-value">{s.pending_proposal}</div></div>
              <div className="metric-tile"><div className="metric-tile-label">Licensing-blocked</div><div className="metric-tile-value">{s.blocked_licensing}</div></div>
            </div>
            <div className="muted small" style={{ marginTop: 8 }}>Coverage: {s.pass} of {s.applicable} applicable controls passing ({s.coverage_pct}%).</div>
          </div>

          {/* ── Per-item detail ──────────────────────────────────────── */}
          <div className="bor-section">
            <div className="bor-section-title">Item-by-item record</div>
            {data.session_items.map(function(it) {
              var c = it.catalog; var pb = it.playbook; var ex = it.session_execution;
              return (
                <div key={c.id} className="bor-item">
                  <div className="bor-item-head">
                    <span className="bor-item-num">#{c.priority}</span>
                    <span className="bor-item-title">{c.title}</span>
                    <span className={'bor-item-status bor-item-status-' + it.current_status}>{statusLabel(it.current_status)}</span>
                  </div>
                  <div className="bor-item-meta">
                    <span>Severity: <strong>{c.severity}</strong></span>
                    {pb && pb.control_id && <span>NIST: <strong>{pb.control_id}</strong></span>}
                    <span>Playbook: <code>{c.playbook_id}</code></span>
                    {pb && <span>Automation: <strong>{pb.automation}</strong></span>}
                    <span>Licensing: {c.requires_licensing}</span>
                  </div>

                  {it.current_reason && (
                    <div className="bor-item-reason"><strong>Reason:</strong> {it.current_reason}</div>
                  )}

                  <div className="bor-item-block">
                    <div className="bor-item-block-label">Why this matters</div>
                    <div className="bor-item-text">{c.why}</div>
                  </div>

                  {pb && pb.description && (
                    <div className="bor-item-block">
                      <div className="bor-item-block-label">What the playbook does</div>
                      <div className="bor-item-text">{pb.description}</div>
                    </div>
                  )}

                  {pb && pb.blast_radius && (
                    <div className="bor-item-block">
                      <div className="bor-item-block-label">Blast radius + rollback path</div>
                      <ul className="bor-item-bullets">
                        <li><strong>Reversible:</strong> {pb.blast_radius.reversible ? 'Yes' : 'No'}</li>
                        <li><strong>Comms required:</strong> {pb.blast_radius.comms_required ? 'Yes' : 'No'}</li>
                        {pb.blast_radius.user_impact && <li><strong>User impact:</strong> {pb.blast_radius.user_impact}</li>}
                        {pb.blast_radius.rollback && <li><strong>Rollback:</strong> {pb.blast_radius.rollback}</li>}
                        {pb.blast_radius.affected_systems && pb.blast_radius.affected_systems.length > 0 && <li><strong>Affected systems:</strong> {pb.blast_radius.affected_systems.join(', ')}</li>}
                      </ul>
                    </div>
                  )}

                  {ex && (
                    <div className="bor-item-block">
                      <div className="bor-item-block-label">Session action</div>
                      <table className="bor-item-table">
                        <tbody>
                          <tr><td>Method</td><td><code>{ex.params.method || ex.params.trigger || ex.mode}</code></td></tr>
                          <tr><td>Status</td><td>{ex.status}</td></tr>
                          {ex.requested_at && <tr><td>Requested at</td><td>{fmtDate(ex.requested_at)}</td></tr>}
                          {ex.approved_at && <tr><td>Approved at</td><td>{fmtDate(ex.approved_at)} {ex.approved_by_email ? '· by ' + ex.approved_by_email : ''}</td></tr>}
                          {ex.completed_at && <tr><td>Completed at</td><td>{fmtDate(ex.completed_at)}</td></tr>}
                          {ex.requested_by_email && <tr><td>Operator</td><td>{ex.requested_by_email}</td></tr>}
                          {ex.operator_notes && <tr><td>Notes</td><td><em>{ex.operator_notes}</em></td></tr>}
                          {ex.error_message && <tr><td>Error</td><td style={{ color: '#ef4444' }}>{ex.error_message}</td></tr>}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {pb && pb.steps && pb.steps.length > 0 && (
                    <div className="bor-item-block">
                      <div className="bor-item-block-label">Steps (verbatim — rollback reference)</div>
                      <ol className="bor-item-steps">
                        {pb.steps.map(function(st, i) {
                          return (
                            <li key={i}>
                              <div className="bor-step-row">
                                <span className={'bor-step-kind bor-step-kind-' + (st.kind || 'manual')}>{(st.kind || 'manual').replace('_', ' ')}</span>
                                <span>{st.description}</span>
                              </div>
                              {st.ps_script && <pre className="bor-step-script">{st.ps_script}</pre>}
                              {st.graph && (
                                <pre className="bor-step-script">{st.graph.method + ' ' + st.graph.path + (st.graph.body ? '\n' + JSON.stringify(st.graph.body, null, 2) : '')}</pre>
                              )}
                              {st.ticket_template && (
                                <div className="bor-step-ticket">
                                  <div><strong>Subject:</strong> {st.ticket_template.subject} <em>({st.ticket_template.priority})</em></div>
                                  <div className="muted small">{st.ticket_template.body}</div>
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ol>
                    </div>
                  )}

                  {pb && pb.references && pb.references.length > 0 && (
                    <div className="bor-item-block">
                      <div className="bor-item-block-label">References</div>
                      <ul className="bor-item-bullets">
                        {pb.references.map(function(r, i) {
                          return <li key={i}><a href={r.url} target="_blank" rel="noopener noreferrer">{r.label}</a></li>;
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── Audit trail ──────────────────────────────────────────── */}
          {data.audit_trail && data.audit_trail.length > 0 && (
            <div className="bor-section">
              <div className="bor-section-title">Audit trail ({data.audit_trail.length} event{data.audit_trail.length === 1 ? '' : 's'})</div>
              <table className="bor-audit-table">
                <thead><tr><th>Timestamp</th><th>Event</th><th>Operator</th><th>Severity</th></tr></thead>
                <tbody>
                  {data.audit_trail.map(function(a) {
                    return (
                      <tr key={a.id}>
                        <td>{fmtDate(a.timestamp)}</td>
                        <td><code>{a.event_type}</code></td>
                        <td>{a.user_email || '—'}</td>
                        <td>{a.severity}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="bor-footer">
            <em>Generated by Aurelius (CloudByte Group) at {fmtDate(data.generated_at)}. Retain for audit + rollback reference. Full machine-readable JSON available via Download JSON button.</em>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── SetupOnboardingCta — promoted launcher for the Onboarding Wizard ─
// Sits at the top of the Setup tab so admins always see the guided
// flow as the first-class onboarding option. Also surfaces an "in
// progress" indicator when localStorage has a saved wizard session
// for this client.
function SetupOnboardingCta({ clientId, clientName }) {
  var [open, setOpen] = useState(false);
  var [inProgress, setInProgress] = useState(false);
  var [savedStep, setSavedStep] = useState(null);

  useEffect(function() {
    try {
      var raw = localStorage.getItem('aurelius_onboarding_' + clientId);
      if (!raw) { setInProgress(false); setSavedStep(null); return; }
      var parsed = JSON.parse(raw);
      if (Date.now() - (parsed.ts || 0) > 24 * 60 * 60 * 1000) { setInProgress(false); setSavedStep(null); return; }
      // Don't treat 'summary' as in-progress — that's the closed state.
      if (parsed.step && parsed.step !== 'welcome' && parsed.step !== 'summary') {
        setInProgress(true);
        setSavedStep(parsed.step);
      } else {
        setInProgress(false);
      }
    } catch (e) { /* localStorage unavailable */ }
  }, [clientId, open]);

  return (
    <>
      <div className="setup-onboarding-cta">
        <div className="setup-onboarding-cta-body">
          <div className="setup-onboarding-cta-eyebrow">{inProgress ? 'Resume onboarding' : 'Recommended next step'}</div>
          <div className="setup-onboarding-cta-title">
            {inProgress
              ? 'Pick up where you left off (at: ' + savedStep + ')'
              : 'Guided tenant onboarding — best-practices baseline in one flow'}
          </div>
          <div className="setup-onboarding-cta-sub">
            Full-screen wizard: verify connectivity → assess 12 canonical controls → deploy each fix with copy-ready PowerShell → review delta → printable summary. Replaces the hunt across Integrations / Setup / Proposals.
          </div>
        </div>
        <button className="qbr-generate-btn" style={{ padding: '12px 28px', fontSize: 14, flex: 'none' }} onClick={function() { setOpen(true); }}>
          {inProgress ? 'Resume wizard →' : 'Start guided onboarding →'}
        </button>
      </div>
      {open && (
        <TenantOnboardingWizard
          clientId={clientId}
          clientName={clientName}
          onClose={function() { setOpen(false); }}
        />
      )}
    </>
  );
}

// ── BaselineDeploymentWizard ──────────────────────────────────────
// Surfaces the canonical 12-item M365 best-practices baseline as a
// single checklist. Each row shows status (pass / fail / pending /
// blocked-by-licensing / unknown) sourced from the persistent ISPM
// state + live inline checks (see backend/baseline-catalog.js).
//
// Operator actions:
//   • Propose this fix      — creates one AI proposal, lands on ◇ bell
//   • Propose all applicable — batches every 'fail' that isn't blocked
//   • Refresh               — re-evaluates against fresh bundle
//
// Mounted at the bottom of SetupTab. Admin-only.
function BaselineDeploymentWizard({ clientId, onProposalCreated }) {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(false);
  var [err, setErr] = useState(null);
  var [busy, setBusy] = useState(false);
  var [busyItemId, setBusyItemId] = useState(null);
  var [toast, setToast] = useState(null);

  function showToast(tone, text) { setToast({ tone: tone, text: text }); setTimeout(function() { setToast(null); }, 5000); }

  function load() {
    setLoading(true); setErr(null);
    authFetch(API + '/clients/' + clientId + '/baseline')
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, status: r.status, d: d }; }); })
      .then(function(x) {
        setLoading(false);
        if (x.ok) setData(x.d);
        else setErr(x.d && x.d.error ? x.d.error : 'Baseline evaluation failed (HTTP ' + x.status + ')');
      })
      .catch(function() { setLoading(false); setErr('Baseline request failed'); });
  }
  useEffect(load, [clientId]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Propose a single item — POST to the same endpoint pattern used
  // by /identity-posture/:findingId/propose, but with the playbook id
  // directly (since not every baseline item has a finding_id).
  function proposeOne(item) {
    setBusyItemId(item.id);
    // Reuse the manual identity-posture propose endpoint when we have
    // a finding_id; otherwise fall back to a per-baseline propose
    // (which the backend handles via the same playbook insert path).
    // For simplicity, we use propose-all's underlying logic: just
    // create one proposal directly using the playbook.
    // The cleanest path is the existing identity-posture propose
    // endpoint when ispm_check_id is set; else use a dedicated route.
    // For now, we POST to /baseline/propose-one (we'll add a server
    // shim if needed — but propose-all handles this case by item id).
    authFetch(API + '/clients/' + clientId + '/baseline/propose-all', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ only: [item.id] })
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusyItemId(null);
        if (x.ok && x.d.created && x.d.created.length > 0) {
          showToast('success', 'Proposal #' + x.d.created[0].proposal_id + ' created for "' + item.title + '". Approve via ◇ bell.');
          if (onProposalCreated) onProposalCreated();
          load();
        } else if (x.ok && x.d.skipped && x.d.skipped.length > 0) {
          showToast('warn', 'Skipped: ' + (x.d.skipped[0].reason || x.d.skipped[0].status || 'no action needed'));
        } else {
          showToast('error', (x.d && x.d.error) || 'Proposal failed');
        }
      })
      .catch(function() { setBusyItemId(null); showToast('error', 'Proposal request failed'); });
  }

  function proposeAll() {
    if (!data) return;
    var failing = data.items.filter(function(i) { return i.status === 'fail'; });
    if (failing.length === 0) { showToast('warn', 'Nothing to propose — no items in fail state.'); return; }
    if (!window.confirm('Create AI proposals for all ' + failing.length + ' failing baseline item(s)? Each lands on the ◇ proposals bell awaiting approval before any change is executed.')) return;
    setBusy(true);
    authFetch(API + '/clients/' + clientId + '/baseline/propose-all', { method: 'POST' })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusy(false);
        if (x.ok) {
          showToast(x.d.failed.length > 0 ? 'warn' : 'success',
            x.d.created.length + ' proposal(s) created · ' + x.d.skipped.length + ' skipped · ' + x.d.failed.length + ' failed');
          if (onProposalCreated) onProposalCreated();
          load();
        } else {
          showToast('error', (x.d && x.d.error) || 'Batch propose failed');
        }
      })
      .catch(function() { setBusy(false); showToast('error', 'Batch propose request failed'); });
  }

  if (loading && !data) return <div className="muted" style={{ padding: 16, fontSize: 12 }}>Evaluating baseline…</div>;
  if (err) return <div className="admin-banner error" style={{ fontSize: 12 }}>{err}</div>;
  if (!data) return null;

  var s = data.summary;
  var failingCount = data.items.filter(function(i) { return i.status === 'fail'; }).length;

  // Group items by category for readability
  var byCategory = { identity: [], endpoints: [], hygiene: [] };
  data.items.forEach(function(i) {
    var cat = byCategory[i.category] ? i.category : 'identity';
    byCategory[cat].push(i);
  });

  // Status badge styling
  var SEV_COLORS = { critical: '#ef4444', high: '#f59e0b', medium: '#60a5fa', low: '#a78bfa' };
  function statusBadge(item) {
    var label, cls;
    switch (item.status) {
      case 'pass': label = '✓ Pass'; cls = 'bdw-status-pass'; break;
      case 'fail': label = '✗ Fail'; cls = 'bdw-status-fail'; break;
      case 'pending_proposal': label = '◇ Pending approval'; cls = 'bdw-status-pending'; break;
      case 'blocked_licensing': label = '🔒 Licensing-blocked'; cls = 'bdw-status-blocked'; break;
      case 'manual_check': label = '✎ Manual check'; cls = 'bdw-status-manual'; break;
      default: label = '? Unknown'; cls = 'bdw-status-unknown';
    }
    return <span className={'bdw-status ' + cls}>{label}</span>;
  }

  return (
    <ContentBand
      title="Best-practices baseline"
      subtitle={'Canonical M365 baseline · ' + s.pass + ' of ' + s.applicable + ' applicable controls passing (' + s.coverage_pct + '%)'}
      rightContent={
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="insights-btn" disabled={loading} onClick={load}>{loading ? 'Refreshing…' : '↻ Refresh'}</button>
          <button className="admin-btn primary sm" disabled={busy || failingCount === 0} onClick={proposeAll}>
            {busy ? 'Proposing…' : 'Propose all (' + failingCount + ')'}
          </button>
        </div>
      }
    >
      {toast && <div className={'admin-banner ' + toast.tone} style={{ fontSize: 12, marginBottom: 10 }}>{toast.text}</div>}

      {/* Progress bar */}
      <div className="bdw-progress">
        <div className="bdw-progress-bar">
          <div className="bdw-progress-fill" style={{ width: s.coverage_pct + '%' }} />
        </div>
        <div className="bdw-progress-stats">
          <span className="bdw-stat bdw-stat-pass">{s.pass} pass</span>
          <span className="bdw-stat bdw-stat-fail">{s.fail} fail</span>
          {s.pending_proposal > 0 && <span className="bdw-stat bdw-stat-pending">{s.pending_proposal} pending</span>}
          {s.blocked_licensing > 0 && <span className="bdw-stat bdw-stat-blocked">{s.blocked_licensing} licensing-blocked</span>}
          {(s.unknown + (s.manual_check || 0)) > 0 && <span className="bdw-stat bdw-stat-unknown">{s.unknown + (s.manual_check || 0)} unknown</span>}
        </div>
      </div>

      {data.tenant_licensing && data.tenant_licensing.tier === 'free' && s.blocked_licensing > 0 && (
        <div className="admin-banner warn" style={{ fontSize: 12, marginBottom: 12 }}>
          <strong>Tenant on Entra ID Free.</strong> {s.blocked_licensing} baseline control(s) are blocked because Custom Conditional Access + Intune require Microsoft 365 Business Premium or above. Upgrade to unlock those rows.
        </div>
      )}

      {Object.keys(byCategory).map(function(cat) {
        if (byCategory[cat].length === 0) return null;
        return (
          <div key={cat} className="bdw-category">
            <div className="bdw-category-title">{cat === 'identity' ? 'Identity controls' : cat === 'endpoints' ? 'Endpoint controls' : 'Hygiene / housekeeping'}</div>
            <div className="bdw-items">
              {byCategory[cat].map(function(item) {
                var canPropose = item.status === 'fail';
                return (
                  <div key={item.id} className={'bdw-item bdw-item-' + item.status}>
                    <div className="bdw-item-main">
                      <div className="bdw-item-row1">
                        <span className="bdw-item-sev" style={{ color: SEV_COLORS[item.severity], background: SEV_COLORS[item.severity] + '20', border: '1px solid ' + SEV_COLORS[item.severity] + '40' }}>{item.severity}</span>
                        {statusBadge(item)}
                        <span className="bdw-item-priority">#{item.priority}</span>
                        <span className="bdw-item-title">{item.title}</span>
                      </div>
                      <div className="bdw-item-short">{item.short}</div>
                      {item.reason && <div className="bdw-item-reason">{item.reason}</div>}
                      <div className="bdw-item-meta">
                        <span>Playbook: <code>{item.playbook_id}</code></span>
                        {item.requires_licensing && item.requires_licensing !== 'free' && <span>· Requires: <strong>{item.requires_licensing.toUpperCase()}</strong></span>}
                      </div>
                    </div>
                    <div className="bdw-item-actions">
                      {canPropose && (
                        <button className="admin-btn xs primary" disabled={busyItemId === item.id} onClick={function() { proposeOne(item); }}>
                          {busyItemId === item.id ? '…' : 'Propose'}
                        </button>
                      )}
                      {item.status === 'pending_proposal' && (
                        <span className="bdw-pending-link" title={'Proposal #' + item.pending_proposal_id + ' awaiting approval'}>Awaiting ◇</span>
                      )}
                      {item.status === 'pass' && <span className="bdw-tick" title="Verified deployed">✓</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </ContentBand>
  );
}

function SetupTab({ c, clientId, onNavigate }) {
  // Hooks first (Rules of Hooks — no early returns above these).
  // The fetch + render are gated on the user's actual role, not the URL.
  // A client-role user logging into the main / URL has IS_CLIENT_VIEW=false
  // but is still not an admin — we must NOT call /api/admin/* for them.
  var admin = isAdminRole();
  var [config, setConfig] = useState(null);
  var [configLoading, setConfigLoading] = useState(admin);
  var [configErr, setConfigErr] = useState(null);

  useEffect(function() {
    if (!admin) return;
    setConfigLoading(true); setConfigErr(null);
    authFetch(API + '/admin/clients/' + clientId + '/config')
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setConfigLoading(false);
        if (x.ok) setConfig(x.d);
        else setConfigErr(x.d.error || 'Failed to load client config');
      })
      .catch(function() { setConfigLoading(false); setConfigErr('Failed to load client config'); });
  }, [clientId, admin]);

  // Hard guard: this view is admin-only by design. Returns null for
  // ANY non-admin user — both client-role users on the main / URL
  // (where IS_CLIENT_VIEW would be false) and client portal viewers.
  // Hooks above are unconditional per Rules of Hooks.
  if (!admin) return null;

  if (configLoading) return <div className="tab-content"><SkeletonLoader /></div>;
  if (configErr) return <div className="tab-content"><div className="admin-banner error">{configErr}</div></div>;

  // Use the fetched config (full integration fields) — fall back to
  // the constructed `c` for cmmc / name / etc. if needed.
  var liveCfg = config || c || {};
  var isCmmc = !!liveCfg.cmmc;
  var legacyNinja = isOnLegacyNinjaOne(liveCfg);
  // Compute status only for integrations applicable to this tier.
  // CMMC clients drop Huntress + Blumira; commercial drop Sentinel.
  var applicable = getApplicableIntegrations(liveCfg);
  var statuses = applicable.map(function(def) {
    return Object.assign({}, getIntegrationStatus(liveCfg, def.kind), { kind: def.kind, def: def });
  });
  var connected = statuses.filter(function(s) { return s.status === 'connected' || s.status === 'configured'; }).length;
  var inProgress = statuses.filter(function(s) { return s.status === 'consent_granted' || s.status === 'pending_consent'; }).length;
  var pending = statuses.filter(function(s) { return s.status === 'not_started'; }).length;
  var totalApplicable = statuses.length;
  var pct = totalApplicable > 0 ? Math.round((connected / totalApplicable) * 100) : 0;

  // Priority-ordered onboarding sequence. Each entry references an
  // integration kind; we filter to only those applicable to this
  // client's tier (CMMC drops Huntress, commercial drops Sentinel).
  var rawSequence = [
    { kind: 'identity',                                priority: 1, why: 'Foundational. Drives the Identity tab + cross-checks for compliance + sign-in audit.' },
    { kind: 'endpoints',                               priority: 1, why: 'NinjaOne FedRAMP/Gov backs the Posture, Tickets, and remediation surfaces. Commercial NinjaOne is being deprecated; all clients are migrating to FedRAMP regardless of CMMC status.' },
    { kind: 'edr',                                     priority: 2, why: 'Huntress drives the Threat score on commercial clients. CMMC clients run Atomus-managed EDR — wired separately, not visible in Aurelius.' },
    { kind: isCmmc ? 'siem-cmmc' : 'siem-commercial', priority: 2, why: 'SIEM tier — Sentinel for CMMC clients (Atomus-coordinated), Blumira for commercial.' },
    { kind: 'network',                                 priority: 3, why: 'UniFi gateway / switch / AP / camera health.' },
    { kind: 'cloud-cost',                              priority: 3, why: 'Azure VM monitoring + cost data. RBAC reader on the subscription, tied to the per-tenant Graph cert.' },
    { kind: 'projects',                                priority: 4, why: 'Asana project tracking + per-engagement progress.' },
    { kind: 'devops',                                  priority: 4, why: 'Jira issue + sprint health (DevOps clients only).' },
    { kind: 'billing',                                 priority: 4, why: 'Pax8 vendor billing — Microsoft + Azure + Atlassian.' }
  ];
  var sequence = rawSequence.filter(function(step) {
    var def = INTEGRATION_DEFINITIONS.find(function(d) { return d.kind === step.kind; });
    // Tier filter uses liveCfg (which carries cmmc:bool from server)
    // for consistency with the rest of the SetupTab — `c` may also
    // have it but liveCfg is the canonical source post-fetch.
    return isIntegrationApplicable(def, liveCfg);
  });
  // Group steps by priority for visual sectioning.
  var groupLabels = { 1: 'Foundational', 2: 'Security signal', 3: 'Operations', 4: 'Workflow' };
  var groups = [1, 2, 3, 4].map(function(p) {
    return { priority: p, label: groupLabels[p], steps: sequence.filter(function(s) { return s.priority === p; }) };
  });

  function jumpToIntegration(def) {
    gotoAdminIntegrationsForClient(clientId, def);
  }

  return (
    <div className="tab-content setup-tab">
      <TabDescription text={'Workspace setup for ' + (liveCfg.name || (c && c.name) || 'this client') + (isCmmc ? ' (CMMC tier — Atomus-led security stack)' : ' (commercial tier)') + '. Walk down the list to bring each integration online — every tab unlocks as soon as its integration is connected.'} />

      <div className="setup-progress">
        <div className="setup-progress-head">
          <div>
            <div className="setup-progress-eyebrow">Getting started · {isCmmc ? 'CMMC tier' : 'Commercial tier'}</div>
            <div className="setup-progress-title">Welcome to {liveCfg.name || (c && c.name) || 'this client'}&rsquo;s workspace</div>
            {isCmmc && (
              <div className="setup-tier-note">
                Showing the CMMC integration set. Huntress (commercial EDR) and Blumira (commercial SIEM) don&rsquo;t apply — Atomus runs the EDR, and Microsoft Sentinel handles the SIEM tier.
              </div>
            )}
          </div>
          <div className="setup-progress-stats">
            <div className="setup-progress-pct">{pct}<span>%</span></div>
            <div className="setup-progress-counts">
              <span className="setup-stat ok"><strong>{connected}</strong> connected</span>
              {inProgress > 0 && <span className="setup-stat warn"><strong>{inProgress}</strong> in progress</span>}
              <span className="setup-stat muted"><strong>{pending}</strong> remaining</span>
            </div>
          </div>
        </div>
        <div className="setup-progress-bar"><div className="setup-progress-fill" style={{ width: pct + '%' }} /></div>
      </div>

      {legacyNinja && (
        <div className="admin-banner warn setup-migration-banner">
          <strong>NinjaOne migration in progress.</strong> This client is wired on commercial NinjaOne (<code>ninjaone_org_id</code>) but not yet on FedRAMP/Gov (<code>fedramp_org_name</code>). All clients are migrating to FedRAMP regardless of CMMC status — commercial NinjaOne is being deprecated. Open admin Integrations and add the FedRAMP org name to complete the migration.
        </div>
      )}

      {groups.map(function(group) {
        if (group.steps.length === 0) return null;
        return (
          <div key={group.priority} className="setup-group">
            <div className="setup-group-eyebrow">{group.priority}. {group.label}</div>
            <div className="setup-group-list">
              {group.steps.map(function(step) {
                // Use liveCfg (the fetched safe-config from /api/admin/clients/:id/config)
                // — NOT the constructed `c` prop. `c` doesn't carry integration fields
                // like ninjaone_org_id / tenant_id / etc., so calling
                // getIntegrationStatus(c, ...) here was returning 'not_started' for
                // every card even when the progress meter above (which already used
                // liveCfg) correctly showed 8/9 connected. Same root cause as the
                // earlier 0/9 bug, just in the per-card render path.
                var status = getIntegrationStatus(liveCfg, step.kind);
                var def = status.def;
                if (!def) return null;
                var done = (status.status === 'connected' || status.status === 'configured');
                var stepInProgress = (status.status === 'consent_granted' || status.status === 'pending_consent');
                var stepIcon = done ? '✓' : stepInProgress ? '◐' : '○';
                var stepClass = 'setup-step ' + (done ? 'setup-step-done' : stepInProgress ? 'setup-step-progress' : 'setup-step-pending');
                return (
                  <div key={step.kind} className={stepClass}>
                    <span className="setup-step-num">{stepIcon}</span>
                    <div className="setup-step-body">
                      <div className="setup-step-head">
                        <span className="setup-step-icon" aria-hidden="true">{def.icon}</span>
                        <span className="setup-step-vendor">{def.vendor}</span>
                        <span className={'setup-step-status setup-step-status-' + status.status}>
                          {status.status === 'connected'        ? 'Connected' :
                           status.status === 'configured'       ? 'Configured' :
                           status.status === 'consent_granted'  ? 'Consent granted' :
                           status.status === 'pending_consent'  ? 'Awaiting consent' : 'Not connected'}
                        </span>
                      </div>
                      <div className="setup-step-why">{step.why}</div>
                    </div>
                    <div className="setup-step-actions">
                      {!done && (
                        <button
                          type="button"
                          className={'admin-btn sm ' + (status.status === 'not_started' ? 'primary' : '')}
                          onClick={function() { jumpToIntegration(def); }}
                        >
                          {stepInProgress ? 'Finish' : 'Connect'}
                        </button>
                      )}
                      {done && onNavigate && (
                        <button
                          type="button"
                          className="admin-btn sm"
                          onClick={function() {
                            // Navigate to the tab this integration powers.
                            // Best-effort mapping; fall back to overview.
                            var target = ({
                              identity: 'identity', endpoints: 'posture', edr: 'security',
                              'siem-commercial': 'siem', 'siem-cmmc': 'siem',
                              network: 'network', 'cloud-cost': 'budget',
                              projects: 'projects', devops: 'devops', billing: 'budget'
                            })[step.kind] || 'overview';
                            onNavigate(target);
                          }}
                        >
                          View →
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Guided onboarding wizard launch — promoted prominently above
          the inline baseline so operators don't have to discover the
          guided flow. */}
      {liveCfg.tenant_id && liveCfg.graph_client_id && (
        <SetupOnboardingCta clientId={clientId} clientName={liveCfg.name || c.name} />
      )}

      {/* Baseline deployment wizard — only render once Entra is wired
          (no point evaluating a baseline against an unconnected tenant).
          For tenants where the integration is partway through, the
          wizard's own loading + error states handle the not-ready cases. */}
      {liveCfg.tenant_id && liveCfg.graph_client_id && (
        <BaselineDeploymentWizard clientId={clientId} />
      )}

      <div className="setup-footer">
        <div><strong>Need help?</strong></div>
        <ul>
          <li>SSO + Graph setup walkthrough: <code>docs/connect-entra-flow.md</code></li>
          <li>Full scope disclosure: open <strong>Admin → Permissions</strong> for what each Aurelius feature reads (and what we deliberately don&rsquo;t)</li>
          <li>Per-tenant Graph app cert exchange is a manual step — the client GA registers the app, uploads the public cert, and hands the app_id back. The graph_client_id field captures it.</li>
        </ul>
      </div>
    </div>
  );
}

// IntegrationEmptyState — replaces ad-hoc EmptyStateAction usage on
// data tabs that depend on a specific integration. Shows the data tab
// is gated by a missing wiring + offers a clear next step:
//   - admin viewer: "Open admin Integrations →" button
//   - client viewer: "Contact CloudByte Group" static text
//
// Pass the integration kind. Optional cmmc-aware hint when the client
// is on the CMMC tier and the commercial-tier integration is missing.
function IntegrationEmptyState({ kind, clientId, isCmmc, extraDetail }) {
  var def = INTEGRATION_DEFINITIONS.find(function(d) { return d.kind === kind; });
  if (!def) return <EmptyStateAction icon="🔌" title="Integration not connected" text="This data view requires an integration that isn&rsquo;t configured for this client yet." cta="Contact CloudByte Group to enable" />;
  // ADMIN gate based on user role, not URL. A client-role user logging
  // into the main / URL has IS_CLIENT_VIEW=false but is still not an
  // admin — they should see "Contact CloudByte Group", not a Connect
  // button that would 403 against /api/admin/* endpoints.
  var isAdmin = isAdminRole();
  // CMMC-aware swap: if the kind is the commercial-tier integration
  // and the client is on the CMMC tier, suggest the CMMC alternative.
  var suggestedDef = def;
  if (isCmmc && def.cmmc_alternative) {
    var alt = INTEGRATION_DEFINITIONS.find(function(d) { return d.kind === def.cmmc_alternative; });
    if (alt) suggestedDef = alt;
  }
  return (
    <div className="empty-state-action integration-empty-state">
      <div className="empty-state-icon">{suggestedDef.icon}</div>
      <div className="empty-state-title">{suggestedDef.vendor} not connected</div>
      <div className="empty-state-text">
        {suggestedDef.description}
        {extraDetail && <div style={{ marginTop: 8, opacity: 0.85 }}>{extraDetail}</div>}
      </div>
      {isAdmin ? (
        <div className="empty-state-cta">
          <button
            type="button"
            className="admin-btn primary"
            onClick={function() { gotoAdminIntegrationsForClient(clientId, suggestedDef); }}
          >
            Connect {suggestedDef.vendor} →
          </button>
          <div className="integration-empty-state-hint">
            Opens the admin Integrations tab and auto-opens this client&rsquo;s Edit modal.
          </div>
        </div>
      ) : (
        <div className="empty-state-cta">Contact CloudByte Group to enable {suggestedDef.vendor}.</div>
      )}
    </div>
  );
}

// IntegrationsHealthSummary — checklist of every integration's status
// for one client. Rendered at the top of the Edit Client modal so the
// admin sees the full picture at a glance before they start clicking
// through the long form.
function IntegrationsHealthSummary({ form, clientName }) {
  if (!form) return null;
  // For each integration applicable to this client's tier, compute
  // status from the in-progress form fields. CMMC clients drop
  // Huntress + Blumira; commercial clients drop Sentinel.
  var statuses = getApplicableIntegrations(form).map(function(def) {
    return getIntegrationStatus(form, def.kind);
  });
  // Group: connected / partial (entra mid-consent) / not_started
  var connected = statuses.filter(function(s) { return s.status === 'connected' || s.status === 'configured'; });
  var pending   = statuses.filter(function(s) { return s.status === 'consent_granted' || s.status === 'pending_consent'; });
  var notStarted = statuses.filter(function(s) { return s.status === 'not_started'; });
  return (
    <div className="integrations-health-summary">
      <div className="integrations-health-summary-head">
        <span className="integrations-health-summary-eyebrow" title="Workspace coverage counts conceptual integration areas (identity / endpoints / EDR / SIEM / network / etc.), tier-filtered. The 'wired vendors' count on the admin client list is a different lens — it counts underlying vendor mappings, which can exceed the area count because some areas have multiple vendors.">Workspace coverage</span>
        <span className="integrations-health-summary-stats">
          <span className="ihs-stat ihs-stat-ok"><strong>{connected.length}</strong> of {statuses.length} connected</span>
          {pending.length > 0 && <span className="ihs-stat ihs-stat-warn"><strong>{pending.length}</strong> in progress</span>}
          <span className="ihs-stat ihs-stat-muted"><strong>{notStarted.length}</strong> not connected</span>
        </span>
      </div>
      <div className="integrations-health-summary-grid">
        {statuses.map(function(s) {
          var statusLabel =
            s.status === 'connected'        ? 'Connected' :
            s.status === 'configured'       ? 'Configured' :
            s.status === 'consent_granted'  ? 'Consent granted' :
            s.status === 'pending_consent'  ? 'Awaiting consent' :
            s.status === 'not_started'      ? 'Not connected' : s.status;
          return (
            <div key={s.kind} className={'ihs-row ihs-row-' + s.status}>
              <span className="ihs-row-icon" aria-hidden="true">{s.def.icon}</span>
              <span className="ihs-row-vendor">{s.def.vendor}</span>
              <span className={'ihs-row-status ihs-row-status-' + s.status}>{statusLabel}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OverviewTab({ c, live, clientId, onNavigate }) {
  const [history, setHistory] = useState([]);
  const [summaries, setSummaries] = useState({});
  const [summaryLoading, setSummaryLoading] = useState(true);

  // Fetch trend history
  useEffect(function() {
    if (!clientId) return;
    authFetch(API + '/clients/' + clientId + '/history?days=30')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(data) {
        if (data && data.entries) {
          setHistory(data.entries.map(function(e) {
            return {
              date: e.date,
              overall: e.scores ? e.scores.overall : null,
              endpoint: e.scores ? e.scores.endpoint : null,
              threat: e.scores ? e.scores.threat : null,
              total: e.endpoints ? e.endpoints.total : null,
              healthy: e.endpoints ? e.endpoints.healthy : null,
              critical: e.endpoints ? e.endpoints.critical : null,
              activeThreats: e.threats ? e.threats.active : null,
              agents: e.huntress_agents ? e.huntress_agents.total : null,
            };
          }));
        }
      }).catch(function() {});
  }, [clientId]);

  // Fetch summaries from all areas
  useEffect(function() {
    if (!clientId) return;
    setSummaryLoading(true);
    Promise.all([
      authFetch(API + '/clients/' + clientId + '/network').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
      authFetch(API + '/clients/' + clientId + '/tickets').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
      authFetch(API + '/clients/' + clientId + '/projects').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
      authFetch(API + '/clients/' + clientId + '/subscriptions').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
      authFetch(API + '/clients/' + clientId + '/changes').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
    ]).then(function(results) {
      setSummaries({ network: results[0], tickets: results[1], projects: results[2], budget: results[3], changes: results[4] });
      setSummaryLoading(false);
    });
  }, [clientId]);

  var net = summaries.network && summaries.network.enabled ? summaries.network.summary : null;
  var tix = summaries.tickets && summaries.tickets.metrics ? summaries.tickets.metrics : null;
  var proj = summaries.projects && summaries.projects.projects ? summaries.projects.projects : null;
  var budget = summaries.budget && summaries.budget.enabled ? summaries.budget.summary : null;
  var changes = summaries.changes && summaries.changes.summary ? summaries.changes.summary : null;

  var projTotalTasks = proj ? proj.reduce(function(s, p) { return s + p.totalTasks; }, 0) : 0;
  var projCompleted = proj ? proj.reduce(function(s, p) { return s + p.completedTasks; }, 0) : 0;
  var projOverdue = proj ? proj.reduce(function(s, p) { return s + p.overdueTasks; }, 0) : 0;
  var projProgress = projTotalTasks > 0 ? Math.round((projCompleted / projTotalTasks) * 100) : 0;

  // SIEM ring only shows a score once Huntress Managed SIEM is actually
  // ingesting (score > 0); otherwise it renders neutral "not configured"
  // rather than a misleading red 0 during onboarding.
  var siemSummary = c.scores.siem > 0 ? {
    total: (c.scores.siem === 95 ? 0 : c.scores.siem === 85 ? '< 5' : '5+'),
  } : null;

  return (
    <div className="tab-content">
      <TabDescription text="Executive summary of your security posture, operations, and project status. Scores are calculated from live endpoint health, threat activity, credential hygiene, and SIEM coverage." />
      <DataFreshness live={true} />
      {/* AI Proposals card was removed from Overview to keep this tab focused
          on the executive summary. Pending proposals now surface via a
          TopBar badge (◇ glyph + count) that opens the ProposalsDrawer
          from any tab. The card still renders contextually inside the
          Compliance tab as a workspace home for CMMC operators. */}
      <HealthVerdict score={c.scores.overall} context={
        c.scores.overall >= 90 ? 'All security domains are performing well. No immediate action required.' :
        c.scores.overall >= 75 ? 'Most areas are healthy, but ' + (c.scores.endpoint < 75 ? 'endpoint health' : c.scores.threat < 75 ? 'active threats' : c.scores.credential < 75 ? 'credential hygiene' : 'SIEM coverage') + ' needs attention.' :
        c.scores.overall >= 50 ? 'Multiple security areas need review. Recommend scheduling a remediation call.' :
        'Critical deficiencies detected across multiple domains. Immediate action recommended.'
      } />
      {/* Score Rings */}
      <div className="scores-row">
        <ScoreRing score={c.scores.overall} label="Overall" hero={true} />
        <ScoreRing score={c.scores.endpoint} label="Endpoint" />
        <ScoreRing score={c.scores.threat} label="Threat" />
        <ScoreRing score={c.scores.credential} label="Credential" />
        <ScoreRing score={siemSummary ? c.scores.siem : null} label="SIEM" />
      </div>

      {/* 30-Day Trends */}
      {history.length > 0 && (
        <CollapsibleSection title="30-Day Trends" defaultOpen={true}>
          <div className="sparklines-grid">
            <TrendSparkline data={history} dataKey="overall" color="#60a5fa" label="Overall Score" domain={[0, 100]} suffix="%" />
            <TrendSparkline data={history} dataKey="endpoint" color="#22c55e" label="Endpoint Score" domain={[0, 100]} suffix="%" />
            <TrendSparkline data={history} dataKey="healthy" color="#22c55e" label="Healthy Endpoints" />
            <TrendSparkline data={history} dataKey="critical" color="#ef4444" label="Unreachable" invertDelta={true} />
            <TrendSparkline data={history} dataKey="activeThreats" color="#f59e0b" label="Active Threats" invertDelta={true} />
            <TrendSparkline data={history} dataKey="agents" color="#a78bfa" label="Huntress Agents" />
          </div>
        </CollapsibleSection>
      )}

      {/* Security at a Glance — KPI Strip */}
      <KPIStrip items={[
        { label: 'Total Endpoints', value: c.endpoints.total, trend: computeTrend(history, 'total'), subtitle: 'Devices under management', live: live.ninjaone, onClick: onNavigate ? function() { onNavigate('posture'); } : undefined },
        { label: 'Online Now', value: c.endpoints.online || 0, trend: computeTrend(history, 'healthy'), accent: '#22c55e', subtitle: c.endpoints.total > 0 ? Math.round(((c.endpoints.online || 0) / c.endpoints.total) * 100) + '% of fleet' : '', live: live.ninjaone },
        { label: 'Needs Attention', value: c.endpoints.stale || c.endpoints.critical || 0, trend: computeTrend(history, 'critical'), accent: (c.endpoints.stale || c.endpoints.critical || 0) > 0 ? '#ef4444' : '#22c55e', subtitle: (c.endpoints.stale || c.endpoints.critical || 0) > 0 ? 'Not seen in 7+ days — may need remediation' : 'All devices reporting normally', live: live.ninjaone, onClick: (c.endpoints.stale || c.endpoints.critical || 0) > 0 && onNavigate ? function() { onNavigate('posture'); } : undefined },
        { label: 'Active Threats', value: c.threats.active, trend: computeTrend(history, 'activeThreats'), accent: c.threats.active > 0 ? '#ef4444' : '#22c55e', subtitle: c.threats.active > 0 ? 'Requires immediate investigation' : 'No active threats detected', live: live.huntress, onClick: c.threats.active > 0 && onNavigate ? function() { onNavigate('security'); } : undefined },
      ].concat(c.huntress_agents ? [{ label: 'EDR Agents', value: c.huntress_agents.total, trend: computeTrend(history, 'agents'), subtitle: c.huntress_agents.online + ' active in last 24h (' + (c.huntress_agents.total > 0 ? Math.round((c.huntress_agents.online / c.huntress_agents.total) * 100) : 0) + '% coverage)', live: live.huntress }] : [])} />

      {/* Fleet Status — Segment Bar */}
      <ContentBand title="Fleet Distribution" subtitle={c.endpoints.total + ' managed devices'} rightContent={onNavigate ? <button className="tab-drill-link" onClick={function() { sessionStorage.setItem('aurelius_tab_entry', 'posture:devices'); onNavigate('posture'); }}>View All Devices ›</button> : null}>
        <SegmentBar size="lg" segments={[
          { label: 'Online', value: c.endpoints.online || 0, color: '#22c55e', onClick: onNavigate ? function() { onNavigate('posture'); } : undefined },
          { label: 'Off-Hours', value: c.endpoints.offHours || 0, color: '#64748b' },
          { label: 'Needs Attention', value: c.endpoints.stale || c.endpoints.critical || 0, color: '#ef4444', onClick: (c.endpoints.stale || c.endpoints.critical || 0) > 0 && onNavigate ? function() { onNavigate('posture'); } : undefined },
        ]} />
      </ContentBand>

      {/* Operations — Metric List */}
      <ContentBand title="Operations" subtitle="Infrastructure, support, and change management">
        {(net || tix || changes || summaryLoading) ? (
          <MetricList>
            {net && <MetricListItem icon="◉" iconColor="#22c55e" label="Network Infrastructure" sublabel={net.offline > 0 ? net.offline + ' currently powered off' : 'All devices active'} value={net.online + ' / ' + net.total} badge={net.firmware_outdated > 0 ? net.firmware_outdated + ' outdated firmware' : null} badgeColor={net.firmware_outdated > 0 ? '#f59e0b' : null} onClick={onNavigate ? function() { onNavigate('network'); } : undefined} />}
            {tix && <MetricListItem icon="◎" iconColor={tix.waitingForSupport > 0 ? '#f59e0b' : '#22c55e'} label="Support Tickets" sublabel={tix.waitingForSupport > 0 ? tix.waitingForSupport + ' waiting for support' : 'None waiting'} value={tix.open} trail={tix.open > 0 ? 'open' : null} onClick={onNavigate ? function() { onNavigate('support'); } : undefined} />}
            {changes && (function() { var changeTotal = changes.submitted + changes.under_review + changes.approved + changes.in_progress; return <MetricListItem icon="△" iconColor={changes.submitted > 0 ? '#f59e0b' : '#64748b'} label="Change Requests" sublabel={changes.submitted > 0 ? changes.submitted + ' awaiting review' : 'None pending'} value={changeTotal} onClick={onNavigate ? function() { onNavigate('changes'); } : undefined} />; })()}
            {summaryLoading && !net && !tix && !changes && <MetricListItem label="Loading operations data..." sublabel="Fetching from integrations" />}
          </MetricList>
        ) : (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '8px 0' }}>No operations data configured yet.</div>
        )}
      </ContentBand>

      {/* Projects & Budget — Metric List */}
      {(proj || budget) && (
        <ContentBand title="Projects & Budget" subtitle="Active work and spend tracking">
          <MetricList>
            {proj && proj.length > 0 && (
              <MetricListItem icon="◆" iconColor="#a78bfa" label={'Active Projects — ' + proj.length} sublabel={projOverdue > 0 ? projOverdue + ' overdue tasks' : 'On track'} value={<MicroBar value={projCompleted} max={projTotalTasks} color={projProgress >= 75 ? '#22c55e' : projProgress >= 50 ? '#f59e0b' : '#ef4444'} label={projProgress + '%'} />} onClick={onNavigate ? function() { onNavigate('projects'); } : undefined} />
            )}
            {budget && (
              <>
                <MetricListItem icon="$" iconColor="#5b9ef4" label="Estimated Monthly Spend" sublabel={budget.totalSubscriptions + ' active subscriptions'} value={'$' + Math.round(budget.totalMonthlyCost).toLocaleString()} valueColor="#5b9ef4" onClick={onNavigate ? function() { onNavigate('budget'); } : undefined} />
              </>
            )}
          </MetricList>
        </ContentBand>
      )}
    </div>
  );
}
// ── Identity Tab (Entra / Graph) ──────────────────────────────────
// ── Identity Components ───────────────────────────────────────────
function IdentityUserTiles({ summary, users }) {
  var u = summary.users;
  var disabledWithLicense = (users || []).filter(function(x) { return !x.enabled && x.licensed; }).length;
  var licensedPct = u.total > 0 ? Math.round((u.licensed / u.total) * 100) : 0;
  var guestPct = u.total > 0 ? Math.round((u.guests / u.total) * 100) : 0;
  return (
    <div className="metric-tiles">
      <div className="metric-tile metric-tile-good">
        <div className="metric-tile-label">Enabled</div>
        <div className="metric-tile-value">{u.enabled || 0}</div>
        <div className="metric-tile-context">Active identities</div>
      </div>
      <div className={'metric-tile' + (disabledWithLicense > 0 ? ' metric-tile-alert' : '')}>
        <div className="metric-tile-label">Disabled</div>
        <div className="metric-tile-value">{u.disabled || 0}</div>
        <div className="metric-tile-context">{disabledWithLicense > 0 ? disabledWithLicense + ' still consuming licenses' : 'No license waste'}</div>
      </div>
      <div className={'metric-tile' + (guestPct > 20 ? ' metric-tile-alert' : '')}>
        <div className="metric-tile-label">Guests</div>
        <div className="metric-tile-value">{u.guests || 0}</div>
        <div className="metric-tile-context">{guestPct}% of directory</div>
      </div>
      <div className="metric-tile">
        <div className="metric-tile-label">Licensed</div>
        <div className="metric-tile-value">{u.licensed || 0}</div>
        <div className="metric-tile-context">{licensedPct}% of directory</div>
      </div>
    </div>
  );
}

function IdentityDeviceTiles({ summary }) {
  var d = summary.devices;
  var compliantPct = d.total > 0 ? Math.round((d.compliant / d.total) * 100) : 0;
  var managedPct = d.total > 0 ? Math.round((d.managed / d.total) * 100) : 0;
  var nonCompliant = Math.max(0, (d.total || 0) - (d.compliant || 0));
  var osParts = [];
  if (d.windows) osParts.push(d.windows + ' Win');
  if (d.mac) osParts.push(d.mac + ' Mac');
  if (d.mobile) osParts.push(d.mobile + ' Mobile');
  return (
    <div className="metric-tiles">
      <div className={'metric-tile ' + (compliantPct >= 90 ? 'metric-tile-good' : compliantPct < 70 ? 'metric-tile-alert' : '')}>
        <div className="metric-tile-label">Compliant</div>
        <div className="metric-tile-value">{compliantPct}<span className="metric-tile-unit">%</span></div>
        <div className="metric-tile-context">{d.compliant || 0} of {d.total || 0} devices</div>
      </div>
      <div className={'metric-tile' + (nonCompliant > 0 ? ' metric-tile-alert' : ' metric-tile-good')}>
        <div className="metric-tile-label">Non-Compliant</div>
        <div className="metric-tile-value">{nonCompliant}</div>
        <div className="metric-tile-context">{nonCompliant > 0 ? 'May be blocked by conditional access' : 'All policies met'}</div>
      </div>
      <div className={'metric-tile' + (managedPct < 80 && d.total > 0 ? ' metric-tile-alert' : '')}>
        <div className="metric-tile-label">Managed</div>
        <div className="metric-tile-value">{managedPct}<span className="metric-tile-unit">%</span></div>
        <div className="metric-tile-context">{d.managed || 0} enrolled in MDM</div>
      </div>
      <div className="metric-tile">
        <div className="metric-tile-label">Enrolled</div>
        <div className="metric-tile-value">{d.total || 0}</div>
        <div className="metric-tile-context">{osParts.length > 0 ? osParts.join(' · ') : 'No devices'}</div>
      </div>
    </div>
  );
}

function GapAnalysisTiles({ gapAnalysis, onNavigate }) {
  var entraOnly = gapAnalysis.entraOnly.length;
  var ninjaOnly = gapAnalysis.ninjaOnly.length;
  var matched = Math.max(0, Math.min(gapAnalysis.entraTotal, gapAnalysis.ninjaTotal) - Math.max(entraOnly, ninjaOnly));
  var tiles = [
    { key: 'matched', label: 'Matched', sublabel: 'In both Entra + NinjaOne', count: matched, alert: false, clickable: false },
    { key: 'entra', label: 'Entra Only', sublabel: entraOnly > 0 ? 'Missing RMM agent — deploy NinjaOne' : 'Full coverage', count: entraOnly, alert: entraOnly > 0, clickable: entraOnly > 0 },
    { key: 'ninja', label: 'NinjaOne Only', sublabel: ninjaOnly > 0 ? 'Missing Entra enrollment — check AAD join' : 'Full coverage', count: ninjaOnly, alert: ninjaOnly > 0, clickable: ninjaOnly > 0 }
  ];
  return (
    <div className="fleet-tiles">
      {tiles.map(function(t) {
        var isAlertState = t.alert && t.count > 0;
        var classes = 'ticket-flow-stage ticket-flow-stage-gap-' + t.key +
          (t.count === 0 ? ' ticket-flow-stage-empty' : '') +
          (t.clickable && onNavigate ? ' ticket-flow-stage-clickable' : '') +
          (isAlertState ? ' ticket-flow-stage-aging' : '');
        return (
          <div key={t.key} className={classes} onClick={t.clickable && onNavigate ? function() { onNavigate('gaps'); } : undefined}>
            <div className="ticket-flow-stage-label">{t.label}</div>
            <div className="ticket-flow-stage-count">{t.count}</div>
            <div className="ticket-flow-stage-sub">{t.sublabel}</div>
          </div>
        );
      })}
    </div>
  );
}

function IdentityTab({ clientId, live }) {
  // Parse landing-context hint (e.g. from Red Team drawer click on a named user or device)
  // Supported keys:
  //   identity:users:<name>     -> open users view, seed search with name
  //   identity:devices[:<name>] -> open devices view, optional search seed
  //   identity:intune           -> open intune view
  //   identity:gaps             -> open gaps view
  var identityHint = sessionStorage.getItem('aurelius_tab_entry');
  var initialView = 'summary';
  var initialUserSearch = '';
  var initialDeviceSearch = '';
  var hintBanner = null;
  if (identityHint && identityHint.indexOf('identity:') === 0) {
    var parts = identityHint.split(':');
    if (parts[1] === 'users') { initialView = 'users'; if (parts[2]) { initialUserSearch = parts[2]; hintBanner = { kind: 'user', name: parts[2] }; } }
    else if (parts[1] === 'devices') { initialView = 'devices'; if (parts[2]) { initialDeviceSearch = parts[2]; hintBanner = { kind: 'device', name: parts[2] }; } }
    else if (parts[1] === 'intune') { initialView = 'intune'; }
    else if (parts[1] === 'gaps') { initialView = 'gaps'; }
    sessionStorage.removeItem('aurelius_tab_entry');
  }
  const [entra, setEntra] = useState(null);
  const [posture, setPosture] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(initialView);
  const [arrivalBanner, setArrivalBanner] = useState(hintBanner);
  var userSort = useTableSort('name', 'asc');
  var deviceSort = useTableSort('name', 'asc');
  var intuneSort = useTableSort('name', 'asc');
  var gapEntraSort = useTableSort('name', 'asc');
  var gapNinjaSort = useTableSort('name', 'asc');
  const [userSearch, setUserSearch] = useState(initialUserSearch);
  const [deviceSearch, setDeviceSearch] = useState(initialDeviceSearch);
  const [intuneSearch, setIntuneSearch] = useState('');
  const [userFilters, setUserFilters] = useState({});
  const [deviceFilters, setDeviceFilters] = useState({});
  const [intuneFilters, setIntuneFilters] = useState({});
  var [userPage, setUserPage] = useState(0);
  var [devicePage, setDevicePage] = useState(0);
  var [intunePage, setIntunePage] = useState(0);
  useEffect(function() {
    setLoading(true);
    setEntra(null);
    setPosture(null);
    Promise.all([
      authFetch(API + '/clients/' + clientId + '/entra').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
      authFetch(API + '/clients/' + clientId + '/posture').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
    ]).then(function(results) {
      setEntra(results[0]);
      setPosture(results[1]);
      setLoading(false);
    });
  }, [clientId]);

  if (loading) return <div className="tab-content"><SkeletonLoader /></div>;
  if (!entra || !entra.summary) return <div className="tab-content"><IntegrationEmptyState kind="identity" clientId={clientId} /></div>;

  var s = entra.summary;
  var enabledPct = s.users.total > 0 ? Math.round((s.users.enabled / s.users.total) * 100) : 0;
  var managedPct = s.devices.total > 0 ? Math.round((s.devices.managed / s.devices.total) * 100) : 0;
  var compliantPct = s.devices.total > 0 ? Math.round((s.devices.compliant / s.devices.total) * 100) : 0;
  var encryptedPct = s.intune.total > 0 ? Math.round((s.intune.encrypted / s.intune.total) * 100) : 0;

  var gapAnalysis = null;
  if (posture && posture.devices && entra.devices) {
    var ninjaNames = posture.devices.map(function(d) { return (d.name || '').toLowerCase(); });
    var entraNames = entra.devices.map(function(d) { return (d.name || '').toLowerCase(); });
    var inEntraNotNinja = entra.devices.filter(function(d) {
      var name = (d.name || '').toLowerCase();
      return name && !ninjaNames.some(function(n) { return n === name || name.includes(n) || n.includes(name); });
    });
    var inNinjaNotEntra = posture.devices.filter(function(d) {
      var name = (d.name || '').toLowerCase();
      return name && !entraNames.some(function(n) { return n === name || name.includes(n) || n.includes(name); });
    });
    gapAnalysis = {
      entraOnly: inEntraNotNinja,
      ninjaOnly: inNinjaNotEntra,
      entraTotal: entra.devices.length,
      ninjaTotal: posture.devices.length,
    };
  }

  return (
    <div className="tab-content">
      {arrivalBanner && (
        <div className="arrival-banner">
          <span className="arrival-banner-badge">FROM RED TEAM</span>
          <span className="arrival-banner-text">
            {arrivalBanner.kind === 'user'
              ? <>You're investigating <strong>{arrivalBanner.name}</strong> — surfaced by a Red Team simulation. Filtered below.</>
              : <>You're investigating <strong>{arrivalBanner.name}</strong> — involved in a Red Team simulation. Filtered below.</>}
          </span>
          <button className="arrival-banner-close" onClick={function() { setArrivalBanner(null); }}>&#10005;</button>
        </div>
      )}
      {view === 'summary' && (
        <>
          <TabDescription text="User and device identity management across your Microsoft Entra tenant. Includes compliance status, device enrollment, and gap analysis between Entra and your RMM agent." />
          <DataFreshness live={true} />
          {/* Tenant-licensing banner — surfaces when the tenant's tier
              blocks meaningful Identity / posture coverage. Driven by
              the licensing probe (run via admin Verify connection). */}
          {entra && entra.licensing && entra.licensing.tier === 'free' && (
            <div className="admin-banner warn" style={{ marginBottom: 12, fontSize: 12, padding: '10px 14px' }}>
              <strong>Tenant on Entra ID Free.</strong> Several identity-posture checks (last sign-in, conditional access policies, sign-in-risk, user-risk) require Entra ID P1+. They will show as <em>"requires Entra ID P1"</em> below until the tenant is upgraded to Microsoft 365 Business Premium / E3 / E5.
              {!entra.licensing.has_intune && <span> Intune device-compliance checks also require an Intune license.</span>}
            </div>
          )}
          <ContentBand title="User Directory" subtitle={s.users.total + ' identities in Microsoft Entra'} rightContent={<><span className="live-badge">LIVE</span><button className="tab-drill-link" onClick={function() { setView('users'); }}>Users ›</button><button className="tab-drill-link" onClick={function() { setView('devices'); }}>Devices ›</button>{entra.managedDevices && entra.managedDevices.length > 0 && <button className="tab-drill-link" onClick={function() { setView('intune'); }}>Intune ›</button>}{gapAnalysis && (gapAnalysis.entraOnly.length > 0 || gapAnalysis.ninjaOnly.length > 0) && <button className="tab-drill-link" onClick={function() { setView('gaps'); }}>Gaps ›</button>}</>}>
            <IdentityUserTiles summary={s} users={entra.users} />
          </ContentBand>
          <ContentBand title="Device Compliance — Entra ID" subtitle={s.devices.total + ' enrolled devices'}>
            <IdentityDeviceTiles summary={s} />
          </ContentBand>
          <ContentBand title="Intune Management">
            <MetricList>
              <MetricListItem icon="◈" iconColor="#22c55e" label="Intune Managed Devices" sublabel={s.intune.total + ' enrolled'} value={<MicroBar value={encryptedPct} max={100} color={encryptedPct >= 90 ? '#22c55e' : encryptedPct >= 70 ? '#f59e0b' : '#ef4444'} label={encryptedPct + '% encrypted'} />} />
              <MetricListItem icon="◎" iconColor={s.intune.nonCompliant > 0 ? '#ef4444' : '#22c55e'} label="Compliance Status" sublabel={s.intune.compliant + ' compliant · ' + s.intune.nonCompliant + ' non-compliant'} value={s.intune.nonCompliant > 0 ? s.intune.nonCompliant : '✓'} valueColor={s.intune.nonCompliant > 0 ? '#ef4444' : '#22c55e'} />
            </MetricList>
          </ContentBand>
          {gapAnalysis && (
            <ContentBand title="Device Gap Analysis — Entra vs NinjaOne" subtitle="Systems should match — gaps mean missing coverage">
              <GapAnalysisTiles gapAnalysis={gapAnalysis} onNavigate={setView} />
            </ContentBand>
          )}
          <IdentityPostureSection clientId={clientId} />
          {/* Proposals card was removed in favour of the TopBar bell affordance —
              clicking Propose on a finding above triggers the badge to appear in
              the header so the operator can act from any tab. */}
        </>
      )}
      {view === 'users' && (function() {
        var userAccessors = { name: function(u) { return u.name; }, email: function(u) { return u.email || ''; }, status: function(u) { return u.enabled ? 'Enabled' : 'Disabled'; }, licensed: function(u) { return u.licensed ? 'Yes' : 'No'; }, type: function(u) { return u.type; } };
        var searchedUsers = applyFilters(
          filterRows(entra.users, userSearch, [function(u) { return u.name; }, function(u) { return u.email || ''; }]),
          userFilters,
          { status: function(u){return u.enabled ? 'Enabled' : 'Disabled';}, type: function(u){return u.type;}, licensed: function(u){return u.licensed ? 'Yes' : 'No';} }
        );
        var sortedUsers = sortRows(searchedUsers, userSort.sortField, userSort.sortDir, userAccessors);
        var enabledCount = entra.users.filter(function(u){return u.enabled;}).length;
        var disabledCount = entra.users.length - enabledCount;
        var memberCount = entra.users.filter(function(u){return u.type==='Member';}).length;
        var guestCount = entra.users.filter(function(u){return u.type==='Guest';}).length;
        var uPageSize = 25;
        var uTotalPages = Math.max(1, Math.ceil(sortedUsers.length / uPageSize));
        var uSafePage = Math.min(userPage, uTotalPages - 1);
        var pagedUsers = sortedUsers.slice(uSafePage * uPageSize, (uSafePage + 1) * uPageSize);
        return (
        <>
          <div className="section-title">
            All Users ({entra.users.length})
            <button className="insights-btn" style={{ marginLeft: 12, verticalAlign: 'middle' }} onClick={function() { setView('summary'); }}>Back</button>
          </div>
          <TableSearch value={userSearch} onChange={function(v) { setUserSearch(v); setUserPage(0); }} count={sortedUsers.length} total={entra.users.length} noun="users" onExport={function() { exportTableCSV(['Name','Email','Status','Licensed','Type'], sortedUsers.map(function(u) { return [u.name, u.email, u.enabled ? 'Enabled' : 'Disabled', u.licensed ? 'Yes' : 'No', u.type]; }), 'entra-users'); }} />
          <TableFilters filters={[
            { key: 'status', label: 'Status', options: [
              { value: 'Enabled', label: 'Enabled', color: '#22c55e', count: enabledCount },
              { value: 'Disabled', label: 'Disabled', color: '#64748b', count: disabledCount },
            ]},
            { key: 'type', label: 'Type', options: [
              { value: 'Member', label: 'Member', count: memberCount },
              { value: 'Guest', label: 'Guest', count: guestCount },
            ]},
            { key: 'licensed', label: 'Licensed', options: [
              { value: 'Yes', label: 'Licensed', color: '#22c55e' },
              { value: 'No', label: 'Unlicensed', color: '#64748b' },
            ]},
          ]} active={userFilters} onChange={function(f) { setUserFilters(f); setUserPage(0); }} />
          <div className="posture-table-wrap">
            <table className="posture-table">
              <thead><tr><SortTh field="name" current={userSort.sortField} dir={userSort.sortDir} onSort={userSort.toggleSort}>Name</SortTh><SortTh field="email" current={userSort.sortField} dir={userSort.sortDir} onSort={userSort.toggleSort}>Email</SortTh><SortTh field="status" current={userSort.sortField} dir={userSort.sortDir} onSort={userSort.toggleSort}>Status</SortTh><SortTh field="licensed" current={userSort.sortField} dir={userSort.sortDir} onSort={userSort.toggleSort}>Licensed</SortTh><SortTh field="type" current={userSort.sortField} dir={userSort.sortDir} onSort={userSort.toggleSort}>Type</SortTh></tr></thead>
              <tbody>
                {pagedUsers.length === 0 && <tr><td colSpan="5" className="table-no-results">No users match your search</td></tr>}
                {pagedUsers.map(function(u, i) {
                  return (
                    <tr key={i} className={!u.enabled ? 'posture-row-offline' : ''}>
                      <td className="posture-device-name" title={u.name}><span className={'status-dot ' + (u.enabled ? 'status-dot-good' : 'status-dot-muted')} />{u.name}</td>
                      <td title={u.email || ''}>{u.email || '—'}</td>
                      <td><span className={'posture-status ' + (u.enabled ? 'posture-online' : 'posture-offline')}>{u.enabled ? 'Enabled' : 'Disabled'}</span></td>
                      <td>{u.licensed ? 'Yes' : 'No'}</td>
                      <td>{u.type}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination page={uSafePage} totalPages={uTotalPages} onPageChange={setUserPage} totalItems={sortedUsers.length} startIndex={uSafePage * uPageSize} endIndex={Math.min((uSafePage + 1) * uPageSize, sortedUsers.length)} />
        </>
      );
      })()}
      {view === 'devices' && (function() {
        var da = { name: function(d) { return d.name; }, registeredOwner: function(d) { return d.registeredOwner || ''; }, os: function(d) { return d.os || ''; }, compliant: function(d) { return d.compliant === true ? 'Yes' : 'No'; }, trustType: function(d) { return d.trustType || ''; }, lastSignIn: function(d) { return d.lastSignIn ? new Date(d.lastSignIn).getTime() : 0; } };
        var searched = applyFilters(
          filterRows(entra.devices, deviceSearch, [function(d) { return d.name; }, function(d) { return d.os; }, function(d) { return d.registeredOwner; }]),
          deviceFilters,
          { os: function(d){return (d.os||'').split(' ')[0];}, compliant: function(d){return d.compliant===true?'Yes':d.compliant===false?'No':'Unknown';} }
        );
        var sorted = sortRows(searched, deviceSort.sortField, deviceSort.sortDir, da);
        var osGroups = {}; entra.devices.forEach(function(d){var o=(d.os||'Unknown').split(' ')[0]; osGroups[o]=(osGroups[o]||0)+1;});
        var dPageSize = 25;
        var dTotalPages = Math.max(1, Math.ceil(sorted.length / dPageSize));
        var dSafePage = Math.min(devicePage, dTotalPages - 1);
        var pagedDevices = sorted.slice(dSafePage * dPageSize, (dSafePage + 1) * dPageSize);
        return (
        <>
          <div className="section-title">
            Entra Devices ({entra.devices.length})
            <button className="insights-btn" style={{ marginLeft: 12, verticalAlign: 'middle' }} onClick={function() { setView('summary'); }}>Back</button>
          </div>
          <TableSearch value={deviceSearch} onChange={function(v) { setDeviceSearch(v); setDevicePage(0); }} count={sorted.length} total={entra.devices.length} noun="devices" onExport={function() { exportTableCSV(['Device','User','OS','Version','Compliant','Managed','Trust Type','Last Sign-In'], sorted.map(function(d) { return [d.name, d.registeredOwner || '', d.os || '', d.osVersion || '', d.compliant === true ? 'Yes' : d.compliant === false ? 'No' : '', d.managed === true ? 'Yes' : d.managed === false ? 'No' : '', d.trustType || '', d.lastSignIn ? new Date(d.lastSignIn).toLocaleDateString() : '']; }), 'entra-devices'); }} />
          <TableFilters filters={[
            { key: 'os', label: 'OS', options: Object.entries(osGroups).sort(function(a,b){return b[1]-a[1];}).map(function([o,c]) { return { value: o, label: o, count: c }; }) },
            { key: 'compliant', label: 'Compliance', options: [
              { value: 'Yes', label: 'Compliant', color: '#22c55e' },
              { value: 'No', label: 'Non-Compliant', color: '#ef4444' },
            ]},
          ]} active={deviceFilters} onChange={function(f) { setDeviceFilters(f); setDevicePage(0); }} />
          <div className="posture-table-wrap">
            <table className="posture-table">
              <thead><tr><SortTh field="name" current={deviceSort.sortField} dir={deviceSort.sortDir} onSort={deviceSort.toggleSort}>Device</SortTh><SortTh field="registeredOwner" current={deviceSort.sortField} dir={deviceSort.sortDir} onSort={deviceSort.toggleSort}>User</SortTh><SortTh field="os" current={deviceSort.sortField} dir={deviceSort.sortDir} onSort={deviceSort.toggleSort}>OS</SortTh><th>Version</th><SortTh field="compliant" current={deviceSort.sortField} dir={deviceSort.sortDir} onSort={deviceSort.toggleSort}>Compliant</SortTh><th>Managed</th><SortTh field="trustType" current={deviceSort.sortField} dir={deviceSort.sortDir} onSort={deviceSort.toggleSort}>Trust Type</SortTh><SortTh field="lastSignIn" current={deviceSort.sortField} dir={deviceSort.sortDir} onSort={deviceSort.toggleSort}>Last Sign-In</SortTh></tr></thead>
              <tbody>
                {pagedDevices.length === 0 && <tr><td colSpan="8" className="table-no-results">No devices match your search</td></tr>}
                {pagedDevices.map(function(d, i) {
                  var lastSignIn = d.lastSignIn ? new Date(d.lastSignIn).toLocaleDateString() : '—';
                  return (
                    <tr key={i}>
                      <td className="posture-device-name" title={d.name}>{d.name}</td>
                      <td title={d.registeredOwner || ''}>{d.registeredOwner ? d.registeredOwner.split('@')[0] : '—'}</td>
                      <td>{d.os || '—'}</td>
                      <td>{d.osVersion || '—'}</td>
                      <td><span className={'posture-av-state posture-av-' + (d.compliant === true ? 'on' : d.compliant === false ? 'off' : 'unknown')}>{d.compliant === true ? 'Yes' : d.compliant === false ? 'No' : '—'}</span></td>
                      <td>{d.managed === true ? 'Yes' : d.managed === false ? 'No' : '—'}</td>
                      <td>{d.trustType || '—'}</td>
                      <td className="posture-date">{lastSignIn}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination page={dSafePage} totalPages={dTotalPages} onPageChange={setDevicePage} totalItems={sorted.length} startIndex={dSafePage * dPageSize} endIndex={Math.min((dSafePage + 1) * dPageSize, sorted.length)} />
        </>
      );
      })()}
      {view === 'intune' && entra.managedDevices && (function() {
        var ia = { name: function(d) { return d.name; }, os: function(d) { return d.os || ''; }, compliance: function(d) { return d.compliance || ''; }, encrypted: function(d) { return d.encrypted === true ? 'Yes' : 'No'; }, user: function(d) { return d.user || ''; }, model: function(d) { return d.model || ''; }, lastSync: function(d) { return d.lastSync ? new Date(d.lastSync).getTime() : 0; } };
        var searched = filterRows(entra.managedDevices, intuneSearch, [function(d) { return d.name; }, function(d) { return d.user; }, function(d) { return d.model; }]);
        var sorted = sortRows(searched, intuneSort.sortField, intuneSort.sortDir, ia);
        var iPageSize = 25;
        var iTotalPages = Math.max(1, Math.ceil(sorted.length / iPageSize));
        var iSafePage = Math.min(intunePage, iTotalPages - 1);
        var pagedIntune = sorted.slice(iSafePage * iPageSize, (iSafePage + 1) * iPageSize);
        return (
        <>
          <div className="section-title">
            Intune Managed Devices ({entra.managedDevices.length})
            <button className="insights-btn" style={{ marginLeft: 12, verticalAlign: 'middle' }} onClick={function() { setView('summary'); }}>Back</button>
          </div>
          <TableSearch value={intuneSearch} onChange={function(v) { setIntuneSearch(v); setIntunePage(0); }} count={sorted.length} total={entra.managedDevices.length} noun="devices" onExport={function() { exportTableCSV(['Device','OS','Compliance','Encrypted','User','Model','Last Sync'], sorted.map(function(d) { return [d.name, d.os || '', d.compliance || '', d.encrypted === true ? 'Yes' : d.encrypted === false ? 'No' : '', d.user || '', d.model || '', d.lastSync ? new Date(d.lastSync).toLocaleDateString() : '']; }), 'intune-devices'); }} />
          <div className="posture-table-wrap">
            <table className="posture-table">
              <thead><tr><SortTh field="name" current={intuneSort.sortField} dir={intuneSort.sortDir} onSort={intuneSort.toggleSort}>Device</SortTh><SortTh field="os" current={intuneSort.sortField} dir={intuneSort.sortDir} onSort={intuneSort.toggleSort}>OS</SortTh><SortTh field="compliance" current={intuneSort.sortField} dir={intuneSort.sortDir} onSort={intuneSort.toggleSort}>Compliance</SortTh><SortTh field="encrypted" current={intuneSort.sortField} dir={intuneSort.sortDir} onSort={intuneSort.toggleSort}>Encrypted</SortTh><SortTh field="user" current={intuneSort.sortField} dir={intuneSort.sortDir} onSort={intuneSort.toggleSort}>User</SortTh><SortTh field="model" current={intuneSort.sortField} dir={intuneSort.sortDir} onSort={intuneSort.toggleSort}>Model</SortTh><SortTh field="lastSync" current={intuneSort.sortField} dir={intuneSort.sortDir} onSort={intuneSort.toggleSort}>Last Sync</SortTh></tr></thead>
              <tbody>
                {pagedIntune.length === 0 && <tr><td colSpan="7" className="table-no-results">No devices match your search</td></tr>}
                {pagedIntune.map(function(d, i) {
                  var lastSync = d.lastSync ? new Date(d.lastSync).toLocaleDateString() : '—';
                  return (
                    <tr key={i}>
                      <td className="posture-device-name" title={d.name}>{d.name}</td>
                      <td>{d.os || '—'}</td>
                      <td><span className={'posture-av-state posture-av-' + (d.compliance === 'compliant' ? 'on' : d.compliance === 'noncompliant' ? 'off' : 'unknown')}>{d.compliance || '—'}</span></td>
                      <td><span className={'posture-av-state posture-av-' + (d.encrypted === true ? 'on' : d.encrypted === false ? 'off' : 'unknown')}>{d.encrypted === true ? 'Yes' : d.encrypted === false ? 'No' : '—'}</span></td>
                      <td title={d.user || ''}>{d.user || '—'}</td>
                      <td>{d.model || '—'}</td>
                      <td className="posture-date">{lastSync}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination page={iSafePage} totalPages={iTotalPages} onPageChange={setIntunePage} totalItems={sorted.length} startIndex={iSafePage * iPageSize} endIndex={Math.min((iSafePage + 1) * iPageSize, sorted.length)} />
        </>
      );
      })()}
      {view === 'gaps' && gapAnalysis && (
        <>
          <div className="section-title">
            Device Gap Analysis
            <button className="insights-btn" style={{ marginLeft: 12, verticalAlign: 'middle' }} onClick={function() { setView('summary'); }}>Back</button>
          </div>
          {gapAnalysis.entraOnly.length > 0 && (
            <>
              <div className="section-title">In Entra but NOT in NinjaOne ({gapAnalysis.entraOnly.length})</div>
              <p className="gap-description">These devices are registered in Microsoft Entra but have no NinjaOne RMM agent.</p>
              <div className="posture-table-wrap">
                <table className="posture-table">
                  <thead><tr><SortTh field="name" current={gapEntraSort.sortField} dir={gapEntraSort.sortDir} onSort={gapEntraSort.toggleSort}>Device</SortTh><th>User</th><SortTh field="os" current={gapEntraSort.sortField} dir={gapEntraSort.sortDir} onSort={gapEntraSort.toggleSort}>OS</SortTh><th>Managed</th><th>Compliant</th><SortTh field="trustType" current={gapEntraSort.sortField} dir={gapEntraSort.sortDir} onSort={gapEntraSort.toggleSort}>Trust Type</SortTh><th>Last Sign-In</th></tr></thead>
                  <tbody>
                    {sortRows(gapAnalysis.entraOnly, gapEntraSort.sortField, gapEntraSort.sortDir, { name: function(d) { return d.name; }, os: function(d) { return d.os || ''; }, trustType: function(d) { return d.trustType || ''; } }).map(function(d, i) {
                      return (
                        <tr key={i}>
                          <td className="posture-device-name">{d.name}</td>
                          <td title={d.registeredOwner || ''}>{d.registeredOwner ? d.registeredOwner.split('@')[0] : '—'}</td>
                          <td>{d.os || '—'}</td>
                          <td>{d.managed === true ? 'Yes' : 'No'}</td>
                          <td>{d.compliant === true ? 'Yes' : 'No'}</td>
                          <td style={{ fontSize: 12 }}>{d.trustType || '—'}</td>
                          <td className="posture-date">{d.lastSignIn ? new Date(d.lastSignIn).toLocaleDateString() : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {gapAnalysis.ninjaOnly.length > 0 && (
            <>
              <div className="section-title">In NinjaOne but NOT in Entra ({gapAnalysis.ninjaOnly.length})</div>
              <p className="gap-description">These devices have the NinjaOne RMM agent but are not registered in Microsoft Entra.</p>
              <div className="posture-table-wrap">
                <table className="posture-table">
                  <thead><tr><SortTh field="name" current={gapNinjaSort.sortField} dir={gapNinjaSort.sortDir} onSort={gapNinjaSort.toggleSort}>Device</SortTh><SortTh field="type" current={gapNinjaSort.sortField} dir={gapNinjaSort.sortDir} onSort={gapNinjaSort.toggleSort}>Type</SortTh><SortTh field="status" current={gapNinjaSort.sortField} dir={gapNinjaSort.sortDir} onSort={gapNinjaSort.toggleSort}>Status</SortTh><th>Antivirus</th><th>Last Contact</th></tr></thead>
                  <tbody>
                    {sortRows(gapAnalysis.ninjaOnly, gapNinjaSort.sortField, gapNinjaSort.sortDir, { name: function(d) { return d.name; }, type: function(d) { return d.nodeClass || ''; }, status: function(d) { return d.offline ? 'Offline' : 'Online'; } }).map(function(d, i) {
                      return (
                        <tr key={i}>
                          <td className="posture-device-name">{d.name}</td>
                          <td>{(d.nodeClass || '').replace('WINDOWS_', '').replace('_', ' ')}</td>
                          <td><span className={'posture-status ' + (d.offline ? 'posture-offline' : 'posture-online')}>{d.offline ? 'Offline' : 'Online'}</span></td>
                          <td>{d.av ? d.av.product : 'None'}</td>
                          <td className="posture-date">{d.lastContact ? new Date(d.lastContact * 1000).toLocaleDateString() : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {gapAnalysis.entraOnly.length === 0 && gapAnalysis.ninjaOnly.length === 0 && (
            <div className="empty-state">No device gaps detected.</div>
          )}
        </>
      )}
    </div>
  );
}

// ── Keeper Tab (dedicated credential management view) ────────────
function KeeperTab({ clientId }) {
  return (
    <div className="tab-content">
      <TabDescription text="Credential and secret management via Keeper. Tracks vault enrollment, password hygiene, shared folder governance, and enforcement policies across every user and team." />
      <DataFreshness live={true} />
      <KeeperVaultSection clientId={clientId} />
    </div>
  );
}

// ── Red Team Tab (continuous adversarial simulation) ──────────────
// ── Red Team — wrapper with sub-tabs (Phase 1 restructure) ─────────
// Splits the previously-monolithic Red Team workspace into:
//   • Overview        — the existing executive narrative (last night's
//                       run, breach rehearsal score, what got through,
//                       attack categories). Rendered as a vision /
//                       always-on snapshot.
//   • Engagements     — pen-test engagement lifecycle (scoping → ROE →
//                       executing → reporting → debrief → closed).
//                       Real, stateful, audit-trailed.
//   • Attack Surface  — Phase 2: live identity attack-path analysis,
//                       gated on an active executing engagement.
//   • Settings        — engagement defaults + ROE template references.
//
// The Attack Surface sub-tab is hidden when there is no active
// engagement — enforces the "no live testing without authorization"
// principle structurally (UI can't even invoke the endpoint).
function RedTeamTab({ clientId, onNavigate }) {
  var [subTab, setSubTab] = useState('overview');
  var [engagements, setEngagements] = useState(null);
  var [activeEngagement, setActiveEngagement] = useState(null);
  var [refreshTick, setRefreshTick] = useState(0);
  var admin = isAdminRole() && !IS_CLIENT_VIEW;

  function refreshEngagements() {
    if (!admin) return;
    authFetch(API + '/clients/' + clientId + '/redteam/engagements')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) {
        if (!d) { setEngagements([]); setActiveEngagement(null); return; }
        setEngagements(d.engagements || []);
        var active = (d.engagements || []).find(function(e) { return e.id === d.active_engagement_id; });
        setActiveEngagement(active || null);
      })
      .catch(function() { setEngagements([]); setActiveEngagement(null); });
  }
  useEffect(refreshEngagements, [clientId, refreshTick]);  // eslint-disable-line react-hooks/exhaustive-deps

  var executing = activeEngagement && activeEngagement.status === 'executing';

  // Top-level Red Team sub-tabs — S1.10 visual unification. Matches the
  // engagement workspace nav aesthetic: clean horizontal tabs, subtle
  // group labels, active-state purple underline, no heavy icon glyphs.
  // Linear / Figma / Notion convention rather than the older
  // compliance-subtab CSS class treatment.
  function renderSubtab(key, label, opts) {
    var active = subTab === key;
    var badge = opts && opts.badge;
    return (
      <button key={key} type="button" onClick={function() { setSubTab(key); }} style={{ background: 'none', border: 'none', padding: '8px 14px', borderBottom: active ? '2px solid #a78bfa' : '2px solid transparent', color: active ? '#fff' : '#94a3b8', fontWeight: 600, cursor: 'pointer', fontSize: 11, letterSpacing: '0.4px', textTransform: 'uppercase' }}>
        {label}
        {badge}
      </button>
    );
  }

  return (
    <div className="tab-content">
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '1px solid rgba(255,255,255,0.06)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.8px', color: '#475569', textTransform: 'uppercase', padding: '0 8px', alignSelf: 'center', marginBottom: 8 }}>Activity</div>
        {renderSubtab('overview', 'Overview')}
        {admin && renderSubtab('engagements', 'Engagements', activeEngagement ? { badge: <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: 'rgba(167,139,250,0.15)', color: '#a78bfa', letterSpacing: '0.4px' }}>{activeEngagement.status.toUpperCase().replace(/_/g, ' ')}</span> } : null)}
        {admin && <div style={{ width: 1, alignSelf: 'stretch', background: 'rgba(255,255,255,0.06)', margin: '0 6px' }}></div>}
        {admin && <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.8px', color: '#475569', textTransform: 'uppercase', padding: '0 8px', alignSelf: 'center', marginBottom: 8 }}>Configuration</div>}
        {admin && renderSubtab('catalog', 'Catalog')}
        {admin && renderSubtab('phishing', 'Phishing')}
        {admin && renderSubtab('settings', 'Settings')}
      </div>

      {subTab === 'overview' && <RedTeamOverview clientId={clientId} onNavigate={onNavigate} activeEngagement={activeEngagement} onSwitchToEngagements={function() { setSubTab('engagements'); }} />}
      {subTab === 'engagements' && admin && <RedTeamEngagements clientId={clientId} engagements={engagements} onChange={function() { setRefreshTick(refreshTick + 1); }} />}
      {subTab === 'catalog' && admin && <RedTeamCatalog clientId={clientId} />}
      {subTab === 'phishing' && admin && <RedTeamPhishing clientId={clientId} />}
      {subTab === 'settings' && admin && <RedTeamSettings clientId={clientId} />}
    </div>
  );
}

// Renamed from RedTeamTab — preserves the existing always-on overview
// narrative as a sub-tab. Adds an engagement banner at the top when an
// engagement is active so the operator knows live analysis is available.
function RedTeamOverview({ clientId, onNavigate, activeEngagement, onSwitchToEngagements }) {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [detailId, setDetailId] = useState(null);
  useEffect(function() {
    setLoading(true); setData(null);
    authFetch(API + '/clients/' + clientId + '/redteam')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) { setData(d); setLoading(false); })
      .catch(function() { setLoading(false); });
  }, [clientId]);

  var openDetail = function(attackId) { setDetailId(attackId); };
  var closeDetail = function() { setDetailId(null); };

  // Convert a Red Team recommendation into a pre-filled Change Request
  var convertToChangeRequest = function(rec, sourceFinding) {
    var prefill = rec.change_request_prefill;
    if (!prefill) return;
    sessionStorage.setItem('aurelius_change_prefill', JSON.stringify({
      title: prefill.title,
      description: prefill.description,
      type: prefill.type,
      category: prefill.category,
      priority: prefill.priority,
      reference: 'Red Team finding ' + (sourceFinding || rec.id),
      impact_summary: prefill.impact_summary,
      source: 'redteam',
      source_id: sourceFinding || rec.id
    }));
    if (onNavigate) onNavigate('changes');
  };

  // Navigate to a user in Identity tab with the user name in search
  var goToUser = function(userName) {
    sessionStorage.setItem('aurelius_tab_entry', 'identity:users:' + userName);
    if (onNavigate) onNavigate('identity');
  };

  // Navigate to a device in Posture tab with the device name in search
  var goToDevice = function(deviceName) {
    sessionStorage.setItem('aurelius_tab_entry', 'posture:devices:' + deviceName);
    if (onNavigate) onNavigate('posture');
  };

  if (loading) return <div className="tab-content"><SkeletonLoader /></div>;
  if (!data || !data.enabled) {
    // No demo data, but if there's an active engagement we want to point
    // the operator at it instead of pretending pen-testing isn't available.
    if (activeEngagement) {
      return (
        <div className="tab-content">
          <EmptyStateAction
            icon="⚔"
            title={'Active engagement: ' + activeEngagement.name}
            text={'Status: ' + activeEngagement.status + '. Live simulation snapshots aren\'t generated yet for this client — but the Engagements and Attack Surface sub-tabs above are functional. Open the engagement to track lifecycle and run live attack-path analysis.'}
            cta={
              <button type="button" className="qbr-generate-btn" style={{ flex: 'none', padding: '10px 24px', fontSize: 12 }} onClick={onSwitchToEngagements}>
                Open engagement →
              </button>
            }
          />
        </div>
      );
    }
    return (
      <div className="tab-content">
        <EmptyStateAction
          icon="⚔"
          title="Red Team Not Activated"
          text="Aurelius Red Team runs adversarial simulations against your environment — phishing, credential attacks, lateral movement, data exfiltration, and supply-chain compromise. Live identity attack-path analysis is available once an engagement is in 'executing' status."
          cta={
            <button type="button" className="qbr-generate-btn" style={{ flex: 'none', padding: '10px 24px', fontSize: 12 }} onClick={onSwitchToEngagements}>
              Start an engagement →
            </button>
          }
        />
      </div>
    );
  }

  var s = data.summary;
  var br = data.breach_rehearsal;
  var crit = data.critical_event;
  function timeAgoLabel(iso) { var diff = Date.now() - new Date(iso).getTime(); var h = Math.floor(diff / 3600000); if (h < 1) return Math.floor(diff / 60000) + 'm ago'; if (h < 24) return h + 'h ago'; return Math.floor(h / 24) + 'd ago'; }
  function timeUntilLabel(iso) { var diff = new Date(iso).getTime() - Date.now(); var h = Math.floor(diff / 3600000); var m = Math.floor((diff % 3600000) / 60000); if (h < 0) return 'running now'; return h + 'h ' + m + 'm'; }
  var outcomeStyle = { blocked: { color: '#22c55e', label: 'Blocked' }, partial: { color: '#f59e0b', label: 'Partial' }, successful: { color: '#ef4444', label: 'Got Through' } };
  var stageStyle = { succeeded: { color: '#ef4444', label: 'Succeeded', dot: '●' }, detected: { color: '#f59e0b', label: 'Detected', dot: '◐' }, blocked: { color: '#22c55e', label: 'Blocked', dot: '■' }, simulated: { color: '#a78bfa', label: 'Simulated', dot: '◇' } };
  var priorityStyle = { high: { color: '#ef4444', label: 'High Priority' }, medium: { color: '#f59e0b', label: 'Medium' }, low: { color: '#64748b', label: 'Low' } };

  return (
    <div className="tab-content">
      <TabDescription text="Aurelius Red Team runs continuous autonomous adversarial simulations against your environment — phishing, credential attacks, lateral movement, data exfiltration, and supply-chain compromise. Every morning: here's exactly what almost got through." />
      <DataFreshness live={true} />

      {/* Hero — Last Run */}
      <ContentBand title="Last Night's Rehearsal" subtitle={'Completed ' + timeAgoLabel(data.last_run) + ' · Next run in ' + timeUntilLabel(data.next_run)} rightContent={<span className="live-badge red-team-live">● ACTIVE</span>}>
        <div className="metric-tiles">
          <div className="metric-tile metric-tile-good">
            <div className="metric-tile-label">Attacks Blocked</div>
            <div className="metric-tile-value">{s.blocked}<span className="metric-tile-unit"> / {s.total_attacks_24h}</span></div>
            <div className="metric-tile-context">Stopped at earliest kill-chain stage</div>
          </div>
          <div className={'metric-tile' + (s.partial > 0 ? ' metric-tile-alert' : '')}>
            <div className="metric-tile-label">Partial Success</div>
            <div className="metric-tile-value">{s.partial}</div>
            <div className="metric-tile-context">Progressed further than expected</div>
          </div>
          <div className={'metric-tile' + (s.successful > 0 ? ' metric-tile-alert' : ' metric-tile-good')}>
            <div className="metric-tile-label">Got Through</div>
            <div className="metric-tile-value">{s.successful}</div>
            <div className="metric-tile-context">{s.successful > 0 ? 'See detailed breakdown below' : 'Full chain defended'}</div>
          </div>
          <div className="metric-tile">
            <div className="metric-tile-label">Attack Surface Score</div>
            <div className="metric-tile-value">{s.attack_surface_score}<span className="metric-tile-unit">/100</span></div>
            <div className="metric-tile-context">{s.total_simulations_30d} simulations in last 30 days</div>
          </div>
        </div>
      </ContentBand>

      {/* Breach Rehearsal Score */}
      <ContentBand title="Breach Rehearsal Score" subtitle="Estimated wall-clock time for a sophisticated attacker to reach full compromise">
        <div className="redteam-rehearsal">
          <div className="redteam-rehearsal-hero">
            <div className="redteam-rehearsal-value">{br.estimated_time_to_compromise_hours}<span className="redteam-rehearsal-unit">h</span></div>
            <div className="redteam-rehearsal-label">Time to full compromise</div>
            <div className="redteam-rehearsal-trend">
              <span className="redteam-trend-icon redteam-trend-down">↓</span>
              <span>{Math.abs(br.delta_pct)}% harder than last quarter ({br.previous_quarter_hours}h)</span>
            </div>
          </div>
          <div className="redteam-rehearsal-benchmark">
            <div className="redteam-benchmark-row">
              <span className="redteam-benchmark-label">You</span>
              <div className="redteam-benchmark-bar"><div className="redteam-benchmark-bar-fill redteam-benchmark-you" style={{ width: Math.min(100, (br.estimated_time_to_compromise_hours / 6) * 100) + '%' }} /></div>
              <span className="redteam-benchmark-value">{br.estimated_time_to_compromise_hours}h</span>
            </div>
            <div className="redteam-benchmark-row">
              <span className="redteam-benchmark-label">Peer Median</span>
              <div className="redteam-benchmark-bar"><div className="redteam-benchmark-bar-fill redteam-benchmark-peer" style={{ width: Math.min(100, (br.peer_benchmark_hours / 6) * 100) + '%' }} /></div>
              <span className="redteam-benchmark-value">{br.peer_benchmark_hours}h</span>
            </div>
            <div className="redteam-benchmark-note">You're in the <strong>{br.peer_quartile} quartile</strong> for tech-services firms 50-100 employees · Longer is better (harder to compromise)</div>
          </div>
        </div>
      </ContentBand>

      {/* Critical Event — What Got Through */}
      <ContentBand title="What Almost Got Through" subtitle="Click for the full breakdown — plain English, step-by-step, with named users and remediation scripts">
        <div className="redteam-critical redteam-critical-clickable" onClick={function() { openDetail(crit.id); }}>
          <div className="redteam-critical-header">
            <div>
              <div className="redteam-critical-type">{crit.type_label}</div>
              <div className="redteam-critical-meta">Target: <button className="redteam-entity-link" onClick={function(e) { e.stopPropagation(); goToUser(crit.target_user); }}>{crit.target_user}</button> ({crit.target_role}) · Device: <button className="redteam-entity-link" onClick={function(e) { e.stopPropagation(); goToDevice(crit.target_device); }}>{crit.target_device}</button> · {timeAgoLabel(crit.time)}</div>
            </div>
            <span className="redteam-critical-outcome" style={{ background: outcomeStyle[crit.outcome].color + '18', color: outcomeStyle[crit.outcome].color, borderColor: outcomeStyle[crit.outcome].color + '40' }}>{outcomeStyle[crit.outcome].label}</span>
          </div>
          <div className="redteam-critical-lure">
            <span className="redteam-critical-lure-label">What happened</span>
            <span>{crit.lure}</span>
          </div>
          <div className="redteam-critical-narrative">{crit.narrative}</div>
          <div className="redteam-critical-cta">
            <span>Click for full breakdown, named users, step-by-step, and copy-paste remediation scripts</span>
            <span className="redteam-critical-cta-arrow">→</span>
          </div>

          {crit.training_status && (
            <div className="redteam-training-box">
              <div className="redteam-training-header">
                <span className="redteam-training-badge">Huntress MSAT</span>
                <span className="redteam-training-title">Affected user's training record</span>
              </div>
              <div className="redteam-training-grid">
                <div className="redteam-training-cell">
                  <div className="redteam-training-cell-label">Last training</div>
                  <div className="redteam-training-cell-value">{crit.training_status.last_training}</div>
                  <div className="redteam-training-cell-sub">Completed {Math.round((Date.now() - new Date(crit.training_status.last_training_date).getTime()) / 86400000)}d ago · Score {crit.training_status.last_training_score}%</div>
                </div>
                <div className="redteam-training-cell">
                  <div className="redteam-training-cell-label">Last phishing simulation</div>
                  <div className="redteam-training-cell-value redteam-training-cell-warn">{crit.training_status.last_phishing_sim_result}</div>
                  <div className="redteam-training-cell-sub">{Math.round((Date.now() - new Date(crit.training_status.last_phishing_sim_date).getTime()) / 86400000)}d ago</div>
                </div>
                <div className="redteam-training-cell">
                  <div className="redteam-training-cell-label">Overall awareness score</div>
                  <div className={'redteam-training-cell-value ' + (crit.training_status.overall_score >= 80 ? 'redteam-training-cell-good' : crit.training_status.overall_score >= 60 ? 'redteam-training-cell-warn' : 'redteam-training-cell-alert')}>{crit.training_status.overall_score}<span className="redteam-training-cell-unit">/100</span></div>
                  <div className="redteam-training-cell-sub">{crit.training_status.overall_percentile}th percentile in organization</div>
                </div>
              </div>
            </div>
          )}

          <div className="redteam-killchain-label">Kill Chain</div>
          <div className="redteam-killchain">
            {crit.kill_chain.map(function(stage, i) {
              var style = stageStyle[stage.status] || stageStyle.simulated;
              return (
                <div key={i} className="redteam-killchain-stage">
                  <div className="redteam-killchain-connector" style={i === 0 ? { visibility: 'hidden' } : {}} />
                  <div className="redteam-killchain-node" style={{ background: style.color + '20', color: style.color, borderColor: style.color }}>
                    <span className="redteam-killchain-dot">{style.dot}</span>
                  </div>
                  <div className="redteam-killchain-stage-name">{stage.stage}</div>
                  <div className="redteam-killchain-stage-status" style={{ color: style.color }}>{style.label}</div>
                  <div className="redteam-killchain-stage-detail">{stage.detail}</div>
                </div>
              );
            })}
          </div>

          <div className="redteam-recs-label">Recommended Closures</div>
          <div className="redteam-recs">
            {crit.recommendations.map(function(r, i) {
              var hasPrefill = !!r.change_request_prefill;
              return (
                <div key={i} className={'redteam-rec redteam-rec-' + r.priority}>
                  <div className="redteam-rec-header">
                    <span className="redteam-rec-priority" style={{ color: priorityStyle[r.priority].color }}>{priorityStyle[r.priority].label}</span>
                    <span className="redteam-rec-impact">Blocks {r.impact_pct}% of this vector</span>
                  </div>
                  <div className="redteam-rec-title">{r.title}</div>
                  <div className="redteam-rec-detail">{r.detail}</div>
                  <div className="redteam-rec-meta">
                    <span>Cost: <strong>{r.cost}</strong></span>
                    <span>Timeline: <strong>{r.timeline}</strong></span>
                    {hasPrefill && r.change_request_prefill.impact_summary && (
                      <>
                        <span>Users: <strong>{r.change_request_prefill.impact_summary.users_affected}</strong></span>
                        {r.change_request_prefill.impact_summary.devices_affected > 0 && <span>Devices: <strong>{r.change_request_prefill.impact_summary.devices_affected}</strong></span>}
                      </>
                    )}
                  </div>
                  {hasPrefill && (
                    <div className="redteam-rec-actions">
                      <button className="redteam-convert-btn" onClick={function() { convertToChangeRequest(r, crit.id); }}>
                        <span className="redteam-convert-btn-icon">→</span>
                        <span>Convert to Change Request</span>
                      </button>
                      <span className="redteam-convert-hint">Pre-fills impact analysis, cost, timeline</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </ContentBand>

      {/* Attack Timeline */}
      <ContentBand title="Attack Timeline" subtitle="Every simulation run in the last 24 hours — click any row for the full breakdown">
        <div className="redteam-timeline">
          {data.timeline.map(function(a) {
            var style = outcomeStyle[a.outcome] || outcomeStyle.blocked;
            return (
              <div key={a.id} className={'redteam-timeline-row redteam-timeline-clickable redteam-timeline-' + a.outcome} onClick={function() { openDetail(a.id); }}>
                <div className="redteam-timeline-time">{a.time_label}</div>
                <div className="redteam-timeline-dot" style={{ background: style.color }} />
                <div className="redteam-timeline-body">
                  <div className="redteam-timeline-type">{a.type_label}</div>
                  <div className="redteam-timeline-target">{a.target}</div>
                  <div className="redteam-timeline-result">{a.result}</div>
                </div>
                <div className="redteam-timeline-right">
                  <span className="redteam-timeline-outcome" style={{ background: style.color + '18', color: style.color }}>{style.label}</span>
                  {a.detect_source && <div className="redteam-timeline-detect">{a.detect_source}{a.detect_time_min !== null ? ' · ' + a.detect_time_min + 'm' : ''}</div>}
                  <div className="redteam-timeline-more">Details ›</div>
                </div>
              </div>
            );
          })}
        </div>
      </ContentBand>

      {/* Attack Detail Drawer */}
      <AttackDetailPanel isOpen={!!detailId} attackId={detailId} clientId={clientId} onClose={closeDetail} onNavigate={onNavigate} onConvertToCR={function(rec) { convertToChangeRequest(rec, detailId); setDetailId(null); }} goToUser={function(u) { setDetailId(null); goToUser(u); }} goToDevice={function(d) { setDetailId(null); goToDevice(d); }} />

      {/* Attack Categories */}
      <ContentBand title="Attack Categories — 7-Day Performance" subtitle="How many simulations ran per category and how many succeeded">
        <div className="redteam-categories">
          {data.categories.map(function(c) {
            var rate = c.success_rate;
            var color = rate === 0 ? '#22c55e' : rate < 15 ? '#f59e0b' : '#ef4444';
            return (
              <div key={c.id} className="redteam-category">
                <div className="redteam-category-icon" style={{ color: color, background: color + '14' }}>{c.icon}</div>
                <div className="redteam-category-body">
                  <div className="redteam-category-label">{c.label}</div>
                  <div className="redteam-category-stats">
                    <span><strong>{c.runs_7d}</strong> runs</span>
                    <span><strong>{c.successful_7d}</strong> through</span>
                    <span style={{ color: color }}><strong>{rate}%</strong> success rate</span>
                  </div>
                  <div className="redteam-category-bar">
                    <div className="redteam-category-bar-fill" style={{ width: (rate + 2) + '%', background: color }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </ContentBand>

      {/* Remediation Queue */}
      <ContentBand title="Remediation Queue" subtitle="Prioritized by expected impact against observed attack patterns">
        <div className="redteam-remediations">
          {data.remediation_queue.map(function(r, i) {
            return (
              <div key={i} className={'redteam-remediation redteam-remediation-' + r.priority}>
                <div className="redteam-remediation-priority" style={{ color: priorityStyle[r.priority].color }}>{priorityStyle[r.priority].label}</div>
                <div className="redteam-remediation-body">
                  <div className="redteam-remediation-title">{r.title}</div>
                  <div className="redteam-remediation-category">{r.category}</div>
                </div>
                <div className="redteam-remediation-meta">
                  <div className="redteam-remediation-impact"><strong>{r.blocks_pct}%</strong> blocked</div>
                  <div className="redteam-remediation-cost">{r.cost}</div>
                  <div className="redteam-remediation-timeline">{r.timeline}</div>
                </div>
              </div>
            );
          })}
        </div>
      </ContentBand>

      {/* Upcoming Attacks */}
      <ContentBand title="Scheduled Simulations" subtitle="Pre-announced so your team can observe or pause if needed">
        <MetricList>
          {data.upcoming.map(function(u, i) {
            return <MetricListItem key={i} icon="⚔" iconColor="#a78bfa" label={u.type} sublabel={'Target: ' + u.target + ' · Campaign: ' + u.campaign} value={u.when} valueColor="var(--text-primary)" />;
          })}
        </MetricList>
      </ContentBand>

      {/* 6-Week Trend */}
      <ContentBand title="6-Week Trend" subtitle="Time-to-compromise and block-rate trajectory — lower compromise time and higher block rate = stronger posture">
        <div className="redteam-trend">
          {data.trend.map(function(t, i) {
            var heightPct = Math.min(100, (t.time_to_compromise_hours / 6) * 100);
            var blockPct = t.block_rate_pct;
            var isCurrent = i === data.trend.length - 1;
            return (
              <div key={i} className={'redteam-trend-col' + (isCurrent ? ' is-current' : '')}>
                <div className="redteam-trend-bar-wrap">
                  <div className="redteam-trend-bar" style={{ height: heightPct + '%' }} />
                </div>
                <div className="redteam-trend-label">{t.time_to_compromise_hours}h</div>
                <div className="redteam-trend-sublabel">{blockPct}% blocked</div>
                <div className="redteam-trend-period">{t.period}</div>
              </div>
            );
          })}
        </div>
      </ContentBand>
    </div>
  );
}

// ── EngagementContextBar (S1.7) ─────────────────────────────────────
// Sticky workspace header that gives the operator persistent context
// no matter which engagement tab they're on. Inspired by Linear's
// project header + Cobalt's engagement dashboard top bar.
// Shows: back-to-list button, engagement name + ID, status pill,
// phase stepper, and the primary "advance phase" action.
function EngagementContextBar({ engagement, onBack, onTransition, onHelp, busy, statusLabels, statusColors, statusFlow }) {
  var idx = statusFlow.indexOf(engagement.status);
  var isActive = idx !== -1 && engagement.status !== 'closed';
  var nextStatus = isActive && idx < statusFlow.length - 1 ? statusFlow[idx + 1] : null;
  var statusColor = statusColors[engagement.status] || '#64748b';

  // Compute days remaining in window if dates set
  var daysRemaining = null;
  if (engagement.engagement_window_end) {
    var ms = new Date(engagement.engagement_window_end).getTime() - Date.now();
    daysRemaining = Math.ceil(ms / (1000 * 60 * 60 * 24));
  }

  return (
    <div style={{ background: 'rgba(167,139,250,0.04)', border: '1px solid rgba(167,139,250,0.18)', borderRadius: 10, padding: '18px 22px', marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14, flexWrap: 'wrap' }}>
        <button type="button" onClick={onBack} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', color: '#cbd5e1', padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          ← All engagements
        </button>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: statusColor, background: statusColor + '20', border: '1px solid ' + statusColor + '40', padding: '2px 8px', borderRadius: 999 }}>
              {statusLabels[engagement.status] || engagement.status}
            </span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}>RTE-{engagement.id}</span>
            {engagement.engagement_type && (
              <span style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {engagement.engagement_type.replace(/_/g, ' ')}
              </span>
            )}
            {daysRemaining !== null && daysRemaining >= 0 && (
              <span style={{ fontSize: 10, color: daysRemaining < 7 ? '#fbbf24' : '#94a3b8' }}>
                {daysRemaining} day{daysRemaining === 1 ? '' : 's'} until window ends
              </span>
            )}
            {daysRemaining !== null && daysRemaining < 0 && (
              <span style={{ fontSize: 10, color: '#f87171' }}>Window ended {Math.abs(daysRemaining)} day{Math.abs(daysRemaining) === 1 ? '' : 's'} ago</span>
            )}
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>{engagement.name}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {isActive && nextStatus && (
            <button type="button" className="admin-btn primary sm" disabled={busy} onClick={function() { onTransition(nextStatus); }}>
              → {statusLabels[nextStatus]}
            </button>
          )}
          {isActive && (
            <button type="button" className="admin-btn sm" disabled={busy} onClick={function() { onTransition('cancelled'); }}>Cancel</button>
          )}
          {onHelp && (
            <button type="button" onClick={onHelp} title="Engagement workflow help" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', color: '#cbd5e1', cursor: 'pointer', width: 30, height: 30, borderRadius: '50%', fontSize: 14, fontWeight: 700, padding: 0 }}>?</button>
          )}
        </div>
      </div>
      <PhaseStepper status={engagement.status} />
    </div>
  );
}

// ── EngagementHelpDrawer (S1.10) ───────────────────────────────────
// Slide-out help drawer replacing the older wall-of-text workflow
// guide. Notion / Stripe / Linear pattern: contextual help opens in a
// drawer on demand, not crammed into every page. Triggered by a `?`
// icon button in the Engagements list view AND in the workspace
// context bar — same drawer, two entry points.
function EngagementHelpDrawer({ open, onClose }) {

  var STEPS = [
    {
      n: 1, title: 'Build the client catalog',
      where: 'Red Team → Catalog',
      body: 'Inventory the apps, APIs, external attack surface, internal scope, cloud tenants, and SaaS providers. Use Import from Graph for Entra apps; bulk-paste IP/CIDR/domain lists for attack surface. Catalog rows survive across engagements — you build them once and reuse.'
    },
    {
      n: 2, title: 'Create the engagement',
      where: 'this tab → + New Engagement',
      body: 'Set a name; status starts as scoping. Aurelius auto-seeds the 10-item onboarding checklist and the 4 standard attestations (insurance, backup, tester E&O, tester GL). Only one engagement may be active per client at a time.'
    },
    {
      n: 3, title: 'Open the engagement workspace',
      where: 'click the engagement row',
      body: 'Clicking an engagement enters its workspace — the engagement becomes the working context with a sticky header, phase stepper, and seven tabs (Overview, Engagement record, Scope, Contacts, Readiness checklist, Attestations, Authorization). Overview lands first and tells you what is blocking the next phase transition.'
    },
    {
      n: 4, title: 'Fill the pre-engagement record',
      where: 'workspace → Engagement record tab',
      body: 'Type (vuln assessment / pen test / red team / purple team / appsec / compliance-driven), knowledge level, disclosure model, threat profiles, primary objectives, frameworks, compliance lens, exfil tolerance, testing window, hours, blackout periods.'
    },
    {
      n: 5, title: 'Pick scope from the catalog',
      where: 'workspace → Scope tab',
      body: 'Multi-select apps, APIs, attack surface, internal scope, cloud tenants, SaaS providers from the catalog. Out-of-scope assets go in the free-text field. SaaS rows show their Appendix B approval status inline — do NOT scope in a provider still in not_requested or denied.'
    },
    {
      n: 6, title: 'Assign engagement contacts',
      where: 'workspace → Contacts tab',
      body: 'Six roles. Engagement signatory (the officer who will sign the ATT letter) and primary contact are required. Out-of-band emergency is a non-corporate channel for suspected real-adversary situations.'
    },
    {
      n: 7, title: 'Capture attestations + walk the readiness checklist',
      where: 'workspace → Attestations + Readiness checklist tabs',
      body: 'Mark client insurance + backup attestations as signed once you have offline confirmation. Add a SaaS approval row per third-party SaaS in scope. Walk the 10-item readiness checklist alongside — clear items as the prerequisites land. The workspace Overview tab keeps a running count of blockers.'
    },
    {
      n: 8, title: 'Scope-lock + client signoff via magic link',
      where: 'workspace → Overview → Scope-lock → Issue magic link',
      body: 'When the readiness gate is green, click Scope-lock to freeze snapshots. Then issue a magic link from the signoff panel below. Copy the URL, send it to the engagement signatory (Outlook / Teams / out-of-band). They review the full pre-engagement record at /signoff/<token>, type-sign each required attestation + the ATT letter (no Aurelius login needed). Aurelius captures IP / user-agent / document hash for each signature. When all required signatures land, the engagement auto-transitions to ROE signed.'
    },
    {
      n: 9, title: 'Move to executing — Attack Surface tab unlocks',
      where: 'context bar → → Executing button',
      body: 'Transitions the engagement into the active testing state. The Attack Surface tab (Execution group) appears inside the workspace — live identity attack-path analysis runs on demand against the current Entra tenant. Findings persist into the Findings tab and feed Reports.'
    },
    {
      n: 10, title: 'Run scanners + triage findings',
      where: 'workspace → Scanners + Findings tabs',
      body: 'Scanners runs Aurelius-native passive recon against the in-scope attack surface — httpx probes URLs for status / headers / tech-stack leaks; Shodan (if API key configured) enriches each host with exposed-service and known-CVE data. Plus crt.sh subdomain discovery in the Catalog tab. Findings auto-populate from these scanners + Phase 2 attack-path analysis + any manual entries. Severity sort, status filter, inline triage in the Findings tab. Activity gives you the chronological audit log of everything.'
    },
    {
      n: 11, title: 'Generate deliverables',
      where: 'workspace → Reports tab',
      body: 'Per-engagement deliverables render from the engagement record — pre-engagement package PDF (coming next slice), findings export, executive summary, technical report, close-out deck. No standalone template maintenance.'
    }
  ];

  if (!open) return null;
  return (
    <div onClick={onClose} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9998, display: 'flex', justifyContent: 'flex-end' }}>
      <div onClick={function(ev) { ev.stopPropagation(); }} style={{ width: '100%', maxWidth: 560, background: '#0b1220', borderLeft: '1px solid rgba(167,139,250,0.3)', height: '100vh', overflow: 'auto', boxShadow: '-8px 0 32px rgba(0,0,0,0.4)' }}>
        <div style={{ position: 'sticky', top: 0, background: '#0b1220', padding: '18px 22px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: 10, zIndex: 1 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.6px', color: '#a78bfa', marginBottom: 2 }}>HELP</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>Engagement workflow</div>
          </div>
          <button type="button" onClick={onClose} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#cbd5e1', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '4px 10px', borderRadius: 4 }}>×</button>
        </div>
        <div style={{ padding: '20px 22px' }}>
      <div style={{ fontSize: 12, color: '#cbd5e1', marginBottom: 16, lineHeight: 1.6 }}>
        Aurelius drives pen-test engagements end-to-end. The catalog is the persistent client inventory; an engagement is a single authorized assessment window scoped against it. The workspace (entered by clicking an engagement row) is where everything per-engagement lives.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
        {STEPS.map(function(s) {
          var disabled = s.where === 'pending';
          return (
            <div key={s.n} style={{ display: 'flex', gap: 12, padding: 10, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 6, opacity: disabled ? 0.55 : 1 }}>
              <div style={{ flex: 'none', width: 26, height: 26, borderRadius: '50%', background: disabled ? 'rgba(100,116,139,0.2)' : 'rgba(167,139,250,0.2)', color: disabled ? '#94a3b8' : '#a78bfa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>{s.n}</div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 2 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{s.title}</span>
                  <span style={{ fontSize: 10, color: disabled ? '#64748b' : '#a78bfa', fontFamily: 'monospace' }}>{s.where}</span>
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.5 }}>{s.body}</div>
              </div>
            </div>
          );
        })}
      </div>
        </div>
      </div>
    </div>
  );
}

// ── RedTeamEngagements ────────────────────────────────────────────
// Pen-test engagement lifecycle UI. Real, stateful, audit-trailed.
// Operator can:
//   • Create a new engagement (form: name, scope, lead tester, ROE link)
//   • Transition status: scoping → roe_signed → executing → reporting → debrief → closed
//   • View past engagements
function RedTeamEngagements({ clientId, engagements, onChange }) {
  var [creating, setCreating] = useState(false);
  var [form, setForm] = useState({ name: '', lead_tester: '', authorization_letter_url: '', notes: '', scope: '' });
  var [busy, setBusy] = useState(false);
  var [toast, setToast] = useState(null);
  var [workspaceId, setWorkspaceId] = useState(null);
  var [helpOpen, setHelpOpen] = useState(false);

  var STATUS_FLOW = ['scoping', 'roe_signed', 'executing', 'reporting', 'debrief', 'closed'];
  var STATUS_LABELS = {
    scoping: 'Scoping',
    roe_signed: 'ROE Signed',
    executing: 'Executing',
    reporting: 'Reporting',
    debrief: 'Debrief',
    closed: 'Closed',
    cancelled: 'Cancelled'
  };
  var STATUS_COLORS = {
    scoping: '#60a5fa',
    roe_signed: '#a78bfa',
    executing: '#22c55e',
    reporting: '#f59e0b',
    debrief: '#60a5fa',
    closed: '#64748b',
    cancelled: '#94a3b8'
  };

  function showToast(tone, text) {
    setToast({ tone: tone, text: text });
    setTimeout(function() { setToast(null); }, 5000);
  }

  function handleCreate() {
    if (!form.name.trim()) { showToast('error', 'Name is required'); return; }
    setBusy(true);
    var body = {
      name: form.name.trim(),
      lead_tester: form.lead_tester.trim() || null,
      authorization_letter_url: form.authorization_letter_url.trim() || null,
      notes: form.notes.trim() || null
    };
    if (form.scope.trim()) {
      try { body.scope = JSON.parse(form.scope); }
      catch (e) { body.scope = { description: form.scope.trim() }; }
    }
    authFetch(API + '/clients/' + clientId + '/redteam/engagements', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusy(false);
        if (x.ok) {
          showToast('success', 'Engagement created (id ' + x.d.engagement_id + ')');
          setCreating(false);
          setForm({ name: '', lead_tester: '', authorization_letter_url: '', notes: '', scope: '' });
          if (onChange) onChange();
        } else {
          showToast('error', x.d.error || 'Failed to create');
        }
      })
      .catch(function() { setBusy(false); showToast('error', 'Request failed'); });
  }

  function handleTransition(engagement, newStatus) {
    var ok = window.confirm('Transition "' + engagement.name + '" to ' + (STATUS_LABELS[newStatus] || newStatus) + '?');
    if (!ok) return;
    setBusy(true);
    authFetch(API + '/clients/' + clientId + '/redteam/engagements/' + engagement.id, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus })
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusy(false);
        showToast(x.ok ? 'success' : 'error', x.ok ? 'Transitioned to ' + STATUS_LABELS[newStatus] : (x.d.error || 'Transition failed'));
        if (x.ok && onChange) onChange();
      })
      .catch(function() { setBusy(false); showToast('error', 'Request failed'); });
  }

  if (engagements === null) return <div className="tab-content"><SkeletonLoader /></div>;

  var active = engagements.find(function(e) { return ['scoping', 'roe_signed', 'executing', 'reporting', 'debrief'].indexOf(e.status) !== -1; });
  var hasActive = !!active;

  // Workspace mode — render the selected engagement as a full workspace
  // instead of the list view. PlexTrac/Cobalt pattern: an engagement is
  // a workspace once you enter it.
  if (workspaceId) {
    var workspaceEngagement = engagements.find(function(e) { return e.id === workspaceId; });
    if (!workspaceEngagement) {
      // Engagement disappeared (refresh while in workspace) — bounce back to list
      setTimeout(function() { setWorkspaceId(null); }, 0);
      return <div className="tab-content"><SkeletonLoader /></div>;
    }
    return (
      <div className="tab-content">
        {toast && <div className={'admin-banner ' + toast.tone} style={{ fontSize: 12, marginBottom: 12 }}>{toast.text}</div>}
        <EngagementContextBar
          engagement={workspaceEngagement}
          onBack={function() { setWorkspaceId(null); }}
          onTransition={function(s) { handleTransition(workspaceEngagement, s); }}
          onHelp={function() { setHelpOpen(true); }}
          busy={busy}
          statusLabels={STATUS_LABELS}
          statusColors={STATUS_COLORS}
          statusFlow={STATUS_FLOW}
        />
        <EngagementDetail
          clientId={clientId}
          engagement={workspaceEngagement}
          onSaved={function() { if (onChange) onChange(); }}
        />
        <EngagementHelpDrawer open={helpOpen} onClose={function() { setHelpOpen(false); }} />
      </div>
    );
  }

  return (
    <div className="tab-content">
      <TabDescription text="Pen-test engagement lifecycle. Each engagement bounds a single authorized assessment window with its own scope, ROE, lead tester, and audit trail. Live attack-surface analysis is gated on an executing engagement." />
      {toast && <div className={'admin-banner ' + toast.tone} style={{ fontSize: 12, marginBottom: 12 }}>{toast.text}</div>}
      <EngagementHelpDrawer open={helpOpen} onClose={function() { setHelpOpen(false); }} />

      {!creating && (
        <div style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            className="qbr-generate-btn"
            style={{ flex: 'none', padding: '10px 24px', fontSize: 12 }}
            onClick={function() { setCreating(true); }}
            disabled={hasActive}
            title={hasActive ? 'Close or cancel the active engagement before starting a new one' : ''}
          >+ New Engagement</button>
          {hasActive && <span className="muted" style={{ fontSize: 12 }}>Active engagement in flight — close it before starting another.</span>}
          <div style={{ flex: 1 }}></div>
          <button type="button" onClick={function() { setHelpOpen(true); }} title="Engagement workflow help" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', color: '#cbd5e1', cursor: 'pointer', width: 30, height: 30, borderRadius: '50%', fontSize: 14, fontWeight: 700, padding: 0 }}>?</button>
        </div>
      )}

      {creating && (
        <ContentBand title="New Engagement" subtitle="Provide scope + lead tester. Status starts as 'scoping' — advance manually as you complete each phase.">
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-lg)', padding: 24, maxWidth: 720 }}>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-muted)', marginBottom: 4, display: 'block' }}>Name *</label>
              <input style={{ background: 'var(--bg-base)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '10px 14px', color: 'var(--text-primary)', fontSize: 13, fontFamily: 'var(--font-body)', width: '100%', outline: 'none' }} value={form.name} onChange={function(e) { setForm(Object.assign({}, form, { name: e.target.value })); }} placeholder="Q2 2026 — External + Identity Pen Test" />
            </div>
            <div style={{ display: 'flex', gap: 14, marginBottom: 14 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-muted)', marginBottom: 4, display: 'block' }}>Lead Tester</label>
                <input style={{ background: 'var(--bg-base)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '10px 14px', color: 'var(--text-primary)', fontSize: 13, fontFamily: 'var(--font-body)', width: '100%', outline: 'none' }} value={form.lead_tester} onChange={function(e) { setForm(Object.assign({}, form, { lead_tester: e.target.value })); }} placeholder="Alex Voskidis" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-muted)', marginBottom: 4, display: 'block' }}>Authorization Letter URL</label>
                <input style={{ background: 'var(--bg-base)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '10px 14px', color: 'var(--text-primary)', fontSize: 13, fontFamily: 'var(--font-body)', width: '100%', outline: 'none' }} value={form.authorization_letter_url} onChange={function(e) { setForm(Object.assign({}, form, { authorization_letter_url: e.target.value })); }} placeholder="https://docs.cloudbytegroup.com/..." />
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-muted)', marginBottom: 4, display: 'block' }}>Scope (free text or JSON)</label>
              <textarea style={{ background: 'var(--bg-base)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '10px 14px', color: 'var(--text-primary)', fontSize: 13, fontFamily: 'var(--font-body)', width: '100%', outline: 'none', minHeight: 80, resize: 'vertical' }} value={form.scope} onChange={function(e) { setForm(Object.assign({}, form, { scope: e.target.value })); }} placeholder="In-scope: identity (Entra), email security, OAuth audit. Out-of-scope: production exploitation, vishing, physical." />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-muted)', marginBottom: 4, display: 'block' }}>Notes</label>
              <textarea style={{ background: 'var(--bg-base)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '10px 14px', color: 'var(--text-primary)', fontSize: 13, fontFamily: 'var(--font-body)', width: '100%', outline: 'none', minHeight: 60, resize: 'vertical' }} value={form.notes} onChange={function(e) { setForm(Object.assign({}, form, { notes: e.target.value })); }} placeholder="Any additional context (white-team contacts, scheduling notes, etc.)" />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="qbr-generate-btn" style={{ flex: 'none', padding: '10px 24px', fontSize: 12 }} disabled={busy || !form.name.trim()} onClick={handleCreate}>{busy ? 'Creating…' : 'Create Engagement'}</button>
              <button className="insights-btn" disabled={busy} onClick={function() { setCreating(false); }}>Cancel</button>
            </div>
          </div>
        </ContentBand>
      )}

      {engagements.length === 0 ? (
        <div style={{ marginTop: 16, padding: 32, background: 'rgba(167,139,250,0.04)', border: '1px solid rgba(167,139,250,0.2)', borderRadius: 12, display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ flex: 'none', width: 60, height: 60, borderRadius: '50%', background: 'rgba(167,139,250,0.15)', color: '#a78bfa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 700 }}>◈</div>
          <div style={{ flex: 1, minWidth: 280 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.6px', color: '#a78bfa', marginBottom: 4 }}>GET STARTED</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#fff', marginBottom: 8 }}>Start your first pen-test engagement</div>
            <div style={{ fontSize: 12, color: '#cbd5e1', lineHeight: 1.6, marginBottom: 16 }}>
              An engagement is a single authorized assessment window. You'll define scope by picking from the client catalog, assign contacts, capture attestations, scope-lock to freeze the authorized scope, then advance through testing → reporting → debrief.
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button type="button" className="qbr-generate-btn" style={{ flex: 'none', padding: '10px 24px', fontSize: 12 }} onClick={function() { setCreating(true); }}>+ Create first engagement</button>
              <button type="button" onClick={function() { setHelpOpen(true); }} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', color: '#cbd5e1', padding: '10px 18px', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>? Open workflow guide</button>
            </div>
          </div>
        </div>
      ) : (
        <ContentBand title="All Engagements" subtitle={engagements.length + ' engagement(s) — most recent first'}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {engagements.map(function(e) {
              var idx = STATUS_FLOW.indexOf(e.status);
              var isActive = idx !== -1 && e.status !== 'closed';
              var nextStatus = isActive && idx < STATUS_FLOW.length - 1 ? STATUS_FLOW[idx + 1] : null;
              return (
                <div key={e.id}
                  onClick={function() { setWorkspaceId(e.id); }}
                  style={{ background: 'var(--bg-card)', border: '1px solid var(--border-default)', borderLeft: '3px solid ' + (STATUS_COLORS[e.status] || '#64748b'), borderRadius: 'var(--radius-lg)', padding: 16, cursor: 'pointer', transition: 'border-color 0.15s ease' }}
                  onMouseEnter={function(ev) { ev.currentTarget.style.borderColor = 'rgba(167,139,250,0.4)'; }}
                  onMouseLeave={function(ev) { ev.currentTarget.style.borderColor = 'var(--border-default)'; }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 240 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: STATUS_COLORS[e.status] || '#64748b', background: (STATUS_COLORS[e.status] || '#64748b') + '20', border: '1px solid ' + (STATUS_COLORS[e.status] || '#64748b') + '40', padding: '3px 10px', borderRadius: 999 }}>{STATUS_LABELS[e.status] || e.status}</span>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}>RTE-{e.id}</span>
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{new Date(e.created_at).toLocaleDateString()}</span>
                      </div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{e.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        {e.lead_tester && <span>Lead: <strong style={{ color: 'var(--text-secondary)' }}>{e.lead_tester}</strong></span>}
                        {e.created_by_email && <span>Created by: <strong style={{ color: 'var(--text-secondary)' }}>{e.created_by_email}</strong></span>}
                        {e.started_at && <span>Started: <strong style={{ color: 'var(--text-secondary)' }}>{new Date(e.started_at).toLocaleDateString()}</strong></span>}
                        {e.ended_at && <span>Ended: <strong style={{ color: 'var(--text-secondary)' }}>{new Date(e.ended_at).toLocaleDateString()}</strong></span>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }} onClick={function(ev) { ev.stopPropagation(); }}>
                      <button className="insights-btn" onClick={function() { setWorkspaceId(e.id); }}>Open workspace →</button>
                      {isActive && nextStatus && (
                        <button className="admin-btn primary sm" disabled={busy} onClick={function() { handleTransition(e, nextStatus); }}>
                          → {STATUS_LABELS[nextStatus]}
                        </button>
                      )}
                      {isActive && (
                        <button className="admin-btn sm" disabled={busy} onClick={function() { handleTransition(e, 'cancelled'); }}>Cancel</button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </ContentBand>
      )}
    </div>
  );
}

// ── EngagementContactsSection (S1.5) ────────────────────────────────
// 6 fixed roles per the pre-engagement package Section 11. One row per
// role, upsert on save. engagement_signatory is required for scope-lock
// (lands in a follow-up commit).
function EngagementContactsSection({ clientId, engagementId, contacts, onChange }) {
  var [busy, setBusy] = useState(false);
  var [toast, setToast] = useState(null);
  var [drafts, setDrafts] = useState({});

  var ROLES = [
    { key: 'engagement_signatory', label: 'Engagement Signatory', help: 'Officer with authority to bind the organization. Signs the Authorization-to-Test letter.', required: true },
    { key: 'primary', label: 'Primary Contact', help: 'Day-to-day point of contact during testing.', required: true },
    { key: 'secondary', label: 'Secondary / Escalation', help: 'Reachable when primary is unavailable.', required: false },
    { key: 'technical_lead', label: 'Technical Lead', help: 'Architecture / application questions.', required: false },
    { key: 'soc_lead', label: 'SOC / Detection Lead', help: 'Coordinates with defenders during testing.', required: false },
    { key: 'oob_emergency', label: 'Out-of-Band Emergency', help: 'Non-corporate channel for real emergencies (suspected real adversary, regulatory exposure).', required: false }
  ];

  function showToast(tone, text) { setToast({ tone: tone, text: text }); setTimeout(function() { setToast(null); }, 4000); }

  function rowFor(roleKey) {
    return (contacts || []).find(function(c) { return c.role === roleKey; });
  }
  function valueFor(roleKey, field) {
    if (drafts[roleKey] && drafts[roleKey][field] !== undefined) return drafts[roleKey][field];
    var row = rowFor(roleKey);
    return (row && row[field]) || '';
  }
  function setDraft(roleKey, field, value) {
    setDrafts(Object.assign({}, drafts, { [roleKey]: Object.assign({}, drafts[roleKey] || {}, { [field]: value }) }));
  }
  function isDirty(roleKey) {
    var d = drafts[roleKey];
    if (!d) return false;
    var row = rowFor(roleKey);
    for (var f in d) { if (d[f] !== ((row && row[f]) || '')) return true; }
    return false;
  }

  function saveRow(roleKey) {
    var name = valueFor(roleKey, 'name');
    if (!name || !name.trim()) { showToast('error', 'Name is required'); return; }
    setBusy(true);
    var body = {
      name: name.trim(),
      email: valueFor(roleKey, 'email') || null,
      phone: valueFor(roleKey, 'phone') || null,
      notes: valueFor(roleKey, 'notes') || null
    };
    authFetch(API + '/clients/' + clientId + '/redteam/engagements/' + engagementId + '/contacts/' + roleKey, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusy(false);
        if (x.ok) { showToast('success', 'Saved'); setDrafts(Object.assign({}, drafts, { [roleKey]: undefined })); if (onChange) onChange(); }
        else { showToast('error', x.d.error || 'Failed'); }
      })
      .catch(function() { setBusy(false); showToast('error', 'Request failed'); });
  }

  function clearRow(roleKey) {
    if (!window.confirm('Clear this contact?')) return;
    setBusy(true);
    authFetch(API + '/clients/' + clientId + '/redteam/engagements/' + engagementId + '/contacts/' + roleKey, { method: 'DELETE' })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusy(false);
        if (x.ok) { showToast('success', 'Cleared'); setDrafts(Object.assign({}, drafts, { [roleKey]: undefined })); if (onChange) onChange(); }
        else { showToast('error', x.d.error || 'Failed'); }
      })
      .catch(function() { setBusy(false); showToast('error', 'Request failed'); });
  }

  if (contacts === null) return <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)' }}>Loading contacts…</div>;

  var inputStyle = { width: '100%', padding: 8, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, color: '#fff', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' };

  return (
    <div>
      {toast && <div className={'admin-banner ' + toast.tone} style={{ fontSize: 12, marginBottom: 12 }}>{toast.text}</div>}
      <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 16 }}>Six contact roles per Section 11 of the pre-engagement package. The engagement signatory and primary contact are the minimum for scope-lock (lands in a follow-up commit).</div>

      {ROLES.map(function(r) {
        var row = rowFor(r.key);
        var dirty = isDirty(r.key);
        var hasData = row || drafts[r.key];
        return (
          <div key={r.key} style={{ padding: 16, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderLeft: '3px solid ' + (r.required ? (row ? '#22c55e' : '#fbbf24') : (row ? '#a78bfa' : 'rgba(167,139,250,0.3)')), borderRadius: 6, marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{r.label}</div>
              {r.required && <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3, background: 'rgba(251,191,36,0.15)', color: '#fbbf24', letterSpacing: '0.4px' }}>REQUIRED</span>}
              {row && !dirty && <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3, background: 'rgba(34,197,94,0.15)', color: '#22c55e', letterSpacing: '0.4px' }}>SAVED</span>}
              {dirty && <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3, background: 'rgba(96,165,250,0.15)', color: '#60a5fa', letterSpacing: '0.4px' }}>UNSAVED</span>}
            </div>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 10 }}>{r.help}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 2fr 1fr', gap: 10 }}>
              <input type="text" value={valueFor(r.key, 'name')} onChange={function(e) { setDraft(r.key, 'name', e.target.value); }} placeholder="Name" style={inputStyle} />
              <input type="email" value={valueFor(r.key, 'email')} onChange={function(e) { setDraft(r.key, 'email', e.target.value); }} placeholder="email@example.edu" style={inputStyle} />
              <input type="tel" value={valueFor(r.key, 'phone')} onChange={function(e) { setDraft(r.key, 'phone', e.target.value); }} placeholder="Phone" style={inputStyle} />
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button type="button" className="btn btn-sm btn-primary" onClick={function() { saveRow(r.key); }} disabled={busy || !dirty}>{row ? 'Update' : 'Save'}</button>
              {row && <button type="button" className="btn btn-sm btn-danger" onClick={function() { clearRow(r.key); }} disabled={busy}>Clear</button>}
              {dirty && <button type="button" className="btn btn-sm" onClick={function() { setDrafts(Object.assign({}, drafts, { [r.key]: undefined })); }} disabled={busy}>Discard</button>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── EngagementChecklistSection (S1.5) ───────────────────────────────
// 10 fixed items from Section 3.2 of the pre-engagement package, seeded
// at engagement creation. Status changes save immediately; notes save
// on blur. Backend backfills any missing rows on GET so engagements
// created before S1.5 still display the full checklist.
function EngagementChecklistSection({ clientId, engagementId, checklist, onChange }) {
  var [busy, setBusy] = useState(false);
  var [toast, setToast] = useState(null);
  var [noteDrafts, setNoteDrafts] = useState({});

  var ITEMS = [
    { key: 'tenant_id_confirmed', label: 'Tenant ID confirmed and shared', help: 'Client confirms the Entra/Microsoft 365 tenant ID and shares with the testing team.' },
    { key: 'global_admin_for_consent', label: 'Global Admin available for Aurelius consent', help: 'A Global Administrator can grant the Aurelius Graph application read-only consent during onboarding.' },
    { key: 'signatory_identified', label: 'Engagement signatory identified', help: 'Officer with authority to bind the organization, ready to sign the Authorization-to-Test letter.' },
    { key: 'escalation_contact_identified', label: 'Escalation contact identified', help: 'Secondary contact reachable on a non-corporate channel during testing windows.' },
    { key: 'concurrent_scans_confirmed_absent', label: 'No concurrent vuln scans during the window', help: 'Internal vulnerability scans confirmed paused or out-of-window so they do not collide with the engagement.' },
    { key: 'third_party_saas_listed', label: 'Third-party SaaS providers listed', help: 'Each in-scope SaaS provider captured in the catalog with its approval status (Appendix B).' },
    { key: 'backup_attestation_captured', label: 'Backup attestation captured', help: 'Written confirmation that recent backups exist, have been tested, and the RPO is acceptable.' },
    { key: 'insurance_attestation_captured', label: 'Insurance attestation captured', help: 'Cyber liability insurance in force; carrier notified if their policy requires it.' },
    { key: 'aurelius_baseline_run', label: 'Aurelius onboarding wizard run end-to-end', help: 'Baseline + ISPM snapshot completed, report attached to this package.' },
    { key: 'att_letter_signed', label: 'Authorization-to-Test letter signed', help: 'Signed by the engagement signatory and counter-signed by the testing team.' }
  ];

  function showToast(tone, text) { setToast({ tone: tone, text: text }); setTimeout(function() { setToast(null); }, 4000); }

  var STATUS_COLORS = {
    pending: { fg: '#94a3b8', bg: 'rgba(100,116,139,0.15)', label: 'Pending' },
    in_progress: { fg: '#60a5fa', bg: 'rgba(96,165,250,0.15)', label: 'In progress' },
    completed: { fg: '#22c55e', bg: 'rgba(34,197,94,0.15)', label: 'Completed' },
    waived: { fg: '#94a3b8', bg: 'rgba(100,116,139,0.15)', label: 'Waived' },
    blocked: { fg: '#f87171', bg: 'rgba(248,113,113,0.15)', label: 'Blocked' }
  };

  function itemFor(key) { return (checklist || []).find(function(c) { return c.item_key === key; }); }

  function updateStatus(itemKey, newStatus) {
    setBusy(true);
    authFetch(API + '/clients/' + clientId + '/redteam/engagements/' + engagementId + '/checklist/' + itemKey, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: newStatus })
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) { setBusy(false); if (x.ok) { if (onChange) onChange(); } else { showToast('error', x.d.error || 'Failed'); } })
      .catch(function() { setBusy(false); showToast('error', 'Request failed'); });
  }
  function saveNotes(itemKey) {
    var notes = noteDrafts[itemKey];
    if (notes === undefined) return;
    setBusy(true);
    authFetch(API + '/clients/' + clientId + '/redteam/engagements/' + engagementId + '/checklist/' + itemKey, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ notes: notes })
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusy(false);
        if (x.ok) { setNoteDrafts(Object.assign({}, noteDrafts, { [itemKey]: undefined })); if (onChange) onChange(); }
        else { showToast('error', x.d.error || 'Failed'); }
      })
      .catch(function() { setBusy(false); showToast('error', 'Request failed'); });
  }

  if (checklist === null) return <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)' }}>Loading checklist…</div>;

  var completed = (checklist || []).filter(function(c) { return c.status === 'completed' || c.status === 'waived'; }).length;
  var total = ITEMS.length;
  var pct = Math.round((completed / total) * 100);
  var inputStyle = { width: '100%', padding: 8, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, color: '#fff', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' };

  return (
    <div>
      {toast && <div className={'admin-banner ' + toast.tone} style={{ fontSize: 12, marginBottom: 12 }}>{toast.text}</div>}

      <div style={{ padding: 14, background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.2)', borderRadius: 8, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#a78bfa', marginBottom: 4 }}>ONBOARDING READINESS</div>
          <div style={{ fontSize: 12, color: '#cbd5e1' }}>{completed} of {total} items completed (waived counts as cleared). Section 3.2 of the pre-engagement package.</div>
        </div>
        <div style={{ width: 200 }}>
          <div style={{ height: 8, background: 'rgba(255,255,255,0.08)', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ width: pct + '%', height: '100%', background: pct === 100 ? '#22c55e' : '#a78bfa', transition: 'width 0.3s ease' }}></div>
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4, textAlign: 'right' }}>{pct}%</div>
        </div>
      </div>

      {ITEMS.map(function(it, idx) {
        var row = itemFor(it.key);
        var status = row ? row.status : 'pending';
        var color = STATUS_COLORS[status] || STATUS_COLORS.pending;
        var noteDraft = noteDrafts[it.key];
        var notesValue = noteDraft !== undefined ? noteDraft : ((row && row.notes) || '');
        var notesDirty = noteDraft !== undefined && noteDraft !== ((row && row.notes) || '');
        return (
          <div key={it.key} style={{ padding: 14, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderLeft: '3px solid ' + color.fg, borderRadius: 6, marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 8 }}>
              <div style={{ fontSize: 10, color: '#64748b', fontFamily: 'monospace', marginTop: 2, minWidth: 20 }}>{(idx + 1).toString().padStart(2, '0')}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 2 }}>{it.label}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>{it.help}</div>
                {row && row.completed_at && <div style={{ fontSize: 10, color: '#22c55e', marginTop: 4 }}>✓ Completed {new Date(row.completed_at).toLocaleDateString()}{row.completed_by_email ? ' by ' + row.completed_by_email : ''}</div>}
              </div>
              <select value={status} onChange={function(e) { updateStatus(it.key, e.target.value); }} disabled={busy} style={Object.assign({}, inputStyle, { width: 140, color: color.fg, fontWeight: 600 })}>
                <option value="pending">Pending</option>
                <option value="in_progress">In progress</option>
                <option value="completed">Completed</option>
                <option value="waived">Waived</option>
                <option value="blocked">Blocked</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <input type="text" value={notesValue} onChange={function(e) { setNoteDrafts(Object.assign({}, noteDrafts, { [it.key]: e.target.value })); }} onBlur={function() { if (notesDirty) saveNotes(it.key); }} placeholder="Notes (optional)" style={Object.assign({}, inputStyle, { fontSize: 12 })} />
              {notesDirty && <button type="button" className="btn btn-sm" onClick={function() { saveNotes(it.key); }} disabled={busy}>Save note</button>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── EngagementNextActions (S1.10) ───────────────────────────────────
// Phase-aware prescriptive guidance — replaces the wall-of-text
// EngagementsWorkflowGuide. PlexTrac-style checklist widget that
// surfaces the 2-3 most impactful next steps for the current engagement
// state, with click-to-navigate CTAs. Smaller, denser, more usable than
// a static 11-step doc dump.
function EngagementNextActions({ engagement, form, contacts, checklist, attestations, scopeCounts, onNavigateTab }) {
  if (!engagement) return null;

  function findContact(role) { return (contacts || []).find(function(c) { return c.role === role; }); }
  function attestSettled(kind) {
    var a = (attestations || []).find(function(x) { return x.kind === kind; });
    return a && (a.status === 'signed' || a.status === 'waived' || a.status === 'not_applicable');
  }

  var totalScope = (scopeCounts && (scopeCounts.app + scopeCounts.api + scopeCounts.attack + scopeCounts.internal + scopeCounts.cloud + scopeCounts.saas)) || 0;
  var requiredAttest = ['cyber_liability_insurance','backup_restore_tested','tester_eo_insurance','tester_gl_insurance'];
  var clearedAttest = requiredAttest.filter(attestSettled).length;
  var sig = findContact('engagement_signatory');
  var primary = findContact('primary');

  // Phase-aware action set. Each: { title, body, ctaLabel, tab }
  var actions = [];

  if (engagement.status === 'scoping') {
    // Build the readiness-blocker list in priority order
    var possible = [
      { ok: !!form.engagement_type, tab: 'record', title: 'Set the engagement type', body: 'Pentest / red team / purple team / vuln assessment / appsec / compliance-driven — pins the engagement product on paper.' },
      { ok: totalScope > 0, tab: 'scope', title: 'Pick scope items from the catalog', body: 'Apps, APIs, attack surface, internal scope, cloud tenants, SaaS providers. Build the catalog first if it\'s empty.' },
      { ok: sig && sig.email, tab: 'contacts', title: 'Assign the engagement signatory', body: 'The officer with authority to sign the ATT letter on behalf of the client. Email is required.' },
      { ok: !!primary, tab: 'contacts', title: 'Assign the primary contact', body: 'Day-to-day point of contact during testing.' },
      { ok: clearedAttest === requiredAttest.length, tab: 'attestations', title: 'Settle the 4 required attestations', body: 'Mark each as signed (with evidence), waived, or not-applicable. ' + clearedAttest + ' of ' + requiredAttest.length + ' cleared.' },
      { ok: !!form.authorization_letter_url, tab: 'auth', title: 'Paste the Authorization-to-Test letter URL', body: 'Signed letter URL goes on the Authorization tab. Required before scope-lock.' },
      { ok: form.engagement_window_start && form.engagement_window_end, tab: 'record', title: 'Set the engagement window', body: 'Start and end dates with timezone. Drives the days-remaining counter and blackout-period validation.' }
    ];
    var blockers = possible.filter(function(p) { return !p.ok; });
    if (blockers.length === 0) {
      actions.push({
        title: 'Scope-lock the engagement',
        body: 'All pre-conditions cleared. Click Scope-lock below to freeze snapshots and activate the pre-engagement package.',
        ctaLabel: 'Scroll to Scope-lock ↓',
        action: function() { window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }); }
      });
      actions.push({
        title: 'Verify the pre-engagement package before lock',
        body: 'Reports tab → Open print view. Confirm the rendered document looks right before committing the snapshot.',
        ctaLabel: 'Go to Reports →',
        tab: 'reports'
      });
    } else {
      // Surface the top 3 blockers as the next actions
      blockers.slice(0, 3).forEach(function(b) {
        actions.push({ title: b.title, body: b.body, ctaLabel: 'Go to ' + b.tab + ' →', tab: b.tab });
      });
    }
  } else if (engagement.status === 'scope_locked') {
    actions.push({
      title: 'Issue magic link for client signoff',
      body: 'Generates a 72-hour token-protected URL. The client signatory opens it (no Aurelius login needed), reviews the full engagement record, and types signatures inline for the ATT letter + each required attestation.',
      ctaLabel: 'Scroll to signoff panel ↓',
      action: function() { window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }); }
    });
    actions.push({
      title: 'Preview what the client will see',
      body: 'Open the pre-engagement package — it renders from the frozen snapshots and is the canonical engagement document. The magic-link page shows the same content + inline e-sig widgets.',
      ctaLabel: 'Go to Reports →',
      tab: 'reports'
    });
  } else if (engagement.status === 'pending_client_signoff') {
    actions.push({
      title: 'Track signature progress',
      body: 'Magic link is live. The signoff panel shows captured signatures (with IP + timestamp) and pending ones. Auto-transitions to ROE signed when complete.',
      ctaLabel: 'Scroll to signoff panel ↓',
      action: function() { window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }); }
    });
    actions.push({
      title: 'Watch for client corrections',
      body: 'If the client raises a correction instead of signing, it shows up in the signoff panel and the activity timeline. Address via Unlock → edit → relock → re-issue.',
      ctaLabel: 'Go to Activity →',
      tab: 'activity'
    });
  } else if (engagement.status === 'roe_signed') {
    actions.push({
      title: 'Pre-test brief with the client',
      body: 'Confirm the testing window, OOB emergency contact, and stop-test command word. Once kicked off, transition to Executing.',
      ctaLabel: 'Use → Executing button in context bar'
    });
    actions.push({
      title: 'Move to Executing when testing starts',
      body: 'Transitioning unlocks the Attack Surface tab — live identity attack-path analysis runs against the current Entra tenant. Findings persist with the engagement.',
      ctaLabel: '(no nav)',
      action: function() { window.scrollTo({ top: 0, behavior: 'smooth' }); }
    });
  } else if (engagement.status === 'executing') {
    actions.push({
      title: 'Run attack-path analysis',
      body: 'Phase 2 identity attack-path analyzer. 9 categories of risk. Findings persist into the Findings tab.',
      ctaLabel: 'Go to Attack surface →',
      tab: 'attack_surface'
    });
    actions.push({
      title: 'Triage findings as they land',
      body: 'Severity sort, status filter, inline triage. Critical and High findings should be notified to the primary contact within 4 hours per RoE.',
      ctaLabel: 'Go to Findings →',
      tab: 'findings'
    });
  } else if (engagement.status === 'reporting') {
    actions.push({
      title: 'Generate engagement deliverables',
      body: 'Pre-engagement package is already live. Exec summary, technical report, close-out deck come in later slices — for now, export findings JSON for upstream tooling.',
      ctaLabel: 'Go to Reports →',
      tab: 'reports'
    });
    actions.push({
      title: 'Close out remaining findings',
      body: 'Findings should be either remediated, retested, or accepted_risk before transitioning to debrief.',
      ctaLabel: 'Go to Findings →',
      tab: 'findings'
    });
  } else if (engagement.status === 'debrief') {
    actions.push({
      title: 'Schedule the client debrief',
      body: 'Walkthrough of findings, remediation priorities, retest schedule. When complete, transition to Closed.',
      ctaLabel: 'Use → Closed button in context bar'
    });
  } else if (engagement.status === 'closed') {
    actions.push({
      title: 'Engagement closed — read-only',
      body: 'All scope, contacts, attestations, and findings are preserved for audit. Activity timeline retains the full event log.',
      ctaLabel: 'Go to Activity →',
      tab: 'activity'
    });
  }

  if (actions.length === 0) return null;

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#a78bfa' }}>NEXT ACTIONS</span>
        <span style={{ fontSize: 10, color: '#64748b' }}>·  Phase: {engagement.status.replace(/_/g, ' ')}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: actions.length === 1 ? '1fr' : 'repeat(' + Math.min(actions.length, 3) + ', 1fr)', gap: 10 }}>
        {actions.map(function(a, i) {
          var clickable = !!a.tab || !!a.action;
          return (
            <div key={i}
              onClick={clickable ? function() { if (a.tab && onNavigateTab) onNavigateTab(a.tab); else if (a.action) a.action(); } : null}
              style={{ padding: 14, background: 'rgba(167,139,250,0.05)', border: '1px solid rgba(167,139,250,0.2)', borderRadius: 8, cursor: clickable ? 'pointer' : 'default', transition: 'all 0.15s ease' }}
              onMouseEnter={function(ev) { if (clickable) { ev.currentTarget.style.background = 'rgba(167,139,250,0.12)'; ev.currentTarget.style.borderColor = 'rgba(167,139,250,0.45)'; } }}
              onMouseLeave={function(ev) { if (clickable) { ev.currentTarget.style.background = 'rgba(167,139,250,0.05)'; ev.currentTarget.style.borderColor = 'rgba(167,139,250,0.2)'; } }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 4 }}>
                <span style={{ flex: 'none', width: 18, height: 18, borderRadius: '50%', background: 'rgba(167,139,250,0.2)', color: '#a78bfa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700 }}>{i + 1}</span>
                <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: '#fff' }}>{a.title}</span>
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.5, marginBottom: 8 }}>{a.body}</div>
              <div style={{ fontSize: 10, color: '#a78bfa', fontWeight: 600, letterSpacing: '0.4px' }}>{a.ctaLabel}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── SignoffControlPanel (S1.9b) ─────────────────────────────────────
// Admin-side controls for the magic-link client signoff. Issues the
// magic link, displays the signing URL with copy button, shows live
// signature-capture progress (captured + pending kinds, signatory
// names, timestamps, IPs), surfaces client corrections, and allows
// revocation. Auto-refreshes after the operator copies the link so
// the panel stays current as the client signs.
function SignoffControlPanel({ clientId, engagement, onChange }) {
  var [status, setStatus] = useState(null);
  var [busy, setBusy] = useState(false);
  var [toast, setToast] = useState(null);
  var [revokeConfirm, setRevokeConfirm] = useState(false);
  var [showLinkCopy, setShowLinkCopy] = useState(false);
  var [generatedToken, setGeneratedToken] = useState(null);

  function showToast(tone, text) { setToast({ tone: tone, text: text }); setTimeout(function() { setToast(null); }, 5000); }

  function refresh() {
    authFetch(API + '/clients/' + clientId + '/redteam/engagements/' + engagement.id + '/signoff/status')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) { setStatus(d); })
      .catch(function() { setStatus({ active_link: null, signoffs: [], corrections: [], required_signoffs: [] }); });
  }
  useEffect(refresh, [clientId, engagement.id, engagement.status]);  // eslint-disable-line react-hooks/exhaustive-deps
  // Auto-poll every 10s while the panel is visible and the link is active
  useEffect(function() {
    if (!status || !status.active_link || status.active_link.revoked_at) return;
    var id = setInterval(refresh, 10000);
    return function() { clearInterval(id); };
  }, [status]);  // eslint-disable-line react-hooks/exhaustive-deps

  function requestSignoff() {
    setBusy(true);
    authFetch(API + '/clients/' + clientId + '/redteam/engagements/' + engagement.id + '/signoff/request', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({})
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusy(false);
        if (x.ok) {
          showToast('success', 'Magic link issued — copy and send to the engagement signatory.');
          setGeneratedToken(x.d.signoff_url);
          setShowLinkCopy(true);
          refresh();
          if (onChange) onChange();
        } else { showToast('error', x.d.error || 'Failed'); }
      })
      .catch(function() { setBusy(false); showToast('error', 'Request failed'); });
  }

  function revokeSignoff() {
    setBusy(true);
    authFetch(API + '/clients/' + clientId + '/redteam/engagements/' + engagement.id + '/signoff/revoke', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({})
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusy(false);
        if (x.ok) { showToast('success', 'Magic link revoked.'); setRevokeConfirm(false); setGeneratedToken(null); setShowLinkCopy(false); refresh(); if (onChange) onChange(); }
        else { showToast('error', x.d.error || 'Failed'); }
      })
      .catch(function() { setBusy(false); showToast('error', 'Request failed'); });
  }

  function copyLink() {
    if (!generatedToken) return;
    var url = window.location.origin + generatedToken;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(function() { showToast('success', 'Link copied to clipboard'); });
    } else {
      // Fallback for environments without Clipboard API
      var el = document.createElement('textarea');
      el.value = url;
      document.body.appendChild(el);
      el.select();
      try { document.execCommand('copy'); showToast('success', 'Link copied'); } catch (e) { showToast('error', 'Copy failed — select manually'); }
      document.body.removeChild(el);
    }
  }

  if (status === null) return <div style={{ marginTop: 12, padding: 14, color: '#94a3b8', fontSize: 12 }}>Loading signoff status…</div>;

  var link = status.active_link;
  var linkActive = link && !link.revoked_at && new Date(link.expires_at) > new Date();
  var signoffs = status.signoffs || [];
  var required = status.required_signoffs || [];
  var corrections = status.corrections || [];

  // Build a map: kind+saas → captured signoff
  var capturedMap = {};
  signoffs.forEach(function(s) {
    var saasFromPayload = s.signature_payload && s.signature_payload.saas_provider;
    var key = saasFromPayload ? s.kind + '::' + saasFromPayload : s.kind;
    capturedMap[key] = s;
  });

  var capturedCount = required.filter(function(r) {
    var key = r.saas_provider ? r.kind + '::' + r.saas_provider : r.kind;
    return capturedMap[key];
  }).length;
  var totalRequired = required.length;
  var pct = totalRequired > 0 ? Math.round((capturedCount / totalRequired) * 100) : 0;

  var SIGNOFF_LABELS = {
    att_letter: 'Authorization-to-Test letter',
    attestation_insurance: 'Cyber liability insurance',
    attestation_backup: 'Backup restore tested',
    attestation_dpa: 'Data Processing Agreement',
    attestation_cross_border: 'Cross-border jurisdiction',
    attestation_carrier_notified: 'Carrier notified',
    attestation_saas_approval: 'SaaS provider approval',
    roe_acknowledgment: 'RoE acknowledgment'
  };

  return (
    <div style={{ marginTop: 12 }}>
      {toast && <div className={'admin-banner ' + toast.tone} style={{ fontSize: 12, marginBottom: 12 }}>{toast.text}</div>}

      <div style={{ padding: 18, background: 'rgba(96,165,250,0.05)', border: '1px solid rgba(96,165,250,0.25)', borderRadius: 8 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: linkActive ? 14 : 0 }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(96,165,250,0.18)', color: '#60a5fa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700, flex: 'none' }}>✉</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#60a5fa', marginBottom: 2 }}>CLIENT SIGNOFF</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 6 }}>
              {!linkActive && 'Ready to issue magic link'}
              {linkActive && capturedCount === totalRequired && 'All signatures captured ✓'}
              {linkActive && capturedCount < totalRequired && capturedCount + ' of ' + totalRequired + ' signatures captured'}
            </div>
            <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.5 }}>
              {!linkActive && 'Issue a magic link below. The client signatory receives a token-protected URL — no Aurelius login needed. They review the engagement record, e-sign each required attestation + the Authorization-to-Test letter, and the engagement auto-transitions to ROE signed.'}
              {linkActive && 'Live link issued. Share the URL with the engagement signatory. Live progress updates every 10 seconds as signatures are captured.'}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 'none' }}>
            {!linkActive && (
              <button type="button" className="admin-btn primary" onClick={requestSignoff} disabled={busy}>
                {busy ? 'Issuing…' : 'Issue magic link'}
              </button>
            )}
            {linkActive && generatedToken && (
              <button type="button" className="admin-btn primary" onClick={copyLink} disabled={busy}>Copy link URL</button>
            )}
            {linkActive && (
              <button type="button" className="btn btn-sm" onClick={function() { setRevokeConfirm(true); }} disabled={busy}>Revoke link</button>
            )}
          </div>
        </div>

        {linkActive && (
          <div>
            <div style={{ height: 8, background: 'rgba(255,255,255,0.08)', borderRadius: 4, overflow: 'hidden', marginBottom: 16 }}>
              <div style={{ width: pct + '%', height: '100%', background: pct === 100 ? '#22c55e' : '#60a5fa', transition: 'width 0.3s ease' }}></div>
            </div>

            {showLinkCopy && generatedToken && (
              <div style={{ padding: 10, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(96,165,250,0.2)', borderRadius: 6, marginBottom: 12, fontFamily: 'monospace', fontSize: 11, color: '#cbd5e1', wordBreak: 'break-all' }}>
                {window.location.origin + generatedToken}
              </div>
            )}

            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.5px', color: '#94a3b8', marginBottom: 6 }}>REQUIRED SIGNATURES</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 6, marginBottom: 12 }}>
              {required.map(function(r) {
                var key = r.saas_provider ? r.kind + '::' + r.saas_provider : r.kind;
                var sig = capturedMap[key];
                var done = !!sig;
                return (
                  <div key={key} style={{ padding: 10, background: done ? 'rgba(34,197,94,0.06)' : 'rgba(255,255,255,0.02)', border: '1px solid ' + (done ? 'rgba(34,197,94,0.25)' : 'rgba(255,255,255,0.06)'), borderRadius: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ width: 14, height: 14, borderRadius: '50%', background: done ? '#22c55e' : 'rgba(255,255,255,0.08)', color: done ? '#fff' : '#64748b', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, flex: 'none' }}>{done ? '✓' : '○'}</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: done ? '#22c55e' : '#cbd5e1' }}>{SIGNOFF_LABELS[r.kind] || r.kind}{r.saas_provider ? ' · ' + r.saas_provider : ''}</span>
                    </div>
                    {done && (
                      <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4, paddingLeft: 22 }}>
                        {sig.signatory_name}{sig.signatory_title ? ' (' + sig.signatory_title + ')' : ''} · {new Date(sig.signed_at).toLocaleString()}
                        {sig.ip_address && <div style={{ fontFamily: 'monospace', fontSize: 9 }}>IP: {sig.ip_address}</div>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={{ fontSize: 11, color: '#64748b', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              <span>Link expires {new Date(link.expires_at).toLocaleString()}</span>
              {link.last_used_ip && <span>Last viewed from {link.last_used_ip}</span>}
              {link.used_at && <span>First opened {new Date(link.used_at).toLocaleString()}</span>}
            </div>
          </div>
        )}
      </div>

      {corrections.length > 0 && (
        <div style={{ marginTop: 12, padding: 14, background: 'rgba(185,138,42,0.08)', border: '1px solid rgba(185,138,42,0.3)', borderRadius: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#fbbf24', marginBottom: 8 }}>CORRECTIONS RAISED BY CLIENT</div>
          {corrections.map(function(c) {
            return (
              <div key={c.id} style={{ padding: 10, background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 4, marginBottom: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#fff' }}>{c.raised_by_name}<span style={{ color: '#94a3b8', fontWeight: 400 }}> · {c.raised_by_email}</span></span>
                  <span style={{ fontSize: 10, color: '#64748b' }}>{new Date(c.raised_at).toLocaleString()}</span>
                </div>
                {c.target_section && <div style={{ fontSize: 10, color: '#fbbf24', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>{c.target_section.replace(/_/g, ' ')}</div>}
                <div style={{ fontSize: 12, color: '#cbd5e1', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{c.narrative}</div>
              </div>
            );
          })}
          <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 6 }}>The client raised corrections instead of (or alongside) signing. Address them via Unlock + edit + relock + re-issue signoff, or contact the client directly.</div>
        </div>
      )}

      {revokeConfirm && (
        <div onClick={function() { if (!busy) setRevokeConfirm(false); }} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
          <div onClick={function(ev) { ev.stopPropagation(); }} style={{ background: '#0f172a', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 12, padding: 24, maxWidth: 520, width: '100%' }}>
            <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.6px', color: '#f87171', marginBottom: 12 }}>REVOKE MAGIC LINK</div>
            <div style={{ fontSize: 13, color: '#cbd5e1', lineHeight: 1.6, marginBottom: 16 }}>
              Revoking invalidates the current link. Captured signatures stay in the audit trail (engagement_signoffs) but the engagement returns to <code style={{ color: '#a78bfa' }}>scope_locked</code>. Issue a fresh link to restart signoff.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn" onClick={function() { setRevokeConfirm(false); }} disabled={busy}>Cancel</button>
              <button type="button" className="admin-btn" style={{ background: '#f87171', borderColor: '#f87171' }} onClick={revokeSignoff} disabled={busy}>{busy ? 'Revoking…' : 'Confirm revoke'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── ScopeLockPanel (S1.9) ───────────────────────────────────────────
// The legal-bright-line action. Pre-lock: shows a scope-lock button
// that's enabled when the readiness gate is green, with a confirmation
// modal explaining what's about to happen. Post-lock: shows the
// frozen-at/by metadata, a button to view the rendered pre-engagement
// package, and an admin-only unlock escape hatch.
function ScopeLockPanel({ clientId, engagement, readyForLock, blockerCount, onLocked }) {
  var [confirming, setConfirming] = useState(false);
  var [busy, setBusy] = useState(false);
  var [toast, setToast] = useState(null);
  var [unlockConfirm, setUnlockConfirm] = useState(false);

  function showToast(tone, text) { setToast({ tone: tone, text: text }); setTimeout(function() { setToast(null); }, 5000); }

  function doLock() {
    setBusy(true);
    authFetch(API + '/clients/' + clientId + '/redteam/engagements/' + engagement.id + '/scope-lock', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({})
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusy(false);
        if (x.ok) {
          var c = x.d.snapshot_counts || {};
          showToast('success', 'Scope locked. Snapshots frozen: ' + (c.app + c.api + c.attack + c.internal + c.cloud + c.saas) + ' items.');
          setConfirming(false);
          if (onLocked) onLocked();
        } else if (x.d.blockers) {
          showToast('error', 'Pre-conditions not met: ' + x.d.blockers.join('; '));
        } else {
          showToast('error', x.d.error || 'Scope-lock failed');
        }
      })
      .catch(function() { setBusy(false); showToast('error', 'Request failed'); });
  }
  function doUnlock() {
    setBusy(true);
    authFetch(API + '/clients/' + clientId + '/redteam/engagements/' + engagement.id + '/scope-unlock', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({})
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusy(false);
        if (x.ok) { showToast('success', 'Scope unlocked. Engagement returned to scoping.'); setUnlockConfirm(false); if (onLocked) onLocked(); }
        else { showToast('error', x.d.error || 'Unlock failed'); }
      })
      .catch(function() { setBusy(false); showToast('error', 'Request failed'); });
  }

  function openPackage() {
    var base = window.location.origin + API;
    var url = base + '/clients/' + clientId + '/redteam/engagements/' + engagement.id + '/pre-engagement-package';
    // Pre-engagement package endpoint requires auth — fetch as blob then open in new window
    authFetch(url)
      .then(function(r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
      .then(function(html) {
        var w = window.open('', '_blank');
        if (!w) { showToast('error', 'Pop-up blocked. Allow pop-ups for this site.'); return; }
        w.document.open();
        w.document.write(html);
        w.document.close();
      })
      .catch(function(err) { showToast('error', 'Failed to load package: ' + err.message); });
  }

  // Pre-lock view (status === 'scoping')
  if (engagement.status === 'scoping') {
    return (
      <div style={{ marginTop: 16 }}>
        {toast && <div className={'admin-banner ' + toast.tone} style={{ fontSize: 12, marginBottom: 12 }}>{toast.text}</div>}
        <div style={{ padding: 18, background: readyForLock ? 'rgba(34,197,94,0.06)' : 'rgba(255,255,255,0.02)', border: '1px solid ' + (readyForLock ? 'rgba(34,197,94,0.3)' : 'rgba(255,255,255,0.08)'), borderRadius: 8 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: readyForLock ? 'rgba(34,197,94,0.18)' : 'rgba(255,255,255,0.04)', color: readyForLock ? '#22c55e' : '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, flex: 'none' }}>🔒</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: readyForLock ? '#22c55e' : '#94a3b8', marginBottom: 2 }}>SCOPE-LOCK</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 6 }}>
                {readyForLock ? 'Ready to scope-lock' : 'Resolve readiness gate blockers first'}
              </div>
              <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.5 }}>
                Scope-lock freezes the engagement's selected scope items into immutable snapshot tables. After lock, the engagement's authorized scope is the legal-of-record — the catalog can keep evolving for other engagements without disturbing this one. Status moves to <strong>scope_locked</strong>. Magic-link client signoff for the ATT letter + attestations lands in S1.9b — until then, the operator handles those offline and manually advances to ROE signed.
              </div>
            </div>
            <button type="button" className="admin-btn primary" disabled={!readyForLock || busy} onClick={function() { setConfirming(true); }} style={{ flex: 'none' }}>
              {busy ? 'Working…' : 'Scope-lock'}
            </button>
          </div>
        </div>

        {confirming && (
          <div onClick={function() { if (!busy) setConfirming(false); }} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
            <div onClick={function(ev) { ev.stopPropagation(); }} style={{ background: '#0f172a', border: '1px solid rgba(167,139,250,0.3)', borderRadius: 12, padding: 24, maxWidth: 560, width: '100%' }}>
              <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.6px', color: '#fbbf24', marginBottom: 12 }}>CONFIRM SCOPE-LOCK</div>
              <div style={{ fontSize: 13, color: '#cbd5e1', lineHeight: 1.6, marginBottom: 16 }}>
                You're about to freeze the scope of <strong style={{ color: '#fff' }}>{engagement.name}</strong>. This will:
              </div>
              <ul style={{ fontSize: 12, color: '#cbd5e1', lineHeight: 1.6, marginBottom: 16, paddingLeft: 20 }}>
                <li>Copy every in-scope catalog row (apps, APIs, attack surface, internal, cloud, SaaS) into engagement-bound snapshot tables.</li>
                <li>Transition the engagement status to <code style={{ color: '#a78bfa' }}>scope_locked</code>.</li>
                <li>Make the Scope tab read-only — further edits go through unlock-edit-relock.</li>
                <li>Activate the pre-engagement package output (Reports tab).</li>
              </ul>
              <div style={{ fontSize: 11, color: '#fbbf24', marginBottom: 16, padding: 10, background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.3)', borderRadius: 6 }}>
                <strong>Reversible:</strong> admin can scope-unlock from this same panel, which deletes the snapshots and returns to scoping. Any signoffs collected via magic-link (when that lands in S1.9b) are also invalidated on unlock — operators should not unlock casually once client signatures are in.
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" className="btn" onClick={function() { setConfirming(false); }} disabled={busy}>Cancel</button>
                <button type="button" className="admin-btn primary" onClick={doLock} disabled={busy}>{busy ? 'Locking…' : 'Confirm scope-lock'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Post-lock view (scope_locked / pending_client_signoff / roe_signed+)
  var locked = engagement.scope_locked_at;
  var canSignoff = engagement.status === 'scope_locked' || engagement.status === 'pending_client_signoff';
  return (
    <div style={{ marginTop: 16 }}>
      {toast && <div className={'admin-banner ' + toast.tone} style={{ fontSize: 12, marginBottom: 12 }}>{toast.text}</div>}
      <div style={{ padding: 18, background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 8 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(34,197,94,0.18)', color: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, flex: 'none' }}>🔒</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#22c55e', marginBottom: 2 }}>SCOPE LOCKED</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 6 }}>
              Scope frozen on {locked ? new Date(locked).toLocaleString() : '—'}
              {engagement.scope_locked_by_email ? ' by ' + engagement.scope_locked_by_email : ''}
            </div>
            <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.5 }}>
              The pre-engagement package renders from the frozen snapshots. Issue a magic link below to capture client e-signatures for the Authorization-to-Test letter and required attestations — the engagement auto-transitions to <code style={{ color: '#a78bfa' }}>roe_signed</code> when all required signatures are in.
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 'none' }}>
            <button type="button" className="admin-btn primary" onClick={openPackage} disabled={busy}>Open pre-engagement package</button>
            {canSignoff && (
              <button type="button" className="btn btn-sm" onClick={function() { setUnlockConfirm(true); }} disabled={busy}>Unlock (admin)</button>
            )}
          </div>
        </div>
      </div>

      {canSignoff && (
        <SignoffControlPanel clientId={clientId} engagement={engagement} onChange={onLocked} />
      )}

      {unlockConfirm && (
        <div onClick={function() { if (!busy) setUnlockConfirm(false); }} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
          <div onClick={function(ev) { ev.stopPropagation(); }} style={{ background: '#0f172a', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 12, padding: 24, maxWidth: 520, width: '100%' }}>
            <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.6px', color: '#f87171', marginBottom: 12 }}>UNLOCK SCOPE</div>
            <div style={{ fontSize: 13, color: '#cbd5e1', lineHeight: 1.6, marginBottom: 16 }}>
              Unlocking will delete all engagement snapshots and return the status to <code style={{ color: '#a78bfa' }}>scoping</code>. If any magic-link signatures were collected, they are invalidated and the client will need to re-sign after relock. Use only when the scope itself genuinely needs to change.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn" onClick={function() { setUnlockConfirm(false); }} disabled={busy}>Cancel</button>
              <button type="button" className="admin-btn" style={{ background: '#f87171', borderColor: '#f87171' }} onClick={doUnlock} disabled={busy}>{busy ? 'Unlocking…' : 'Confirm unlock'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── EngagementScanners (S1.12) ──────────────────────────────────────
// Engagement-bound scanner runs. Lists available scanners (Shodan,
// httpx), shows configuration state, last-run timestamps, and a
// "Run now" button per scanner. Results flow into the Findings tab.
// Scanner runs are gated on engagement.status === 'executing' or
// later — server enforces this too with a 412 if called early.
function EngagementScanners({ clientId, engagementId, engagementStatus, onChange }) {
  var [status, setStatus] = useState(null);
  var [busy, setBusy] = useState(null);  // tracks which scanner is currently running
  var [toast, setToast] = useState(null);

  function showToast(tone, text) { setToast({ tone: tone, text: text }); setTimeout(function() { setToast(null); }, 6000); }

  function refresh() {
    authFetch(API + '/clients/' + clientId + '/redteam/engagements/' + engagementId + '/scanners/status')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) { setStatus(d); })
      .catch(function() { setStatus({ shodan_configured: false, recent_runs: [], findings_by_category: [] }); });
  }
  useEffect(refresh, [clientId, engagementId]);  // eslint-disable-line react-hooks/exhaustive-deps

  var canRun = ['executing','reporting','debrief','closed'].indexOf(engagementStatus) !== -1;

  function runScanner(scannerKey, path) {
    setBusy(scannerKey);
    authFetch(API + '/clients/' + clientId + '/redteam/engagements/' + engagementId + '/scanners/' + path + '/run', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({})
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusy(null);
        if (x.ok) {
          var parts = [];
          if (x.d.probed != null) parts.push(x.d.probed + ' probed');
          if (x.d.scanned != null) parts.push(x.d.scanned + ' scanned');
          if (x.d.hosts_found != null) parts.push(x.d.hosts_found + ' hosts with data');
          if (x.d.findings_inserted != null) parts.push(x.d.findings_inserted + ' new findings');
          showToast('success', scannerKey + ' run complete: ' + parts.join(', '));
          refresh();
          if (onChange) onChange();
        } else { showToast('error', x.d.error || 'Scan failed'); }
      })
      .catch(function() { setBusy(null); showToast('error', 'Request failed'); });
  }

  if (status === null) return <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)' }}>Loading scanner status…</div>;

  var SCANNERS = [
    {
      key: 'httpx',
      path: 'httpx',
      title: 'HTTP probe (Aurelius native)',
      desc: 'Fetches each URL / domain in scope, captures status code + title + server banner + security headers. Generates findings for dead URLs, server-version leaks, X-Powered-By disclosure, and missing security headers (HSTS / CSP / X-Frame-Options / etc.).',
      configured: true,
      runtime: 'Free, no API key',
      severity_range: 'informational → low'
    },
    {
      key: 'shodan',
      path: 'shodan',
      title: 'Shodan host enrichment',
      desc: 'Queries Shodan\'s internet-wide scanner data for each IP / domain in scope. Returns exposed ports, service banners + versions, and known CVEs that Shodan has detected on the host. Generates findings for exposed services and version-confirmed CVEs.',
      configured: status.shodan_configured,
      runtime: status.shodan_configured ? 'SHODAN_API_KEY configured' : 'SHODAN_API_KEY not set on server',
      severity_range: 'low → high'
    }
  ];

  // Latest run per scanner key
  var lastRunByKey = {};
  (status.recent_runs || []).forEach(function(r) {
    var key = r.event_type === 'SCANNER_HTTPX_RUN' ? 'httpx' : r.event_type === 'SCANNER_SHODAN_RUN' ? 'shodan' : null;
    if (key && !lastRunByKey[key]) lastRunByKey[key] = r;
  });

  return (
    <div>
      {toast && <div className={'admin-banner ' + toast.tone} style={{ fontSize: 12, marginBottom: 12 }}>{toast.text}</div>}

      <div style={{ padding: 14, background: 'rgba(167,139,250,0.04)', border: '1px solid rgba(167,139,250,0.18)', borderRadius: 8, marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#a78bfa', marginBottom: 6 }}>SCANNERS</div>
        <div style={{ fontSize: 12, color: '#cbd5e1', lineHeight: 1.5 }}>
          Aurelius-native scanners run against the in-scope attack surface for this engagement. Findings persist to the Findings tab, idempotent across re-runs (same finding_key = no duplicate). Discovery scanners (crt.sh subdomain expansion) live on the Catalog → Attack Surface tab — those don't require an active engagement.
          {!canRun && <span style={{ color: '#fbbf24' }}> · Scanner runs require engagement status=executing or later. Current status: <strong>{engagementStatus}</strong>.</span>}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 12, marginBottom: 16 }}>
        {SCANNERS.map(function(s) {
          var last = lastRunByKey[s.key];
          var thisBusy = busy === s.key;
          var canRunThis = canRun && s.configured;
          return (
            <div key={s.key} style={{ padding: 16, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{s.title}</span>
                {s.configured ? <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3, background: 'rgba(34,197,94,0.15)', color: '#22c55e', letterSpacing: '0.5px' }}>READY</span> : <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3, background: 'rgba(251,191,36,0.15)', color: '#fbbf24', letterSpacing: '0.5px' }}>NOT CONFIGURED</span>}
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.5, marginBottom: 10 }}>{s.desc}</div>
              <div style={{ fontSize: 10, color: '#64748b', marginBottom: 10 }}>
                Runtime: {s.runtime} · Severity range: {s.severity_range}
                {last && <div>Last run: {new Date(last.created_at).toLocaleString()} · {(last.metadata && (last.metadata.findings_inserted != null ? last.metadata.findings_inserted + ' findings inserted' : 'see audit')) || ''}</div>}
              </div>
              <button type="button" className={canRunThis ? 'btn btn-sm btn-primary' : 'btn btn-sm'} disabled={!canRunThis || busy} onClick={function() { runScanner(s.key, s.path); }}>
                {thisBusy ? 'Running…' : 'Run scanner'}
              </button>
              {!s.configured && s.key === 'shodan' && (
                <div style={{ fontSize: 10, color: '#fbbf24', marginTop: 6 }}>Set <code style={{ color: '#a78bfa' }}>SHODAN_API_KEY</code> in App Service config to enable.</div>
              )}
            </div>
          );
        })}
      </div>

      {(status.recent_runs || []).length > 0 && (
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.6px', color: '#94a3b8', marginBottom: 8 }}>RECENT SCANNER ACTIVITY</div>
          {(status.recent_runs || []).slice(0, 10).map(function(r) {
            var scannerName = r.event_type === 'SCANNER_HTTPX_RUN' ? 'httpx' : r.event_type === 'SCANNER_SHODAN_RUN' ? 'shodan' : r.event_type === 'SCANNER_CRTSH_DISCOVER' ? 'crt.sh discover' : r.event_type === 'SCANNER_CRTSH_IMPORT' ? 'crt.sh import' : r.event_type;
            var m = r.metadata || {};
            var summary = '';
            if (m.probed != null) summary = m.probed + ' probed';
            else if (m.scanned != null) summary = m.scanned + ' scanned';
            else if (m.found != null) summary = m.found + ' candidates found';
            else if (m.added != null) summary = m.added + ' imported';
            if (m.findings_inserted != null) summary += (summary ? ' · ' : '') + m.findings_inserted + ' new findings';
            return (
              <div key={r.created_at + scannerName} style={{ padding: 8, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)', borderRadius: 4, marginBottom: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11 }}>
                <span><strong style={{ color: '#a78bfa' }}>{scannerName}</strong> · <span style={{ color: '#cbd5e1' }}>{summary}</span></span>
                <span style={{ color: '#64748b', fontSize: 10 }}>{new Date(r.created_at).toLocaleString()}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── EngagementFindings (S1.8) ───────────────────────────────────────
// Unified findings view inside the engagement workspace. Pulls every
// row in redteam_findings for this engagement — attack-path findings
// (auto-generated from the Phase 2 analyzer when status=executing) and
// any manual findings added later. Severity sort, status filter,
// inline triage. The pen-test platform pattern (PlexTrac, Cobalt)
// makes Findings a first-class workspace surface, not a sub-view of
// an analyzer.
function EngagementFindings({ clientId, engagementId, engagementStatus }) {
  var [findings, setFindings] = useState(null);
  var [busy, setBusy] = useState(false);
  var [toast, setToast] = useState(null);
  var [filterSeverity, setFilterSeverity] = useState('all');
  var [filterStatus, setFilterStatus] = useState('all');
  var [expandedId, setExpandedId] = useState(null);

  function showToast(tone, text) { setToast({ tone: tone, text: text }); setTimeout(function() { setToast(null); }, 4000); }

  function refresh() {
    authFetch(API + '/clients/' + clientId + '/redteam/findings?engagement_id=' + engagementId)
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) { setFindings(d ? (d.findings || []) : []); })
      .catch(function() { setFindings([]); });
  }
  useEffect(refresh, [clientId, engagementId]);  // eslint-disable-line react-hooks/exhaustive-deps

  function updateFinding(finding, patch) {
    setBusy(true);
    authFetch(API + '/clients/' + clientId + '/redteam/findings/' + finding.id, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch)
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusy(false);
        if (x.ok) { showToast('success', 'Updated'); refresh(); }
        else { showToast('error', x.d.error || 'Failed'); }
      })
      .catch(function() { setBusy(false); showToast('error', 'Request failed'); });
  }

  if (findings === null) return <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)' }}>Loading findings…</div>;

  var SEVERITY_ORDER = { critical: 0, high: 1, medium: 2, low: 3, informational: 4 };
  var SEVERITY_COLORS = {
    critical: { fg: '#f87171', bg: 'rgba(248,113,113,0.15)' },
    high: { fg: '#fb923c', bg: 'rgba(251,146,60,0.15)' },
    medium: { fg: '#fbbf24', bg: 'rgba(251,191,36,0.15)' },
    low: { fg: '#60a5fa', bg: 'rgba(96,165,250,0.15)' },
    informational: { fg: '#94a3b8', bg: 'rgba(148,163,184,0.15)' }
  };
  var STATUS_COLORS = {
    open: { fg: '#f87171', label: 'Open' },
    triaged: { fg: '#fbbf24', label: 'Triaged' },
    remediated: { fg: '#a78bfa', label: 'Remediated' },
    retested: { fg: '#60a5fa', label: 'Retested' },
    accepted_risk: { fg: '#94a3b8', label: 'Accepted risk' },
    closed: { fg: '#22c55e', label: 'Closed' }
  };

  // Filter + sort
  var filtered = findings.slice();
  if (filterSeverity !== 'all') filtered = filtered.filter(function(f) { return f.severity === filterSeverity; });
  if (filterStatus !== 'all') filtered = filtered.filter(function(f) { return f.status === filterStatus; });
  filtered.sort(function(a, b) {
    var sa = SEVERITY_ORDER[a.severity] !== undefined ? SEVERITY_ORDER[a.severity] : 99;
    var sb = SEVERITY_ORDER[b.severity] !== undefined ? SEVERITY_ORDER[b.severity] : 99;
    if (sa !== sb) return sa - sb;
    return new Date(b.discovered_at) - new Date(a.discovered_at);
  });

  // Severity counts
  var counts = { critical: 0, high: 0, medium: 0, low: 0, informational: 0 };
  findings.forEach(function(f) { if (counts[f.severity] !== undefined) counts[f.severity]++; });
  var openCount = findings.filter(function(f) { return f.status === 'open' || f.status === 'triaged'; }).length;

  return (
    <div>
      {toast && <div className={'admin-banner ' + toast.tone} style={{ fontSize: 12, marginBottom: 12 }}>{toast.text}</div>}

      <div style={{ padding: 14, background: 'rgba(167,139,250,0.04)', border: '1px solid rgba(167,139,250,0.18)', borderRadius: 8, marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#a78bfa', marginBottom: 6 }}>FINDINGS</div>
        <div style={{ fontSize: 12, color: '#cbd5e1', lineHeight: 1.5 }}>
          Every finding logged against this engagement — Phase 2 attack-path analysis (auto-generated when status=executing), plus any manual findings added during testing or retest. Sorted by severity. Use the triage controls inline.
          {engagementStatus !== 'executing' && engagementStatus !== 'reporting' && engagementStatus !== 'debrief' && engagementStatus !== 'closed' && (
            <span style={{ color: '#fbbf24' }}> Attack-path analysis runs when the engagement is in Executing — until then, this tab shows zero auto-findings.</span>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {['all','critical','high','medium','low','informational'].map(function(sev) {
          var on = filterSeverity === sev;
          var c = sev === 'all' ? null : SEVERITY_COLORS[sev];
          var n = sev === 'all' ? findings.length : (counts[sev] || 0);
          return (
            <button key={sev} type="button" onClick={function() { setFilterSeverity(sev); }} style={{ background: on ? (c ? c.bg : 'rgba(167,139,250,0.15)') : 'transparent', border: '1px solid ' + (on ? (c ? c.fg : '#a78bfa') : 'rgba(255,255,255,0.1)'), color: on ? (c ? c.fg : '#a78bfa') : '#94a3b8', padding: '4px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
              {sev} <span style={{ marginLeft: 4, opacity: 0.7 }}>{n}</span>
            </button>
          );
        })}
        <div style={{ width: 1, background: 'rgba(255,255,255,0.08)', margin: '0 6px' }}></div>
        {['all','open','triaged','remediated','closed','accepted_risk'].map(function(st) {
          var on = filterStatus === st;
          var c = st === 'all' ? null : STATUS_COLORS[st];
          return (
            <button key={st} type="button" onClick={function() { setFilterStatus(st); }} style={{ background: on ? (c ? c.fg + '22' : 'rgba(167,139,250,0.15)') : 'transparent', border: '1px solid ' + (on ? (c ? c.fg : '#a78bfa') : 'rgba(255,255,255,0.1)'), color: on ? (c ? c.fg : '#a78bfa') : '#94a3b8', padding: '4px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
              {(c ? c.label : st)}
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', color: '#64748b', fontSize: 13, border: '1px dashed rgba(255,255,255,0.1)', borderRadius: 8 }}>
          {findings.length === 0
            ? 'No findings on this engagement yet. Attack-path findings auto-populate when status transitions to Executing. Manual findings can be added via the Findings API (manual create UI coming).'
            : 'No findings match the current filter. Clear filters to see all ' + findings.length + ' finding(s).'}
        </div>
      ) : (
        filtered.map(function(f) {
          var sevCol = SEVERITY_COLORS[f.severity] || SEVERITY_COLORS.informational;
          var statusCol = STATUS_COLORS[f.status] || STATUS_COLORS.open;
          var expanded = expandedId === f.id;
          return (
            <div key={f.id} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderLeft: '3px solid ' + sevCol.fg, borderRadius: 6, padding: 14, marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 3, background: sevCol.bg, color: sevCol.fg, letterSpacing: '0.5px' }}>{f.severity.toUpperCase()}</span>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 3, background: statusCol.fg + '22', color: statusCol.fg, letterSpacing: '0.5px' }}>{statusCol.label.toUpperCase()}</span>
                    {f.cvss && <span style={{ fontSize: 10, color: '#94a3b8' }}>CVSS {f.cvss}</span>}
                    {f.category && <span style={{ fontSize: 10, color: '#64748b' }}>{f.category}</span>}
                    <span style={{ fontSize: 10, color: '#64748b' }}>{new Date(f.discovered_at).toLocaleDateString()}</span>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 2 }}>{f.title}</div>
                  {f.description && <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.5 }}>{f.description}</div>}
                  {expanded && (
                    <div style={{ marginTop: 10, padding: 10, background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 4 }}>
                      {f.recommended_fix && (
                        <div style={{ marginBottom: 8 }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: '#22c55e', letterSpacing: '0.5px', marginBottom: 4 }}>RECOMMENDED FIX</div>
                          <div style={{ fontSize: 11, color: '#cbd5e1', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{f.recommended_fix}</div>
                        </div>
                      )}
                      {f.evidence && (
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: '#60a5fa', letterSpacing: '0.5px', marginBottom: 4 }}>EVIDENCE</div>
                          <pre style={{ fontSize: 10, color: '#94a3b8', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{typeof f.evidence === 'string' ? f.evidence : JSON.stringify(f.evidence, null, 2)}</pre>
                        </div>
                      )}
                      {f.notes && (
                        <div style={{ marginTop: 8, fontSize: 11, color: '#94a3b8' }}>{f.notes}</div>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 'none' }}>
                  <select value={f.status} onChange={function(e) { updateFinding(f, { status: e.target.value }); }} disabled={busy} style={{ width: 140, padding: 6, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, color: '#fff', fontSize: 11 }}>
                    <option value="open">Open</option>
                    <option value="triaged">Triaged</option>
                    <option value="remediated">Remediated</option>
                    <option value="retested">Retested</option>
                    <option value="accepted_risk">Accepted risk</option>
                    <option value="closed">Closed</option>
                  </select>
                  <button type="button" className="btn btn-sm" onClick={function() { setExpandedId(expanded ? null : f.id); }}>{expanded ? 'Hide' : 'Details'}</button>
                </div>
              </div>
            </div>
          );
        })
      )}

      {findings.length > 0 && (
        <div style={{ marginTop: 16, fontSize: 11, color: '#64748b', textAlign: 'center' }}>
          {findings.length} total · {openCount} open or triaged
        </div>
      )}
    </div>
  );
}

// ── EngagementActivity (S1.8) ───────────────────────────────────────
// Timeline of audit-log events for this engagement. Every Red Team
// route writes audit rows with engagement_id in metadata; this tab
// reads them back chronologically. Grouped by day, color-coded by
// severity. The "what happened when" view every mature pen-test
// platform has (PlexTrac calls this Activity, Cobalt calls it History).
function EngagementActivity({ clientId, engagementId }) {
  var [events, setEvents] = useState(null);

  function refresh() {
    authFetch(API + '/clients/' + clientId + '/redteam/engagements/' + engagementId + '/activity')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) { setEvents(d ? (d.events || []) : []); })
      .catch(function() { setEvents([]); });
  }
  useEffect(refresh, [clientId, engagementId]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (events === null) return <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)' }}>Loading activity…</div>;

  // Pretty event-type labels
  var EVENT_LABELS = {
    REDTEAM_ENGAGEMENT_CREATED: 'Engagement created',
    REDTEAM_ENGAGEMENT_UPDATED: 'Engagement updated',
    REDTEAM_ATTACK_SURFACE_ANALYSED: 'Attack-path analysis run',
    REDTEAM_FINDING_UPDATED: 'Finding updated',
    CATALOG_APP_CREATED: 'Catalog app added',
    CATALOG_APP_UPDATED: 'Catalog app updated',
    CATALOG_APP_DELETED: 'Catalog app deleted',
    CATALOG_APP_IMPORT_GRAPH: 'Apps imported from Graph',
    CATALOG_API_CREATED: 'Catalog API added',
    CATALOG_API_UPDATED: 'Catalog API updated',
    CATALOG_API_DELETED: 'Catalog API deleted',
    CATALOG_ATTACK_SURFACE_CREATED: 'Attack-surface entry added',
    CATALOG_ATTACK_SURFACE_BULK: 'Attack-surface bulk import',
    CATALOG_ATTACK_SURFACE_DELETED: 'Attack-surface entry removed',
    CATALOG_INTERNAL_SCOPE_CREATED: 'Internal-scope entry added',
    CATALOG_INTERNAL_SCOPE_BULK: 'Internal-scope bulk import',
    CATALOG_INTERNAL_SCOPE_DELETED: 'Internal-scope entry removed',
    CATALOG_CLOUD_TENANT_CREATED: 'Cloud tenant added',
    CATALOG_CLOUD_TENANT_DELETED: 'Cloud tenant removed',
    CATALOG_SAAS_CREATED: 'SaaS provider added',
    CATALOG_SAAS_UPDATED: 'SaaS provider updated',
    CATALOG_SAAS_DELETED: 'SaaS provider removed',
    ENGAGEMENT_CONTACT_UPSERTED: 'Contact saved',
    ENGAGEMENT_CONTACT_DELETED: 'Contact cleared',
    ENGAGEMENT_CHECKLIST_UPDATED: 'Checklist item updated',
    ENGAGEMENT_ATTESTATION_CREATED: 'Attestation added',
    ENGAGEMENT_ATTESTATION_UPDATED: 'Attestation updated',
    ENGAGEMENT_ATTESTATION_DELETED: 'Attestation deleted'
  };

  var SEVERITY_COLORS = {
    INFO: '#60a5fa',
    WARN: '#fbbf24',
    ERROR: '#f87171'
  };

  if (events.length === 0) {
    return (
      <div>
        <div style={{ padding: 14, background: 'rgba(167,139,250,0.04)', border: '1px solid rgba(167,139,250,0.18)', borderRadius: 8, marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#a78bfa', marginBottom: 6 }}>ACTIVITY TIMELINE</div>
          <div style={{ fontSize: 12, color: '#cbd5e1', lineHeight: 1.5 }}>
            Every action on this engagement writes an audit row — engagement updates, scope edits, contact saves, attestation signoffs, finding triage. The timeline gives you (or the auditor) a chronological "what happened when" without leaving Aurelius.
          </div>
        </div>
        <div style={{ padding: 32, textAlign: 'center', color: '#64748b', fontSize: 13, border: '1px dashed rgba(255,255,255,0.1)', borderRadius: 8 }}>
          No activity yet for this engagement.
        </div>
      </div>
    );
  }

  // Group by day
  var groups = {};
  events.forEach(function(e) {
    var day = new Date(e.created_at).toLocaleDateString();
    if (!groups[day]) groups[day] = [];
    groups[day].push(e);
  });
  var dayOrder = Object.keys(groups);

  function describeEvent(e) {
    var label = EVENT_LABELS[e.event_type] || e.event_type;
    var m = e.metadata || {};
    var extra = '';
    if (m.name) extra = ' — ' + m.name;
    else if (m.kind) extra = ' — ' + String(m.kind).replace(/_/g, ' ');
    else if (m.role) extra = ' — ' + String(m.role).replace(/_/g, ' ');
    else if (m.item_key) extra = ' — ' + String(m.item_key).replace(/_/g, ' ');
    else if (m.prev_status && m.new_status) extra = ' — ' + m.prev_status + ' → ' + m.new_status;
    else if (m.new_status) extra = ' — ' + m.new_status;
    else if (m.imported !== undefined) extra = ' — ' + m.imported + ' imported, ' + (m.skipped || 0) + ' skipped';
    else if (m.added !== undefined) extra = ' — ' + m.added + ' added, ' + (m.duplicates || 0) + ' dupes';
    return { label: label, extra: extra };
  }

  return (
    <div>
      <div style={{ padding: 14, background: 'rgba(167,139,250,0.04)', border: '1px solid rgba(167,139,250,0.18)', borderRadius: 8, marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#a78bfa', marginBottom: 6 }}>ACTIVITY TIMELINE</div>
        <div style={{ fontSize: 12, color: '#cbd5e1', lineHeight: 1.5 }}>
          {events.length} event{events.length === 1 ? '' : 's'} for this engagement. Most recent first. Hover over a row for the raw metadata payload.
        </div>
      </div>

      {dayOrder.map(function(day) {
        return (
          <div key={day} style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.6px', color: '#64748b', marginBottom: 8, textTransform: 'uppercase' }}>{day}</div>
            <div style={{ position: 'relative', paddingLeft: 24 }}>
              <div style={{ position: 'absolute', left: 8, top: 6, bottom: 6, width: 2, background: 'rgba(255,255,255,0.06)' }}></div>
              {groups[day].map(function(e) {
                var d = describeEvent(e);
                var color = SEVERITY_COLORS[e.severity] || '#94a3b8';
                var time = new Date(e.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                return (
                  <div key={e.id} style={{ position: 'relative', padding: '6px 0', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    <div style={{ position: 'absolute', left: -22, top: 12, width: 10, height: 10, borderRadius: '50%', background: color, border: '2px solid var(--bg-base)' }}></div>
                    <div style={{ fontSize: 11, color: '#64748b', fontFamily: 'monospace', flex: 'none', minWidth: 52 }}>{time}</div>
                    <div style={{ flex: 1, minWidth: 0 }} title={JSON.stringify(e.metadata || {}, null, 2)}>
                      <div style={{ fontSize: 12, color: '#fff' }}>{d.label}<span style={{ color: '#94a3b8' }}>{d.extra}</span></div>
                      {e.user_email && <div style={{ fontSize: 10, color: '#64748b' }}>{e.user_email}{e.ip ? ' · ' + e.ip : ''}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── EngagementReportsTab (S1.8) ─────────────────────────────────────
// Placeholder Reports surface inside the workspace. Lists the per-
// engagement deliverables. PDF generation lands in the next slice
// (S1.9 — Reports + scope-lock). For now: show the cards, link to
// the existing RedTeamReports component for any working report types,
// and mark forthcoming ones explicitly so the operator knows the
// roadmap.
function EngagementReportsTab({ clientId, engagement }) {
  var [busy, setBusy] = useState(false);
  var [toast, setToast] = useState(null);
  function showToast(tone, text) { setToast({ tone: tone, text: text }); setTimeout(function() { setToast(null); }, 4000); }

  function openPackage() {
    setBusy(true);
    authFetch(API + '/clients/' + clientId + '/redteam/engagements/' + engagement.id + '/pre-engagement-package')
      .then(function(r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
      .then(function(html) {
        setBusy(false);
        var w = window.open('', '_blank');
        if (!w) { showToast('error', 'Pop-up blocked. Allow pop-ups for this site.'); return; }
        w.document.open();
        w.document.write(html);
        w.document.close();
      })
      .catch(function(err) { setBusy(false); showToast('error', 'Failed to load package: ' + err.message); });
  }

  var DELIVERABLES = [
    {
      key: 'pre_engagement_pdf',
      title: 'Pre-engagement package',
      desc: 'The canonical engagement record — scope, contacts, attestations, compliance lens, signature blocks. Reads from snapshot tables when scope-locked, from the live engagement otherwise. Opens print-friendly HTML in a new window; print to PDF (Ctrl/Cmd+P) for the final artifact.',
      status: 'live',
      action: busy ? 'Loading…' : 'Open print view',
      onClick: openPackage
    },
    {
      key: 'findings_export',
      title: 'Findings export (JSON / CSV)',
      desc: 'Engagement-scoped findings export for ingestion into Confluence / Jira / external SSP tools.',
      status: 'coming',
      action: 'Coming in S1.9b'
    },
    {
      key: 'exec_summary',
      title: 'Executive summary PDF',
      desc: 'Audience: signatory, board, audit. 4-8 page narrative summary with business risk framing.',
      status: 'coming',
      action: 'Coming after reporting phase'
    },
    {
      key: 'technical_report',
      title: 'Technical report PDF',
      desc: 'Audience: technical lead, engineers. Per-finding write-ups with reproduction steps, evidence, CVSS, remediation guidance.',
      status: 'coming',
      action: 'Coming after reporting phase'
    },
    {
      key: 'closeout_deck',
      title: 'Close-out meeting deck (PPTX)',
      desc: 'Walkthrough deck for the debrief meeting. Findings, remediation priorities, retest schedule.',
      status: 'coming',
      action: 'Coming after reporting phase'
    }
  ];

  return (
    <div>
      {toast && <div className={'admin-banner ' + toast.tone} style={{ fontSize: 12, marginBottom: 12 }}>{toast.text}</div>}
      <div style={{ padding: 14, background: 'rgba(167,139,250,0.04)', border: '1px solid rgba(167,139,250,0.18)', borderRadius: 8, marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#a78bfa', marginBottom: 6 }}>ENGAGEMENT REPORTS</div>
        <div style={{ fontSize: 12, color: '#cbd5e1', lineHeight: 1.5 }}>
          Every deliverable for this engagement renders from the engagement record — no separate template maintenance. Reports are scoped to this engagement only, so authorship and audit defense are clean. {engagement.status === 'scoping' || engagement.status === 'roe_signed' || engagement.status === 'scope_locked' ? 'The pre-engagement package is the most useful deliverable to generate before testing begins.' : 'Once findings are in, exec summary and technical report become the primary output.'}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
        {DELIVERABLES.map(function(d) {
          var isLive = d.status === 'live';
          return (
            <div key={d.key} style={{ padding: 16, background: isLive ? 'rgba(34,197,94,0.04)' : 'rgba(255,255,255,0.02)', border: '1px solid ' + (isLive ? 'rgba(34,197,94,0.25)' : 'rgba(255,255,255,0.06)'), borderRadius: 8, opacity: isLive ? 1 : 0.7 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{d.title}</span>
                {isLive && <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3, background: 'rgba(34,197,94,0.2)', color: '#22c55e', letterSpacing: '0.5px' }}>LIVE</span>}
                {!isLive && <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3, background: 'rgba(100,116,139,0.2)', color: '#94a3b8', letterSpacing: '0.5px' }}>ROADMAP</span>}
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.5, marginBottom: 10 }}>{d.desc}</div>
              <button type="button" className={isLive ? 'btn btn-sm btn-primary' : 'btn btn-sm'} disabled={!isLive || busy} onClick={d.onClick} title={isLive ? '' : 'Not yet implemented'}>{d.action}</button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── PhaseStepper (S1.7) ─────────────────────────────────────────────
// Stripe/Cobalt-style horizontal phase indicator. Shows the engagement
// lifecycle at a glance — past steps marked complete, current step
// highlighted purple, future steps muted. scope_locked + signoff steps
// are reserved for the upcoming magic-link slice; they appear muted in
// the stepper as a roadmap signal.
function PhaseStepper({ status }) {
  var PHASES = [
    { key: 'scoping', label: 'Scoping' },
    { key: 'scope_locked', label: 'Scope locked' },
    { key: 'pending_client_signoff', label: 'Client signoff' },
    { key: 'roe_signed', label: 'ROE signed' },
    { key: 'executing', label: 'Executing' },
    { key: 'reporting', label: 'Reporting' },
    { key: 'debrief', label: 'Debrief' },
    { key: 'closed', label: 'Closed' }
  ];

  // Build progression — past phases marked complete, current marked active.
  var currentIdx = PHASES.findIndex(function(p) { return p.key === status; });
  // If cancelled, none of the phases are "active" — show all muted with cancelled badge above.
  var isCancelled = status === 'cancelled';

  return (
    <div style={{ padding: '12px 0' }}>
      {isCancelled && (
        <div style={{ marginBottom: 10, fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 4, background: 'rgba(248,113,113,0.15)', color: '#f87171', display: 'inline-block', letterSpacing: '0.5px' }}>ENGAGEMENT CANCELLED</div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 0, opacity: isCancelled ? 0.4 : 1 }}>
        {PHASES.map(function(p, i) {
          var done = !isCancelled && currentIdx !== -1 && i < currentIdx;
          var active = !isCancelled && currentIdx !== -1 && i === currentIdx;
          var future = !done && !active;
          var muted = future && p.future;
          var color = done ? '#22c55e' : active ? '#a78bfa' : muted ? '#475569' : '#64748b';
          var bg = done ? 'rgba(34,197,94,0.15)' : active ? 'rgba(167,139,250,0.18)' : 'transparent';
          return (
            <React.Fragment key={p.key}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 'none', minWidth: 0 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: bg,
                  border: '2px solid ' + color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 700, color: color,
                  boxShadow: active ? '0 0 12px rgba(167,139,250,0.4)' : 'none',
                  transition: 'all 0.2s ease'
                }}>
                  {done ? '✓' : (i + 1)}
                </div>
                <div style={{ marginTop: 6, fontSize: 10, fontWeight: 600, color: color, textAlign: 'center', letterSpacing: '0.3px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                  {p.label}
                </div>
                {p.future && future && (
                  <div style={{ marginTop: 2, fontSize: 8, color: '#475569', letterSpacing: '0.4px' }}>SOON</div>
                )}
              </div>
              {i < PHASES.length - 1 && (
                <div style={{
                  flex: 1, height: 2, minWidth: 12,
                  background: done ? '#22c55e' : 'rgba(255,255,255,0.06)',
                  margin: '0 4px',
                  marginTop: -16
                }}></div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// ── EngagementReadinessGate (S1.7) ──────────────────────────────────
// Calculates blockers for the next phase transition. PlexTrac/Cobalt
// surface this as a checklist that tells the operator exactly what is
// keeping them from advancing. Each row is green (clear), amber
// (pending), or red (blocker). Until magic-link signoff lands, the
// operator advances scoping → roe_signed manually after handling the
// ATT letter offline; this panel tells them when they're ready to do
// that without missing anything.
function EngagementReadinessGate({ engagement, contacts, checklist, attestations, scopeCounts }) {
  if (!engagement || !contacts || !checklist || !attestations) return null;

  function findContact(role) { return contacts.find(function(c) { return c.role === role; }); }
  function findAttest(kind) { return attestations.find(function(a) { return a.kind === kind; }); }
  function attestSettled(kind) {
    var a = findAttest(kind);
    return a && (a.status === 'signed' || a.status === 'waived' || a.status === 'not_applicable');
  }

  var sig = findContact('engagement_signatory');
  var primary = findContact('primary');
  var totalScope = (scopeCounts && (scopeCounts.app + scopeCounts.api + scopeCounts.attack + scopeCounts.internal + scopeCounts.cloud + scopeCounts.saas)) || 0;
  var requiredAttestKinds = ['cyber_liability_insurance','backup_restore_tested','tester_eo_insurance','tester_gl_insurance'];
  var clearedAttest = requiredAttestKinds.filter(attestSettled).length;
  var requiredChecklistKeys = ['signatory_identified','escalation_contact_identified','backup_attestation_captured','insurance_attestation_captured','att_letter_signed'];
  var checklistMap = {};
  (checklist || []).forEach(function(c) { checklistMap[c.item_key] = c; });
  var clearedChecklist = requiredChecklistKeys.filter(function(k) {
    var item = checklistMap[k];
    return item && (item.status === 'completed' || item.status === 'waived');
  }).length;

  // Build readiness rows. Each: { label, status: 'ok'|'pending'|'blocker', hint }
  var rows = [
    {
      label: 'Engagement type set',
      status: engagement.engagement_type ? 'ok' : 'blocker',
      hint: 'Set on the Record tab'
    },
    {
      label: 'Engagement window dates set',
      status: (engagement.engagement_window_start && engagement.engagement_window_end) ? 'ok' : 'pending',
      hint: 'Record tab → Testing window'
    },
    {
      label: 'At least 1 scope item picked',
      status: totalScope > 0 ? 'ok' : 'blocker',
      hint: 'Scope tab — picks from the Catalog'
    },
    {
      label: 'Engagement signatory assigned (with email)',
      status: sig && sig.email ? 'ok' : 'blocker',
      hint: 'Contacts tab — required for ATT letter signoff'
    },
    {
      label: 'Primary contact assigned',
      status: primary ? 'ok' : 'blocker',
      hint: 'Contacts tab'
    },
    {
      label: 'Required attestations settled (' + clearedAttest + '/' + requiredAttestKinds.length + ')',
      status: clearedAttest === requiredAttestKinds.length ? 'ok' : 'blocker',
      hint: 'Attestations tab — sign, waive, or mark not-applicable'
    },
    {
      label: 'Authorization-to-Test letter on file',
      status: engagement.authorization_letter_url ? 'ok' : 'blocker',
      hint: 'Authorization tab — paste signed letter URL'
    },
    {
      label: 'Core checklist items cleared (' + clearedChecklist + '/' + requiredChecklistKeys.length + ')',
      status: clearedChecklist === requiredChecklistKeys.length ? 'ok' : 'pending',
      hint: 'Readiness checklist tab'
    }
  ];

  var blockers = rows.filter(function(r) { return r.status === 'blocker'; }).length;
  var ready = blockers === 0;
  var headlineColor = ready ? '#22c55e' : '#fbbf24';
  var headlineText = ready ? 'Ready to transition to ROE signed' : (blockers + ' blocker' + (blockers === 1 ? '' : 's') + ' before ROE signed');

  function rowDot(status) {
    var color = status === 'ok' ? '#22c55e' : status === 'pending' ? '#fbbf24' : '#f87171';
    var glyph = status === 'ok' ? '✓' : status === 'pending' ? '◐' : '✕';
    return <span style={{ display: 'inline-flex', width: 18, height: 18, borderRadius: '50%', background: color + '22', color: color, alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>{glyph}</span>;
  }

  return (
    <div style={{ padding: 18, background: 'rgba(255,255,255,0.02)', border: '1px solid ' + (ready ? 'rgba(34,197,94,0.3)' : 'rgba(251,191,36,0.3)'), borderRadius: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <div style={{ width: 36, height: 36, borderRadius: '50%', background: headlineColor + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', color: headlineColor, fontSize: 18, fontWeight: 700 }}>{ready ? '✓' : '!'}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: headlineColor, marginBottom: 2 }}>READINESS GATE</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{headlineText}</div>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {rows.map(function(r, i) {
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: 10, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 6 }}>
              <div style={{ marginTop: 1 }}>{rowDot(r.status)}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#fff' }}>{r.label}</div>
                <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>{r.hint}</div>
              </div>
            </div>
          );
        })}
      </div>
      {!ready && (
        <div style={{ marginTop: 12, padding: 10, background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: 6, fontSize: 11, color: '#fbbf24' }}>
          Resolve red blockers above before transitioning the engagement. Amber rows are recommended but not blocking — they will be enforced once scope-lock + magic-link client signoff land.
        </div>
      )}
    </div>
  );
}

// ── EngagementAttestationsSection (S1.6) ────────────────────────────
// Section 10 of the pre-engagement package — the written attestations
// that gate scope-lock. 4 are auto-seeded with the engagement (client
// cyber liability, client backup, tester E&O, tester GL); operator adds
// the rest on demand (SaaS approvals, DPA for GDPR, cross-border).
// Magic-link client signoff (S1.7+) will flip status to 'signed' via
// the public route; admin can also override when offline confirmation
// exists (PDF cert, email attestation, etc.).
function EngagementAttestationsSection({ clientId, engagementId, attestations, saasCatalog, onChange }) {
  var [busy, setBusy] = useState(false);
  var [toast, setToast] = useState(null);
  var [signoffOpen, setSignoffOpen] = useState({});
  var [signoffForm, setSignoffForm] = useState({});
  var [showAdd, setShowAdd] = useState(false);
  var [addForm, setAddForm] = useState({ kind: 'saas_approval', saas_provider: '', notes: '' });

  function showToast(tone, text) { setToast({ tone: tone, text: text }); setTimeout(function() { setToast(null); }, 4000); }

  var STANDARD_KINDS = ['cyber_liability_insurance','backup_restore_tested','tester_eo_insurance','tester_gl_insurance'];

  var KIND_META = {
    cyber_liability_insurance: {
      label: 'Client cyber liability insurance',
      who: 'Client',
      help: 'Client confirms cyber liability insurance is in force during the engagement window. Carrier name + policy reference go in evidence_url or notes.',
      required: true
    },
    backup_restore_tested: {
      label: 'Backup restore tested',
      who: 'Client',
      help: 'Client confirms recent backups exist, have been tested, and the RPO is acceptable in case testing causes unforeseen impact. Hard prerequisite — testing team will not begin without this.',
      required: true
    },
    carrier_notified: {
      label: 'Insurance carrier notified',
      who: 'Client',
      help: 'If the client carrier requires advance notice of penetration testing, confirm notification was sent. Skip with status=not_applicable when the policy does not require it.',
      required: false
    },
    tester_eo_insurance: {
      label: 'Testing team E&O insurance',
      who: 'Testing team',
      help: 'CyberVault / CBG confirms errors & omissions insurance is in force during the engagement window.',
      required: true
    },
    tester_gl_insurance: {
      label: 'Testing team general liability insurance',
      who: 'Testing team',
      help: 'CyberVault / CBG confirms general liability insurance is in force.',
      required: true
    },
    saas_approval: {
      label: 'SaaS provider approval',
      who: 'Client + provider',
      help: 'Each in-scope third-party SaaS provider must have its own approval status row (Appendix B of the pre-engagement package). Set status=signed when the provider has authorized testing.',
      required: false
    },
    dpa: {
      label: 'Data Processing Agreement',
      who: 'Both parties',
      help: 'Required when the engagement may handle personal data of EU / UK data subjects. DPA defines the testing team as processor.',
      required: false
    },
    cross_border_jurisdiction: {
      label: 'Cross-border jurisdiction acknowledgement',
      who: 'Both parties',
      help: 'Add when any in-scope systems are hosted, or any in-scope users reside, in a jurisdiction with restrictive computer-misuse, data-protection, or export-control law.',
      required: false
    }
  };

  var STATUS_COLORS = {
    pending: { fg: '#fbbf24', bg: 'rgba(251,191,36,0.12)', label: 'Pending' },
    signed: { fg: '#22c55e', bg: 'rgba(34,197,94,0.12)', label: 'Signed' },
    waived: { fg: '#94a3b8', bg: 'rgba(100,116,139,0.15)', label: 'Waived' },
    not_applicable: { fg: '#94a3b8', bg: 'rgba(100,116,139,0.15)', label: 'N/A' }
  };

  function updateRow(row, patch) {
    setBusy(true);
    authFetch(API + '/clients/' + clientId + '/redteam/engagements/' + engagementId + '/attestations/' + row.id, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch)
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusy(false);
        if (x.ok) { showToast('success', 'Saved'); if (onChange) onChange(); }
        else { showToast('error', x.d.error || 'Failed'); }
      })
      .catch(function() { setBusy(false); showToast('error', 'Request failed'); });
  }

  function submitSignoff(row) {
    var f = signoffForm[row.id] || {};
    if (!f.signed_by || !f.signed_by.trim()) { showToast('error', 'Signatory name required'); return; }
    updateRow(row, { status: 'signed', signed_by: f.signed_by.trim(), evidence_url: f.evidence_url || null, notes: f.notes || row.notes || null });
    setSignoffOpen(Object.assign({}, signoffOpen, { [row.id]: false }));
    setSignoffForm(Object.assign({}, signoffForm, { [row.id]: undefined }));
  }

  function addAttestation() {
    if (addForm.kind === 'saas_approval' && !addForm.saas_provider) { showToast('error', 'SaaS provider required'); return; }
    setBusy(true);
    authFetch(API + '/clients/' + clientId + '/redteam/engagements/' + engagementId + '/attestations', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        kind: addForm.kind,
        saas_provider: addForm.kind === 'saas_approval' ? addForm.saas_provider : null,
        notes: addForm.notes || null
      })
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusy(false);
        if (x.ok) { showToast('success', 'Added'); setShowAdd(false); setAddForm({ kind: 'saas_approval', saas_provider: '', notes: '' }); if (onChange) onChange(); }
        else { showToast('error', x.d.error || 'Failed'); }
      })
      .catch(function() { setBusy(false); showToast('error', 'Request failed'); });
  }

  function deleteRow(row) {
    if (!window.confirm('Delete this attestation? (Standard ones cannot be deleted — mark them as Not Applicable instead.)')) return;
    setBusy(true);
    authFetch(API + '/clients/' + clientId + '/redteam/engagements/' + engagementId + '/attestations/' + row.id, { method: 'DELETE' })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) { setBusy(false); if (x.ok) { showToast('success', 'Deleted'); if (onChange) onChange(); } else { showToast('error', x.d.error || 'Failed'); } })
      .catch(function() { setBusy(false); showToast('error', 'Request failed'); });
  }

  if (attestations === null) return <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)' }}>Loading attestations…</div>;

  var inputStyle = { width: '100%', padding: 8, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, color: '#fff', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' };

  var requiredSigned = attestations.filter(function(a) { return STANDARD_KINDS.indexOf(a.kind) !== -1 && (a.status === 'signed' || a.status === 'not_applicable'); }).length;
  var requiredTotal = STANDARD_KINDS.length;

  return (
    <div>
      {toast && <div className={'admin-banner ' + toast.tone} style={{ fontSize: 12, marginBottom: 12 }}>{toast.text}</div>}

      <div style={{ padding: 14, background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: 8, marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#fbbf24', marginBottom: 4 }}>WHY THIS SECTION MATTERS</div>
        <div style={{ fontSize: 12, color: '#cbd5e1', lineHeight: 1.5 }}>
          Penetration testing without written attestations is uninsured risk for both parties. These rows capture, in the engagement record, that the client has cyber liability insurance, that backups are tested, that the testing team carries its own E&amp;O and GL, and that every third-party SaaS in scope has been cleared. Once magic-link signoff lands, the client signatory will be able to sign these directly. Until then, mark <strong>signed</strong> manually when you have offline confirmation (PDF cert, email attestation, ticket).
          {' '}<strong>{requiredSigned} of {requiredTotal} required attestations cleared.</strong>
        </div>
      </div>

      {attestations.map(function(row) {
        var meta = KIND_META[row.kind] || { label: row.kind, who: '—', help: '', required: false };
        var color = STATUS_COLORS[row.status] || STATUS_COLORS.pending;
        var open = signoffOpen[row.id];
        var f = signoffForm[row.id] || { signed_by: row.signed_by || '', evidence_url: row.evidence_url || '', notes: row.notes || '' };
        return (
          <div key={row.id} style={{ padding: 14, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderLeft: '3px solid ' + color.fg, borderRadius: 6, marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{meta.label}{row.saas_provider && <span style={{ color: '#a78bfa' }}> · {row.saas_provider}</span>}</span>
                  <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3, background: color.bg, color: color.fg, letterSpacing: '0.4px' }}>{color.label.toUpperCase()}</span>
                  {meta.required && <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3, background: 'rgba(251,191,36,0.15)', color: '#fbbf24', letterSpacing: '0.4px' }}>REQUIRED</span>}
                  <span style={{ fontSize: 9, color: '#64748b' }}>SIGNED BY: {meta.who}</span>
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6 }}>{meta.help}</div>
                {row.signed_by && <div style={{ fontSize: 11, color: '#22c55e' }}>✓ Signed by {row.signed_by}{row.signed_at ? ' on ' + new Date(row.signed_at).toLocaleDateString() : ''}</div>}
                {row.evidence_url && <div style={{ fontSize: 11, color: '#60a5fa', marginTop: 2 }}><a href={row.evidence_url} target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa', textDecoration: 'none' }}>Evidence ↗</a></div>}
                {row.notes && <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{row.notes}</div>}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {row.status !== 'signed' && (
                  <button type="button" className="btn btn-sm btn-primary" onClick={function() { setSignoffOpen(Object.assign({}, signoffOpen, { [row.id]: !open })); }} disabled={busy}>{open ? 'Cancel' : 'Mark signed'}</button>
                )}
                {row.status === 'signed' && (
                  <button type="button" className="btn btn-sm" onClick={function() { if (window.confirm('Revert this attestation to pending?')) updateRow(row, { status: 'pending' }); }} disabled={busy}>Revert</button>
                )}
                <select value={row.status} onChange={function(e) { updateRow(row, { status: e.target.value }); }} disabled={busy} style={Object.assign({}, inputStyle, { width: 130, fontSize: 12 })}>
                  <option value="pending">Pending</option>
                  <option value="signed">Signed</option>
                  <option value="waived">Waived</option>
                  <option value="not_applicable">Not applicable</option>
                </select>
                {STANDARD_KINDS.indexOf(row.kind) === -1 && (
                  <button type="button" className="btn btn-sm btn-danger" onClick={function() { deleteRow(row); }} disabled={busy}>Delete</button>
                )}
              </div>
            </div>
            {open && (
              <div style={{ marginTop: 10, padding: 12, background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 6 }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.6px', color: '#22c55e', marginBottom: 8 }}>RECORD OFFLINE SIGNOFF</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                  <label style={{ fontSize: 11, color: '#94a3b8' }}>Signatory name<input type="text" value={f.signed_by} onChange={function(e) { setSignoffForm(Object.assign({}, signoffForm, { [row.id]: Object.assign({}, f, { signed_by: e.target.value }) })); }} placeholder="Name of person who signed off" style={inputStyle} /></label>
                  <label style={{ fontSize: 11, color: '#94a3b8' }}>Evidence URL<input type="text" value={f.evidence_url} onChange={function(e) { setSignoffForm(Object.assign({}, signoffForm, { [row.id]: Object.assign({}, f, { evidence_url: e.target.value }) })); }} placeholder="https://...  (cert PDF, ticket, email link)" style={inputStyle} /></label>
                </div>
                <label style={{ fontSize: 11, color: '#94a3b8' }}>Notes<input type="text" value={f.notes} onChange={function(e) { setSignoffForm(Object.assign({}, signoffForm, { [row.id]: Object.assign({}, f, { notes: e.target.value }) })); }} placeholder="Policy ref, carrier name, expiration, etc." style={inputStyle} /></label>
                <div style={{ marginTop: 10 }}>
                  <button type="button" className="btn btn-sm btn-primary" onClick={function() { submitSignoff(row); }} disabled={busy}>Confirm signed</button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      <div style={{ marginTop: 16, padding: 14, background: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.12)', borderRadius: 8 }}>
        {!showAdd ? (
          <button type="button" className="btn btn-primary" onClick={function() { setShowAdd(true); }}>+ Add SaaS / DPA / Cross-border attestation</button>
        ) : (
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#a78bfa', marginBottom: 10 }}>ADD ATTESTATION</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
              <label style={{ fontSize: 11, color: '#94a3b8' }}>Kind
                <select value={addForm.kind} onChange={function(e) { setAddForm(Object.assign({}, addForm, { kind: e.target.value })); }} style={inputStyle}>
                  <option value="saas_approval">SaaS provider approval</option>
                  <option value="carrier_notified">Insurance carrier notified</option>
                  <option value="dpa">Data Processing Agreement (GDPR / UK GDPR)</option>
                  <option value="cross_border_jurisdiction">Cross-border jurisdiction</option>
                </select>
              </label>
              {addForm.kind === 'saas_approval' && (
                <label style={{ fontSize: 11, color: '#94a3b8' }}>SaaS provider
                  <select value={addForm.saas_provider} onChange={function(e) { setAddForm(Object.assign({}, addForm, { saas_provider: e.target.value })); }} style={inputStyle}>
                    <option value="">— pick from catalog —</option>
                    {(saasCatalog || []).map(function(s) { return <option key={s.id} value={s.provider}>{s.provider}{s.provider_label ? ' · ' + s.provider_label : ''}</option>; })}
                  </select>
                </label>
              )}
            </div>
            <label style={{ fontSize: 11, color: '#94a3b8' }}>Notes (optional)<input type="text" value={addForm.notes} onChange={function(e) { setAddForm(Object.assign({}, addForm, { notes: e.target.value })); }} style={inputStyle} /></label>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button type="button" className="btn btn-primary" onClick={addAttestation} disabled={busy}>Add</button>
              <button type="button" className="btn" onClick={function() { setShowAdd(false); }} disabled={busy}>Cancel</button>
            </div>
            {addForm.kind === 'saas_approval' && (saasCatalog || []).length === 0 && (
              <div style={{ fontSize: 11, color: '#fbbf24', marginTop: 8 }}>No SaaS providers in catalog. Add them via Catalog → SaaS first.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── EngagementDetail (S1.2 — pre-engagement record + scope picker) ─
// Editable form for an engagement: the full pre-engagement record
// (engagement type, knowledge level, disclosure, threat profile,
// objectives, frameworks, compliance lens, exfil tolerance, window,
// hours, blackouts) plus a scope picker that selects apps + APIs from
// the client catalog. Scope selections persist to engagement.scope
// JSONB until snapshot tables land with scope-lock.
function EngagementDetail({ clientId, engagement, onSaved }) {
  var [catalogApps, setCatalogApps] = useState(null);
  var [catalogApis, setCatalogApis] = useState(null);
  var [catalogAttackSurface, setCatalogAttackSurface] = useState(null);
  var [catalogInternalScope, setCatalogInternalScope] = useState(null);
  var [catalogCloud, setCatalogCloud] = useState(null);
  var [catalogSaas, setCatalogSaas] = useState(null);
  var [contacts, setContacts] = useState(null);
  var [checklist, setChecklist] = useState(null);
  var [attestations, setAttestations] = useState(null);
  var [busy, setBusy] = useState(false);
  var [toast, setToast] = useState(null);
  var [section, setSection] = useState('overview');

  function showToast(tone, text) {
    setToast({ tone: tone, text: text });
    setTimeout(function() { setToast(null); }, 4500);
  }

  // Pull catalog so the scope picker has options.
  useEffect(function() {
    authFetch(API + '/clients/' + clientId + '/catalog/apps')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) { setCatalogApps(d ? (d.apps || []) : []); })
      .catch(function() { setCatalogApps([]); });
    authFetch(API + '/clients/' + clientId + '/catalog/apis')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) { setCatalogApis(d ? (d.apis || []) : []); })
      .catch(function() { setCatalogApis([]); });
    authFetch(API + '/clients/' + clientId + '/catalog/attack-surface')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) { setCatalogAttackSurface(d ? (d.entries || []) : []); })
      .catch(function() { setCatalogAttackSurface([]); });
    authFetch(API + '/clients/' + clientId + '/catalog/internal-scope')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) { setCatalogInternalScope(d ? (d.entries || []) : []); })
      .catch(function() { setCatalogInternalScope([]); });
    authFetch(API + '/clients/' + clientId + '/catalog/cloud-tenants')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) { setCatalogCloud(d ? (d.entries || []) : []); })
      .catch(function() { setCatalogCloud([]); });
    authFetch(API + '/clients/' + clientId + '/catalog/saas')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) { setCatalogSaas(d ? (d.entries || []) : []); })
      .catch(function() { setCatalogSaas([]); });
  }, [clientId]);

  // Load engagement-bound contacts + checklist
  function loadContacts() {
    authFetch(API + '/clients/' + clientId + '/redteam/engagements/' + engagement.id + '/contacts')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) { setContacts(d ? (d.contacts || []) : []); })
      .catch(function() { setContacts([]); });
  }
  function loadChecklist() {
    authFetch(API + '/clients/' + clientId + '/redteam/engagements/' + engagement.id + '/checklist')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) { setChecklist(d ? (d.items || []) : []); })
      .catch(function() { setChecklist([]); });
  }
  function loadAttestations() {
    authFetch(API + '/clients/' + clientId + '/redteam/engagements/' + engagement.id + '/attestations')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) { setAttestations(d ? (d.attestations || []) : []); })
      .catch(function() { setAttestations([]); });
  }
  useEffect(function() { loadContacts(); loadChecklist(); loadAttestations(); }, [clientId, engagement.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-fill form from the engagement record. scope JSONB may already
  // contain { app_ids, api_ids, out_of_scope_notes } from a prior save,
  // or it may be free-form text from before structured scope landed.
  function initialForm() {
    var sc = engagement.scope || {};
    if (typeof sc === 'string') sc = { description: sc };
    return {
      engagement_type: engagement.engagement_type || '',
      knowledge_level: engagement.knowledge_level || '',
      disclosure_model: engagement.disclosure_model || '',
      threat_profiles: Array.isArray(engagement.threat_profiles) ? engagement.threat_profiles : [],
      objectives_primary: Array.isArray(engagement.objectives_primary) ? engagement.objectives_primary : [],
      objectives_secondary: Array.isArray(engagement.objectives_secondary) ? engagement.objectives_secondary : [],
      frameworks: Array.isArray(engagement.frameworks) ? engagement.frameworks : [],
      compliance_frameworks: Array.isArray(engagement.compliance_frameworks) ? engagement.compliance_frameworks : [],
      exfil_tolerance: engagement.exfil_tolerance || '',
      exfil_custom_rules: engagement.exfil_custom_rules || '',
      engagement_window_start: engagement.engagement_window_start ? String(engagement.engagement_window_start).slice(0, 10) : '',
      engagement_window_end: engagement.engagement_window_end ? String(engagement.engagement_window_end).slice(0, 10) : '',
      engagement_timezone: engagement.engagement_timezone || '',
      testing_hours: Array.isArray(engagement.testing_hours) ? engagement.testing_hours : [],
      blackout_periods: Array.isArray(engagement.blackout_periods) ? engagement.blackout_periods : [],
      scope_app_ids: Array.isArray(sc.app_ids) ? sc.app_ids : [],
      scope_api_ids: Array.isArray(sc.api_ids) ? sc.api_ids : [],
      scope_attack_surface_ids: Array.isArray(sc.attack_surface_ids) ? sc.attack_surface_ids : [],
      scope_internal_scope_ids: Array.isArray(sc.internal_scope_ids) ? sc.internal_scope_ids : [],
      scope_cloud_tenant_ids: Array.isArray(sc.cloud_tenant_ids) ? sc.cloud_tenant_ids : [],
      scope_saas_ids: Array.isArray(sc.saas_ids) ? sc.saas_ids : [],
      scope_out_of_scope_notes: sc.out_of_scope_notes || '',
      scope_description_legacy: sc.description || '',
      lead_tester: engagement.lead_tester || '',
      authorization_letter_url: engagement.authorization_letter_url || '',
      notes: engagement.notes || ''
    };
  }
  var [form, setForm] = useState(initialForm);
  // Re-init form when a different engagement is expanded
  useEffect(function() { setForm(initialForm()); }, [engagement.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  function set(field, value) { setForm(Object.assign({}, form, { [field]: value })); }
  function toggleInArray(field, value) {
    var arr = (form[field] || []).slice();
    var i = arr.indexOf(value);
    if (i >= 0) arr.splice(i, 1); else arr.push(value);
    set(field, arr);
  }

  function handleSave() {
    setBusy(true);
    var sc = {
      app_ids: form.scope_app_ids,
      api_ids: form.scope_api_ids,
      attack_surface_ids: form.scope_attack_surface_ids,
      internal_scope_ids: form.scope_internal_scope_ids,
      cloud_tenant_ids: form.scope_cloud_tenant_ids,
      saas_ids: form.scope_saas_ids,
      out_of_scope_notes: form.scope_out_of_scope_notes || null,
      description: form.scope_description_legacy || null
    };
    var body = {
      engagement_type: form.engagement_type || null,
      knowledge_level: form.knowledge_level || null,
      disclosure_model: form.disclosure_model || null,
      threat_profiles: form.threat_profiles,
      objectives_primary: form.objectives_primary.filter(function(o) { return o && o.trim(); }),
      objectives_secondary: form.objectives_secondary.filter(function(o) { return o && o.trim(); }),
      frameworks: form.frameworks,
      compliance_frameworks: form.compliance_frameworks,
      exfil_tolerance: form.exfil_tolerance || null,
      exfil_custom_rules: form.exfil_tolerance === 'custom' ? (form.exfil_custom_rules || null) : null,
      engagement_window_start: form.engagement_window_start || null,
      engagement_window_end: form.engagement_window_end || null,
      engagement_timezone: form.engagement_timezone || null,
      testing_hours: form.testing_hours,
      blackout_periods: form.blackout_periods,
      scope: sc,
      lead_tester: form.lead_tester || null,
      authorization_letter_url: form.authorization_letter_url || null,
      notes: form.notes || null
    };
    authFetch(API + '/clients/' + clientId + '/redteam/engagements/' + engagement.id, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusy(false);
        if (x.ok) { showToast('success', 'Saved'); if (onSaved) onSaved(); }
        else { showToast('error', x.d.error || 'Save failed'); }
      })
      .catch(function() { setBusy(false); showToast('error', 'Request failed'); });
  }

  if (catalogApps === null || catalogApis === null || catalogAttackSurface === null || catalogInternalScope === null || catalogCloud === null || catalogSaas === null) return <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)' }}>Loading catalog…</div>;

  // ── Reusable form atoms (scoped to this component) ─────────────────
  var inputStyle = { width: '100%', padding: 8, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, color: '#fff', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' };
  var labelStyle = { fontSize: 11, color: '#94a3b8', display: 'block', marginBottom: 4 };
  var subSectionStyle = { padding: 16, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, marginBottom: 12 };

  function Chips(opts, field, columns) {
    columns = columns || 2;
    var picked = form[field] || [];
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(' + columns + ', 1fr)', gap: 6 }}>
        {opts.map(function(o) {
          var on = picked.indexOf(o.value) !== -1;
          return (
            <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: on ? '#fff' : '#cbd5e1', padding: '6px 10px', background: on ? 'rgba(167,139,250,0.12)' : 'rgba(255,255,255,0.02)', border: '1px solid ' + (on ? 'rgba(167,139,250,0.4)' : 'rgba(255,255,255,0.08)'), borderRadius: 4, cursor: 'pointer' }}>
              <input type="checkbox" checked={on} onChange={function() { toggleInArray(field, o.value); }} />
              <span>{o.label}</span>
            </label>
          );
        })}
      </div>
    );
  }

  var ENGAGEMENT_TYPES = [
    { value: 'vuln_assessment', label: 'Vulnerability Assessment' },
    { value: 'pentest', label: 'Penetration Test' },
    { value: 'red_team', label: 'Red Team Exercise' },
    { value: 'purple_team', label: 'Purple Team' },
    { value: 'appsec', label: 'Application Security' },
    { value: 'compliance_driven', label: 'Compliance-driven' }
  ];
  var THREAT_PROFILES = [
    { value: 'opportunistic_external', label: 'Opportunistic external' },
    { value: 'targeted_external', label: 'Targeted external (phishing-led)' },
    { value: 'insider_standard', label: 'Insider (standard user)' },
    { value: 'insider_privileged', label: 'Insider (privileged / rogue admin)' },
    { value: 'supply_chain', label: 'Supply-chain compromise' },
    { value: 'nation_state', label: 'Nation-state-grade' }
  ];
  var FRAMEWORKS = [
    { value: 'ptes', label: 'PTES' },
    { value: 'nist_800_115', label: 'NIST 800-115' },
    { value: 'owasp_wstg', label: 'OWASP WSTG' },
    { value: 'owasp_asvs', label: 'OWASP ASVS' },
    { value: 'owasp_api_top10', label: 'OWASP API Top 10' },
    { value: 'mitre_attack', label: 'MITRE ATT&CK' },
    { value: 'cis_v8', label: 'CIS Controls v8' },
    { value: 'aurelius_baseline', label: 'Aurelius baseline' }
  ];
  var COMPLIANCE = [
    { value: 'ferpa', label: 'FERPA (.edu)' },
    { value: 'glba', label: 'GLBA Safeguards (.edu Title IV)' },
    { value: 'nist_800_171', label: 'NIST 800-171 / CUI' },
    { value: 'cmmc_l1', label: 'CMMC L1' },
    { value: 'cmmc_l2', label: 'CMMC L2' },
    { value: 'hipaa', label: 'HIPAA' },
    { value: 'pci_dss', label: 'PCI DSS' },
    { value: 'soc2', label: 'SOC 2' },
    { value: 'iso_27001', label: 'ISO 27001' },
    { value: 'gdpr', label: 'GDPR / UK GDPR' },
    { value: 'state_breach', label: 'State breach laws' }
  ];
  var TESTING_HOURS = [
    { value: 'business', label: 'Business hours' },
    { value: 'after_hours', label: 'After hours' },
    { value: 'weekends', label: 'Weekends' },
    { value: '24x7', label: '24x7' }
  ];

  return (
    <div>
      {toast && <div className={'admin-banner ' + toast.tone} style={{ fontSize: 12, marginBottom: 12 }}>{toast.text}</div>}

      {/* Workspace nav grouped: Setup / Pre-engagement / Execution / Output.
          Mature pen-test platforms (PlexTrac / Cobalt) cluster engagement
          tabs by phase rather than presenting one flat row, so the operator
          can see where they are in the lifecycle. */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '1px solid rgba(255,255,255,0.06)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {(function() {
          var executing = ['executing','reporting','debrief','closed'].indexOf(engagement.status) !== -1;
          var GROUPS = [
            { label: 'Setup', tabs: [{ key: 'overview', label: 'Overview' }] },
            { label: 'Pre-engagement', tabs: [
              { key: 'record', label: 'Record' },
              { key: 'scope', label: 'Scope' },
              { key: 'contacts', label: 'Contacts' },
              { key: 'checklist', label: 'Checklist' },
              { key: 'attestations', label: 'Attestations' },
              { key: 'auth', label: 'Authorization' }
            ] },
            { label: 'Execution', tabs: [{ key: 'findings', label: 'Findings' }]
              .concat(executing ? [{ key: 'attack_surface', label: 'Attack surface' }, { key: 'scanners', label: 'Scanners' }] : [])
              .concat([{ key: 'activity', label: 'Activity' }]) },
            { label: 'Output', tabs: [{ key: 'reports', label: 'Reports' }] }
          ];
          var nodes = [];
          GROUPS.forEach(function(g, gi) {
            if (gi > 0) {
              nodes.push(<div key={'sep-' + gi} style={{ width: 1, alignSelf: 'stretch', background: 'rgba(255,255,255,0.06)', margin: '0 6px' }}></div>);
            }
            nodes.push(<div key={'label-' + gi} style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.8px', color: '#475569', textTransform: 'uppercase', padding: '0 8px', alignSelf: 'center', marginBottom: 8 }}>{g.label}</div>);
            g.tabs.forEach(function(t) {
              var active = section === t.key;
              nodes.push(
                <button key={t.key} type="button" onClick={function() { setSection(t.key); }} style={{ background: 'none', border: 'none', padding: '8px 12px', borderBottom: active ? '2px solid #a78bfa' : '2px solid transparent', color: active ? '#fff' : '#94a3b8', fontWeight: 600, cursor: 'pointer', fontSize: 11, letterSpacing: '0.4px', textTransform: 'uppercase' }}>{t.label}</button>
              );
            });
          });
          return nodes;
        })()}
      </div>

      {section === 'overview' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16 }}>
            {(function() {
              var scopeCounts = {
                app: form.scope_app_ids.length, api: form.scope_api_ids.length,
                attack: form.scope_attack_surface_ids.length, internal: form.scope_internal_scope_ids.length,
                cloud: form.scope_cloud_tenant_ids.length, saas: form.scope_saas_ids.length
              };
              var totalScope = scopeCounts.app + scopeCounts.api + scopeCounts.attack + scopeCounts.internal + scopeCounts.cloud + scopeCounts.saas;
              var contactsCount = (contacts || []).length;
              var signedAttest = (attestations || []).filter(function(a) { return a.status === 'signed' || a.status === 'waived' || a.status === 'not_applicable'; }).length;
              var totalAttest = (attestations || []).length;
              var clearedChecklist = (checklist || []).filter(function(c) { return c.status === 'completed' || c.status === 'waived'; }).length;
              var totalChecklist = (checklist || []).length;

              function metric(label, value, sub, color) {
                return (
                  <div style={{ padding: 14, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8 }}>
                    <div style={{ fontSize: 10, color: '#64748b', letterSpacing: '0.5px', fontWeight: 600, marginBottom: 6 }}>{label}</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: color || '#fff', lineHeight: 1 }}>{value}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>{sub}</div>
                  </div>
                );
              }

              return [
                metric('SCOPE ITEMS', totalScope, scopeCounts.app + ' apps · ' + scopeCounts.attack + ' attack surface · ' + scopeCounts.cloud + ' cloud', totalScope > 0 ? '#fff' : '#fbbf24'),
                metric('CONTACTS', contactsCount + '/6', contactsCount >= 2 ? 'signatory + primary set' : 'minimum 2 roles needed', contactsCount >= 2 ? '#22c55e' : '#fbbf24'),
                metric('ATTESTATIONS', signedAttest + '/' + (totalAttest || '?'), 'signed / waived / not-applicable', totalAttest > 0 && signedAttest === totalAttest ? '#22c55e' : signedAttest > 0 ? '#fbbf24' : '#fff'),
                metric('CHECKLIST', clearedChecklist + '/' + totalChecklist, totalChecklist > 0 ? Math.round((clearedChecklist / totalChecklist) * 100) + '% cleared' : '—', clearedChecklist === totalChecklist && totalChecklist > 0 ? '#22c55e' : '#fff')
              ];
            })()}
          </div>

          {(function() {
            var scopeCounts = {
              app: form.scope_app_ids.length, api: form.scope_api_ids.length,
              attack: form.scope_attack_surface_ids.length, internal: form.scope_internal_scope_ids.length,
              cloud: form.scope_cloud_tenant_ids.length, saas: form.scope_saas_ids.length
            };
            var totalScope = scopeCounts.app + scopeCounts.api + scopeCounts.attack + scopeCounts.internal + scopeCounts.cloud + scopeCounts.saas;
            // Mirror ReadinessGate's blocker calculation so the scope-lock
            // button is gated on the same conditions.
            var contactSig = (contacts || []).find(function(c) { return c.role === 'engagement_signatory'; });
            var contactPrim = (contacts || []).find(function(c) { return c.role === 'primary'; });
            var requiredAttest = ['cyber_liability_insurance','backup_restore_tested','tester_eo_insurance','tester_gl_insurance'];
            var clearedAttest = requiredAttest.filter(function(k) { var a = (attestations || []).find(function(x) { return x.kind === k; }); return a && (a.status === 'signed' || a.status === 'waived' || a.status === 'not_applicable'); }).length;
            var blockerCount = 0;
            if (!form.engagement_type) blockerCount++;
            if (totalScope === 0) blockerCount++;
            if (!contactSig || !contactSig.email) blockerCount++;
            if (!contactPrim) blockerCount++;
            if (clearedAttest !== requiredAttest.length) blockerCount++;
            if (!form.authorization_letter_url) blockerCount++;
            var readyForLock = blockerCount === 0;
            return (
              <div>
                <EngagementNextActions
                  engagement={engagement}
                  form={form}
                  contacts={contacts} checklist={checklist} attestations={attestations}
                  scopeCounts={scopeCounts}
                  onNavigateTab={function(t) { setSection(t); }}
                />
                {engagement.status === 'scoping' && (
                  <div style={{ marginTop: 4 }}>
                    <details style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, padding: 12 }}>
                      <summary style={{ cursor: 'pointer', fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#94a3b8' }}>READINESS GATE DETAIL — what's blocking scope-lock</summary>
                      <div style={{ marginTop: 12 }}>
                        <EngagementReadinessGate
                          engagement={Object.assign({}, engagement, {
                            engagement_type: form.engagement_type,
                            engagement_window_start: form.engagement_window_start,
                            engagement_window_end: form.engagement_window_end,
                            authorization_letter_url: form.authorization_letter_url
                          })}
                          contacts={contacts} checklist={checklist} attestations={attestations}
                          scopeCounts={scopeCounts}
                        />
                      </div>
                    </details>
                  </div>
                )}
                <ScopeLockPanel
                  clientId={clientId}
                  engagement={engagement}
                  readyForLock={readyForLock}
                  blockerCount={blockerCount}
                  onLocked={function() { if (onSaved) onSaved(); }}
                />
              </div>
            );
          })()}
        </div>
      )}

      {section === 'record' && (
        <div>
          <div style={subSectionStyle}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#a78bfa', marginBottom: 10 }}>ENGAGEMENT TYPE & THREAT MODEL</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
              <label style={labelStyle}>Engagement type
                <select value={form.engagement_type} onChange={function(e) { set('engagement_type', e.target.value); }} style={inputStyle}>
                  <option value="">—</option>
                  {ENGAGEMENT_TYPES.map(function(o) { return <option key={o.value} value={o.value}>{o.label}</option>; })}
                </select>
              </label>
              <label style={labelStyle}>Knowledge level
                <select value={form.knowledge_level} onChange={function(e) { set('knowledge_level', e.target.value); }} style={inputStyle}>
                  <option value="">—</option>
                  <option value="black_box">Black box</option>
                  <option value="grey_box">Grey box</option>
                  <option value="white_box">White box</option>
                </select>
              </label>
              <label style={labelStyle}>Disclosure model
                <select value={form.disclosure_model} onChange={function(e) { set('disclosure_model', e.target.value); }} style={inputStyle}>
                  <option value="">—</option>
                  <option value="announced">Announced</option>
                  <option value="limited">Limited-knowledge</option>
                  <option value="unannounced">Unannounced</option>
                </select>
              </label>
            </div>
            <div style={Object.assign({}, labelStyle, { marginBottom: 6 })}>Threat profiles to emulate</div>
            {Chips(THREAT_PROFILES, 'threat_profiles', 2)}
          </div>

          <div style={subSectionStyle}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#a78bfa', marginBottom: 10 }}>OBJECTIVES</div>
            <div style={Object.assign({}, labelStyle, { marginBottom: 6 })}>Primary objectives (up to 3)</div>
            {[0, 1, 2].map(function(i) {
              return (
                <input key={i} type="text" value={form.objectives_primary[i] || ''} onChange={function(e) {
                  var arr = (form.objectives_primary || []).slice();
                  arr[i] = e.target.value;
                  set('objectives_primary', arr);
                }} placeholder={'Primary objective ' + (i + 1)} style={Object.assign({}, inputStyle, { marginBottom: 6 })} />
              );
            })}
            <div style={Object.assign({}, labelStyle, { marginTop: 8, marginBottom: 6 })}>Secondary / aspirational (one per line)</div>
            <textarea rows={3} value={(form.objectives_secondary || []).join('\n')} onChange={function(e) { set('objectives_secondary', e.target.value.split('\n')); }} style={Object.assign({}, inputStyle, { resize: 'vertical' })} />
          </div>

          <div style={subSectionStyle}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#a78bfa', marginBottom: 10 }}>FRAMEWORKS & COMPLIANCE LENS</div>
            <div style={Object.assign({}, labelStyle, { marginBottom: 6 })}>Frameworks the testing team will follow</div>
            {Chips(FRAMEWORKS, 'frameworks', 2)}
            <div style={Object.assign({}, labelStyle, { marginTop: 12, marginBottom: 6 })}>Compliance frameworks driving this engagement</div>
            {Chips(COMPLIANCE, 'compliance_frameworks', 2)}
          </div>

          <div style={subSectionStyle}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#a78bfa', marginBottom: 10 }}>EXFILTRATION TOLERANCE</div>
            <label style={labelStyle}>Cap
              <select value={form.exfil_tolerance} onChange={function(e) { set('exfil_tolerance', e.target.value); }} style={inputStyle}>
                <option value="">—</option>
                <option value="none">None (canary files only)</option>
                <option value="cap_100">Up to 100 sample records</option>
                <option value="aggregate_only">Aggregate counts only</option>
                <option value="custom">Custom</option>
              </select>
            </label>
            {form.exfil_tolerance === 'custom' && (
              <div style={{ marginTop: 8 }}>
                <label style={labelStyle}>Custom rules</label>
                <textarea rows={3} value={form.exfil_custom_rules} onChange={function(e) { set('exfil_custom_rules', e.target.value); }} style={Object.assign({}, inputStyle, { resize: 'vertical' })} />
              </div>
            )}
          </div>

          <div style={subSectionStyle}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#a78bfa', marginBottom: 10 }}>TESTING WINDOW</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
              <label style={labelStyle}>Start<input type="date" value={form.engagement_window_start} onChange={function(e) { set('engagement_window_start', e.target.value); }} style={inputStyle} /></label>
              <label style={labelStyle}>End<input type="date" value={form.engagement_window_end} onChange={function(e) { set('engagement_window_end', e.target.value); }} style={inputStyle} /></label>
              <label style={labelStyle}>Timezone<input type="text" value={form.engagement_timezone} onChange={function(e) { set('engagement_timezone', e.target.value); }} placeholder="America/New_York" style={inputStyle} /></label>
            </div>
            <div style={Object.assign({}, labelStyle, { marginBottom: 6 })}>Approved testing hours</div>
            {Chips(TESTING_HOURS, 'testing_hours', 4)}
            <div style={Object.assign({}, labelStyle, { marginTop: 12, marginBottom: 6 })}>Blackout periods (one per line: YYYY-MM-DD to YYYY-MM-DD — reason)</div>
            <textarea rows={2} value={(form.blackout_periods || []).map(function(b) { return (b.start || '') + ' to ' + (b.end || '') + ' — ' + (b.reason || ''); }).join('\n')} onChange={function(e) {
              var lines = e.target.value.split('\n').map(function(line) {
                var m = line.match(/^\s*(\S+)\s+to\s+(\S+)\s*[—-]?\s*(.*)$/);
                if (!m) return null;
                return { start: m[1], end: m[2], reason: (m[3] || '').trim() };
              }).filter(Boolean);
              set('blackout_periods', lines);
            }} placeholder="2026-12-22 to 2027-01-02 — Holiday freeze" style={Object.assign({}, inputStyle, { resize: 'vertical' })} />
          </div>
        </div>
      )}

      {section === 'scope' && (
        <div>
          {engagement.scope_locked_at && (
            <div style={{ padding: 14, background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 8, marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 18 }}>🔒</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#22c55e', marginBottom: 2 }}>SCOPE LOCKED — READ ONLY</div>
                  <div style={{ fontSize: 12, color: '#cbd5e1', lineHeight: 1.5 }}>
                    Scope frozen on {new Date(engagement.scope_locked_at).toLocaleString()}. The picks below reflect the engagement record but are not editable — the authorized scope is frozen in snapshot tables. To modify scope, scope-unlock from the Overview tab (deletes snapshots, returns engagement to scoping).
                  </div>
                </div>
              </div>
            </div>
          )}
          <div style={subSectionStyle}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#a78bfa', marginBottom: 10 }}>IN-SCOPE APPLICATIONS</div>
            {catalogApps.length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center', color: '#64748b', fontSize: 12 }}>No apps in catalog. Add some via the Catalog sub-tab first.</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {catalogApps.map(function(a) {
                  var on = form.scope_app_ids.indexOf(a.id) !== -1;
                  return (
                    <label key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: on ? '#fff' : '#cbd5e1', padding: '8px 12px', background: on ? 'rgba(167,139,250,0.12)' : 'rgba(255,255,255,0.02)', border: '1px solid ' + (on ? 'rgba(167,139,250,0.4)' : 'rgba(255,255,255,0.08)'), borderRadius: 4, cursor: 'pointer' }}>
                      <input type="checkbox" checked={on} disabled={!!engagement.scope_locked_at} onChange={function() { toggleInArray('scope_app_ids', a.id); }} />
                      <span style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600 }}>{a.name}{a.crown_jewel_flag && <span title="Crown jewel" style={{ marginLeft: 6, color: '#fbbf24' }}>♦</span>}</div>
                        <div style={{ fontSize: 10, color: '#64748b' }}>{a.exposure || '—'}{a.criticality_tier ? ' · ' + a.criticality_tier.replace(/^tier_\d_/, '').replace(/_/g, ' ') : ''}</div>
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div style={subSectionStyle}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#a78bfa', marginBottom: 10 }}>IN-SCOPE APIs</div>
            {catalogApis.length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center', color: '#64748b', fontSize: 12 }}>No APIs in catalog. Add some via the Catalog sub-tab first.</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {catalogApis.map(function(a) {
                  var on = form.scope_api_ids.indexOf(a.id) !== -1;
                  var parent = a.app_id && catalogApps.find(function(x) { return x.id === a.app_id; });
                  return (
                    <label key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: on ? '#fff' : '#cbd5e1', padding: '8px 12px', background: on ? 'rgba(167,139,250,0.12)' : 'rgba(255,255,255,0.02)', border: '1px solid ' + (on ? 'rgba(167,139,250,0.4)' : 'rgba(255,255,255,0.08)'), borderRadius: 4, cursor: 'pointer' }}>
                      <input type="checkbox" checked={on} disabled={!!engagement.scope_locked_at} onChange={function() { toggleInArray('scope_api_ids', a.id); }} />
                      <span style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600 }}>{a.name}</div>
                        <div style={{ fontSize: 10, color: '#64748b' }}>{(a.protocol || '?').toUpperCase()}{a.auth_kind ? ' · ' + a.auth_kind : ''}{parent ? ' · ' + parent.name : ''}</div>
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div style={subSectionStyle}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#a78bfa', marginBottom: 10 }}>EXTERNAL ATTACK SURFACE</div>
            {catalogAttackSurface.length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center', color: '#64748b', fontSize: 12 }}>No external attack-surface entries in catalog. Add some via the Catalog → Attack Surface tab first.</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {catalogAttackSurface.map(function(a) {
                  var on = form.scope_attack_surface_ids.indexOf(a.id) !== -1;
                  return (
                    <label key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: on ? '#fff' : '#cbd5e1', padding: '8px 12px', background: on ? 'rgba(167,139,250,0.12)' : 'rgba(255,255,255,0.02)', border: '1px solid ' + (on ? 'rgba(167,139,250,0.4)' : 'rgba(255,255,255,0.08)'), borderRadius: 4, cursor: 'pointer' }}>
                      <input type="checkbox" checked={on} disabled={!!engagement.scope_locked_at} onChange={function() { toggleInArray('scope_attack_surface_ids', a.id); }} />
                      <span style={{ flex: 1 }}>
                        <div style={{ fontFamily: 'monospace', fontWeight: 600 }}>{a.value}{a.third_party_hosted && <span title="Third-party hosted — separate vendor approval needed" style={{ marginLeft: 6, fontSize: 9, color: '#fbbf24' }}>3RD-PARTY</span>}</div>
                        <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase' }}>{a.kind}</div>
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div style={subSectionStyle}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#a78bfa', marginBottom: 10 }}>INTERNAL SCOPE</div>
            {catalogInternalScope.length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center', color: '#64748b', fontSize: 12 }}>No internal scope entries in catalog. Add some via the Catalog → Internal Scope tab first.</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {catalogInternalScope.map(function(a) {
                  var on = form.scope_internal_scope_ids.indexOf(a.id) !== -1;
                  return (
                    <label key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: on ? '#fff' : '#cbd5e1', padding: '8px 12px', background: on ? 'rgba(167,139,250,0.12)' : 'rgba(255,255,255,0.02)', border: '1px solid ' + (on ? 'rgba(167,139,250,0.4)' : 'rgba(255,255,255,0.08)'), borderRadius: 4, cursor: 'pointer' }}>
                      <input type="checkbox" checked={on} disabled={!!engagement.scope_locked_at} onChange={function() { toggleInArray('scope_internal_scope_ids', a.id); }} />
                      <span style={{ flex: 1 }}>
                        <div style={{ fontFamily: 'monospace', fontWeight: 600 }}>{a.value}</div>
                        <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase' }}>{a.kind.replace(/_/g, ' ')}</div>
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div style={subSectionStyle}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#a78bfa', marginBottom: 10 }}>CLOUD TENANTS</div>
            {catalogCloud.length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center', color: '#64748b', fontSize: 12 }}>No cloud tenants in catalog. Add some via the Catalog → Cloud tab first.</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {catalogCloud.map(function(a) {
                  var on = form.scope_cloud_tenant_ids.indexOf(a.id) !== -1;
                  return (
                    <label key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: on ? '#fff' : '#cbd5e1', padding: '8px 12px', background: on ? 'rgba(167,139,250,0.12)' : 'rgba(255,255,255,0.02)', border: '1px solid ' + (on ? 'rgba(167,139,250,0.4)' : 'rgba(255,255,255,0.08)'), borderRadius: 4, cursor: 'pointer' }}>
                      <input type="checkbox" checked={on} disabled={!!engagement.scope_locked_at} onChange={function() { toggleInArray('scope_cloud_tenant_ids', a.id); }} />
                      <span style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600 }}>{a.provider.toUpperCase()}{a.provider_label ? ' · ' + a.provider_label : ''}</div>
                        <div style={{ fontFamily: 'monospace', fontSize: 10, color: '#94a3b8' }}>{a.external_id || '(no ID)'}{a.purpose ? ' — ' + a.purpose : ''}</div>
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div style={subSectionStyle}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#a78bfa', marginBottom: 10 }}>SaaS PROVIDERS <span style={{ fontSize: 10, fontWeight: 400, color: '#64748b' }}>(approval status shown — provider must be APPROVED before active testing)</span></div>
            {catalogSaas.length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center', color: '#64748b', fontSize: 12 }}>No SaaS providers in inventory. Add some via the Catalog → SaaS tab first.</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {catalogSaas.map(function(a) {
                  var on = form.scope_saas_ids.indexOf(a.id) !== -1;
                  var statusColor = ({ approved: '#22c55e', requested: '#60a5fa', not_requested: '#fbbf24', denied: '#f87171', not_required: '#94a3b8' })[a.approval_status] || '#94a3b8';
                  return (
                    <label key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: on ? '#fff' : '#cbd5e1', padding: '8px 12px', background: on ? 'rgba(167,139,250,0.12)' : 'rgba(255,255,255,0.02)', border: '1px solid ' + (on ? 'rgba(167,139,250,0.4)' : 'rgba(255,255,255,0.08)'), borderRadius: 4, cursor: 'pointer' }}>
                      <input type="checkbox" checked={on} disabled={!!engagement.scope_locked_at} onChange={function() { toggleInArray('scope_saas_ids', a.id); }} />
                      <span style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontWeight: 600 }}>{a.provider}</span>
                          <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3, background: statusColor + '22', color: statusColor, letterSpacing: '0.4px' }}>{(a.approval_status || '').replace(/_/g, ' ').toUpperCase()}</span>
                        </div>
                        {a.business_function && <div style={{ fontSize: 10, color: '#94a3b8' }}>{a.business_function}</div>}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div style={subSectionStyle}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#a78bfa', marginBottom: 10 }}>OUT-OF-SCOPE</div>
            <textarea rows={4} value={form.scope_out_of_scope_notes} onChange={function(e) { set('scope_out_of_scope_notes', e.target.value); }} placeholder="Systems, behaviors, or windows that must not be touched. The testing team treats entries here as a hard boundary." style={Object.assign({}, inputStyle, { resize: 'vertical' })} />
          </div>

          {form.scope_description_legacy && (
            <div style={Object.assign({}, subSectionStyle, { borderColor: 'rgba(251,191,36,0.3)' })}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#fbbf24', marginBottom: 6 }}>LEGACY SCOPE NOTES (pre-S1.2)</div>
              <div style={{ fontSize: 12, color: '#cbd5e1', whiteSpace: 'pre-wrap', marginBottom: 8 }}>{form.scope_description_legacy}</div>
              <button type="button" className="btn btn-sm" onClick={function() { set('scope_description_legacy', ''); }}>Clear legacy notes</button>
            </div>
          )}
        </div>
      )}

      {section === 'contacts' && (
        <EngagementContactsSection clientId={clientId} engagementId={engagement.id} contacts={contacts} onChange={loadContacts} />
      )}

      {section === 'checklist' && (
        <EngagementChecklistSection clientId={clientId} engagementId={engagement.id} checklist={checklist} onChange={loadChecklist} />
      )}

      {section === 'attestations' && (
        <EngagementAttestationsSection clientId={clientId} engagementId={engagement.id} attestations={attestations} saasCatalog={catalogSaas} onChange={loadAttestations} />
      )}

      {section === 'findings' && (
        <EngagementFindings clientId={clientId} engagementId={engagement.id} engagementStatus={engagement.status} />
      )}

      {section === 'activity' && (
        <EngagementActivity clientId={clientId} engagementId={engagement.id} />
      )}

      {section === 'attack_surface' && (
        <RedTeamAttackSurface clientId={clientId} engagement={engagement} />
      )}

      {section === 'scanners' && (
        <EngagementScanners clientId={clientId} engagementId={engagement.id} engagementStatus={engagement.status} onChange={onSaved} />
      )}

      {section === 'reports' && (
        <EngagementReportsTab clientId={clientId} engagement={engagement} />
      )}

      {section === 'auth' && (
        <div>
          <div style={subSectionStyle}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#a78bfa', marginBottom: 10 }}>OPERATOR & AUTHORIZATION</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <label style={labelStyle}>Lead tester<input type="text" value={form.lead_tester} onChange={function(e) { set('lead_tester', e.target.value); }} style={inputStyle} /></label>
              <label style={labelStyle}>Authorization letter URL<input type="text" value={form.authorization_letter_url} onChange={function(e) { set('authorization_letter_url', e.target.value); }} placeholder="https://…" style={inputStyle} /></label>
            </div>
            <label style={labelStyle}>Notes
              <textarea rows={4} value={form.notes} onChange={function(e) { set('notes', e.target.value); }} style={Object.assign({}, inputStyle, { resize: 'vertical' })} />
            </label>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 16, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={busy}>{busy ? 'Saving…' : 'Save changes'}</button>
        <span style={{ fontSize: 11, color: '#64748b', alignSelf: 'center' }}>Saves all sections at once.</span>
      </div>
    </div>
  );
}

// ── SimpleListCatalog (S1.3 — attack surface + internal scope) ─────
// Reusable list view for catalog surfaces that are just "kind + value"
// rows: external attack surface (IPs, CIDRs, domains, subdomains, URLs)
// and internal scope (CIDRs, VLANs, segments). Bulk-paste autoclassifies
// each line, list view shows kind badges, inline edit / delete.
function SimpleListCatalog({ clientId, endpoint, label, kinds, hasThirdParty, placeholder, discoveryAdapter }) {
  var [rows, setRows] = useState(null);
  var [busy, setBusy] = useState(false);
  var [toast, setToast] = useState(null);
  var [bulkOpen, setBulkOpen] = useState(false);
  var [bulkText, setBulkText] = useState('');
  var [editingId, setEditingId] = useState(null);
  var [editForm, setEditForm] = useState({});
  var [creating, setCreating] = useState(false);
  var [createForm, setCreateForm] = useState({ kind: kinds[0] && kinds[0].value, value: '', notes: '' });
  var [discoverOpen, setDiscoverOpen] = useState(false);
  var [discoverInput, setDiscoverInput] = useState('');
  var [discoverCandidates, setDiscoverCandidates] = useState(null);
  var [discoverPicked, setDiscoverPicked] = useState({});
  var [discoverLoading, setDiscoverLoading] = useState(false);

  function showToast(tone, text) { setToast({ tone: tone, text: text }); setTimeout(function() { setToast(null); }, 5000); }

  function refresh() {
    authFetch(API + '/clients/' + clientId + '/catalog/' + endpoint)
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) { setRows(d ? (d.entries || []) : []); })
      .catch(function() { setRows([]); });
  }
  useEffect(refresh, [clientId, endpoint]);  // eslint-disable-line react-hooks/exhaustive-deps

  function submitCreate() {
    if (!createForm.value || !createForm.value.trim()) { showToast('error', 'Value required'); return; }
    setBusy(true);
    authFetch(API + '/clients/' + clientId + '/catalog/' + endpoint, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(createForm)
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusy(false);
        if (x.ok) { showToast('success', 'Added'); setCreating(false); setCreateForm({ kind: kinds[0] && kinds[0].value, value: '', notes: '' }); refresh(); }
        else { showToast('error', x.d.error || 'Failed'); }
      })
      .catch(function() { setBusy(false); showToast('error', 'Request failed'); });
  }

  function submitBulk() {
    if (!bulkText.trim()) { showToast('error', 'Paste some lines first'); return; }
    setBusy(true);
    authFetch(API + '/clients/' + clientId + '/catalog/' + endpoint + '/bulk', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: bulkText })
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusy(false);
        if (x.ok) {
          var parts = ['Added ' + x.d.added];
          if (x.d.duplicates) parts.push(x.d.duplicates + ' duplicate(s)');
          if (x.d.unparseable && x.d.unparseable.length) parts.push(x.d.unparseable.length + ' unparseable');
          showToast('success', parts.join(', '));
          setBulkText(''); setBulkOpen(false); refresh();
        } else { showToast('error', x.d.error || 'Bulk import failed'); }
      })
      .catch(function() { setBusy(false); showToast('error', 'Request failed'); });
  }

  function startEdit(row) { setEditingId(row.id); setEditForm({ kind: row.kind, value: row.value, third_party_hosted: !!row.third_party_hosted, notes: row.notes || '' }); }
  function cancelEdit() { setEditingId(null); setEditForm({}); }
  function submitEdit() {
    setBusy(true);
    authFetch(API + '/clients/' + clientId + '/catalog/' + endpoint + '/' + editingId, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(editForm)
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusy(false);
        if (x.ok) { showToast('success', 'Updated'); cancelEdit(); refresh(); }
        else { showToast('error', x.d.error || 'Failed'); }
      })
      .catch(function() { setBusy(false); showToast('error', 'Request failed'); });
  }

  function handleDelete(row) {
    if (!window.confirm('Delete "' + row.value + '" from catalog?')) return;
    setBusy(true);
    authFetch(API + '/clients/' + clientId + '/catalog/' + endpoint + '/' + row.id, { method: 'DELETE' })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusy(false);
        if (x.ok) { showToast('success', 'Deleted'); refresh(); }
        else { showToast('error', x.d.error || 'Failed'); }
      })
      .catch(function() { setBusy(false); showToast('error', 'Request failed'); });
  }

  if (rows === null) return <div className="tab-content"><SkeletonLoader /></div>;

  var inputStyle = { width: '100%', padding: 8, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, color: '#fff', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' };

  // Group rows by kind for the list view
  var grouped = {};
  rows.forEach(function(r) { (grouped[r.kind] = grouped[r.kind] || []).push(r); });
  var kindOrder = kinds.map(function(k) { return k.value; });
  Object.keys(grouped).forEach(function(k) { if (kindOrder.indexOf(k) === -1) kindOrder.push(k); });

  return (
    <div>
      {toast && <div className={'admin-banner ' + toast.tone} style={{ fontSize: 12, marginBottom: 12 }}>{toast.text}</div>}

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-primary" onClick={function() { setCreating(true); setBulkOpen(false); }} disabled={busy}>+ Add one</button>
        <button type="button" className="btn" onClick={function() { setBulkOpen(true); setCreating(false); }} disabled={busy}>⤓ Bulk paste</button>
        {discoveryAdapter && (
          <button type="button" className="btn" onClick={function() { setDiscoverOpen(true); setDiscoverInput(''); setDiscoverCandidates(null); setDiscoverPicked({}); }} disabled={busy}>🔍 {discoveryAdapter.label}</button>
        )}
        <div style={{ flex: 1 }}></div>
        <div style={{ fontSize: 11, color: '#64748b', alignSelf: 'center' }}>{rows.length} total</div>
      </div>

      {discoverOpen && discoveryAdapter && (
        <div onClick={function() { if (!busy) setDiscoverOpen(false); }} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
          <div onClick={function(ev) { ev.stopPropagation(); }} style={{ background: '#0f172a', border: '1px solid rgba(167,139,250,0.3)', borderRadius: 12, padding: 24, maxWidth: 720, width: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.6px', color: '#a78bfa', marginBottom: 8 }}>{discoveryAdapter.label.toUpperCase()}</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 14 }}>{discoveryAdapter.description || 'Discover additional entries via the configured scanner. Pick which results to add to the catalog.'}</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <input type="text" value={discoverInput} onChange={function(e) { setDiscoverInput(e.target.value); }} placeholder={discoveryAdapter.inputPlaceholder} style={{ flex: 1, padding: 10, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, color: '#fff', fontSize: 13 }} />
              <button type="button" className="btn btn-primary" disabled={busy || discoverLoading || !discoverInput.trim()} onClick={function() {
                setDiscoverLoading(true);
                setDiscoverCandidates(null);
                authFetch(API + '/clients/' + clientId + '/scanners/' + discoveryAdapter.endpointKey + '/discover', {
                  method: 'POST', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(Object.fromEntries([[discoveryAdapter.inputField, discoverInput.trim()]]))
                })
                  .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
                  .then(function(x) {
                    setDiscoverLoading(false);
                    if (x.ok) {
                      setDiscoverCandidates(x.d.candidates || []);
                      var preset = {};
                      (x.d.candidates || []).forEach(function(c) { if (!c.already_imported) preset[c.value] = true; });
                      setDiscoverPicked(preset);
                    } else { showToast('error', x.d.error || 'Discovery failed'); }
                  })
                  .catch(function(e) { setDiscoverLoading(false); showToast('error', 'Request failed'); });
              }}>{discoverLoading ? 'Searching…' : 'Search'}</button>
            </div>
            {discoverLoading && <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Querying scanner…</div>}
            {discoverCandidates && (
              <div style={{ flex: 1, overflow: 'auto', marginBottom: 16, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 6 }}>
                {discoverCandidates.length === 0 ? (
                  <div style={{ padding: 24, textAlign: 'center', color: '#64748b', fontSize: 13 }}>No results returned by the scanner.</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: 'rgba(255,255,255,0.03)' }}>
                        <th style={{ padding: 8, width: 36 }}></th>
                        <th style={{ padding: 8, textAlign: 'left', color: '#94a3b8', fontSize: 10 }}>VALUE</th>
                        <th style={{ padding: 8, textAlign: 'left', color: '#94a3b8', fontSize: 10 }}>KIND</th>
                        <th style={{ padding: 8, textAlign: 'left', color: '#94a3b8', fontSize: 10 }}>STATUS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {discoverCandidates.map(function(c) {
                        var disabled = c.already_imported || busy;
                        return (
                          <tr key={c.value} style={{ borderTop: '1px solid rgba(255,255,255,0.04)', opacity: disabled ? 0.55 : 1 }}>
                            <td style={{ padding: 8, textAlign: 'center' }}>
                              <input type="checkbox" checked={!!discoverPicked[c.value]} disabled={disabled} onChange={function(e) { setDiscoverPicked(Object.assign({}, discoverPicked, { [c.value]: e.target.checked })); }} />
                            </td>
                            <td style={{ padding: 8, fontFamily: 'monospace', color: '#fff' }}>{c.value}</td>
                            <td style={{ padding: 8, color: '#94a3b8', textTransform: 'uppercase', fontSize: 10 }}>{c.kind}</td>
                            <td style={{ padding: 8 }}>{c.already_imported ? <span style={{ color: '#64748b', fontSize: 10 }}>ALREADY IMPORTED</span> : <span style={{ color: '#a78bfa', fontSize: 10 }}>AVAILABLE</span>}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 14 }}>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>{Object.keys(discoverPicked).filter(function(k) { return discoverPicked[k]; }).length} selected</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="btn" onClick={function() { setDiscoverOpen(false); }} disabled={busy}>Cancel</button>
                <button type="button" className="btn btn-primary" disabled={busy || !discoverCandidates || Object.keys(discoverPicked).filter(function(k) { return discoverPicked[k]; }).length === 0} onClick={function() {
                  setBusy(true);
                  var picks = discoverCandidates.filter(function(c) { return discoverPicked[c.value] && !c.already_imported; }).map(function(c) { return { value: c.value, kind: c.kind }; });
                  authFetch(API + '/clients/' + clientId + '/scanners/' + discoveryAdapter.endpointKey + '/import', {
                    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ picks: picks })
                  })
                    .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
                    .then(function(x) {
                      setBusy(false);
                      if (x.ok) {
                        showToast('success', 'Imported ' + x.d.added + (x.d.duplicates ? ' (' + x.d.duplicates + ' duplicates)' : ''));
                        setDiscoverOpen(false); refresh();
                      } else { showToast('error', x.d.error || 'Import failed'); }
                    })
                    .catch(function() { setBusy(false); showToast('error', 'Request failed'); });
                }}>Import selected</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {creating && (
        <div style={{ padding: 16, background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.2)', borderRadius: 8, marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#a78bfa', marginBottom: 12 }}>NEW ENTRY</div>
          <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 12 }}>
            <label style={{ fontSize: 11, color: '#94a3b8' }}>Kind
              <select value={createForm.kind || ''} onChange={function(e) { setCreateForm(Object.assign({}, createForm, { kind: e.target.value })); }} style={inputStyle}>
                {kinds.map(function(k) { return <option key={k.value} value={k.value}>{k.label}</option>; })}
              </select>
            </label>
            <label style={{ fontSize: 11, color: '#94a3b8' }}>Value
              <input type="text" value={createForm.value} onChange={function(e) { setCreateForm(Object.assign({}, createForm, { value: e.target.value })); }} placeholder={placeholder} style={inputStyle} />
            </label>
            {hasThirdParty && (
              <label style={{ fontSize: 11, color: '#94a3b8', gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="checkbox" checked={!!createForm.third_party_hosted} onChange={function(e) { setCreateForm(Object.assign({}, createForm, { third_party_hosted: e.target.checked })); }} />
                Third-party hosted (CDN / WAF / edge — needs separate vendor pre-approval)
              </label>
            )}
            <label style={{ fontSize: 11, color: '#94a3b8', gridColumn: '1 / -1' }}>Notes
              <input type="text" value={createForm.notes || ''} onChange={function(e) { setCreateForm(Object.assign({}, createForm, { notes: e.target.value })); }} style={inputStyle} />
            </label>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button type="button" className="btn btn-primary" onClick={submitCreate} disabled={busy}>Add</button>
            <button type="button" className="btn" onClick={function() { setCreating(false); }} disabled={busy}>Cancel</button>
          </div>
        </div>
      )}

      {bulkOpen && (
        <div style={{ padding: 16, background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.2)', borderRadius: 8, marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#a78bfa', marginBottom: 8 }}>BULK PASTE</div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>
            One per line. Lines are auto-classified ({kinds.map(function(k) { return k.label; }).join(' / ').toLowerCase()}). Lines starting with # are ignored. Duplicates are silently skipped.
          </div>
          <textarea rows={8} value={bulkText} onChange={function(e) { setBulkText(e.target.value); }} placeholder={placeholder} style={Object.assign({}, inputStyle, { resize: 'vertical', fontFamily: 'monospace', fontSize: 12 })} />
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button type="button" className="btn btn-primary" onClick={submitBulk} disabled={busy || !bulkText.trim()}>Import lines</button>
            <button type="button" className="btn" onClick={function() { setBulkOpen(false); setBulkText(''); }} disabled={busy}>Cancel</button>
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', color: '#64748b', fontSize: 13, border: '1px dashed rgba(255,255,255,0.1)', borderRadius: 8 }}>
          No {label.toLowerCase()} entries yet. Add one or bulk-paste a list.
        </div>
      ) : (
        kindOrder.filter(function(k) { return (grouped[k] || []).length > 0; }).map(function(kindVal) {
          var kindLabel = (kinds.find(function(k) { return k.value === kindVal; }) || { label: kindVal.toUpperCase() }).label;
          return (
            <div key={kindVal} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.6px', color: '#94a3b8', marginBottom: 6, textTransform: 'uppercase' }}>{kindLabel} <span style={{ fontWeight: 400, color: '#64748b' }}>({grouped[kindVal].length})</span></div>
              <div style={{ overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <tbody>
                    {grouped[kindVal].map(function(r) {
                      if (editingId === r.id) {
                        return (
                          <tr key={r.id} style={{ borderTop: '1px solid rgba(255,255,255,0.04)', background: 'rgba(167,139,250,0.06)' }}>
                            <td colSpan={3} style={{ padding: 10 }}>
                              <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr auto', gap: 8, alignItems: 'center' }}>
                                <select value={editForm.kind || ''} onChange={function(e) { setEditForm(Object.assign({}, editForm, { kind: e.target.value })); }} style={inputStyle}>
                                  {kinds.map(function(k) { return <option key={k.value} value={k.value}>{k.label}</option>; })}
                                </select>
                                <input type="text" value={editForm.value || ''} onChange={function(e) { setEditForm(Object.assign({}, editForm, { value: e.target.value })); }} style={inputStyle} />
                                <div style={{ display: 'flex', gap: 6 }}>
                                  <button type="button" className="btn btn-sm btn-primary" onClick={submitEdit} disabled={busy}>Save</button>
                                  <button type="button" className="btn btn-sm" onClick={cancelEdit} disabled={busy}>Cancel</button>
                                </div>
                              </div>
                              {hasThirdParty && (
                                <label style={{ fontSize: 11, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
                                  <input type="checkbox" checked={!!editForm.third_party_hosted} onChange={function(e) { setEditForm(Object.assign({}, editForm, { third_party_hosted: e.target.checked })); }} />
                                  Third-party hosted
                                </label>
                              )}
                              <input type="text" value={editForm.notes || ''} onChange={function(e) { setEditForm(Object.assign({}, editForm, { notes: e.target.value })); }} placeholder="Notes" style={Object.assign({}, inputStyle, { marginTop: 6 })} />
                            </td>
                          </tr>
                        );
                      }
                      return (
                        <tr key={r.id} style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                          <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontSize: 12, color: '#fff' }}>
                            {r.value}
                            {r.third_party_hosted && <span title="Third-party hosted" style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3, background: 'rgba(251,191,36,0.15)', color: '#fbbf24', letterSpacing: '0.4px' }}>3RD-PARTY</span>}
                            {r.notes && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{r.notes}</div>}
                          </td>
                          <td style={{ padding: '8px 10px', fontSize: 10, color: '#94a3b8', textAlign: 'right', width: 80 }}>
                            {r.source_ref && r.source_ref.kind === 'manual' && (r.source_ref.bulk ? <span style={{ color: '#60a5fa' }}>BULK</span> : <span>MANUAL</span>)}
                          </td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', width: 140 }}>
                            <button type="button" className="btn btn-sm" onClick={function() { startEdit(r); }} disabled={busy} style={{ marginRight: 4 }}>Edit</button>
                            <button type="button" className="btn btn-sm btn-danger" onClick={function() { handleDelete(r); }} disabled={busy}>Delete</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

// ── CloudTenantsCatalog (S1.4) ─────────────────────────────────────
function CloudTenantsCatalog({ clientId }) {
  var [rows, setRows] = useState(null);
  var [busy, setBusy] = useState(false);
  var [toast, setToast] = useState(null);
  var [editingId, setEditingId] = useState(null);
  var [creating, setCreating] = useState(false);
  var [form, setForm] = useState({ provider: 'azure', provider_label: '', external_id: '', regions: '', purpose: '', notes: '' });

  function showToast(tone, text) { setToast({ tone: tone, text: text }); setTimeout(function() { setToast(null); }, 5000); }

  function refresh() {
    authFetch(API + '/clients/' + clientId + '/catalog/cloud-tenants')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) { setRows(d ? (d.entries || []) : []); })
      .catch(function() { setRows([]); });
  }
  useEffect(refresh, [clientId]);  // eslint-disable-line react-hooks/exhaustive-deps

  function startCreate() { setCreating(true); setEditingId(null); setForm({ provider: 'azure', provider_label: '', external_id: '', regions: '', purpose: '', notes: '' }); }
  function startEdit(row) { setCreating(false); setEditingId(row.id); setForm({ provider: row.provider, provider_label: row.provider_label || '', external_id: row.external_id || '', regions: Array.isArray(row.regions) ? row.regions.join(', ') : '', purpose: row.purpose || '', notes: row.notes || '' }); }
  function cancel() { setCreating(false); setEditingId(null); }

  function submit() {
    setBusy(true);
    var body = {
      provider: form.provider, provider_label: form.provider_label || null,
      external_id: form.external_id || null,
      regions: form.regions ? form.regions.split(',').map(function(s) { return s.trim(); }).filter(Boolean) : null,
      purpose: form.purpose || null, notes: form.notes || null
    };
    var url = API + '/clients/' + clientId + '/catalog/cloud-tenants' + (editingId ? '/' + editingId : '');
    authFetch(url, { method: editingId ? 'PATCH' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusy(false);
        if (x.ok) { showToast('success', editingId ? 'Updated' : 'Added'); cancel(); refresh(); }
        else { showToast('error', x.d.error || 'Failed'); }
      })
      .catch(function() { setBusy(false); showToast('error', 'Request failed'); });
  }

  function handleDelete(row) {
    if (!window.confirm('Delete this ' + row.provider.toUpperCase() + ' tenant from catalog?')) return;
    setBusy(true);
    authFetch(API + '/clients/' + clientId + '/catalog/cloud-tenants/' + row.id, { method: 'DELETE' })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) { setBusy(false); if (x.ok) { showToast('success', 'Deleted'); refresh(); } else { showToast('error', x.d.error || 'Failed'); } })
      .catch(function() { setBusy(false); showToast('error', 'Request failed'); });
  }

  if (rows === null) return <div className="tab-content"><SkeletonLoader /></div>;
  var inputStyle = { width: '100%', padding: 8, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, color: '#fff', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' };
  var providerColors = { azure: '#0078d4', aws: '#ff9900', gcp: '#4285f4', oracle: '#c74634', other: '#94a3b8' };

  function FormBox(title) {
    return (
      <div style={{ padding: 16, background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.2)', borderRadius: 8, marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#a78bfa', marginBottom: 12 }}>{title}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr 1fr', gap: 12 }}>
          <label style={{ fontSize: 11, color: '#94a3b8' }}>Provider
            <select value={form.provider} onChange={function(e) { setForm(Object.assign({}, form, { provider: e.target.value })); }} style={inputStyle}>
              <option value="azure">Azure</option>
              <option value="aws">AWS</option>
              <option value="gcp">GCP</option>
              <option value="oracle">Oracle Cloud</option>
              <option value="other">Other</option>
            </select>
          </label>
          {form.provider === 'other' && (
            <label style={{ fontSize: 11, color: '#94a3b8' }}>Provider name<input type="text" value={form.provider_label} onChange={function(e) { setForm(Object.assign({}, form, { provider_label: e.target.value })); }} style={inputStyle} /></label>
          )}
          <label style={{ fontSize: 11, color: '#94a3b8', gridColumn: form.provider === 'other' ? 'auto' : 'span 2' }}>
            {form.provider === 'azure' ? 'Tenant ID' : form.provider === 'aws' ? 'Account ID' : form.provider === 'gcp' ? 'Project ID' : 'External ID'}
            <input type="text" value={form.external_id} onChange={function(e) { setForm(Object.assign({}, form, { external_id: e.target.value })); }} placeholder={form.provider === 'azure' ? '00000000-0000-0000-0000-000000000000' : ''} style={Object.assign({}, inputStyle, { fontFamily: 'monospace' })} />
          </label>
          <label style={{ fontSize: 11, color: '#94a3b8', gridColumn: '1 / -1' }}>Regions (comma-separated)<input type="text" value={form.regions} onChange={function(e) { setForm(Object.assign({}, form, { regions: e.target.value })); }} placeholder="eastus, westus2" style={inputStyle} /></label>
          <label style={{ fontSize: 11, color: '#94a3b8', gridColumn: '1 / -1' }}>Purpose<input type="text" value={form.purpose} onChange={function(e) { setForm(Object.assign({}, form, { purpose: e.target.value })); }} placeholder="Production workloads / Dev / Identity hub / etc." style={inputStyle} /></label>
          <label style={{ fontSize: 11, color: '#94a3b8', gridColumn: '1 / -1' }}>Notes<input type="text" value={form.notes} onChange={function(e) { setForm(Object.assign({}, form, { notes: e.target.value })); }} style={inputStyle} /></label>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button type="button" className="btn btn-primary" onClick={submit} disabled={busy}>{editingId ? 'Save' : 'Add'}</button>
          <button type="button" className="btn" onClick={cancel} disabled={busy}>Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {toast && <div className={'admin-banner ' + toast.tone} style={{ fontSize: 12, marginBottom: 12 }}>{toast.text}</div>}

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button type="button" className="btn btn-primary" onClick={startCreate} disabled={busy}>+ Add cloud tenant</button>
        <div style={{ flex: 1 }}></div>
        <div style={{ fontSize: 11, color: '#64748b', alignSelf: 'center' }}>{rows.length} total</div>
      </div>

      {creating && FormBox('NEW CLOUD TENANT')}

      {rows.length === 0 && !creating ? (
        <div style={{ padding: 32, textAlign: 'center', color: '#64748b', fontSize: 13, border: '1px dashed rgba(255,255,255,0.1)', borderRadius: 8 }}>
          No cloud tenants in catalog yet. Add the Azure / AWS / GCP accounts that host in-scope assets.
        </div>
      ) : (
        <div>
          {rows.map(function(r) {
            if (editingId === r.id) return <div key={r.id}>{FormBox('EDIT CLOUD TENANT')}</div>;
            var color = providerColors[r.provider] || '#94a3b8';
            return (
              <div key={r.id} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderLeft: '3px solid ' + color, borderRadius: 6, padding: 14, marginBottom: 8, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: color + '20', color: color, letterSpacing: '0.5px' }}>{r.provider.toUpperCase()}{r.provider_label ? ' · ' + r.provider_label : ''}</span>
                    {r.external_id && <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#fff' }}>{r.external_id}</span>}
                  </div>
                  {r.purpose && <div style={{ fontSize: 12, color: '#cbd5e1', marginBottom: 2 }}>{r.purpose}</div>}
                  {r.regions && r.regions.length > 0 && <div style={{ fontSize: 11, color: '#94a3b8' }}>Regions: {r.regions.join(', ')}</div>}
                  {r.notes && <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{r.notes}</div>}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button type="button" className="btn btn-sm" onClick={function() { startEdit(r); }} disabled={busy}>Edit</button>
                  <button type="button" className="btn btn-sm btn-danger" onClick={function() { handleDelete(r); }} disabled={busy}>Delete</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── SaasInventoryCatalog (S1.4) ────────────────────────────────────
// SaaS providers + Appendix B approval workflow. Status drives a colored
// badge so the engagement signatory can see at a glance which providers
// are cleared, which are mid-flight, and which need follow-up before
// testing starts.
function SaasInventoryCatalog({ clientId }) {
  var [rows, setRows] = useState(null);
  var [busy, setBusy] = useState(false);
  var [toast, setToast] = useState(null);
  var [editingId, setEditingId] = useState(null);
  var [creating, setCreating] = useState(false);
  function blankForm() { return { provider: '', provider_label: '', tenant_or_org_id: '', business_function: '', approval_status: 'not_requested', approval_reference: '', approval_expires_at: '', notes: '' }; }
  var [form, setForm] = useState(blankForm());

  function showToast(tone, text) { setToast({ tone: tone, text: text }); setTimeout(function() { setToast(null); }, 5000); }

  function refresh() {
    authFetch(API + '/clients/' + clientId + '/catalog/saas')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) { setRows(d ? (d.entries || []) : []); })
      .catch(function() { setRows([]); });
  }
  useEffect(refresh, [clientId]);  // eslint-disable-line react-hooks/exhaustive-deps

  function startCreate() { setCreating(true); setEditingId(null); setForm(blankForm()); }
  function startEdit(row) {
    setCreating(false); setEditingId(row.id);
    setForm({
      provider: row.provider, provider_label: row.provider_label || '',
      tenant_or_org_id: row.tenant_or_org_id || '', business_function: row.business_function || '',
      approval_status: row.approval_status, approval_reference: row.approval_reference || '',
      approval_expires_at: row.approval_expires_at ? String(row.approval_expires_at).slice(0, 10) : '',
      notes: row.notes || ''
    });
  }
  function cancel() { setCreating(false); setEditingId(null); }

  function submit() {
    if (!form.provider || !form.provider.trim()) { showToast('error', 'Provider required'); return; }
    setBusy(true);
    var body = {
      provider: form.provider, provider_label: form.provider_label || null,
      tenant_or_org_id: form.tenant_or_org_id || null,
      business_function: form.business_function || null,
      approval_status: form.approval_status,
      approval_reference: form.approval_reference || null,
      approval_expires_at: form.approval_expires_at || null,
      notes: form.notes || null
    };
    var url = API + '/clients/' + clientId + '/catalog/saas' + (editingId ? '/' + editingId : '');
    authFetch(url, { method: editingId ? 'PATCH' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) { setBusy(false); if (x.ok) { showToast('success', editingId ? 'Updated' : 'Added'); cancel(); refresh(); } else { showToast('error', x.d.error || 'Failed'); } })
      .catch(function() { setBusy(false); showToast('error', 'Request failed'); });
  }

  function handleDelete(row) {
    if (!window.confirm('Delete ' + row.provider + ' from SaaS inventory?')) return;
    setBusy(true);
    authFetch(API + '/clients/' + clientId + '/catalog/saas/' + row.id, { method: 'DELETE' })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) { setBusy(false); if (x.ok) { showToast('success', 'Deleted'); refresh(); } else { showToast('error', x.d.error || 'Failed'); } })
      .catch(function() { setBusy(false); showToast('error', 'Request failed'); });
  }

  if (rows === null) return <div className="tab-content"><SkeletonLoader /></div>;
  var inputStyle = { width: '100%', padding: 8, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, color: '#fff', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' };
  var APPROVAL_COLORS = {
    not_required: { bg: 'rgba(100,116,139,0.15)', fg: '#94a3b8', label: 'Not required' },
    not_requested: { bg: 'rgba(251,191,36,0.15)', fg: '#fbbf24', label: 'Not requested' },
    requested: { bg: 'rgba(96,165,250,0.15)', fg: '#60a5fa', label: 'Requested' },
    approved: { bg: 'rgba(34,197,94,0.15)', fg: '#22c55e', label: 'Approved' },
    denied: { bg: 'rgba(248,113,113,0.15)', fg: '#f87171', label: 'Denied' }
  };

  function FormBox(title) {
    return (
      <div style={{ padding: 16, background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.2)', borderRadius: 8, marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#a78bfa', marginBottom: 12 }}>{title}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label style={{ fontSize: 11, color: '#94a3b8' }}>Provider<input type="text" value={form.provider} onChange={function(e) { setForm(Object.assign({}, form, { provider: e.target.value })); }} placeholder="salesforce / okta / atlassian / workday / …" style={inputStyle} /></label>
          <label style={{ fontSize: 11, color: '#94a3b8' }}>Tenant / org ID<input type="text" value={form.tenant_or_org_id} onChange={function(e) { setForm(Object.assign({}, form, { tenant_or_org_id: e.target.value })); }} placeholder="e.g. ace.my.salesforce.com" style={Object.assign({}, inputStyle, { fontFamily: 'monospace' })} /></label>
          <label style={{ fontSize: 11, color: '#94a3b8', gridColumn: '1 / -1' }}>Business function<input type="text" value={form.business_function} onChange={function(e) { setForm(Object.assign({}, form, { business_function: e.target.value })); }} placeholder="CRM / SSO / Helpdesk / HRIS / etc." style={inputStyle} /></label>
          <label style={{ fontSize: 11, color: '#94a3b8' }}>Approval status
            <select value={form.approval_status} onChange={function(e) { setForm(Object.assign({}, form, { approval_status: e.target.value })); }} style={inputStyle}>
              <option value="not_requested">Not requested</option>
              <option value="requested">Requested</option>
              <option value="approved">Approved</option>
              <option value="denied">Denied</option>
              <option value="not_required">Not required (out of scope for testing)</option>
            </select>
          </label>
          <label style={{ fontSize: 11, color: '#94a3b8' }}>Approval expires<input type="date" value={form.approval_expires_at} onChange={function(e) { setForm(Object.assign({}, form, { approval_expires_at: e.target.value })); }} style={inputStyle} /></label>
          <label style={{ fontSize: 11, color: '#94a3b8', gridColumn: '1 / -1' }}>Approval reference (ticket / form / URL)<input type="text" value={form.approval_reference} onChange={function(e) { setForm(Object.assign({}, form, { approval_reference: e.target.value })); }} style={inputStyle} /></label>
          <label style={{ fontSize: 11, color: '#94a3b8', gridColumn: '1 / -1' }}>Notes<input type="text" value={form.notes} onChange={function(e) { setForm(Object.assign({}, form, { notes: e.target.value })); }} style={inputStyle} /></label>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button type="button" className="btn btn-primary" onClick={submit} disabled={busy}>{editingId ? 'Save' : 'Add'}</button>
          <button type="button" className="btn" onClick={cancel} disabled={busy}>Cancel</button>
        </div>
      </div>
    );
  }

  // Group by approval status for the list view
  var grouped = { approved: [], requested: [], not_requested: [], denied: [], not_required: [] };
  rows.forEach(function(r) { (grouped[r.approval_status] = grouped[r.approval_status] || []).push(r); });
  var statusOrder = ['not_requested','requested','approved','denied','not_required'];

  return (
    <div>
      {toast && <div className={'admin-banner ' + toast.tone} style={{ fontSize: 12, marginBottom: 12 }}>{toast.text}</div>}

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button type="button" className="btn btn-primary" onClick={startCreate} disabled={busy}>+ Add SaaS provider</button>
        <div style={{ flex: 1 }}></div>
        <div style={{ fontSize: 11, color: '#64748b', alignSelf: 'center' }}>{rows.length} total</div>
      </div>

      {creating && FormBox('NEW SAAS PROVIDER')}

      {rows.length === 0 && !creating ? (
        <div style={{ padding: 32, textAlign: 'center', color: '#64748b', fontSize: 13, border: '1px dashed rgba(255,255,255,0.1)', borderRadius: 8 }}>
          No SaaS providers in inventory yet. Add the third-party SaaS platforms in scope — each needs its own approval status per Appendix B of the pre-engagement package.
        </div>
      ) : (
        statusOrder.filter(function(s) { return (grouped[s] || []).length > 0; }).map(function(status) {
          var col = APPROVAL_COLORS[status];
          return (
            <div key={status} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.6px', color: col.fg, marginBottom: 6, textTransform: 'uppercase' }}>{col.label} <span style={{ fontWeight: 400, color: '#64748b' }}>({grouped[status].length})</span></div>
              {grouped[status].map(function(r) {
                if (editingId === r.id) return <div key={r.id}>{FormBox('EDIT SAAS PROVIDER')}</div>;
                return (
                  <div key={r.id} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderLeft: '3px solid ' + col.fg, borderRadius: 6, padding: 14, marginBottom: 8, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{r.provider}{r.provider_label ? ' · ' + r.provider_label : ''}</span>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: col.bg, color: col.fg, letterSpacing: '0.5px' }}>{col.label.toUpperCase()}</span>
                      </div>
                      {r.tenant_or_org_id && <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#cbd5e1' }}>{r.tenant_or_org_id}</div>}
                      {r.business_function && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{r.business_function}</div>}
                      {r.approval_reference && <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>Ref: {r.approval_reference}</div>}
                      {r.approval_expires_at && <div style={{ fontSize: 11, color: status === 'approved' ? (new Date(r.approval_expires_at) < new Date() ? '#f87171' : '#22c55e') : '#94a3b8' }}>Expires {new Date(r.approval_expires_at).toLocaleDateString()}</div>}
                      {r.notes && <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{r.notes}</div>}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button type="button" className="btn btn-sm" onClick={function() { startEdit(r); }} disabled={busy}>Edit</button>
                      <button type="button" className="btn btn-sm btn-danger" onClick={function() { handleDelete(r); }} disabled={busy}>Delete</button>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })
      )}
    </div>
  );
}

// ── IdentityArchCatalog (S1.11) ─────────────────────────────────────
// Single-form catalog for one row per client. "Import from Aurelius"
// pulls a suggested payload from existing tenant data (Graph admin
// count, enterprise app count, IdP type from tenant_id). Operator
// confirms or edits before saving.
function IdentityArchCatalog({ clientId }) {
  var [data, setData] = useState(null);
  var [form, setForm] = useState({});
  var [busy, setBusy] = useState(false);
  var [toast, setToast] = useState(null);

  function showToast(tone, text) { setToast({ tone: tone, text: text }); setTimeout(function() { setToast(null); }, 4000); }

  function refresh() {
    authFetch(API + '/clients/' + clientId + '/catalog/identity-arch')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) {
        var row = d ? d.identity_arch : null;
        setData(row);
        setForm(row || {});
      })
      .catch(function() { setData(null); setForm({}); });
  }
  useEffect(refresh, [clientId]);  // eslint-disable-line react-hooks/exhaustive-deps

  function importFromAurelius() {
    setBusy(true);
    authFetch(API + '/clients/' + clientId + '/catalog/identity-arch/suggest')
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusy(false);
        if (x.ok) {
          var suggestion = x.d.suggestion || {};
          var merged = Object.assign({}, form);
          // Only fill nulls — preserve operator edits
          Object.keys(suggestion).forEach(function(k) {
            if (k === 'source_ref') return;
            if (merged[k] == null && suggestion[k] != null) merged[k] = suggestion[k];
          });
          setForm(merged);
          showToast('success', 'Suggested values loaded. Review and save.');
        } else { showToast('error', x.d.error || 'Suggest failed'); }
      })
      .catch(function() { setBusy(false); showToast('error', 'Request failed'); });
  }

  function save() {
    setBusy(true);
    authFetch(API + '/clients/' + clientId + '/catalog/identity-arch', {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form)
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusy(false);
        if (x.ok) { showToast('success', 'Saved'); refresh(); }
        else { showToast('error', x.d.error || 'Save failed'); }
      })
      .catch(function() { setBusy(false); showToast('error', 'Request failed'); });
  }

  function set(field, value) { setForm(Object.assign({}, form, { [field]: value })); }
  function toggleSso(method) {
    var arr = (form.sso_methods || []).slice();
    var i = arr.indexOf(method);
    if (i >= 0) arr.splice(i, 1); else arr.push(method);
    set('sso_methods', arr);
  }

  var inputStyle = { width: '100%', padding: 8, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, color: '#fff', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' };
  var labelStyle = { fontSize: 11, color: '#94a3b8', display: 'block', marginBottom: 4 };

  return (
    <div>
      {toast && <div className={'admin-banner ' + toast.tone} style={{ fontSize: 12, marginBottom: 12 }}>{toast.text}</div>}
      <div style={{ padding: 14, background: 'rgba(167,139,250,0.04)', border: '1px solid rgba(167,139,250,0.2)', borderRadius: 8, marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#a78bfa', marginBottom: 4 }}>IDENTITY & ACCESS ARCHITECTURE</div>
          <div style={{ fontSize: 12, color: '#cbd5e1', lineHeight: 1.5 }}>
            One row per client. Click <strong>Import from Aurelius</strong> to derive a starting payload from the tenant — primary IdP from Graph, standing GA count from Entra roles, enterprise-app count from service principals. Empty fields get filled, your edits are preserved. Renders as Section 3 of the pre-engagement package.
          </div>
        </div>
        <button type="button" className="btn" onClick={importFromAurelius} disabled={busy}>⤓ Import from Aurelius</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <label style={labelStyle}>Primary IdP
          <select value={form.idp_primary || ''} onChange={function(e) { set('idp_primary', e.target.value || null); }} style={inputStyle}>
            <option value="">—</option>
            <option value="entra_id">Microsoft Entra ID (cloud-only)</option>
            <option value="entra_hybrid">Microsoft Entra ID (hybrid sync)</option>
            <option value="ad_on_prem">On-premises Active Directory</option>
            <option value="okta">Okta</option>
            <option value="ping">Ping Identity</option>
            <option value="jumpcloud">JumpCloud</option>
            <option value="google_workspace">Google Workspace</option>
            <option value="other">Other</option>
          </select>
        </label>
        <label style={labelStyle}>Hybrid sync method
          <select value={form.hybrid_sync_method || ''} onChange={function(e) { set('hybrid_sync_method', e.target.value || null); }} style={inputStyle}>
            <option value="">—</option>
            <option value="aad_connect">AAD Connect</option>
            <option value="cloud_sync">Cloud Sync</option>
            <option value="none">None</option>
          </select>
        </label>
        <div style={{ gridColumn: '1 / -1' }}>
          <div style={labelStyle}>SSO methods (multi-select)</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6 }}>
            {['saml','oidc','ws_fed','kerberos','oauth_cc'].map(function(m) {
              var on = (form.sso_methods || []).indexOf(m) !== -1;
              return (
                <label key={m} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: on ? '#fff' : '#cbd5e1', padding: '6px 10px', background: on ? 'rgba(167,139,250,0.12)' : 'rgba(255,255,255,0.02)', border: '1px solid ' + (on ? 'rgba(167,139,250,0.4)' : 'rgba(255,255,255,0.08)'), borderRadius: 4, cursor: 'pointer' }}>
                  <input type="checkbox" checked={on} onChange={function() { toggleSso(m); }} />
                  {m.toUpperCase().replace(/_/g, ' ')}
                </label>
              );
            })}
          </div>
        </div>

        {[
          { f: 'b2b_enabled', l: 'B2B guest collaboration enabled' },
          { f: 'b2c_enabled', l: 'B2C tenant in use' },
          { f: 'pim_in_use', l: 'PIM (Privileged Identity Mgmt) in use' },
          { f: 'paws_in_use', l: 'Privileged Access Workstations in use' },
          { f: 'laps_in_use', l: 'Windows LAPS in use' },
          { f: 'local_admin_disabled', l: 'Local admin disabled on endpoints' }
        ].map(function(item) {
          return (
            <label key={item.f} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#cbd5e1', padding: '8px 12px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 4 }}>
              <input type="checkbox" checked={!!form[item.f]} onChange={function(e) { set(item.f, e.target.checked); }} />
              <span style={{ flex: 1 }}>{item.l}</span>
            </label>
          );
        })}

        <label style={labelStyle}>Standing Global Admins
          <input type="number" value={form.standing_ga_count == null ? '' : form.standing_ga_count} onChange={function(e) { set('standing_ga_count', e.target.value === '' ? null : parseInt(e.target.value, 10)); }} style={inputStyle} />
        </label>
        <label style={labelStyle}>Break-glass accounts
          <input type="number" value={form.break_glass_count == null ? '' : form.break_glass_count} onChange={function(e) { set('break_glass_count', e.target.value === '' ? null : parseInt(e.target.value, 10)); }} style={inputStyle} />
        </label>
        <label style={Object.assign({}, labelStyle, { gridColumn: '1 / -1' })}>Break-glass naming convention
          <input type="text" value={form.break_glass_naming || ''} onChange={function(e) { set('break_glass_naming', e.target.value || null); }} placeholder="e.g. emergency-access-01@tenant.onmicrosoft.com" style={inputStyle} />
        </label>
        <label style={labelStyle}>Service accounts (estimate)
          <input type="number" value={form.service_account_count_estimate == null ? '' : form.service_account_count_estimate} onChange={function(e) { set('service_account_count_estimate', e.target.value === '' ? null : parseInt(e.target.value, 10)); }} style={inputStyle} />
        </label>
        <label style={labelStyle}>Enterprise apps (estimate)
          <input type="number" value={form.enterprise_app_count_estimate == null ? '' : form.enterprise_app_count_estimate} onChange={function(e) { set('enterprise_app_count_estimate', e.target.value === '' ? null : parseInt(e.target.value, 10)); }} style={inputStyle} />
        </label>
        <label style={Object.assign({}, labelStyle, { gridColumn: '1 / -1' })}>Notes
          <textarea rows={3} value={form.notes || ''} onChange={function(e) { set('notes', e.target.value || null); }} style={Object.assign({}, inputStyle, { resize: 'vertical', fontFamily: 'inherit' })} />
        </label>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 14, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <button type="button" className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save identity arch'}</button>
        {data && data.source_ref && (
          <span style={{ fontSize: 10, color: '#64748b', alignSelf: 'center' }}>
            Source: {data.source_ref.kind === 'aurelius_auto_discover' ? 'Aurelius auto-discovery' : 'Manual'}{data.manual_override ? ' (operator override)' : ''} · Last updated {new Date(data.updated_at).toLocaleString()}
          </span>
        )}
      </div>
    </div>
  );
}

// ── ControlsCatalog (S1.11) ─────────────────────────────────────────
function ControlsCatalog({ clientId }) {
  var [data, setData] = useState(null);
  var [form, setForm] = useState({});
  var [busy, setBusy] = useState(false);
  var [toast, setToast] = useState(null);

  function showToast(tone, text) { setToast({ tone: tone, text: text }); setTimeout(function() { setToast(null); }, 4000); }

  function refresh() {
    authFetch(API + '/clients/' + clientId + '/catalog/controls')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) {
        var row = d ? d.controls : null;
        setData(row);
        setForm(row || {});
      })
      .catch(function() { setData(null); setForm({}); });
  }
  useEffect(refresh, [clientId]);  // eslint-disable-line react-hooks/exhaustive-deps

  function importFromAurelius() {
    setBusy(true);
    authFetch(API + '/clients/' + clientId + '/catalog/controls/suggest')
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusy(false);
        if (x.ok) {
          var suggestion = x.d.suggestion || {};
          var merged = Object.assign({}, form);
          Object.keys(suggestion).forEach(function(k) {
            if (k === 'source_ref') return;
            if (merged[k] == null && suggestion[k] != null) merged[k] = suggestion[k];
          });
          setForm(merged);
          showToast('success', 'Suggested values loaded from integration config. Review and save.');
        } else { showToast('error', x.d.error || 'Suggest failed'); }
      })
      .catch(function() { setBusy(false); showToast('error', 'Request failed'); });
  }

  function save() {
    setBusy(true);
    authFetch(API + '/clients/' + clientId + '/catalog/controls', {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form)
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusy(false);
        if (x.ok) { showToast('success', 'Saved'); refresh(); }
        else { showToast('error', x.d.error || 'Save failed'); }
      })
      .catch(function() { setBusy(false); showToast('error', 'Request failed'); });
  }

  function set(field, value) { setForm(Object.assign({}, form, { [field]: value })); }
  function setList(field, csvString) {
    var arr = csvString.split(/[,\n]/).map(function(s) { return s.trim(); }).filter(Boolean);
    set(field, arr.length > 0 ? arr : null);
  }
  function listValue(field) {
    return Array.isArray(form[field]) ? form[field].join(', ') : '';
  }

  var inputStyle = { width: '100%', padding: 8, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, color: '#fff', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' };
  var labelStyle = { fontSize: 11, color: '#94a3b8', display: 'block', marginBottom: 4 };

  return (
    <div>
      {toast && <div className={'admin-banner ' + toast.tone} style={{ fontSize: 12, marginBottom: 12 }}>{toast.text}</div>}
      <div style={{ padding: 14, background: 'rgba(167,139,250,0.04)', border: '1px solid rgba(167,139,250,0.2)', borderRadius: 8, marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#a78bfa', marginBottom: 4 }}>SECURITY CONTROLS INVENTORY</div>
          <div style={{ fontSize: 12, color: '#cbd5e1', lineHeight: 1.5 }}>
            One row per client. <strong>Import from Aurelius</strong> derives products from your integration config — Huntress / Blumira / UniFi / Defender / etc. — then you fill in what's not auto-detectable. Renders as Section 4 of the pre-engagement package.
          </div>
        </div>
        <button type="button" className="btn" onClick={importFromAurelius} disabled={busy}>⤓ Import from Aurelius</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <label style={Object.assign({}, labelStyle, { gridColumn: '1 / -1' })}>EDR / endpoint protection (comma-separated)
          <input type="text" value={listValue('edr_products')} onChange={function(e) { setList('edr_products', e.target.value); }} placeholder="Huntress, Microsoft Defender for Endpoint" style={inputStyle} />
        </label>
        <label style={Object.assign({}, labelStyle, { gridColumn: '1 / -1' })}>SIEM / MDR
          <input type="text" value={listValue('siem_mdr_products')} onChange={function(e) { setList('siem_mdr_products', e.target.value); }} placeholder="Blumira, Microsoft Sentinel" style={inputStyle} />
        </label>
        <label style={Object.assign({}, labelStyle, { gridColumn: '1 / -1' })}>Network controls
          <input type="text" value={listValue('network_controls')} onChange={function(e) { setList('network_controls', e.target.value); }} placeholder="UniFi, Palo Alto NGFW, Cloudflare WAF" style={inputStyle} />
        </label>
        <label style={Object.assign({}, labelStyle, { gridColumn: '1 / -1' })}>Email security
          <input type="text" value={listValue('email_security_products')} onChange={function(e) { setList('email_security_products', e.target.value); }} placeholder="Defender for Office 365, Proofpoint" style={inputStyle} />
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#cbd5e1', padding: '8px 12px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 4 }}>
          <input type="checkbox" checked={!!form.spf_dkim_dmarc_configured} onChange={function(e) { set('spf_dkim_dmarc_configured', e.target.checked); }} />
          <span style={{ flex: 1 }}>SPF/DKIM/DMARC configured on all owned domains</span>
        </label>
        <label style={labelStyle}>DMARC policy
          <select value={form.dmarc_policy || ''} onChange={function(e) { set('dmarc_policy', e.target.value || null); }} style={inputStyle}>
            <option value="">—</option>
            <option value="none">p=none</option>
            <option value="quarantine">p=quarantine</option>
            <option value="reject">p=reject</option>
          </select>
        </label>

        <label style={Object.assign({}, labelStyle, { gridColumn: '1 / -1' })}>Vulnerability scanners
          <input type="text" value={listValue('vuln_scanners')} onChange={function(e) { setList('vuln_scanners', e.target.value); }} placeholder="Nessus, Qualys, Rapid7 InsightVM" style={inputStyle} />
        </label>
        <label style={labelStyle}>Scan cadence
          <select value={form.vuln_scan_cadence || ''} onChange={function(e) { set('vuln_scan_cadence', e.target.value || null); }} style={inputStyle}>
            <option value="">—</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="ad_hoc">Ad-hoc</option>
          </select>
        </label>
        <label style={labelStyle}>Backup solution
          <input type="text" value={form.backup_solution || ''} onChange={function(e) { set('backup_solution', e.target.value || null); }} placeholder="Veeam, Datto, Microsoft 365 Backup" style={inputStyle} />
        </label>
        <label style={labelStyle}>Last restore test (date)
          <input type="date" value={form.last_restore_test_at ? String(form.last_restore_test_at).slice(0, 10) : ''} onChange={function(e) { set('last_restore_test_at', e.target.value || null); }} style={inputStyle} />
        </label>
        <label style={labelStyle}>RPO (hours)
          <input type="number" value={form.rpo_hours == null ? '' : form.rpo_hours} onChange={function(e) { set('rpo_hours', e.target.value === '' ? null : parseInt(e.target.value, 10)); }} style={inputStyle} />
        </label>
        <label style={labelStyle}>RTO (hours)
          <input type="number" value={form.rto_hours == null ? '' : form.rto_hours} onChange={function(e) { set('rto_hours', e.target.value === '' ? null : parseInt(e.target.value, 10)); }} style={inputStyle} />
        </label>
        <label style={Object.assign({}, labelStyle, { gridColumn: '1 / -1' })}>Notes
          <textarea rows={3} value={form.notes || ''} onChange={function(e) { set('notes', e.target.value || null); }} style={Object.assign({}, inputStyle, { resize: 'vertical', fontFamily: 'inherit' })} />
        </label>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 14, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <button type="button" className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save controls inventory'}</button>
        {data && data.source_ref && (
          <span style={{ fontSize: 10, color: '#64748b', alignSelf: 'center' }}>
            Source: {data.source_ref.kind === 'aurelius_auto_discover' ? 'Aurelius auto-discovery' : 'Manual'}{data.manual_override ? ' (operator override)' : ''} · Last updated {new Date(data.updated_at).toLocaleString()}
          </span>
        )}
      </div>
    </div>
  );
}

// ── RedTeamCatalog (S1 — apps + APIs surface) ─────────────────────
// Client-level inventory used to scope pen-test engagements. Catalog
// rows evolve over time, survive across engagements, and become the
// source for engagement-bound snapshot tables once scope-lock lands.
// This first cut ships the Apps + APIs surfaces; sibling surfaces
// (attack surface, identity arch, controls, etc.) land in follow-ups.
function RedTeamCatalog({ clientId }) {
  var [innerTab, setInnerTab] = useState('apps');
  var [apps, setApps] = useState(null);
  var [apis, setApis] = useState(null);
  var [busy, setBusy] = useState(false);
  var [toast, setToast] = useState(null);
  var [creating, setCreating] = useState(false);
  var [editingId, setEditingId] = useState(null);
  var [form, setForm] = useState({});
  var [graphModalOpen, setGraphModalOpen] = useState(false);
  var [graphCandidates, setGraphCandidates] = useState(null);
  var [graphLoading, setGraphLoading] = useState(false);
  var [graphPicked, setGraphPicked] = useState({});

  function showToast(tone, text) {
    setToast({ tone: tone, text: text });
    setTimeout(function() { setToast(null); }, 5000);
  }

  function refresh() {
    authFetch(API + '/clients/' + clientId + '/catalog/apps')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) { setApps(d ? (d.apps || []) : []); })
      .catch(function() { setApps([]); });
    authFetch(API + '/clients/' + clientId + '/catalog/apis')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) { setApis(d ? (d.apis || []) : []); })
      .catch(function() { setApis([]); });
  }
  useEffect(refresh, [clientId]);  // eslint-disable-line react-hooks/exhaustive-deps

  function startCreate(kind) {
    setCreating(true); setEditingId(null);
    if (kind === 'app') {
      setForm({ name: '', url: '', exposure: '', auth_method: '', criticality_tier: '', crown_jewel_flag: false, owner_email: '', notes: '' });
    } else {
      setForm({ name: '', app_id: null, protocol: 'rest', base_url: '', auth_kind: '', docs_available: false, docs_url: '', rate_limited: false, openapi_spec_url: '', notes: '' });
    }
  }
  function startEdit(row, kind) {
    setEditingId(row.id); setCreating(false);
    if (kind === 'app') {
      setForm({ name: row.name || '', url: row.url || '', exposure: row.exposure || '', auth_method: row.auth_method || '', criticality_tier: row.criticality_tier || '', crown_jewel_flag: !!row.crown_jewel_flag, owner_email: row.owner_email || '', notes: row.notes || '' });
    } else {
      setForm({ name: row.name || '', app_id: row.app_id || null, protocol: row.protocol || '', base_url: row.base_url || '', auth_kind: row.auth_kind || '', docs_available: !!row.docs_available, docs_url: row.docs_url || '', rate_limited: !!row.rate_limited, openapi_spec_url: row.openapi_spec_url || '', notes: row.notes || '' });
    }
  }
  function cancelForm() { setCreating(false); setEditingId(null); setForm({}); }

  function submitForm(kind) {
    if (!form.name || !form.name.trim()) { showToast('error', 'Name is required'); return; }
    setBusy(true);
    var url = API + '/clients/' + clientId + '/catalog/' + (kind === 'app' ? 'apps' : 'apis') + (editingId ? '/' + editingId : '');
    var method = editingId ? 'PATCH' : 'POST';
    authFetch(url, { method: method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusy(false);
        if (x.ok) { showToast('success', editingId ? 'Updated' : 'Added'); cancelForm(); refresh(); }
        else { showToast('error', x.d.error || 'Failed'); }
      })
      .catch(function() { setBusy(false); showToast('error', 'Request failed'); });
  }

  function handleDelete(row, kind) {
    if (!window.confirm('Delete "' + row.name + '" from catalog?')) return;
    setBusy(true);
    authFetch(API + '/clients/' + clientId + '/catalog/' + (kind === 'app' ? 'apps' : 'apis') + '/' + row.id, { method: 'DELETE' })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusy(false);
        if (x.ok) { showToast('success', 'Deleted'); refresh(); }
        else { showToast('error', x.d.error || 'Failed to delete'); }
      })
      .catch(function() { setBusy(false); showToast('error', 'Request failed'); });
  }

  function openGraphImport() {
    setGraphModalOpen(true);
    setGraphCandidates(null);
    setGraphLoading(true);
    setGraphPicked({});
    authFetch(API + '/clients/' + clientId + '/catalog/apps/graph-candidates')
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setGraphLoading(false);
        if (x.ok) { setGraphCandidates(x.d.candidates || []); }
        else { showToast('error', x.d.error || 'Graph not available for this client'); setGraphModalOpen(false); }
      })
      .catch(function() { setGraphLoading(false); showToast('error', 'Graph request failed'); setGraphModalOpen(false); });
  }
  function commitGraphImport() {
    var ids = Object.keys(graphPicked).filter(function(k) { return graphPicked[k]; });
    if (ids.length === 0) { showToast('error', 'Pick at least one app'); return; }
    setBusy(true);
    authFetch(API + '/clients/' + clientId + '/catalog/apps/import-graph', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ graph_app_ids: ids })
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusy(false);
        if (x.ok) {
          showToast('success', 'Imported ' + x.d.imported + ' app(s)' + (x.d.skipped ? ' (' + x.d.skipped + ' skipped)' : ''));
          setGraphModalOpen(false); refresh();
        } else { showToast('error', x.d.error || 'Import failed'); }
      })
      .catch(function() { setBusy(false); showToast('error', 'Request failed'); });
  }

  function provenanceBadge(sr) {
    if (!sr || !sr.kind) return <span style={{ fontSize: 9, color: '#94a3b8' }}>—</span>;
    var label = sr.kind === 'graph_app' ? 'Graph' : sr.kind === 'manual' ? 'Manual' : sr.kind === 'client_config' ? 'Config' : sr.kind;
    var color = sr.kind === 'graph_app' ? '#60a5fa' : sr.kind === 'manual' ? '#a78bfa' : '#64748b';
    return <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: 'rgba(255,255,255,0.06)', color: color, letterSpacing: '0.4px' }}>{label.toUpperCase()}</span>;
  }

  if (apps === null || apis === null) return <div className="tab-content"><SkeletonLoader /></div>;

  return (
    <div className="tab-content">
      <TabDescription text="Client-level inventory of apps, APIs, external attack surface, internal scope, cloud tenants, and SaaS providers. Catalog rows survive across engagements — build them once and re-pick into each engagement scope. Apps support Import from Graph (Entra enterprise apps); attack surface and internal scope support bulk-paste with auto-classify. SaaS rows carry Appendix B approval-status workflow." />
      {toast && <div className={'admin-banner ' + toast.tone} style={{ fontSize: 12, marginBottom: 12 }}>{toast.text}</div>}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <button type="button" onClick={function() { cancelForm(); setInnerTab('apps'); }} style={{ background: 'none', border: 'none', padding: '8px 14px', borderBottom: innerTab === 'apps' ? '2px solid #a78bfa' : '2px solid transparent', color: innerTab === 'apps' ? '#fff' : '#94a3b8', fontWeight: 600, cursor: 'pointer', fontSize: 12, letterSpacing: '0.4px' }}>
          APPS  <span style={{ fontWeight: 400, marginLeft: 4 }}>{apps.length}</span>
        </button>
        <button type="button" onClick={function() { cancelForm(); setInnerTab('apis'); }} style={{ background: 'none', border: 'none', padding: '8px 14px', borderBottom: innerTab === 'apis' ? '2px solid #a78bfa' : '2px solid transparent', color: innerTab === 'apis' ? '#fff' : '#94a3b8', fontWeight: 600, cursor: 'pointer', fontSize: 12, letterSpacing: '0.4px' }}>
          APIs  <span style={{ fontWeight: 400, marginLeft: 4 }}>{apis.length}</span>
        </button>
        <button type="button" onClick={function() { cancelForm(); setInnerTab('attack_surface'); }} style={{ background: 'none', border: 'none', padding: '8px 14px', borderBottom: innerTab === 'attack_surface' ? '2px solid #a78bfa' : '2px solid transparent', color: innerTab === 'attack_surface' ? '#fff' : '#94a3b8', fontWeight: 600, cursor: 'pointer', fontSize: 12, letterSpacing: '0.4px' }}>
          ATTACK SURFACE
        </button>
        <button type="button" onClick={function() { cancelForm(); setInnerTab('internal_scope'); }} style={{ background: 'none', border: 'none', padding: '8px 14px', borderBottom: innerTab === 'internal_scope' ? '2px solid #a78bfa' : '2px solid transparent', color: innerTab === 'internal_scope' ? '#fff' : '#94a3b8', fontWeight: 600, cursor: 'pointer', fontSize: 12, letterSpacing: '0.4px' }}>
          INTERNAL SCOPE
        </button>
        <button type="button" onClick={function() { cancelForm(); setInnerTab('cloud_tenants'); }} style={{ background: 'none', border: 'none', padding: '8px 14px', borderBottom: innerTab === 'cloud_tenants' ? '2px solid #a78bfa' : '2px solid transparent', color: innerTab === 'cloud_tenants' ? '#fff' : '#94a3b8', fontWeight: 600, cursor: 'pointer', fontSize: 12, letterSpacing: '0.4px' }}>
          CLOUD
        </button>
        <button type="button" onClick={function() { cancelForm(); setInnerTab('saas'); }} style={{ background: 'none', border: 'none', padding: '8px 14px', borderBottom: innerTab === 'saas' ? '2px solid #a78bfa' : '2px solid transparent', color: innerTab === 'saas' ? '#fff' : '#94a3b8', fontWeight: 600, cursor: 'pointer', fontSize: 12, letterSpacing: '0.4px' }}>
          SaaS
        </button>
        <button type="button" onClick={function() { cancelForm(); setInnerTab('identity_arch'); }} style={{ background: 'none', border: 'none', padding: '8px 14px', borderBottom: innerTab === 'identity_arch' ? '2px solid #a78bfa' : '2px solid transparent', color: innerTab === 'identity_arch' ? '#fff' : '#94a3b8', fontWeight: 600, cursor: 'pointer', fontSize: 12, letterSpacing: '0.4px' }}>
          IDENTITY ARCH
        </button>
        <button type="button" onClick={function() { cancelForm(); setInnerTab('controls'); }} style={{ background: 'none', border: 'none', padding: '8px 14px', borderBottom: innerTab === 'controls' ? '2px solid #a78bfa' : '2px solid transparent', color: innerTab === 'controls' ? '#fff' : '#94a3b8', fontWeight: 600, cursor: 'pointer', fontSize: 12, letterSpacing: '0.4px' }}>
          CONTROLS
        </button>
      </div>

      {innerTab === 'apps' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <button type="button" className="btn btn-primary" onClick={function() { startCreate('app'); }} disabled={busy}>+ Add app</button>
            <button type="button" className="btn" onClick={openGraphImport} disabled={busy}>⤓ Import from Graph</button>
          </div>

          {(creating || editingId) && innerTab === 'apps' && (
            <div style={{ padding: 16, background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.2)', borderRadius: 8, marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#a78bfa', marginBottom: 12 }}>{editingId ? 'EDIT APP' : 'NEW APP'}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <label style={{ fontSize: 11, color: '#94a3b8' }}>Name<input type="text" value={form.name || ''} onChange={function(e) { setForm(Object.assign({}, form, { name: e.target.value })); }} style={{ width: '100%', marginTop: 4, padding: 8, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, color: '#fff', fontSize: 13 }} /></label>
                <label style={{ fontSize: 11, color: '#94a3b8' }}>URL / endpoint<input type="text" value={form.url || ''} onChange={function(e) { setForm(Object.assign({}, form, { url: e.target.value })); }} style={{ width: '100%', marginTop: 4, padding: 8, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, color: '#fff', fontSize: 13 }} /></label>
                <label style={{ fontSize: 11, color: '#94a3b8' }}>Exposure
                  <select value={form.exposure || ''} onChange={function(e) { setForm(Object.assign({}, form, { exposure: e.target.value })); }} style={{ width: '100%', marginTop: 4, padding: 8, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, color: '#fff', fontSize: 13 }}>
                    <option value="">—</option>
                    <option value="public">Public</option>
                    <option value="internal">Internal</option>
                    <option value="mixed">Mixed</option>
                  </select>
                </label>
                <label style={{ fontSize: 11, color: '#94a3b8' }}>Criticality tier
                  <select value={form.criticality_tier || ''} onChange={function(e) { setForm(Object.assign({}, form, { criticality_tier: e.target.value })); }} style={{ width: '100%', marginTop: 4, padding: 8, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, color: '#fff', fontSize: 13 }}>
                    <option value="">—</option>
                    <option value="tier_1_critical">Tier 1 (critical)</option>
                    <option value="tier_2_sensitive">Tier 2 (sensitive)</option>
                    <option value="tier_3_routine">Tier 3 (routine)</option>
                  </select>
                </label>
                <label style={{ fontSize: 11, color: '#94a3b8' }}>Auth method<input type="text" value={form.auth_method || ''} onChange={function(e) { setForm(Object.assign({}, form, { auth_method: e.target.value })); }} placeholder="OAuth / SAML / API key / etc." style={{ width: '100%', marginTop: 4, padding: 8, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, color: '#fff', fontSize: 13 }} /></label>
                <label style={{ fontSize: 11, color: '#94a3b8' }}>Owner email<input type="text" value={form.owner_email || ''} onChange={function(e) { setForm(Object.assign({}, form, { owner_email: e.target.value })); }} style={{ width: '100%', marginTop: 4, padding: 8, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, color: '#fff', fontSize: 13 }} /></label>
                <label style={{ fontSize: 11, color: '#94a3b8', gridColumn: '1 / -1' }}>Notes<textarea value={form.notes || ''} onChange={function(e) { setForm(Object.assign({}, form, { notes: e.target.value })); }} rows={2} style={{ width: '100%', marginTop: 4, padding: 8, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, color: '#fff', fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }} /></label>
                <label style={{ fontSize: 11, color: '#94a3b8', gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="checkbox" checked={!!form.crown_jewel_flag} onChange={function(e) { setForm(Object.assign({}, form, { crown_jewel_flag: e.target.checked })); }} />
                  Crown jewel (high-impact data — extra care during testing)
                </label>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button type="button" className="btn btn-primary" onClick={function() { submitForm('app'); }} disabled={busy}>{editingId ? 'Save' : 'Add'}</button>
                <button type="button" className="btn" onClick={cancelForm} disabled={busy}>Cancel</button>
              </div>
            </div>
          )}

          {apps.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#64748b', fontSize: 13, border: '1px dashed rgba(255,255,255,0.1)', borderRadius: 8 }}>
              No apps in catalog yet. Add manually or import from Entra (Graph).
            </div>
          ) : (
            <div style={{ overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'rgba(255,255,255,0.03)', textAlign: 'left' }}>
                    <th style={{ padding: 10, color: '#94a3b8', fontSize: 10, letterSpacing: '0.4px', fontWeight: 600 }}>NAME</th>
                    <th style={{ padding: 10, color: '#94a3b8', fontSize: 10, letterSpacing: '0.4px', fontWeight: 600 }}>EXPOSURE</th>
                    <th style={{ padding: 10, color: '#94a3b8', fontSize: 10, letterSpacing: '0.4px', fontWeight: 600 }}>TIER</th>
                    <th style={{ padding: 10, color: '#94a3b8', fontSize: 10, letterSpacing: '0.4px', fontWeight: 600 }}>AUTH</th>
                    <th style={{ padding: 10, color: '#94a3b8', fontSize: 10, letterSpacing: '0.4px', fontWeight: 600 }}>SOURCE</th>
                    <th style={{ padding: 10, color: '#94a3b8', fontSize: 10, letterSpacing: '0.4px', fontWeight: 600, textAlign: 'right' }}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {apps.map(function(a) {
                    return (
                      <tr key={a.id} style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                        <td style={{ padding: 10 }}>
                          <div style={{ fontWeight: 600 }}>{a.name}{a.crown_jewel_flag && <span title="Crown jewel" style={{ marginLeft: 6, color: '#fbbf24', fontSize: 11 }}>♦</span>}</div>
                          {a.url && <div style={{ fontSize: 11, color: '#64748b' }}>{a.url}</div>}
                        </td>
                        <td style={{ padding: 10, fontSize: 12, color: '#cbd5e1' }}>{a.exposure || '—'}</td>
                        <td style={{ padding: 10, fontSize: 12, color: a.criticality_tier === 'tier_1_critical' ? '#f87171' : a.criticality_tier === 'tier_2_sensitive' ? '#fbbf24' : '#94a3b8' }}>{a.criticality_tier ? a.criticality_tier.replace(/^tier_\d_/, '').replace(/_/g, ' ') : '—'}</td>
                        <td style={{ padding: 10, fontSize: 12, color: '#cbd5e1' }}>{a.auth_method || '—'}</td>
                        <td style={{ padding: 10 }}>{provenanceBadge(a.source_ref)}</td>
                        <td style={{ padding: 10, textAlign: 'right' }}>
                          <button type="button" className="btn btn-sm" onClick={function() { startEdit(a, 'app'); }} disabled={busy} style={{ marginRight: 4 }}>Edit</button>
                          <button type="button" className="btn btn-sm btn-danger" onClick={function() { handleDelete(a, 'app'); }} disabled={busy}>Delete</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {innerTab === 'apis' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <button type="button" className="btn btn-primary" onClick={function() { startCreate('api'); }} disabled={busy}>+ Add API</button>
          </div>

          {(creating || editingId) && innerTab === 'apis' && (
            <div style={{ padding: 16, background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.2)', borderRadius: 8, marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', color: '#a78bfa', marginBottom: 12 }}>{editingId ? 'EDIT API' : 'NEW API'}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <label style={{ fontSize: 11, color: '#94a3b8' }}>Name<input type="text" value={form.name || ''} onChange={function(e) { setForm(Object.assign({}, form, { name: e.target.value })); }} style={{ width: '100%', marginTop: 4, padding: 8, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, color: '#fff', fontSize: 13 }} /></label>
                <label style={{ fontSize: 11, color: '#94a3b8' }}>Parent app (optional)
                  <select value={form.app_id || ''} onChange={function(e) { setForm(Object.assign({}, form, { app_id: e.target.value ? parseInt(e.target.value, 10) : null })); }} style={{ width: '100%', marginTop: 4, padding: 8, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, color: '#fff', fontSize: 13 }}>
                    <option value="">—</option>
                    {apps.map(function(a) { return <option key={a.id} value={a.id}>{a.name}</option>; })}
                  </select>
                </label>
                <label style={{ fontSize: 11, color: '#94a3b8' }}>Protocol
                  <select value={form.protocol || ''} onChange={function(e) { setForm(Object.assign({}, form, { protocol: e.target.value })); }} style={{ width: '100%', marginTop: 4, padding: 8, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, color: '#fff', fontSize: 13 }}>
                    <option value="">—</option>
                    <option value="rest">REST</option>
                    <option value="graphql">GraphQL</option>
                    <option value="grpc">gRPC</option>
                    <option value="soap">SOAP</option>
                    <option value="other">Other</option>
                  </select>
                </label>
                <label style={{ fontSize: 11, color: '#94a3b8' }}>Auth
                  <select value={form.auth_kind || ''} onChange={function(e) { setForm(Object.assign({}, form, { auth_kind: e.target.value })); }} style={{ width: '100%', marginTop: 4, padding: 8, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, color: '#fff', fontSize: 13 }}>
                    <option value="">—</option>
                    <option value="api_key">API key</option>
                    <option value="oauth">OAuth</option>
                    <option value="jwt">JWT</option>
                    <option value="mtls">mTLS</option>
                    <option value="none">None</option>
                  </select>
                </label>
                <label style={{ fontSize: 11, color: '#94a3b8' }}>Base URL<input type="text" value={form.base_url || ''} onChange={function(e) { setForm(Object.assign({}, form, { base_url: e.target.value })); }} style={{ width: '100%', marginTop: 4, padding: 8, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, color: '#fff', fontSize: 13 }} /></label>
                <label style={{ fontSize: 11, color: '#94a3b8' }}>Docs URL<input type="text" value={form.docs_url || ''} onChange={function(e) { setForm(Object.assign({}, form, { docs_url: e.target.value, docs_available: !!e.target.value })); }} style={{ width: '100%', marginTop: 4, padding: 8, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, color: '#fff', fontSize: 13 }} /></label>
                <label style={{ fontSize: 11, color: '#94a3b8' }}>OpenAPI spec URL<input type="text" value={form.openapi_spec_url || ''} onChange={function(e) { setForm(Object.assign({}, form, { openapi_spec_url: e.target.value })); }} placeholder="Saves hours of recon for testers" style={{ width: '100%', marginTop: 4, padding: 8, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, color: '#fff', fontSize: 13 }} /></label>
                <label style={{ fontSize: 11, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 6, marginTop: 18 }}>
                  <input type="checkbox" checked={!!form.rate_limited} onChange={function(e) { setForm(Object.assign({}, form, { rate_limited: e.target.checked })); }} />
                  Rate-limited
                </label>
                <label style={{ fontSize: 11, color: '#94a3b8', gridColumn: '1 / -1' }}>Notes<textarea value={form.notes || ''} onChange={function(e) { setForm(Object.assign({}, form, { notes: e.target.value })); }} rows={2} style={{ width: '100%', marginTop: 4, padding: 8, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, color: '#fff', fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }} /></label>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button type="button" className="btn btn-primary" onClick={function() { submitForm('api'); }} disabled={busy}>{editingId ? 'Save' : 'Add'}</button>
                <button type="button" className="btn" onClick={cancelForm} disabled={busy}>Cancel</button>
              </div>
            </div>
          )}

          {apis.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#64748b', fontSize: 13, border: '1px dashed rgba(255,255,255,0.1)', borderRadius: 8 }}>
              No APIs in catalog yet. Add them manually — handing over OpenAPI / Swagger specs to the testing team saves significant recon time.
            </div>
          ) : (
            <div style={{ overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'rgba(255,255,255,0.03)', textAlign: 'left' }}>
                    <th style={{ padding: 10, color: '#94a3b8', fontSize: 10, letterSpacing: '0.4px', fontWeight: 600 }}>NAME</th>
                    <th style={{ padding: 10, color: '#94a3b8', fontSize: 10, letterSpacing: '0.4px', fontWeight: 600 }}>PARENT APP</th>
                    <th style={{ padding: 10, color: '#94a3b8', fontSize: 10, letterSpacing: '0.4px', fontWeight: 600 }}>PROTOCOL</th>
                    <th style={{ padding: 10, color: '#94a3b8', fontSize: 10, letterSpacing: '0.4px', fontWeight: 600 }}>AUTH</th>
                    <th style={{ padding: 10, color: '#94a3b8', fontSize: 10, letterSpacing: '0.4px', fontWeight: 600 }}>DOCS</th>
                    <th style={{ padding: 10, color: '#94a3b8', fontSize: 10, letterSpacing: '0.4px', fontWeight: 600, textAlign: 'right' }}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {apis.map(function(a) {
                    var parent = a.app_id && apps.find(function(x) { return x.id === a.app_id; });
                    return (
                      <tr key={a.id} style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                        <td style={{ padding: 10 }}>
                          <div style={{ fontWeight: 600 }}>{a.name}</div>
                          {a.base_url && <div style={{ fontSize: 11, color: '#64748b' }}>{a.base_url}</div>}
                        </td>
                        <td style={{ padding: 10, fontSize: 12, color: '#cbd5e1' }}>{parent ? parent.name : '—'}</td>
                        <td style={{ padding: 10, fontSize: 12, color: '#cbd5e1', textTransform: 'uppercase' }}>{a.protocol || '—'}</td>
                        <td style={{ padding: 10, fontSize: 12, color: '#cbd5e1' }}>{a.auth_kind || '—'}</td>
                        <td style={{ padding: 10, fontSize: 12, color: a.openapi_spec_url ? '#22c55e' : (a.docs_available ? '#a78bfa' : '#64748b') }}>{a.openapi_spec_url ? 'OpenAPI ✓' : (a.docs_available ? 'Docs ✓' : '—')}</td>
                        <td style={{ padding: 10, textAlign: 'right' }}>
                          <button type="button" className="btn btn-sm" onClick={function() { startEdit(a, 'api'); }} disabled={busy} style={{ marginRight: 4 }}>Edit</button>
                          <button type="button" className="btn btn-sm btn-danger" onClick={function() { handleDelete(a, 'api'); }} disabled={busy}>Delete</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {innerTab === 'attack_surface' && (
        <SimpleListCatalog
          clientId={clientId}
          endpoint="attack-surface"
          label="External attack surface"
          kinds={[
            { value: 'ip', label: 'IP' },
            { value: 'cidr', label: 'CIDR' },
            { value: 'domain', label: 'Domain' },
            { value: 'subdomain', label: 'Subdomain' },
            { value: 'url', label: 'URL' }
          ]}
          hasThirdParty={true}
          placeholder="10.0.0.0/24&#10;example.edu&#10;portal.example.edu&#10;https://app.example.edu/login"
          discoveryAdapter={{
            label: 'Discover subdomains via crt.sh',
            description: 'Queries certificate transparency logs for every TLS certificate issued under the domain. Returns deduplicated subdomain list. Free, no auth, no scope-lock required.',
            endpointKey: 'crtsh',
            inputField: 'domain',
            inputPlaceholder: 'example.edu (omit https://, no subdomain prefix)'
          }}
        />
      )}

      {innerTab === 'internal_scope' && (
        <SimpleListCatalog
          clientId={clientId}
          endpoint="internal-scope"
          label="Internal scope"
          kinds={[
            { value: 'cidr', label: 'CIDR' },
            { value: 'vlan', label: 'VLAN' },
            { value: 'segment', label: 'Segment' },
            { value: 'dmz', label: 'DMZ' },
            { value: 'management_network', label: 'Management network' }
          ]}
          hasThirdParty={false}
          placeholder="10.10.0.0/16&#10;192.168.50.0/24&#10;VLAN 200&#10;dmz-prod&#10;mgmt-net"
        />
      )}

      {innerTab === 'cloud_tenants' && <CloudTenantsCatalog clientId={clientId} />}
      {innerTab === 'saas' && <SaasInventoryCatalog clientId={clientId} />}
      {innerTab === 'identity_arch' && <IdentityArchCatalog clientId={clientId} />}
      {innerTab === 'controls' && <ControlsCatalog clientId={clientId} />}

      {graphModalOpen && (
        <div onClick={function() { if (!busy) setGraphModalOpen(false); }} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
          <div onClick={function(e) { e.stopPropagation(); }} style={{ background: '#0f172a', border: '1px solid rgba(167,139,250,0.3)', borderRadius: 12, padding: 24, maxWidth: 800, width: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.6px', color: '#a78bfa', marginBottom: 12 }}>IMPORT FROM ENTRA (GRAPH)</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 16 }}>Enterprise apps in this client's tenant. Microsoft-published apps appear last. Already-imported apps are disabled.</div>
            {graphLoading && <div style={{ padding: 24, textAlign: 'center' }}><SkeletonLoader /></div>}
            {!graphLoading && graphCandidates && (
              <div style={{ overflow: 'auto', flex: 1, marginBottom: 16 }}>
                {graphCandidates.length === 0 ? (
                  <div style={{ padding: 24, textAlign: 'center', color: '#64748b', fontSize: 13 }}>No enterprise apps found in this tenant.</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: 'rgba(255,255,255,0.03)', textAlign: 'left' }}>
                        <th style={{ padding: 8, width: 36 }}></th>
                        <th style={{ padding: 8, color: '#94a3b8', fontSize: 10 }}>NAME</th>
                        <th style={{ padding: 8, color: '#94a3b8', fontSize: 10 }}>PUBLISHER</th>
                        <th style={{ padding: 8, color: '#94a3b8', fontSize: 10 }}>STATUS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {graphCandidates.map(function(c) {
                        var disabled = c.already_imported || busy;
                        return (
                          <tr key={c.graph_app_id} style={{ borderTop: '1px solid rgba(255,255,255,0.04)', opacity: disabled ? 0.5 : 1 }}>
                            <td style={{ padding: 8 }}>
                              <input type="checkbox" disabled={disabled} checked={!!graphPicked[c.graph_app_id]} onChange={function(e) { setGraphPicked(Object.assign({}, graphPicked, { [c.graph_app_id]: e.target.checked })); }} />
                            </td>
                            <td style={{ padding: 8, color: '#fff' }}>{c.display_name || '(unnamed)'}<div style={{ fontSize: 10, color: '#64748b' }}>{c.graph_app_id}</div></td>
                            <td style={{ padding: 8, color: '#cbd5e1' }}>{c.publisher_name || (c.is_microsoft_published ? 'Microsoft' : '—')}</td>
                            <td style={{ padding: 8 }}>{c.already_imported ? <span style={{ color: '#64748b', fontSize: 10 }}>ALREADY IN CATALOG</span> : c.is_microsoft_published ? <span style={{ color: '#94a3b8', fontSize: 10 }}>MS-PUBLISHED</span> : <span style={{ color: '#a78bfa', fontSize: 10 }}>AVAILABLE</span>}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 16 }}>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>{Object.keys(graphPicked).filter(function(k) { return graphPicked[k]; }).length} selected</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="btn" onClick={function() { setGraphModalOpen(false); }} disabled={busy}>Cancel</button>
                <button type="button" className="btn btn-primary" onClick={commitGraphImport} disabled={busy || Object.keys(graphPicked).filter(function(k) { return graphPicked[k]; }).length === 0}>Import selected</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── RedTeamAttackSurface ──────────────────────────────────────────
// Phase 2 — live identity attack-path analysis. Calls the backend
// /redteam/attack-surface endpoint, which is gated on an executing
// engagement (412 if no active engagement). Renders findings grouped
// by severity with category badges and rich evidence.
function RedTeamAttackSurface({ clientId, engagement }) {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [err, setErr] = useState(null);
  var [running, setRunning] = useState(false);
  var [expandedId, setExpandedId] = useState(null);

  function runAnalysis() {
    setRunning(true);
    setErr(null);
    authFetch(API + '/clients/' + clientId + '/redteam/attack-surface')
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d, status: r.status }; }); })
      .then(function(x) {
        setRunning(false); setLoading(false);
        if (x.ok) setData(x.d);
        else setErr(x.d.error || 'Analysis failed (HTTP ' + x.status + ')');
      })
      .catch(function() { setRunning(false); setLoading(false); setErr('Analysis request failed'); });
  }
  useEffect(runAnalysis, [clientId]);  // eslint-disable-line react-hooks/exhaustive-deps

  var SEV_COLORS = { critical: '#ef4444', high: '#f59e0b', medium: '#60a5fa', low: '#a78bfa', informational: '#64748b' };
  var CATEGORY_LABELS = { attack_path: 'Attack Path', oauth_audit: 'OAuth Audit', ca_gap: 'CA Gap', privileged_identity: 'Privileged Identity', misconfig: 'Misconfiguration' };

  if (loading) return <div className="tab-content"><SkeletonLoader /></div>;
  if (err) {
    return (
      <div className="tab-content">
        <div className="admin-banner error" style={{ fontSize: 13 }}>{err}</div>
      </div>
    );
  }
  if (!data) return null;

  var counts = data.counts || {};

  return (
    <div className="tab-content">
      <TabDescription text="Live identity attack-path analysis against your Microsoft Entra tenant. Read-only Graph queries — no exploitation, no live attacks. Findings persist to the engagement so they show up in the report." />
      <ContentBand
        title="Attack Surface — Live Analysis"
        subtitle={'Engagement: ' + (engagement && engagement.name) + ' · Analysed ' + (data.analysed_at ? new Date(data.analysed_at).toLocaleString() : 'unknown')}
        rightContent={
          <button className="insights-btn" disabled={running} onClick={runAnalysis} title="Re-run the analysis against fresh Graph data">
            {running ? 'Running…' : 'Re-run'}
          </button>
        }
      >
        <div className="metric-tiles">
          <div className={'metric-tile' + ((counts.critical || 0) > 0 ? ' metric-tile-alert' : ' metric-tile-good')}>
            <div className="metric-tile-label">Critical</div>
            <div className="metric-tile-value" style={{ color: (counts.critical || 0) > 0 ? '#ef4444' : undefined }}>{counts.critical || 0}</div>
            <div className="metric-tile-context">Direct path to compromise</div>
          </div>
          <div className={'metric-tile' + ((counts.high || 0) > 0 ? ' metric-tile-alert' : '')}>
            <div className="metric-tile-label">High</div>
            <div className="metric-tile-value" style={{ color: (counts.high || 0) > 0 ? '#f59e0b' : undefined }}>{counts.high || 0}</div>
            <div className="metric-tile-context">Significant exposure</div>
          </div>
          <div className="metric-tile">
            <div className="metric-tile-label">Medium</div>
            <div className="metric-tile-value">{counts.medium || 0}</div>
            <div className="metric-tile-context">Hardening opportunity</div>
          </div>
          <div className="metric-tile">
            <div className="metric-tile-label">Total findings</div>
            <div className="metric-tile-value">{data.total || 0}</div>
            <div className="metric-tile-context">Persisted to engagement</div>
          </div>
        </div>
      </ContentBand>

      {data.findings && data.findings.length > 0 ? (
        <ContentBand title="Findings" subtitle="Sorted by severity. Click any finding for evidence + recommended remediation.">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {data.findings.map(function(f, i) {
              var color = SEV_COLORS[f.severity] || '#64748b';
              var key = f.finding_key || ('finding-' + i);
              var expanded = expandedId === key;
              return (
                <div key={key} style={{ background: 'var(--bg-card)', border: '1px solid var(--border-default)', borderLeft: '3px solid ' + color, borderRadius: 'var(--radius-lg)', padding: 14, cursor: 'pointer' }} onClick={function() { setExpandedId(expanded ? null : key); }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 240 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.5px', color: color, background: color + '20', border: '1px solid ' + color + '40', padding: '2px 8px', borderRadius: 999, textTransform: 'uppercase' }}>{f.severity}</span>
                        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--text-muted)', background: 'var(--bg-base)', border: '1px solid var(--border-default)', padding: '2px 8px', borderRadius: 999 }}>{CATEGORY_LABELS[f.category] || f.category}</span>
                        {f.cvss != null && f.cvss > 0 && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)' }}>CVSS {f.cvss}</span>}
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>{f.title}</div>
                    </div>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{expanded ? '▾' : '▸'}</span>
                  </div>
                  {expanded && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border-subtle)' }}>
                      {f.description && <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 12 }}>{f.description}</div>}
                      {f.evidence && (
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 6 }}>Evidence</div>
                          <pre style={{ fontSize: 11, color: 'var(--text-secondary)', background: 'var(--bg-base)', padding: 10, borderRadius: 4, margin: 0, overflow: 'auto', maxHeight: 280, whiteSpace: 'pre-wrap' }}>{JSON.stringify(f.evidence, null, 2)}</pre>
                        </div>
                      )}
                      {f.recommended_fix && (
                        <div style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.20)', padding: 12, borderRadius: 6 }}>
                          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: '#22c55e', marginBottom: 4 }}>Recommended Fix</div>
                          <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.6 }}>{f.recommended_fix}</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </ContentBand>
      ) : (
        <div className="empty-state" style={{ marginTop: 16 }}>No findings detected. Either the analysis ran successfully against a hardened tenant, or the bundle was incomplete.</div>
      )}
    </div>
  );
}

// ── RedTeamPhishing (Phase 4) ─────────────────────────────────────
// Three data sources, one workspace:
//   1. EDR Phishing Pressure  — derived from Huntress incident reports
//                                (zero manual work; refresh on demand)
//   2. CSV Import              — Huntress SAT campaign export
//                                (until Huntress publishes a SAT API)
//   3. KnowBe4 Sync            — when configured per-client
// Plus: Attack-Path Correlation surface — highest-priority users:
// failed phish AND has path to GA = top remediation target.
function RedTeamPhishing({ clientId }) {
  var [pressure, setPressure] = useState(null);
  var [pressureLoading, setPressureLoading] = useState(false);
  var [pressureErr, setPressureErr] = useState(null);
  var [campaigns, setCampaigns] = useState([]);
  var [campaignsLoading, setCampaignsLoading] = useState(true);
  var [csvBusy, setCsvBusy] = useState(false);
  var [kb4Busy, setKb4Busy] = useState(false);
  var [kb4Status, setKb4Status] = useState(null);
  var [toast, setToast] = useState(null);
  var [config, setConfig] = useState(null);

  function showToast(tone, text) {
    setToast({ tone: tone, text: text });
    setTimeout(function() { setToast(null); }, 6000);
  }

  function loadCampaigns() {
    setCampaignsLoading(true);
    authFetch(API + '/clients/' + clientId + '/redteam/phishing/campaigns')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) { setCampaigns((d && d.campaigns) || []); setCampaignsLoading(false); })
      .catch(function() { setCampaignsLoading(false); });
  }

  function loadConfig() {
    authFetch(API + '/admin/clients/' + clientId + '/config')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(setConfig);
  }

  useEffect(function() { loadCampaigns(); loadConfig(); }, [clientId]);  // eslint-disable-line react-hooks/exhaustive-deps

  function runPressure() {
    setPressureLoading(true); setPressureErr(null);
    authFetch(API + '/clients/' + clientId + '/redteam/phishing/pressure')
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, status: r.status, d: d }; }); })
      .then(function(x) {
        setPressureLoading(false);
        if (x.ok) { setPressure(x.d); loadCampaigns(); }
        else setPressureErr(x.d.error || 'Analysis failed (HTTP ' + x.status + ')');
      })
      .catch(function() { setPressureLoading(false); setPressureErr('Request failed'); });
  }

  function handleCsvFile(file) {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { showToast('error', 'CSV exceeds 10 MB'); return; }
    var reader = new FileReader();
    reader.onload = function(ev) {
      setCsvBusy(true);
      authFetch(API + '/clients/' + clientId + '/redteam/phishing/import-csv', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ csv: ev.target.result })
      })
        .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
        .then(function(x) {
          setCsvBusy(false);
          if (x.ok) { showToast('success', x.d.campaigns_imported + ' campaign(s) imported (' + x.d.total_rows + ' rows)'); loadCampaigns(); }
          else showToast('error', x.d.error || 'Import failed');
        })
        .catch(function() { setCsvBusy(false); showToast('error', 'Upload failed'); });
    };
    reader.readAsText(file);
  }

  function syncKnowBe4() {
    setKb4Busy(true); setKb4Status(null);
    authFetch(API + '/clients/' + clientId + '/redteam/phishing/sync-knowbe4', { method: 'POST' })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, status: r.status, d: d }; }); })
      .then(function(x) {
        setKb4Busy(false);
        if (x.ok) { showToast('success', x.d.campaigns_synced + ' KnowBe4 campaign(s) synced'); loadCampaigns(); setKb4Status({ configured: true, ok: true }); }
        else if (x.status === 412) setKb4Status({ configured: false, message: x.d.message });
        else setKb4Status({ configured: true, ok: false, error: x.d.error || 'Sync failed' });
      })
      .catch(function() { setKb4Busy(false); setKb4Status({ configured: true, ok: false, error: 'Request failed' }); });
  }

  var SOURCE_LABEL = { huntress_csv: 'Huntress SAT (CSV)', edr_observed: 'Huntress EDR (live)', knowbe4: 'KnowBe4', manual: 'Manual' };
  var SOURCE_COLOR = { huntress_csv: '#a78bfa', edr_observed: '#22c55e', knowbe4: '#f59e0b', manual: '#64748b' };
  var fmtDate = function(iso) { if (!iso) return '—'; try { return new Date(iso).toLocaleDateString(); } catch (x) { return iso; } };

  return (
    <div className="tab-content">
      <TabDescription text="Phishing & social-engineering telemetry. Pulls phishing pressure live from Huntress EDR, imports Huntress SAT campaign exports, and syncs KnowBe4 when configured. Failed users are correlated against attack-path findings — that intersection is your top remediation priority." />
      {toast && <div className={'admin-banner ' + toast.tone} style={{ fontSize: 12, marginBottom: 12 }}>{toast.text}</div>}

      {/* B — EDR Phishing Pressure (live) */}
      <ContentBand
        title="Phishing Pressure — Live from Huntress EDR"
        subtitle={pressure && pressure.sampled_at ? 'Sampled ' + new Date(pressure.sampled_at).toLocaleString() : 'Click "Analyse" to derive phishing pressure from the last batch of Huntress EDR incidents'}
        rightContent={<button className="insights-btn" disabled={pressureLoading} onClick={runPressure}>{pressureLoading ? 'Analysing…' : (pressure ? 'Re-analyse' : 'Analyse')}</button>}
      >
        {pressureErr && <div className="admin-banner error" style={{ fontSize: 12 }}>{pressureErr}</div>}
        {pressure && (
          <>
            <div className="metric-tiles" style={{ marginBottom: 12 }}>
              <div className={'metric-tile' + (pressure.summary.users_under_pressure > 0 ? ' metric-tile-alert' : ' metric-tile-good')}>
                <div className="metric-tile-label">Users Under Pressure</div>
                <div className="metric-tile-value">{pressure.summary.users_under_pressure}</div>
                <div className="metric-tile-context">Targeted in last EDR window</div>
              </div>
              <div className="metric-tile">
                <div className="metric-tile-label">Phishing Incidents</div>
                <div className="metric-tile-value">{pressure.summary.phishing_incidents}</div>
                <div className="metric-tile-context">email_security_detections &gt; 0</div>
              </div>
              <div className="metric-tile">
                <div className="metric-tile-label">Identity Attacks</div>
                <div className="metric-tile-value">{pressure.summary.identity_incidents}</div>
                <div className="metric-tile-context">M365 managed_identity events</div>
              </div>
              <div className={'metric-tile' + (pressure.correlated_with_attack_path.length > 0 ? ' metric-tile-alert' : '')}>
                <div className="metric-tile-label">Correlated High-Risk</div>
                <div className="metric-tile-value" style={{ color: pressure.correlated_with_attack_path.length > 0 ? '#ef4444' : undefined }}>{pressure.correlated_with_attack_path.length}</div>
                <div className="metric-tile-context">Failed AND has attack path</div>
              </div>
            </div>

            {pressure.correlated_with_attack_path.length > 0 && (
              <div style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.30)', borderRadius: 8, padding: 14, marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase', color: '#ef4444', marginBottom: 8 }}>Top Remediation Targets</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>These users have phished credentials AND a documented attack path. Real-world compromise of these accounts would have been catastrophic.</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {pressure.correlated_with_attack_path.slice(0, 10).map(function(u, i) {
                    var sevColors = { critical: '#ef4444', high: '#f59e0b', medium: '#60a5fa', low: '#a78bfa', informational: '#64748b' };
                    var c = sevColors[u.max_finding_severity] || '#64748b';
                    return (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', fontSize: 12 }}>
                        <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase', color: c, background: c + '20', border: '1px solid ' + c + '40', padding: '2px 8px', borderRadius: 999 }}>{u.max_finding_severity}</span>
                        <code style={{ fontFamily: 'var(--font-mono)', fontSize: 11, background: 'var(--bg-card)', padding: '1px 6px', borderRadius: 3 }}>{u.upn}</code>
                        {u.display_name && <span style={{ color: 'var(--text-secondary)' }}>{u.display_name}</span>}
                        <span style={{ color: 'var(--text-muted)' }}>· phish action: <strong>{u.phishing_action}</strong></span>
                        <span style={{ color: 'var(--text-muted)' }}>· {u.related_finding_count} related finding(s)</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {pressure.pressure_by_user && pressure.pressure_by_user.length > 0 && (
              <div style={{ overflowX: 'auto' }}>
                <table className="posture-table">
                  <thead><tr><th>UPN</th><th>Incident Count</th><th>Types</th><th>Severities</th><th>Last Activity</th></tr></thead>
                  <tbody>
                    {pressure.pressure_by_user.slice(0, 25).map(function(u, i) {
                      var sevList = Object.keys(u.severities).map(function(s) { return s + ': ' + u.severities[s]; }).join(', ');
                      return (
                        <tr key={i}>
                          <td><code style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{u.upn}</code></td>
                          <td style={{ fontVariantNumeric: 'tabular-nums' }}>{u.count}</td>
                          <td>{u.types.join(', ')}</td>
                          <td style={{ fontSize: 11 }}>{sevList}</td>
                          <td style={{ fontSize: 11, color: 'var(--text-muted)' }}>{u.last_at ? new Date(u.last_at).toLocaleString() : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {pressure.pressure_by_user.length === 0 && (
              <div className="muted" style={{ fontSize: 12, padding: 12 }}>No phishing or identity-attack incidents in the current Huntress EDR sample. Either you're clean — or the sample window is too narrow.</div>
            )}
          </>
        )}
      </ContentBand>

      {/* A — CSV Import */}
      <ContentBand title="Import Huntress SAT Campaign Results" subtitle="Until Huntress exposes a SAT API, export campaign results from the SAT portal as CSV and drop here. Columns: campaign_name, sent_at, user_email, action, detected_at (case-insensitive; subset OK).">
        <div style={{ background: 'var(--bg-card)', border: '2px dashed var(--border-default)', borderRadius: 'var(--radius-lg)', padding: 24, textAlign: 'center' }}
          onDragOver={function(e) { e.preventDefault(); e.stopPropagation(); }}
          onDrop={function(e) { e.preventDefault(); e.stopPropagation(); var f = e.dataTransfer.files && e.dataTransfer.files[0]; if (f) handleCsvFile(f); }}
        >
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>Drop CSV here or click to browse</div>
          <input id="phishing-csv-input" type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={function(e) { handleCsvFile(e.target.files[0]); e.target.value = ''; }} />
          <button className="qbr-generate-btn" style={{ flex: 'none', padding: '10px 24px', fontSize: 12 }} disabled={csvBusy} onClick={function() { document.getElementById('phishing-csv-input').click(); }}>{csvBusy ? 'Importing…' : 'Choose CSV File'}</button>
        </div>
      </ContentBand>

      {/* C — KnowBe4 sync */}
      <ContentBand title="KnowBe4 Sync" subtitle={config && config.knowbe4_token_set ? 'Configured — sync pulls campaigns + recipients from KnowBe4 API' : 'Not configured — set knowbe4_api_token + knowbe4_region under admin integrations'}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button className="insights-btn" disabled={kb4Busy || (config && !config.knowbe4_token_set)} onClick={syncKnowBe4}>
            {kb4Busy ? 'Syncing…' : 'Sync KnowBe4 now'}
          </button>
          {config && !config.knowbe4_token_set && <span className="muted" style={{ fontSize: 12 }}>Add knowbe4_api_token in admin → integrations to enable.</span>}
          {kb4Status && !kb4Status.configured && <span className="muted" style={{ fontSize: 12 }}>{kb4Status.message}</span>}
          {kb4Status && kb4Status.configured && kb4Status.error && <span style={{ color: '#ef4444', fontSize: 12 }}>{kb4Status.error}</span>}
        </div>
      </ContentBand>

      {/* All campaigns (any source) */}
      <ContentBand title="Campaigns" subtitle={campaigns.length + ' total — most recent first'}>
        {campaignsLoading ? <SkeletonLoader /> : campaigns.length === 0 ? (
          <div className="muted" style={{ fontSize: 12, padding: 12 }}>No campaigns yet. Run an EDR analysis, import a Huntress CSV, or sync KnowBe4.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="posture-table">
              <thead><tr><th>Source</th><th>Name</th><th>Sent</th><th>Targets</th><th>Clicks</th><th>Cred Capture</th><th>Reported</th></tr></thead>
              <tbody>
                {campaigns.map(function(c) {
                  var color = SOURCE_COLOR[c.source] || '#64748b';
                  return (
                    <tr key={c.id}>
                      <td><span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase', color: color, background: color + '20', border: '1px solid ' + color + '40', padding: '2px 8px', borderRadius: 999 }}>{SOURCE_LABEL[c.source] || c.source}</span></td>
                      <td style={{ fontSize: 12, color: 'var(--text-primary)' }}>{c.name}</td>
                      <td style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fmtDate(c.sent_at)}</td>
                      <td style={{ fontVariantNumeric: 'tabular-nums' }}>{c.target_count}</td>
                      <td style={{ fontVariantNumeric: 'tabular-nums', color: c.click_count > 0 ? '#f59e0b' : undefined }}>{c.click_count}</td>
                      <td style={{ fontVariantNumeric: 'tabular-nums', color: c.credential_capture_count > 0 ? '#ef4444' : undefined }}>{c.credential_capture_count}</td>
                      <td style={{ fontVariantNumeric: 'tabular-nums', color: c.reported_count > 0 ? '#22c55e' : undefined }}>{c.reported_count}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </ContentBand>
    </div>
  );
}

// ── RedTeamReports ────────────────────────────────────────────────
// Phase 3 — engagement deliverables. Three report variants:
//   • Executive — 1-2 page narrative for the client's leadership
//   • Technical — full finding list with evidence + fixes (the
//                 deliverable for the client's security/IT team)
//   • Compliance — pen-test attestation pack for cyber-insurance
//                  underwriting and CMMC 3.12.1 evidence
//
// All three render from the same /report endpoint payload — variant
// selection just toggles which sections appear. Print-friendly CSS
// (.redteam-report-print) hides nav/sidebar so window.print()
// produces a clean PDF.
function RedTeamReports({ clientId, engagements }) {
  var [selectedEngagement, setSelectedEngagement] = useState(null);
  var [reportData, setReportData] = useState(null);
  var [loading, setLoading] = useState(false);
  var [variant, setVariant] = useState('executive');
  var [err, setErr] = useState(null);

  function loadReport(eng) {
    setSelectedEngagement(eng);
    setLoading(true); setErr(null); setReportData(null);
    authFetch(API + '/clients/' + clientId + '/redteam/engagements/' + eng.id + '/report')
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setLoading(false);
        if (x.ok) setReportData(x.d);
        else setErr(x.d.error || 'Report load failed');
      })
      .catch(function() { setLoading(false); setErr('Report request failed'); });
  }

  function handlePrint() { window.print(); }

  if (!engagements || engagements.length === 0) {
    return (
      <div className="tab-content">
        <div className="empty-state" style={{ marginTop: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>No engagements yet</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Reports are generated per engagement. Start one in the Engagements sub-tab.</div>
        </div>
      </div>
    );
  }

  if (selectedEngagement && reportData) {
    return (
      <div className="tab-content redteam-report-wrap">
        <div className="redteam-report-toolbar no-print">
          <button className="insights-btn" onClick={function() { setSelectedEngagement(null); setReportData(null); }}>← Back to engagements</button>
          <div className="redteam-report-variant-pills">
            <button type="button" className={'redteam-report-variant' + (variant === 'executive' ? ' active' : '')} onClick={function() { setVariant('executive'); }}>Executive Summary</button>
            <button type="button" className={'redteam-report-variant' + (variant === 'technical' ? ' active' : '')} onClick={function() { setVariant('technical'); }}>Technical Report</button>
            <button type="button" className={'redteam-report-variant' + (variant === 'compliance' ? ' active' : '')} onClick={function() { setVariant('compliance'); }}>Compliance Attestation</button>
          </div>
          <button className="qbr-generate-btn" style={{ flex: 'none', padding: '8px 18px', fontSize: 12 }} onClick={handlePrint}>Print / Save as PDF</button>
        </div>
        <RedTeamReportView context={reportData} variant={variant} />
      </div>
    );
  }

  return (
    <div className="tab-content">
      <TabDescription text="Engagement deliverables. Each engagement yields three report variants — pick one to render an export-ready document." />
      {err && <div className="admin-banner error" style={{ fontSize: 12, marginBottom: 12 }}>{err}</div>}
      {loading && <div style={{ marginTop: 16 }}><SkeletonLoader /></div>}
      {!loading && (
        <ContentBand title="Available Reports" subtitle={engagements.length + ' engagement(s)'}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {engagements.map(function(e) {
              return (
                <div key={e.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-lg)', padding: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 240 }}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>RTE-{e.id}</span>
                      <span>·</span>
                      <span>{e.status}</span>
                      <span>·</span>
                      <span>{new Date(e.created_at).toLocaleDateString()}</span>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{e.name}</div>
                    {e.lead_tester && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Lead: {e.lead_tester}</div>}
                  </div>
                  <button className="qbr-generate-btn" style={{ flex: 'none', padding: '8px 18px', fontSize: 12 }} onClick={function() { loadReport(e); }}>View Report →</button>
                </div>
              );
            })}
          </div>
        </ContentBand>
      )}
    </div>
  );
}

// Pure rendering component — given a report context + variant, produces
// the on-screen + print-ready HTML.
function RedTeamReportView({ context, variant }) {
  if (!context) return null;
  var e = context.engagement;
  var s = context.summary;
  var counts = s.counts_by_severity || {};
  var fmtDate = function(iso) { if (!iso) return '—'; try { return new Date(iso).toLocaleDateString(); } catch (x) { return iso; } };

  // ── Common header ─────────────────────────────────────────────
  var header = (
    <div className="redteam-report-header">
      <div className="redteam-report-eyebrow">CloudByte Group · Aurelius Red Team</div>
      <div className="redteam-report-title">
        {variant === 'executive' && 'Executive Summary'}
        {variant === 'technical' && 'Technical Assessment Report'}
        {variant === 'compliance' && 'Penetration Test Attestation'}
      </div>
      <div className="redteam-report-subtitle">{e.name}</div>
      <div className="redteam-report-meta">
        <div><span className="redteam-report-meta-label">Engagement ID</span><span>RTE-{e.id}</span></div>
        <div><span className="redteam-report-meta-label">Lead Tester</span><span>{e.lead_tester || '—'}</span></div>
        <div><span className="redteam-report-meta-label">Started</span><span>{fmtDate(e.started_at)}</span></div>
        <div><span className="redteam-report-meta-label">Ended</span><span>{fmtDate(e.ended_at) === '—' ? 'In progress' : fmtDate(e.ended_at)}</span></div>
        <div><span className="redteam-report-meta-label">Duration</span><span>{e.duration_days ? e.duration_days + ' day(s)' : '—'}</span></div>
        <div><span className="redteam-report-meta-label">Generated</span><span>{fmtDate(context.generated_at)}</span></div>
      </div>
    </div>
  );

  // ── Severity pill helper ──────────────────────────────────────
  var sevPill = function(sev) {
    var colors = { critical: '#ef4444', high: '#f59e0b', medium: '#60a5fa', low: '#a78bfa', informational: '#64748b' };
    var c = colors[sev] || '#64748b';
    return <span className="redteam-report-sev-pill" style={{ color: c, background: c + '20', border: '1px solid ' + c + '40' }}>{(sev || '').toUpperCase()}</span>;
  };

  // ── EXECUTIVE ─────────────────────────────────────────────────
  if (variant === 'executive') {
    return (
      <div className="redteam-report redteam-report-print">
        {header}
        <div className="redteam-report-section">
          <div className="redteam-report-section-title">Headline</div>
          <div className="redteam-report-headline">
            {(counts.critical || 0) > 0
              ? <><strong style={{ color: '#ef4444' }}>{counts.critical || 0}</strong> critical finding(s) require immediate remediation.</>
              : (counts.high || 0) > 0
                ? <><strong style={{ color: '#f59e0b' }}>{counts.high || 0}</strong> high-severity finding(s) recommended for prompt remediation.</>
                : <>Engagement closed without critical or high-severity findings.</>
            }
            {' '}Total of {s.total_findings} finding(s) across {Object.keys(s.counts_by_category || {}).length} category(ies).
          </div>
        </div>

        <div className="redteam-report-section">
          <div className="redteam-report-section-title">Findings at a Glance</div>
          <div className="redteam-report-counts">
            <div className="redteam-report-count-tile" style={{ borderColor: (counts.critical || 0) > 0 ? '#ef4444' : 'transparent' }}>
              <div className="redteam-report-count-value" style={{ color: (counts.critical || 0) > 0 ? '#ef4444' : undefined }}>{counts.critical || 0}</div>
              <div className="redteam-report-count-label">Critical</div>
            </div>
            <div className="redteam-report-count-tile" style={{ borderColor: (counts.high || 0) > 0 ? '#f59e0b' : 'transparent' }}>
              <div className="redteam-report-count-value" style={{ color: (counts.high || 0) > 0 ? '#f59e0b' : undefined }}>{counts.high || 0}</div>
              <div className="redteam-report-count-label">High</div>
            </div>
            <div className="redteam-report-count-tile">
              <div className="redteam-report-count-value">{counts.medium || 0}</div>
              <div className="redteam-report-count-label">Medium</div>
            </div>
            <div className="redteam-report-count-tile">
              <div className="redteam-report-count-value">{counts.low || 0}</div>
              <div className="redteam-report-count-label">Low</div>
            </div>
            <div className="redteam-report-count-tile">
              <div className="redteam-report-count-value">{counts.informational || 0}</div>
              <div className="redteam-report-count-label">Informational</div>
            </div>
          </div>
        </div>

        {s.top_critical && s.top_critical.length > 0 && (
          <div className="redteam-report-section">
            <div className="redteam-report-section-title">Top Critical Issues</div>
            <ol className="redteam-report-top-list">
              {s.top_critical.map(function(f, i) {
                return (
                  <li key={i}>
                    <strong>{f.title}</strong>
                    {f.recommended_fix && <div className="redteam-report-top-fix">{f.recommended_fix}</div>}
                  </li>
                );
              })}
            </ol>
          </div>
        )}

        {context.recommendations && context.recommendations.length > 0 && (
          <div className="redteam-report-section">
            <div className="redteam-report-section-title">Recommended Remediation Effort</div>
            <ul className="redteam-report-recs">
              {context.recommendations.slice(0, 8).map(function(r, i) {
                return (
                  <li key={i}>
                    <code>{r.playbook_id}</code>
                    <span> — {r.finding_count} finding(s)</span>
                  </li>
                );
              })}
            </ul>
            <div className="redteam-report-note">Each playbook ID maps to a documented remediation procedure in the Aurelius cmmc-remediations catalog. CloudByte Group can execute approved-and-authorized playbooks via the Aurelius Proposals workflow.</div>
          </div>
        )}

        <div className="redteam-report-signoff">
          <div>Prepared by CloudByte Group</div>
          <div className="redteam-report-meta-label">Engagement closed {fmtDate(e.ended_at) !== '—' ? fmtDate(e.ended_at) : '—'}</div>
        </div>
      </div>
    );
  }

  // ── TECHNICAL ─────────────────────────────────────────────────
  if (variant === 'technical') {
    var byCategoryFindings = {};
    (context.findings || []).forEach(function(f) {
      if (!byCategoryFindings[f.category]) byCategoryFindings[f.category] = [];
      byCategoryFindings[f.category].push(f);
    });
    var CATEGORY_LABELS = { attack_path: 'Identity Attack Path', oauth_audit: 'OAuth Application Security', ca_gap: 'Conditional Access Gaps', privileged_identity: 'Privileged Identity', misconfig: 'Configuration Hygiene' };

    return (
      <div className="redteam-report redteam-report-print">
        {header}

        {e.scope && (
          <div className="redteam-report-section">
            <div className="redteam-report-section-title">Engagement Scope</div>
            <pre className="redteam-report-scope">{typeof e.scope === 'string' ? e.scope : JSON.stringify(e.scope, null, 2)}</pre>
          </div>
        )}

        <div className="redteam-report-section">
          <div className="redteam-report-section-title">Methodology</div>
          {context.methodology.map(function(m, i) {
            return (
              <div key={i} className="redteam-report-method-area">
                <div className="redteam-report-method-area-title">{m.area}</div>
                <ul>{m.items.map(function(item, j) { return <li key={j}>{item}</li>; })}</ul>
              </div>
            );
          })}
        </div>

        <div className="redteam-report-section">
          <div className="redteam-report-section-title">Summary</div>
          <table className="redteam-report-summary-table">
            <thead><tr><th>Severity</th><th>Count</th></tr></thead>
            <tbody>
              <tr><td>{sevPill('critical')}</td><td>{counts.critical || 0}</td></tr>
              <tr><td>{sevPill('high')}</td><td>{counts.high || 0}</td></tr>
              <tr><td>{sevPill('medium')}</td><td>{counts.medium || 0}</td></tr>
              <tr><td>{sevPill('low')}</td><td>{counts.low || 0}</td></tr>
              <tr><td>{sevPill('informational')}</td><td>{counts.informational || 0}</td></tr>
              <tr><td><strong>Total</strong></td><td><strong>{s.total_findings}</strong></td></tr>
            </tbody>
          </table>
        </div>

        <div className="redteam-report-section">
          <div className="redteam-report-section-title">Findings</div>
          {Object.keys(byCategoryFindings).length === 0
            ? <div className="redteam-report-note">No findings recorded for this engagement.</div>
            : Object.keys(byCategoryFindings).sort().map(function(cat) {
                return (
                  <div key={cat} className="redteam-report-category-block">
                    <div className="redteam-report-category-title">{CATEGORY_LABELS[cat] || cat}</div>
                    {byCategoryFindings[cat].map(function(f, i) {
                      return (
                        <div key={i} className="redteam-report-finding">
                          <div className="redteam-report-finding-head">
                            {sevPill(f.severity)}
                            {f.cvss != null && f.cvss > 0 && <span className="redteam-report-cvss">CVSS {f.cvss}</span>}
                            <span className="redteam-report-finding-status">{f.status}</span>
                            <span className="redteam-report-finding-title">{f.title}</span>
                          </div>
                          {f.description && <div className="redteam-report-finding-desc">{f.description}</div>}
                          {f.evidence && (
                            <div className="redteam-report-finding-evidence">
                              <div className="redteam-report-finding-evidence-label">Evidence</div>
                              <pre>{JSON.stringify(f.evidence, null, 2)}</pre>
                            </div>
                          )}
                          {f.recommended_fix && (
                            <div className="redteam-report-finding-fix">
                              <div className="redteam-report-finding-fix-label">Recommended Fix</div>
                              <div>{f.recommended_fix}</div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
        </div>

        <div className="redteam-report-signoff">
          <div>Prepared by CloudByte Group · Aurelius Red Team</div>
          <div className="redteam-report-meta-label">Generated {fmtDate(context.generated_at)}</div>
        </div>
      </div>
    );
  }

  // ── COMPLIANCE ────────────────────────────────────────────────
  return (
    <div className="redteam-report redteam-report-print redteam-report-compliance">
      {header}

      <div className="redteam-report-section">
        <div className="redteam-report-section-title">Attestation</div>
        <div className="redteam-report-attest">
          CloudByte Group, operating as the security service provider for the named client,
          attests that a penetration-testing engagement was conducted within the date range
          listed above against the in-scope identity and Conditional Access surface of the
          client's Microsoft Entra tenant. The engagement followed an authorized scope
          and Rules of Engagement (on file, reference: <code>{e.authorization_letter_url || '—'}</code>),
          and produced {s.total_findings} finding(s) catalogued in the corresponding
          Technical Report ({(counts.critical || 0)} critical, {(counts.high || 0)} high,
          {' ' + (counts.medium || 0)} medium, {(counts.low || 0)} low, {(counts.informational || 0)} informational).
        </div>
      </div>

      <div className="redteam-report-section">
        <div className="redteam-report-section-title">Standards Reference</div>
        <ul className="redteam-report-standards">
          <li>NIST SP 800-171 Rev 2 — control 3.12.1 (security control assessment) — fulfilled by this engagement</li>
          <li>NIST SP 800-115 — Technical Guide to Information Security Testing (methodology reference)</li>
          <li>Engagement scope, ROE, and underlying technical evidence retained per CloudByte Group records-retention policy</li>
        </ul>
      </div>

      <div className="redteam-report-section">
        <div className="redteam-report-section-title">Signatures</div>
        <div className="redteam-report-signatures">
          <div className="redteam-report-sig-block">
            <div className="redteam-report-sig-line">_______________________________</div>
            <div className="redteam-report-sig-label">Lead Tester (CloudByte Group)</div>
            <div className="redteam-report-sig-name">{e.lead_tester || '________________________________'}</div>
            <div className="redteam-report-sig-date">Date: {fmtDate(e.ended_at)}</div>
          </div>
          <div className="redteam-report-sig-block">
            <div className="redteam-report-sig-line">_______________________________</div>
            <div className="redteam-report-sig-label">Client Authorized Representative</div>
            <div className="redteam-report-sig-name">________________________________</div>
            <div className="redteam-report-sig-date">Date: ________________________</div>
          </div>
        </div>
      </div>

      <div className="redteam-report-signoff">
        <div>Prepared by CloudByte Group · Aurelius Red Team</div>
        <div className="redteam-report-meta-label">Engagement RTE-{e.id} · Generated {fmtDate(context.generated_at)}</div>
      </div>
    </div>
  );
}

// ── RedTeamSettings ───────────────────────────────────────────────
// Phase 1 stub. Future: ROE templates, authorized testers,
// scheduling windows, white-team contact defaults.
function RedTeamSettings({ clientId }) {
  return (
    <div className="tab-content">
      <TabDescription text="Engagement defaults and authorization settings. Configured per-client; applies to new engagements." />
      <div className="empty-state" style={{ marginTop: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Settings — coming next phase</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', maxWidth: 600, margin: '0 auto', lineHeight: 1.6 }}>
          ROE template references, authorized tester list, default scheduling window, white-team
          contacts, and authorization-letter storage will land here. For now, configure these
          per-engagement when you create an engagement.
        </div>
      </div>
    </div>
  );
}

// ── Attack Detail Panel (slide-out drawer from Red Team) ─────────
function AttackDetailPanel({ isOpen, attackId, clientId, onClose, onConvertToCR, goToUser, goToDevice }) {
  var [detail, setDetail] = useState(null);
  var [loading, setLoading] = useState(false);
  var [showTechnical, setShowTechnical] = useState(false);
  var [expandedStep, setExpandedStep] = useState(null);
  var [copiedStep, setCopiedStep] = useState(null);
  useEffect(function() {
    if (!isOpen) return;
    var handler = function(e) { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return function() { window.removeEventListener('keydown', handler); };
  }, [isOpen, onClose]);

  useEffect(function() {
    if (!isOpen || !attackId) return;
    setLoading(true); setDetail(null); setShowTechnical(false); setExpandedStep(null); setCopiedStep(null);
    authFetch(API + '/clients/' + clientId + '/redteam/' + attackId)
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) { setDetail(d); setLoading(false); })
      .catch(function() { setLoading(false); });
  }, [isOpen, attackId, clientId]);

  if (!isOpen) return null;

  var outcomeStyle = { blocked: { color: '#22c55e', label: 'Blocked' }, partial: { color: '#f59e0b', label: 'Partial' }, successful: { color: '#ef4444', label: 'Got Through' } };
  var userOutcomeIcon = { clicked: '✕', compromised_credentials: '✕', recognized_phish: '✓', reported_email: '✓', ignored: '○', declined_consent: '✓' };
  var userOutcomeLabel = { clicked: 'Clicked link', compromised_credentials: 'Credentials captured', recognized_phish: 'Spotted the phish', reported_email: 'Reported to security', ignored: 'Did not interact', declined_consent: 'Declined permission grant' };
  var userOutcomeColor = { clicked: '#ef4444', compromised_credentials: '#ef4444', recognized_phish: '#22c55e', reported_email: '#22c55e', ignored: '#64748b', declined_consent: '#22c55e' };

  var copyToClipboard = function(text, stepNum) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(function() { setCopiedStep(stepNum); setTimeout(function() { setCopiedStep(null); }, 2000); });
    }
  };

  return (
    <>
      <div className="qbr-overlay" onClick={onClose} />
      <div className="attack-detail-panel">
        <div className="attack-detail-header">
          <div>
            <div className="attack-detail-eyebrow">RED TEAM FINDING</div>
            <div className="attack-detail-title">{detail ? detail.type_label : 'Loading…'}</div>
            {detail && <div className="attack-detail-meta">ID: {detail.id} · {new Date(detail.time).toLocaleString()}</div>}
          </div>
          <button className="qbr-close-btn" onClick={onClose}>&#10005;</button>
        </div>

        <div className="attack-detail-body">
          {loading && <div className="qbr-loading"><div className="qbr-loading-spinner" /><div className="qbr-loading-stage">Loading attack detail…</div></div>}
          {!loading && detail && (
            <>
              {/* Executive Summary — plain English, top billing */}
              <div className="attack-detail-section attack-detail-summary">
                <div className="attack-detail-section-title">In plain English</div>
                <div className="attack-detail-summary-text">{detail.plain_summary}</div>
                <div className="attack-detail-impact">
                  <div className="attack-detail-impact-label">Business impact</div>
                  <div className="attack-detail-impact-text">{detail.business_impact}</div>
                </div>
              </div>

              {/* Who was involved */}
              <div className="attack-detail-section">
                <div className="attack-detail-section-title">Who was involved · click any name to view their security profile</div>
                <div className="attack-detail-users">
                  {detail.targets.users.map(function(u, i) {
                    var color = userOutcomeColor[u.outcome] || '#64748b';
                    return (
                      <button key={i} className="attack-detail-user" onClick={function() { goToUser(u.name); }}>
                        <span className="attack-detail-user-icon" style={{ background: color + '20', color: color }}>{userOutcomeIcon[u.outcome] || '●'}</span>
                        <div className="attack-detail-user-body">
                          <div className="attack-detail-user-name">{u.name}</div>
                          <div className="attack-detail-user-role">{u.role}</div>
                        </div>
                        <div className="attack-detail-user-outcome" style={{ color: color }}>{userOutcomeLabel[u.outcome] || u.outcome}</div>
                      </button>
                    );
                  })}
                </div>
                {detail.targets.devices.length > 0 && (
                  <div className="attack-detail-devices">
                    <div className="attack-detail-section-sub">Devices involved</div>
                    {detail.targets.devices.map(function(d, i) {
                      return (
                        <button key={i} className="attack-detail-device" onClick={function() { goToDevice(d.name); }}>
                          <span className="attack-detail-device-icon">◈</span>
                          <div>
                            <div className="attack-detail-device-name">{d.name}</div>
                            <div className="attack-detail-device-sub">{d.role_in_attack} · Owner: {d.owner}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Step-by-step */}
              <div className="attack-detail-section">
                <div className="attack-detail-section-title">
                  How it unfolded · step by step
                  <button className="attack-detail-toggle" onClick={function() { setShowTechnical(!showTechnical); }}>
                    {showTechnical ? 'Show plain-language only' : 'Also show technical details'}
                  </button>
                </div>
                <div className="attack-detail-steps">
                  {detail.steps.map(function(step, i) {
                    return (
                      <div key={i} className="attack-detail-step">
                        <div className="attack-detail-step-time">{step.time}</div>
                        <div className="attack-detail-step-body">
                          <div className="attack-detail-step-plain">{step.plain}</div>
                          {showTechnical && <div className="attack-detail-step-technical">{step.technical}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Technical Indicators (collapsible) */}
              {showTechnical && (
                <div className="attack-detail-section attack-detail-technical">
                  <div className="attack-detail-section-title">Technical indicators</div>
                  <div className="attack-detail-mitre">
                    <span className="attack-detail-mitre-label">MITRE ATT&amp;CK</span>
                    <a href={detail.indicators.mitre_url} target="_blank" rel="noopener noreferrer" className="attack-detail-mitre-link">{detail.indicators.mitre_technique} ↗</a>
                  </div>
                  <div className="attack-detail-iocs-label">Indicators of Compromise (IOCs)</div>
                  <div className="attack-detail-iocs">
                    {detail.indicators.iocs.map(function(ioc, i) {
                      return (
                        <div key={i} className="attack-detail-ioc">
                          <span className="attack-detail-ioc-type">{ioc.type}</span>
                          <span className="attack-detail-ioc-value">{ioc.value}</span>
                          <span className="attack-detail-ioc-note">{ioc.note}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="attack-detail-iocs-label">Detection sources</div>
                  <div className="attack-detail-detections">
                    {detail.detection_sources.map(function(d, i) {
                      return (
                        <div key={i} className="attack-detail-detection">
                          <span className="attack-detail-detection-source">{d.source}</span>
                          <span className="attack-detail-detection-signal">{d.signal}</span>
                          {d.alert_id && <span className="attack-detail-detection-alert">{d.alert_id}</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Remediation Playbook */}
              <div className="attack-detail-section attack-detail-playbook">
                <div className="attack-detail-section-title">Remediation playbook · ordered by priority</div>
                {detail.playbook.map(function(p, i) {
                  var isExpanded = expandedStep === i;
                  var hasScript = p.powershell || p.conditional_access_template || p.huntress_console;
                  return (
                    <div key={i} className={'attack-detail-playbook-step' + (isExpanded ? ' is-expanded' : '')}>
                      <div className="attack-detail-playbook-header" onClick={function() { setExpandedStep(isExpanded ? null : i); }}>
                        <span className="attack-detail-playbook-num">{p.step}</span>
                        <div className="attack-detail-playbook-title-wrap">
                          <div className="attack-detail-playbook-title">{p.title}</div>
                          <div className="attack-detail-playbook-plain">{p.plain}</div>
                        </div>
                        <div className="attack-detail-playbook-time">{p.estimated_time}</div>
                        <span className="attack-detail-playbook-chevron">{isExpanded ? '▾' : '▸'}</span>
                      </div>
                      {isExpanded && (
                        <div className="attack-detail-playbook-body">
                          {p.powershell && (
                            <div className="attack-detail-code-block">
                              <div className="attack-detail-code-header">
                                <span>PowerShell · copy to run in Exchange Online / Microsoft Graph PowerShell</span>
                                <button className="attack-detail-code-copy" onClick={function() { copyToClipboard(p.powershell, 'ps-' + i); }}>{copiedStep === 'ps-' + i ? '✓ Copied' : 'Copy'}</button>
                              </div>
                              <pre className="attack-detail-code">{p.powershell}</pre>
                            </div>
                          )}
                          {p.conditional_access_template && (
                            <div className="attack-detail-code-block">
                              <div className="attack-detail-code-header">
                                <span>Conditional Access policy template · paste into Entra admin portal</span>
                                <button className="attack-detail-code-copy" onClick={function() { copyToClipboard(JSON.stringify(p.conditional_access_template, null, 2), 'ca-' + i); }}>{copiedStep === 'ca-' + i ? '✓ Copied' : 'Copy'}</button>
                              </div>
                              <pre className="attack-detail-code">{JSON.stringify(p.conditional_access_template, null, 2)}</pre>
                            </div>
                          )}
                          {p.huntress_console && (
                            <div className="attack-detail-link-block">
                              <span>Huntress MSAT Console: </span>
                              <a href={p.huntress_console} target="_blank" rel="noopener noreferrer">{p.huntress_console} ↗</a>
                            </div>
                          )}
                          {p.is_change_request && (
                            <div className="attack-detail-playbook-actions">
                              <button className="redteam-convert-btn" onClick={function() { onConvertToCR({ change_request_prefill: {
                                title: 'Deploy FIDO2 hardware keys — Finance & Executive roles',
                                description: 'Source: Aurelius Red Team ' + detail.id + '\n\n' + p.plain,
                                type: 'Security Change', category: 'Hardware Request', priority: 'High',
                                impact_summary: { users_affected: 8, devices_affected: 8, license_impact: '$0 (covered by existing E5)', cost_estimate: '$4,200 one-time', timeline: '2 weeks' }
                              }}); }}>
                                <span className="redteam-convert-btn-icon">→</span>
                                <span>Convert to Change Request</span>
                              </button>
                              <span className="redteam-convert-hint">Sends through normal approval workflow with full impact pre-filled</span>
                            </div>
                          )}
                          {!hasScript && !p.is_change_request && (
                            <div className="attack-detail-no-script">No automation needed — outcome was positive or requires operational follow-up only.</div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ── Device Posture Tab ────────────────────────────────────────────
// ── Posture Fleet Components ──────────────────────────────────────
function FleetStatusTiles({ summary, filter, onFilterClick }) {
  var s = summary;
  var tiles = [
    { key: 'online', label: 'Online', sublabel: 'Fleet operational', count: s.online || 0, alert: false },
    { key: 'offline', label: 'Powered Off', sublabel: 'Offline devices', count: s.offline || 0, alert: false },
    { key: 'stale', label: 'Stale', sublabel: 'Not seen 7+ days', count: s.stale || 0, alert: true }
  ];
  return (
    <div className="fleet-tiles">
      {tiles.map(function(t) {
        var count = t.count;
        var isActive = filter === t.key;
        var isClickable = count > 0 && onFilterClick;
        var isAlertState = t.alert && count > 0;
        var classes = 'ticket-flow-stage ticket-flow-stage-fleet-' + t.key +
          (count === 0 ? ' ticket-flow-stage-empty' : '') +
          (isActive ? ' ticket-flow-stage-active' : '') +
          (isClickable ? ' ticket-flow-stage-clickable' : '') +
          (isAlertState ? ' ticket-flow-stage-aging' : '');
        return (
          <div key={t.key} className={classes} onClick={isClickable ? function() { onFilterClick(isActive ? null : t.key); } : undefined}>
            <div className="ticket-flow-stage-label">{t.label}</div>
            <div className="ticket-flow-stage-count">{count}</div>
            <div className="ticket-flow-stage-sub">{t.sublabel}</div>
          </div>
        );
      })}
    </div>
  );
}

function FleetProtectionTiles({ summary, onFilterClick }) {
  var s = summary;
  var avPct = s.total > 0 ? Math.round((s.avProtected / s.total) * 100) : 0;
  var avAlertLevel = avPct < 70 ? 'alert' : (avPct < 90 ? '' : 'good');
  var typeParts = [];
  if (s.windows) typeParts.push(s.windows + ' Windows');
  if (s.mac) typeParts.push(s.mac + ' macOS');
  if (s.linux) typeParts.push(s.linux + ' Linux');
  if (s.servers) typeParts.push(s.servers + ' Servers');
  return (
    <div className="metric-tiles">
      <div className={'metric-tile' + (avAlertLevel ? ' metric-tile-' + avAlertLevel : '')}>
        <div className="metric-tile-label">AV Coverage</div>
        <div className="metric-tile-value">{avPct}<span className="metric-tile-unit">%</span></div>
        <div className="metric-tile-context">{s.avProtected} of {s.total} protected</div>
      </div>
      <div
        className={'metric-tile' + (s.avUnprotected > 0 ? ' metric-tile-alert' : ' metric-tile-good') + (s.avUnprotected > 0 && onFilterClick ? ' metric-tile-clickable' : '')}
        onClick={s.avUnprotected > 0 && onFilterClick ? function() { onFilterClick('av-unprotected'); } : undefined}
      >
        <div className="metric-tile-label">Unprotected</div>
        <div className="metric-tile-value">{s.avUnprotected || 0}</div>
        <div className="metric-tile-context">{s.avUnprotected > 0 ? 'Devices without active AV' : 'All devices protected'}</div>
      </div>
      <div
        className={'metric-tile' + (s.defenderDisabled > 0 ? ' metric-tile-alert' : '') + (s.defenderDisabled > 0 && onFilterClick ? ' metric-tile-clickable' : '')}
        onClick={s.defenderDisabled > 0 && onFilterClick ? function() { onFilterClick('defender-off'); } : undefined}
      >
        <div className="metric-tile-label">Defender Disabled</div>
        <div className="metric-tile-value">{s.defenderDisabled || 0}</div>
        <div className="metric-tile-context">{s.defenderDisabled > 0 ? 'Check policy enforcement' : 'Defender active where supported'}</div>
      </div>
      <div className="metric-tile">
        <div className="metric-tile-label">Total Managed</div>
        <div className="metric-tile-value">{s.total || 0}</div>
        <div className="metric-tile-context">{typeParts.length > 0 ? typeParts.join(' · ') : 'Device fleet'}</div>
      </div>
    </div>
  );
}

function PostureTab({ clientId, live }) {
  const [posture, setPosture] = useState(null);
  const [loading, setLoading] = useState(true);
  // Parse entry hint (e.g. from Overview "View All Devices" or Red Team device link).
  // Supported keys:
  //   posture:devices             -> open device list
  //   posture:devices:<name>      -> open device list, seed search with device name
  var entryHint = sessionStorage.getItem('aurelius_tab_entry');
  var initialView = 'summary';
  var initialDevSearch = '';
  var postureArrivalBanner = null;
  if (entryHint && entryHint.indexOf('posture:devices') === 0) {
    initialView = 'devices';
    var hintParts = entryHint.split(':');
    if (hintParts[2]) { initialDevSearch = hintParts[2]; postureArrivalBanner = { name: hintParts[2] }; }
    sessionStorage.removeItem('aurelius_tab_entry');
  }
  const [view, setView] = useState(initialView);
  const [arrivalBanner, setArrivalBanner] = useState(postureArrivalBanner);
  const [filter, setFilter] = useState(null);
  const [licensing, setLicensing] = useState(null);
  var devSort = useTableSort('name', 'asc');
  var licSort = useTableSort('name', 'asc');
  var [devSearch, setDevSearch] = useState(initialDevSearch);
  var [devPage, setDevPage] = useState(0);
  var [devFilters, setDevFilters] = useState({});

  useEffect(function() {
    setLoading(true);
    setPosture(null);
    setLicensing(null);
    authFetch(API + '/clients/' + clientId + '/posture')
      .then(function(r) { return r.json(); })
      .then(function(data) { setPosture(data); setLoading(false); })
      .catch(function() { setLoading(false); });
    authFetch(API + '/clients/' + clientId + '/licensing')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(data) { setLicensing(data); })
      .catch(function() {});
  }, [clientId]);

  if (loading) return <div className="tab-content"><SkeletonLoader /></div>;
  if (!posture || !posture.summary) return <div className="tab-content"><IntegrationEmptyState kind="endpoints" clientId={clientId} /></div>;

  var s = posture.summary;
  var avPct = s.total > 0 ? Math.round((s.avProtected / s.total) * 100) : 0;
  var onlinePct = s.total > 0 ? Math.round((s.online / s.total) * 100) : 0;
  var stalePct = s.total > 0 ? Math.round(((s.stale || 0) / s.total) * 100) : 0;
  var managedPct = 100 - stalePct;
  var filterLabels = {
    'online': 'Online Devices', 'offline': 'Offline Devices',
    'av-protected': 'AV Protected', 'av-unprotected': 'AV Unprotected', 'defender-off': 'Defender Disabled',
  };

  var filteredDevices = posture.devices;
  if (filter === 'online') filteredDevices = posture.devices.filter(function(d) { return !d.offline; });
  else if (filter === 'offline') filteredDevices = posture.devices.filter(function(d) { return d.offline; });
  else if (filter === 'av-protected') filteredDevices = posture.devices.filter(function(d) { return d.av && d.av.state === 'ON'; });
  else if (filter === 'av-unprotected') filteredDevices = posture.devices.filter(function(d) { return !d.av || d.av.state !== 'ON'; });
  else if (filter === 'defender-off') filteredDevices = posture.devices.filter(function(d) { return d.defenderDisabled; });

  return (
    <div className="tab-content">
      {arrivalBanner && (
        <div className="arrival-banner">
          <span className="arrival-banner-badge">FROM RED TEAM</span>
          <span className="arrival-banner-text">You're investigating device <strong>{arrivalBanner.name}</strong> — involved in a Red Team simulation. Filtered below.</span>
          <button className="arrival-banner-close" onClick={function() { setArrivalBanner(null); }}>&#10005;</button>
        </div>
      )}
      {view === 'summary' ? (
        <>
          <TabDescription text="Real-time device health from NinjaOne RMM. Shows antivirus coverage, defender status, and device reachability across your managed fleet." />
          <DataFreshness live={live.ninjaone} />
          <ContentBand title="Device Fleet Status" subtitle={s.total + ' managed devices — click any tile to drill in'} rightContent={<>{live.ninjaone && <span className="live-badge">LIVE</span>}<button className="tab-drill-link" onClick={function() { setFilter(null); setView('devices'); }}>View All Devices ›</button></>}>
            <FleetStatusTiles summary={s} filter={null} onFilterClick={function(key) { if (key) { setFilter(key); setView('devices'); } }} />
            <FleetProtectionTiles summary={s} onFilterClick={function(key) { if (key) { setFilter(key); setView('devices'); } }} />
          </ContentBand>
          <CollapsibleSection title="Device Types" defaultOpen={true}>
            <div className="stats-grid-compact">
              <StatCardCompact title="Windows" value={s.windows} />
              <StatCardCompact title="macOS" value={s.mac} />
              <StatCardCompact title="Linux" value={s.linux} />
              <StatCardCompact title="Servers" value={s.servers} />
            </div>
          </CollapsibleSection>
          <CollapsibleSection title="Antivirus Products" defaultOpen={true}>
            <div className="stats-grid-compact">
              {Object.entries(s.avProducts).map(function([name, count]) {
                return <StatCardCompact key={name} title={name} value={count} />;
              })}
            </div>
            {Object.keys(s.avProducts).length === 0 && <div className="empty-state">No antivirus data reported.</div>}
          </CollapsibleSection>
          {licensing && licensing.enabled && (
            <CollapsibleSection title="Microsoft Licensing" defaultOpen={true} rightContent={<span className="live-badge">LIVE</span>}>
              <div className="stats-grid-compact">
                <StatCardCompact title="License SKUs" value={licensing.summary.total_skus} live={true} />
                <StatCardCompact title="Purchased" value={licensing.summary.total_purchased} live={true} />
                <StatCardCompact title="Assigned" value={licensing.summary.total_assigned} accent="#34d399" />
                <StatCardCompact title="Available" value={licensing.summary.total_available} accent={licensing.summary.total_available > 5 ? '#f5a623' : '#34d399'} />
                <StatCardCompact title="Utilization" value={licensing.summary.utilization_pct + '%'} accent={licensing.summary.utilization_pct >= 80 ? '#34d399' : licensing.summary.utilization_pct >= 60 ? '#f5a623' : '#f06565'} />
                <StatCardCompact title="Est. Monthly Cost" value={'$' + licensing.summary.estimated_monthly_cost.toLocaleString()} accent="#5b9ef4" />
              </div>
              <div className="posture-table-wrap" style={{ marginTop: 14 }}>
                <table className="posture-table">
                  <thead><tr><SortTh field="name" current={licSort.sortField} dir={licSort.sortDir} onSort={licSort.toggleSort}>License</SortTh><SortTh field="purchased" current={licSort.sortField} dir={licSort.sortDir} onSort={licSort.toggleSort}>Purchased</SortTh><SortTh field="assigned" current={licSort.sortField} dir={licSort.sortDir} onSort={licSort.toggleSort}>Assigned</SortTh><SortTh field="available" current={licSort.sortField} dir={licSort.sortDir} onSort={licSort.toggleSort}>Available</SortTh><SortTh field="cost" current={licSort.sortField} dir={licSort.sortDir} onSort={licSort.toggleSort}>Cost / License</SortTh><SortTh field="total" current={licSort.sortField} dir={licSort.sortDir} onSort={licSort.toggleSort}>Monthly Total</SortTh><SortTh field="util" current={licSort.sortField} dir={licSort.sortDir} onSort={licSort.toggleSort}>Utilization</SortTh></tr></thead>
                  <tbody>
                    {sortRows(licensing.licenses.filter(function(l) { return !l.isFree; }), licSort.sortField, licSort.sortDir, { name: function(l) { return l.name; }, purchased: function(l) { return l.purchased; }, assigned: function(l) { return l.assigned; }, available: function(l) { return l.purchased - l.assigned; }, cost: function(l) { return l.costPerLicense || 0; }, total: function(l) { return l.monthlyTotal || 0; }, util: function(l) { return l.purchased > 0 ? l.assigned / l.purchased : 0; } }).map(function(l) {
                      var utilPct = l.purchased > 0 ? Math.round((l.assigned / l.purchased) * 100) : 0;
                      var utilColor = utilPct >= 80 ? '#34d399' : utilPct >= 50 ? '#f5a623' : '#f06565';
                      return (
                        <tr key={l.skuPartNumber}>
                          <td className="posture-device-name">{l.name}</td>
                          <td>{l.purchased}</td><td>{l.assigned}</td>
                          <td style={{ color: l.available > 0 ? '#f5a623' : '#34d399' }}>{l.available}</td>
                          <td style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 12 }}>{l.costPerLicense > 0 ? '$' + l.costPerLicense.toFixed(2) : '—'}</td>
                          <td style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 12 }}>{l.monthlyCost > 0 ? '$' + l.monthlyCost.toLocaleString() : '—'}</td>
                          <td><span style={{ color: utilColor, fontWeight: 600 }}>{utilPct}%</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CollapsibleSection>
          )}
        </>
      ) : (function() {
        var searchFields = [function(d) { return d.name; }, function(d) { return d.lastUser; }];
        var devAccessors = { name: function(d) { return d.name; }, user: function(d) { return d.lastUser || ''; }, type: function(d) { return d.nodeClass || ''; }, status: function(d) { return d.offline ? 'Offline' : 'Online'; }, av: function(d) { return d.av ? d.av.product : ''; }, lastContact: function(d) { return d.lastContact || 0; } };
        // Device type groups (for filter options)
        var typeGroups = {};
        filteredDevices.forEach(function(d) {
          var t = (d.nodeClass || '').indexOf('LAPTOP') >= 0 ? 'Laptop' : (d.nodeClass || '').indexOf('MAC') >= 0 ? 'Mac' : (d.nodeClass || '').indexOf('SERVER') >= 0 ? 'Server' : 'Workstation';
          typeGroups[t] = (typeGroups[t] || 0) + 1;
        });
        var filterAccessors = {
          status: function(d) { return d.offline ? 'Offline' : 'Online'; },
          type: function(d) { var nc = d.nodeClass || ''; return nc.indexOf('LAPTOP') >= 0 ? 'Laptop' : nc.indexOf('MAC') >= 0 ? 'Mac' : nc.indexOf('SERVER') >= 0 ? 'Server' : 'Workstation'; },
          avstate: function(d) { return d.av && d.av.state === 'ON' ? 'Protected' : d.av && d.av.state === 'OFF' ? 'Disabled' : 'Missing'; }
        };
        var searchedDevs = filterRows(filteredDevices, devSearch, searchFields);
        var tableFilteredDevs = applyFilters(searchedDevs, devFilters, filterAccessors);
        var sortedDevs = sortRows(tableFilteredDevs, devSort.sortField, devSort.sortDir, devAccessors);
        var pageSize = 25;
        var totalPages = Math.max(1, Math.ceil(sortedDevs.length / pageSize));
        var safePage = Math.min(devPage, totalPages - 1);
        var pagedDevs = sortedDevs.slice(safePage * pageSize, (safePage + 1) * pageSize);
        return (
        <>
          <div className="section-title">
            {filter ? filterLabels[filter] : 'All Devices'} ({filteredDevices.length}{filter ? ' of ' + posture.devices.length : ''})
            {filter && <button className="insights-btn" style={{ marginLeft: 8, verticalAlign: 'middle' }} onClick={function() { setFilter(null); }}>Clear Filter</button>}
            <button className="insights-btn" style={{ marginLeft: 8, verticalAlign: 'middle' }} onClick={function() { setView('summary'); setFilter(null); }}>Back to Summary</button>
          </div>
          <TableSearch value={devSearch} onChange={function(v) { setDevSearch(v); setDevPage(0); }} count={sortedDevs.length} total={filteredDevices.length} noun="devices" onExport={function() { exportTableCSV(['Device','User','Type','Status','Antivirus','AV State','Last Contact'], sortedDevs.map(function(d) { return [d.name, d.lastUser || '', (d.nodeClass || '').replace('WINDOWS_','').replace('_',' '), d.offline ? 'Offline' : 'Online', d.av ? d.av.product : 'None', d.av ? d.av.state : '', d.lastContact ? new Date(d.lastContact * 1000).toLocaleDateString() : '']; }), 'posture-devices'); }} />
          <TableFilters filters={[
            { key: 'status', label: 'Status', options: [
              { value: 'Online', label: 'Online', color: '#22c55e', count: filteredDevices.filter(function(d) { return !d.offline; }).length },
              { value: 'Offline', label: 'Offline', color: '#64748b', count: filteredDevices.filter(function(d) { return d.offline; }).length }
            ]},
            { key: 'type', label: 'Type', options: Object.entries(typeGroups).sort(function(a, b) { return b[1] - a[1]; }).map(function(pair) { return { value: pair[0], label: pair[0], count: pair[1] }; }) },
            { key: 'avstate', label: 'AV', options: [
              { value: 'Protected', label: 'Protected', color: '#22c55e', count: filteredDevices.filter(function(d) { return d.av && d.av.state === 'ON'; }).length },
              { value: 'Disabled', label: 'Disabled', color: '#f59e0b', count: filteredDevices.filter(function(d) { return d.av && d.av.state === 'OFF'; }).length },
              { value: 'Missing', label: 'Missing', color: '#ef4444', count: filteredDevices.filter(function(d) { return !d.av; }).length }
            ]}
          ]} active={devFilters} onChange={function(f) { setDevFilters(f); setDevPage(0); }} />
          <div className="posture-table-wrap">
            <table className="posture-table">
              <thead><tr><SortTh field="name" current={devSort.sortField} dir={devSort.sortDir} onSort={devSort.toggleSort}>Device</SortTh><SortTh field="user" current={devSort.sortField} dir={devSort.sortDir} onSort={devSort.toggleSort}>User</SortTh><SortTh field="type" current={devSort.sortField} dir={devSort.sortDir} onSort={devSort.toggleSort}>Type</SortTh><SortTh field="status" current={devSort.sortField} dir={devSort.sortDir} onSort={devSort.toggleSort}>Status</SortTh><SortTh field="av" current={devSort.sortField} dir={devSort.sortDir} onSort={devSort.toggleSort}>Antivirus</SortTh><th>AV State</th><SortTh field="lastContact" current={devSort.sortField} dir={devSort.sortDir} onSort={devSort.toggleSort}>Last Contact</SortTh></tr></thead>
              <tbody>
                {pagedDevs.length === 0 && <tr><td colSpan="7" className="table-no-results">No devices match your search</td></tr>}
                {pagedDevs.map(function(d) {
                  var lastContact = d.lastContact ? new Date(d.lastContact * 1000).toLocaleDateString() : '—';
                  return (
                    <tr key={d.id} className={d.offline ? 'posture-row-offline' : ''}>
                      <td className="posture-device-name" title={d.name}><span className={'status-dot ' + (d.offline ? 'status-dot-muted' : 'status-dot-good')} />{d.name}</td>
                      <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{d.lastUser || '—'}</td>
                      <td>{(d.nodeClass || '').replace('WINDOWS_', '').replace('_', ' ')}</td>
                      <td><span className={'posture-status ' + (d.offline ? 'posture-offline' : 'posture-online')}>{d.offline ? 'Offline' : 'Online'}</span></td>
                      <td>{(d.av ? d.av.product : 'None')}{d.defenderDisabled && <span className="posture-warning"> (Defender OFF)</span>}</td>
                      <td><span className={'posture-av-state posture-av-' + ((d.av ? d.av.state : '—') === 'ON' ? 'on' : (d.av ? d.av.state : '—') === 'OFF' ? 'off' : 'unknown')}>{d.av ? d.av.state : '—'}</span></td>
                      <td className="posture-date">{lastContact}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination page={safePage} totalPages={totalPages} onPageChange={setDevPage} totalItems={sortedDevs.length} startIndex={safePage * pageSize} endIndex={Math.min((safePage + 1) * pageSize, sortedDevs.length)} />
        </>
        );
      })()}
    </div>
  );
}
// ── Security Tab (Enriched Command Center) ────────────────────────
function SecurityTab({ clientId, c, live, onNavigate }) {
  const [siemData, setSiemData] = useState(null);
  const [postureData, setPostureData] = useState(null);
  const [identityData, setIdentityData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(function() {
    setLoading(true);
    setSiemData(null);
    setPostureData(null);
    setIdentityData(null);
    Promise.all([
      authFetch(API + '/clients/' + clientId + '/siem').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
      authFetch(API + '/clients/' + clientId + '/posture').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
      authFetch(API + '/clients/' + clientId + '/entra').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
    ]).then(function(results) {
      setSiemData(results[0]);
      setPostureData(results[1]);
      setIdentityData(results[2]);
      setLoading(false);
    });
  }, [clientId]);

  var siemSummary = siemData && siemData.enabled ? siemData.summary : null;
  var postureSummary = postureData && postureData.summary ? postureData.summary : null;
  var identitySummary = identityData && identityData.summary ? identityData.summary : null;

  var avPct = postureSummary && postureSummary.total > 0 ? Math.round((postureSummary.avProtected / postureSummary.total) * 100) : null;
  var compliantPct = identitySummary && identitySummary.devices.total > 0 ? Math.round((identitySummary.devices.compliant / identitySummary.devices.total) * 100) : null;
  var encryptedPct = identitySummary && identitySummary.intune.total > 0 ? Math.round((identitySummary.intune.encrypted / identitySummary.intune.total) * 100) : null;
  var edrCoverage = c.huntress_agents && c.endpoints.total > 0 ? Math.round((c.huntress_agents.total / c.endpoints.total) * 100) : null;

  function timeAgo(dateStr) {
    if (!dateStr) return '';
    var diff = Date.now() - new Date(dateStr).getTime();
    var hours = Math.floor(diff / 3600000);
    if (hours < 24) return hours + 'h ago';
    return Math.floor(hours / 24) + 'd ago';
  }

  var priorityColors = { 1: '#ef4444', 2: '#f59e0b', 3: '#60a5fa' };
  var priorityLabels = { 1: 'P1', 2: 'P2', 3: 'P3' };

  // Determine which security domains have ACTUAL data feeding them. A domain
  // without a configured integration shouldn't drag the verdict to "Critical"
  // — it's just an absence of data, not evidence of a problem. We score
  // ONLY the configured domains and flag the rest as informational context.
  var securityDomains = [
    { key: 'endpoint',   label: 'Endpoint health',   integration: 'NinjaOne', configured: !!(postureData && postureData.summary && postureData.summary.total > 0), score: c.scores.endpoint },
    { key: 'threat',     label: 'Threat detection',  integration: 'Huntress', configured: !!(c.huntress_agents && c.huntress_agents.total > 0),                  score: c.scores.threat },
    { key: 'siem',       label: 'SIEM & ITDR',       integration: 'Huntress', configured: !!(siemData && siemData.ingesting),                                     score: c.scores.siem },
    { key: 'credential', label: 'Identity controls', integration: 'Entra',    configured: !!(identityData && identityData.summary),                                score: c.scores.credential || null }
  ];
  var configuredDomains = securityDomains.filter(function(d) { return d.configured; });
  var unconfiguredDomains = securityDomains.filter(function(d) { return !d.configured; });
  var minScore = configuredDomains.length > 0 ? Math.min.apply(null, configuredDomains.map(function(d) { return d.score || 0; })) : null;
  var verdictContext;
  if (configuredDomains.length === 0) {
    verdictContext = 'No security integrations are wired for this client yet. Configure NinjaOne, Huntress, Entra, or Blumira to populate this view.';
  } else if (minScore >= 90) {
    verdictContext = 'All ' + configuredDomains.length + ' configured security domain' + (configuredDomains.length === 1 ? ' is' : 's are') + ' performing well.' +
      (unconfiguredDomains.length > 0 ? ' (' + unconfiguredDomains.map(function(d) { return d.integration; }).join(', ') + ' not yet configured.)' : '');
  } else if (minScore >= 75) {
    verdictContext = 'Most security areas are healthy. Focus on the lowest-scoring domain for improvement priority.' +
      (unconfiguredDomains.length > 0 ? ' (' + unconfiguredDomains.map(function(d) { return d.integration; }).join(', ') + ' not yet configured.)' : '');
  } else if (minScore >= 50) {
    var weakest = configuredDomains.reduce(function(w, d) { return (d.score || 0) < (w.score || 0) ? d : w; });
    verdictContext = weakest.label + ' needs attention (' + (weakest.score || 0) + '%). Review the score breakdown.';
  } else {
    var weakest2 = configuredDomains.reduce(function(w, d) { return (d.score || 0) < (w.score || 0) ? d : w; });
    verdictContext = 'Critical gap in ' + weakest2.label + ' (' + (weakest2.score || 0) + '%). Immediate review recommended.';
  }

  return (
    <div className="tab-content">
      <TabDescription text="Unified security posture combining endpoint health, threat detection (Huntress EDR), SIEM findings (Blumira), and identity access controls (Microsoft Entra)." />
      <DataFreshness live={true} />
      <HealthVerdict score={minScore} context={verdictContext} />
      {/* Security Score Rings — pass null for unconfigured domains so they
          render a neutral 'Not configured' state instead of a red 0% ring. */}
      <div className="scores-row">
        <ScoreRing score={securityDomains[0].configured ? securityDomains[0].score : null} label="Endpoint" />
        <ScoreRing score={securityDomains[1].configured ? securityDomains[1].score : null} label="Threat" />
        <ScoreRing score={securityDomains[2].configured ? securityDomains[2].score : null} label="SIEM" />
        <ScoreRing score={securityDomains[3].configured ? securityDomains[3].score : null} label="Credential" />
      </div>

      {/* Endpoint Fleet — Segment Bar + Quick Stats */}
      <ContentBand title="Endpoint Fleet" subtitle={c.endpoints.total + ' managed devices'} rightContent={<>{live.ninjaone && <span className="live-badge">LIVE</span>}<button className="tab-drill-link" onClick={function() { sessionStorage.setItem('aurelius_tab_entry', 'posture:devices'); onNavigate('posture'); }}>View All Devices ›</button></>}>
        <SegmentBar segments={[
          { label: 'Online', value: c.endpoints.online || 0, color: '#22c55e' },
          { label: 'Off-Hours', value: c.endpoints.offHours || 0, color: '#64748b' },
          { label: 'Needs Attention', value: c.endpoints.stale || c.endpoints.critical || 0, color: '#ef4444', onClick: (c.endpoints.stale || c.endpoints.critical || 0) > 0 ? function() { onNavigate('posture'); } : undefined },
        ]} />
        <QuickStats items={[
          avPct !== null ? { value: avPct + '%', label: 'AV Coverage', color: avPct >= 90 ? '#22c55e' : avPct >= 70 ? '#f59e0b' : '#ef4444' } : null,
          postureSummary && postureSummary.defenderDisabled > 0 ? { value: postureSummary.defenderDisabled, label: 'Defender Off', color: '#f59e0b' } : null,
          edrCoverage !== null ? { value: edrCoverage + '%', label: 'EDR Coverage', color: edrCoverage >= 90 ? '#22c55e' : '#f59e0b' } : null,
        ].filter(Boolean)} />
      </ContentBand>

      {/* Threat Detection — Metric List */}
      <ContentBand title="Threat Detection" subtitle="Huntress EDR activity" rightContent={live.huntress ? <span className="live-badge">LIVE</span> : null}>
        <MetricList>
          <MetricListItem icon="⊘" iconColor="#22c55e" label="Threats Mitigated" sublabel="Resolved by Huntress SOC" value={c.threats.mitigated} valueColor="#22c55e" />
          <MetricListItem icon="◎" iconColor={c.threats.investigating > 0 ? '#f59e0b' : '#22c55e'} label="Under Investigation" sublabel="Active SOC review" value={c.threats.investigating} valueColor={c.threats.investigating > 0 ? '#f59e0b' : 'var(--text-primary)'} />
          {c.threats.active > 0 && <MetricListItem icon="⚠" iconColor="#ef4444" label="Active Threats" sublabel="Requires immediate attention" value={c.threats.active} valueColor="#ef4444" onClick={function() { onNavigate('posture'); }} />}
          {c.huntress_agents && <MetricListItem icon="◈" iconColor="#a78bfa" label="EDR Agents Deployed" sublabel={c.huntress_agents.online + ' active in last 24h'} value={c.huntress_agents.total} />}
        </MetricList>
      </ContentBand>

      {/* SIEM Highlights — Segment Bar + Metric List */}
      <ContentBand title="SIEM & ITDR — Huntress" subtitle={siemData && siemData.enabled ? (siemData.ingesting ? 'Managed SIEM ingesting' : 'Managed SIEM — onboarding') : null} rightContent={<>{siemData && siemData.enabled && <span className="live-badge">LIVE</span>}{siemData && siemData.enabled && <button className="tab-drill-link" onClick={function() { onNavigate('siem'); }}>View Detections ›</button>}</>}>
        {siemData && siemData.enabled ? (
          <>
            {!siemData.ingesting && (
              <div style={{ padding: '2px 0 12px', fontSize: 12.5, color: 'var(--text-secondary)' }}>
                Managed SIEM onboarding — {(siemData.coverage && siemData.coverage.log_sources) || 0} log source(s) connected. ITDR identity + EDR endpoint detections are already active below.
              </div>
            )}
            <SegmentBar segments={[
              { label: 'High/Critical', value: (siemData.summary && siemData.summary.high_open) || 0, color: '#ef4444' },
              { label: 'Other open', value: Math.max(0, ((siemData.summary && siemData.summary.total_open) || 0) - ((siemData.summary && siemData.summary.high_open) || 0)), color: '#f59e0b' },
              { label: 'Identities · ITDR', value: (siemData.coverage && siemData.coverage.identity_count) || 0, color: '#60a5fa' },
              { label: 'EDR agents', value: (siemData.coverage && siemData.coverage.agents) || 0, color: '#a78bfa' },
            ].filter(function(s) { return s.value > 0; })} />
            {siemData.detections && siemData.detections.length > 0 && (
              <div className="security-mini-table" style={{ marginTop: 12 }}>
                <div className="mini-table-header">Recent Detections</div>
                {siemData.detections.slice(0, 5).map(function(d, idx) {
                  var sc = (function(v) { v = (v || '').toLowerCase(); return v === 'critical' ? '#ef4444' : v === 'high' ? '#f59e0b' : v === 'medium' ? '#a78bfa' : '#60a5fa'; })(d.severity);
                  return (
                    <div key={d.id || idx} className="mini-table-row">
                      <span className="mini-badge" style={{ background: sc + '20', color: sc }}>{d.severity || '—'}</span>
                      <span className="mini-table-name">{d.subject}</span>
                      <span className="mini-table-time">{timeAgo(d.sent_at)}</span>
                    </div>
                  );
                })}
              </div>
            )}
            {((siemData.summary && siemData.summary.total_open) || 0) === 0 && <div style={{ padding: '12px 0', fontSize: 13, color: '#22c55e', fontWeight: 600 }}>✓ No open detections</div>}
          </>
        ) : (
          <div className="empty-state" style={{ padding: '24px 16px' }}>
            {loading ? 'Loading SIEM data...' : 'Huntress is not configured for this client.'}
          </div>
        )}
      </ContentBand>

      {/* Identity & Access — Metric List */}
      <ContentBand title="Identity & Access — Microsoft Entra" rightContent={<>{identityData && identityData.summary && <span className="live-badge">LIVE</span>}{identityData && identityData.summary && <button className="tab-drill-link" onClick={function() { sessionStorage.setItem('aurelius_tab_entry', 'identity:devices'); onNavigate('identity'); }}>View Details ›</button>}</>}>
        {identitySummary ? (
          <MetricList>
            <MetricListItem icon="◉" iconColor="#22c55e" label="Directory Users" sublabel={identitySummary.users.enabled + ' enabled · ' + (identitySummary.users.guests || 0) + ' guests'} value={identitySummary.users.total} />
            <MetricListItem icon="◎" iconColor={compliantPct !== null && compliantPct >= 80 ? '#22c55e' : '#f59e0b'} label="Device Compliance" sublabel={identitySummary.devices.total + ' enrolled devices'} value={<MicroBar value={compliantPct || 0} max={100} color={compliantPct !== null ? (compliantPct >= 80 ? '#22c55e' : compliantPct >= 50 ? '#f59e0b' : '#ef4444') : '#64748b'} label={compliantPct !== null ? compliantPct + '%' : '—'} />} onClick={function() { sessionStorage.setItem('aurelius_tab_entry', 'identity:devices'); onNavigate('identity'); }} />
            <MetricListItem icon="◈" iconColor={encryptedPct !== null && encryptedPct >= 90 ? '#22c55e' : '#f59e0b'} label="Disk Encryption" sublabel="BitLocker / FileVault coverage" value={<MicroBar value={encryptedPct || 0} max={100} color={encryptedPct !== null ? (encryptedPct >= 90 ? '#22c55e' : encryptedPct >= 70 ? '#f59e0b' : '#ef4444') : '#64748b'} label={encryptedPct !== null ? encryptedPct + '%' : '—'} />} />
            {identitySummary.intune.nonCompliant > 0 && <MetricListItem icon="⚠" iconColor="#ef4444" label="Non-Compliant Devices" sublabel="Require policy remediation" value={identitySummary.intune.nonCompliant} valueColor="#ef4444" onClick={function() { sessionStorage.setItem('aurelius_tab_entry', 'identity:intune'); onNavigate('identity'); }} />}
          </MetricList>
        ) : (
          <div className="empty-state" style={{ padding: '24px 16px' }}>
            {loading ? 'Loading identity data...' : 'Microsoft Entra is not configured for this client.'}
          </div>
        )}
      </ContentBand>

    </div>
  );
}

// ── Keeper Credential Vault Section ───────────────────────────────
function KeeperVaultSection({ clientId }) {
  var [keeper, setKeeper] = useState(null);
  var [loading, setLoading] = useState(true);

  useEffect(function() {
    setLoading(true); setKeeper(null);
    authFetch(API + '/clients/' + clientId + '/keeper')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(data) { setKeeper(data); setLoading(false); })
      .catch(function() { setLoading(false); });
  }, [clientId]);

  if (loading) {
    return (
      <ContentBand title="Credential Management — Keeper">
        <div className="empty-state" style={{ padding: '24px 16px' }}>Loading vault data…</div>
      </ContentBand>
    );
  }

  if (!keeper || !keeper.enabled) {
    return (
      <ContentBand title="Credential Management — Keeper" rightContent={<span className="coming-soon">Coming Soon</span>}>
        <div className="empty-state" style={{ padding: '24px 16px' }}>Keeper integration pending MSP API access. Managed companies are configured.</div>
      </ContentBand>
    );
  }

  var s = keeper.summary;
  var activityIcons = { share: '⤴', rotate: '↻', policy: '◆', create: '+', access: '◉', invite: '◐', audit: '✓' };
  var activityColors = { share: '#60a5fa', rotate: '#22c55e', policy: '#a78bfa', create: '#22c55e', access: '#64748b', invite: '#f59e0b', audit: '#22c55e' };

  return (
    <>
      <ContentBand title="Credential Management — Keeper" subtitle={s.total_records + ' credentials under management · last sync ' + Math.round((Date.now() - new Date(s.last_sync).getTime()) / 60000) + 'm ago'} rightContent={<span className="live-badge">LIVE</span>}>
        <div className="metric-tiles">
          <div className={'metric-tile ' + (s.enrollment_pct === 100 ? 'metric-tile-good' : '')}>
            <div className="metric-tile-label">Vault Enrollment</div>
            <div className="metric-tile-value">{s.enrollment_pct}<span className="metric-tile-unit">%</span></div>
            <div className="metric-tile-context">{s.enrolled_users} users enrolled</div>
          </div>
          <div className={'metric-tile ' + (s.mfa_adoption_pct >= 90 ? 'metric-tile-good' : s.mfa_adoption_pct < 70 ? 'metric-tile-alert' : '')}>
            <div className="metric-tile-label">MFA Adoption</div>
            <div className="metric-tile-value">{s.mfa_adoption_pct}<span className="metric-tile-unit">%</span></div>
            <div className="metric-tile-context">Enforced on all privileged access</div>
          </div>
          <div className={'metric-tile' + (s.weak_passwords > 0 || s.reused_passwords > 0 ? ' metric-tile-alert' : ' metric-tile-good')}>
            <div className="metric-tile-label">Password Health</div>
            <div className="metric-tile-value">{s.avg_password_strength}<span className="metric-tile-unit">/100</span></div>
            <div className="metric-tile-context">{s.weak_passwords} weak · {s.reused_passwords} reused · {s.breached_passwords} breached</div>
          </div>
          <div className="metric-tile">
            <div className="metric-tile-label">Shared Folders</div>
            <div className="metric-tile-value">{s.shared_folders}</div>
            <div className="metric-tile-context">Team credential sharing</div>
          </div>
        </div>
      </ContentBand>

      {keeper.security_insights && keeper.security_insights.length > 0 && (
        <ContentBand title="Vault Security Insights" subtitle="Continuous analysis of credential hygiene">
          <div className="insights-list">
            {keeper.security_insights.map(function(ins, i) {
              if (ins.level === 'good') return (
                <div key={i} className="insight-clear">
                  <div className="insight-clear-icon">&#10003;</div>
                  <div>
                    <div className="insight-clear-label">All Clear</div>
                    <div className="insight-clear-text">{ins.text}</div>
                  </div>
                </div>
              );
              return (
                <div key={i} className={'insight-card insight-card-' + ins.level}>
                  <div className={'insight-level-badge insight-level-' + ins.level}>{insightLevelLabels[ins.level] || ins.level}</div>
                  <div className="insight-card-text">{ins.text}</div>
                </div>
              );
            })}
          </div>
        </ContentBand>
      )}

      <ContentBand title="Shared Folders" subtitle="Team credential repositories with least-privilege access">
        <div className="keeper-folders-grid">
          {keeper.shared_folders.map(function(f, i) {
            var ageHours = Math.round((Date.now() - new Date(f.last_modified).getTime()) / 3600000);
            var ageLabel = ageHours < 1 ? 'just now' : ageHours < 24 ? ageHours + 'h ago' : Math.round(ageHours / 24) + 'd ago';
            return (
              <div key={i} className="keeper-folder">
                <div className="keeper-folder-icon">◈</div>
                <div className="keeper-folder-body">
                  <div className="keeper-folder-name">{f.name}</div>
                  <div className="keeper-folder-meta">
                    <span>{f.member_count} members</span>
                    <span>{f.record_count} records</span>
                    <span className="keeper-folder-time">{ageLabel}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </ContentBand>

      <ContentBand title="Enforcement Policies" subtitle="Active organization-wide security policies">
        <MetricList>
          {keeper.policies.map(function(p, i) {
            return <MetricListItem key={i} icon="◆" iconColor={p.status === 'enforced' ? '#22c55e' : '#f59e0b'} label={p.name} sublabel={p.value} badge={p.status.toUpperCase()} badgeColor={p.status === 'enforced' ? '#22c55e' : '#f59e0b'} />;
          })}
        </MetricList>
      </ContentBand>

      <ContentBand title="Recent Activity" subtitle="Vault access and modification events">
        <div className="keeper-activity">
          {keeper.recent_activity.map(function(a, i) {
            var ageMin = Math.round((Date.now() - new Date(a.time).getTime()) / 60000);
            var ageLabel = ageMin < 60 ? ageMin + 'm ago' : ageMin < 1440 ? Math.round(ageMin / 60) + 'h ago' : Math.round(ageMin / 1440) + 'd ago';
            return (
              <div key={i} className="keeper-activity-item">
                <div className="keeper-activity-icon" style={{ background: activityColors[a.type] + '20', color: activityColors[a.type] }}>{activityIcons[a.type] || '●'}</div>
                <div className="keeper-activity-body">
                  <div className="keeper-activity-main"><strong>{a.user}</strong> {a.action.toLowerCase()} <span className="keeper-activity-target">{a.target}</span></div>
                  <div className="keeper-activity-time">{ageLabel}</div>
                </div>
              </div>
            );
          })}
        </div>
      </ContentBand>
    </>
  );
}

// ── Network Tab (UniFi) ───────────────────────────────────────────
// Pill-style segmented control for vendor switching on the Network tab.
// Only renders when a client has BOTH UniFi + Meraki configured. Single-
// vendor clients see no extra UI noise — the tab is identical to before.
function NetworkVendorSwitcher({ value, onChange, vendors, counts }) {
  var options = [{ key: 'all', label: 'All', count: counts.all }];
  if (vendors.includes('unifi'))  options.push({ key: 'unifi',  label: 'UniFi',  count: counts.unifi });
  if (vendors.includes('meraki')) options.push({ key: 'meraki', label: 'Meraki', count: counts.meraki });
  return (
    <div className="net-vendor-switcher" role="tablist" aria-label="Network vendor filter">
      {options.map(function(o) {
        var active = value === o.key;
        return (
          <button
            key={o.key}
            type="button"
            role="tab"
            aria-selected={active}
            className={'net-vendor-btn' + (active ? ' active' : '') + ' net-vendor-btn-' + o.key}
            onClick={function() { onChange(o.key); }}
          >
            {o.key !== 'all' && <span className={'net-vendor-dot net-vendor-dot-' + o.key} />}
            <span>{o.label}</span>
            <span className="net-vendor-count">{o.count}</span>
          </button>
        );
      })}
    </div>
  );
}

function NetworkTab({ clientId }) {
  const [netData, setNetData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('summary');
  var netSort = useTableSort('name', 'asc');
  var [netSearch, setNetSearch] = useState('');
  const [filter, setFilter] = useState(null);
  var [netFilters, setNetFilters] = useState({});
  // Vendor filter — only meaningful when client has both UniFi + Meraki.
  // 'all' = combined view; 'unifi' / 'meraki' filter to that vendor's
  // devices + recompute the KPI summary client-side.
  var [vendorFilter, setVendorFilter] = useState('all');

  useEffect(function() {
    setLoading(true); setNetData(null); setView('summary'); setFilter(null); setVendorFilter('all');
    authFetch(API + '/clients/' + clientId + '/network')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(data) { setNetData(data); setLoading(false); })
      .catch(function() { setLoading(false); });
  }, [clientId]);

  if (loading) return <div className="tab-content"><SkeletonLoader /></div>;
  if (!netData || !netData.enabled) return <div className="tab-content"><IntegrationEmptyState kind="network" clientId={clientId} /></div>;

  var configuredVendors = netData.configuredVendors || (netData.vendors || []);
  var multiVendor = configuredVendors.length > 1;

  // Apply vendor filter: when 'all' use everything; otherwise filter to that vendor's devices and recompute summary client-side
  var rawDevices = netData.devices || [];
  var rawSites = netData.sites || [];
  var devices = vendorFilter === 'all' ? rawDevices : rawDevices.filter(function(d) { return d.vendor === vendorFilter; });
  var sites   = vendorFilter === 'all' ? rawSites   : rawSites.filter(function(site) { return site.vendor === vendorFilter; });

  // When filtering to a vendor, recompute summary from the filtered slice; otherwise use server-computed
  var s;
  if (vendorFilter === 'all') {
    s = netData.summary;
  } else {
    var fwCurrent = devices.filter(function(d) { return d.firmwareStatus === 'upToDate'; }).length;
    var fwOutdated = devices.filter(function(d) { return d.firmwareStatus && d.firmwareStatus !== 'upToDate'; }).length;
    s = {
      total: devices.length,
      online: devices.filter(function(d) { return d.status === 'online'; }).length,
      offline: devices.filter(function(d) { return d.status !== 'online'; }).length,
      sites: sites.length,
      gateways: devices.filter(function(d) { return d.vendor === 'meraki' ? d.bucket === 'gateway' : (d.productLine === 'network' && d.isConsole); }).length,
      access_points: devices.filter(function(d) { return d.vendor === 'meraki' ? d.bucket === 'access_point' : (d.productLine === 'network' && !d.isConsole && (d.shortname || '').match(/^(U6|U7|UAP|G7)/i)); }).length,
      switches: devices.filter(function(d) { return d.vendor === 'meraki' ? d.bucket === 'switch' : (d.productLine === 'network' && (d.shortname || '').match(/^(US|USL|USW)/i)); }).length,
      cameras: devices.filter(function(d) { return d.vendor === 'meraki' ? d.bucket === 'camera' : false; }).length,
      sensors: devices.filter(function(d) { return d.vendor === 'meraki' ? d.bucket === 'sensor' : false; }).length,
      protect: devices.filter(function(d) { return d.vendor === 'unifi' && d.productLine === 'protect'; }).length,
      access: devices.filter(function(d) { return d.vendor === 'unifi' && d.productLine === 'access'; }).length,
      firmware_current: fwCurrent,
      firmware_outdated: fwOutdated
    };
  }

  function uptimeStr(startupTime) {
    if (!startupTime) return '—';
    var diff = Date.now() - new Date(startupTime).getTime();
    var days = Math.floor(diff / 86400000);
    var hours = Math.floor((diff % 86400000) / 3600000);
    return days > 0 ? days + 'd ' + hours + 'h' : hours + 'h';
  }

  var filterLabels = { 'online': 'Online Devices', 'offline': 'Offline Devices', 'network': 'Network Devices', 'protect': 'Protect Devices', 'access': 'Access Control', 'outdated': 'Firmware Outdated' };
  var filteredDevices = devices;
  if (filter === 'online') filteredDevices = devices.filter(function(d) { return d.status === 'online'; });
  else if (filter === 'offline') filteredDevices = devices.filter(function(d) { return d.status !== 'online'; });
  else if (filter === 'network') filteredDevices = devices.filter(function(d) { return d.productLine === 'network'; });
  else if (filter === 'protect') filteredDevices = devices.filter(function(d) { return d.productLine === 'protect'; });
  else if (filter === 'access') filteredDevices = devices.filter(function(d) { return d.productLine === 'access'; });
  else if (filter === 'outdated') filteredDevices = devices.filter(function(d) { return d.firmwareStatus !== 'upToDate'; });

  var onlinePct = s.total > 0 ? Math.round((s.online / s.total) * 100) : 0;
  var fwPct = s.total > 0 ? Math.round((s.firmware_current / s.total) * 100) : 0;

  return (
    <div className="tab-content">
      {view === 'summary' ? (
        <>
          <TabDescription text={
            'Network infrastructure managed through ' + (configuredVendors.length === 0 ? 'your provider' : configuredVendors.map(function(v) { return v === 'meraki' ? 'Cisco Meraki' : 'UniFi'; }).join(' and ')) + '. Tracks device uptime, firmware compliance, and site-level health across gateways, switches, access points, and cameras.'
          } />
          <DataFreshness live={true} />
          {multiVendor && <NetworkVendorSwitcher value={vendorFilter} onChange={setVendorFilter} vendors={configuredVendors} counts={{
            all: rawDevices.length,
            unifi: rawDevices.filter(function(d) { return d.vendor === 'unifi'; }).length,
            meraki: rawDevices.filter(function(d) { return d.vendor === 'meraki'; }).length
          }} />}
          <HealthVerdict score={fwPct} context={
            fwPct >= 90 ? 'Network infrastructure healthy — ' + fwPct + '% firmware current.' + (s.offline > 0 ? ' ' + s.offline + ' device(s) currently offline.' : '') :
            fwPct >= 70 ? (s.total - s.firmware_current) + ' device(s) running outdated firmware. Schedule a maintenance window for updates.' :
            'Network maintenance needed — ' + (s.total - s.firmware_current) + ' device(s) need firmware updates.'
          } />
          <ContentBand title="Network Status" subtitle={s.total + ' managed devices across ' + s.sites + ' site(s)'} rightContent={<><span className="live-badge">LIVE</span><button className="tab-drill-link" onClick={function() { setFilter(null); setView('devices'); }}>View All Devices ›</button></>}>
            <SegmentBar size="lg" segments={[
              { label: 'Online', value: s.online, color: '#22c55e', onClick: s.online > 0 ? function() { setFilter('online'); setView('devices'); } : undefined },
              { label: 'Offline', value: s.offline, color: '#64748b', onClick: s.offline > 0 ? function() { setFilter('offline'); setView('devices'); } : undefined },
            ]} />
            <div className="metric-tiles">
              <div className={'metric-tile ' + (fwPct >= 90 ? 'metric-tile-good' : fwPct < 70 ? 'metric-tile-alert' : '')}>
                <div className="metric-tile-label">Firmware Current</div>
                <div className="metric-tile-value">{fwPct}<span className="metric-tile-unit">%</span></div>
                <div className="metric-tile-context">{s.firmware_current} of {s.total} devices current</div>
              </div>
              <div
                className={'metric-tile' + ((s.total - s.firmware_current) > 0 ? ' metric-tile-alert' : ' metric-tile-good') + ((s.total - s.firmware_current) > 0 ? ' metric-tile-clickable' : '')}
                onClick={(s.total - s.firmware_current) > 0 ? function() { setFilter('outdated'); setView('devices'); } : undefined}
              >
                <div className="metric-tile-label">Outdated Firmware</div>
                <div className="metric-tile-value">{s.total - s.firmware_current}</div>
                <div className="metric-tile-context">{(s.total - s.firmware_current) > 0 ? 'Schedule maintenance window' : 'All firmware current'}</div>
              </div>
              <div className="metric-tile">
                <div className="metric-tile-label">Total Devices</div>
                <div className="metric-tile-value">{s.total}</div>
                <div className="metric-tile-context">Across {s.sites} site{s.sites === 1 ? '' : 's'}</div>
              </div>
              <div className="metric-tile">
                <div className="metric-tile-label">Online</div>
                <div className="metric-tile-value">{onlinePct}<span className="metric-tile-unit">%</span></div>
                <div className="metric-tile-context">{s.online} currently reachable</div>
              </div>
            </div>
          </ContentBand>
          <CollapsibleSection title="Device Inventory" defaultOpen={true}>
            <div className="stats-grid-compact">
              {s.gateways > 0 && <StatCardCompact title="Gateways" value={s.gateways} />}
              {s.access_points > 0 && <StatCardCompact title="Access Points" value={s.access_points} />}
              {s.switches > 0 && <StatCardCompact title="Switches" value={s.switches} />}
              {s.protect > 0 && <StatCardCompact title="Cameras" value={s.protect} />}
              {s.access > 0 && <StatCardCompact title="Access Control" value={s.access} />}
            </div>
          </CollapsibleSection>
          {sites.length > 1 && (
            <ContentBand title="Site Health">
              <MetricList>
                {sites.map(function(site) {
                  return <MetricListItem key={site.label} icon="◉" iconColor={site.total === site.online ? '#22c55e' : '#64748b'} label={site.label} sublabel={site.offline > 0 ? site.offline + ' currently off' : 'All active'} value={site.online + ' / ' + site.total} />;
                })}
              </MetricList>
            </ContentBand>
          )}
        </>
      ) : (
        (function() {
          // Unified bucket derivation — works for both UniFi (productLine + isConsole + shortname)
          // and Meraki (uses the .bucket field already set by the backend).
          var bucketOf = function(d) {
            if (d.vendor === 'meraki') return d.bucket || 'other';
            if (d.productLine === 'network') {
              if (d.isConsole) return 'gateway';
              if ((d.shortname || '').match(/^(U6|U7|UAP|G7)/i)) return 'access_point';
              if ((d.shortname || '').match(/^(US|USL|USW)/i)) return 'switch';
              return 'network';
            }
            if (d.productLine === 'protect') return 'camera';
            if (d.productLine === 'access')  return 'access_control';
            return 'other';
          };
          var bucketLabels = { gateway: 'Gateway', switch: 'Switch', access_point: 'Access Point', network: 'Network', camera: 'Camera', sensor: 'Sensor', access_control: 'Access', other: 'Other' };
          var bucketColors = { gateway: '#5b9ef4', switch: '#60a5fa', access_point: '#2dd4bf', network: '#5b9ef4', camera: '#a78bfa', sensor: '#22c55e', access_control: '#f5a623', other: '#94a3b8' };

          var netSearchFields = [function(d) { return d.name; }, function(d) { return d.model; }, function(d) { return d.ip; }];
          var netFilterAccessors = {
            status: function(d) { return d.status === 'online' ? 'Online' : 'Offline'; },
            type: function(d) { return bucketLabels[bucketOf(d)] || 'Other'; },
            firmware: function(d) { return d.firmwareStatus === 'upToDate' ? 'Current' : 'Outdated'; },
            site: function(d) { return d.site || '—'; }
          };
          var netAccessors = { name: function(d) { return d.name; }, model: function(d) { return d.model; }, site: function(d) { return d.site || ''; }, type: function(d) { return bucketLabels[bucketOf(d)] || ''; }, status: function(d) { return d.status; }, ip: function(d) { return d.ip || ''; } };
          var searched = filterRows(filteredDevices, netSearch, netSearchFields);
          var tableFiltered = applyFilters(searched, netFilters, netFilterAccessors);
          var sortedNet = sortRows(tableFiltered, netSort.sortField, netSort.sortDir, netAccessors);
          var netSiteGroups = {};
          filteredDevices.forEach(function(d) { netSiteGroups[d.site || '—'] = (netSiteGroups[d.site || '—'] || 0) + 1; });
          return (
        <>
          <div className="section-title">
            {filter ? filterLabels[filter] : 'All Devices'} ({sortedNet.length}{filter || Object.keys(netFilters).length > 0 ? ' of ' + devices.length : ''})
            {filter && <button className="insights-btn" style={{ marginLeft: 8, verticalAlign: 'middle' }} onClick={function() { setFilter(null); }}>Clear Filter</button>}
            <button className="insights-btn" style={{ marginLeft: 8, verticalAlign: 'middle' }} onClick={function() { setView('summary'); setFilter(null); setNetFilters({}); }}>Back to Summary</button>
          </div>
          {multiVendor && <NetworkVendorSwitcher value={vendorFilter} onChange={setVendorFilter} vendors={configuredVendors} counts={{
            all: rawDevices.length,
            unifi: rawDevices.filter(function(d) { return d.vendor === 'unifi'; }).length,
            meraki: rawDevices.filter(function(d) { return d.vendor === 'meraki'; }).length
          }} />}
          <TableSearch value={netSearch} onChange={setNetSearch} count={sortedNet.length} total={filteredDevices.length} noun="devices" onExport={function() { exportTableCSV(['Device','Model','Site','Type','Status','IP','Firmware','Uptime'], sortedNet.map(function(d) { return [d.name, d.model, d.site || '', d.productLine || '', d.status, d.ip || '', d.firmwareStatus || '', d.startupTime || '']; }), 'network-devices'); }} />
          <TableFilters filters={[
            { key: 'status', label: 'Status', options: [
              { value: 'Online', label: 'Online', color: '#22c55e', count: filteredDevices.filter(function(d) { return d.status === 'online'; }).length },
              { value: 'Offline', label: 'Offline', color: '#64748b', count: filteredDevices.filter(function(d) { return d.status !== 'online'; }).length }
            ]},
            { key: 'type', label: 'Type', options: (function() {
              // Build the type filter from whatever buckets are actually
              // present in the filtered device list, vendor-agnostic.
              var counts = {};
              filteredDevices.forEach(function(d) { var b = bucketOf(d); counts[b] = (counts[b] || 0) + 1; });
              return Object.keys(counts).map(function(b) {
                return { value: bucketLabels[b] || 'Other', label: bucketLabels[b] || 'Other', color: bucketColors[b] || '#94a3b8', count: counts[b] };
              }).filter(function(o) { return o.count > 0; }).sort(function(a, b) { return b.count - a.count; });
            })() },
            { key: 'firmware', label: 'Firmware', options: [
              { value: 'Current', label: 'Current', color: '#22c55e', count: filteredDevices.filter(function(d) { return d.firmwareStatus === 'upToDate'; }).length },
              { value: 'Outdated', label: 'Outdated', color: '#ef4444', count: filteredDevices.filter(function(d) { return d.firmwareStatus !== 'upToDate'; }).length }
            ]},
            { key: 'site', label: 'Site', options: Object.entries(netSiteGroups).sort(function(a, b) { return b[1] - a[1]; }).map(function(p) { return { value: p[0], label: p[0], count: p[1] }; }) }
          ]} active={netFilters} onChange={setNetFilters} />
          <div className="posture-table-wrap">
            <table className="posture-table">
              <thead><tr><SortTh field="name" current={netSort.sortField} dir={netSort.sortDir} onSort={netSort.toggleSort}>Device</SortTh><SortTh field="model" current={netSort.sortField} dir={netSort.sortDir} onSort={netSort.toggleSort}>Model</SortTh><SortTh field="site" current={netSort.sortField} dir={netSort.sortDir} onSort={netSort.toggleSort}>Site</SortTh><SortTh field="type" current={netSort.sortField} dir={netSort.sortDir} onSort={netSort.toggleSort}>Type</SortTh><SortTh field="status" current={netSort.sortField} dir={netSort.sortDir} onSort={netSort.toggleSort}>Status</SortTh><SortTh field="ip" current={netSort.sortField} dir={netSort.sortDir} onSort={netSort.toggleSort}>IP</SortTh><th>Firmware</th><th>Uptime</th></tr></thead>
              <tbody>
                {sortedNet.length === 0 && <tr><td colSpan="8" className="table-no-results">No devices match your filters</td></tr>}
                {sortedNet.map(function(d) {
                  // Compute a unified bucket per device so UniFi and Meraki get
                  // consistent type labels + colors. Meraki devices already
                  // carry a bucket field from the backend; UniFi is derived
                  // from productLine + isConsole + shortname.
                  var bucket;
                  if (d.vendor === 'meraki') {
                    bucket = d.bucket || 'other';
                  } else if (d.productLine === 'network') {
                    bucket = d.isConsole ? 'gateway' : ((d.shortname || '').match(/^(U6|U7|UAP|G7)/i) ? 'access_point' : (d.shortname || '').match(/^(US|USL|USW)/i) ? 'switch' : 'network');
                  } else if (d.productLine === 'protect') bucket = 'camera';
                  else if (d.productLine === 'access')   bucket = 'access_control';
                  else bucket = 'other';
                  var bucketStyle = {
                    gateway:        { label: 'Gateway',      bg: 'rgba(91,158,244,0.14)',  fg: '#5b9ef4' },
                    switch:         { label: 'Switch',       bg: 'rgba(96,165,250,0.12)',  fg: '#60a5fa' },
                    access_point:   { label: 'Access Point', bg: 'rgba(45,212,191,0.12)',  fg: '#2dd4bf' },
                    network:        { label: 'Network',      bg: 'rgba(91,158,244,0.12)',  fg: '#5b9ef4' },
                    camera:         { label: 'Camera',       bg: 'rgba(167,139,250,0.12)', fg: '#a78bfa' },
                    sensor:         { label: 'Sensor',       bg: 'rgba(34,197,94,0.12)',   fg: '#22c55e' },
                    access_control: { label: 'Access',       bg: 'rgba(245,166,35,0.12)',  fg: '#f5a623' },
                    other:          { label: d.productLine || 'Other', bg: 'rgba(148,163,184,0.14)', fg: '#94a3b8' }
                  }[bucket];
                  var typeLabel = bucketStyle.label;
                  return (
                    <tr key={d.id} className={d.status !== 'online' ? 'posture-row-offline' : ''}>
                      <td className="posture-device-name">
                        <span className={'status-dot ' + (d.status === 'online' ? 'status-dot-good' : 'status-dot-muted')} />
                        {d.name}
                        {multiVendor && <span className={'net-vendor-chip net-vendor-chip-' + d.vendor} title={d.vendor === 'meraki' ? 'Cisco Meraki' : 'Ubiquiti UniFi'}>{d.vendor === 'meraki' ? 'Meraki' : 'UniFi'}</span>}
                      </td>
                      <td>{d.model}</td><td>{d.site}</td>
                      <td><span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: bucketStyle.bg, color: bucketStyle.fg, fontWeight: 600 }}>{typeLabel}</span></td>
                      <td><span className={'posture-status ' + (d.status === 'online' ? 'posture-online' : 'posture-offline')}>{d.status === 'online' ? 'Online' : 'Offline'}</span></td>
                      <td style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 12 }}>{d.ip || '—'}</td>
                      <td><span className={'posture-av-state ' + (d.firmwareStatus === 'upToDate' ? 'posture-av-on' : 'posture-av-off')}>{d.firmwareStatus === 'upToDate' ? 'Current' : (d.firmwareStatus || '—')}</span></td>
                      <td className="posture-date">{uptimeStr(d.startupTime)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
          );
        })()
      )}
    </div>
  );
}
// ── SIEM Tab (Blumira) ────────────────────────────────────────────
// ── SIEM Components ───────────────────────────────────────────────
function SIEMMetricTiles({ summary, findings }) {
  var s = summary;
  var now = Date.now();
  var oldestMs = null;
  (findings || []).forEach(function(f) {
    if (!f.created) return;
    var t = new Date(f.created).getTime();
    if (t > 0 && (oldestMs === null || t < oldestMs)) oldestMs = t;
  });
  var oldestAgeMin = oldestMs !== null ? (now - oldestMs) / 60000 : null;
  var oldestAlert = oldestAgeMin !== null && oldestAgeMin > 7 * 24 * 60;
  return (
    <div className="metric-tiles">
      <div className="metric-tile">
        <div className="metric-tile-label">Total Open</div>
        <div className="metric-tile-value">{s.total_open || 0}</div>
        <div className="metric-tile-context">Active findings in queue</div>
      </div>
      <div className={'metric-tile' + (s.threats > 0 ? ' metric-tile-alert' : ' metric-tile-good')}>
        <div className="metric-tile-label">Confirmed Threats</div>
        <div className="metric-tile-value">{s.threats || 0}</div>
        <div className="metric-tile-context">{s.threats > 0 ? 'Immediate response required' : 'No active threats'}</div>
      </div>
      <div className={'metric-tile' + (s.priority_1 > 0 ? ' metric-tile-alert' : '')}>
        <div className="metric-tile-label">P1 Critical</div>
        <div className="metric-tile-value">{s.priority_1 || 0}</div>
        <div className="metric-tile-context">{s.priority_1 > 0 ? 'Highest severity open' : 'No P1 findings'}</div>
      </div>
      <div className={'metric-tile' + (oldestAlert ? ' metric-tile-alert' : '')}>
        <div className="metric-tile-label">Oldest Open</div>
        <div className="metric-tile-value">{formatDuration(oldestAgeMin)}</div>
        <div className="metric-tile-context">{oldestAgeMin !== null ? 'Age of longest-standing finding' : 'No open findings'}</div>
      </div>
    </div>
  );
}

function SIEMTypePriorityHeatmap({ findings }) {
  if (!findings || findings.length === 0) return <div className="priority-heatmap-empty">No open findings to analyze.</div>;

  var types = [
    { key: 30, label: 'Threat', color: '#ef4444' },
    { key: 20, label: 'Suspect', color: '#f59e0b' },
    { key: 15, label: 'Risk', color: '#a78bfa' },
    { key: 10, label: 'Info', color: '#64748b' }
  ];
  var priorities = [
    { key: 1, label: 'P1 Critical' },
    { key: 2, label: 'P2 High' },
    { key: 3, label: 'P3 Medium' }
  ];

  var matrix = {};
  types.forEach(function(t) { matrix[t.key] = {}; priorities.forEach(function(p) { matrix[t.key][p.key] = 0; }); });
  findings.forEach(function(f) { if (matrix[f.type] && matrix[f.type][f.priority] !== undefined) matrix[f.type][f.priority]++; });

  var activeTypes = types.filter(function(t) { return priorities.some(function(p) { return matrix[t.key][p.key] > 0; }); });
  if (activeTypes.length === 0) return <div className="priority-heatmap-empty">No findings to analyze.</div>;

  var max = 0;
  activeTypes.forEach(function(t) { priorities.forEach(function(p) { if (matrix[t.key][p.key] > max) max = matrix[t.key][p.key]; }); });

  var isHotspot = function(typeKey, priorityKey) {
    if (typeKey === 30 && matrix[typeKey][priorityKey] > 0) return true; // any Threat
    if (typeKey === 20 && priorityKey === 1 && matrix[typeKey][priorityKey] > 0) return true; // P1 Suspect
    return false;
  };

  return (
    <div className="priority-heatmap-wrap">
      <div className="priority-heatmap" style={{ gridTemplateColumns: '120px repeat(' + priorities.length + ', 1fr)' }}>
        <div className="priority-heatmap-corner"></div>
        {priorities.map(function(p) { return <div key={p.key} className="priority-heatmap-col-label">{p.label}</div>; })}
        {activeTypes.map(function(t) {
          return (
            <React.Fragment key={t.key}>
              <div className="priority-heatmap-row-label">
                <span className="priority-heatmap-row-dot" style={{ background: t.color }} />
                {t.label}
              </div>
              {priorities.map(function(p) {
                var count = matrix[t.key][p.key];
                var hotspot = isHotspot(t.key, p.key);
                var intensity = max > 0 ? count / max : 0;
                var bg = 'transparent';
                if (count > 0) {
                  if (hotspot) bg = 'rgba(239, 68, 68, ' + (0.18 + intensity * 0.32) + ')';
                  else bg = 'rgba(91, 158, 244, ' + (0.1 + intensity * 0.3) + ')';
                }
                return (
                  <div key={p.key} className={'priority-heatmap-cell' + (count === 0 ? ' cell-empty' : '') + (hotspot ? ' cell-hotspot' : '')} style={{ background: bg }}>
                    {count > 0 ? count : <span className="cell-dash">—</span>}
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>
      <div className="priority-heatmap-legend">
        <span className="priority-heatmap-legend-item"><span className="priority-heatmap-legend-swatch legend-hotspot" /> Hotspot — immediate attention</span>
        <span className="priority-heatmap-legend-item"><span className="priority-heatmap-legend-swatch legend-active" /> Open finding</span>
      </div>
    </div>
  );
}

function SIEMTab({ clientId, c }) {
  const [siemData, setSiemData] = useState(null);
  const [loading, setLoading] = useState(true);
  var siemSort = useTableSort('sent_at', 'desc');
  const [siemSearch, setSiemSearch] = useState('');
  const [siemFilters, setSiemFilters] = useState({});

  useEffect(function() {
    setLoading(true); setSiemData(null);
    authFetch(API + '/clients/' + clientId + '/siem')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(data) { setSiemData(data); setLoading(false); })
      .catch(function() { setLoading(false); });
  }, [clientId]);

  if (loading) return <div className="tab-content"><SkeletonLoader /></div>;
  // Tier-aware empty state: CMMC clients use Microsoft Sentinel (wired
  // through Atomus approval), commercial clients use Blumira. The
  // IntegrationEmptyState component handles the swap automatically when
  // we pass isCmmc — siem-commercial has cmmc_alternative='siem-cmmc'.
  if (!siemData || !siemData.enabled) {
    var isCmmcClient = !!(c && c.cmmc);
    return (
      <div className="tab-content">
        <IntegrationEmptyState
          kind="siem-commercial"
          clientId={clientId}
          isCmmc={isCmmcClient}
          extraDetail={isCmmcClient
            ? 'Sentinel deployment is coordinated through Atomus to keep the boundary clean with the CMMC compliance program.'
            : null}
        />
      </div>
    );
  }

  var cov = siemData.coverage || {};
  var sum = siemData.summary || {};
  var detections = siemData.detections || [];
  var ingesting = !!siemData.ingesting;
  function sevColor(sev) { var v = (sev || '').toLowerCase(); return v === 'critical' ? '#ef4444' : v === 'high' ? '#f59e0b' : v === 'medium' ? '#a78bfa' : v === 'low' ? '#60a5fa' : '#64748b'; }

  function timeAgo(dateStr) {
    if (!dateStr) return '—';
    var diff = Date.now() - new Date(dateStr).getTime();
    var mins = Math.floor(diff / 60000);
    if (mins < 60) return mins + 'm ago';
    var hours = Math.floor(mins / 60);
    if (hours < 24) return hours + 'h ago';
    var days = Math.floor(hours / 24);
    return days < 30 ? days + 'd ago' : Math.floor(days / 30) + 'mo ago';
  }

  var filteredDet = filterRows(detections, siemSearch, [function(d) { return d.subject; }, function(d) { return d.summary; }, function(d) { return d.platform; }]);
  var sortedDet = sortRows(filteredDet, siemSort.sortField, siemSort.sortDir, {
    severity: function(d) { return d.severity; }, subject: function(d) { return d.subject; }, sent_at: function(d) { return d.sent_at; }
  });

  return (
    <div className="tab-content">
      <TabDescription text="Managed SIEM & ITDR via Huntress. Once log sources are connected, SIEM event collection powers detections here alongside Huntress identity (ITDR) and endpoint (EDR) monitoring." />
      <DataFreshness live={true} />

      {!ingesting && (
        <div className="admin-banner info" style={{ marginBottom: 14 }}>
          <strong>Managed SIEM — onboarding.</strong> No log sources are ingesting for this tenant yet ({cov.log_sources || 0} connected). Connect log sources in Huntress to start SIEM event collection. ITDR identity monitoring and EDR endpoint detections below are already active.
        </div>
      )}

      <ContentBand title="Huntress coverage" subtitle="Per-product footprint for this tenant" rightContent={<span className="live-badge">LIVE</span>}>
        <QuickStats items={[
          { label: 'SIEM log sources', value: cov.log_sources || 0 },
          { label: 'Identities · ITDR', value: cov.identity_count || 0 },
          { label: 'EDR agents', value: cov.agents || 0 },
          { label: 'SAT learners', value: cov.sat_learners || 0 },
          { label: 'M365 users', value: cov.m365_users || 0 },
        ]} />
      </ContentBand>

      <ContentBand title="Detections" subtitle={(sum.total_open || 0) + ' open · ' + (sum.high_open || 0) + ' high/critical · ' + (sum.total_detections || 0) + ' total'}>
        {detections.length === 0 ? (
          <div className="empty-state">No Huntress detections for this tenant{ingesting ? '' : ' yet'}.</div>
        ) : (
          <>
            <TableSearch value={siemSearch} onChange={setSiemSearch} count={sortedDet.length} total={detections.length} noun="detections" onExport={function() { exportTableCSV(['Severity','Detection','Platform','Status','Detected'], sortedDet.map(function(d) { return [d.severity || '', d.subject || '', d.platform || '', d.status || '', d.sent_at ? new Date(d.sent_at).toLocaleString() : '']; }), 'huntress-detections'); }} />
            <div className="posture-table-wrap">
              <table className="posture-table">
                <thead>
                  <tr>
                    <SortTh field="severity" current={siemSort.sortField} dir={siemSort.sortDir} onSort={siemSort.toggleSort}>Severity</SortTh>
                    <SortTh field="subject" current={siemSort.sortField} dir={siemSort.sortDir} onSort={siemSort.toggleSort}>Detection</SortTh>
                    <th>Platform</th>
                    <th>Status</th>
                    <SortTh field="sent_at" current={siemSort.sortField} dir={siemSort.sortDir} onSort={siemSort.toggleSort}>Detected</SortTh>
                  </tr>
                </thead>
                <tbody>
                  {sortedDet.map(function(d, idx) {
                    return (
                      <tr key={d.id || idx}>
                        <td><span className="inline-badge" style={{ backgroundColor: sevColor(d.severity) }}>{d.severity || '—'}</span></td>
                        <td className="posture-device-name" style={{ maxWidth: 380 }}>
                          <div>{d.subject}</div>
                          {d.summary && <div className="table-sub">{d.summary.length > 120 ? d.summary.substring(0, 120) + '...' : d.summary}</div>}
                        </td>
                        <td>{d.platform || '—'}</td>
                        <td>{d.status || '—'}</td>
                        <td className="posture-date">{timeAgo(d.sent_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </ContentBand>
    </div>
  );
}

// ── Ticket Command Center Components ──────────────────────────────
function TicketFlowPipeline({ tickets, metrics }) {
  var stages = [
    { key: 'new', label: 'New', sublabel: 'Awaiting CloudByte', count: metrics.waitingForSupport || 0, statusParent: 1000, threshold: 24 },
    { key: 'inprogress', label: 'In Progress', sublabel: 'Actively working', count: metrics.inProgress || 0, statusParent: 2000, threshold: 72 },
    { key: 'waiting', label: 'Waiting', sublabel: 'On client response', count: metrics.waitingForUser || 0, statusParent: 3000, threshold: 120 },
    { key: 'pending', label: 'Pending', sublabel: 'Scheduled / blocked', count: metrics.pending || 0, statusParent: 4000, threshold: 72 },
    { key: 'closed', label: 'Closed', sublabel: 'Resolved', count: metrics.closed || 0, statusParent: null }
  ];
  return (
    <div className="ticket-flow">
      {stages.map(function(stage, i) {
        var aged = 0;
        if (stage.threshold && stage.count > 0 && stage.statusParent) {
          var thresholdMs = stage.threshold * 3600 * 1000;
          aged = tickets.filter(function(t) {
            return t.statusParent === stage.statusParent && t.created && (Date.now() - new Date(t.created).getTime()) > thresholdMs;
          }).length;
        }
        var isEmpty = stage.count === 0;
        return (
          <React.Fragment key={stage.key}>
            <div className={'ticket-flow-stage ticket-flow-stage-' + stage.key + (aged > 0 ? ' ticket-flow-stage-aging' : '') + (isEmpty ? ' ticket-flow-stage-empty' : '')}>
              <div className="ticket-flow-stage-label">{stage.label}</div>
              <div className="ticket-flow-stage-count">{stage.count}</div>
              <div className="ticket-flow-stage-sub">{stage.sublabel}</div>
              {aged > 0 && <div className="ticket-flow-stage-aging-badge">{aged} over {stage.threshold >= 48 ? (stage.threshold / 24) + 'd' : stage.threshold + 'h'}</div>}
            </div>
            {i < stages.length - 1 && <div className="ticket-flow-connector"><span className="ticket-flow-arrow">›</span></div>}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function PriorityStatusHeatmap({ tickets }) {
  var priorities = ['HIGH', 'MEDIUM', 'LOW', 'NONE'];
  var statuses = [
    { key: 'new', label: 'New', parents: [1000] },
    { key: 'inprog', label: 'In Progress', parents: [2000] },
    { key: 'waiting', label: 'Waiting', parents: [3000] },
    { key: 'pending', label: 'Pending', parents: [4000] },
    { key: 'closed', label: 'Closed', parents: [5000, 6000] }
  ];
  var priorityLabels = { HIGH: 'High', MEDIUM: 'Medium', LOW: 'Low', NONE: 'Unset' };
  var priorityColors = { HIGH: '#ef4444', MEDIUM: '#f59e0b', LOW: '#22c55e', NONE: '#94a3b8' };

  var matrix = {};
  priorities.forEach(function(p) { matrix[p] = {}; statuses.forEach(function(s) { matrix[p][s.key] = 0; }); });
  var activeMax = 0;
  tickets.forEach(function(t) {
    var priority = priorities.indexOf(t.priority) >= 0 ? t.priority : 'NONE';
    var statusKey = null;
    statuses.forEach(function(s) { if (s.parents.indexOf(t.statusParent) >= 0) statusKey = s.key; });
    if (statusKey) {
      matrix[priority][statusKey]++;
      if (statusKey !== 'closed' && matrix[priority][statusKey] > activeMax) activeMax = matrix[priority][statusKey];
    }
  });

  var isHotspot = function(priority, statusKey) {
    if (statusKey === 'closed') return false;
    if (priority === 'HIGH' && matrix[priority][statusKey] > 0) return true;
    if (priority === 'MEDIUM' && (statusKey === 'new' || statusKey === 'waiting' || statusKey === 'pending') && matrix[priority][statusKey] > 2) return true;
    return false;
  };

  return (
    <div className="priority-heatmap-wrap">
      <div className="priority-heatmap">
        <div className="priority-heatmap-corner"></div>
        {statuses.map(function(s) { return <div key={s.key} className="priority-heatmap-col-label">{s.label}</div>; })}
        {priorities.map(function(p) {
          return (
            <React.Fragment key={p}>
              <div className="priority-heatmap-row-label">
                <span className="priority-heatmap-row-dot" style={{ background: priorityColors[p] }} />
                {priorityLabels[p]}
              </div>
              {statuses.map(function(s) {
                var count = matrix[p][s.key];
                var intensity = activeMax > 0 && s.key !== 'closed' ? count / activeMax : 0;
                var hotspot = isHotspot(p, s.key);
                var bg = 'transparent';
                if (count > 0) {
                  if (hotspot) bg = 'rgba(239, 68, 68, ' + (0.18 + intensity * 0.32) + ')';
                  else if (s.key === 'closed') bg = 'rgba(34, 197, 94, 0.08)';
                  else bg = 'rgba(91, 158, 244, ' + (0.1 + intensity * 0.3) + ')';
                }
                return (
                  <div key={s.key} className={'priority-heatmap-cell' + (count === 0 ? ' cell-empty' : '') + (hotspot ? ' cell-hotspot' : '')} style={{ background: bg }}>
                    {count > 0 ? count : <span className="cell-dash">—</span>}
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>
      <div className="priority-heatmap-legend">
        <span className="priority-heatmap-legend-item"><span className="priority-heatmap-legend-swatch legend-hotspot" /> Hotspot — needs attention</span>
        <span className="priority-heatmap-legend-item"><span className="priority-heatmap-legend-swatch legend-active" /> Active</span>
        <span className="priority-heatmap-legend-item"><span className="priority-heatmap-legend-swatch legend-closed" /> Resolved</span>
      </div>
    </div>
  );
}

function SourceChannelCards({ bySource }) {
  var sourceMeta = {
    END_USER: { label: 'End User Portal', icon: '\u25F0', color: '#5b9ef4', desc: 'Self-service submissions' },
    EMAIL: { label: 'Email', icon: '\u2709', color: '#a78bfa', desc: 'Direct email channel' },
    TECHNICIAN: { label: 'Technician', icon: '\u2699', color: '#22c55e', desc: 'Created by CloudByte' }
  };
  var entries = Object.entries(bySource);
  var total = entries.reduce(function(s, e) { return s + e[1]; }, 0);
  return (
    <div className="source-cards">
      {entries.map(function(pair) {
        var key = pair[0]; var count = pair[1];
        var meta = sourceMeta[key] || { label: key, icon: '\u25CF', color: '#64748b', desc: '' };
        var pct = total > 0 ? Math.round((count / total) * 100) : 0;
        return (
          <div key={key} className="source-card">
            <div className="source-card-icon" style={{ background: meta.color + '18', color: meta.color }}>{meta.icon}</div>
            <div className="source-card-body">
              <div className="source-card-name">{meta.label}</div>
              <div className="source-card-desc">{meta.desc}</div>
              <div className="source-card-stats">
                <span className="source-card-count">{count}</span>
                <span className="source-card-pct">{pct}% of total</span>
              </div>
              <div className="source-card-bar"><div className="source-card-bar-fill" style={{ width: pct + '%', background: meta.color }} /></div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function formatDuration(minutes) {
  if (minutes == null || isNaN(minutes)) return '—';
  if (minutes < 60) return Math.max(0, Math.round(minutes)) + 'm';
  if (minutes < 48 * 60) return Math.round(minutes / 60) + 'h';
  var days = minutes / (60 * 24);
  return (days >= 10 ? Math.round(days) : days.toFixed(1)) + 'd';
}

function SmartMetricTiles({ metrics, tickets }) {
  var m = metrics;
  var active = (m.open || 0);
  var stalled = m.waitingForSupport || 0;
  var now = Date.now();

  // Time to Acknowledge — avg age of tickets currently in "New" stage (parentId 1000)
  var newTickets = (tickets || []).filter(function(t) { return t.statusParent === 1000 && t.created; });
  var tta = null;
  if (newTickets.length > 0) {
    var totalAgeMin = newTickets.reduce(function(sum, t) { return sum + (now - new Date(t.created).getTime()) / 60000; }, 0);
    tta = totalAgeMin / newTickets.length;
  }
  var ttaAlert = tta !== null && tta > 24 * 60; // alert if avg > 24h

  // MTTR — avg time from creation to lastUpdate for closed tickets
  var closedWithTimes = (tickets || []).filter(function(t) {
    return (t.statusParent === 5000 || t.statusParent === 6000) && t.created && t.lastUpdate;
  });
  var mttr = null;
  if (closedWithTimes.length > 0) {
    var totalResMin = closedWithTimes.reduce(function(sum, t) {
      return sum + (new Date(t.lastUpdate).getTime() - new Date(t.created).getTime()) / 60000;
    }, 0);
    mttr = totalResMin / closedWithTimes.length;
  }

  return (
    <div className="metric-tiles">
      <div className="metric-tile">
        <div className="metric-tile-label">Active Queue</div>
        <div className="metric-tile-value">{active}</div>
        <div className="metric-tile-context">{m.closed || 0} resolved all-time</div>
      </div>
      <div className={'metric-tile' + (stalled > 0 ? ' metric-tile-alert' : ' metric-tile-good')}>
        <div className="metric-tile-label">Awaiting CloudByte</div>
        <div className="metric-tile-value">{stalled}</div>
        <div className="metric-tile-context">{stalled > 0 ? 'Response required' : 'Queue is clear'}</div>
      </div>
      <div className={'metric-tile' + (ttaAlert ? ' metric-tile-alert' : '')}>
        <div className="metric-tile-label">Time to Acknowledge</div>
        <div className="metric-tile-value">{formatDuration(tta)}</div>
        <div className="metric-tile-context">{newTickets.length > 0 ? 'Avg wait for first CloudByte response' : 'No new tickets in queue'}</div>
      </div>
      <div className="metric-tile">
        <div className="metric-tile-label">Mean Time to Resolution</div>
        <div className="metric-tile-value">{formatDuration(mttr)}</div>
        <div className="metric-tile-context">{mttr !== null ? 'Avg open → close wall-clock' : 'Awaiting resolution data'}</div>
      </div>
    </div>
  );
}

// ── Support Tab (NinjaOne FedRAMP Tickets) ────────────────────────
// Ticket display helpers (shared casing/color so the summary + table read
// consistently). NinjaOne is the source of truth; we only reformat for display.
var TIX_TYPE_LABEL = { PROBLEM: 'Problem', QUESTION: 'Question', TASK: 'Task', INCIDENT: 'Incident', CHANGE_REQUEST: 'Change request', SERVICE_REQUEST: 'Service request', PROJECT: 'Project' };
function tixType(t) { return t ? (TIX_TYPE_LABEL[t] || (t.charAt(0) + t.slice(1).toLowerCase().replace(/_/g, ' '))) : '—'; }
function tixPrioLabel(p) { return (!p || p === 'NONE') ? '—' : (p.charAt(0) + p.slice(1).toLowerCase()); }
function tixPrioColor(p) { return (p === 'HIGH' || p === 'URGENT') ? '#ef4444' : p === 'MEDIUM' ? '#f59e0b' : p === 'LOW' ? '#22c55e' : '#64748b'; }
function tixPrioRank(p) { var r = { URGENT: 0, HIGH: 1, MEDIUM: 2, LOW: 3 }; return r[p] != null ? r[p] : 4; }
function tixStatusColor(pid) { return pid === 1000 ? '#f59e0b' : pid === 2000 ? '#60a5fa' : pid === 3000 ? '#a78bfa' : pid === 4000 ? '#94a3b8' : pid === 5000 ? '#22c55e' : pid === 6000 ? '#64748b' : '#94a3b8'; }
function tixIsClosed(t) { return t.statusParent === 5000 || t.statusParent === 6000; }

var SLA_ACK_MIN = 30; // first-response SLA target (minutes)

function SupportTab({ clientId, live }) {
  const [ticketData, setTicketData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('summary');
  var tixSort = useTableSort('id', 'desc');
  const [tixSearch, setTixSearch] = useState('');
  const [tixFilters, setTixFilters] = useState({});
  const [range, setRange] = useState('90');   // '7' | '30' | '90' | 'all'
  const [quick, setQuick] = useState(null);    // KPI-card quick filter
  const [now, setNow] = useState(Date.now());

  useEffect(function() {
    setLoading(true); setTicketData(null); setView('summary'); setQuick(null);
    authFetch(API + '/clients/' + clientId + '/tickets')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(data) { setTicketData(data); setLoading(false); })
      .catch(function() { setLoading(false); });
  }, [clientId]);
  // Live tick so the first-response SLA countdown updates without a refetch.
  useEffect(function() { var id = setInterval(function() { setNow(Date.now()); }, 30000); return function() { clearInterval(id); }; }, []);

  if (loading) return <div className="tab-content"><SkeletonLoader /></div>;
  if (!ticketData || !ticketData.metrics) return <div className="tab-content"><IntegrationEmptyState kind="endpoints" clientId={clientId} extraDetail="Support tickets ride on the NinjaOne RMM connection. Once NinjaOne is wired, ticket activity surfaces here automatically." /></div>;

  function createdMs(t) { return t.created ? new Date(t.created).getTime() : 0; }
  // First-response SLA: only meaningful for tickets still awaiting support
  // (NinjaOne's board doesn't expose an ack timestamp for already-acked ones).
  function sla(t) {
    if (t.statusParent !== 1000) return { state: 'na' };
    var rem = createdMs(t) + SLA_ACK_MIN * 60000 - now;
    if (rem <= 0) return { state: 'breach', mins: Math.round(-rem / 60000) };
    return { state: 'pending', mins: Math.ceil(rem / 60000) };
  }

  // Date-range scope (by created date) — drives KPIs, pipeline, and the table.
  var rangeMs = range === 'all' ? 0 : parseInt(range, 10) * 86400000;
  var scoped = ticketData.tickets.filter(function(t) { return range === 'all' || createdMs(t) >= now - rangeMs; });

  var cnt = function(fn) { return scoped.filter(fn).length; };
  var breaches = scoped.filter(function(t) { return sla(t).state === 'breach'; }).length;
  // Avg handle time over CLOSED tickets in the selected range (per-ticket
  // timeTrackedMinutes comes straight from NinjaOne), so it moves with the
  // date range like every other KPI. '—' when the window has no closed work.
  var handleSet = scoped.filter(function(t) { return tixIsClosed(t) && t.timeTrackedMinutes > 0; });
  var avgHandle = handleSet.length ? Math.round(handleSet.reduce(function(s, t) { return s + t.timeTrackedMinutes; }, 0) / handleSet.length) : null;
  var kpis = [
    { key: 'total', label: 'Total', value: scoped.length, tone: 'neutral', filter: null },
    { key: 'open', label: 'Open', value: cnt(function(t) { return !tixIsClosed(t); }), tone: 'info', filter: { open: true } },
    { key: 'ack', label: 'Needs first response', value: cnt(function(t) { return t.statusParent === 1000; }), tone: 'warn', filter: { parent: 1000 } },
    { key: 'breach', label: 'SLA breached', value: breaches, tone: breaches > 0 ? 'crit' : 'good', filter: { sla: 'breach' } },
    { key: 'resolved', label: 'Resolved', value: cnt(function(t) { return t.statusParent === 5000; }), tone: 'good', filter: { parent: 5000 } },
    { key: 'avg', label: 'Avg handle time', value: avgHandle == null ? '—' : avgHandle + 'm', tone: 'neutral', filter: null }
  ];
  function openFiltered(f) { setQuick(f); setTixFilters({}); setView('tickets'); }

  var rangeCtl = (
    <div className="tix-range">
      {[['7', '7d'], ['30', '30d'], ['90', '90d'], ['all', 'All']].map(function(o) {
        return <button key={o[0]} type="button" className={'tix-range-btn' + (range === o[0] ? ' active' : '')} onClick={function() { setRange(o[0]); }}>{o[1]}</button>;
      })}
    </div>
  );

  return (
    <div className="tab-content">
      {view === 'summary' ? (
        <>
          <TabDescription text="Support tickets from NinjaOne (source of truth). First-response SLA target: 30 minutes." />
          <div className="tix-toolbar"><DataFreshness live={true} /><span className="tix-toolbar-spacer" />{rangeCtl}</div>

          <div className="tix-kpis">
            {kpis.map(function(k) {
              return (
                <button key={k.key} type="button" className={'tix-kpi tix-kpi-' + k.tone + (k.filter ? ' clickable' : '')} disabled={!k.filter} onClick={function() { if (k.filter) openFiltered(k.filter); }}>
                  <span className="tix-kpi-val">{k.value}</span>
                  <span className="tix-kpi-lbl">{k.label}</span>
                </button>
              );
            })}
          </div>

          {breaches > 0 && (function() {
            var stalledTix = scoped.filter(function(t) { return sla(t).state === 'breach'; });
            var bodyLines = ['Hi CloudByte Support,', '', 'The following ticket(s) have breached the 30-minute first-response SLA:', ''];
            stalledTix.forEach(function(t) { bodyLines.push('• Ticket #' + t.id + ' — ' + t.summary); bodyLines.push('  Status: ' + t.status + ' | Priority: ' + tixPrioLabel(t.priority) + (t.assignee ? ' | Assigned: ' + t.assignee : ' | Unassigned')); bodyLines.push(''); });
            bodyLines.push('Could you provide an update on these items?', '', 'Thank you');
            return (
              <a href={'mailto:support@cloudbytegroup.com?subject=' + encodeURIComponent('Aurelius — ' + stalledTix.length + ' SLA-breached ticket(s)') + '&body=' + encodeURIComponent(bodyLines.join('\n'))} className="verdict-mailto" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 6, padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, color: '#ef4444', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.28)', textDecoration: 'none' }}>✉ Escalate {stalledTix.length} SLA-breached ticket(s)</a>
            );
          })()}

          <ContentBand title="Ticket Flow — NinjaOne" subtitle="Pipeline across ticket stages" rightContent={<>{!IS_DEMO_VIEW && <a href="https://fed.ninjarmm.com/#/ticketing" target="_blank" rel="noopener noreferrer" className="tab-drill-link" style={{ textDecoration: 'none' }}>Open NinjaOne ↗</a>}<span className="live-badge">LIVE</span><button className="tab-drill-link" onClick={function() { setView('tickets'); }}>View All Tickets ›</button></>}>
            <TicketFlowPipeline tickets={scoped} metrics={ticketData.metrics} />
          </ContentBand>
          <ContentBand title="Priority × Status Matrix" subtitle="Hotspots by priority and stage">
            <PriorityStatusHeatmap tickets={scoped} />
          </ContentBand>
          <ContentBand title="Ticket Channels" subtitle="Where tickets originate">
            <SourceChannelCards bySource={ticketData.metrics.bySource} />
          </ContentBand>
        </>
      ) : (
        (function() {
          var quickFn = function(t) {
            if (!quick) return true;
            if (quick.open) return !tixIsClosed(t);
            if (quick.parent != null) return t.statusParent === quick.parent;
            if (quick.sla === 'breach') return sla(t).state === 'breach';
            return true;
          };
          var base = scoped.filter(quickFn);
          var tixFiltered = applyFilters(
            filterRows(base, tixSearch, [function(t){return t.summary;}, function(t){return t.requester;}, function(t){return t.assignee;}, function(t){return '#'+t.id;}]),
            tixFilters,
            { status: function(t) { return t.status; }, priority: function(t) { return t.priority; }, type: function(t) { return t.type; } }
          );
          var tixSorted = sortRows(tixFiltered, tixSort.sortField, tixSort.sortDir, {
            id: function(t){return t.id;}, summary: function(t){return t.summary;}, status: function(t){return t.status;},
            priority: function(t){return tixPrioRank(t.priority);}, requester: function(t){return t.requester;},
            assignee: function(t){return t.assignee;}, type: function(t){return tixType(t.type);},
            sla: function(t){ var s = sla(t); return s.state === 'breach' ? -1e9 - s.mins : s.state === 'pending' ? s.mins : 1e9; },
            created: function(t){return createdMs(t);}
          });
          var statuses = {}; var priorities = {}; var types = {};
          base.forEach(function(t) { statuses[t.status] = (statuses[t.status]||0)+1; if (t.priority && t.priority !== 'NONE') priorities[t.priority] = (priorities[t.priority]||0)+1; if (t.type) types[t.type] = (types[t.type]||0)+1; });
          var quickLabel = quick ? (quick.open ? 'Open' : quick.sla === 'breach' ? 'SLA breached' : quick.parent != null ? (base[0] ? base[0].status : 'Filtered') : 'Filtered') : null;
          return (
        <>
          <div className="section-title">
            All Tickets ({base.length})
            {quickLabel && <span className="tix-quickchip">{quickLabel} <button type="button" onClick={function() { setQuick(null); }} title="Clear filter">×</button></span>}
            <span className="tix-toolbar-spacer" />
            {rangeCtl}
            <button className="insights-btn" style={{ marginLeft: 12, verticalAlign: 'middle' }} onClick={function() { setView('summary'); }}>Back to Summary</button>
          </div>
          <TableSearch value={tixSearch} onChange={setTixSearch} count={tixSorted.length} total={base.length} noun="tickets" onExport={function() { exportTableCSV(['ID','Type','Summary','Status','Priority','Requester','Assignee','SLA','Time (min)','Created'], tixSorted.map(function(t) { var s = sla(t); return [t.id, tixType(t.type), t.summary, t.status, tixPrioLabel(t.priority), t.requester || '', t.assignee || '', s.state === 'breach' ? 'Breached +' + s.mins + 'm' : s.state === 'pending' ? s.mins + 'm to ack' : '', t.timeTrackedMinutes || '', t.created ? new Date(t.created).toLocaleString() : '']; }), 'support-tickets'); }} />
          <TableFilters filters={[
            { key: 'status', label: 'Status', options: Object.entries(statuses).map(function([s,c]) { return { value: s, label: s, count: c }; }) },
            { key: 'priority', label: 'Priority', options: Object.entries(priorities).sort(function(a,b){return tixPrioRank(a[0])-tixPrioRank(b[0]);}).map(function([p,c]) { return { value: p, label: tixPrioLabel(p), color: tixPrioColor(p), count: c }; }) },
            { key: 'type', label: 'Type', options: Object.entries(types).map(function([ty,c]) { return { value: ty, label: tixType(ty), count: c }; }) },
          ]} active={tixFilters} onChange={setTixFilters} />
          <div className="posture-table-wrap">
            <table className="posture-table">
              <thead>
                <tr>
                  <SortTh field="id" current={tixSort.sortField} dir={tixSort.sortDir} onSort={tixSort.toggleSort}><span title="NinjaOne ticket number — opens the ticket in NinjaOne">ID</span></SortTh>
                  <SortTh field="type" current={tixSort.sortField} dir={tixSort.sortDir} onSort={tixSort.toggleSort}><span title="Ticket type as set in NinjaOne (problem, question, service request, …)">Type</span></SortTh>
                  <SortTh field="summary" current={tixSort.sortField} dir={tixSort.sortDir} onSort={tixSort.toggleSort}><span title="Ticket subject line">Summary</span></SortTh>
                  <SortTh field="status" current={tixSort.sortField} dir={tixSort.sortDir} onSort={tixSort.toggleSort}><span title="Current NinjaOne status the tech has the ticket in">Status</span></SortTh>
                  <SortTh field="priority" current={tixSort.sortField} dir={tixSort.sortDir} onSort={tixSort.toggleSort}><span title="Ticket priority">Priority</span></SortTh>
                  <SortTh field="requester" current={tixSort.sortField} dir={tixSort.sortDir} onSort={tixSort.toggleSort}><span title="Who opened the ticket">Requester</span></SortTh>
                  <SortTh field="assignee" current={tixSort.sortField} dir={tixSort.sortDir} onSort={tixSort.toggleSort}><span title="Technician assigned">Assignee</span></SortTh>
                  <SortTh field="sla" current={tixSort.sortField} dir={tixSort.sortDir} onSort={tixSort.toggleSort}><span title="First-response SLA (30 min). Live countdown for tickets awaiting support; breached shows how far over.">SLA</span></SortTh>
                  <SortTh field="created" current={tixSort.sortField} dir={tixSort.sortDir} onSort={tixSort.toggleSort}><span title="When the ticket was created">Created</span></SortTh>
                </tr>
              </thead>
              <tbody>
                {tixSorted.length === 0 && <tr><td colSpan="9" className="table-no-results">No tickets match your search</td></tr>}
                {tixSorted.map(function(t) {
                  var closed = tixIsClosed(t);
                  var s = sla(t);
                  return (
                    <tr key={t.id} className={closed ? 'posture-row-offline' : ''}>
                      <td><a href={t.url || 'https://fed.ninjarmm.com/#/ticketing'} target="_blank" rel="noopener noreferrer" className="table-link" title={'Open ticket #' + t.id + ' in NinjaOne'}>#{t.id} ↗</a></td>
                      <td><span className="tix-type">{tixType(t.type)}</span></td>
                      <td className="posture-device-name" style={{ maxWidth: 300 }} title={t.summary}><span className={'status-dot ' + (t.statusParent === 1000 ? 'status-dot-warning' : closed ? 'status-dot-muted' : 'status-dot-info')} />{t.summary}</td>
                      <td><span className="posture-status" style={{ color: tixStatusColor(t.statusParent) }}>{t.status}</span></td>
                      <td><span className="tix-prio" style={{ color: tixPrioColor(t.priority), background: tixPrioColor(t.priority) + '1f', borderColor: tixPrioColor(t.priority) + '55' }}>{tixPrioLabel(t.priority)}</span></td>
                      <td>{t.requester || '—'}</td>
                      <td>{t.assignee || 'Unassigned'}</td>
                      <td title={t.timeTrackedMinutes > 0 ? 'Handle time: ' + t.timeTrackedMinutes + 'm' : ''}>
                        {s.state === 'breach' ? <span className="tix-sla breach">Breached +{s.mins}m</span>
                          : s.state === 'pending' ? <span className={'tix-sla ' + (s.mins <= 10 ? 'warn' : 'ok')}>{s.mins}m to ack</span>
                          : <span className="tix-sla na">—</span>}
                      </td>
                      <td className="posture-date" title={t.created ? new Date(t.created).toLocaleString() : ''}>{t.created ? new Date(t.created).toLocaleDateString() : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
          ); })()
      )}
    </div>
  );
}

// ── Projects Tab (Asana) ──────────────────────────────────────────
// ── Projects v2 helper components ────────────────────────────────────
// Goal: replace the bare-name / bare-date / disconnected-pill renderings
// with narrative chips a client can read at a glance. Every avatar and
// date carries semantic meaning rather than asking the reader to infer.

// Deterministic color from email (or name fallback) so the same person
// always gets the same circle. Avoids reseeding on each render.
function _projInitials(name) {
  if (!name) return '?';
  var parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
function _projColorPair(seed) {
  // Hash → hue. Pair: vivid foreground tone + matching translucent bg so
  // we can tint dark + light themes consistently with one variable.
  var s = seed || 'unknown';
  var h = 0;
  for (var i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  var hue = Math.abs(h) % 360;
  return { hue: hue, fg: 'hsl(' + hue + ', 65%, 65%)', bg: 'hsla(' + hue + ', 60%, 50%, 0.18)', border: 'hsla(' + hue + ', 60%, 55%, 0.35)' };
}
function ProjAvatar({ name, email, size, title }) {
  var sz = size || 26;
  if (!name) {
    return <span className="proj-avatar proj-avatar-empty" style={{ width: sz, height: sz, fontSize: Math.round(sz * 0.42) }} title={title || 'Unassigned'}>?</span>;
  }
  var c = _projColorPair(email || name);
  return (
    <span
      className="proj-avatar"
      style={{ width: sz, height: sz, fontSize: Math.round(sz * 0.42), color: c.fg, background: c.bg, borderColor: c.border }}
      title={title || (email ? name + ' · ' + email : name)}
    >{_projInitials(name)}</span>
  );
}

// Narrative date chip — never renders a bare timestamp. Always says what
// the date *means* so a client can scan the row and understand state.
function ProjDueChip({ task }) {
  if (!task) return null;
  if (task.dueState === 'done') {
    var copy = task.completedAgo == null
      ? 'Done'
      : task.completedAgo === 0 ? 'Done today' : 'Done ' + task.completedAgo + 'd ago';
    return <span className="proj-chip proj-chip-done" title={task.completedAt ? new Date(task.completedAt).toLocaleString() : ''}><span className="proj-chip-dot" />{copy}</span>;
  }
  if (task.dueState === 'overdue') {
    var od = task.daysOverdue || 0;
    return <span className="proj-chip proj-chip-overdue" title={task.due ? 'Was due ' + new Date(task.due).toLocaleDateString() : ''}><span className="proj-chip-dot" />Overdue {od}d</span>;
  }
  if (task.dueState === 'due-soon') {
    var u = task.daysUntilDue;
    var label = u === 0 ? 'Due today' : u === 1 ? 'Due tomorrow' : 'Due in ' + u + 'd';
    return <span className="proj-chip proj-chip-due-soon" title={task.due ? new Date(task.due).toLocaleDateString() : ''}><span className="proj-chip-dot" />{label}</span>;
  }
  if (task.dueState === 'on-track') {
    return <span className="proj-chip proj-chip-on-track" title={task.due ? new Date(task.due).toLocaleDateString() : ''}><span className="proj-chip-dot" />Due in {task.daysUntilDue}d</span>;
  }
  return <span className="proj-chip proj-chip-no-due"><span className="proj-chip-dot" />No due date</span>;
}

// Single task row — avatar + name + metadata row beneath, optional inset
// subtask checklist. Replaces the flat field-dump that was the core
// readability problem.
function ProjTaskCard({ task, depth }) {
  var [openSubs, setOpenSubs] = useState(false);
  var d = depth || 0;
  var hasSubs = (task.subtaskCount || 0) > 0;
  var subDoneCount = task.subtasks ? task.subtasks.filter(function(s) { return s.completed; }).length : null;
  var subTotal = task.subtaskCount || (task.subtasks ? task.subtasks.length : 0);

  var rowCls = 'proj-task'
    + (task.dueState === 'overdue' ? ' proj-task-overdue' : '')
    + (task.dueState === 'due-soon' ? ' proj-task-due-soon' : '')
    + (task.completed ? ' proj-task-done' : '')
    + (d > 0 ? ' proj-task-sub' : '');

  return (
    <div className={rowCls}>
      <div className="proj-task-row">
        <div className="proj-task-left">
          <ProjAvatar name={task.assignee && task.assignee.name} email={task.assignee && task.assignee.email} size={d > 0 ? 22 : 28} title={task.assignee ? (task.assignee.name + (task.assignee.email ? ' · ' + task.assignee.email : '')) : 'Unassigned'} />
          <div className="proj-task-text">
            <div className="proj-task-title">
              {task.completed && <span className="proj-task-check" aria-hidden="true">✓</span>}
              <span className={task.completed ? 'proj-task-name strike' : 'proj-task-name'}>{task.name}</span>
              {task.permalink && <a className="proj-task-link" href={task.permalink} target="_blank" rel="noopener noreferrer" title="Open in Asana">↗</a>}
            </div>
            <div className="proj-task-meta">
              {task.assignee && task.assignee.name
                ? <span className="proj-task-meta-piece">{task.assignee.name}</span>
                : <span className="proj-task-meta-piece muted">Unassigned</span>
              }
              <ProjDueChip task={task} />
              {task.customFields && task.customFields.map(function(cf, ci) {
                return <span key={ci} className="proj-task-meta-cf"><span className="proj-task-meta-cf-name">{cf.name}</span>{cf.value}</span>;
              })}
              {hasSubs && (
                <button
                  type="button"
                  className="proj-task-subs-toggle"
                  onClick={function() { setOpenSubs(!openSubs); }}
                  title={task.subtasks ? 'Toggle subtasks' : 'Open in Asana to view subtasks'}
                >
                  <span className="proj-task-subs-icon">{openSubs ? '▾' : '▸'}</span>
                  {subDoneCount != null
                    ? subDoneCount + ' / ' + subTotal + ' subtasks'
                    : subTotal + ' subtask' + (subTotal === 1 ? '' : 's')}
                </button>
              )}
            </div>
            {task.description && (
              <div className="proj-task-desc">{task.description}</div>
            )}
          </div>
        </div>
      </div>

      {hasSubs && openSubs && task.subtasks && task.subtasks.length > 0 && (
        <div className="proj-subtask-list">
          {task.subtasks.map(function(st) {
            return (
              <div key={st.gid} className={'proj-subtask' + (st.completed ? ' done' : '') + (st.dueState === 'overdue' ? ' overdue' : '')}>
                <span className="proj-subtask-check" aria-hidden="true">{st.completed ? '✓' : '○'}</span>
                <span className={st.completed ? 'proj-subtask-name strike' : 'proj-subtask-name'}>{st.name}</span>
                {st.assignee && st.assignee.name && <ProjAvatar name={st.assignee.name} email={st.assignee.email} size={18} />}
                <ProjDueChip task={st} />
                {st.permalink && <a className="proj-task-link" href={st.permalink} target="_blank" rel="noopener noreferrer" title="Open in Asana">↗</a>}
              </div>
            );
          })}
        </div>
      )}
      {hasSubs && openSubs && (!task.subtasks || task.subtasks.length === 0) && (
        <div className="proj-subtask-empty">Subtasks not loaded — open in Asana for the full list.</div>
      )}
    </div>
  );
}

// 3-stat health ribbon at top of expansion. Translates the project's
// numeric state into "what should I look at next" callouts.
function ProjHealthRibbon({ project }) {
  var nextDeliverable = project.nextMilestone;
  var risks = project.overdueTasks || 0;
  var dueSoon = project.dueSoonTasks || 0;

  // "Active focus" — phase currently in flight (from the lifecycle spine)
  var activePhase = (project.phases || []).find(function(p) { return p.state === 'active'; });

  return (
    <div className="proj-ribbon">
      <div className={'proj-ribbon-card' + (nextDeliverable ? '' : ' empty')}>
        <div className="proj-ribbon-label">Next milestone</div>
        {nextDeliverable
          ? (
            <>
              <div className="proj-ribbon-value">{nextDeliverable.name}</div>
              <div className="proj-ribbon-context">
                {nextDeliverable.dueState === 'overdue'
                  ? <span className="proj-ribbon-warn">{nextDeliverable.daysOverdue}d overdue</span>
                  : nextDeliverable.due
                    ? (nextDeliverable.daysUntilDue === 0 ? 'Today' : nextDeliverable.daysUntilDue === 1 ? 'Tomorrow' : 'In ' + nextDeliverable.daysUntilDue + ' days')
                    : 'No date set'
                }
                {nextDeliverable.assignee && nextDeliverable.assignee.name && ' · ' + nextDeliverable.assignee.name}
              </div>
            </>
          )
          : <div className="proj-ribbon-value muted">No milestones set</div>
        }
      </div>

      <div className={'proj-ribbon-card' + (activePhase ? '' : ' empty')}>
        <div className="proj-ribbon-label">Active focus</div>
        {activePhase
          ? (
            <>
              <div className="proj-ribbon-value">{activePhase.name}</div>
              <div className="proj-ribbon-context">
                {activePhase.tasks.length} open
                {activePhase.dueSoon > 0 && ' · ' + activePhase.dueSoon + ' due soon'}
                {activePhase.overdue > 0 && <> · <span className="proj-ribbon-warn">{activePhase.overdue} overdue</span></>}
              </div>
            </>
          )
          : <div className="proj-ribbon-value muted">All phases complete</div>
        }
      </div>

      <div className={'proj-ribbon-card' + (risks > 0 ? ' alert' : dueSoon > 0 ? ' caution' : '')}>
        <div className="proj-ribbon-label">{risks > 0 ? 'Risks' : 'Outlook'}</div>
        {risks > 0
          ? (
            <>
              <div className="proj-ribbon-value">{risks} overdue</div>
              <div className="proj-ribbon-context">{dueSoon > 0 ? dueSoon + ' more due within 7 days' : 'No upcoming due dates'}</div>
            </>
          )
          : dueSoon > 0
            ? (
              <>
                <div className="proj-ribbon-value">{dueSoon} due soon</div>
                <div className="proj-ribbon-context">Within 7 days · no overdue items</div>
              </>
            )
            : (
              <>
                <div className="proj-ribbon-value">On track</div>
                <div className="proj-ribbon-context">No overdue or due-soon items</div>
              </>
            )
        }
      </div>
    </div>
  );
}

// Lifecycle spine — phase progression with embedded counts + state pill.
function ProjLifecycle({ phases }) {
  if (!phases || phases.length === 0) return null;
  return (
    <div className="proj-lifecycle">
      {phases.map(function(phase, idx) {
        var pct = phase.total > 0 ? Math.round((phase.completed / phase.total) * 100) : 0;
        var stateLabel = phase.state === 'complete' ? 'Complete' : phase.state === 'active' ? 'In progress' : 'Upcoming';
        return (
          <div key={phase.gid} className={'proj-phase proj-phase-' + phase.state}>
            <div className="proj-phase-head">
              <div className="proj-phase-marker">
                <span className="proj-phase-dot" />
                {idx < phases.length - 1 && <span className="proj-phase-line" />}
              </div>
              <div className="proj-phase-headline">
                <div className="proj-phase-name">
                  {phase.name}
                  <span className="proj-phase-state">{stateLabel}</span>
                </div>
                <div className="proj-phase-summary">
                  <span className="proj-phase-progress">{phase.completed} of {phase.total} done</span>
                  {phase.overdue > 0 && <span className="proj-phase-overdue">{phase.overdue} overdue</span>}
                  {phase.dueSoon > 0 && <span className="proj-phase-duesoon">{phase.dueSoon} due soon</span>}
                  <span className="proj-phase-pct">{pct}%</span>
                </div>
                <div className="proj-phase-track"><div className="proj-phase-track-fill" style={{ width: pct + '%' }} /></div>
              </div>
            </div>
            {(phase.tasks.length > 0 || (phase.state === 'complete' && phase.recentlyCompleted.length > 0)) && (
              <div className="proj-phase-body">
                {phase.tasks.map(function(t) { return <ProjTaskCard key={t.gid} task={t} />; })}
                {phase.state === 'complete' && phase.recentlyCompleted.length > 0 && (
                  <div className="proj-phase-recent">
                    <div className="proj-phase-recent-label">Recently completed</div>
                    {phase.recentlyCompleted.slice(0, 5).map(function(t) { return <ProjTaskCard key={t.gid} task={t} />; })}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Milestones strip — distinct visual treatment from regular tasks.
function ProjMilestonesStrip({ milestones }) {
  if (!milestones || milestones.length === 0) return null;
  return (
    <div className="proj-section">
      <div className="proj-section-head">Milestones</div>
      <div className="proj-milestones">
        {milestones.map(function(m) {
          var stateCls = m.completed ? 'done' : m.dueState === 'overdue' ? 'overdue' : m.dueState === 'due-soon' ? 'due-soon' : 'upcoming';
          var dateCopy = m.completed
            ? (m.completedAgo == null ? 'Completed' : m.completedAgo === 0 ? 'Completed today' : 'Completed ' + m.completedAgo + 'd ago')
            : m.dueState === 'overdue' ? m.daysOverdue + 'd overdue'
            : m.dueState === 'due-soon' ? (m.daysUntilDue === 0 ? 'Today' : 'In ' + m.daysUntilDue + 'd')
            : m.due ? 'In ' + (m.daysUntilDue == null ? '?' : m.daysUntilDue) + 'd'
            : 'No date';
          return (
            <div key={m.gid} className={'proj-milestone ' + stateCls}>
              <span className="proj-milestone-icon" aria-hidden="true">{m.completed ? '◆' : '◇'}</span>
              <div className="proj-milestone-text">
                <div className="proj-milestone-name">{m.name}</div>
                <div className="proj-milestone-meta">
                  <span>{dateCopy}</span>
                  {m.assignee && m.assignee.name && <><span className="proj-meta-sep">·</span><ProjAvatar name={m.assignee.name} email={m.assignee.email} size={18} /><span>{m.assignee.name}</span></>}
                </div>
              </div>
              {m.permalink && <a className="proj-task-link" href={m.permalink} target="_blank" rel="noopener noreferrer" title="Open in Asana">↗</a>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Stakeholder pile — avatars + open-task counts for each contributor.
function ProjStakeholders({ stakeholders, owner }) {
  if ((!stakeholders || stakeholders.length === 0) && !owner) return null;
  // Limit to first 8 stakeholders so the pile doesn't overflow on huge projects
  var shown = (stakeholders || []).slice(0, 8);
  var hidden = Math.max(0, (stakeholders || []).length - shown.length);
  return (
    <div className="proj-section">
      <div className="proj-section-head">Stakeholders</div>
      <div className="proj-stakeholders">
        {owner && owner.name && (
          <div className="proj-stake proj-stake-owner" title={owner.email ? owner.name + ' · ' + owner.email : owner.name}>
            <ProjAvatar name={owner.name} email={owner.email} size={32} />
            <div className="proj-stake-text">
              <div className="proj-stake-name">{owner.name}</div>
              <div className="proj-stake-role">Project owner</div>
            </div>
          </div>
        )}
        {shown.map(function(s) {
          // Skip duplicate of owner — owner is shown separately above
          if (owner && (s.email && owner.email ? s.email === owner.email : s.name === owner.name) && s.openCount === 0) return null;
          return (
            <div key={s.email || s.name} className="proj-stake" title={s.email ? s.name + ' · ' + s.email : s.name}>
              <ProjAvatar name={s.name} email={s.email} size={32} />
              <div className="proj-stake-text">
                <div className="proj-stake-name">{s.name}</div>
                <div className="proj-stake-role">
                  {s.openCount === 0 ? 'No open tasks' : s.openCount + ' open'}
                  {s.overdueCount > 0 && <span className="proj-stake-warn"> · {s.overdueCount} overdue</span>}
                </div>
              </div>
            </div>
          );
        })}
        {hidden > 0 && <div className="proj-stake-more">+{hidden} more</div>}
      </div>
    </div>
  );
}

// Activity feed — flattened comments + status changes from the project's
// most recently touched tasks. Each row is "Author · verb · task · time".
function ProjActivityFeed({ activity }) {
  if (!activity || activity.length === 0) return null;
  return (
    <div className="proj-section">
      <div className="proj-section-head">Recent activity <span className="proj-section-sub">last {activity.length} events across active tasks</span></div>
      <div className="proj-activity">
        {activity.map(function(ev) {
          var author = ev.author && ev.author.name;
          var ago = ev.ago == null ? null : ev.ago === 0 ? 'today' : ev.ago === 1 ? 'yesterday' : ev.ago + 'd ago';
          return (
            <div key={ev.gid} className={'proj-activity-row proj-activity-' + (ev.verb || 'event').replace(/\s+/g, '-')}>
              <ProjAvatar name={author} email={ev.author && ev.author.email} size={26} />
              <div className="proj-activity-text">
                <div className="proj-activity-headline">
                  <strong>{author || 'Someone'}</strong>
                  <span className="proj-activity-verb">{ev.verb || 'updated'}</span>
                  <span className="proj-activity-task">"{ev.taskName}"</span>
                </div>
                {ev.text && <div className="proj-activity-comment">{ev.text}</div>}
                {ago && <div className="proj-activity-time">{ago}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Status update block — formatted like a project briefing snippet
// rather than truncated freeform text. Quotes the status with author + age.
function ProjStatusBlock({ statusUpdate }) {
  if (!statusUpdate || !statusUpdate.text) return null;
  // Strip URLs + collapse runs of dashes/whitespace, keep first ~360 chars
  var clean = statusUpdate.text.replace(/https?:\/\/\S+/g, '').replace(/-{3,}/g, '').replace(/\s{2,}/g, ' ').trim();
  if (!clean) return null;
  var short = clean.length > 360 ? clean.slice(0, 360).replace(/\s+\S*$/, '') + '…' : clean;
  var ageCopy = statusUpdate.createdAgo == null
    ? null
    : statusUpdate.createdAgo === 0 ? 'today'
    : statusUpdate.createdAgo === 1 ? 'yesterday'
    : statusUpdate.createdAgo + ' days ago';
  var colorClass = statusUpdate.color === 'green' ? 'good' : statusUpdate.color === 'yellow' ? 'caution' : statusUpdate.color === 'red' ? 'alert' : 'info';
  return (
    <div className={'proj-status-block ' + colorClass}>
      <div className="proj-status-head">
        Latest update
        {(statusUpdate.author || ageCopy) && (
          <span className="proj-status-byline">
            {statusUpdate.author && <>by <strong>{statusUpdate.author}</strong></>}
            {statusUpdate.author && ageCopy && ' · '}
            {ageCopy}
          </span>
        )}
      </div>
      <div className="proj-status-text">"{short}"</div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Work / PM — CBG's internal project management workspace (Asana
// replacement). Client-independent, admin-only. Backend: /api/pm/*.
// Board columns are user-defined sections; tasks carry assignee /
// priority / due / tags / subtasks / comments. Drag cards between
// columns to move + reorder. See backend/pm.js.
// ════════════════════════════════════════════════════════════════════

// Accent palette for project chips + column tints. Keys are stored on the
// project; hex is resolved here so the DB stays theme-agnostic.
var PM_COLORS = {
  slate:   '#64748b', blue:  '#3b82f6', violet: '#8b5cf6', emerald: '#10b981',
  amber:   '#f59e0b', rose:  '#f43f5e', cyan:   '#06b6d4', pink:    '#ec4899',
  lime:    '#84cc16', orange:'#f97316'
};
var PM_COLOR_KEYS = ['slate','blue','violet','emerald','amber','rose','cyan','pink','lime','orange'];
function pmColor(key) { return PM_COLORS[key] || PM_COLORS.slate; }
var PM_ICONS = ['📋','🚀','🛡️','🔧','🖥️','🔐','📡','📊','🧩','🗂️','⚙️','🧭','🏗️','💡','📁'];

// Priority metadata — label, dot color, sort rank (higher = more urgent).
var PM_PRIORITY = {
  none:   { label: 'No priority', color: '#64748b', rank: 0 },
  low:    { label: 'Low',         color: '#38bdf8', rank: 1 },
  medium: { label: 'Medium',      color: '#f59e0b', rank: 2 },
  high:   { label: 'High',        color: '#f97316', rank: 3 },
  urgent: { label: 'Urgent',      color: '#f43f5e', rank: 4 }
};
var PM_PRIORITY_ORDER = ['none','low','medium','high','urgent'];

// Human-friendly due date + urgency class.
function pmDueMeta(dateStr, completed) {
  if (!dateStr) return null;
  var d = new Date(dateStr + (dateStr.length <= 10 ? 'T00:00:00' : ''));
  if (isNaN(d.getTime())) return null;
  var today = new Date(); today.setHours(0, 0, 0, 0);
  var due = new Date(d); due.setHours(0, 0, 0, 0);
  var days = Math.round((due - today) / 86400000);
  var label;
  if (days === 0) label = 'Today';
  else if (days === 1) label = 'Tomorrow';
  else if (days === -1) label = 'Yesterday';
  else if (days > 1 && days <= 6) label = due.toLocaleDateString(undefined, { weekday: 'short' });
  else label = due.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  var tone = 'normal';
  if (!completed) { if (days < 0) tone = 'overdue'; else if (days === 0) tone = 'today'; else if (days === 1) tone = 'soon'; }
  return { label: label, tone: tone, days: days };
}

// Relative timestamp for the activity feed ("just now", "3 hours ago", "Aug 1").
function pmTimeAgo(iso) {
  if (!iso) return '';
  var then = new Date(iso).getTime();
  if (isNaN(then)) return '';
  var s = Math.floor((Date.now() - then) / 1000);
  if (s < 45) return 'just now';
  if (s < 90) return '1 min ago';
  var m = Math.floor(s / 60); if (m < 60) return m + ' min ago';
  var h = Math.floor(m / 60); if (h < 24) return h + (h === 1 ? ' hour ago' : ' hours ago');
  var d = Math.floor(h / 24); if (d === 1) return 'yesterday';
  if (d < 7) return d + ' days ago';
  var dt = new Date(iso);
  return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + (dt.getFullYear() !== new Date().getFullYear() ? ', ' + dt.getFullYear() : '');
}
function pmPrettyDate(ds) {
  if (!ds) return '';
  var dt = new Date(ds + 'T00:00:00');
  if (isNaN(dt.getTime())) return ds;
  return dt.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}
function pmFileSize(bytes) {
  if (bytes == null) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}
// ── Lightweight, XSS-safe markdown (bold/italic/strike/code/links/lists/
// quotes/headings). We build React elements from substrings, so all text is
// escaped by React; only whitelisted URL schemes become links. Mentions
// (@name) are rendered as chips (resolved against the members list). ────
function pmSafeUrl(u) {
  if (/^(https?:|mailto:)/i.test(u)) return u;
  if (/^www\./i.test(u)) return 'https://' + u;
  return null;
}
function pmInline(text, members) {
  var out = []; var key = 0;
  var re = /`([^`]+)`|\*\*([^*]+)\*\*|__([^_]+)__|\*([^*\n]+)\*|_([^_\n]+)_|~~([^~]+)~~|\[([^\]]+)\]\(([^)\s]+)\)|@\[([^\]]+)\]/g;
  var m, last = 0;
  function push(node) { out.push(node); }
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) push(<React.Fragment key={key++}>{text.slice(last, m.index)}</React.Fragment>);
    if (m[1] != null) push(<code key={key++} className="pm-md-code">{m[1]}</code>);
    else if (m[2] != null) push(<strong key={key++}>{m[2]}</strong>);
    else if (m[3] != null) push(<strong key={key++}>{m[3]}</strong>);
    else if (m[4] != null) push(<em key={key++}>{m[4]}</em>);
    else if (m[5] != null) push(<em key={key++}>{m[5]}</em>);
    else if (m[6] != null) push(<del key={key++}>{m[6]}</del>);
    else if (m[7] != null) { var href = pmSafeUrl(m[8]); push(href ? <a key={key++} href={href} target="_blank" rel="noopener noreferrer">{m[7]}</a> : <React.Fragment key={key++}>{m[0]}</React.Fragment>); }
    else if (m[9] != null) {
      var nm = m[9].trim();
      var matched = members && members.find(function(mm) { return mm.name && mm.name.toLowerCase() === nm.toLowerCase(); });
      push(<span key={key++} className={'pm-mention' + (matched ? '' : ' pm-mention-unknown')}>@{matched ? matched.name : nm}</span>);
    }
    last = re.lastIndex;
  }
  if (last < text.length) push(<React.Fragment key={key++}>{text.slice(last)}</React.Fragment>);
  return out;
}
function PmMarkdown({ text, members }) {
  if (!text) return null;
  var lines = String(text).replace(/\r\n/g, '\n').split('\n');
  var blocks = []; var i = 0; var key = 0;
  function blockStart(l) { return /^```/.test(l) || /^(#{1,3})\s/.test(l) || /^\s*>\s?/.test(l) || /^\s*[-*]\s+/.test(l) || /^\s*\d+\.\s+/.test(l); }
  while (i < lines.length) {
    var line = lines[i];
    if (/^```/.test(line)) { var buf = []; i++; while (i < lines.length && !/^```/.test(lines[i])) { buf.push(lines[i]); i++; } i++; blocks.push(<pre key={key++} className="pm-md-pre"><code>{buf.join('\n')}</code></pre>); continue; }
    if (/^\s*$/.test(line)) { i++; continue; }
    var h = line.match(/^(#{1,3})\s+(.*)$/);
    if (h) { var Tag = 'h' + (h[1].length + 2); blocks.push(<Tag key={key++} className="pm-md-h">{pmInline(h[2], members)}</Tag>); i++; continue; }
    if (/^\s*>\s?/.test(line)) { var qb = []; while (i < lines.length && /^\s*>\s?/.test(lines[i])) { qb.push(lines[i].replace(/^\s*>\s?/, '')); i++; } blocks.push(<blockquote key={key++} className="pm-md-quote">{pmInline(qb.join('\n'), members)}</blockquote>); continue; }
    if (/^\s*[-*]\s+/.test(line)) { var items = []; while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) { items.push(lines[i].replace(/^\s*[-*]\s+/, '')); i++; } blocks.push(<ul key={key++} className="pm-md-ul">{items.map(function(it, ix) { return <li key={ix}>{pmInline(it, members)}</li>; })}</ul>); continue; }
    if (/^\s*\d+\.\s+/.test(line)) { var oitems = []; while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) { oitems.push(lines[i].replace(/^\s*\d+\.\s+/, '')); i++; } blocks.push(<ol key={key++} className="pm-md-ol">{oitems.map(function(it, ix) { return <li key={ix}>{pmInline(it, members)}</li>; })}</ol>); continue; }
    var para = []; while (i < lines.length && !/^\s*$/.test(lines[i]) && !blockStart(lines[i])) { para.push(lines[i]); i++; }
    var pnodes = []; para.forEach(function(pl, ix) { if (ix > 0) pnodes.push(<br key={'br' + ix} />); pmInline(pl, members).forEach(function(n) { pnodes.push(n); }); });
    blocks.push(<p key={key++} className="pm-md-p">{pnodes}</p>);
  }
  return <div className="pm-md">{blocks}</div>;
}

// Rich-text editor: a textarea with a markdown toolbar (bold/italic/strike/
// code/link/list/quote) + keyboard shortcuts. Stores markdown; PmMarkdown
// renders it. Mention autocomplete is layered on in PmRichEditor via members.
function PmRichEditor({ value, onChange, onBlur, onSubmit, placeholder, rows, autoFocus, members, compact }) {
  var ref = useRef(null);
  var [mention, setMention] = useState(null); // { query, start, matches }

  function selInfo() { var ta = ref.current; return { ta: ta, s: ta ? ta.selectionStart : 0, e: ta ? ta.selectionEnd : 0, v: value || '' }; }
  function restore(ns, ne) { setTimeout(function() { var ta = ref.current; if (ta) { ta.focus(); ta.setSelectionRange(ns, ne == null ? ns : ne); } }, 0); }
  function wrap(pre, post) {
    var x = selInfo(); if (!x.ta) return;
    var sel = x.v.slice(x.s, x.e);
    onChange(x.v.slice(0, x.s) + pre + sel + post + x.v.slice(x.e));
    restore(x.s + pre.length, x.e + pre.length);
  }
  function linePrefix(prefix) {
    var x = selInfo(); if (!x.ta) return;
    var lineStart = x.v.lastIndexOf('\n', x.s - 1) + 1;
    var block = x.v.slice(lineStart, x.e);
    var prefixed = block.split('\n').map(function(l) { return prefix + l; }).join('\n');
    onChange(x.v.slice(0, lineStart) + prefixed + x.v.slice(x.e));
    restore(lineStart + prefixed.length);
  }
  function insertLink() {
    var x = selInfo(); if (!x.ta) return;
    var url = window.prompt('Link URL:', 'https://');
    if (!url) return;
    var sel = x.v.slice(x.s, x.e) || 'link';
    var ins = '[' + sel + '](' + url + ')';
    onChange(x.v.slice(0, x.s) + ins + x.v.slice(x.e));
    restore(x.s + ins.length);
  }

  // Mention autocomplete: detect "@word" immediately before the caret.
  function onChangeText(e) {
    var v = e.target.value; onChange(v);
    var caret = e.target.selectionStart;
    var upto = v.slice(0, caret);
    var mm = upto.match(/@([A-Za-z][\w.\- ]{0,30})$/);
    if (mm && members && members.length) {
      var q = mm[1].toLowerCase();
      var matches = members.filter(function(x2) { return (x2.name || '').toLowerCase().indexOf(q) !== -1; }).slice(0, 6);
      setMention(matches.length ? { start: caret - mm[0].length, query: mm[1], matches: matches } : null);
    } else setMention(null);
  }
  function pickMention(mem) {
    var x = selInfo(); if (!x.ta || !mention) return;
    var ins = '@[' + mem.name + '] ';
    var nv = x.v.slice(0, mention.start) + ins + x.v.slice(x.e);
    onChange(nv); setMention(null); restore(mention.start + ins.length);
  }
  function onKeyDown(e) {
    if (mention && (e.key === 'Enter' || e.key === 'Tab')) { e.preventDefault(); pickMention(mention.matches[0]); return; }
    if (mention && e.key === 'Escape') { setMention(null); return; }
    var mod = e.metaKey || e.ctrlKey;
    if (mod && (e.key === 'b' || e.key === 'B')) { e.preventDefault(); wrap('**', '**'); }
    else if (mod && (e.key === 'i' || e.key === 'I')) { e.preventDefault(); wrap('*', '*'); }
    else if (mod && e.key === 'Enter' && onSubmit) { e.preventDefault(); onSubmit(); }
  }
  function tb(label, title, fn, cls) {
    return <button type="button" className={'pm-rte-btn' + (cls ? ' ' + cls : '')} title={title} onMouseDown={function(e) { e.preventDefault(); }} onClick={fn}>{label}</button>;
  }

  return (
    <div className={'pm-rte' + (compact ? ' pm-rte-compact' : '')}>
      <div className="pm-rte-toolbar">
        {tb(<b>B</b>, 'Bold (Ctrl+B)', function() { wrap('**', '**'); })}
        {tb(<i>I</i>, 'Italic (Ctrl+I)', function() { wrap('*', '*'); })}
        {tb(<s>S</s>, 'Strikethrough', function() { wrap('~~', '~~'); })}
        {tb(<span style={{ fontFamily: 'monospace' }}>{'<>'}</span>, 'Code', function() { wrap('`', '`'); })}
        {tb('🔗', 'Link', insertLink)}
        {tb('• List', 'Bulleted list', function() { linePrefix('- '); })}
        {tb('1. List', 'Numbered list', function() { linePrefix('1. '); })}
        {tb('❝', 'Quote', function() { linePrefix('> '); })}
      </div>
      <div className="pm-rte-wrap">
        <textarea ref={ref} className="pm-rte-textarea" value={value || ''} placeholder={placeholder} rows={rows || 4} autoFocus={autoFocus}
          onChange={onChangeText} onKeyDown={onKeyDown} onBlur={function(e) { setTimeout(function() { setMention(null); }, 150); if (onBlur) onBlur(e); }} />
        {mention && (
          <div className="pm-mention-pop">
            {mention.matches.map(function(mem) {
              return <button key={mem.id} className="pm-menu-item" onMouseDown={function(e) { e.preventDefault(); }} onClick={function() { pickMention(mem); }}>
                <PmAvatar member={mem} size={20} /> {mem.name}
              </button>;
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// Human label for a system activity event (comments render separately).
function pmActivityText(a) {
  var d = a.data || {};
  switch (a.type) {
    case 'created':     return d.subtask ? 'created this subtask' : 'created this task';
    case 'completed':   return 'completed this task';
    case 'reopened':    return 'marked incomplete';
    case 'assigned':    return 'assigned this to ' + (d.name || 'someone');
    case 'unassigned':  return 'removed the assignee';
    case 'due_set':     return 'set the due date to ' + pmPrettyDate(d.date);
    case 'due_cleared': return 'cleared the due date';
    case 'priority':    return 'set priority to ' + ((PM_PRIORITY[d.to] || {}).label || d.to);
    case 'moved':       return 'moved this to ' + (d.name || 'a section');
    case 'renamed':     return 'renamed this task';
    case 'attached':    return 'attached ' + (d.name || 'a file');
    case 'unattached':  return 'removed ' + (d.name || 'an attachment');
    default:            return a.type;
  }
}

function pmInitials(name, email) {
  var s = (name || '').trim();
  if (s) { var p = s.split(/\s+/); return (p.length === 1 ? p[0].slice(0, 2) : (p[0][0] + p[p.length - 1][0])).toUpperCase(); }
  var e = (email || '').trim();
  return e ? e.slice(0, 2).toUpperCase() : '?';
}
// Deterministic avatar tint from a member id.
var PM_AVATAR_TINTS = ['#3b82f6','#8b5cf6','#10b981','#f59e0b','#f43f5e','#06b6d4','#ec4899','#84cc16'];
function pmAvatarTint(id) { return PM_AVATAR_TINTS[(parseInt(id, 10) || 0) % PM_AVATAR_TINTS.length]; }

// Work's line-icon set. One coherent system (18px grid, 1.5px stroke,
// currentColor) — replaces the emoji chrome that read as "generated".
function PmIcon(props) {
  var name = props.name, size = props.size || 16;
  var c = { width: size, height: size, viewBox: '0 0 18 18', fill: 'none', stroke: 'currentColor', strokeWidth: props.weight || 1.5, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true, className: 'pm-icon' };
  switch (name) {
    case 'check':     return <svg {...c}><polyline points="3.8,9.4 7.2,12.8 14.2,5.2" /></svg>;
    case 'flag':      return <svg {...c}><path d="M5 2.5 V15.5" /><path d="M5 3.4 H13.2 L11 6.2 L13.2 9 H5" /></svg>;
    case 'calendar':  return <svg {...c}><rect x="2.8" y="3.8" width="12.4" height="11.4" rx="1.6" /><line x1="2.8" y1="7" x2="15.2" y2="7" /><line x1="6" y1="2.3" x2="6" y2="4.8" /><line x1="12" y1="2.3" x2="12" y2="4.8" /></svg>;
    case 'clip':      return <svg {...c}><path d="M12.5 6.3 L7.1 11.7 C6 12.8 4.3 12.8 3.2 11.7 C2.1 10.6 2.1 8.9 3.2 7.8 L9 2 C9.7 1.3 10.9 1.3 11.6 2 C12.3 2.7 12.3 3.9 11.6 4.6 L6 10.2 C5.65 10.55 5.1 10.55 4.75 10.2 C4.4 9.85 4.4 9.3 4.75 8.95 L9.8 3.9" /></svg>;
    case 'subtask':   return <svg {...c}><path d="M3.6 4.6 L5 6 L7.6 3.4" /><line x1="9.4" y1="4.8" x2="14.4" y2="4.8" /><path d="M3.6 11.6 L5 13 L7.6 10.4" /><line x1="9.4" y1="11.8" x2="14.4" y2="11.8" /></svg>;
    case 'comment':   return <svg {...c}><path d="M3 4.4 H15 V11.6 H8.4 L5.4 14.4 V11.6 H3 Z" /></svg>;
    case 'user':      return <svg {...c}><circle cx="9" cy="6.4" r="2.7" /><path d="M3.6 15 C3.6 12 6 10.2 9 10.2 C12 10.2 14.4 12 14.4 15" /></svg>;
    case 'users':     return <svg {...c}><circle cx="7" cy="6.6" r="2.3" /><path d="M2.6 14.6 C2.6 12 4.6 10.6 7 10.6 C9.4 10.6 11.4 12 11.4 14.6" /><path d="M11.4 5 C12.7 5 13.6 6 13.6 7.2 C13.6 8.3 12.8 9.2 11.7 9.4" /><path d="M12.4 10.8 C14.2 11 15.6 12.2 15.6 14.2" /></svg>;
    case 'bell':      return <svg {...c}><path d="M6 8.4 C6 5.9 7.3 4.3 9 4.3 C10.7 4.3 12 5.9 12 8.4 C12 11.2 13.4 12.2 13.4 12.2 H4.6 C4.6 12.2 6 11.2 6 8.4 Z" /><path d="M7.6 12.2 C7.6 13.2 8.2 13.9 9 13.9 C9.8 13.9 10.4 13.2 10.4 12.2" /><line x1="9" y1="2.6" x2="9" y2="4.3" /></svg>;
    case 'search':    return <svg {...c}><circle cx="7.8" cy="7.8" r="4.4" /><line x1="11.2" y1="11.2" x2="15" y2="15" /></svg>;
    case 'grip':      return <svg {...c} strokeWidth="0" fill="currentColor"><circle cx="6.5" cy="4.5" r="1.15" /><circle cx="11.5" cy="4.5" r="1.15" /><circle cx="6.5" cy="9" r="1.15" /><circle cx="11.5" cy="9" r="1.15" /><circle cx="6.5" cy="13.5" r="1.15" /><circle cx="11.5" cy="13.5" r="1.15" /></svg>;
    case 'kebab':     return <svg {...c} strokeWidth="0" fill="currentColor"><circle cx="9" cy="4" r="1.3" /><circle cx="9" cy="9" r="1.3" /><circle cx="9" cy="14" r="1.3" /></svg>;
    case 'chevron-down': return <svg {...c}><polyline points="5,7 9,11 13,7" /></svg>;
    case 'sparkle':   return <svg {...c}><path d="M9 2.5 C9.5 5.5 10.5 6.5 13.5 7 C10.5 7.5 9.5 8.5 9 11.5 C8.5 8.5 7.5 7.5 4.5 7 C7.5 6.5 8.5 5.5 9 2.5 Z" /><path d="M13.5 11 C13.7 12.3 14.1 12.7 15.4 13 C14.1 13.3 13.7 13.7 13.5 15 C13.3 13.7 12.9 13.3 11.6 13 C12.9 12.7 13.3 12.3 13.5 11 Z" /></svg>;
    case 'repeat':    return <svg {...c}><path d="M4 8 V7 C4 5.3 5.3 4 7 4 H12" /><polyline points="10,2 12.5,4 10,6" /><path d="M14 10 V11 C14 12.7 12.7 14 11 14 H6" /><polyline points="8,16 5.5,14 8,12" /></svg>;
    case 'shield':    return <svg {...c}><path d="M9 2.4 L14.5 4.4 V8.6 C14.5 12 12 14.6 9 15.6 C6 14.6 3.5 12 3.5 8.6 V4.4 Z" /><polyline points="6.6,8.8 8.3,10.5 11.5,7" /></svg>;
    case 'plus':      return <svg {...c}><line x1="9" y1="4" x2="9" y2="14" /><line x1="4" y1="9" x2="14" y2="9" /></svg>;
    case 'x':         return <svg {...c}><line x1="5" y1="5" x2="13" y2="13" /><line x1="13" y1="5" x2="5" y2="13" /></svg>;
    case 'link':      return <svg {...c}><path d="M7.5 10.5 L10.5 7.5" /><path d="M8 5.5 L9.5 4 C10.7 2.8 12.6 2.8 13.8 4 C15 5.2 15 7.1 13.8 8.3 L12.3 9.8" /><path d="M10 12.5 L8.5 14 C7.3 15.2 5.4 15.2 4.2 14 C3 12.8 3 10.9 4.2 9.7 L5.7 8.2" /></svg>;
    case 'clock':     return <svg {...c}><circle cx="9" cy="9" r="6.4" /><polyline points="9,5.4 9,9 11.6,10.4" /></svg>;
    case 'play':      return <svg {...c} strokeWidth="0" fill="currentColor"><path d="M5.5 3.8 L13.5 9 L5.5 14.2 Z" /></svg>;
    case 'stop':      return <svg {...c} strokeWidth="0" fill="currentColor"><rect x="4.6" y="4.6" width="8.8" height="8.8" rx="1.6" /></svg>;
    case 'pause':     return <svg {...c} strokeWidth="0" fill="currentColor"><rect x="5" y="4.4" width="2.6" height="9.2" rx="1" /><rect x="10.4" y="4.4" width="2.6" height="9.2" rx="1" /></svg>;
    case 'ticket':    return <svg {...c}><path d="M3 6 C3 5.2 3.6 4.6 4.4 4.6 H13.6 C14.4 4.6 15 5.2 15 6 V7 A1.4 1.4 0 0 0 15 9.8 V12 C15 12.8 14.4 13.4 13.6 13.4 H4.4 C3.6 13.4 3 12.8 3 12 V9.8 A1.4 1.4 0 0 0 3 7 Z" /><line x1="9" y1="5.4" x2="9" y2="12.6" strokeDasharray="1.4 1.6" /></svg>;
    case 'dollar':    return <svg {...c}><line x1="9" y1="2.6" x2="9" y2="15.4" /><path d="M12 5.4 C12 4.2 10.7 3.4 9 3.4 C7.3 3.4 6 4.2 6 5.6 C6 9 12 7.6 12 11.2 C12 12.8 10.7 13.6 9 13.6 C7.3 13.6 6 12.8 6 11.4" /></svg>;
    case 'inbox':     return <svg {...c}><path d="M2.8 9.4 L4.8 4 H13.2 L15.2 9.4 V13.6 A1 1 0 0 1 14.2 14.6 H3.8 A1 1 0 0 1 2.8 13.6 Z" /><path d="M2.8 9.4 H6.2 A1 1 0 0 1 7.2 10.4 A1 1 0 0 0 8.2 11.4 H9.8 A1 1 0 0 0 10.8 10.4 A1 1 0 0 1 11.8 9.4 H15.2" /></svg>;
    case 'building':  return <svg {...c}><rect x="4" y="2.8" width="10" height="12.4" rx="1.2" /><line x1="6.4" y1="5.4" x2="7.8" y2="5.4" /><line x1="10.2" y1="5.4" x2="11.6" y2="5.4" /><line x1="6.4" y1="8" x2="7.8" y2="8" /><line x1="10.2" y1="8" x2="11.6" y2="8" /><path d="M7.6 15.2 V12 H10.4 V15.2" /></svg>;
    // file-type glyphs (a page silhouette + a differentiating mark)
    case 'file':      return <svg {...c}><path d="M5 2.4 H10.4 L14 6 V15.6 H5 Z" /><polyline points="10.4,2.4 10.4,6 14,6" /></svg>;
    case 'file-image':return <svg {...c}><path d="M5 2.4 H10.4 L14 6 V15.6 H5 Z" /><polyline points="10.4,2.4 10.4,6 14,6" /><circle cx="7.4" cy="9.4" r="0.9" /><path d="M6 13.2 L8.4 10.8 L10 12.4 L11.4 11 L13 12.8" /></svg>;
    case 'file-pdf':  return <svg {...c}><path d="M5 2.4 H10.4 L14 6 V15.6 H5 Z" /><polyline points="10.4,2.4 10.4,6 14,6" /><path d="M6.6 12.6 V9.4 H7.7 C8.4 9.4 8.4 10.6 7.7 10.6 H6.6" /></svg>;
    case 'file-sheet':return <svg {...c}><path d="M5 2.4 H10.4 L14 6 V15.6 H5 Z" /><polyline points="10.4,2.4 10.4,6 14,6" /><line x1="6.4" y1="10.4" x2="12" y2="10.4" /><line x1="9.2" y1="9" x2="9.2" y2="13.6" /></svg>;
    default:          return <svg {...c}><circle cx="9" cy="9" r="3" /></svg>;
  }
}
function PmFileIcon({ ct, name }) {
  var n = (name || '').toLowerCase();
  if (/^image\//.test(ct || '')) return <PmIcon name="file-image" size={18} />;
  if (/pdf/.test(ct || '') || /\.pdf$/.test(n)) return <PmIcon name="file-pdf" size={18} />;
  if (/(sheet|excel|csv)/.test(ct || '') || /\.(xlsx?|csv)$/.test(n)) return <PmIcon name="file-sheet" size={18} />;
  return <PmIcon name="file" size={18} />;
}

// Custom line illustrations for empty states (no emoji).
function PmEmptyArt({ kind }) {
  if (kind === 'done') {
    return (
      <svg className="pm-empty-art" viewBox="0 0 120 96" fill="none" aria-hidden="true">
        <rect x="26" y="12" width="68" height="82" rx="9" className="pm-ea-panel" />
        <line x1="38" y1="32" x2="82" y2="32" className="pm-ea-line" />
        <line x1="38" y1="46" x2="72" y2="46" className="pm-ea-line" />
        <line x1="38" y1="60" x2="78" y2="60" className="pm-ea-line" />
        <circle cx="88" cy="72" r="17" className="pm-ea-accent-fill" />
        <polyline points="80,72 86,78 96,66" className="pm-ea-check" />
      </svg>
    );
  }
  return (
    <svg className="pm-empty-art" viewBox="0 0 132 96" fill="none" aria-hidden="true">
      <rect x="8" y="16" width="35" height="74" rx="8" className="pm-ea-panel" />
      <rect x="49" y="16" width="35" height="74" rx="8" className="pm-ea-panel" />
      <rect x="90" y="16" width="35" height="74" rx="8" className="pm-ea-panel" />
      <rect x="14" y="26" width="23" height="13" rx="3.5" className="pm-ea-card" />
      <rect x="14" y="45" width="23" height="13" rx="3.5" className="pm-ea-card" />
      <rect x="55" y="26" width="23" height="13" rx="3.5" className="pm-ea-accent" />
      <rect x="55" y="45" width="23" height="13" rx="3.5" className="pm-ea-card" />
      <rect x="96" y="26" width="23" height="13" rx="3.5" className="pm-ea-card" />
    </svg>
  );
}

function PmAvatar({ member, size }) {
  var s = size || 24;
  if (!member) {
    return <span className="pm-avatar pm-avatar-empty" style={{ width: s, height: s, fontSize: s * 0.42 }} title="Unassigned">–</span>;
  }
  return <span className="pm-avatar" style={{ width: s, height: s, fontSize: s * 0.4, background: pmAvatarTint(member.id) }} title={member.name || member.email}>{member.initials || pmInitials(member.name, member.email)}</span>;
}

// Small dropdown used for assignee + priority. Closes on outside click.
// Dropdown menu rendered through a PORTAL with fixed, viewport-aware
// positioning — so it's never clipped by the drawer's scroll container or
// pushed off a screen edge (it flips up / clamps horizontally as needed).
function PmMenu({ trigger, children, align, up }) {
  var [open, setOpen] = useState(false);
  var [anchor, setAnchor] = useState(null);   // trigger rect snapshot
  var [style, setStyle] = useState(null);      // computed fixed position
  var wrapRef = useRef(null);
  var menuRef = useRef(null);

  function toggle(e) {
    e.stopPropagation();
    if (open) { setOpen(false); return; }
    var r = wrapRef.current.getBoundingClientRect();
    setAnchor({ top: r.bottom, bottom: r.top, left: r.left, right: r.right });
    setStyle(null);
    setOpen(true);
  }
  function close() { setOpen(false); }

  useEffect(function() {
    if (!open) return;
    function onDoc(e) {
      if ((menuRef.current && menuRef.current.contains(e.target)) || (wrapRef.current && wrapRef.current.contains(e.target))) return;
      setOpen(false);
    }
    function onScroll(e) { if (menuRef.current && e && e.target && e.target.nodeType && menuRef.current.contains(e.target)) return; setOpen(false); }
    function onResize() { setOpen(false); }
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return function() { document.removeEventListener('mousedown', onDoc); window.removeEventListener('scroll', onScroll, true); window.removeEventListener('resize', onResize); };
  }, [open]);

  useEffect(function() {
    if (!open || !anchor || !menuRef.current) return;
    function place() {
      if (!menuRef.current) return;
      var m = menuRef.current.getBoundingClientRect();
      var vw = window.innerWidth, vh = window.innerHeight, pad = 8;
      var left = align === 'right' ? (anchor.right - m.width) : anchor.left;
      if (left + m.width > vw - pad) left = vw - pad - m.width;
      if (left < pad) left = pad;
      var top = anchor.top + 4;
      if (top + m.height > vh - pad) { var upTop = anchor.bottom - 4 - m.height; top = upTop >= pad ? upTop : Math.max(pad, vh - pad - m.height); }
      setStyle({ position: 'fixed', top: top, left: left, visibility: 'visible' });
    }
    place();
    var ro = null;
    if (window.ResizeObserver) { ro = new window.ResizeObserver(place); ro.observe(menuRef.current); }
    return function() { if (ro) ro.disconnect(); };
  }, [open, anchor]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <span className="pm-menu-wrap" ref={wrapRef}>
      <span onClick={toggle}>{trigger}</span>
      {open && anchor && createPortal(
        <div ref={menuRef} className="pm-menu pm-menu-portal" onClick={function(e) { e.stopPropagation(); }}
          style={style || { position: 'fixed', top: anchor.top + 4, left: anchor.left, visibility: 'hidden' }}>
          {typeof children === 'function' ? children(close) : children}
        </div>, document.body)}
    </span>
  );
}

// Confirmation dialog — the delete safeguard used everywhere in Work. Focuses
// Cancel by default and only Escape is bound, so a stray Enter never deletes.
function PmConfirm({ title, message, confirmLabel, danger, onConfirm, onCancel }) {
  var cancelRef = useRef(null);
  useEffect(function() {
    if (cancelRef.current) cancelRef.current.focus();
    function onKey(e) { if (e.key === 'Escape') { e.preventDefault(); onCancel(); } }
    document.addEventListener('keydown', onKey);
    return function() { document.removeEventListener('keydown', onKey); };
  }, []);
  return (
    <div className="pm-confirm-overlay" onClick={onCancel}>
      <div className="pm-confirm" onClick={function(e) { e.stopPropagation(); }} role="alertdialog" aria-label={title}>
        <div className="pm-confirm-title">{title}</div>
        {message && <div className="pm-confirm-msg">{message}</div>}
        <div className="pm-confirm-actions">
          <button className="pm-ghost-btn" ref={cancelRef} onClick={onCancel}>Cancel</button>
          <button className={'pm-primary-btn' + (danger ? ' pm-danger-btn' : '')} onClick={onConfirm}>{confirmLabel || 'Delete'}</button>
        </div>
      </div>
    </div>
  );
}

function PmPriorityPill({ priority, onChange }) {
  var meta = PM_PRIORITY[priority] || PM_PRIORITY.none;
  var pill = (
    <span className={'pm-prio-pill' + (priority === 'none' ? ' pm-prio-none' : '')} style={priority === 'none' ? {} : { color: meta.color }}>
      <span className="pm-prio-flag" style={{ color: priority === 'none' ? 'var(--text-muted)' : meta.color }}><PmIcon name="flag" size={13} /></span>
      {priority === 'none' ? 'Priority' : meta.label}
    </span>
  );
  if (!onChange) return pill;
  return (
    <PmMenu trigger={pill}>
      {function(close) { return PM_PRIORITY_ORDER.map(function(p) {
        var m = PM_PRIORITY[p];
        return <button key={p} className="pm-menu-item" onClick={function() { onChange(p); close(); }}>
          <span className="pm-prio-dot" style={{ background: m.color, opacity: p === 'none' ? 0.4 : 1 }} />{m.label}
          {priority === p && <span className="pm-menu-check">✓</span>}
        </button>;
      }); }}
    </PmMenu>
  );
}

function PmAssignee({ assigneeId, members, onChange, size }) {
  var member = members.find(function(m) { return m.id === assigneeId; });
  var trigger = <span className="pm-assignee-trigger"><PmAvatar member={member} size={size || 24} /></span>;
  if (!onChange) return trigger;
  return (
    <PmMenu trigger={trigger} align="right">
      {function(close) { return (
        <div className="pm-assignee-list">
          <div className="pm-menu-label">Assignee</div>
          <button className="pm-menu-item" onClick={function() { onChange(null); close(); }}>
            <PmAvatar member={null} size={22} /> Unassigned {!assigneeId && <span className="pm-menu-check">✓</span>}
          </button>
          {members.map(function(m) {
            return <button key={m.id} className="pm-menu-item" onClick={function() { onChange(m.id); close(); }}>
              <PmAvatar member={m} size={22} /> {m.name} {assigneeId === m.id && <span className="pm-menu-check">✓</span>}
            </button>;
          })}
        </div>
      ); }}
    </PmMenu>
  );
}

function pmNotifText(n) {
  switch (n.kind) {
    case 'mentioned': return 'mentioned you' + (n.data && n.data.via === 'comment' ? ' in a comment' : n.data && n.data.via === 'description' ? ' in a description' : '');
    case 'assigned':  return 'assigned you a task';
    case 'commented': return 'commented on a task you follow';
    default:          return n.kind;
  }
}

function WorkNotifications({ onOpenTask }) {
  var [open, setOpen] = useState(false);
  var [unread, setUnread] = useState(0);
  var [items, setItems] = useState(null);
  var ref = useRef(null);

  function loadCount() { apiGet('/pm/notifications/count').then(function(r) { if (r.ok) setUnread(r.data.unread || 0); }); }
  function loadList() { apiGet('/pm/notifications').then(function(r) { if (r.ok) { setItems(r.data.notifications || []); setUnread(r.data.unread || 0); } }); }
  useEffect(function() { loadCount(); var t = setInterval(loadCount, 60000); return function() { clearInterval(t); }; }, []);
  useEffect(function() {
    if (!open) return;
    loadList();
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onDoc);
    return function() { document.removeEventListener('mousedown', onDoc); };
  }, [open]);

  function markAll() { apiSend('POST', '/pm/notifications/read', {}).then(function() { setUnread(0); setItems(function(list) { return (list || []).map(function(n) { return Object.assign({}, n, { read: true }); }); }); }); }
  function openItem(n) {
    apiSend('POST', '/pm/notifications/read', { ids: [n.id] });
    setUnread(function(u) { return Math.max(0, u - (n.read ? 0 : 1)); });
    setOpen(false);
    if (n.projectId) onOpenTask(n.projectId, n.taskId);
  }

  return (
    <span className="pm-notif-wrap" ref={ref}>
      <button className="pm-notif-bell" onClick={function() { setOpen(!open); }} title="Notifications">
        <PmIcon name="bell" size={17} />{unread > 0 && <span className="pm-notif-badge">{unread > 9 ? '9+' : unread}</span>}
      </button>
      {open && (
        <div className="pm-notif-panel">
          <div className="pm-notif-head"><span>Notifications</span>{unread > 0 && <button className="pm-notif-markall" onClick={markAll}>Mark all read</button>}</div>
          <div className="pm-notif-list">
            {!items ? <div className="pm-notif-empty">Loading…</div> : items.length === 0 ? <div className="pm-notif-empty">You're all caught up.</div> : items.map(function(n) {
              return (
                <button key={n.id} className={'pm-notif-item' + (n.read ? '' : ' unread')} onClick={function() { openItem(n); }}>
                  <PmAvatar member={n.actor} size={28} />
                  <div className="pm-notif-body">
                    <div className="pm-notif-text"><span className="pm-activity-who">{n.actor ? n.actor.name : 'Someone'}</span> {pmNotifText(n)}</div>
                    <div className="pm-notif-sub">{n.taskTitle ? '“' + n.taskTitle + '”' : ''} {n.projectName && <span style={{ color: pmColor(n.projectColor) }}>· {n.projectIcon} {n.projectName}</span>}</div>
                    <div className="pm-notif-time">{pmTimeAgo(n.createdAt)}</div>
                  </div>
                  {!n.read && <span className="pm-notif-dot" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </span>
  );
}

// ══════════════════════════════════════════════════════════════════════
//  PSA / Service — in-house service desk + time tracking (Halo replacement).
//  Admin-only, client-independent. Reuses Work's Pm* primitives (icons,
//  avatars, menus, confirm dialog, markdown editor) so the two internal
//  modules read as one system. The time layer is cross-cutting: an entry
//  logs against a ticket, a Work item, or internal — recorded once.
// ══════════════════════════════════════════════════════════════════════

var SVC_STATUS = {
  new:         { label: 'New',         color: '#3b82f6', dot: '#3b82f6' },
  open:        { label: 'Open',        color: '#0ea5e9', dot: '#0ea5e9' },
  in_progress: { label: 'In progress', color: '#8b5cf6', dot: '#8b5cf6' },
  waiting:     { label: 'Waiting',     color: '#f59e0b', dot: '#f59e0b' },
  resolved:    { label: 'Resolved',    color: '#10b981', dot: '#10b981' },
  closed:      { label: 'Closed',      color: '#94a3b8', dot: '#94a3b8' },
};
var SVC_STATUS_ORDER = ['new', 'open', 'in_progress', 'waiting', 'resolved', 'closed'];
var SVC_PRIORITY = {
  low:    { label: 'Low',    color: '#94a3b8' },
  medium: { label: 'Medium', color: '#3b82f6' },
  high:   { label: 'High',   color: '#f59e0b' },
  urgent: { label: 'Urgent', color: '#f43f5e' },
};
var SVC_PRIORITY_ORDER = ['urgent', 'high', 'medium', 'low'];
var SVC_TYPE = { incident: 'Incident', request: 'Request', problem: 'Problem', question: 'Question', maintenance: 'Maintenance' };
var SVC_TYPE_ORDER = ['incident', 'request', 'problem', 'question', 'maintenance'];

function psaSignal() { try { window.dispatchEvent(new CustomEvent('psa:timer')); } catch (e) {} }

function svcDuration(min) {
  min = Math.round(min || 0);
  if (min <= 0) return '0m';
  var h = Math.floor(min / 60), m = min % 60;
  if (h && m) return h + 'h ' + m + 'm';
  if (h) return h + 'h';
  return m + 'm';
}
function svcClock(sec) {
  sec = Math.max(0, Math.floor(sec || 0));
  var h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
  var mm = String(m).padStart(2, '0'), ss = String(s).padStart(2, '0');
  return (h > 0 ? h + ':' + mm : mm) + ':' + ss;
}
function svcHoursCell(min) { if (!min) return ''; return String(Math.round((min / 60) * 100) / 100); }
function svcMoney(n) { n = Number(n) || 0; return '$' + n.toLocaleString(undefined, { maximumFractionDigits: 2 }); }
// Parse a flexible duration string → minutes. Accepts "1:30", "1.5", "1.5h",
// "90m", "1h30m", "45". A bare decimal is read as hours (people type "1.5").
function svcParseDuration(str) {
  var s = String(str || '').trim().toLowerCase();
  if (!s) return NaN;
  var m;
  if ((m = s.match(/^(\d+):([0-5]?\d)$/))) return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
  if ((m = s.match(/^(\d+)\s*h\s*(\d+)\s*m?$/))) return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
  if ((m = s.match(/^(\d*\.?\d+)\s*h(?:rs?|ours?)?$/))) return Math.round(parseFloat(m[1]) * 60);
  if ((m = s.match(/^(\d+)\s*m(?:in(?:s|utes?)?)?$/))) return parseInt(m[1], 10);
  if ((m = s.match(/^\d*\.?\d+$/))) return Math.round(parseFloat(s) * 60);
  return NaN;
}
function svcISO(d) { var mm = String(d.getMonth() + 1).padStart(2, '0'), dd = String(d.getDate()).padStart(2, '0'); return d.getFullYear() + '-' + mm + '-' + dd; }
function svcToday() { return svcISO(new Date()); }
function svcWeekStart(d) { var x = new Date(d.getFullYear(), d.getMonth(), d.getDate()); var wd = (x.getDay() + 6) % 7; x.setDate(x.getDate() - wd); return x; } // Monday
function svcAddDays(d, n) { var x = new Date(d.getFullYear(), d.getMonth(), d.getDate()); x.setDate(x.getDate() + n); return x; }
function svcWeekLabel(monISO) {
  var mon = new Date(monISO + 'T00:00:00'), sun = svcAddDays(mon, 6);
  var a = mon.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  var b = sun.toLocaleDateString(undefined, { month: (mon.getMonth() === sun.getMonth() ? undefined : 'short'), day: 'numeric' });
  return a + ' – ' + b;
}
function svcActivityText(a) {
  var d = a.data || {};
  switch (a.type) {
    case 'created':     return 'opened this ticket';
    case 'status':      return 'moved to ' + ((SVC_STATUS[d.to] || {}).label || d.to);
    case 'priority':    return 'set priority to ' + ((SVC_PRIORITY[d.to] || {}).label || d.to);
    case 'type':        return 'changed type to ' + (SVC_TYPE[d.to] || d.to);
    case 'assigned':    return 'assigned this to ' + (d.name || 'someone');
    case 'unassigned':  return 'removed the assignee';
    case 'client':      return 'set the client to ' + (d.name || '—');
    case 'due_set':     return 'set a due date';
    case 'due_cleared': return 'cleared the due date';
    case 'renamed':     return 'renamed this ticket';
    default:            return a.type;
  }
}

// ── Editable pills ────────────────────────────────────────────────────
function SvcStatusPill({ status, onChange, size }) {
  var meta = SVC_STATUS[status] || SVC_STATUS.new;
  var pill = (
    <span className={'svc-status-pill' + (size === 'sm' ? ' svc-status-sm' : '')} style={{ color: meta.color, background: meta.color + '1f' }}>
      <span className="svc-status-dot" style={{ background: meta.dot }} />{meta.label}
    </span>
  );
  if (!onChange) return pill;
  return (
    <PmMenu trigger={<span className="svc-pill-trigger">{pill}</span>}>
      {function (close) { return SVC_STATUS_ORDER.map(function (s) {
        var m = SVC_STATUS[s];
        return <button key={s} className="pm-menu-item" onClick={function () { onChange(s); close(); }}>
          <span className="svc-status-dot" style={{ background: m.dot }} />{m.label}
          {status === s && <span className="pm-menu-check">✓</span>}
        </button>;
      }); }}
    </PmMenu>
  );
}
function SvcPriorityPill({ priority, onChange }) {
  var meta = SVC_PRIORITY[priority] || SVC_PRIORITY.medium;
  var pill = (
    <span className="svc-prio-pill" style={{ color: meta.color }}>
      <span className="svc-prio-flag" style={{ color: meta.color }}><PmIcon name="flag" size={12} /></span>{meta.label}
    </span>
  );
  if (!onChange) return pill;
  return (
    <PmMenu trigger={<span className="svc-pill-trigger">{pill}</span>}>
      {function (close) { return SVC_PRIORITY_ORDER.map(function (p) {
        var m = SVC_PRIORITY[p];
        return <button key={p} className="pm-menu-item" onClick={function () { onChange(p); close(); }}>
          <span className="pm-prio-dot" style={{ background: m.color }} />{m.label}
          {priority === p && <span className="pm-menu-check">✓</span>}
        </button>;
      }); }}
    </PmMenu>
  );
}
function SvcTypeChip({ type, onChange }) {
  var chip = <span className="svc-type-chip">{SVC_TYPE[type] || type}</span>;
  if (!onChange) return chip;
  return (
    <PmMenu trigger={<span className="svc-pill-trigger">{chip}</span>}>
      {function (close) { return SVC_TYPE_ORDER.map(function (t) {
        return <button key={t} className="pm-menu-item" onClick={function () { onChange(t); close(); }}>{SVC_TYPE[t]}{type === t && <span className="pm-menu-check">✓</span>}</button>;
      }); }}
    </PmMenu>
  );
}
function SvcAssignee({ assigneeId, members, onChange, size }) {
  var member = members.find(function (m) { return m.id === assigneeId; });
  var trigger = <span className="svc-assignee-trigger"><PmAvatar member={member} size={size || 26} /></span>;
  if (!onChange) return trigger;
  return (
    <PmMenu trigger={trigger} align="right">
      {function (close) { return (
        <div className="pm-assignee-list">
          <div className="pm-menu-label">Assignee</div>
          <button className="pm-menu-item" onClick={function () { onChange(null); close(); }}>
            <PmAvatar member={null} size={22} /> Unassigned {!assigneeId && <span className="pm-menu-check">✓</span>}
          </button>
          {members.map(function (m) {
            return <button key={m.id} className="pm-menu-item" onClick={function () { onChange(m.id); close(); }}>
              <PmAvatar member={m} size={22} /> {m.name} {assigneeId === m.id && <span className="pm-menu-check">✓</span>}
            </button>;
          })}
        </div>
      ); }}
    </PmMenu>
  );
}

// ── Work-target picker (what a time entry logs against) ───────────────
// Searches /psa/loggable and returns { workKind, workId, label, clientId }.
function SvcTargetPicker({ onPick, autoFocus }) {
  var [q, setQ] = useState('');
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  useEffect(function () {
    var alive = true; setLoading(true);
    var t = setTimeout(function () {
      apiGet('/psa/loggable' + (q ? '?q=' + encodeURIComponent(q) : '')).then(function (r) {
        if (!alive) return; setData(r.ok ? r.data : { tickets: [], pmTasks: [], pmProjects: [] }); setLoading(false);
      });
    }, q ? 180 : 0);
    return function () { alive = false; clearTimeout(t); };
  }, [q]);
  function row(kind, id, label, sub, clientId, icon) {
    return <button key={kind + id} className="svc-target-row" onClick={function () { onPick({ workKind: kind, workId: id, label: label, clientId: clientId || null }); }}>
      <span className="svc-target-icon"><PmIcon name={icon} size={15} /></span>
      <span className="svc-target-main"><span className="svc-target-label">{label}</span>{sub && <span className="svc-target-sub">{sub}</span>}</span>
    </button>;
  }
  return (
    <div className="svc-target-picker" onClick={function (e) { e.stopPropagation(); }}>
      <div className="svc-target-search">
        <PmIcon name="search" size={14} />
        <input autoFocus={autoFocus} value={q} placeholder="Search tickets, tasks, projects…" onChange={function (e) { setQ(e.target.value); }} />
      </div>
      <div className="svc-target-list">
        <button className="svc-target-row" onClick={function () { onPick({ workKind: 'internal', workId: null, label: 'Internal', clientId: null }); }}>
          <span className="svc-target-icon"><PmIcon name="clock" size={15} /></span>
          <span className="svc-target-main"><span className="svc-target-label">Internal / non-client</span><span className="svc-target-sub">Meetings, admin, R&D</span></span>
        </button>
        {loading && <div className="svc-target-empty">Searching…</div>}
        {!loading && data && (
          <>
            <div className="svc-target-group">Tickets</div>
            {(data.tickets || []).length
              ? data.tickets.map(function (t) { return row('ticket', t.id, '#' + t.id + ' ' + t.title, t.clientName || 'Internal', t.clientId, 'ticket'); })
              : <div className="svc-target-empty svc-target-empty-sm">{q ? 'No matching tickets.' : 'No open tickets yet — create one, or Import from NinjaOne.'}</div>}
            {data.pmTasks && data.pmTasks.length > 0 && <div className="svc-target-group">My tasks</div>}
            {data.pmTasks && data.pmTasks.map(function (t) { return row('pm_task', t.id, t.title, t.projectName, t.clientId, 'check'); })}
            {data.pmProjects && data.pmProjects.length > 0 && <div className="svc-target-group">Projects</div>}
            {data.pmProjects && data.pmProjects.map(function (p) { return row('pm_project', p.id, p.name, p.clientName || 'Internal', p.clientId, 'work'); })}
          </>
        )}
      </div>
    </div>
  );
}

// ── Global timer pill (lives in the app header, visible on every tab) ──
function PsaTimerPill({ onOpen }) {
  var [running, setRunning] = useState(null);
  var [now, setNow] = useState(Date.now());
  var [busy, setBusy] = useState(false);

  function load() { apiGet('/psa/timer').then(function (r) { if (r.ok) setRunning(r.data.running || null); }); }
  useEffect(function () {
    load();
    function onSig() { load(); }
    window.addEventListener('psa:timer', onSig);
    var poll = setInterval(load, 60000);
    return function () { window.removeEventListener('psa:timer', onSig); clearInterval(poll); };
  }, []);
  useEffect(function () {
    if (!running) return;
    var t = setInterval(function () { setNow(Date.now()); }, 1000);
    return function () { clearInterval(t); };
  }, [running]);

  function start(target) {
    setBusy(true);
    apiSend('POST', '/psa/timer/start', { workKind: target.workKind, workId: target.workId, clientId: target.clientId }).then(function (r) {
      setBusy(false);
      if (r.ok) { setRunning(r.data.running); psaSignal(); }
    });
  }
  function stop() {
    setBusy(true);
    apiSend('POST', '/psa/timer/stop', {}).then(function (r) { setBusy(false); if (r.ok) { setRunning(null); psaSignal(); } });
  }

  if (running) {
    var elapsed = Math.floor((now - new Date(running.startedAt).getTime()) / 1000);
    return (
      <div className="psa-pill psa-pill-on" title={'Tracking: ' + (running.workLabel || 'Internal')}>
        <span className="psa-pill-live" />
        <button className="psa-pill-body" onClick={onOpen}>
          <span className="psa-pill-label">{running.workLabel || 'Internal'}</span>
          <span className="psa-pill-time">{svcClock(elapsed)}</span>
        </button>
        <button className="psa-pill-stop" onClick={stop} disabled={busy} title="Stop timer"><PmIcon name="stop" size={12} /></button>
      </div>
    );
  }
  return (
    <PmMenu align="right" trigger={
      <button className="psa-pill psa-pill-idle" title="Start a timer"><PmIcon name="clock" size={15} /><span className="psa-pill-idle-label">Start timer</span></button>
    }>
      {function (close) { return (
        <div className="svc-quickstart">
          <div className="svc-quickstart-head">Start a timer</div>
          <SvcTargetPicker autoFocus onPick={function (t) { start(t); close(); }} />
        </div>
      ); }}
    </PmMenu>
  );
}

// ── New ticket modal ──────────────────────────────────────────────────
function SvcNewTicketModal({ members, clientList, onClose, onCreated }) {
  var [title, setTitle] = useState('');
  var [desc, setDesc] = useState('');
  var [clientId, setClientId] = useState('');
  var [priority, setPriority] = useState('medium');
  var [type, setType] = useState('incident');
  var [assigneeId, setAssigneeId] = useState(null);
  var [requester, setRequester] = useState('');
  var [saving, setSaving] = useState(false);
  var [err, setErr] = useState(null);

  function save() {
    if (!title.trim()) { setErr('Give the ticket a title.'); return; }
    setSaving(true); setErr(null);
    apiSend('POST', '/psa/tickets', {
      title: title, description: desc, clientId: clientId || null, priority: priority, type: type,
      assigneeId: assigneeId, requesterName: requester || null,
    }).then(function (r) {
      setSaving(false);
      if (!r.ok) { setErr(r.error || 'Could not create the ticket.'); return; }
      onCreated(r.data.ticket);
    });
  }
  return (
    <div className="pm-modal-overlay" onClick={onClose}>
      <div className="pm-modal svc-modal" onClick={function (e) { e.stopPropagation(); }}>
        <div className="pm-modal-head"><h3>New ticket</h3><button className="pm-icon-btn" onClick={onClose}><PmIcon name="x" size={16} /></button></div>
        <div className="pm-modal-body">
          <label className="svc-field"><span className="svc-field-label">Title</span>
            <input className="svc-input" autoFocus value={title} placeholder="Short summary of the issue or request" onChange={function (e) { setTitle(e.target.value); }} onKeyDown={function (e) { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) save(); }} />
          </label>
          <label className="svc-field"><span className="svc-field-label">Description</span>
            <PmRichEditor value={desc} onChange={setDesc} members={members} placeholder="What's going on? Steps, context, links…" rows={4} />
          </label>
          <div className="svc-field-row">
            <label className="svc-field"><span className="svc-field-label">Client</span>
              <select className="svc-input" value={clientId} onChange={function (e) { setClientId(e.target.value); }}>
                <option value="">Internal (no client)</option>
                {clientList.map(function (cl) { return <option key={cl.id} value={cl.id}>{cl.name}</option>; })}
              </select>
            </label>
            <label className="svc-field"><span className="svc-field-label">Requester</span>
              <input className="svc-input" value={requester} placeholder="Who reported it" onChange={function (e) { setRequester(e.target.value); }} />
            </label>
          </div>
          <div className="svc-field-row">
            <label className="svc-field"><span className="svc-field-label">Type</span>
              <select className="svc-input" value={type} onChange={function (e) { setType(e.target.value); }}>
                {SVC_TYPE_ORDER.map(function (t) { return <option key={t} value={t}>{SVC_TYPE[t]}</option>; })}
              </select>
            </label>
            <label className="svc-field"><span className="svc-field-label">Priority</span>
              <select className="svc-input" value={priority} onChange={function (e) { setPriority(e.target.value); }}>
                {SVC_PRIORITY_ORDER.map(function (p) { return <option key={p} value={p}>{SVC_PRIORITY[p].label}</option>; })}
              </select>
            </label>
            <label className="svc-field"><span className="svc-field-label">Assignee</span>
              <select className="svc-input" value={assigneeId || ''} onChange={function (e) { setAssigneeId(e.target.value ? parseInt(e.target.value, 10) : null); }}>
                <option value="">Unassigned</option>
                {members.map(function (m) { return <option key={m.id} value={m.id}>{m.name}</option>; })}
              </select>
            </label>
          </div>
          {err && <div className="svc-form-err">{err}</div>}
        </div>
        <div className="pm-modal-foot">
          <button className="pm-ghost-btn" onClick={onClose}>Cancel</button>
          <button className="pm-primary-btn" onClick={save} disabled={saving}>{saving ? 'Creating…' : 'Create ticket'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Log/edit time modal ───────────────────────────────────────────────
function SvcTimeModal({ entry, presetTarget, clientList, onClose, onSaved }) {
  var editing = !!entry;
  var [target, setTarget] = useState(
    entry ? { workKind: entry.workKind, workId: entry.workId, label: entry.workLabel || 'Internal', clientId: entry.clientId } :
    (presetTarget || null)
  );
  var [pickOpen, setPickOpen] = useState(false);
  var [dur, setDur] = useState(entry ? svcHoursCell(entry.durationMinutes) : '');
  var [date, setDate] = useState(entry ? entry.workDate : svcToday());
  var [billable, setBillable] = useState(entry ? entry.billable : true);
  var [notes, setNotes] = useState(entry ? entry.notes : '');
  var [intClient, setIntClient] = useState(entry && entry.workKind === 'internal' ? (entry.clientId || '') : '');
  var [saving, setSaving] = useState(false);
  var [err, setErr] = useState(null);

  function save() {
    if (!target) { setErr('Pick what you worked on.'); return; }
    var minutes = svcParseDuration(dur);
    if (!(minutes > 0)) { setErr('Enter a valid duration (e.g. 1:30, 1.5h, 45m).'); return; }
    setSaving(true); setErr(null);
    var body = {
      workKind: target.workKind, workId: target.workId, durationMinutes: minutes,
      workDate: date, billable: billable, notes: notes,
      clientId: target.workKind === 'internal' ? (intClient || null) : undefined,
    };
    var p = editing ? apiSend('PATCH', '/psa/time/' + entry.id, body) : apiSend('POST', '/psa/time', body);
    p.then(function (r) { setSaving(false); if (!r.ok) { setErr(r.error || 'Could not save.'); return; } onSaved(r.data.entry); psaSignal(); });
  }
  return (
    <div className="pm-modal-overlay" onClick={onClose}>
      <div className="pm-modal svc-modal svc-modal-sm" onClick={function (e) { e.stopPropagation(); }}>
        <div className="pm-modal-head"><h3>{editing ? 'Edit time' : 'Log time'}</h3><button className="pm-icon-btn" onClick={onClose}><PmIcon name="x" size={16} /></button></div>
        <div className="pm-modal-body">
          <label className="svc-field"><span className="svc-field-label">Against</span>
            <PmMenu trigger={
              <button className="svc-target-select">
                <span className="svc-target-select-label">{target ? target.label : 'Pick a ticket, task, project…'}</span>
                <PmIcon name="chevron-down" size={14} />
              </button>
            }>
              {function (close) { return <SvcTargetPicker autoFocus onPick={function (t) { setTarget(t); close(); }} />; }}
            </PmMenu>
          </label>
          {target && target.workKind === 'internal' && (
            <label className="svc-field"><span className="svc-field-label">Client (optional)</span>
              <select className="svc-input" value={intClient} onChange={function (e) { setIntClient(e.target.value); }}>
                <option value="">None</option>
                {clientList.map(function (cl) { return <option key={cl.id} value={cl.id}>{cl.name}</option>; })}
              </select>
            </label>
          )}
          <div className="svc-field-row">
            <label className="svc-field"><span className="svc-field-label">Duration</span>
              <input className="svc-input" autoFocus={!editing && !!target} value={dur} placeholder="1:30 · 1.5h · 45m" onChange={function (e) { setDur(e.target.value); }} onKeyDown={function (e) { if (e.key === 'Enter') save(); }} />
              <div className="svc-dur-chips">{['15m', '30m', '1h', '2h'].map(function (q) { return <button key={q} type="button" className="svc-dur-chip" onClick={function () { setDur(q); }}>{q}</button>; })}</div>
            </label>
            <label className="svc-field"><span className="svc-field-label">Date</span>
              <input className="svc-input" type="date" value={date} onChange={function (e) { setDate(e.target.value); }} />
            </label>
          </div>
          <label className="svc-field"><span className="svc-field-label">Notes</span>
            <input className="svc-input" value={notes || ''} placeholder="What did you do?" onChange={function (e) { setNotes(e.target.value); }} />
          </label>
          <label className="svc-check"><input type="checkbox" checked={billable} onChange={function (e) { setBillable(e.target.checked); }} /> <span>Billable</span></label>
          {err && <div className="svc-form-err">{err}</div>}
        </div>
        <div className="pm-modal-foot">
          <button className="pm-ghost-btn" onClick={onClose}>Cancel</button>
          <button className="pm-primary-btn" onClick={save} disabled={saving}>{saving ? 'Saving…' : (editing ? 'Save' : 'Log time')}</button>
        </div>
      </div>
    </div>
  );
}

// ── Rates modal ───────────────────────────────────────────────────────
function SvcRatesModal({ onClose }) {
  var [data, setData] = useState(null);
  var [def, setDef] = useState('');
  var [rates, setRates] = useState({});
  var [saving, setSaving] = useState(false);
  useEffect(function () {
    apiGet('/psa/rates').then(function (r) {
      if (r.ok) {
        setData(r.data); setDef(r.data.default != null ? String(r.data.default) : '');
        var map = {}; (r.data.members || []).forEach(function (m) { map[m.id] = m.rate != null ? String(m.rate) : ''; }); setRates(map);
      } else setData({ members: [] });
    });
  }, []);
  function save() {
    setSaving(true);
    var users = {}; Object.keys(rates).forEach(function (id) { users[id] = rates[id] === '' ? null : Number(rates[id]); });
    apiSend('PUT', '/psa/rates', { default: def === '' ? 0 : Number(def), users: users }).then(function (r) { setSaving(false); if (r.ok) onClose(); });
  }
  return (
    <div className="pm-modal-overlay" onClick={onClose}>
      <div className="pm-modal svc-modal svc-modal-sm" onClick={function (e) { e.stopPropagation(); }}>
        <div className="pm-modal-head"><h3>Billing rates</h3><button className="pm-icon-btn" onClick={onClose}><PmIcon name="x" size={16} /></button></div>
        <div className="pm-modal-body">
          <p className="svc-modal-note">Rates are snapshotted onto each time entry when it's logged, so changing them here never rewrites past history. A person's own rate overrides the default.</p>
          <label className="svc-field"><span className="svc-field-label">Default rate ($/hr)</span>
            <input className="svc-input" type="number" min="0" step="5" value={def} placeholder="0" onChange={function (e) { setDef(e.target.value); }} />
          </label>
          <div className="svc-rate-list">
            {!data ? <div className="svc-target-empty">Loading…</div> : (data.members || []).map(function (m) {
              return <div key={m.id} className="svc-rate-row">
                <PmAvatar member={m} size={26} /><span className="svc-rate-name">{m.name}</span>
                <input className="svc-input svc-rate-input" type="number" min="0" step="5" value={rates[m.id] != null ? rates[m.id] : ''} placeholder={def || '0'} onChange={function (e) { var v = e.target.value; setRates(function (p) { var n = Object.assign({}, p); n[m.id] = v; return n; }); }} />
              </div>;
            })}
          </div>
        </div>
        <div className="pm-modal-foot">
          <button className="pm-ghost-btn" onClick={onClose}>Cancel</button>
          <button className="pm-primary-btn" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save rates'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Import NinjaOne tickets into the Service desk ─────────────────────
function SvcNinjaImportModal({ onClose, onImported }) {
  var [preview, setPreview] = useState(null);
  var [clientId, setClientId] = useState('all');
  var [onlyAssigned, setOnlyAssigned] = useState(true);
  var [excluded, setExcluded] = useState({});   // source -> true when excluded
  var [importing, setImporting] = useState(false);
  var [result, setResult] = useState(null);
  var [err, setErr] = useState(null);

  useEffect(function () {
    apiGet('/psa/ninja/preview').then(function (r) {
      if (r.ok) setPreview(r.data); else { setErr(r.error || 'Could not reach NinjaOne.'); setPreview({ available: false }); }
    });
  }, []);
  function toggleSrc(s) { setExcluded(function (p) { var n = Object.assign({}, p); if (n[s]) delete n[s]; else n[s] = true; return n; }); }
  function doImport() {
    setImporting(true); setErr(null);
    apiSend('POST', '/psa/ninja/import', { clientId: clientId, onlyAssigned: onlyAssigned, excludeSources: Object.keys(excluded) }).then(function (r) {
      setImporting(false);
      if (!r.ok) { setErr(r.error || 'Import failed.'); return; }
      setResult(r.data); psaSignal();
    });
  }
  var totalOpen = (preview && preview.total) || 0;
  return (
    <div className="pm-modal-overlay" onClick={onClose}>
      <div className="pm-modal svc-modal" onClick={function (e) { e.stopPropagation(); }}>
        <div className="pm-modal-head"><h3>Import from NinjaOne</h3><button className="pm-icon-btn" onClick={onClose}><PmIcon name="x" size={16} /></button></div>
        <div className="pm-modal-body">
          {!preview ? <div className="svc-target-empty">Checking NinjaOne…</div>
           : preview.available === false ? <div className="svc-modal-note">{err || 'NinjaOne ticketing isn’t configured for any client, so there’s nothing to import.'}</div>
           : result ? (
             <div className="svc-import-result">
               <div className="svc-import-big">{result.imported}</div>
               <div className="svc-import-caption">ticket{result.imported === 1 ? '' : 's'} imported{result.updated ? ' · ' + result.updated + ' refreshed' : ''}{result.skipped ? ' · ' + result.skipped + ' skipped' : ''}</div>
               {result.imported === 0 && result.updated === 0 && <div className="svc-modal-note">Nothing matched these filters.</div>}
             </div>
           ) : (
            <>
              <p className="svc-modal-note">{totalOpen} open NinjaOne ticket{totalOpen === 1 ? '' : 's'} across your clients. Imported tickets become native Service tickets — assign, comment, set status, and log time — with a link back to NinjaOne. Re-running refreshes them.</p>
              <label className="svc-field"><span className="svc-field-label">Client</span>
                <select className="svc-input" value={clientId} onChange={function (e) { setClientId(e.target.value); }}>
                  <option value="all">All clients ({totalOpen} open)</option>
                  {(preview.clients || []).map(function (c) { return <option key={c.clientId} value={c.clientId}>{c.name} — {c.open} open{c.imported ? ' · ' + c.imported + ' imported' : ''}</option>; })}
                </select>
              </label>
              <label className="svc-check"><input type="checkbox" checked={onlyAssigned} onChange={function (e) { setOnlyAssigned(e.target.checked); }} /> <span>Only tickets with an assignee <span className="svc-muted">(skips unassigned automated alerts)</span></span></label>
              {(preview.sources || []).length > 0 && (
                <div className="svc-field">
                  <span className="svc-field-label">Sources to include</span>
                  <div className="svc-src-list">
                    {preview.sources.map(function (s) {
                      return <label key={s.source} className="svc-src-row"><input type="checkbox" checked={!excluded[s.source]} onChange={function () { toggleSrc(s.source); }} /> <span className="svc-src-name">{s.source}</span><span className="svc-src-count">{s.count}</span></label>;
                    })}
                  </div>
                </div>
              )}
              {err && <div className="svc-form-err">{err}</div>}
            </>
          )}
        </div>
        <div className="pm-modal-foot">
          {result ? <button className="pm-primary-btn" onClick={onImported}>Done</button> : (
            <>
              <button className="pm-ghost-btn" onClick={onClose}>Cancel</button>
              {preview && preview.available !== false && <button className="pm-primary-btn" onClick={doImport} disabled={importing}>{importing ? 'Importing…' : 'Import tickets'}</button>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Ticket detail drawer ──────────────────────────────────────────────
function SvcTicketDrawer({ ticketId, members, clientList, onClose, onChanged }) {
  var [d, setD] = useState(null);
  var [err, setErr] = useState(null);
  var [comment, setComment] = useState('');
  var [internalNote, setInternalNote] = useState(true);
  var [posting, setPosting] = useState(false);
  var [confirmDel, setConfirmDel] = useState(false);
  var [timeModal, setTimeModal] = useState(false);
  var [editTitle, setEditTitle] = useState(false);
  var [titleVal, setTitleVal] = useState('');

  function load() {
    apiGet('/psa/tickets/' + ticketId).then(function (r) {
      if (r.ok) { setD(r.data); setTitleVal(r.data.ticket.title); } else setErr(r.error || 'Could not load ticket.');
    });
  }
  useEffect(function () { load(); /* eslint-disable-next-line */ }, [ticketId]);

  function patch(body) {
    apiSend('PATCH', '/psa/tickets/' + ticketId, body).then(function (r) {
      if (r.ok) { load(); if (onChanged) onChanged(); }
    });
  }
  function postComment() {
    if (!comment.trim()) return;
    setPosting(true);
    apiSend('POST', '/psa/tickets/' + ticketId + '/comments', { body: comment, internal: internalNote }).then(function (r) {
      setPosting(false); if (r.ok) { setComment(''); load(); }
    });
  }
  function del() {
    apiSend('DELETE', '/psa/tickets/' + ticketId).then(function (r) { if (r.ok) { setConfirmDel(false); onClose(); if (onChanged) onChanged(); } });
  }
  function startTimer() {
    apiSend('POST', '/psa/timer/start', { workKind: 'ticket', workId: ticketId }).then(function (r) { if (r.ok) { psaSignal(); } });
  }

  var t = d ? d.ticket : null;
  return (
    <div className="pm-drawer-overlay" onClick={onClose}>
      <div className="pm-drawer svc-drawer" onClick={function (e) { e.stopPropagation(); }}>
        {!d ? (
          <div className="svc-drawer-loading">{err ? <ErrorBanner message={err} onRetry={load} /> : 'Loading…'}</div>
        ) : (
          <>
            <div className="svc-drawer-top">
              <div className="svc-drawer-id">#{t.id}<SvcTypeChip type={t.type} onChange={function (v) { patch({ type: v }); }} /></div>
              <div className="svc-drawer-top-actions">
                <PmMenu align="right" trigger={<button className="pm-icon-btn"><PmIcon name="kebab" size={16} /></button>}>
                  {function (close) { return (
                    <button className="pm-menu-item pm-menu-danger" onClick={function () { close(); setConfirmDel(true); }}><PmIcon name="x" size={14} /> Delete ticket</button>
                  ); }}
                </PmMenu>
                <button className="pm-icon-btn" onClick={onClose}><PmIcon name="x" size={16} /></button>
              </div>
            </div>
            <div className="svc-drawer-body">
              <div className="svc-drawer-main">
                {editTitle ? (
                  <input className="svc-title-input" autoFocus value={titleVal} onChange={function (e) { setTitleVal(e.target.value); }}
                    onBlur={function () { setEditTitle(false); if (titleVal.trim() && titleVal !== t.title) patch({ title: titleVal }); }}
                    onKeyDown={function (e) { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setTitleVal(t.title); setEditTitle(false); } }} />
                ) : (
                  <h2 className="svc-drawer-title" onClick={function () { setEditTitle(true); }}>{t.title}</h2>
                )}

                {t.sourceKind === 'ninja' && (
                  <a className="svc-ninja-banner" href={(t.sourceMeta && t.sourceMeta.url) || '#'} target="_blank" rel="noopener noreferrer">
                    <PmIcon name="ticket" size={14} />
                    <span className="svc-ninja-banner-text">Synced from NinjaOne{t.sourceMeta && t.sourceMeta.ninjaStatus ? ' · ' + t.sourceMeta.ninjaStatus : ''}</span>
                    <span className="svc-ninja-banner-open"><PmIcon name="link" size={12} /> Open in NinjaOne</span>
                  </a>
                )}

                <div className="svc-drawer-section">
                  <div className="svc-section-label">Description</div>
                  <SvcInlineDesc value={t.description} members={members} onSave={function (v) { patch({ description: v }); }} />
                </div>

                <div className="svc-drawer-section">
                  <div className="svc-section-head">
                    <div className="svc-section-label">Time logged</div>
                    <div className="svc-time-head-actions">
                      <button className="pm-ghost-btn svc-mini-btn" onClick={function () { setTimeModal(true); }}><PmIcon name="plus" size={13} /> Log time</button>
                      <button className="pm-ghost-btn svc-mini-btn" onClick={startTimer}><PmIcon name="play" size={12} /> Start timer</button>
                    </div>
                  </div>
                  {d.timeEntries.length === 0 ? (
                    <div className="svc-time-empty">No time logged yet.</div>
                  ) : (
                    <div className="svc-time-list">
                      {d.timeEntries.map(function (e) {
                        return <div key={e.id} className="svc-time-row">
                          <PmAvatar member={{ id: e.userId, name: e.userName, initials: pmInitials(e.userName) }} size={22} />
                          <span className="svc-time-dur">{svcDuration(e.durationMinutes)}</span>
                          <span className="svc-time-notes">{e.notes || <span className="svc-muted">No note</span>}</span>
                          <span className="svc-time-meta">{pmPrettyDate(e.workDate)}{e.billable ? <span className="svc-billable-dot" title="Billable" /> : <span className="svc-nonbill" title="Non-billable">NB</span>}</span>
                        </div>;
                      })}
                      <div className="svc-time-total">
                        <span>{svcDuration(d.totals.minutes)} total</span>
                        <span className="svc-time-amount">{svcMoney(d.totals.amount)} billable</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="svc-drawer-section">
                  <div className="svc-section-label">Activity</div>
                  <SvcTimeline activity={d.activity} members={members} />
                  <div className="svc-comment-box">
                    <PmRichEditor value={comment} onChange={setComment} members={members} placeholder={internalNote ? 'Add an internal note…' : 'Reply to the requester…'} rows={3} onSubmit={postComment} />
                    <div className="svc-comment-actions">
                      <div className="svc-note-toggle">
                        <button className={'svc-seg' + (internalNote ? ' on' : '')} onClick={function () { setInternalNote(true); }}>Internal note</button>
                        <button className={'svc-seg' + (!internalNote ? ' on' : '')} onClick={function () { setInternalNote(false); }}>Public reply</button>
                      </div>
                      <button className="pm-primary-btn svc-mini-btn" onClick={postComment} disabled={posting || !comment.trim()}>{posting ? 'Posting…' : 'Comment'}</button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="svc-drawer-side">
                <div className="svc-prop">
                  <div className="svc-prop-label">Status</div>
                  <SvcStatusPill status={t.status} onChange={function (v) { patch({ status: v }); }} />
                </div>
                <div className="svc-prop">
                  <div className="svc-prop-label">Priority</div>
                  <SvcPriorityPill priority={t.priority} onChange={function (v) { patch({ priority: v }); }} />
                </div>
                <div className="svc-prop">
                  <div className="svc-prop-label">Assignee</div>
                  <div className="svc-prop-assignee"><SvcAssignee assigneeId={t.assigneeId} members={members} onChange={function (v) { patch({ assigneeId: v }); }} size={24} /><span>{t.assignee ? t.assignee.name : 'Unassigned'}</span></div>
                </div>
                <div className="svc-prop">
                  <div className="svc-prop-label">Client</div>
                  <PmMenu trigger={<button className="svc-prop-value-btn"><PmIcon name="building" size={13} />{t.clientName || 'Internal'}</button>}>
                    {function (close) { return (
                      <div className="pm-assignee-list">
                        <button className="pm-menu-item" onClick={function () { patch({ clientId: null }); close(); }}>Internal (no client){!t.clientId && <span className="pm-menu-check">✓</span>}</button>
                        {clientList.map(function (cl) { return <button key={cl.id} className="pm-menu-item" onClick={function () { patch({ clientId: cl.id }); close(); }}>{cl.name}{t.clientId === cl.id && <span className="pm-menu-check">✓</span>}</button>; })}
                      </div>
                    ); }}
                  </PmMenu>
                </div>
                <div className="svc-prop">
                  <div className="svc-prop-label">Requester</div>
                  <SvcInlineText value={t.requesterName} placeholder="Add requester" onSave={function (v) { patch({ requesterName: v }); }} />
                </div>
                <div className="svc-prop">
                  <div className="svc-prop-label">Due</div>
                  <input className="svc-prop-date" type="date" value={t.dueAt ? String(t.dueAt).slice(0, 10) : ''} onChange={function (e) { patch({ dueAt: e.target.value ? e.target.value + 'T17:00:00' : null }); }} />
                </div>
                <div className="svc-prop-meta">
                  <div>Opened {pmTimeAgo(t.createdAt)}{t.creator ? ' by ' + t.creator.name : ''}</div>
                  <div>Updated {pmTimeAgo(t.updatedAt)}</div>
                </div>
              </div>
            </div>
          </>
        )}
        {confirmDel && <PmConfirm title="Delete this ticket?" message="The ticket and its comments will be removed. Time already logged is kept for billing." confirmLabel="Delete ticket" danger onConfirm={del} onCancel={function () { setConfirmDel(false); }} />}
        {timeModal && <SvcTimeModal presetTarget={{ workKind: 'ticket', workId: ticketId, label: '#' + ticketId + ' ' + (t ? t.title : ''), clientId: t ? t.clientId : null }} clientList={clientList} onClose={function () { setTimeModal(false); }} onSaved={function () { setTimeModal(false); load(); }} />}
      </div>
    </div>
  );
}

function SvcTimeline({ activity, members }) {
  if (!activity || !activity.length) return <div className="svc-time-empty">No activity yet.</div>;
  return (
    <div className="svc-timeline">
      {activity.map(function (a) {
        if (a.type === 'comment') {
          return (
            <div key={a.key} className={'svc-tl-comment' + (a.internal ? ' internal' : ' public')}>
              <PmAvatar member={a.actor} size={26} />
              <div className="svc-tl-comment-body">
                <div className="svc-tl-comment-head">
                  <span className="pm-activity-who">{a.actor ? a.actor.name : 'Someone'}</span>
                  <span className={'svc-tl-tag' + (a.internal ? '' : ' public')}>{a.internal ? 'Internal note' : 'Public reply'}</span>
                  <span className="svc-tl-time">{pmTimeAgo(a.createdAt)}</span>
                </div>
                <div className="svc-tl-comment-text"><PmMarkdown text={a.body} members={members} /></div>
              </div>
            </div>
          );
        }
        return (
          <div key={a.key} className="svc-tl-event">
            <span className="svc-tl-dot" />
            <span className="pm-activity-who">{a.actor ? a.actor.name : 'Someone'}</span>
            <span className="svc-tl-event-text">{svcActivityText(a)}</span>
            <span className="svc-tl-time">{pmTimeAgo(a.createdAt)}</span>
          </div>
        );
      })}
    </div>
  );
}

// Inline single-line text editor for side properties.
function SvcInlineText({ value, placeholder, onSave }) {
  var [editing, setEditing] = useState(false);
  var [v, setV] = useState(value || '');
  useEffect(function () { setV(value || ''); }, [value]);
  if (!editing) return <button className="svc-inline-text" onClick={function () { setEditing(true); }}>{value || <span className="svc-muted">{placeholder}</span>}</button>;
  return <input className="svc-input svc-input-sm" autoFocus value={v} onChange={function (e) { setV(e.target.value); }}
    onBlur={function () { setEditing(false); if (v !== (value || '')) onSave(v); }}
    onKeyDown={function (e) { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setV(value || ''); setEditing(false); } }} />;
}

// Inline description: click to edit (rich text), save on button.
function SvcInlineDesc({ value, members, onSave }) {
  var [editing, setEditing] = useState(false);
  var [v, setV] = useState(value || '');
  useEffect(function () { setV(value || ''); }, [value]);
  if (!editing) {
    return value ? (
      <div className="svc-desc-view" onClick={function () { setEditing(true); }}><PmMarkdown text={value} members={members} /></div>
    ) : (
      <button className="svc-desc-empty" onClick={function () { setEditing(true); }}>Add a description…</button>
    );
  }
  return (
    <div className="svc-desc-edit">
      <PmRichEditor value={v} onChange={setV} members={members} rows={5} autoFocus placeholder="Describe the issue or request…" />
      <div className="svc-desc-edit-actions">
        <button className="pm-ghost-btn svc-mini-btn" onClick={function () { setV(value || ''); setEditing(false); }}>Cancel</button>
        <button className="pm-primary-btn svc-mini-btn" onClick={function () { onSave(v); setEditing(false); }}>Save</button>
      </div>
    </div>
  );
}

// ── Ticket queue (list + filters) ─────────────────────────────────────
function SvcQueue({ members, clientList, onOpen, reloadKey }) {
  var [tickets, setTickets] = useState(null);
  var [err, setErr] = useState(null);
  var [status, setStatus] = useState('open');
  var [assignee, setAssignee] = useState('all');
  var [priority, setPriority] = useState('all');
  var [client, setClient] = useState('all');
  var [q, setQ] = useState('');
  var [ninjaOpen, setNinjaOpen] = useState(false);

  function load() {
    setErr(null);
    var params = [];
    if (status !== 'all') params.push('status=' + status);
    if (assignee !== 'all') params.push('assignee=' + assignee);
    if (priority !== 'all') params.push('priority=' + priority);
    if (client !== 'all') params.push('client=' + encodeURIComponent(client));
    if (q.trim()) params.push('q=' + encodeURIComponent(q.trim()));
    apiGet('/psa/tickets' + (params.length ? '?' + params.join('&') : '')).then(function (r) {
      if (r.ok) setTickets(r.data.tickets); else setErr(r.error || 'Could not load tickets.');
    });
  }
  useEffect(function () { var t = setTimeout(load, q ? 200 : 0); return function () { clearTimeout(t); }; /* eslint-disable-next-line */ }, [status, assignee, priority, client, q, reloadKey]);

  return (
    <div className="svc-queue">
      <div className="svc-toolbar">
        <div className="svc-search"><PmIcon name="search" size={15} /><input value={q} placeholder="Search tickets by title or #id" onChange={function (e) { setQ(e.target.value); }} /></div>
        <select className="svc-filter" value={status} onChange={function (e) { setStatus(e.target.value); }}>
          <option value="open">Open queue</option>
          <option value="all">All statuses</option>
          {SVC_STATUS_ORDER.map(function (s) { return <option key={s} value={s}>{SVC_STATUS[s].label}</option>; })}
        </select>
        <select className="svc-filter" value={assignee} onChange={function (e) { setAssignee(e.target.value); }}>
          <option value="all">Anyone</option>
          <option value="me">Assigned to me</option>
          <option value="unassigned">Unassigned</option>
          {members.map(function (m) { return <option key={m.id} value={m.id}>{m.name}</option>; })}
        </select>
        <select className="svc-filter" value={priority} onChange={function (e) { setPriority(e.target.value); }}>
          <option value="all">Any priority</option>
          {SVC_PRIORITY_ORDER.map(function (p) { return <option key={p} value={p}>{SVC_PRIORITY[p].label}</option>; })}
        </select>
        <select className="svc-filter" value={client} onChange={function (e) { setClient(e.target.value); }}>
          <option value="all">Any client</option>
          {clientList.map(function (cl) { return <option key={cl.id} value={cl.id}>{cl.name}</option>; })}
        </select>
        <button className="pm-ghost-btn svc-import-btn" onClick={function () { setNinjaOpen(true); }} title="Pull open NinjaOne tickets into the desk"><PmIcon name="ticket" size={14} /> Import from NinjaOne</button>
      </div>
      {err ? <ErrorBanner message={err} onRetry={load} /> : !tickets ? (
        <div className="svc-list-skeleton">{[0, 1, 2, 3, 4].map(function (i) { return <div key={i} className="svc-skel-row" />; })}</div>
      ) : tickets.length === 0 ? (
        <div className="svc-empty"><PmEmptyArt kind="board" /><div className="svc-empty-title">No tickets here</div><div className="svc-empty-sub">Nothing matches these filters. Try widening them, or open a new ticket.</div></div>
      ) : (
        <div className="svc-table">
          <div className="svc-table-head">
            <span className="svc-col-prio" />
            <span className="svc-col-title">Ticket</span>
            <span className="svc-col-client">Client</span>
            <span className="svc-col-assignee">Owner</span>
            <span className="svc-col-status">Status</span>
            <span className="svc-col-time">Logged</span>
            <span className="svc-col-updated">Updated</span>
          </div>
          {tickets.map(function (t) {
            var pm = SVC_PRIORITY[t.priority] || SVC_PRIORITY.medium;
            return (
              <button key={t.id} className="svc-row" onClick={function () { onOpen(t.id); }}>
                <span className="svc-col-prio"><span className="svc-prio-bar" style={{ background: pm.color }} title={pm.label} /></span>
                <span className="svc-col-title">
                  <span className="svc-row-title">{t.title}</span>
                  <span className="svc-row-sub"><span className="svc-row-id">#{t.id}</span><span className="svc-type-chip svc-type-chip-sm">{SVC_TYPE[t.type]}</span>{t.sourceKind === 'ninja' && <span className="svc-ninja-chip">NinjaOne</span>}{t.commentCount > 0 && <span className="svc-row-comments"><PmIcon name="comment" size={12} /> {t.commentCount}</span>}</span>
                </span>
                <span className="svc-col-client">{t.clientName ? <span className="svc-client-chip">{t.clientName}</span> : <span className="svc-muted">Internal</span>}</span>
                <span className="svc-col-assignee">{t.assignee ? <span className="svc-owner"><PmAvatar member={t.assignee} size={22} /><span className="svc-owner-name">{t.assignee.name}</span></span> : <PmAvatar member={null} size={22} />}</span>
                <span className="svc-col-status"><SvcStatusPill status={t.status} size="sm" /></span>
                <span className="svc-col-time">{t.loggedMinutes ? svcDuration(t.loggedMinutes) : <span className="svc-muted">—</span>}</span>
                <span className="svc-col-updated">{pmTimeAgo(t.updatedAt)}</span>
              </button>
            );
          })}
        </div>
      )}
      {ninjaOpen && <SvcNinjaImportModal onClose={function () { setNinjaOpen(false); }} onImported={function () { setNinjaOpen(false); load(); }} />}
    </div>
  );
}

// ── My timesheet (week grid + entry list) ─────────────────────────────
function SvcTimesheet({ clientList, reloadKey }) {
  var [weekStart, setWeekStart] = useState(svcISO(svcWeekStart(new Date())));
  var [data, setData] = useState(null);
  var [err, setErr] = useState(null);
  var [editEntry, setEditEntry] = useState(null);
  var [addOpen, setAddOpen] = useState(false);
  var [confirmDel, setConfirmDel] = useState(null);

  var days = []; for (var i = 0; i < 7; i++) days.push(svcISO(svcAddDays(new Date(weekStart + 'T00:00:00'), i)));
  function load() {
    setErr(null);
    apiGet('/psa/time?from=' + days[0] + '&to=' + days[6]).then(function (r) { if (r.ok) setData(r.data); else setErr(r.error || 'Could not load timesheet.'); });
  }
  useEffect(function () { load(); /* eslint-disable-next-line */ }, [weekStart, reloadKey]);

  // Build grid rows keyed by work item.
  var rows = [], rowIndex = {};
  (data ? data.entries : []).forEach(function (e) {
    var key = e.workKind + ':' + (e.workId || 0);
    if (rowIndex[key] == null) { rowIndex[key] = rows.length; rows.push({ key: key, label: e.workLabel || 'Internal', client: e.clientName || null, mins: [0, 0, 0, 0, 0, 0, 0], total: 0 }); }
    var row = rows[rowIndex[key]];
    var di = days.indexOf(e.workDate);
    if (di >= 0) { row.mins[di] += e.durationMinutes; row.total += e.durationMinutes; }
  });
  rows.sort(function (a, b) { return b.total - a.total; });
  var dayTotals = [0, 0, 0, 0, 0, 0, 0];
  rows.forEach(function (r) { r.mins.forEach(function (m, i) { dayTotals[i] += m; }); });

  function delEntry(id) { apiSend('DELETE', '/psa/time/' + id).then(function (r) { if (r.ok) { setConfirmDel(null); load(); psaSignal(); } }); }

  return (
    <div className="svc-timesheet">
      <div className="svc-ts-bar">
        <div className="svc-week-nav">
          <button className="pm-icon-btn svc-nav-prev" onClick={function () { setWeekStart(svcISO(svcAddDays(new Date(weekStart + 'T00:00:00'), -7))); }} title="Previous week"><PmIcon name="chevron-down" size={16} /></button>
          <span className="svc-week-label">{svcWeekLabel(weekStart)}</span>
          <button className="pm-icon-btn svc-nav-next" onClick={function () { setWeekStart(svcISO(svcAddDays(new Date(weekStart + 'T00:00:00'), 7))); }} title="Next week"><PmIcon name="chevron-down" size={16} /></button>
          <button className="pm-ghost-btn svc-mini-btn" onClick={function () { setWeekStart(svcISO(svcWeekStart(new Date()))); }}>This week</button>
        </div>
        <button className="pm-primary-btn svc-mini-btn" onClick={function () { setAddOpen(true); }}><PmIcon name="plus" size={13} /> Log time</button>
      </div>

      {err ? <ErrorBanner message={err} onRetry={load} /> : !data ? (
        <div className="svc-list-skeleton">{[0, 1, 2].map(function (i) { return <div key={i} className="svc-skel-row" />; })}</div>
      ) : (
        <>
          <div className="svc-grid-wrap">
            <table className="svc-grid">
              <thead>
                <tr>
                  <th className="svc-grid-item">Work item</th>
                  {days.map(function (d, i) {
                    var dt = new Date(d + 'T00:00:00');
                    var isToday = d === svcToday();
                    return <th key={d} className={'svc-grid-day' + (isToday ? ' today' : '')}>{dt.toLocaleDateString(undefined, { weekday: 'short' })}<span className="svc-grid-date">{dt.getDate()}</span></th>;
                  })}
                  <th className="svc-grid-total">Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td className="svc-grid-empty" colSpan={9}>No time logged this week. Start the timer or log an entry.</td></tr>
                ) : rows.map(function (r) {
                  return <tr key={r.key}>
                    <td className="svc-grid-item"><span className="svc-grid-item-label">{r.label}</span>{r.client && <span className="svc-grid-item-client">{r.client}</span>}</td>
                    {r.mins.map(function (m, i) { return <td key={i} className={'svc-grid-cell' + (m ? ' has' : '') + (days[i] === svcToday() ? ' today' : '')}>{svcHoursCell(m)}</td>; })}
                    <td className="svc-grid-total">{svcDuration(r.total)}</td>
                  </tr>;
                })}
              </tbody>
              {rows.length > 0 && (
                <tfoot>
                  <tr>
                    <td className="svc-grid-item">Daily total</td>
                    {dayTotals.map(function (m, i) { return <td key={i} className={'svc-grid-foot' + (days[i] === svcToday() ? ' today' : '')}>{svcHoursCell(m)}</td>; })}
                    <td className="svc-grid-total">{svcDuration(data.totals.minutes)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          <div className="svc-ts-summary">
            <div className="svc-ts-stat"><span className="svc-ts-stat-num">{svcDuration(data.totals.minutes)}</span><span className="svc-ts-stat-label">Total this week</span></div>
            <div className="svc-ts-stat"><span className="svc-ts-stat-num">{svcDuration(data.totals.billableMinutes)}</span><span className="svc-ts-stat-label">Billable</span></div>
            <div className="svc-ts-stat"><span className="svc-ts-stat-num">{svcMoney(data.totals.amount)}</span><span className="svc-ts-stat-label">Billable value</span></div>
          </div>

          <div className="svc-entry-list">
            <div className="svc-entry-list-head">Entries</div>
            {data.entries.length === 0 ? <div className="svc-time-empty">No entries this week.</div> : data.entries.map(function (e) {
              return <div key={e.id} className="svc-entry-row">
                <span className="svc-entry-date">{new Date(e.workDate + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' })}</span>
                <span className="svc-entry-dur">{svcDuration(e.durationMinutes)}</span>
                <span className="svc-entry-main"><span className="svc-entry-label">{e.workLabel}</span>{e.notes && <span className="svc-entry-notes">{e.notes}</span>}</span>
                {e.clientName && <span className="svc-client-chip svc-client-chip-sm">{e.clientName}</span>}
                <span className={'svc-entry-bill' + (e.billable ? '' : ' nb')}>{e.billable ? svcMoney(e.amount) : 'Non-billable'}</span>
                <span className="svc-entry-actions">
                  <button className="pm-icon-btn" onClick={function () { setEditEntry(e); }} title="Edit"><PmIcon name="clip" size={14} /></button>
                  <button className="pm-icon-btn" onClick={function () { setConfirmDel(e); }} title="Delete"><PmIcon name="x" size={14} /></button>
                </span>
              </div>;
            })}
          </div>
        </>
      )}

      {(addOpen || editEntry) && <SvcTimeModal entry={editEntry} clientList={clientList} onClose={function () { setAddOpen(false); setEditEntry(null); }} onSaved={function () { setAddOpen(false); setEditEntry(null); load(); }} />}
      {confirmDel && <PmConfirm title="Delete this entry?" message={svcDuration(confirmDel.durationMinutes) + ' on ' + confirmDel.workLabel} confirmLabel="Delete" danger onConfirm={function () { delEntry(confirmDel.id); }} onCancel={function () { setConfirmDel(null); }} />}
    </div>
  );
}

// ── Team report (per-user + per-client) ───────────────────────────────
function SvcReport({ reloadKey }) {
  var [range, setRange] = useState('week');
  var [data, setData] = useState(null);
  var [err, setErr] = useState(null);
  function bounds() {
    var now = new Date();
    if (range === 'week') { var mon = svcWeekStart(now); return { from: svcISO(mon), to: svcISO(svcAddDays(mon, 6)) }; }
    if (range === 'month') { var f = new Date(now.getFullYear(), now.getMonth(), 1), t = new Date(now.getFullYear(), now.getMonth() + 1, 0); return { from: svcISO(f), to: svcISO(t) }; }
    var y0 = new Date(now.getFullYear(), 0, 1), y1 = new Date(now.getFullYear(), 11, 31); return { from: svcISO(y0), to: svcISO(y1) };
  }
  function load() { setErr(null); var b = bounds(); apiGet('/psa/time/summary?from=' + b.from + '&to=' + b.to).then(function (r) { if (r.ok) setData(r.data); else setErr(r.error || 'Could not load report.'); }); }
  useEffect(function () { load(); /* eslint-disable-next-line */ }, [range, reloadKey]);

  function util(m) { var total = m.minutes || 0; return total ? Math.round((m.billableMinutes / total) * 100) : 0; }
  return (
    <div className="svc-report">
      <div className="svc-ts-bar">
        <div className="svc-seg-group">
          {[['week', 'This week'], ['month', 'This month'], ['year', 'This year']].map(function (r) {
            return <button key={r[0]} className={'svc-seg' + (range === r[0] ? ' on' : '')} onClick={function () { setRange(r[0]); }}>{r[1]}</button>;
          })}
        </div>
      </div>
      {err ? <ErrorBanner message={err} onRetry={load} /> : !data ? (
        <div className="svc-list-skeleton">{[0, 1, 2].map(function (i) { return <div key={i} className="svc-skel-row" />; })}</div>
      ) : (
        <div className="svc-report-cols">
          <div className="svc-report-card">
            <div className="svc-report-title">By team member</div>
            {data.byUser.length === 0 ? <div className="svc-time-empty">No time in this window.</div> : data.byUser.map(function (m) {
              return <div key={m.user ? m.user.id : Math.random()} className="svc-report-row">
                <PmAvatar member={m.user} size={26} />
                <span className="svc-report-name">{m.user ? m.user.name : 'Unknown'}</span>
                <span className="svc-report-hours">{svcDuration(m.minutes)}</span>
                <span className="svc-report-util"><span className="svc-util-bar"><span className="svc-util-fill" style={{ width: util(m) + '%' }} /></span>{util(m)}%</span>
                <span className="svc-report-amt">{svcMoney(m.amount)}</span>
              </div>;
            })}
          </div>
          <div className="svc-report-card">
            <div className="svc-report-title">By client</div>
            {data.byClient.length === 0 ? <div className="svc-time-empty">No time in this window.</div> : data.byClient.map(function (c, i) {
              return <div key={i} className="svc-report-row">
                <span className="svc-report-name">{c.clientName}</span>
                <span className="svc-report-hours">{svcDuration(c.minutes)}</span>
                <span className="svc-report-amt">{svcMoney(c.amount)}</span>
              </div>;
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Service tab shell ─────────────────────────────────────────────────
function ServiceTab({ clientList, onNavigate }) {
  var [view, setView] = useState('queue');
  var [members, setMembers] = useState([]);
  var [overview, setOverview] = useState(null);
  var [openTicket, setOpenTicket] = useState(null);
  var [newOpen, setNewOpen] = useState(false);
  var [ratesOpen, setRatesOpen] = useState(false);
  var [reloadKey, setReloadKey] = useState(0);

  function loadOverview() { apiGet('/psa/overview').then(function (r) { if (r.ok) setOverview(r.data); }); }
  useEffect(function () {
    apiGet('/psa/members').then(function (r) { if (r.ok) setMembers(r.data.members || []); });
    loadOverview();
    function onSig() { loadOverview(); }
    window.addEventListener('psa:timer', onSig);
    return function () { window.removeEventListener('psa:timer', onSig); };
  }, []);
  function bump() { setReloadKey(function (k) { return k + 1; }); loadOverview(); }

  var c = overview ? overview.counts : {};
  var mt = overview ? overview.myTime : {};
  var stats = [
    { key: 'open', label: 'Open tickets', value: c.openTickets != null ? c.openTickets : '—', icon: 'inbox', onClick: function () { setView('queue'); } },
    { key: 'mine', label: 'Assigned to me', value: c.mineOpen != null ? c.mineOpen : '—', icon: 'user' },
    { key: 'unassigned', label: 'Unassigned', value: c.unassigned != null ? c.unassigned : '—', icon: 'users', warn: c.unassigned > 0 },
    { key: 'overdue', label: 'Overdue', value: c.overdue != null ? c.overdue : '—', icon: 'clock', warn: c.overdue > 0 },
    { key: 'today', label: 'My hours today', value: mt.todayMinutes != null ? svcDuration(mt.todayMinutes) : '—', icon: 'clock', onClick: function () { setView('timesheet'); } },
    { key: 'week', label: 'Billable this week', value: mt.weekAmount != null ? svcMoney(mt.weekAmount) : '—', icon: 'dollar', onClick: function () { setView('timesheet'); } },
  ];

  return (
    <div className="pm-tab svc-tab">
      <div className="svc-header">
        <div className="svc-header-left">
          <h1 className="pm-tab-title">Service</h1>
          <p className="pm-tab-sub">Your service desk and time tracking — tickets, timers, and billable hours in one place.</p>
        </div>
        <div className="svc-header-actions">
          <button className="pm-ghost-btn" onClick={function () { setRatesOpen(true); }} title="Billing rates"><PmIcon name="dollar" size={15} /> Rates</button>
          <button className="pm-primary-btn" onClick={function () { setNewOpen(true); }}><PmIcon name="plus" size={14} /> New ticket</button>
        </div>
      </div>

      <div className="svc-stats">
        {stats.map(function (s) {
          return <button key={s.key} className={'svc-stat' + (s.onClick ? ' clickable' : '') + (s.warn ? ' warn' : '')} onClick={s.onClick || function () {}}>
            <span className="svc-stat-icon"><PmIcon name={s.icon} size={16} /></span>
            <span className="svc-stat-body"><span className="svc-stat-value">{s.value}</span><span className="svc-stat-label">{s.label}</span></span>
          </button>;
        })}
      </div>

      <div className="svc-viewtabs">
        {[['queue', 'Queue'], ['timesheet', 'My timesheet'], ['report', 'Team']].map(function (v) {
          return <button key={v[0]} className={'svc-viewtab' + (view === v[0] ? ' on' : '')} onClick={function () { setView(v[0]); }}>{v[1]}</button>;
        })}
      </div>

      <div className="svc-view">
        {view === 'queue' && <SvcQueue members={members} clientList={clientList} onOpen={setOpenTicket} reloadKey={reloadKey} />}
        {view === 'timesheet' && <SvcTimesheet clientList={clientList} reloadKey={reloadKey} />}
        {view === 'report' && <SvcReport reloadKey={reloadKey} />}
      </div>

      {openTicket && <SvcTicketDrawer ticketId={openTicket} members={members} clientList={clientList} onClose={function () { setOpenTicket(null); }} onChanged={bump} />}
      {newOpen && <SvcNewTicketModal members={members} clientList={clientList} onClose={function () { setNewOpen(false); }} onCreated={function (t) { setNewOpen(false); bump(); setOpenTicket(t.id); }} />}
      {ratesOpen && <SvcRatesModal onClose={function () { setRatesOpen(false); }} />}
    </div>
  );
}

function WorkTab({ clientList }) {
  var [members, setMembers] = useState([]);
  var [overview, setOverview] = useState(null);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState(null);
  var [openProjectId, setOpenProjectId] = useState(null);
  var [openTaskId, setOpenTaskId] = useState(null);
  var [showArchived, setShowArchived] = useState(false);
  var [homeView, setHomeView] = useState(function() { return sessionStorage.getItem('pm_home') || 'projects'; });

  function loadOverview() {
    setLoading(true);
    apiGet('/pm/overview' + (showArchived ? '?archived=1' : '')).then(function(r) {
      if (r.ok) { setOverview(r.data); setError(null); } else { setError(r.error); }
      setLoading(false);
    });
  }
  useEffect(function() {
    apiGet('/pm/members').then(function(r) { if (r.ok) setMembers(r.data.members || []); });
  }, []);
  useEffect(function() { loadOverview(); }, [showArchived]);
  function setHome(v) { setHomeView(v); sessionStorage.setItem('pm_home', v); }
  function openProjectAt(projectId, taskId) { setOpenTaskId(taskId || null); setOpenProjectId(projectId); }

  if (openProjectId) {
    return <WorkProjectView
      projectId={openProjectId}
      initialTaskId={openTaskId}
      members={members}
      clientList={clientList}
      onOpenTask={openProjectAt}
      onBack={function() { setOpenProjectId(null); setOpenTaskId(null); loadOverview(); }}
    />;
  }

  return (
    <div className="tab-content pm-root">
      <div className="pm-home-head">
        <div>
          <h2 className="pm-home-title">Work</h2>
          <p className="pm-home-sub">CloudByte's delivery workspace — projects, tasks, and assignments across the team.</p>
        </div>
        <div className="pm-home-head-actions">
          <WorkNotifications onOpenTask={openProjectAt} />
          {homeView === 'projects' && <button className={'pm-ghost-btn' + (showArchived ? ' active' : '')} onClick={function() { setShowArchived(!showArchived); }}>{showArchived ? 'Active projects' : 'Archived'}</button>}
          <NewProjectButton members={members} clientList={clientList} onCreated={function(p) { openProjectAt(p.id); }} />
        </div>
      </div>

      <div className="pm-homeseg">
        <button className={'pm-homeseg-btn' + (homeView === 'projects' ? ' active' : '')} onClick={function() { setHome('projects'); }}>Projects</button>
        <button className={'pm-homeseg-btn' + (homeView === 'mywork' ? ' active' : '')} onClick={function() { setHome('mywork'); }}>My work{overview && overview.myTasks ? ' · ' + overview.myTasks.length : ''}</button>
      </div>

      {homeView === 'mywork' ? (
        <WorkMyWork onOpenTask={openProjectAt} onCountChange={function() { loadOverview(); }} />
      ) : (
        <React.Fragment>
          {error && <ErrorBanner message={error} onRetry={loadOverview} />}
          {loading && !overview ? <SkeletonLoader /> : overview && (
            <React.Fragment>
              {overview.projects.length === 0 ? (
                <div className="pm-empty">
                  <PmEmptyArt kind="projects" />
                  <div className="pm-empty-title">{showArchived ? 'No archived projects' : 'No projects yet'}</div>
                  <div className="pm-empty-text">{showArchived ? 'Projects you archive will show up here.' : 'Spin up your first project to start tracking delivery work — onboarding, security rollouts, client engagements, anything you run today in Asana.'}</div>
                  {!showArchived && <NewProjectButton members={members} clientList={clientList} onCreated={function(p) { openProjectAt(p.id); }} label="Create your first project" primary />}
                </div>
              ) : (
                <div className="pm-project-grid">
                  {overview.projects.map(function(p) {
                    return <ProjectCard key={p.id} project={p} clientList={clientList} onOpen={function() { openProjectAt(p.id); }} />;
                  })}
                </div>
              )}
            </React.Fragment>
          )}
        </React.Fragment>
      )}
    </div>
  );
}

// Due-date bucket for the My Work view. Completed tasks collapse into their
// own group regardless of date.
var PM_BUCKETS = [
  { key: 'overdue',  label: 'Overdue' },
  { key: 'today',    label: 'Today' },
  { key: 'tomorrow', label: 'Tomorrow' },
  { key: 'week',     label: 'This week' },
  { key: 'later',    label: 'Later' },
  { key: 'nodue',    label: 'No due date' },
  { key: 'done',     label: 'Completed' }
];
function pmBucketFor(task) {
  if (task.completed) return 'done';
  if (!task.dueDate) return 'nodue';
  var meta = pmDueMeta(task.dueDate, false);
  if (!meta) return 'nodue';
  if (meta.days < 0) return 'overdue';
  if (meta.days === 0) return 'today';
  if (meta.days === 1) return 'tomorrow';
  if (meta.days <= 7) return 'week';
  return 'later';
}

function WorkMyWork({ onOpenTask, onCountChange }) {
  var [tasks, setTasks] = useState(null);
  var [error, setError] = useState(null);
  var [showDone, setShowDone] = useState(false);

  function load() {
    apiGet('/pm/my-tasks' + (showDone ? '?done=1' : '')).then(function(r) {
      if (r.ok) { setTasks(r.data.tasks || []); setError(null); } else { setError(r.error); }
    });
  }
  useEffect(function() { load(); }, [showDone]);

  function toggle(t) {
    setTasks(function(list) { return list.map(function(x) { return x.id === t.id ? Object.assign({}, x, { completed: !x.completed }) : x; }); });
    apiSend('PATCH', '/pm/tasks/' + t.id, { completed: !t.completed }).then(function() { if (onCountChange) onCountChange(); if (!showDone) setTimeout(load, 350); });
  }
  function patchField(t, patch) {
    setTasks(function(list) { return list.map(function(x) { return x.id === t.id ? Object.assign({}, x, patch) : x; }); });
    apiSend('PATCH', '/pm/tasks/' + t.id, patch);
  }

  if (error) return <ErrorBanner message={error} onRetry={load} />;
  if (!tasks) return <SkeletonLoader />;

  var byBucket = {};
  tasks.forEach(function(t) { var b = pmBucketFor(t); (byBucket[b] = byBucket[b] || []).push(t); });
  var open = tasks.filter(function(t) { return !t.completed; });

  if (open.length === 0 && !showDone) {
    return (
      <div className="pm-empty">
        <PmEmptyArt kind="done" />
        <div className="pm-empty-title">Nothing on your plate</div>
        <div className="pm-empty-text">No open tasks are assigned to you right now. Assign yourself a task from any project board and it shows up here, grouped by when it's due.</div>
        <button className="pm-ghost-btn" onClick={function() { setShowDone(true); }}>Show completed</button>
      </div>
    );
  }

  return (
    <div className="pm-mywork">
      <div className="pm-mywork-bar">
        <span className="pm-mywork-count">{open.length} open task{open.length === 1 ? '' : 's'}</span>
        <button className={'pm-ghost-btn sm' + (showDone ? ' active' : '')} onClick={function() { setShowDone(!showDone); }}>{showDone ? 'Hide completed' : 'Show completed'}</button>
      </div>
      {PM_BUCKETS.map(function(b) {
        var group = byBucket[b.key];
        if (!group || !group.length) return null;
        if (b.key === 'done' && !showDone) return null;
        group.sort(function(a, x) { return (a.dueDate || '9999').localeCompare(x.dueDate || '9999') || ((PM_PRIORITY[x.priority] || PM_PRIORITY.none).rank - (PM_PRIORITY[a.priority] || PM_PRIORITY.none).rank); });
        return (
          <div key={b.key} className={'pm-mywork-group pm-mywork-' + b.key}>
            <div className="pm-mywork-group-head"><span>{b.label}</span><span className="pm-col-count">{group.length}</span></div>
            {group.map(function(t) {
              var due = pmDueMeta(t.dueDate, t.completed);
              return (
                <div key={t.id} className={'pm-mywork-row' + (t.completed ? ' done' : '')} onClick={function() { onOpenTask(t.projectId, t.id); }}>
                  <button className={'pm-check' + (t.completed ? ' checked' : '')} onClick={function(e) { e.stopPropagation(); toggle(t); }}>{t.completed ? <PmIcon name="check" weight={2.2} size={11} /> : null}</button>
                  <span className="pm-mywork-title">{t.title}</span>
                  <span className="pm-mywork-proj" style={{ color: pmColor(t.projectColor) }} title={t.projectName}>{t.projectIcon} {t.projectName}</span>
                  <span className="pm-list-row-spacer" />
                  <span className="pm-list-cell" onClick={function(e) { e.stopPropagation(); }}><PmPriorityPill priority={t.priority} onChange={function(p) { patchField(t, { priority: p }); }} /></span>
                  {due && <span className={'pm-due-chip pm-due-' + due.tone}>{due.label}</span>}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function ProjectCard({ project, clientList, onOpen }) {
  var p = project;
  var total = p.taskCount || 0, done = p.doneCount || 0;
  var pct = total > 0 ? Math.round((done / total) * 100) : 0;
  var accent = pmColor(p.color);
  var client = p.clientId && clientList ? (clientList.find(function(c) { return c.id === p.clientId; }) || null) : null;
  return (
    <button className="pm-project-card" onClick={onOpen} style={{ '--pm-accent': accent }}>
      <div className="pm-project-card-top">
        <span className="pm-project-icon" style={{ background: accent + '22', borderColor: accent + '44' }}>{p.icon}</span>
        <div className="pm-project-card-titles">
          <div className="pm-project-name">{p.name}</div>
          {p.description && <div className="pm-project-desc">{p.description}</div>}
        </div>
      </div>
      <div className="pm-project-progress"><div className="pm-project-progress-fill" style={{ width: pct + '%', background: accent }} /></div>
      <div className="pm-project-card-foot">
        <span>{done}/{total} done</span>
        {p.overdueCount > 0 && <span className="pm-overdue-badge">{p.overdueCount} overdue</span>}
        {p.visibility !== 'internal' && <span className="pm-vis-badge">{p.visibility === 'client' ? 'Client' : 'Partner'}</span>}
        {client && <span className="pm-client-badge">{client.name}</span>}
      </div>
    </button>
  );
}

function NewProjectButton({ members, clientList, onCreated, label, primary }) {
  var [open, setOpen] = useState(false);
  return (
    <React.Fragment>
      <button className={primary ? 'pm-primary-btn' : 'pm-primary-btn sm'} onClick={function() { setOpen(true); }}>+ {label || 'New project'}</button>
      {open && <NewProjectModal members={members} clientList={clientList} onClose={function() { setOpen(false); }} onCreated={function(p) { setOpen(false); onCreated(p); }} />}
    </React.Fragment>
  );
}

function NewProjectModal({ members, clientList, onClose, onCreated }) {
  var [name, setName] = useState('');
  var [description, setDescription] = useState('');
  var [color, setColor] = useState('blue');
  var [icon, setIcon] = useState('🚀');
  var [visibility, setVisibility] = useState('internal');
  var [clientId, setClientId] = useState('');
  var [saving, setSaving] = useState(false);
  var [err, setErr] = useState(null);

  function submit() {
    if (!name.trim()) { setErr('Give the project a name'); return; }
    setSaving(true); setErr(null);
    apiSend('POST', '/pm/projects', {
      name: name.trim(), description: description.trim(), color: color, icon: icon,
      visibility: visibility, clientId: visibility === 'internal' ? '' : clientId
    }).then(function(r) {
      setSaving(false);
      if (r.ok) onCreated(r.data.project); else setErr(r.error);
    });
  }

  return (
    <div className="pm-modal-overlay" onClick={onClose}>
      <div className="pm-modal" onClick={function(e) { e.stopPropagation(); }}>
        <div className="pm-modal-head"><span>New project</span><button className="pm-x" onClick={onClose}>×</button></div>
        <div className="pm-modal-body">
          <label className="pm-field">
            <span className="pm-field-label">Name</span>
            <input className="pm-input" autoFocus value={name} onChange={function(e) { setName(e.target.value); }} onKeyDown={function(e) { if (e.key === 'Enter') submit(); }} placeholder="e.g. GSI onboarding" />
          </label>
          <label className="pm-field">
            <span className="pm-field-label">Description <span className="pm-opt">(optional)</span></span>
            <input className="pm-input" value={description} onChange={function(e) { setDescription(e.target.value); }} placeholder="Short summary" />
          </label>
          <div className="pm-field-row">
            <div className="pm-field">
              <span className="pm-field-label">Icon</span>
              <div className="pm-icon-picker">{PM_ICONS.map(function(ic) { return <button key={ic} className={'pm-icon-opt' + (icon === ic ? ' active' : '')} onClick={function() { setIcon(ic); }}>{ic}</button>; })}</div>
            </div>
          </div>
          <div className="pm-field">
            <span className="pm-field-label">Color</span>
            <div className="pm-color-picker">{PM_COLOR_KEYS.map(function(k) { return <button key={k} className={'pm-color-opt' + (color === k ? ' active' : '')} style={{ background: PM_COLORS[k] }} onClick={function() { setColor(k); }} title={k} />; })}</div>
          </div>
          <div className="pm-field">
            <span className="pm-field-label">Visibility</span>
            <div className="pm-seg">
              {['internal','client','partner'].map(function(v) { return <button key={v} className={'pm-seg-btn' + (visibility === v ? ' active' : '')} onClick={function() { setVisibility(v); }}>{v.charAt(0).toUpperCase() + v.slice(1)}</button>; })}
            </div>
            <div className="pm-field-hint">Internal is CBG-only. Client / Partner tags are the seam for sharing this project externally later.</div>
          </div>
          {visibility !== 'internal' && clientList && (
            <label className="pm-field">
              <span className="pm-field-label">Linked client <span className="pm-opt">(optional)</span></span>
              <select className="pm-input" value={clientId} onChange={function(e) { setClientId(e.target.value); }}>
                <option value="">— none —</option>
                {clientList.map(function(c) { return <option key={c.id} value={c.id}>{c.name}</option>; })}
              </select>
            </label>
          )}
          {err && <div className="pm-modal-err">{err}</div>}
        </div>
        <div className="pm-modal-foot">
          <button className="pm-ghost-btn" onClick={onClose}>Cancel</button>
          <button className="pm-primary-btn" disabled={saving} onClick={submit}>{saving ? 'Creating…' : 'Create project'}</button>
        </div>
      </div>
    </div>
  );
}

// Build an editable Word document (HTML .doc — opens directly in Word) from
// an AI/deterministic project summary, and trigger a download.
function pmSummaryDocHtml(s) {
  function esc(x) { return String(x == null ? '' : x).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }
  var h = '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word"><head><meta charset="utf-8"><title>' + esc(s.projectName) + ' summary</title></head>';
  h += '<body style="font-family:Calibri,Arial,sans-serif;color:#1a1a1a;">';
  h += '<h1 style="margin-bottom:2px;">' + esc(s.projectName) + ' — Progress summary</h1>';
  h += '<p style="color:#666;margin-top:0;">Last ' + s.days + ' days · generated ' + new Date(s.generatedAt).toLocaleString() + '</p>';
  if (s.headline) h += '<p style="font-size:15px;font-weight:bold;">' + esc(s.headline) + '</p>';
  if (s.narrative) h += '<p>' + esc(s.narrative).replace(/\n/g, '<br>') + '</p>';
  if (s.highlights && s.highlights.length) h += '<h3>Highlights</h3><ul>' + s.highlights.map(function (x) { return '<li>' + esc(x) + '</li>'; }).join('') + '</ul>';
  h += '<h3>Completed (' + s.stats.completed + ')</h3>' + ((s.completed && s.completed.length) ? '<ul>' + s.completed.map(function (c) { return '<li>' + esc(c.title) + (c.at ? ' — ' + c.at : '') + '</li>'; }).join('') + '</ul>' : '<p>None.</p>');
  h += '<h3>Added (' + s.stats.added + ')</h3>' + ((s.added && s.added.length) ? '<ul>' + s.added.map(function (a) { return '<li>' + esc(a.title) + '</li>'; }).join('') + '</ul>' : '<p>None.</p>');
  if (s.attention && s.attention.length) h += '<h3>Needs attention</h3><ul>' + s.attention.map(function (x) { return '<li>' + esc(x) + '</li>'; }).join('') + '</ul>';
  h += '</body></html>';
  return h;
}
function pmDownloadDoc(filename, html) {
  var blob = new Blob(['﻿', html], { type: 'application/msword' });
  var url = URL.createObjectURL(blob);
  var a = document.createElement('a'); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(function () { URL.revokeObjectURL(url); }, 1000);
}

function WorkSummaryModal({ projectId, projectName, onClose }) {
  var [days, setDays] = useState(14);
  var [loading, setLoading] = useState(true);
  var [summary, setSummary] = useState(null);
  var [error, setError] = useState(null);

  function generate(d) {
    setLoading(true); setError(null); setSummary(null);
    apiSend('POST', '/pm/projects/' + projectId + '/summary', { days: d }).then(function (r) {
      setLoading(false);
      if (r.ok) setSummary(r.data.summary); else setError(r.error);
    });
  }
  useEffect(function () { generate(days); }, []); // auto-run on open with default range

  var s = summary;
  return (
    <div className="pm-modal-overlay" onClick={onClose}>
      <div className="pm-modal pm-summary-modal" onClick={function (e) { e.stopPropagation(); }}>
        <div className="pm-modal-head">
          <span><PmIcon name="sparkle" size={16} /> Project summary</span>
          <button className="pm-x" onClick={onClose}>×</button>
        </div>
        <div className="pm-summary-range">
          {[[7, 'Last 7 days'], [14, 'Last 14 days'], [30, 'Last 30 days']].map(function (r) {
            return <button key={r[0]} className={'pm-seg-btn' + (days === r[0] ? ' active' : '')} onClick={function () { setDays(r[0]); generate(r[0]); }}>{r[1]}</button>;
          })}
        </div>
        <div className="pm-modal-body pm-summary-body">
          {loading ? <div className="pm-summary-loading"><span className="pm-summary-spark"><PmIcon name="sparkle" size={22} /></span> Summarizing the last {days} days…</div>
            : error ? <ErrorBanner message={error} onRetry={function () { generate(days); }} />
            : s ? (
              <React.Fragment>
                {s.source === 'deterministic_cmmc' && <div className="pm-summary-note">AI narrative is off for regulated (CMMC/DIB) clients — this is a factual compile.</div>}
                <div className="pm-summary-stats">
                  <div className="pm-summary-stat"><span className="pm-summary-stat-n">{s.stats.completed}</span><span className="pm-summary-stat-l">Completed</span></div>
                  <div className="pm-summary-stat"><span className="pm-summary-stat-n">{s.stats.added}</span><span className="pm-summary-stat-l">Added</span></div>
                  <div className="pm-summary-stat"><span className="pm-summary-stat-n">{s.stats.open}</span><span className="pm-summary-stat-l">Open</span></div>
                  <div className={'pm-summary-stat' + (s.stats.overdue ? ' danger' : '')}><span className="pm-summary-stat-n">{s.stats.overdue}</span><span className="pm-summary-stat-l">Overdue</span></div>
                </div>
                {s.headline && <div className="pm-summary-headline">{s.headline}</div>}
                {s.narrative && <div className="pm-summary-narr">{s.narrative.split('\n').map(function (p, i) { return p.trim() ? <p key={i}>{p}</p> : null; })}</div>}
                {s.highlights && s.highlights.length > 0 && (
                  <div className="pm-summary-sec"><div className="pm-drawer-section-label">Highlights</div><ul className="pm-summary-list">{s.highlights.map(function (h, i) { return <li key={i}>{h}</li>; })}</ul></div>
                )}
                {s.attention && s.attention.length > 0 && (
                  <div className="pm-summary-sec"><div className="pm-drawer-section-label">Needs attention</div><ul className="pm-summary-list pm-summary-attention">{s.attention.map(function (h, i) { return <li key={i}>{h}</li>; })}</ul></div>
                )}
              </React.Fragment>
            ) : null}
        </div>
        <div className="pm-modal-foot">
          <button className="pm-ghost-btn" onClick={onClose}>Close</button>
          <button className="pm-primary-btn" disabled={!s} onClick={function () { pmDownloadDoc((projectName || 'project').replace(/[^\w.-]+/g, '-') + '-summary.doc', pmSummaryDocHtml(s)); }}>Export to Word</button>
        </div>
      </div>
    </div>
  );
}

// Month calendar — tasks placed on their due dates. Navigate months, click a
// task to open it. Tasks without a due date don't appear (see List/Board).
function WorkCalendarView({ tasks, onOpenTask }) {
  var [month, setMonth] = useState(function() { var d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  function fmt(d) { return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); }
  var todayStr = fmt(new Date());
  var byDate = {};
  (tasks || []).forEach(function(t) { if (t.dueDate) { (byDate[t.dueDate] = byDate[t.dueDate] || []).push(t); } });

  var first = new Date(month.getFullYear(), month.getMonth(), 1);
  var start = new Date(first); start.setDate(first.getDate() - first.getDay());
  var cells = [];
  var cur = new Date(start);
  for (var k = 0; k < 42; k++) { cells.push(new Date(cur)); cur.setDate(cur.getDate() + 1); }
  var monthLabel = month.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  function move(delta) { setMonth(new Date(month.getFullYear(), month.getMonth() + delta, 1)); }
  function goToday() { var d = new Date(); setMonth(new Date(d.getFullYear(), d.getMonth(), 1)); }
  var WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="pm-cal">
      <div className="pm-cal-bar">
        <div className="pm-cal-month">{monthLabel}</div>
        <span className="pm-card-meta-spacer" />
        <button className="pm-ghost-btn sm" onClick={goToday}>Today</button>
        <button className="pm-cal-navbtn" onClick={function() { move(-1); }} title="Previous month">‹</button>
        <button className="pm-cal-navbtn" onClick={function() { move(1); }} title="Next month">›</button>
      </div>
      <div className="pm-cal-grid">
        {WD.map(function(d) { return <div key={d} className="pm-cal-wd">{d}</div>; })}
        {cells.map(function(day, idx) {
          var ds = fmt(day);
          var inMonth = day.getMonth() === month.getMonth();
          var here = byDate[ds] || [];
          return (
            <div key={idx} className={'pm-cal-cell' + (inMonth ? '' : ' out') + (ds === todayStr ? ' today' : '')}>
              <div className="pm-cal-daynum">{day.getDate()}</div>
              <div className="pm-cal-tasks">
                {here.slice(0, 4).map(function(t) {
                  var meta = PM_PRIORITY[t.priority] || PM_PRIORITY.none;
                  return <button key={t.id} className={'pm-cal-task' + (t.completed ? ' done' : '')} onClick={function() { onOpenTask(t.id); }} title={t.title}>
                    <span className="pm-cal-task-dot" style={{ background: t.completed ? 'var(--accent-green)' : meta.color }} />{t.title}
                  </button>;
                })}
                {here.length > 4 && <div className="pm-cal-more">+{here.length - 4} more</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Project Files library — every attachment across the project's tasks,
// subtasks and comments, plus files uploaded directly here. One place to
// find and reference documents instead of digging through individual tasks.
function WorkFilesView({ projectId, askConfirm, onOpenTask }) {
  var [files, setFiles] = useState(null);
  var [error, setError] = useState(null);
  var [uploading, setUploading] = useState(0);
  var [dropActive, setDropActive] = useState(false);
  var [q, setQ] = useState('');
  var [typeFilter, setTypeFilter] = useState('all'); // all | images | docs
  var inputRef = useRef(null);

  function load() { apiGet('/pm/projects/' + projectId + '/files').then(function(r) { if (r.ok) { setFiles(r.data.files || []); setError(null); } else setError(r.error); }); }
  useEffect(function() { load(); }, [projectId]);

  function uploadFiles(list) {
    var arr = Array.prototype.slice.call(list || []);
    if (!arr.length) return;
    arr.forEach(function(file) {
      if (file.size > 25 * 1024 * 1024) { window.alert('"' + file.name + '" is larger than 25 MB.'); return; }
      setUploading(function(n) { return n + 1; });
      var fd = new FormData(); fd.append('file', file);
      authFetch(API + '/pm/projects/' + projectId + '/files', { method: 'POST', body: fd })
        .then(function(r) { return r.ok ? r.json() : r.json().then(function(e) { throw new Error(e.error || 'Upload failed'); }); })
        .then(function(data) { setFiles(function(l) { return [data.file].concat(l || []); }); })
        .catch(function(e) { window.alert('Upload failed: ' + e.message); })
        .then(function() { setUploading(function(n) { return Math.max(0, n - 1); }); });
    });
  }
  function removeFile(f) {
    var go = function() { setFiles(function(l) { return (l || []).filter(function(x) { return x.id !== f.id; }); }); apiSend('DELETE', '/pm/attachments/' + f.id); };
    if (askConfirm) askConfirm({ title: 'Remove file?', message: 'Permanently delete “' + f.filename + '”? This cannot be undone.', confirmLabel: 'Delete', danger: true, onConfirm: go });
    else go();
  }

  var filtered = (files || []).filter(function(f) {
    if (typeFilter === 'images' && !f.isImage) return false;
    if (typeFilter === 'docs' && f.isImage) return false;
    if (q.trim() && (f.filename || '').toLowerCase().indexOf(q.trim().toLowerCase()) === -1 && (f.taskTitle || '').toLowerCase().indexOf(q.trim().toLowerCase()) === -1) return false;
    return true;
  });

  return (
    <div className="pm-files" onDragOver={function(e) { if (e.dataTransfer && e.dataTransfer.types && Array.prototype.indexOf.call(e.dataTransfer.types, 'Files') !== -1) { e.preventDefault(); if (!dropActive) setDropActive(true); } }}
      onDragLeave={function(e) { if (e.currentTarget === e.target) setDropActive(false); }}
      onDrop={function(e) { if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length) { e.preventDefault(); setDropActive(false); uploadFiles(e.dataTransfer.files); } }}>
      {dropActive && <div className="pm-drop-overlay"><span>Drop files to add to the library</span></div>}
      <div className="pm-files-toolbar">
        <div className="pm-search-wrap">
          <span className="pm-search-icon"><PmIcon name="search" size={15} /></span>
          <input className="pm-search" value={q} placeholder="Search files…" onChange={function(e) { setQ(e.target.value); }} />
          {q && <button className="pm-search-x" onClick={function() { setQ(''); }}>×</button>}
        </div>
        <div className="pm-seg pm-files-seg">
          {[['all', 'All'], ['images', 'Images'], ['docs', 'Docs']].map(function(t) { return <button key={t[0]} className={'pm-seg-btn' + (typeFilter === t[0] ? ' active' : '')} onClick={function() { setTypeFilter(t[0]); }}>{t[1]}</button>; })}
        </div>
        <span className="pm-card-meta-spacer" />
        <input ref={inputRef} type="file" multiple style={{ display: 'none' }} onChange={function(e) { uploadFiles(e.target.files); e.target.value = ''; }} />
        <button className="pm-primary-btn sm" onClick={function() { if (inputRef.current) inputRef.current.click(); }}>+ Upload files</button>
      </div>

      {error && <ErrorBanner message={error} onRetry={load} />}
      {uploading > 0 && <div className="pm-attach-uploading">Uploading {uploading} file{uploading === 1 ? '' : 's'}…</div>}
      {!files ? <SkeletonLoader /> : filtered.length === 0 ? (
        <div className="pm-empty">
          <PmEmptyArt kind="projects" />
          <div className="pm-empty-title">{(files.length === 0) ? 'No files yet' : 'No files match'}</div>
          <div className="pm-empty-text">{(files.length === 0) ? 'Files attached to any task, subtask, or comment show up here automatically — or upload a document straight to the project library.' : 'Try a different search or filter.'}</div>
          {files.length === 0 && <button className="pm-primary-btn" onClick={function() { if (inputRef.current) inputRef.current.click(); }}>+ Upload files</button>}
        </div>
      ) : (
        <div className="pm-files-grid">
          {filtered.map(function(f) {
            var due = pmTimeAgo(f.createdAt);
            return (
              <div key={f.id} className="pm-file-card">
                <a className="pm-file-preview" href={f.url || '#'} target="_blank" rel="noopener noreferrer">
                  {f.isImage && f.url ? <img src={f.url} alt={f.filename} /> : <span className="pm-file-preview-icon"><PmFileIcon ct={f.contentType} name={f.filename} /></span>}
                </a>
                <button className="pm-file-del" title="Remove" onClick={function() { removeFile(f); }}><PmIcon name="x" size={13} /></button>
                <div className="pm-file-meta">
                  <a className="pm-file-name" href={f.url || '#'} target="_blank" rel="noopener noreferrer" title={f.filename}>{f.filename}</a>
                  <div className="pm-file-sub">{pmFileSize(f.sizeBytes)} · {due}</div>
                  {f.taskId
                    ? <button className="pm-file-source" onClick={function() { onOpenTask(f.taskId); }} title={f.taskTitle}><PmIcon name="subtask" size={11} /> {f.taskTitle}</button>
                    : <span className="pm-file-source pm-file-source-project">Project file</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function WorkProjectView({ projectId, initialTaskId, members, clientList, onBack, onOpenTask }) {
  var [data, setData] = useState(null);      // { project, sections, tasks }
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState(null);
  var [view, setView] = useState(function() { return sessionStorage.getItem('pm_view') || 'board'; });
  var [drawerTaskId, setDrawerTaskId] = useState(initialTaskId || null);
  var [fSearch, setFSearch] = useState('');
  var [fAssignee, setFAssignee] = useState(null); // null = anyone, 'unassigned', or member id
  var [fPriority, setFPriority] = useState(null); // null = any, or a priority key
  var [hideDone, setHideDone] = useState(false);
  var dragRef = useRef(null);              // dragged task id (sync during drag)
  var [draggingId, setDraggingId] = useState(null); // same, for render (ghost/hide)
  var dropHintRef = useRef(null);          // { s: sectionId, before: taskId|null }
  var [dropHint, setDropHint] = useState(null);
  var colDragRef = useRef(null);           // dragged section id (column reorder)
  var [draggingCol, setDraggingCol] = useState(null);
  var [colHint, setColHint] = useState(null); // section id to insert before, or 'end'
  var [selected, setSelected] = useState({});  // multi-select: taskId -> true
  var [confirm, setConfirm] = useState(null);   // delete-safeguard dialog
  var [summaryOpen, setSummaryOpen] = useState(false);

  var memberById = {}; members.forEach(function(m) { memberById[m.id] = m; });
  var selectedIds = Object.keys(selected).map(Number);
  function askConfirm(opts) { setConfirm(opts); }

  function load() {
    apiGet('/pm/projects/' + projectId).then(function(r) {
      if (r.ok) { setData(r.data); setError(null); } else { setError(r.error); }
      setLoading(false);
    });
  }
  useEffect(function() { setLoading(true); load(); }, [projectId]);
  useEffect(function() { if (initialTaskId) setDrawerTaskId(initialTaskId); }, [initialTaskId]);
  function setView2(v) { setView(v); sessionStorage.setItem('pm_view', v); }

  // ── local optimistic mutators ─────────────────────────────────────
  // UPDATE-ONLY: only touches tasks already on the board. Subtasks (edited via
  // the drawer) are NOT board tasks, so patching one must not inject it here.
  function upsertTask(task) {
    setData(function(d) {
      if (!d) return d;
      return Object.assign({}, d, { tasks: d.tasks.map(function(t) { return t.id === task.id ? Object.assign({}, t, task) : t; }) });
    });
  }
  function addTaskLocal(task) {
    setData(function(d) { if (!d) return d; if (d.tasks.some(function(t) { return t.id === task.id; })) return d; return Object.assign({}, d, { tasks: d.tasks.concat([task]) }); });
  }
  function removeTask(id) {
    setData(function(d) { if (!d) return d; return Object.assign({}, d, { tasks: d.tasks.filter(function(t) { return t.id !== id; }) }); });
  }
  function patchTask(id, patch) {
    upsertTask(Object.assign({ id: id }, patch));
    apiSend('PATCH', '/pm/tasks/' + id, patch).then(function(r) {
      if (r.ok) { upsertTask(r.data.task); if (r.data.spawned) addTaskLocal(r.data.spawned); } else load();
    });
  }
  function createTask(sectionId, title) {
    var t = title.trim(); if (!t) return Promise.resolve();
    return apiSend('POST', '/pm/projects/' + projectId + '/tasks', { title: t, sectionId: sectionId }).then(function(r) {
      if (r.ok) addTaskLocal(r.data.task);
    });
  }
  function createSubtask(parentId, sectionId, title) {
    var t = title.trim(); if (!t) return Promise.resolve();
    return apiSend('POST', '/pm/projects/' + projectId + '/tasks', { title: t, parentTaskId: parentId, sectionId: sectionId }).then(function(r) {
      if (r.ok) addTaskLocal(r.data.task);
    });
  }
  function childrenOf(taskId) {
    return (data ? data.tasks : []).filter(function(t) { return t.parentTaskId === taskId; })
      .sort(function(a, b) { return a.sortOrder - b.sortOrder || a.id - b.id; });
  }
  function deleteTask(id) { removeTask(id); if (drawerTaskId === id) setDrawerTaskId(null); apiSend('DELETE', '/pm/tasks/' + id); }

  // ── multi-select + bulk actions ───────────────────────────────────
  function toggleSelect(id) { setSelected(function(s) { var n = Object.assign({}, s); if (n[id]) delete n[id]; else n[id] = true; return n; }); }
  function clearSel() { setSelected({}); }
  function activateCard(id, e) {
    if (e && (e.metaKey || e.ctrlKey || e.shiftKey)) { e.preventDefault(); toggleSelect(id); }
    else setDrawerTaskId(id);
  }
  function bulkPatch(patch) { Object.keys(selected).map(Number).forEach(function(id) { patchTask(id, patch); }); clearSel(); }
  function bulkComplete(done) { Object.keys(selected).map(Number).forEach(function(id) { var tk = data.tasks.find(function(t) { return t.id === id; }); if (tk && !!tk.completed !== done) patchTask(id, { completed: done }); }); clearSel(); }
  function bulkDelete() {
    var ids = Object.keys(selected).map(Number); if (!ids.length) return;
    askConfirm({ title: 'Delete ' + ids.length + ' task' + (ids.length === 1 ? '' : 's') + '?', message: 'This permanently deletes ' + (ids.length === 1 ? 'the selected task and its subtasks' : 'the selected tasks and their subtasks') + '. This cannot be undone.', confirmLabel: 'Delete ' + ids.length, danger: true, onConfirm: function() { ids.forEach(deleteTask); clearSel(); } });
  }
  function boardKeyDown(e) {
    var tag = (e.target.tagName || '').toUpperCase();
    if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) return;
    if (e.key === 'Escape') { clearSel(); var a0 = document.activeElement; if (a0 && a0.blur) a0.blur(); return; }
    var active = document.activeElement;
    var isCard = active && active.classList && active.classList.contains('pm-card');
    if (!isCard) return;
    var id = parseInt(active.getAttribute('data-taskid'), 10);
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      var cards = Array.prototype.slice.call(e.currentTarget.querySelectorAll('.pm-card'));
      var idx = cards.indexOf(active);
      var next = cards[idx + (e.key === 'ArrowDown' ? 1 : -1)];
      if (next) next.focus();
    } else if (e.key === 'Enter') { e.preventDefault(); setDrawerTaskId(id); }
    else if (e.key === 'x' || e.key === ' ') { e.preventDefault(); toggleSelect(id); }
    else if (e.key === 'c') { e.preventDefault(); var tk = data.tasks.find(function(t) { return t.id === id; }); if (tk) patchTask(id, { completed: !tk.completed }); }
    else if (e.key === 'Backspace' || e.key === 'Delete') { e.preventDefault(); var dt = data.tasks.find(function(t) { return t.id === id; }); askConfirm({ title: 'Delete task?', message: 'Delete “' + (dt ? dt.title : 'this task') + '” and its subtasks? This cannot be undone.', confirmLabel: 'Delete', danger: true, onConfirm: function() { deleteTask(id); } }); }
  }

  function sectionTasks(sectionId) {
    return (data ? data.tasks : []).filter(function(t) { return !t.parentTaskId && (sectionId === null ? !t.sectionId : t.sectionId === sectionId); })
      .sort(function(a, b) { return a.sortOrder - b.sortOrder; });
  }
  function orderFor(sectionId, beforeTaskId) {
    var list = sectionTasks(sectionId).filter(function(t) { return t.id !== dragRef.current; });
    if (!beforeTaskId) return list.length ? list[list.length - 1].sortOrder + 1000 : 1000;
    var idx = -1; for (var i = 0; i < list.length; i++) { if (list[i].id === beforeTaskId) { idx = i; break; } }
    if (idx <= 0) return list.length ? list[0].sortOrder - 1000 : 1000;
    return (list[idx - 1].sortOrder + list[idx].sortOrder) / 2;
  }
  // ── task drag (card) ──────────────────────────────────────────────
  function startTaskDrag(id) { dragRef.current = id; setDraggingId(id); dropHintRef.current = null; setDropHint(null); }
  function setTaskHint(sectionId, beforeId) {
    if (dragRef.current == null) return;
    var h = { s: sectionId, before: beforeId };
    dropHintRef.current = h;
    setDropHint(function(prev) { return (prev && prev.s === h.s && prev.before === h.before) ? prev : h; });
  }
  function endTaskDrag() { dragRef.current = null; setDraggingId(null); dropHintRef.current = null; setDropHint(null); }
  function dropTask() {
    var id = dragRef.current, hint = dropHintRef.current;
    endTaskDrag();
    if (id == null || !hint) return;
    patchTask(id, { sectionId: hint.s, sortOrder: orderFor(hint.s, hint.before) });
  }

  // ── column drag (section reorder) ─────────────────────────────────
  function startColDrag(id) { colDragRef.current = id; setDraggingCol(id); setColHint(null); }
  function setColHintTarget(sectionId) {
    if (colDragRef.current == null || colDragRef.current === sectionId) { setColHint(function(p){ return p; }); return; }
    setColHint(function(prev) { return prev === sectionId ? prev : sectionId; });
  }
  function endColDrag() { colDragRef.current = null; setDraggingCol(null); setColHint(null); }
  function dropColumn() {
    var id = colDragRef.current, target = colHint;
    endColDrag();
    if (id == null || id === target) return;
    var ordered = (data.sections || []).slice().sort(function(a, b) { return a.sortOrder - b.sortOrder; });
    var rest = ordered.filter(function(s) { return s.id !== id; });
    var newOrder;
    if (target === 'end' || target == null) {
      newOrder = rest.length ? rest[rest.length - 1].sortOrder + 1000 : 1000;
    } else {
      var idx = -1; for (var i = 0; i < rest.length; i++) { if (rest[i].id === target) { idx = i; break; } }
      if (idx <= 0) newOrder = rest.length ? rest[0].sortOrder - 1000 : 1000;
      else newOrder = (rest[idx - 1].sortOrder + rest[idx].sortOrder) / 2;
    }
    setData(function(d) { return Object.assign({}, d, { sections: d.sections.map(function(s) { return s.id === id ? Object.assign({}, s, { sortOrder: newOrder }) : s; }) }); });
    apiSend('PATCH', '/pm/sections/' + id, { sortOrder: newOrder });
  }

  // ── sections ──────────────────────────────────────────────────────
  function addSection(name) {
    return apiSend('POST', '/pm/projects/' + projectId + '/sections', { name: name }).then(function(r) {
      if (r.ok) setData(function(d) { return Object.assign({}, d, { sections: d.sections.concat([r.data.section]) }); });
    });
  }
  function renameSection(id, name) {
    setData(function(d) { return Object.assign({}, d, { sections: d.sections.map(function(s) { return s.id === id ? Object.assign({}, s, { name: name }) : s; }) }); });
    apiSend('PATCH', '/pm/sections/' + id, { name: name });
  }
  function deleteSection(id) {
    setData(function(d) {
      return Object.assign({}, d, {
        sections: d.sections.filter(function(s) { return s.id !== id; }),
        tasks: d.tasks.map(function(t) { return t.sectionId === id ? Object.assign({}, t, { sectionId: null }) : t; })
      });
    });
    apiSend('DELETE', '/pm/sections/' + id);
  }
  function confirmDeleteSection(section) {
    var n = sectionTasks(section.id).length;
    askConfirm({
      title: 'Delete “' + section.name + '”?',
      message: n > 0 ? ('This section has ' + n + ' task' + (n === 1 ? '' : 's') + '. They won\'t be deleted — they\'ll move to Uncategorized.') : 'Delete this empty section?',
      confirmLabel: 'Delete section', danger: true, onConfirm: function() { deleteSection(section.id); }
    });
  }

  // ── project ───────────────────────────────────────────────────────
  function patchProject(patch) {
    setData(function(d) { return Object.assign({}, d, { project: Object.assign({}, d.project, patch) }); });
    apiSend('PATCH', '/pm/projects/' + projectId, patch);
  }
  function importPoam() {
    apiSend('POST', '/pm/projects/' + projectId + '/import-poam', {}).then(function(r) {
      if (r.ok) { load(); window.alert(r.data.message || ('Imported ' + r.data.imported + ' compliance item' + (r.data.imported === 1 ? '' : 's') + (r.data.skipped ? ' (' + r.data.skipped + ' already tracked)' : '') + '.')); }
      else window.alert(r.error);
    });
  }
  function archiveProject() { apiSend('PATCH', '/pm/projects/' + projectId, { status: 'archived' }).then(onBack); }
  function deleteProject() {
    askConfirm({
      title: 'Delete this project?',
      message: 'This permanently deletes “' + p.name + '” and all of its tasks, subtasks, comments, and attachments. This cannot be undone.',
      confirmLabel: 'Delete project', danger: true, onConfirm: function() { apiSend('DELETE', '/pm/projects/' + projectId).then(onBack); }
    });
  }

  if (loading) return <div className="tab-content pm-root"><button className="pm-back" onClick={onBack}>← Work</button><SkeletonLoader /></div>;
  if (error || !data) return <div className="tab-content pm-root"><button className="pm-back" onClick={onBack}>← Work</button><ErrorBanner message={error || 'Not found'} onRetry={function() { setLoading(true); load(); }} /></div>;

  var p = data.project;
  var accent = pmColor(p.color);
  var total = data.tasks.filter(function(t) { return !t.parentTaskId; }).length;
  var done = data.tasks.filter(function(t) { return !t.parentTaskId && t.completed; }).length;
  var drawerTask = drawerTaskId ? data.tasks.find(function(t) { return t.id === drawerTaskId; }) : null;

  // Display filters. Applied only at render — sectionTasks() stays unfiltered so
  // drag-reorder math (orderFor) still sees the true neighbor positions.
  var filterActive = !!(fSearch.trim() || fAssignee !== null || fPriority !== null || hideDone);
  function matchesFilter(t) {
    if (hideDone && t.completed) return false;
    if (fSearch.trim() && (t.title || '').toLowerCase().indexOf(fSearch.trim().toLowerCase()) === -1) return false;
    if (fAssignee !== null) { if (fAssignee === 'unassigned') { if (t.assigneeId) return false; } else if (t.assigneeId !== fAssignee) return false; }
    if (fPriority !== null && t.priority !== fPriority) return false;
    return true;
  }
  function visible(sectionId) { return sectionTasks(sectionId).filter(matchesFilter); }
  function clearFilters() { setFSearch(''); setFAssignee(null); setFPriority(null); setHideDone(false); }
  var uncategorized = visible(null);

  return (
    <div className="tab-content pm-root pm-project-root" style={{ '--pm-accent': accent }}>
      <div className="pm-proj-bar">
        <div className="pm-proj-bar-left">
          <button className="pm-back" onClick={onBack}>← Work</button>
          <span className="pm-proj-icon-sm" style={{ background: accent + '22', borderColor: accent + '44' }}>{p.icon}</span>
          <div className="pm-proj-heading">
            <div className="pm-proj-name-row">
              <span className="pm-proj-name">{p.name}</span>
              {p.visibility !== 'internal' && <span className="pm-vis-badge">{p.visibility === 'client' ? 'Client' : 'Partner'}</span>}
            </div>
            <div className="pm-proj-substat">{done}/{total} done · {data.sections.length} sections</div>
          </div>
        </div>
        <div className="pm-proj-bar-right">
          <button className="pm-summary-btn" onClick={function() { setSummaryOpen(true); }} title="AI project summary"><PmIcon name="sparkle" size={14} /> Summary</button>
          {onOpenTask && <WorkNotifications onOpenTask={onOpenTask} />}
          <div className="pm-viewseg">
            <button className={'pm-viewseg-btn' + (view === 'board' ? ' active' : '')} onClick={function() { setView2('board'); }}>Board</button>
            <button className={'pm-viewseg-btn' + (view === 'list' ? ' active' : '')} onClick={function() { setView2('list'); }}>List</button>
            <button className={'pm-viewseg-btn' + (view === 'calendar' ? ' active' : '')} onClick={function() { setView2('calendar'); }}>Calendar</button>
            <button className={'pm-viewseg-btn' + (view === 'files' ? ' active' : '')} onClick={function() { setView2('files'); }}>Files</button>
          </div>
          <PmMenu align="right" trigger={<button className="pm-icon-btn" title="Project settings"><PmIcon name="kebab" size={16} /></button>}>
            {function(close) { return (
              <div className="pm-settings-menu">
                <div className="pm-menu-label">Accent</div>
                <div className="pm-color-picker pm-color-picker-sm">{PM_COLOR_KEYS.map(function(k) { return <button key={k} className={'pm-color-opt' + (p.color === k ? ' active' : '')} style={{ background: PM_COLORS[k] }} onClick={function() { patchProject({ color: k }); }} />; })}</div>
                <div className="pm-menu-label">Linked client</div>
                <select className="pm-mini-select pm-settings-client" value={p.clientId || ''} onClick={function(e) { e.stopPropagation(); }} onChange={function(e) { patchProject({ clientId: e.target.value || null }); }}>
                  <option value="">None (internal)</option>
                  {(clientList || []).map(function(c) { return <option key={c.id} value={c.id}>{c.name}</option>; })}
                </select>
                {p.clientId && <button className="pm-menu-item" onClick={function() { close(); importPoam(); }}><PmIcon name="shield" size={15} /> Import compliance items</button>}
                <button className="pm-menu-item" onClick={function() { close(); archiveProject(); }}>Archive project</button>
                <button className="pm-menu-item pm-menu-danger" onClick={function() { close(); deleteProject(); }}>Delete project</button>
              </div>
            ); }}
          </PmMenu>
        </div>
      </div>

      {view !== 'files' && view !== 'calendar' && (
      <div className="pm-proj-toolbar">
        <div className="pm-search-wrap">
          <span className="pm-search-icon"><PmIcon name="search" size={15} /></span>
          <input className="pm-search" value={fSearch} placeholder="Search tasks…" onChange={function(e) { setFSearch(e.target.value); }} />
          {fSearch && <button className="pm-search-x" onClick={function() { setFSearch(''); }}>×</button>}
        </div>
        <PmMenu trigger={<button className={'pm-filter-btn' + (fAssignee !== null ? ' active' : '')}>{fAssignee === null ? 'Assignee' : (fAssignee === 'unassigned' ? 'Unassigned' : (memberById[fAssignee] ? memberById[fAssignee].name : 'Assignee'))}<span className="pm-filter-caret"><PmIcon name="chevron-down" size={12} /></span></button>}>
          {function(close) { return (
            <div className="pm-assignee-list">
              <button className="pm-menu-item" onClick={function() { setFAssignee(null); close(); }}>Anyone{fAssignee === null && <span className="pm-menu-check">✓</span>}</button>
              <button className="pm-menu-item" onClick={function() { setFAssignee('unassigned'); close(); }}><PmAvatar member={null} size={20} /> Unassigned{fAssignee === 'unassigned' && <span className="pm-menu-check">✓</span>}</button>
              {members.map(function(m) { return <button key={m.id} className="pm-menu-item" onClick={function() { setFAssignee(m.id); close(); }}><PmAvatar member={m} size={20} /> {m.name}{fAssignee === m.id && <span className="pm-menu-check">✓</span>}</button>; })}
            </div>
          ); }}
        </PmMenu>
        <PmMenu trigger={<button className={'pm-filter-btn' + (fPriority !== null ? ' active' : '')}>{fPriority === null ? 'Priority' : PM_PRIORITY[fPriority].label}<span className="pm-filter-caret"><PmIcon name="chevron-down" size={12} /></span></button>}>
          {function(close) { return (
            <React.Fragment>
              <button className="pm-menu-item" onClick={function() { setFPriority(null); close(); }}>Any priority{fPriority === null && <span className="pm-menu-check">✓</span>}</button>
              {['urgent', 'high', 'medium', 'low', 'none'].map(function(pk) { return <button key={pk} className="pm-menu-item" onClick={function() { setFPriority(pk); close(); }}><span className="pm-prio-dot" style={{ background: PM_PRIORITY[pk].color, opacity: pk === 'none' ? 0.4 : 1 }} />{PM_PRIORITY[pk].label}{fPriority === pk && <span className="pm-menu-check">✓</span>}</button>; })}
            </React.Fragment>
          ); }}
        </PmMenu>
        <button className={'pm-filter-btn' + (hideDone ? ' active' : '')} onClick={function() { setHideDone(!hideDone); }}>{hideDone ? '✓ Completed hidden' : 'Hide completed'}</button>
        {filterActive && <button className="pm-filter-clear" onClick={clearFilters}>Clear filters</button>}
      </div>
      )}

      {view === 'files' ? (
        <WorkFilesView projectId={projectId} askConfirm={askConfirm} onOpenTask={setDrawerTaskId} />
      ) : view === 'calendar' ? (
        <WorkCalendarView tasks={data.tasks.filter(function(t) { return !t.parentTaskId; })} onOpenTask={setDrawerTaskId} />
      ) : view === 'board' ? (
        <div className="pm-board" onKeyDown={boardKeyDown}>
          {data.sections.slice().sort(function(a, b) { return a.sortOrder - b.sortOrder; }).map(function(s) {
            return <PmColumn key={s.id} section={s} tasks={visible(s.id)} members={members} memberById={memberById}
              onAddTask={function(title) { return createTask(s.id, title); }}
              onCardActivate={activateCard} selected={selected} onToggle={function(t) { patchTask(t.id, { completed: !t.completed }); }}
              onPatch={patchTask} onRename={function(name) { renameSection(s.id, name); }} onDelete={function() { confirmDeleteSection(s); }}
              dragRef={dragRef} draggingId={draggingId} dropHint={dropHint} onCardHint={setTaskHint} onCardDrop={dropTask} onStartDrag={startTaskDrag} onEndDrag={endTaskDrag}
              draggingCol={draggingCol} colHint={colHint} onColHint={setColHintTarget} onColDrop={dropColumn} onStartColDrag={startColDrag} onEndColDrag={endColDrag} />;
          })}
          {draggingCol != null && (
            <div className={'pm-col-endzone' + (colHint === 'end' ? ' active' : '')}
              onDragOver={function(e) { e.preventDefault(); setColHint('end'); }} onDrop={function(e) { e.preventDefault(); dropColumn(); }} />
          )}
          {uncategorized.length > 0 && (
            <PmColumn section={{ id: null, name: 'Uncategorized' }} tasks={uncategorized} members={members} memberById={memberById}
              onAddTask={function(title) { return createTask(null, title); }} onCardActivate={activateCard} selected={selected}
              onToggle={function(t) { patchTask(t.id, { completed: !t.completed }); }} onPatch={patchTask}
              dragRef={dragRef} draggingId={draggingId} dropHint={dropHint} onCardHint={setTaskHint} onCardDrop={dropTask} onStartDrag={startTaskDrag} onEndDrag={endTaskDrag} fixed />
          )}
          <PmAddColumn onAdd={addSection} />
        </div>
      ) : (
        <PmListView data={data} sectionTasks={visible} members={members} childrenOf={childrenOf} selected={selected}
          onCardActivate={activateCard} onToggle={function(t) { patchTask(t.id, { completed: !t.completed }); }}
          onAddTask={createTask} onAddSubtask={createSubtask} onPatch={patchTask}
          dragRef={dragRef} draggingId={draggingId} dropHint={dropHint} onRowHint={setTaskHint} onRowDrop={dropTask} onStartDrag={startTaskDrag} onEndDrag={endTaskDrag} />
      )}

      {selectedIds.length > 0 && (
        <div className="pm-bulkbar">
          <span className="pm-bulk-count">{selectedIds.length} selected</span>
          <button className="pm-bulk-btn" onClick={function() { bulkComplete(true); }}>✓ Complete</button>
          <PmMenu up trigger={<button className="pm-bulk-btn">Assignee ▾</button>}>
            {function(close) { return (
              <div className="pm-assignee-list">
                <button className="pm-menu-item" onClick={function() { bulkPatch({ assigneeId: null }); close(); }}><PmAvatar member={null} size={20} /> Unassigned</button>
                {members.map(function(m) { return <button key={m.id} className="pm-menu-item" onClick={function() { bulkPatch({ assigneeId: m.id }); close(); }}><PmAvatar member={m} size={20} /> {m.name}</button>; })}
              </div>
            ); }}
          </PmMenu>
          <PmMenu up trigger={<button className="pm-bulk-btn">Priority ▾</button>}>
            {function(close) { return PM_PRIORITY_ORDER.map(function(pp) { return <button key={pp} className="pm-menu-item" onClick={function() { bulkPatch({ priority: pp }); close(); }}><span className="pm-prio-dot" style={{ background: PM_PRIORITY[pp].color, opacity: pp === 'none' ? 0.4 : 1 }} />{PM_PRIORITY[pp].label}</button>; }); }}
          </PmMenu>
          <PmMenu up trigger={<button className="pm-bulk-btn">Move ▾</button>}>
            {function(close) { return (
              <div>
                {data.sections.slice().sort(function(a, b) { return a.sortOrder - b.sortOrder; }).map(function(s) { return <button key={s.id} className="pm-menu-item" onClick={function() { bulkPatch({ sectionId: s.id }); close(); }}>{s.name}</button>; })}
                <button className="pm-menu-item" onClick={function() { bulkPatch({ sectionId: null }); close(); }}>Uncategorized</button>
              </div>
            ); }}
          </PmMenu>
          <button className="pm-bulk-btn pm-bulk-danger" onClick={bulkDelete}>Delete</button>
          <button className="pm-bulk-btn pm-bulk-clear" onClick={clearSel}>Clear</button>
        </div>
      )}

      {drawerTaskId && (
        <TaskDrawer taskId={drawerTaskId} seed={drawerTask} project={p} sections={data.sections} members={members}
          siblingTasks={data.tasks.filter(function(t) { return !t.parentTaskId; })}
          onClose={function() { setDrawerTaskId(null); }} onPatched={upsertTask} onDeleted={deleteTask}
          onLocalPatch={patchTask} onOpenTask={setDrawerTaskId} onReloadBoard={function() { load(); }} askConfirm={askConfirm} />
      )}

      {confirm && (
        <PmConfirm title={confirm.title} message={confirm.message} confirmLabel={confirm.confirmLabel} danger={confirm.danger}
          onCancel={function() { setConfirm(null); }}
          onConfirm={function() { var fn = confirm.onConfirm; setConfirm(null); if (fn) fn(); }} />
      )}

      {summaryOpen && <WorkSummaryModal projectId={projectId} projectName={p.name} onClose={function() { setSummaryOpen(false); }} />}
    </div>
  );
}

function PmColumn(props) {
  var section = props.section, tasks = props.tasks, memberById = props.memberById, fixed = props.fixed;
  var onAddTask = props.onAddTask, onCardActivate = props.onCardActivate, onToggle = props.onToggle, selected = props.selected || {};
  var onRename = props.onRename, onDelete = props.onDelete;
  var dragRef = props.dragRef, draggingId = props.draggingId, dropHint = props.dropHint;
  var onCardHint = props.onCardHint, onCardDrop = props.onCardDrop, onStartDrag = props.onStartDrag, onEndDrag = props.onEndDrag;
  var draggingCol = props.draggingCol, colHint = props.colHint, onColHint = props.onColHint, onColDrop = props.onColDrop, onStartColDrag = props.onStartColDrag, onEndColDrag = props.onEndColDrag;

  var [adding, setAdding] = useState(false);
  var [draft, setDraft] = useState('');
  var [editing, setEditing] = useState(false);
  var [name, setName] = useState(section.name);
  var [collapsed, setCollapsed] = useState(false);
  var bodyRef = useRef(null);

  if (collapsed) {
    return (
      <div className="pm-col pm-col-is-collapsed" onClick={function() { setCollapsed(false); }} title="Expand section">
        <button className="pm-col-menu-btn" onClick={function(e) { e.stopPropagation(); setCollapsed(false); }}><PmIcon name="chevron-down" size={14} /></button>
        <span className="pm-col-count">{tasks.length}</span>
        <span className="pm-col-collapsed-name">{section.name}</span>
      </div>
    );
  }

  function submit() { if (draft.trim()) { onAddTask(draft); setDraft(''); } setAdding(false); }

  // Task drag: measure the pointer against rendered cards to decide the
  // insert-before target (a real card id, or null for end-of-column).
  function bodyDragOver(e) {
    if (dragRef.current == null) return;   // ignore column drags here
    e.preventDefault();
    var before = null;
    var cards = bodyRef.current ? bodyRef.current.querySelectorAll('.pm-card') : [];
    for (var i = 0; i < cards.length; i++) {
      var cid = parseInt(cards[i].getAttribute('data-taskid'), 10);
      if (cid === dragRef.current) continue;   // don't measure against the source card
      var rect = cards[i].getBoundingClientRect();
      if (e.clientY < rect.top + rect.height / 2) { before = cid; break; }
    }
    onCardHint(section.id, before);
  }
  function bodyDrop(e) { if (dragRef.current == null) return; e.preventDefault(); onCardDrop(); }

  var isTaskTarget = dropHint && dropHint.s === section.id;
  var isColTarget = !fixed && colHint === section.id && draggingCol !== section.id;

  return (
    <div className={'pm-col' + (isTaskTarget ? ' pm-col-over' : '') + (isColTarget ? ' pm-col-coltarget' : '') + (draggingCol === section.id ? ' pm-col-dragging' : '')}
      onDragOver={function(e) { if (draggingCol != null && !fixed) { e.preventDefault(); onColHint(section.id); } }}
      onDrop={function(e) { if (draggingCol != null && !fixed) { e.preventDefault(); onColDrop(); } }}>
      <div className={'pm-col-head' + (!fixed ? ' pm-col-head-grab' : '')}
        draggable={!fixed && !editing}
        onDragStart={function(e) { if (fixed || editing) return; e.stopPropagation(); onStartColDrag(section.id); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', 'col-' + section.id); } catch (x) {} }}
        onDragEnd={function() { if (!fixed) onEndColDrag(); }}>
        {!fixed && <span className="pm-col-grip" title="Drag to reorder"><PmIcon name="grip" size={14} /></span>}
        {editing && !fixed ? (
          <input className="pm-col-name-input" autoFocus value={name} onChange={function(e) { setName(e.target.value); }}
            onBlur={function() { setEditing(false); if (name.trim() && name !== section.name) onRename(name.trim()); else setName(section.name); }}
            onKeyDown={function(e) { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setName(section.name); setEditing(false); } }} />
        ) : (
          <span className="pm-col-name" onClick={function() { if (!fixed) setEditing(true); }}>{section.name}</span>
        )}
        <span className="pm-col-count">{tasks.length}</span>
        <button className="pm-col-collapse" onClick={function() { setCollapsed(true); }} title="Collapse section"><PmIcon name="chevron-down" size={13} /></button>
        {!fixed && onDelete && (
          <PmMenu align="right" trigger={<button className="pm-col-menu-btn"><PmIcon name="kebab" size={15} /></button>}>
            {function(close) { return (
              <React.Fragment>
                <button className="pm-menu-item" onClick={function() { close(); setEditing(true); }}>Rename</button>
                <button className="pm-menu-item pm-menu-danger" onClick={function() { close(); onDelete(); }}>Delete section</button>
              </React.Fragment>
            ); }}
          </PmMenu>
        )}
      </div>
      <div className="pm-col-body" ref={bodyRef} onDragOver={bodyDragOver} onDrop={bodyDrop}>
        {(function() {
          var out = [];
          tasks.forEach(function(t) {
            if (isTaskTarget && dropHint.before === t.id) out.push(<div key="ph" className="pm-card-placeholder" />);
            out.push(<PmCard key={t.id} task={t} member={memberById[t.assigneeId]} dragging={t.id === draggingId} selected={!!selected[t.id]} onActivate={function(e) { onCardActivate(t.id, e); }}
              onToggle={function() { onToggle(t); }} onStartDrag={onStartDrag} onEndDrag={onEndDrag} />);
          });
          if (isTaskTarget && dropHint.before == null) out.push(<div key="ph-end" className="pm-card-placeholder" />);
          return out;
        })()}
        {adding ? (
          <div className="pm-add-task">
            <textarea className="pm-add-task-input" autoFocus value={draft} placeholder="Task name…"
              onChange={function(e) { setDraft(e.target.value); }}
              onKeyDown={function(e) { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (draft.trim()) { onAddTask(draft); setDraft(''); } } if (e.key === 'Escape') { setDraft(''); setAdding(false); } }}
              onBlur={submit} />
          </div>
        ) : (
          <button className="pm-add-task-btn" onClick={function() { setAdding(true); }}>+ Add task</button>
        )}
      </div>
    </div>
  );
}

function PmCard({ task, member, dragging, selected, onActivate, onToggle, onStartDrag, onEndDrag }) {
  var t = task;
  var due = pmDueMeta(t.dueDate, t.completed);
  var prio = PM_PRIORITY[t.priority] || PM_PRIORITY.none;
  return (
    <div className={'pm-card' + (t.completed ? ' pm-card-done' : '') + (dragging ? ' pm-card-ghost' : '') + (selected ? ' pm-card-selected' : '')} data-taskid={t.id} tabIndex={0}
      draggable
      onDragStart={function(e) { onStartDrag(t.id); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', String(t.id)); } catch (x) {} }}
      onDragEnd={function() { onEndDrag(); }}
      onClick={function(e) { onActivate(e); }}>
      {t.priority !== 'none' && !t.completed && <span className="pm-card-prio-bar" style={{ background: prio.color }} />}
      <div className="pm-card-main">
        <button className={'pm-check' + (t.completed ? ' checked' : '')} onClick={function(e) { e.stopPropagation(); onToggle(); }} title={t.completed ? 'Mark incomplete' : 'Mark complete'}>{t.completed ? <PmIcon name="check" weight={2.2} size={11} /> : null}</button>
        <span className="pm-card-title">{t.title}</span>
      </div>
      {(t.tags && t.tags.length > 0) && <div className="pm-card-tags">{t.tags.map(function(tag, i) { return <span key={i} className="pm-tag">{tag}</span>; })}</div>}
      <div className="pm-card-meta">
        {t.blockedCount > 0 && <span className="pm-blocked-chip" title="Blocked by an incomplete task">Blocked</span>}
        {t.source && t.source.kind === 'poam' && <span className="pm-source-chip" title={'CMMC POA&M ' + (t.source.meta.poamNumber || '') + (t.source.meta.controlId ? ' · ' + t.source.meta.controlId : '')}><PmIcon name="shield" size={11} /> {t.source.meta.poamNumber || 'POA&M'}</span>}
        {t.priority !== 'none' && <span className="pm-prio-mini" style={{ color: prio.color }}><span className="pm-prio-flag" style={{ color: prio.color }}><PmIcon name="flag" size={12} /></span>{prio.label}</span>}
        {due && <span className={'pm-due-chip pm-due-' + due.tone}>{due.label}</span>}
        {t.recurrence && t.recurrence !== 'none' && <span className="pm-card-mini" title="Repeats"><PmIcon name="repeat" size={12} /></span>}
        <span className="pm-card-meta-spacer" />
        {t.subtaskCount > 0 && <span className="pm-card-mini"><PmIcon name="subtask" size={13} /> {t.subtaskCount}</span>}
        {t.commentCount > 0 && <span className="pm-card-mini"><PmIcon name="comment" size={13} /> {t.commentCount}</span>}
        {t.attachmentCount > 0 && <span className="pm-card-mini"><PmIcon name="clip" size={13} /> {t.attachmentCount}</span>}
        {member && <PmAvatar member={member} size={22} />}
      </div>
    </div>
  );
}

function PmAddColumn({ onAdd }) {
  var [adding, setAdding] = useState(false);
  var [name, setName] = useState('');
  function submit() { if (name.trim()) onAdd(name.trim()); setName(''); setAdding(false); }
  if (!adding) return <button className="pm-addcol-btn" onClick={function() { setAdding(true); }}>+ Add section</button>;
  return (
    <div className="pm-addcol">
      <input className="pm-input" autoFocus value={name} placeholder="Section name" onChange={function(e) { setName(e.target.value); }}
        onKeyDown={function(e) { if (e.key === 'Enter') submit(); if (e.key === 'Escape') { setName(''); setAdding(false); } }} onBlur={submit} />
    </div>
  );
}

function PmListView({ data, sectionTasks, members, childrenOf, selected, onCardActivate, onToggle, onAddTask, onAddSubtask, onPatch, dragRef, draggingId, dropHint, onRowHint, onRowDrop, onStartDrag, onEndDrag }) {
  var [sort, setSort] = useState({ key: 'manual', dir: 1 });
  var memberById = {}; members.forEach(function(m) { memberById[m.id] = m; });
  function aName(t) { var m = memberById[t.assigneeId]; return m ? (m.name || '') : '￿'; }
  function setSortKey(key) { setSort(function(s) { return s.key === key ? { key: key, dir: -s.dir } : { key: key, dir: 1 }; }); }
  function sortList(list) {
    if (sort.key === 'manual') return list;
    var d = sort.dir;
    return list.slice().sort(function(a, b) {
      if (sort.key === 'name') return d * (a.title || '').localeCompare(b.title || '');
      if (sort.key === 'due') return d * ((a.dueDate || '9999-99-99').localeCompare(b.dueDate || '9999-99-99'));
      if (sort.key === 'priority') return d * ((PM_PRIORITY[b.priority] || PM_PRIORITY.none).rank - (PM_PRIORITY[a.priority] || PM_PRIORITY.none).rank);
      if (sort.key === 'assignee') return d * aName(a).localeCompare(aName(b));
      return d * (a.sortOrder - b.sortOrder);
    });
  }
  function arrow(key) { return sort.key === key ? <span className="pm-lh-arrow" style={{ transform: sort.dir < 0 ? 'rotate(180deg)' : 'none' }}><PmIcon name="chevron-down" size={11} /></span> : null; }
  var groups = data.sections.slice().sort(function(a, b) { return a.sortOrder - b.sortOrder; }).map(function(s) { return { section: s, tasks: sortList(sectionTasks(s.id)) }; });
  var unc = sectionTasks(null);
  if (unc.length) groups.push({ section: { id: null, name: 'Uncategorized' }, tasks: sortList(unc) });
  return (
    <div className="pm-list">
      <div className="pm-list-header">
        <span className="pm-list-caret-spacer" /><span className="pm-check pm-check-ghost" />
        <button className={'pm-lh pm-lh-title' + (sort.key === 'name' ? ' active' : '')} onClick={function() { setSortKey('name'); }}>Task name {arrow('name')}</button>
        <span className="pm-list-row-spacer" />
        <button className={'pm-lh pm-lc-prio' + (sort.key === 'priority' ? ' active' : '')} onClick={function() { setSortKey('priority'); }}>Priority {arrow('priority')}</button>
        <button className={'pm-lh pm-lc-due' + (sort.key === 'due' ? ' active' : '')} onClick={function() { setSortKey('due'); }}>Due {arrow('due')}</button>
        <button className={'pm-lh pm-lc-assignee' + (sort.key === 'assignee' ? ' active' : '')} onClick={function() { setSortKey('assignee'); }}>Assignee {arrow('assignee')}</button>
      </div>
      {groups.map(function(g) {
        return <PmListGroup key={g.section.id == null ? 'unc' : g.section.id} group={g} members={members} childrenOf={childrenOf} selected={selected}
          onCardActivate={onCardActivate} onToggle={onToggle} onAddTask={onAddTask} onAddSubtask={onAddSubtask} onPatch={onPatch}
          dragEnabled={sort.key === 'manual'} dragRef={dragRef} draggingId={draggingId} dropHint={dropHint} onRowHint={onRowHint} onRowDrop={onRowDrop} onStartDrag={onStartDrag} onEndDrag={onEndDrag} />;
      })}
    </div>
  );
}

function PmListGroup({ group, members, childrenOf, selected, onCardActivate, onToggle, onAddTask, onAddSubtask, onPatch, dragEnabled, dragRef, draggingId, dropHint, onRowHint, onRowDrop, onStartDrag, onEndDrag }) {
  var [adding, setAdding] = useState(false);
  var [draft, setDraft] = useState('');
  var rowsRef = useRef(null);
  function submit() { if (draft.trim()) onAddTask(group.section.id, draft); setDraft(''); setAdding(false); }

  // Measure the pointer against the top-level rows (which carry data-taskid)
  // to decide the insert-before target for a list drag.
  function rowsDragOver(e) {
    if (!dragEnabled || dragRef.current == null) return;
    e.preventDefault();
    var before = null;
    var rows = rowsRef.current ? rowsRef.current.querySelectorAll('.pm-list-row[data-taskid]') : [];
    for (var i = 0; i < rows.length; i++) {
      var rid = parseInt(rows[i].getAttribute('data-taskid'), 10);
      if (rid === dragRef.current) continue;
      var rect = rows[i].getBoundingClientRect();
      if (e.clientY < rect.top + rect.height / 2) { before = rid; break; }
    }
    onRowHint(group.section.id, before);
  }
  function rowsDrop(e) { if (!dragEnabled || dragRef.current == null) return; e.preventDefault(); onRowDrop(); }
  function dropLine(beforeId) { return (dragEnabled && dropHint && dropHint.s === group.section.id && dropHint.before === beforeId) ? <div key={'dl' + beforeId} className="pm-list-drop-line" /> : null; }

  return (
    <div className="pm-list-group">
      <div className="pm-list-group-head"><span>{group.section.name}</span><span className="pm-col-count">{group.tasks.length}</span></div>
      <div className="pm-list-rows" ref={rowsRef} onDragOver={rowsDragOver} onDrop={rowsDrop}>
        {group.tasks.map(function(t) {
          return <React.Fragment key={t.id}>
            {dropLine(t.id)}
            <PmListRow task={t} depth={0} members={members} childrenOf={childrenOf} selected={selected}
              onCardActivate={onCardActivate} onToggle={onToggle} onAddSubtask={onAddSubtask} onPatch={onPatch}
              dragEnabled={dragEnabled} draggingId={draggingId} onStartDrag={onStartDrag} onEndDrag={onEndDrag} />
          </React.Fragment>;
        })}
        {dropLine(null)}
        {adding ? (
          <div className="pm-list-row pm-list-add">
            <span className="pm-list-caret-spacer" /><span className="pm-check" />
            <input className="pm-list-add-input" autoFocus value={draft} placeholder="Task name…" onChange={function(e) { setDraft(e.target.value); }}
              onKeyDown={function(e) { if (e.key === 'Enter') { if (draft.trim()) { onAddTask(group.section.id, draft); setDraft(''); } } if (e.key === 'Escape') { setDraft(''); setAdding(false); } }} onBlur={submit} />
          </div>
        ) : (
          <button className="pm-list-addbtn" onClick={function() { setAdding(true); }}>+ Add task</button>
        )}
      </div>
    </div>
  );
}

// One list row + (when expanded) its subtasks, recursively. This is the List
// view's subtask nesting — the parity gap Asana has and we were missing.
function PmListRow({ task, depth, members, childrenOf, selected, onCardActivate, onToggle, onAddSubtask, onPatch, dragEnabled, draggingId, onStartDrag, onEndDrag }) {
  var t = task;
  var kids = childrenOf(t.id);
  var hasKids = kids.length > 0;
  var [expanded, setExpanded] = useState(false);
  var [addingSub, setAddingSub] = useState(false);
  var [subDraft, setSubDraft] = useState('');
  var doneKids = kids.filter(function(k) { return k.completed; }).length;
  var indent = 8 + depth * 22;
  function submitSub() { if (subDraft.trim()) { onAddSubtask(t.id, t.sectionId, subDraft); setSubDraft(''); setExpanded(true); } setAddingSub(false); }
  return (
    <React.Fragment>
      <div className={'pm-list-row' + (t.completed ? ' done' : '') + (selected && selected[t.id] ? ' selected' : '') + (draggingId === t.id ? ' pm-card-ghost' : '') + (depth === 0 && dragEnabled ? ' pm-list-draggable' : '')}
        data-taskid={depth === 0 ? t.id : undefined}
        draggable={depth === 0 && dragEnabled ? true : undefined}
        onDragStart={depth === 0 && dragEnabled ? function(e) { onStartDrag(t.id); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', String(t.id)); } catch (x) {} } : undefined}
        onDragEnd={depth === 0 && dragEnabled ? function() { onEndDrag(); } : undefined}
        style={{ paddingLeft: indent }} onClick={function(e) { onCardActivate(t.id, e); }}>
        {hasKids
          ? <button className={'pm-list-caret' + (expanded ? '' : ' collapsed')} onClick={function(e) { e.stopPropagation(); setExpanded(!expanded); }} title={expanded ? 'Collapse subtasks' : 'Expand subtasks'}><PmIcon name="chevron-down" size={12} /></button>
          : <span className="pm-list-caret-spacer" />}
        <button className={'pm-check' + (t.completed ? ' checked' : '')} onClick={function(e) { e.stopPropagation(); onToggle(t); }}>{t.completed ? <PmIcon name="check" weight={2.2} size={11} /> : null}</button>
        <span className="pm-list-title">{t.title}</span>
        {t.blockedCount > 0 && <span className="pm-blocked-chip" title="Blocked by an incomplete task">Blocked</span>}
        {t.source && t.source.kind === 'poam' && <span className="pm-source-chip" title={'CMMC POA&M ' + (t.source.meta.poamNumber || '') + (t.source.meta.controlId ? ' · ' + t.source.meta.controlId : '')}><PmIcon name="shield" size={11} /> {t.source.meta.poamNumber || 'POA&M'}</span>}
        {t.recurrence && t.recurrence !== 'none' && <span className="pm-list-repeat" title="Repeats"><PmIcon name="repeat" size={12} /></span>}
        {hasKids && <span className="pm-list-subcount" title="Subtasks completed"><PmIcon name="subtask" size={12} /> {doneKids}/{kids.length}</span>}
        {(t.tags || []).map(function(tag, i) { return <span key={i} className="pm-tag">{tag}</span>; })}
        <span className="pm-list-row-spacer" />
        <span className="pm-list-cell pm-lc-prio" onClick={function(e) { e.stopPropagation(); }}><PmPriorityPill priority={t.priority} onChange={function(p) { onPatch(t.id, { priority: p }); }} /></span>
        <span className="pm-list-cell pm-lc-due pm-list-due" onClick={function(e) { e.stopPropagation(); }}><PmDueInline value={t.dueDate} onChange={function(d) { onPatch(t.id, { dueDate: d }); }} /></span>
        <span className="pm-list-cell pm-lc-assignee" onClick={function(e) { e.stopPropagation(); }}><PmAssignee assigneeId={t.assigneeId} members={members} onChange={function(id) { onPatch(t.id, { assigneeId: id }); }} /></span>
      </div>
      {expanded && (
        <React.Fragment>
          {kids.map(function(k) {
            return <PmListRow key={k.id} task={k} depth={depth + 1} members={members} childrenOf={childrenOf} selected={selected}
              onCardActivate={onCardActivate} onToggle={onToggle} onAddSubtask={onAddSubtask} onPatch={onPatch}
              dragEnabled={dragEnabled} draggingId={draggingId} onStartDrag={onStartDrag} onEndDrag={onEndDrag} />;
          })}
          {addingSub ? (
            <div className="pm-list-row pm-list-add" style={{ paddingLeft: 8 + (depth + 1) * 22 }}>
              <span className="pm-list-caret-spacer" /><span className="pm-check" />
              <input className="pm-list-add-input" autoFocus value={subDraft} placeholder="Subtask name…" onChange={function(e) { setSubDraft(e.target.value); }}
                onKeyDown={function(e) { if (e.key === 'Enter') { if (subDraft.trim()) { onAddSubtask(t.id, t.sectionId, subDraft); setSubDraft(''); } } if (e.key === 'Escape') { setSubDraft(''); setAddingSub(false); } }} onBlur={submitSub} />
            </div>
          ) : (
            <button className="pm-list-addsub" style={{ paddingLeft: 8 + (depth + 1) * 22 + 22 }} onClick={function() { setAddingSub(true); }}>+ Add subtask</button>
          )}
        </React.Fragment>
      )}
    </React.Fragment>
  );
}

// Inline due-date control — shows the due chip (or a "+ Due" affordance) and
// opens a date picker on click. Used on subtask rows for at-a-glance editing.
function PmDueInline({ value, onChange, placeholder }) {
  var meta = value ? pmDueMeta(value, false) : null;
  var trigger = value
    ? <span className={'pm-due-chip pm-due-' + (meta ? meta.tone : 'normal')}>{meta ? meta.label : value}</span>
    : <span className="pm-due-add">{placeholder || '+ Due'}</span>;
  return (
    <PmMenu align="right" trigger={trigger}>
      {function(close) { return (
        <div className="pm-due-pop">
          <input type="date" className="pm-date-input" autoFocus value={value || ''} onChange={function(e) { onChange(e.target.value || null); }} />
          {value && <button className="pm-menu-item" onClick={function() { onChange(null); close(); }}>Clear due date</button>}
        </div>
      ); }}
    </PmMenu>
  );
}

// Searchable task picker in a dropdown — used to add dependencies.
function PmTaskPickerButton({ tasks, excludeIds, onPick, label }) {
  var [q, setQ] = useState('');
  var ex = excludeIds || [];
  var candidates = (tasks || []).filter(function(t) { return ex.indexOf(t.id) === -1 && (t.title || '').toLowerCase().indexOf(q.toLowerCase()) !== -1; }).slice(0, 60);
  return (
    <PmMenu trigger={<button className="pm-dep-add">+ {label}</button>}>
      {function(close) { return (
        <div className="pm-taskpick">
          <input className="pm-input pm-taskpick-input" autoFocus placeholder="Search tasks…" value={q} onChange={function(e) { setQ(e.target.value); }} onClick={function(e) { e.stopPropagation(); }} />
          <div className="pm-taskpick-list">
            {candidates.length === 0 ? <div className="pm-menu-item" style={{ color: 'var(--text-muted)' }}>No matching tasks</div> :
              candidates.map(function(c) { return <button key={c.id} className="pm-menu-item" onClick={function() { onPick(c.id); setQ(''); close(); }}>{c.title}</button>; })}
          </div>
        </div>
      ); }}
    </PmMenu>
  );
}

function TaskActionsMenu({ task, siblingTasks, onDuplicate, onPromote, onDemote, onDelete }) {
  var [mode, setMode] = useState('menu');
  var [q, setQ] = useState('');
  var candidates = (siblingTasks || []).filter(function(x) { return x.id !== task.id && (x.title || '').toLowerCase().indexOf(q.toLowerCase()) !== -1; }).slice(0, 60);
  return (
    <PmMenu align="right" trigger={<button className="pm-icon-btn" title="Task actions"><PmIcon name="kebab" size={16} /></button>}>
      {function(close) {
        if (mode === 'pick') {
          return (
            <div className="pm-taskpick">
              <button className="pm-taskpick-back" onClick={function(e) { e.stopPropagation(); setMode('menu'); }}>← Back</button>
              <div className="pm-menu-label">Make subtask of…</div>
              <input className="pm-input pm-taskpick-input" autoFocus placeholder="Search tasks…" value={q} onChange={function(e) { setQ(e.target.value); }} onClick={function(e) { e.stopPropagation(); }} />
              <div className="pm-taskpick-list">
                {candidates.length === 0 ? <div className="pm-menu-item" style={{ color: 'var(--text-muted)' }}>No matching tasks</div> :
                  candidates.map(function(c) { return <button key={c.id} className="pm-menu-item" onClick={function() { onDemote(c.id); setMode('menu'); close(); }}>{c.title}</button>; })}
              </div>
            </div>
          );
        }
        return (
          <React.Fragment>
            <button className="pm-menu-item" onClick={function() { close(); onDuplicate(); }}>Duplicate task</button>
            {task.parentTaskId
              ? <button className="pm-menu-item" onClick={function() { close(); onPromote(); }}>Promote to task</button>
              : <button className="pm-menu-item" onClick={function(e) { e.stopPropagation(); setMode('pick'); }}>Make subtask of…</button>}
            <button className="pm-menu-item pm-menu-danger" onClick={function() { close(); onDelete(); }}>Delete task</button>
          </React.Fragment>
        );
      }}
    </PmMenu>
  );
}

function TaskDrawer({ taskId, seed, project, sections, members, siblingTasks, onClose, onPatched, onDeleted, onLocalPatch, onOpenTask, onReloadBoard, askConfirm }) {
  var [detail, setDetail] = useState(null);   // { task, parent, subtasks, comments }
  var [title, setTitle] = useState(seed ? seed.title : '');
  var [desc, setDesc] = useState('');
  var [comment, setComment] = useState('');
  var [subDraft, setSubDraft] = useState('');
  var [tagDraft, setTagDraft] = useState('');
  var [uploading, setUploading] = useState(0);   // count of in-flight uploads
  var [dropActive, setDropActive] = useState(false);
  var [editingDesc, setEditingDesc] = useState(false);
  var fileInputRef = useRef(null);
  var memberById = {}; members.forEach(function(m) { memberById[m.id] = m; });

  function load() {
    apiGet('/pm/tasks/' + taskId).then(function(r) {
      if (r.ok) { setDetail(r.data); setTitle(r.data.task.title); setDesc(r.data.task.description || ''); }
    });
  }
  // Refresh detail (task + activity + subtasks) without touching the local
  // title/description drafts — used after an edit to pull the new activity event.
  function reload() { apiGet('/pm/tasks/' + taskId).then(function(r) { if (r.ok) setDetail(r.data); }); }
  // Reset editable fields when switching tasks (e.g. navigating into a subtask)
  // so the previous task's title/description don't flash while loading.
  useEffect(function() { setDetail(null); setTitle(''); setDesc(''); load(); }, [taskId]);

  function patch(patchObj) {
    onLocalPatch(taskId, patchObj);   // updates board + drawer seed
    setDetail(function(d) { if (!d) return d; return Object.assign({}, d, { task: Object.assign({}, d.task, patchObj) }); });
    apiSend('PATCH', '/pm/tasks/' + taskId, patchObj).then(function(r) { if (r.ok) { onPatched(r.data.task); reload(); } });
  }
  var t = detail ? detail.task : (seed || {});

  function addSubtask() {
    if (!subDraft.trim()) return;
    apiSend('POST', '/pm/projects/' + project.id + '/tasks', { title: subDraft.trim(), parentTaskId: taskId, sectionId: t.sectionId }).then(function(r) {
      if (r.ok) { setDetail(function(d) { return Object.assign({}, d, { subtasks: (d.subtasks || []).concat([r.data.task]) }); }); setSubDraft(''); }
    });
  }
  // Inline subtask edits — subtasks are full tasks, so this patches any field
  // (completed / assignee / due / priority) optimistically.
  function patchSub(subId, patchObj) {
    setDetail(function(d) { if (!d) return d; return Object.assign({}, d, { subtasks: d.subtasks.map(function(s) { return s.id === subId ? Object.assign({}, s, patchObj) : s; }) }); });
    apiSend('PATCH', '/pm/tasks/' + subId, patchObj);
  }
  function toggleSub(sub) { patchSub(sub.id, { completed: !sub.completed }); }
  function addComment() {
    if (!comment.trim()) return;
    apiSend('POST', '/pm/tasks/' + taskId + '/comments', { body: comment.trim() }).then(function(r) {
      if (r.ok) {
        var c = r.data.comment;
        var item = { key: 'c' + c.id, type: 'comment', id: c.id, actor: c.author, body: c.body, createdAt: c.createdAt };
        setDetail(function(d) { return Object.assign({}, d, { activity: (d.activity || []).concat([item]) }); });
        setComment('');
      }
    });
  }
  function addTag() {
    var v = tagDraft.trim(); if (!v) return;
    var tags = (t.tags || []).concat([v]);
    patch({ tags: tags }); setTagDraft('');
  }
  function removeTag(idx) { var tags = (t.tags || []).slice(); tags.splice(idx, 1); patch({ tags: tags }); }

  function uploadFiles(fileList) {
    var files = Array.prototype.slice.call(fileList || []);
    if (!files.length) return;
    files.forEach(function(file) {
      if (file.size > 25 * 1024 * 1024) { window.alert('"' + file.name + '" is larger than 25 MB.'); return; }
      setUploading(function(n) { return n + 1; });
      var fd = new FormData(); fd.append('file', file);
      authFetch(API + '/pm/tasks/' + taskId + '/attachments', { method: 'POST', body: fd })
        .then(function(r) { return r.ok ? r.json() : r.json().then(function(e) { throw new Error(e.error || 'Upload failed'); }); })
        .then(function(data) {
          setDetail(function(d) { return d ? Object.assign({}, d, { attachments: (d.attachments || []).concat([data.attachment]) }) : d; });
          reload();
        })
        .catch(function(e) { window.alert('Upload failed: ' + e.message); })
        .then(function() { setUploading(function(n) { return Math.max(0, n - 1); }); });
    });
  }
  function doRemoveAttachment(id) {
    setDetail(function(d) { return d ? Object.assign({}, d, { attachments: (d.attachments || []).filter(function(a) { return a.id !== id; }) }) : d; });
    apiSend('DELETE', '/pm/attachments/' + id).then(reload);
  }
  function removeAttachment(att) {
    var id = typeof att === 'object' ? att.id : att;
    var nm = typeof att === 'object' ? att.filename : 'this file';
    if (askConfirm) askConfirm({ title: 'Remove attachment?', message: 'Permanently delete “' + nm + '”? This cannot be undone.', confirmLabel: 'Delete', danger: true, onConfirm: function() { doRemoveAttachment(id); } });
    else doRemoveAttachment(id);
  }
  function addCollaborator(mem) {
    setDetail(function(d) { if (!d) return d; if ((d.followers || []).some(function(f) { return f.id === mem.id; })) return d; return Object.assign({}, d, { followers: (d.followers || []).concat([mem]) }); });
    apiSend('POST', '/pm/tasks/' + taskId + '/followers', { userId: mem.id });
  }
  function removeCollaborator(id) {
    setDetail(function(d) { return d ? Object.assign({}, d, { followers: (d.followers || []).filter(function(f) { return f.id !== id; }) }) : d; });
    apiSend('DELETE', '/pm/tasks/' + taskId + '/followers/' + id);
  }
  function addDependency(blockingId) {
    apiSend('POST', '/pm/tasks/' + taskId + '/dependencies', { blockingTaskId: blockingId }).then(function(r) {
      if (r.ok) { setDetail(function(d) { return d ? Object.assign({}, d, { blockedBy: (d.blockedBy || []).concat([r.data.dependency]) }) : d; }); if (onReloadBoard) onReloadBoard(); }
      else window.alert(r.error);
    });
  }
  function removeDependency(blockingId) {
    setDetail(function(d) { return d ? Object.assign({}, d, { blockedBy: (d.blockedBy || []).filter(function(x) { return x.id !== blockingId; }) }) : d; });
    apiSend('DELETE', '/pm/tasks/' + taskId + '/dependencies/' + blockingId).then(function() { if (onReloadBoard) onReloadBoard(); });
  }
  function removeBlocking(blockedId) {
    setDetail(function(d) { return d ? Object.assign({}, d, { blocking: (d.blocking || []).filter(function(x) { return x.id !== blockedId; }) }) : d; });
    apiSend('DELETE', '/pm/tasks/' + blockedId + '/dependencies/' + taskId).then(function() { if (onReloadBoard) onReloadBoard(); });
  }
  function duplicateTask() {
    apiSend('POST', '/pm/tasks/' + taskId + '/duplicate', {}).then(function(r) { if (r.ok) { if (onReloadBoard) onReloadBoard(); if (onOpenTask) onOpenTask(r.data.task.id); } else window.alert(r.error); });
  }
  function promoteTask() {
    apiSend('POST', '/pm/tasks/' + taskId + '/promote', {}).then(function(r) { if (r.ok) { if (onReloadBoard) onReloadBoard(); reload(); } else window.alert(r.error); });
  }
  function demoteTask(parentId) {
    apiSend('POST', '/pm/tasks/' + taskId + '/demote', { parentTaskId: parentId }).then(function(r) { if (r.ok) { if (onReloadBoard) onReloadBoard(); reload(); } else window.alert(r.error); });
  }
  function onDrawerDrop(e) {
    if (!e.dataTransfer || !e.dataTransfer.files || !e.dataTransfer.files.length) return;
    e.preventDefault(); setDropActive(false); uploadFiles(e.dataTransfer.files);
  }
  function onDrawerDragOver(e) {
    if (e.dataTransfer && e.dataTransfer.types && Array.prototype.indexOf.call(e.dataTransfer.types, 'Files') !== -1) {
      e.preventDefault(); if (!dropActive) setDropActive(true);
    }
  }

  var due = pmDueMeta(t.dueDate, t.completed);
  return (
    <div className="pm-drawer-overlay" onClick={onClose}>
      <div className={'pm-drawer' + (dropActive ? ' pm-drop-active' : '')} onClick={function(e) { e.stopPropagation(); }}
        onDragOver={onDrawerDragOver} onDragLeave={function(e) { if (e.currentTarget === e.target) setDropActive(false); }} onDrop={onDrawerDrop}>
        {dropActive && <div className="pm-drop-overlay"><span>Drop files to attach</span></div>}
        <div className="pm-drawer-head">
          <button className={'pm-check pm-check-lg' + (t.completed ? ' checked' : '')} onClick={function() { patch({ completed: !t.completed }); }}>{t.completed ? <PmIcon name="check" weight={2.2} size={13} /> : null}</button>
          <span className="pm-drawer-complete-label">{t.completed ? 'Completed' : 'Mark complete'}</span>
          <span className="pm-card-meta-spacer" />
          <TaskActionsMenu task={t} siblingTasks={siblingTasks} onDuplicate={duplicateTask} onPromote={promoteTask} onDemote={demoteTask}
            onDelete={function() { askConfirm ? askConfirm({ title: 'Delete task?', message: 'Delete “' + (t.title || 'this task') + '” and its subtasks, comments, and attachments? This cannot be undone.', confirmLabel: 'Delete', danger: true, onConfirm: function() { onDeleted(taskId); } }) : onDeleted(taskId); }} />
          <button className="pm-x" onClick={onClose}>×</button>
        </div>
        <div className="pm-drawer-body">
          {detail && detail.parent && (
            <button className="pm-breadcrumb" onClick={function() { if (onOpenTask) onOpenTask(detail.parent.id); }}>↑ {detail.parent.title}</button>
          )}
          <textarea className="pm-drawer-title" value={title} rows={1} placeholder="Task title"
            onChange={function(e) { setTitle(e.target.value); }}
            onBlur={function() { if (title.trim() && title !== t.title) patch({ title: title.trim() }); else if (!title.trim()) setTitle(t.title); }} />

          {t.source && t.source.kind === 'poam' && (
            <div className="pm-source-banner">
              <PmIcon name="shield" size={15} />
              <span>Linked to CMMC POA&amp;M <b>{t.source.meta.poamNumber}</b>{t.source.meta.controlId ? ' · control ' + t.source.meta.controlId : ''}{t.source.meta.severity ? ' · ' + t.source.meta.severity + ' severity' : ''}. Completing this task closes the POA&amp;M item.</span>
            </div>
          )}

          <div className="pm-drawer-props">
            <div className="pm-prop"><span className="pm-prop-label">Assignee</span><span className="pm-prop-val"><PmAssignee assigneeId={t.assigneeId} members={members} onChange={function(id) { patch({ assigneeId: id }); }} size={26} /><span className="pm-prop-name">{t.assigneeId && memberById[t.assigneeId] ? memberById[t.assigneeId].name : 'Unassigned'}</span></span></div>
            <div className="pm-prop"><span className="pm-prop-label">Priority</span><span className="pm-prop-val"><PmPriorityPill priority={t.priority} onChange={function(p) { patch({ priority: p }); }} /></span></div>
            <div className="pm-prop"><span className="pm-prop-label">Start date</span><span className="pm-prop-val"><input type="date" className="pm-date-input" value={t.startDate || ''} max={t.dueDate || undefined} onChange={function(e) { patch({ startDate: e.target.value || null }); }} /></span></div>
            <div className="pm-prop"><span className="pm-prop-label">Due date</span><span className="pm-prop-val"><input type="date" className="pm-date-input" value={t.dueDate || ''} onChange={function(e) { patch({ dueDate: e.target.value || null }); }} />{t.dueDate && <input type="time" className="pm-time-input" value={t.dueTime || ''} onChange={function(e) { patch({ dueTime: e.target.value || null }); }} />}{due && due.tone === 'overdue' && <span className="pm-due-chip pm-due-overdue">Overdue</span>}</span></div>
            <div className="pm-prop"><span className="pm-prop-label">Repeat</span><span className="pm-prop-val">
              <select className="pm-mini-select" value={t.recurrence || 'none'} onChange={function(e) { patch({ recurrence: e.target.value }); }}>
                <option value="none">Does not repeat</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="biweekly">Every 2 weeks</option>
                <option value="monthly">Monthly</option>
              </select>
              {t.recurrence && t.recurrence !== 'none' && <span className="pm-repeat-hint" title="A new occurrence is created when you complete this task"><PmIcon name="repeat" size={13} /></span>}
            </span></div>
            <div className="pm-prop"><span className="pm-prop-label">Section</span><span className="pm-prop-val">
              <select className="pm-mini-select" value={t.sectionId || ''} onChange={function(e) { patch({ sectionId: e.target.value ? parseInt(e.target.value, 10) : null }); }}>
                <option value="">Uncategorized</option>
                {sections.map(function(s) { return <option key={s.id} value={s.id}>{s.name}</option>; })}
              </select>
            </span></div>
            <div className="pm-prop"><span className="pm-prop-label">Collaborators</span><span className="pm-prop-val pm-collab">
              {(detail && detail.followers || []).map(function(f) { return <span key={f.id} className="pm-collab-chip" title={f.name}><PmAvatar member={f} size={24} /><button className="pm-collab-x" title="Remove" onClick={function() { removeCollaborator(f.id); }}>×</button></span>; })}
              <PmMenu align="right" trigger={<button className="pm-collab-add" title="Add collaborator">+</button>}>
                {function(close) { var existing = (detail && detail.followers || []).map(function(f) { return f.id; }); var avail = members.filter(function(m) { return existing.indexOf(m.id) === -1; }); return (
                  <div className="pm-assignee-list">
                    <div className="pm-menu-label">Add collaborator</div>
                    {avail.length === 0 ? <div className="pm-menu-item" style={{ color: 'var(--text-muted)' }}>Everyone's added</div> : avail.map(function(m) { return <button key={m.id} className="pm-menu-item" onClick={function() { addCollaborator(m); close(); }}><PmAvatar member={m} size={20} /> {m.name}</button>; })}
                  </div>
                ); }}
              </PmMenu>
            </span></div>
          </div>

          {detail && (
            <div className="pm-drawer-section">
              <div className="pm-drawer-section-label">Dependencies</div>
              <div className="pm-dep-group">
                <div className="pm-dep-head">Blocked by</div>
                {(detail.blockedBy || []).map(function(d) {
                  return <div key={d.id} className={'pm-dep-item' + (d.completed ? ' done' : '')}>
                    <span className={'pm-dep-dot' + (d.completed ? ' ok' : '')} title={d.completed ? 'Done' : 'Not done'} />
                    <span className="pm-dep-title" onClick={function() { if (onOpenTask) onOpenTask(d.id); }}>{d.title}</span>
                    <button className="pm-dep-x" title="Remove" onClick={function() { removeDependency(d.id); }}>×</button>
                  </div>;
                })}
                <PmTaskPickerButton tasks={siblingTasks} excludeIds={[taskId].concat((detail.blockedBy || []).map(function(d) { return d.id; }))} onPick={addDependency} label="Add blocker" />
              </div>
              {(detail.blocking || []).length > 0 && (
                <div className="pm-dep-group">
                  <div className="pm-dep-head">Blocking</div>
                  {detail.blocking.map(function(d) {
                    return <div key={d.id} className={'pm-dep-item' + (d.completed ? ' done' : '')}>
                      <span className="pm-dep-dot arrow">↑</span>
                      <span className="pm-dep-title" onClick={function() { if (onOpenTask) onOpenTask(d.id); }}>{d.title}</span>
                      <button className="pm-dep-x" title="Remove" onClick={function() { removeBlocking(d.id); }}>×</button>
                    </div>;
                  })}
                </div>
              )}
            </div>
          )}

          <div className="pm-drawer-section">
            <div className="pm-drawer-section-label">Tags</div>
            <div className="pm-tag-edit">
              {(t.tags || []).map(function(tag, i) { return <span key={i} className="pm-tag pm-tag-removable">{tag}<button onClick={function() { removeTag(i); }}>×</button></span>; })}
              <input className="pm-tag-input" value={tagDraft} placeholder="+ tag" onChange={function(e) { setTagDraft(e.target.value); }} onKeyDown={function(e) { if (e.key === 'Enter') addTag(); }} onBlur={addTag} />
            </div>
          </div>

          <div className="pm-drawer-section">
            <div className="pm-drawer-section-label">Description</div>
            {editingDesc || !(t.description && t.description.trim()) ? (
              <PmRichEditor value={desc} members={members} placeholder="Add more detail…  ** bold **, - list, @ mention" rows={5} autoFocus={editingDesc}
                onChange={setDesc}
                onBlur={function() { setEditingDesc(false); if (desc !== (t.description || '')) patch({ description: desc }); }} />
            ) : (
              <div className="pm-drawer-desc-view" onClick={function() { setEditingDesc(true); }}><PmMarkdown text={t.description} members={members} /></div>
            )}
          </div>

          {(function() {
            var atts = (detail && detail.attachments) || [];
            var images = atts.filter(function(a) { return a.isImage && a.url; });
            var files = atts.filter(function(a) { return !(a.isImage && a.url); });
            return (
              <div className="pm-drawer-section">
                <div className="pm-drawer-section-label">Attachments {atts.length > 0 && <span className="pm-col-count">{atts.length}</span>}
                  <span className="pm-card-meta-spacer" />
                  <button className="pm-attach-add" onClick={function() { if (fileInputRef.current) fileInputRef.current.click(); }}>+ Add file</button>
                </div>
                <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={function(e) { uploadFiles(e.target.files); e.target.value = ''; }} />
                {images.length > 0 && (
                  <div className="pm-attach-grid">
                    {images.map(function(a) {
                      return (
                        <div key={a.id} className="pm-attach-img">
                          <a href={a.url} target="_blank" rel="noopener noreferrer"><img src={a.url} alt={a.filename} /></a>
                          <button className="pm-attach-del" title="Remove" onClick={function() { removeAttachment(a); }}>×</button>
                          <div className="pm-attach-img-name">{a.filename}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {files.map(function(a) {
                  return (
                    <div key={a.id} className="pm-attach-row">
                      <span className="pm-attach-icon"><PmFileIcon ct={a.contentType} name={a.filename} /></span>
                      <a className="pm-attach-name" href={a.url || '#'} target="_blank" rel="noopener noreferrer">{a.filename}</a>
                      <span className="pm-attach-size">{pmFileSize(a.sizeBytes)}</span>
                      <button className="pm-attach-del" title="Remove" onClick={function() { removeAttachment(a.id); }}>×</button>
                    </div>
                  );
                })}
                {atts.length === 0 && uploading === 0 && (
                  <button className="pm-attach-drop" onClick={function() { if (fileInputRef.current) fileInputRef.current.click(); }}>
                    Drop files here or <span className="pm-attach-browse">browse</span>
                  </button>
                )}
                {uploading > 0 && <div className="pm-attach-uploading">Uploading {uploading} file{uploading === 1 ? '' : 's'}…</div>}
              </div>
            );
          })()}

          {(function() {
            var subs = (detail && detail.subtasks) || [];
            var doneN = subs.filter(function(s) { return s.completed; }).length;
            var pct = subs.length ? Math.round((doneN / subs.length) * 100) : 0;
            return (
              <div className="pm-drawer-section">
                <div className="pm-drawer-section-label">Subtasks {subs.length > 0 && <span className="pm-col-count">{doneN}/{subs.length}</span>}</div>
                {subs.length > 0 && <div className="pm-subtask-progress"><div className="pm-subtask-progress-fill" style={{ width: pct + '%' }} /></div>}
                {subs.map(function(s) {
                  var sDue = pmDueMeta(s.dueDate, s.completed);
                  var sMember = memberById[s.assigneeId];
                  var sPrio = PM_PRIORITY[s.priority] || PM_PRIORITY.none;
                  return (
                    <div key={s.id} className={'pm-subrow' + (s.completed ? ' done' : '')}>
                      <button className={'pm-check' + (s.completed ? ' checked' : '')} onClick={function() { toggleSub(s); }}>{s.completed ? <PmIcon name="check" weight={2.2} size={11} /> : null}</button>
                      {s.priority !== 'none' && !s.completed && <span className="pm-prio-dot" style={{ background: sPrio.color }} title={sPrio.label} />}
                      <span className="pm-subrow-title" onClick={function() { if (onOpenTask) onOpenTask(s.id); }}>{s.title}</span>
                      {s.subtaskCount > 0 && <span className="pm-card-mini" title="Subtasks"><PmIcon name="subtask" size={13} /> {s.subtaskCount}</span>}
                      {s.commentCount > 0 && <span className="pm-card-mini" title="Comments"><PmIcon name="comment" size={13} /> {s.commentCount}</span>}
                      {s.attachmentCount > 0 && <span className="pm-card-mini" title="Attachments"><PmIcon name="clip" size={13} /> {s.attachmentCount}</span>}
                      <span className="pm-list-row-spacer" />
                      <PmDueInline value={s.dueDate} onChange={function(d) { patchSub(s.id, { dueDate: d }); }} />
                      <PmAssignee assigneeId={s.assigneeId} members={members} onChange={function(id) { patchSub(s.id, { assigneeId: id }); }} size={22} />
                      <button className="pm-subrow-open" title="Open subtask" onClick={function() { if (onOpenTask) onOpenTask(s.id); }}>›</button>
                    </div>
                  );
                })}
                <div className="pm-subrow pm-subrow-add">
                  <span className="pm-check" />
                  <input className="pm-subtask-input" value={subDraft} placeholder="Add subtask…" onChange={function(e) { setSubDraft(e.target.value); }} onKeyDown={function(e) { if (e.key === 'Enter') addSubtask(); }} onBlur={addSubtask} />
                </div>
              </div>
            );
          })()}

          <div className="pm-drawer-section">
            <div className="pm-drawer-section-label">Activity</div>
            <div className="pm-activity">
              {detail && (detail.activity || []).map(function(a) {
                if (a.type === 'comment') {
                  return (
                    <div key={a.key} className="pm-activity-item pm-activity-comment">
                      <PmAvatar member={a.actor} size={28} />
                      <div className="pm-activity-comment-body">
                        <div className="pm-activity-comment-head"><span className="pm-comment-author">{a.actor ? a.actor.name : 'Unknown'}</span><span className="pm-comment-time">{pmTimeAgo(a.createdAt)}</span></div>
                        <div className="pm-comment-text"><PmMarkdown text={a.body} members={members} /></div>
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={a.key} className="pm-activity-item pm-activity-event">
                    <PmAvatar member={a.actor} size={22} />
                    <span className="pm-activity-text"><span className="pm-activity-who">{a.actor ? a.actor.name : 'Someone'}</span> {pmActivityText(a)}</span>
                    <span className="pm-activity-time">{pmTimeAgo(a.createdAt)}</span>
                  </div>
                );
              })}
              {detail && (!detail.activity || detail.activity.length === 0) && <div className="pm-comments-empty">No activity yet.</div>}
              {!detail && <div className="pm-comments-empty">Loading…</div>}
            </div>
            <div className="pm-comment-add">
              <PmRichEditor value={comment} onChange={setComment} onSubmit={addComment} members={members} placeholder="Add a comment…  @ to mention  ·  ⌘⏎ to send" rows={2} compact />
              <div className="pm-comment-add-actions"><button className="pm-primary-btn sm" disabled={!comment.trim()} onClick={addComment}>Comment</button></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProjectsTab({ clientId }) {
  const [projectData, setProjectData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(function() {
    setLoading(true); setProjectData(null); setExpanded(null);
    authFetch(API + '/clients/' + clientId + '/projects')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(data) { setProjectData(data); setLoading(false); })
      .catch(function() { setLoading(false); });
  }, [clientId]);

  if (loading) return <div className="tab-content"><SkeletonLoader /></div>;
  if (!projectData || !projectData.projects || projectData.projects.length === 0) return <div className="tab-content"><IntegrationEmptyState kind="projects" clientId={clientId} extraDetail="Asana is connected at the workspace level, then per-client projects are mapped via project GIDs in the Asana section of the integrations modal." /></div>;

  var projects = projectData.projects;
  var totalTasks = projects.reduce(function(s, p) { return s + p.totalTasks; }, 0);
  var totalCompleted = projects.reduce(function(s, p) { return s + p.completedTasks; }, 0);
  var totalOverdue = projects.reduce(function(s, p) { return s + p.overdueTasks; }, 0);
  var overallProgress = totalTasks > 0 ? Math.round((totalCompleted / totalTasks) * 100) : 0;

  return (
    <div className="tab-content">
      <TabDescription text="Project tracking from Asana. Shows active engagements, task completion progress, and overdue items. Click any project card to expand milestones and individual tasks." />
      <DataFreshness live={true} />
      <HealthVerdict score={totalOverdue > 3 ? 50 : totalOverdue > 0 ? 70 : overallProgress >= 75 ? 90 : overallProgress >= 50 ? 78 : 60} context={(function() {
        if (totalOverdue > 3) return totalOverdue + ' tasks are overdue across ' + projects.length + ' project(s). Review timeline and reassign blocked items.';
        if (totalOverdue > 0) return totalOverdue + ' overdue task(s). ' + totalCompleted + ' of ' + totalTasks + ' tasks completed (' + overallProgress + '%).';
        if (overallProgress >= 75) return 'Projects are on track — ' + overallProgress + '% complete with no overdue items.';
        if (overallProgress >= 50) return 'Projects progressing — ' + overallProgress + '% complete, ' + (totalTasks - totalCompleted) + ' tasks remaining.';
        return 'Projects are in early stages — ' + overallProgress + '% complete, ' + (totalTasks - totalCompleted) + ' tasks remaining.';
      })()} />
      {totalOverdue > 0 && (function() {
        var overdueTasks = [];
        projects.forEach(function(p) { p.incompleteTasks.filter(function(t) { return t.overdue; }).forEach(function(t) { overdueTasks.push({ project: p.name, name: t.name, assignee: t.assignee, due: t.due }); }); });
        var bodyLines = ['Hi CloudByte Support,', '', 'The following project task(s) are past their due date and may need attention:', ''];
        overdueTasks.forEach(function(t) {
          var daysOver = Math.ceil((Date.now() - new Date(t.due).getTime()) / 86400000);
          bodyLines.push('• ' + t.name); bodyLines.push('  Project: ' + t.project + ' | Due: ' + new Date(t.due).toLocaleDateString() + ' (' + daysOver + ' day' + (daysOver !== 1 ? 's' : '') + ' overdue)' + (t.assignee ? ' | Assigned: ' + t.assignee : ' | Unassigned')); bodyLines.push('');
        });
        bodyLines.push('Could you provide an update on the timeline for these items?', '', 'Thank you');
        return (
          <a href={'mailto:support@cloudbytegroup.com?subject=' + encodeURIComponent('Aurelius — ' + overdueTasks.length + ' overdue project task(s) for review') + '&body=' + encodeURIComponent(bodyLines.join('\n'))} className="verdict-mailto" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8, marginBottom: 4, padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, color: '#60a5fa', background: 'rgba(96,165,250,0.1)', border: '1px solid rgba(96,165,250,0.25)', textDecoration: 'none', cursor: 'pointer' }}>✉ Email CloudByte Support about overdue tasks</a>
        );
      })()}
      <ContentBand title="Project Portfolio — Asana" subtitle="Task-level breakdown across all active engagements" rightContent={<>{!IS_DEMO_VIEW && <a href="https://app.asana.com" target="_blank" rel="noopener noreferrer" className="tab-drill-link" style={{ textDecoration: 'none' }}>Open Asana ↗</a>}<span className="live-badge">LIVE</span></>}>
        <SegmentBar segments={[
          { label: 'Completed', value: totalCompleted || 0, color: '#22c55e' },
          { label: 'Remaining', value: (totalTasks - totalCompleted) || 0, color: '#60a5fa' },
          { label: 'Overdue', value: totalOverdue || 0, color: '#ef4444' },
        ]} />
        {(function() {
          var onTrack = projects.filter(function(p) { return p.statusColor === 'green'; }).length;
          var atRisk = projects.filter(function(p) { return p.statusColor === 'yellow' || p.statusColor === 'red'; }).length;
          var avgProgress = projects.length > 0 ? Math.round(projects.reduce(function(s, p) { return s + (p.progress || 0); }, 0) / projects.length) : 0;
          return (
            <div className="metric-tiles">
              <div className="metric-tile">
                <div className="metric-tile-label">Active Projects</div>
                <div className="metric-tile-value">{projects.length}</div>
                <div className="metric-tile-context">In flight across portfolio</div>
              </div>
              <div className="metric-tile">
                <div className="metric-tile-label">Avg Progress</div>
                <div className="metric-tile-value">{avgProgress}<span className="metric-tile-unit">%</span></div>
                <div className="metric-tile-context">Mean completion across projects</div>
              </div>
              <div className={'metric-tile' + (onTrack === projects.length && projects.length > 0 ? ' metric-tile-good' : '')}>
                <div className="metric-tile-label">On Track</div>
                <div className="metric-tile-value">{onTrack}</div>
                <div className="metric-tile-context">Status reported green</div>
              </div>
              <div className={'metric-tile' + (atRisk > 0 ? ' metric-tile-alert' : ' metric-tile-good')}>
                <div className="metric-tile-label">At Risk</div>
                <div className="metric-tile-value">{atRisk}</div>
                <div className="metric-tile-context">{atRisk > 0 ? 'Yellow or red status — review timeline' : 'No projects flagged'}</div>
              </div>
            </div>
          );
        })()}
      </ContentBand>
      {projects.map(function(p, idx) {
        var isExpanded = expanded === idx;
        var progressColor = p.progress >= 75 ? '#22c55e' : p.progress >= 50 ? '#f59e0b' : '#ef4444';
        var statusClass = p.statusColor === 'green' ? 'project-status-green' : p.statusColor === 'yellow' ? 'project-status-yellow' : p.statusColor === 'red' ? 'project-status-red' : 'project-status-blue';
        var statusDotColor = p.statusColor === 'green' ? '#22c55e' : p.statusColor === 'yellow' ? '#f59e0b' : p.statusColor === 'red' ? '#ef4444' : '#60a5fa';
        return (
          <div key={p.gid} className="project-card">
            <div className="project-card-header" onClick={function() { setExpanded(isExpanded ? null : idx); }}>
              <div className="project-card-left">
                <div className="project-card-name">{p.color && <span className="project-color-dot" style={{ background: p.color === 'light-green' ? '#22c55e' : p.color === 'light-blue' ? '#60a5fa' : p.color === 'light-red' ? '#ef4444' : p.color === 'light-orange' ? '#f59e0b' : p.color === 'light-purple' ? '#a78bfa' : p.color === 'light-pink' ? '#f472b6' : p.color === 'light-teal' ? '#2dd4bf' : '#94a3b8' }} />}{p.name}</div>
                <div className="project-card-meta">
                  {p.statusColor && <span className={'project-status-badge ' + statusClass}><span className="project-status-dot" style={{ background: statusDotColor }} />{p.statusColor === 'green' ? 'On Track' : p.statusColor === 'yellow' ? 'At Risk' : p.statusColor === 'red' ? 'Off Track' : 'Update Available'}</span>}
                  {p.owner && p.owner.name && <span className="project-owner">{p.owner.name}</span>}
                  {(p.startDate || p.dueDate) && <span className="project-date-range">{p.startDate ? new Date(p.startDate).toLocaleDateString() : '?'} → {p.dueDate ? new Date(p.dueDate).toLocaleDateString() : 'No end date'}</span>}
                  {p.overdueTasks > 0 && <span className="project-overdue-badge">{p.overdueTasks} overdue</span>}
                </div>
              </div>
              <div className="project-card-right">
                <span className="project-progress-number" style={{ color: progressColor }}>{p.progress}%</span>
                <div className="project-task-count">{p.completedTasks}/{p.totalTasks} tasks</div>
                <span className="project-expand">{isExpanded ? '▲' : '▼'}</span>
              </div>
            </div>
            <div className="project-progress-bar"><div className="project-progress-fill" style={{ width: p.progress + '%', backgroundColor: progressColor }} /></div>
            {isExpanded && (function() {
              // Defensive: detect whether this expansion has anything meaningful
              // to show. If every section would be empty/null, render a clear
              // empty-state message instead of a silent blank panel.
              var hasPhases = p.phases && p.phases.length > 0;
              var hasUnphased = p.unphasedTasks && p.unphasedTasks.length > 0;
              var hasMilestones = p.milestones && p.milestones.length > 0;
              var hasStakeholders = (p.stakeholders && p.stakeholders.length > 0) || (p.owner && p.owner.name);
              var hasStatusUpdate = p.statusUpdate && p.statusUpdate.text;
              var hasAnything = hasPhases || hasUnphased || hasMilestones || hasStakeholders || hasStatusUpdate;
              return (
              <div className="project-card-body proj-v2">
                {/* Always-visible summary header — guarantees the expansion never appears blank,
                    and makes the data clearly visible even if a downstream component fails to render. */}
                <div className="proj-summary-line">
                  <span className="proj-summary-piece"><strong>{p.totalTasks || 0}</strong> task{(p.totalTasks || 0) === 1 ? '' : 's'}</span>
                  {hasPhases && <><span className="proj-summary-sep">·</span><span className="proj-summary-piece"><strong>{p.phases.length}</strong> phase{p.phases.length === 1 ? '' : 's'}</span></>}
                  {(p.completedTasks || 0) > 0 && <><span className="proj-summary-sep">·</span><span className="proj-summary-piece"><strong>{p.completedTasks}</strong> done</span></>}
                  {(p.overdueTasks || 0) > 0 && <><span className="proj-summary-sep">·</span><span className="proj-summary-piece overdue"><strong>{p.overdueTasks}</strong> overdue</span></>}
                  {(p.dueSoonTasks || 0) > 0 && <><span className="proj-summary-sep">·</span><span className="proj-summary-piece due-soon"><strong>{p.dueSoonTasks}</strong> due this week</span></>}
                  {p.owner && p.owner.name && <><span className="proj-summary-sep">·</span><span className="proj-summary-piece">Owner: <strong>{p.owner.name}</strong></span></>}
                  {p.lastModifiedAgo != null && <><span className="proj-summary-sep">·</span><span className="proj-summary-piece muted">Activity {p.lastModifiedAgo === 0 ? 'today' : p.lastModifiedAgo + 'd ago'}</span></>}
                </div>

                {hasAnything ? (
                  <>
                    <ProjHealthRibbon project={p} />
                    <ProjStatusBlock statusUpdate={p.statusUpdate} />
                    <ProjMilestonesStrip milestones={p.milestones} />
                    {hasPhases && (
                      <div className="proj-section">
                        <div className="proj-section-head">Lifecycle</div>
                        <ProjLifecycle phases={p.phases} />
                      </div>
                    )}
                    {hasUnphased && (
                      <div className="proj-section">
                        <div className="proj-section-head">Unphased work <span className="proj-section-sub">({p.unphasedTasks.length}) — not yet assigned to a phase</span></div>
                        <div className="proj-task-list">
                          {p.unphasedTasks.map(function(t) { return <ProjTaskCard key={t.gid} task={t} />; })}
                        </div>
                      </div>
                    )}
                    <ProjStakeholders stakeholders={p.stakeholders} owner={p.owner} />
                    <ProjActivityFeed activity={p.recentActivity} />
                  </>
                ) : (
                  <div className="proj-empty-state">
                    <div className="proj-empty-state-icon">○</div>
                    <div className="proj-empty-state-title">No detailed data yet</div>
                    <div className="proj-empty-state-text">
                      This project hasn't been broken into sections, milestones, or assigned tasks in Asana.
                      Once the engineering team adds structure, it'll show up here.
                    </div>
                  </div>
                )}

                {p.permalink && (
                  <div className="proj-card-footer">
                    <a className="proj-deeplink" href={p.permalink} target="_blank" rel="noopener noreferrer">Open project in Asana ↗</a>
                    {p.lastModifiedAgo != null && <span className="proj-card-footer-meta">Last activity {p.lastModifiedAgo === 0 ? 'today' : p.lastModifiedAgo + 'd ago'}</span>}
                  </div>
                )}
              </div>
              );
            })()}
          </div>
        );
      })}
    </div>
  );
}

// ── DevOps Tab (Jira) — MODIFIED: billing moved to Budget tab ─────
// ── DevOps Components ─────────────────────────────────────────────
function DevOpsMetricTiles({ jiraData }) {
  var backlog = jiraData.totalOpen || 0;
  var overdue = jiraData.totalOverdue || 0;
  var velocity = jiraData.totalDone30d || 0;

  // Count In Progress from matrix
  var inProgress = 0;
  if (jiraData.priorityStatusMatrix) {
    Object.values(jiraData.priorityStatusMatrix).forEach(function(row) {
      inProgress += (row['In Progress'] || 0);
    });
  }

  // Throughput: velocity vs backlog
  var throughput = backlog > 0 ? Math.round((velocity / (velocity + backlog)) * 100) : null;

  return (
    <div className="metric-tiles">
      <div className="metric-tile">
        <div className="metric-tile-label">Open Backlog</div>
        <div className="metric-tile-value">{backlog}</div>
        <div className="metric-tile-context">Across {jiraData.projectCount || 0} project{jiraData.projectCount === 1 ? '' : 's'}</div>
      </div>
      <div className={'metric-tile' + (overdue > 0 ? ' metric-tile-alert' : ' metric-tile-good')}>
        <div className="metric-tile-label">Overdue</div>
        <div className="metric-tile-value">{overdue}</div>
        <div className="metric-tile-context">{overdue > 0 ? 'Past due date — needs triage' : 'All issues on track'}</div>
      </div>
      <div className="metric-tile">
        <div className="metric-tile-label">In Progress</div>
        <div className="metric-tile-value">{inProgress}</div>
        <div className="metric-tile-context">Actively being worked</div>
      </div>
      <div className="metric-tile">
        <div className="metric-tile-label">Velocity (30d)</div>
        <div className="metric-tile-value">{velocity}</div>
        <div className="metric-tile-context">{throughput !== null ? throughput + '% throughput rate' : 'Completed last 30 days'}</div>
      </div>
    </div>
  );
}

function JiraPriorityStatusHeatmap({ matrix }) {
  if (!matrix || Object.keys(matrix).length === 0) return <div className="priority-heatmap-empty">No issue data available.</div>;

  var priorityOrder = ['Highest', 'High', 'Medium', 'Low', 'Lowest', 'None'];
  var priorityColors = { Highest: '#ef4444', High: '#f59e0b', Medium: '#60a5fa', Low: '#22c55e', Lowest: '#94a3b8', None: '#64748b' };
  var statuses = [
    { key: 'To Do', label: 'To Do' },
    { key: 'In Progress', label: 'In Progress' },
    { key: 'Done', label: 'Done (30d)' }
  ];

  // Only show priorities that have data
  var activePriorities = priorityOrder.filter(function(p) {
    if (!matrix[p]) return false;
    return statuses.some(function(s) { return (matrix[p][s.key] || 0) > 0; });
  });
  if (activePriorities.length === 0) return <div className="priority-heatmap-empty">No issue data available.</div>;

  // Max for intensity scaling (excluding Done column)
  var activeMax = 0;
  activePriorities.forEach(function(p) {
    statuses.forEach(function(s) {
      if (s.key === 'Done') return;
      var count = (matrix[p] && matrix[p][s.key]) || 0;
      if (count > activeMax) activeMax = count;
    });
  });

  var isHotspot = function(priority, statusKey) {
    if (statusKey === 'Done') return false;
    var count = (matrix[priority] && matrix[priority][statusKey]) || 0;
    if (count === 0) return false;
    if (priority === 'Highest') return true;
    if (priority === 'High' && count > 2) return true;
    return false;
  };

  return (
    <div className="priority-heatmap-wrap">
      <div className="priority-heatmap" style={{ gridTemplateColumns: '120px repeat(' + statuses.length + ', 1fr)' }}>
        <div className="priority-heatmap-corner"></div>
        {statuses.map(function(s) { return <div key={s.key} className="priority-heatmap-col-label">{s.label}</div>; })}
        {activePriorities.map(function(p) {
          return (
            <React.Fragment key={p}>
              <div className="priority-heatmap-row-label">
                <span className="priority-heatmap-row-dot" style={{ background: priorityColors[p], color: priorityColors[p] }} />
                {p}
              </div>
              {statuses.map(function(s) {
                var count = (matrix[p] && matrix[p][s.key]) || 0;
                var intensity = activeMax > 0 && s.key !== 'Done' ? count / activeMax : 0;
                var hotspot = isHotspot(p, s.key);
                var bg = 'transparent';
                if (count > 0) {
                  if (hotspot) bg = 'rgba(239, 68, 68, ' + (0.18 + intensity * 0.32) + ')';
                  else if (s.key === 'Done') bg = 'rgba(34, 197, 94, 0.08)';
                  else bg = 'rgba(91, 158, 244, ' + (0.1 + intensity * 0.3) + ')';
                }
                return (
                  <div key={s.key} className={'priority-heatmap-cell' + (count === 0 ? ' cell-empty' : '') + (hotspot ? ' cell-hotspot' : '')} style={{ background: bg }}>
                    {count > 0 ? count : <span className="cell-dash">—</span>}
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>
      <div className="priority-heatmap-legend">
        <span className="priority-heatmap-legend-item"><span className="priority-heatmap-legend-swatch legend-hotspot" /> Hotspot — high priority still active</span>
        <span className="priority-heatmap-legend-item"><span className="priority-heatmap-legend-swatch legend-active" /> Active work</span>
        <span className="priority-heatmap-legend-item"><span className="priority-heatmap-legend-swatch legend-closed" /> Done (30d)</span>
      </div>
    </div>
  );
}

function DevOpsTab({ clientId, onNavigate }) {
  const [jiraData, setJiraData] = useState(null);
  const [loading, setLoading] = useState(true);
  var jiraSort = useTableSort('updated', 'desc');
  const [jiraSearch, setJiraSearch] = useState('');
  const [jiraFilters, setJiraFilters] = useState({});
  const [expanded, setExpanded] = useState(null);

  useEffect(function() {
    setLoading(true); setJiraData(null); setExpanded(null);
    authFetch(API + '/clients/' + clientId + '/jira')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(data) { setJiraData(data); setLoading(false); })
      .catch(function() { setLoading(false); });
  }, [clientId]);

  if (loading) return <div className="tab-content"><SkeletonLoader /></div>;
  if (!jiraData || !jiraData.enabled) return <div className="tab-content"><IntegrationEmptyState kind="devops" clientId={clientId} /></div>;

  var priorityColors = { Highest: '#ef4444', High: '#f59e0b', Medium: '#60a5fa', Low: '#22c55e', Lowest: '#64748b' };
  var statusCatColors = { 'To Do': '#64748b', 'In Progress': '#60a5fa', 'Done': '#22c55e' };

  function timeAgo(dateStr) {
    if (!dateStr) return '—';
    var diff = Date.now() - new Date(dateStr).getTime();
    var mins = Math.floor(diff / 60000);
    if (mins < 60) return mins + 'm ago';
    var hours = Math.floor(mins / 60);
    if (hours < 24) return hours + 'h ago';
    var days = Math.floor(hours / 24);
    return days < 30 ? days + 'd ago' : Math.floor(days / 30) + 'mo ago';
  }

  var jiraAll = jiraData.recentIssues || [];
  var jiraFiltered = applyFilters(
    filterRows(jiraAll, jiraSearch, [function(i){return i.key;}, function(i){return i.summary;}, function(i){return i.assignee;}, function(i){return i.project;}]),
    jiraFilters,
    { priority: function(i){return i.priority;}, statusCategory: function(i){return i.statusCategory;} }
  );
  var sortedIssues = sortRows(jiraFiltered, jiraSort.sortField, jiraSort.sortDir, {
    key: function(i){return i.key;}, summary: function(i){return i.summary;}, status: function(i){return i.status;},
    priority: function(i){return i.priority;}, assignee: function(i){return i.assignee;}, updated: function(i){return i.updated;}
  });

  return (
    <div className="tab-content">
      <TabDescription text="Development and IT operations tracked in Jira. Shows issue velocity, priority distribution, and team workload across all active projects." />
      <DataFreshness live={true} />
      <ContentBand title="DevOps Overview — Jira" rightContent={<span className="live-badge">LIVE</span>}>
        <DevOpsMetricTiles jiraData={jiraData} />
      </ContentBand>
      {jiraData.billing && (
        <ContentBand title="Atlassian Billing" rightContent={onNavigate ? <button className="tab-drill-link" onClick={function() { onNavigate('budget'); }}>View in Budget ›</button> : null}>
          <MetricList>
            <MetricListItem icon="$" iconColor="#60a5fa" label="Monthly Cost" value={'$' + jiraData.billing.totalMonthlyCost.toLocaleString() + '/mo'} valueColor="#60a5fa" trail={jiraData.billing.totalLicensedUsers + ' users'} />
          </MetricList>
        </ContentBand>
      )}
      {jiraData.priorityStatusMatrix && Object.keys(jiraData.priorityStatusMatrix).length > 0 && (
        <ContentBand title="Priority × Status Matrix" subtitle="Hotspots highlight high-priority issues still active — find bottlenecks instantly">
          <JiraPriorityStatusHeatmap matrix={jiraData.priorityStatusMatrix} />
        </ContentBand>
      )}
      {jiraData.projects && jiraData.projects.length > 0 && (
        <>
          <div className="section-title">Projects</div>
          {jiraData.projects.map(function(p, idx) {
            var isExpanded = expanded === idx;
            return (
              <div key={p.key} className="project-card">
                <div className="project-card-header" onClick={function() { setExpanded(isExpanded ? null : idx); }}>
                  <div className="project-card-left">
                    <div className="project-card-name">{p.name || p.key} <span style={{ color: '#64748b', fontSize: 12, fontWeight: 400 }}>({p.key})</span></div>
                    <div className="project-card-meta">
                      <span style={{ color: '#60a5fa' }}>{p.open} open</span>
                      <span style={{ color: '#22c55e', marginLeft: 12 }}>{p.done30d} done (30d)</span>
                      {p.overdue > 0 && <span className="project-overdue-badge">{p.overdue} overdue</span>}
                    </div>
                  </div>
                  <div className="project-card-right"><span className="project-expand">{isExpanded ? '▲' : '▼'}</span></div>
                </div>
                {isExpanded && (
                  <div className="project-card-body">
                    {Object.keys(p.statusBreakdown).length > 0 && (<div className="project-sections"><div className="project-section-title">Status</div><div className="project-section-list">{Object.entries(p.statusBreakdown).map(function([status, count]) { return <span key={status} className="project-section-tag">{status}: {count}</span>; })}</div></div>)}
                    {Object.keys(p.assigneeBreakdown).length > 0 && (<div className="project-sections"><div className="project-section-title">Team Workload</div><div className="project-section-list">{Object.entries(p.assigneeBreakdown).sort(function(a, b) { return b[1] - a[1]; }).map(function([name, count]) { return <span key={name} className="project-section-tag">{name}: {count}</span>; })}</div></div>)}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
      {Object.keys(jiraData.assigneeBreakdown).length > 0 && (
        <ContentBand title="Team Workload (All Projects)">
          <MetricList>
            {Object.entries(jiraData.assigneeBreakdown).sort(function(a, b) { return b[1] - a[1]; }).map(function([name, count]) { var maxIssues = Math.max.apply(null, Object.values(jiraData.assigneeBreakdown)); return <MetricListItem key={name} icon="◉" iconColor="#a78bfa" label={name} value={<MicroBar value={count} max={maxIssues} color="#a78bfa" label={count} />} />; })}
          </MetricList>
        </ContentBand>
      )}
      {jiraAll.length > 0 && (
        <>
          <div className="section-title">Recent Issues ({jiraAll.length})</div>
          <TableSearch value={jiraSearch} onChange={setJiraSearch} count={sortedIssues.length} total={jiraAll.length} noun="issues" onExport={function() { exportTableCSV(['Key','Summary','Status','Priority','Assignee','Updated','URL'], sortedIssues.map(function(i) { return [i.key, i.summary, i.status, i.priority||'', i.assignee||'', i.updated ? new Date(i.updated).toLocaleDateString() : '', i.url||'']; }), 'jira-issues'); }} />
          <TableFilters filters={[
            { key: 'priority', label: 'Priority', options: (function() { var p={}; jiraAll.forEach(function(i){if(i.priority)p[i.priority]=(p[i.priority]||0)+1;}); return Object.entries(p).map(function([k,c]) { return { value: k, label: k, color: priorityColors[k]||'#64748b', count: c }; }); })() },
            { key: 'statusCategory', label: 'Status', options: (function() { var s={}; jiraAll.forEach(function(i){if(i.statusCategory)s[i.statusCategory]=(s[i.statusCategory]||0)+1;}); return Object.entries(s).map(function([k,c]) { return { value: k, label: k, color: statusCatColors[k]||'#94a3b8', count: c }; }); })() },
          ]} active={jiraFilters} onChange={setJiraFilters} />
          <div className="posture-table-wrap">
            <table className="posture-table">
              <thead>
                <tr>
                  <SortTh field="key" current={jiraSort.sortField} dir={jiraSort.sortDir} onSort={jiraSort.toggleSort}>Key</SortTh>
                  <SortTh field="summary" current={jiraSort.sortField} dir={jiraSort.sortDir} onSort={jiraSort.toggleSort}>Summary</SortTh>
                  <SortTh field="status" current={jiraSort.sortField} dir={jiraSort.sortDir} onSort={jiraSort.toggleSort}>Status</SortTh>
                  <SortTh field="priority" current={jiraSort.sortField} dir={jiraSort.sortDir} onSort={jiraSort.toggleSort}>Priority</SortTh>
                  <SortTh field="assignee" current={jiraSort.sortField} dir={jiraSort.sortDir} onSort={jiraSort.toggleSort}>Assignee</SortTh>
                  <SortTh field="updated" current={jiraSort.sortField} dir={jiraSort.sortDir} onSort={jiraSort.toggleSort}>Updated</SortTh>
                  <th>Link</th>
                </tr>
              </thead>
              <tbody>
                {sortedIssues.map(function(issue) {
                  var isOverdue = issue.duedate && new Date(issue.duedate) < new Date();
                  return (
                    <tr key={issue.key} className={isOverdue ? 'posture-row-offline' : ''}>
                      <td className="table-mono">{issue.key}</td>
                      <td className="posture-device-name" style={{ maxWidth: 300 }}>
                        <div>{issue.summary}</div>
                        {issue.issueType && <div className="table-sub">{issue.issueType} · {issue.project}</div>}
                      </td>
                      <td><span className="inline-badge" style={{ backgroundColor: issue.statusCategory === 'In Progress' ? 'rgba(96,165,250,0.15)' : issue.statusCategory === 'Done' ? 'rgba(34,197,94,0.15)' : 'rgba(100,116,139,0.15)', color: statusCatColors[issue.statusCategory] || '#94a3b8' }}>{issue.status}</span></td>
                      <td><span style={{ color: priorityColors[issue.priority] || '#94a3b8', fontWeight: 600, fontSize: 12 }}>{issue.priority || '—'}</span></td>
                      <td>{issue.assignee}</td>
                      <td className="posture-date">{timeAgo(issue.updated)}{isOverdue && <span className="project-overdue-badge">OVERDUE</span>}</td>
                      <td>{issue.url ? <a href={issue.url} target="_blank" rel="noopener noreferrer" className="table-link">View ↗</a> : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
// ── Changes Tab (Change Management) ───────────────────────────────
// ── Changes Workflow Components ───────────────────────────────────
function ChangesFlowPipeline({ changes, summary, onStageClick, statusFilter }) {
  var now = Date.now();
  var stages = [
    { key: 'submitted', label: 'Submitted', sublabel: 'Awaiting triage', count: summary.submitted || 0, thresholdDays: 3 },
    { key: 'under_review', label: 'Under Review', sublabel: 'Being evaluated', count: summary.under_review || 0, thresholdDays: 7 },
    { key: 'approved', label: 'Approved', sublabel: 'Ready to start', count: summary.approved || 0, thresholdDays: 14 },
    { key: 'in_progress', label: 'In Progress', sublabel: 'Being implemented', count: summary.in_progress || 0, thresholdDays: 30 },
    { key: 'completed', label: 'Completed', sublabel: 'Resolved & closed', count: summary.completed || 0, thresholdDays: null }
  ];
  return (
    <div className="ticket-flow">
      {stages.map(function(stage, i) {
        var aged = 0;
        if (stage.thresholdDays && stage.count > 0) {
          var thresholdMs = stage.thresholdDays * 86400 * 1000;
          aged = (changes || []).filter(function(c) {
            return c.status === stage.key && c.submittedAt && (now - new Date(c.submittedAt).getTime()) > thresholdMs;
          }).length;
        }
        var isEmpty = stage.count === 0;
        var isActive = statusFilter === stage.key;
        var isClickable = stage.count > 0 && onStageClick;
        var classes = 'ticket-flow-stage ticket-flow-stage-' + stage.key +
          (aged > 0 ? ' ticket-flow-stage-aging' : '') +
          (isEmpty ? ' ticket-flow-stage-empty' : '') +
          (isActive ? ' ticket-flow-stage-active' : '') +
          (isClickable ? ' ticket-flow-stage-clickable' : '');
        return (
          <React.Fragment key={stage.key}>
            <div className={classes} onClick={isClickable ? function() { onStageClick(isActive ? null : stage.key); } : undefined}>
              <div className="ticket-flow-stage-label">{stage.label}</div>
              <div className="ticket-flow-stage-count">{stage.count}</div>
              <div className="ticket-flow-stage-sub">{stage.sublabel}</div>
              {aged > 0 && <div className="ticket-flow-stage-aging-badge">{aged} over {stage.thresholdDays}d</div>}
            </div>
            {i < stages.length - 1 && <div className="ticket-flow-connector"><span className="ticket-flow-arrow">›</span></div>}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function ChangesTerminalRow({ summary, onStageClick, statusFilter }) {
  var denied = summary.denied || 0;
  var cancelled = summary.cancelled || 0;
  if (denied === 0 && cancelled === 0) return null;
  return (
    <div className="changes-terminal-row">
      <span className="changes-terminal-label">Terminal states</span>
      {denied > 0 && (
        <button className={'changes-terminal-item changes-terminal-denied' + (statusFilter === 'denied' ? ' active' : '')} onClick={function() { onStageClick(statusFilter === 'denied' ? null : 'denied'); }}>
          <span className="changes-terminal-dot"></span>
          Denied
          <span className="changes-terminal-count">{denied}</span>
        </button>
      )}
      {cancelled > 0 && (
        <button className={'changes-terminal-item changes-terminal-cancelled' + (statusFilter === 'cancelled' ? ' active' : '')} onClick={function() { onStageClick(statusFilter === 'cancelled' ? null : 'cancelled'); }}>
          <span className="changes-terminal-dot"></span>
          Cancelled
          <span className="changes-terminal-count">{cancelled}</span>
        </button>
      )}
    </div>
  );
}

function ChangesMetricTiles({ changes, summary }) {
  var awaiting = (summary.submitted || 0) + (summary.under_review || 0);
  var activeWork = (summary.approved || 0) + (summary.in_progress || 0);

  var decided = (summary.approved || 0) + (summary.in_progress || 0) + (summary.completed || 0) + (summary.denied || 0);
  var approvedTotal = decided - (summary.denied || 0);
  var approvalRate = decided > 0 ? Math.round((approvedTotal / decided) * 100) : null;

  var completedChanges = (changes || []).filter(function(c) { return c.status === 'completed' && c.submittedAt && c.completedAt; });
  var avgCompletion = null;
  if (completedChanges.length > 0) {
    var totalMin = completedChanges.reduce(function(sum, c) {
      return sum + (new Date(c.completedAt).getTime() - new Date(c.submittedAt).getTime()) / 60000;
    }, 0);
    avgCompletion = totalMin / completedChanges.length;
  }

  return (
    <div className="metric-tiles">
      <div className={'metric-tile' + (awaiting > 0 ? ' metric-tile-alert' : ' metric-tile-good')}>
        <div className="metric-tile-label">Awaiting Review</div>
        <div className="metric-tile-value">{awaiting}</div>
        <div className="metric-tile-context">{awaiting > 0 ? 'Admin action required' : 'Review queue clear'}</div>
      </div>
      <div className="metric-tile">
        <div className="metric-tile-label">Active Work</div>
        <div className="metric-tile-value">{activeWork}</div>
        <div className="metric-tile-context">Approved or implementing</div>
      </div>
      <div className="metric-tile">
        <div className="metric-tile-label">Avg Time to Close</div>
        <div className="metric-tile-value">{formatDuration(avgCompletion)}</div>
        <div className="metric-tile-context">{avgCompletion !== null ? 'Submission to completion' : 'No completions yet'}</div>
      </div>
      <div className="metric-tile">
        <div className="metric-tile-label">Approval Rate</div>
        <div className="metric-tile-value">{approvalRate !== null ? approvalRate : '—'}{approvalRate !== null && <span className="metric-tile-unit">%</span>}</div>
        <div className="metric-tile-context">{approvalRate !== null ? 'Of all decided requests' : 'No decisions yet'}</div>
      </div>
    </div>
  );
}

function ChangesTab({ clientId, isAdmin }) {
  const [data, setData] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [statusFilter, setStatusFilter] = useState(null);
  const [kindFilter, setKindFilter] = useState('all');           // 'all' | 'changes' | 'proposals'
  const [selectedChange, setSelectedChange] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ title: '', description: '', type: 'IT Change', category: 'Other', priority: 'Medium', name: '', email: '' });
  var chgSort = useTableSort('submitted', 'desc');
  var [chgSearch, setChgSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentName, setCommentName] = useState('');
  const [reviewName, setReviewName] = useState('');
  const [reviewEmail, setReviewEmail] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [completionNotes, setCompletionNotes] = useState('');
  const [proposalToast, setProposalToast] = useState(null);

  // Fetches BOTH file-based change records AND the full AI-proposal history
  // (last 90d, all statuses) in parallel, so the workstream view is the
  // single canonical home for both kinds of change requests. The Changes
  // tab is intentionally the place where you see resolved proposals in
  // context with everything else; the bell drawer stays focused on what's
  // actionable right now.
  function fetchChanges() {
    setLoading(true);
    var changesP = authFetch(API + '/clients/' + clientId + '/changes')
      .then(function(r) { return r.ok ? r.json() : null; })
      .catch(function() { return null; });
    var proposalsP = isAdminRole()
      ? authFetch(API + '/clients/' + clientId + '/cmmc/proposals?include_resolved=true')
          .then(function(r) { return r.ok ? r.json() : null; })
          .catch(function() { return null; })
      : Promise.resolve(null);
    Promise.all([changesP, proposalsP]).then(function(results) {
      setData(results[0]);
      setProposals(results[1] && results[1].proposals ? results[1].proposals : []);
      setLoading(false);
    });
  }
  useEffect(function() { setView('list'); setSelectedChange(null); setShowForm(false); setStatusFilter(null); setKindFilter('all'); fetchChanges(); }, [clientId]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Approve / Reject handlers for AI proposal rows.
  function handleProposalAction(proposal, action) {
    if (action === 'approve' && !window.confirm('Approve "' + proposal.title + '"? This executes the playbook against ' + (proposal.target_count || 'auto-resolved') + ' target(s) immediately.')) return;
    var reason = null;
    if (action === 'reject') {
      reason = window.prompt('Reject reason (optional):');
      if (reason === null) return;
    }
    setActionLoading(true);
    var url = API + '/cmmc/proposals/' + proposal.id + '/' + action;
    var opts = action === 'reject'
      ? { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason: reason }) }
      : { method: 'POST' };
    authFetch(url, opts)
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setActionLoading(false);
        if (x.ok) {
          setProposalToast({ tone: 'success', text: action === 'approve' ? ('Approved & executed proposal #' + proposal.id) : ('Rejected proposal #' + proposal.id) });
          setSelectedChange(null);
          fetchChanges();
        } else {
          setProposalToast({ tone: 'error', text: x.d.error || (action + ' failed') });
        }
        setTimeout(function() { setProposalToast(null); }, 6000);
      })
      .catch(function() { setActionLoading(false); setProposalToast({ tone: 'error', text: action + ' request failed' }); });
  }

  // Check for pre-fill from Red Team or other tabs
  useEffect(function() {
    var prefillRaw = sessionStorage.getItem('aurelius_change_prefill');
    if (!prefillRaw) return;
    try {
      var prefill = JSON.parse(prefillRaw);
      setFormData(function(prev) { return Object.assign({}, prev, {
        title: prefill.title || '',
        description: prefill.description || '',
        type: prefill.type || 'IT Change',
        category: prefill.category || 'Other',
        priority: prefill.priority || 'Medium',
        reference: prefill.reference || '',
        _sourceContext: prefill
      }); });
      setShowForm(true);
      sessionStorage.removeItem('aurelius_change_prefill');
    } catch (e) { /* ignore */ }
  }, []);

  function handleSubmit() {
    if (!formData.title || !formData.description || !formData.name || !formData.email) return;
    setSubmitting(true);
    authFetch(API + '/clients/' + clientId + '/changes', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title: formData.title, description: formData.description, type: formData.type, category: formData.category, priority: formData.priority, submittedBy: { name: formData.name, email: formData.email }, reference: formData.reference || null }) })
      .then(function(r) { return r.json(); }).then(function(d) { if (d.success) { setShowForm(false); setFormData({ title: '', description: '', type: 'IT Change', category: 'Other', priority: 'Medium', name: formData.name, email: formData.email }); fetchChanges(); } setSubmitting(false); }).catch(function() { setSubmitting(false); });
  }
  function handleAction(changeId, action, extraBody) {
    setActionLoading(true);
    authFetch(API + '/clients/' + clientId + '/changes/' + changeId, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(Object.assign({ action: action }, extraBody || {})) })
      .then(function(r) { return r.json(); }).then(function(d) { if (d.success) { setSelectedChange(d.change); fetchChanges(); } setActionLoading(false); }).catch(function() { setActionLoading(false); });
  }

  if (loading) return <div className="tab-content"><SkeletonLoader /></div>;
  var rawChanges = data ? data.changes : [];

  // Map automation_executions.status (the AI proposal lifecycle) onto
  // the existing change-record state machine so federated rows render
  // in the right pipeline bucket and the Flow / Terminal / Metric tiles
  // count both kinds together.
  //
  //   awaiting_approval → submitted   (in the queue, waiting on approver)
  //   running           → in_progress (approver said yes, ops running)
  //   completed         → completed   (success)
  //   failed            → denied      (approved but execution errored)
  //   cancelled         → cancelled   (operator rejected before exec)
  function mapProposalStatus(automationStatus) {
    if (automationStatus === 'awaiting_approval') return 'submitted';
    if (automationStatus === 'running') return 'in_progress';
    if (automationStatus === 'completed') return 'completed';
    if (automationStatus === 'failed') return 'denied';
    if (automationStatus === 'cancelled') return 'cancelled';
    return automationStatus || 'submitted';
  }

  // Synthesize each AI proposal into a change-record-like row so it can
  // share the existing table + filter + sort plumbing. _kind discriminates
  // for click-through (proposals open ProposalDetailPanel; changes open the
  // existing detail view).
  function proposalAsItem(p) {
    var sevToPri = { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' };
    var triggerLabel = p.trigger === 'auto_drift' ? 'Aurelius (auto-drift)'
                     : p.trigger === 'auto_first_eval' ? 'Aurelius (initial eval)'
                     : p.trigger === 'manual_button' ? 'Operator (Identity Posture)'
                     : p.trigger === 'ai_tool' ? 'Aurelius (AI agent)'
                     : 'Aurelius (AI)';
    return {
      _kind: 'proposal',
      _proposal: p,
      id: 'AI-' + p.id,
      title: p.title,
      type: 'AI Proposal',
      category: p.control_id || 'AI',
      priority: sevToPri[p.severity] || 'Medium',
      status: mapProposalStatus(p.status),
      submittedBy: { name: triggerLabel, email: 'system@aurelius' },
      submittedAt: p.requested_at,
      completedAt: p.completed_at || null,
      description: p.description || null,
      severity: p.severity,
      automationStatus: p.status
    };
  }
  var proposalItems = (proposals || []).map(proposalAsItem);

  // Merge changes + proposals based on kindFilter
  var items;
  if (kindFilter === 'changes') items = rawChanges.slice();
  else if (kindFilter === 'proposals') items = proposalItems.slice();
  else items = rawChanges.concat(proposalItems);
  if (statusFilter) items = items.filter(function(c) { return c.status === statusFilter; });

  // Pending count = proposals still awaiting approval. Resolved
  // proposals (approved/cancelled/etc.) are visible in the table but
  // don't pump the kind-pill counter or the bell badge.
  var pendingProposalCount = proposalItems.filter(function(p) { return p.automationStatus === 'awaiting_approval'; }).length;

  // Derive a unified pipeline summary from `items` so the Flow + Terminal
  // + Metric tiles reflect *both* file-based changes and AI proposals
  // (in the current kindFilter scope). Falls back to data.summary when
  // proposals aren't loaded (e.g. non-admin client view).
  function deriveSummary(arr) {
    var s = { total: 0, submitted: 0, under_review: 0, approved: 0, denied: 0, in_progress: 0, completed: 0, cancelled: 0 };
    arr.forEach(function(c) {
      s.total++;
      if (s[c.status] !== undefined) s[c.status]++;
    });
    return s;
  }
  // Pipeline summary always reflects the kindFilter scope so the tiles
  // stay coherent with the table below them.
  var pipelineItems;
  if (kindFilter === 'changes') pipelineItems = rawChanges;
  else if (kindFilter === 'proposals') pipelineItems = proposalItems;
  else pipelineItems = rawChanges.concat(proposalItems);
  var summary = deriveSummary(pipelineItems);

  var statusColors = { submitted: '#60a5fa', under_review: '#f59e0b', approved: '#22c55e', denied: '#ef4444', in_progress: '#a78bfa', completed: '#22c55e', cancelled: '#64748b' };
  var statusLabels = { submitted: 'Submitted', under_review: 'Under Review', approved: 'Approved', denied: 'Denied', in_progress: 'In Progress', completed: 'Completed', cancelled: 'Cancelled' };
  var priorityColors = { Low: '#22c55e', Medium: '#60a5fa', High: '#f59e0b', Critical: '#ef4444' };
  var typeColors = { 'IT Change': '#60a5fa', 'Security Change': '#a78bfa', 'AI Proposal': '#a78bfa' };

  // Distinct labels for the AI lifecycle so the table doesn't
  // mis-translate (file-based 'denied' = "rejected by reviewer";
  // proposal-side it would mean execution failed after approval — those
  // are very different concepts).
  function proposalStatusLabel(autoStatus) {
    switch (autoStatus) {
      case 'awaiting_approval': return 'Awaiting approval';
      case 'running': return 'Executing';
      case 'completed': return 'Approved & executed';
      case 'failed': return 'Execution failed';
      case 'cancelled': return 'Rejected';
      default: return autoStatus || 'Unknown';
    }
  }

  function timeAgo(dateStr) {
    if (!dateStr) return '—';
    var diff = Date.now() - new Date(dateStr).getTime();
    var mins = Math.floor(diff / 60000);
    if (mins < 60) return mins + 'm ago';
    var hours = Math.floor(mins / 60);
    if (hours < 24) return hours + 'h ago';
    return Math.floor(hours / 24) + 'd ago';
  }
  function badgeStyle(color) { return { display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, color: color, background: color + '18', border: '1px solid ' + color + '30' }; }
  var inputStyle = { background: 'var(--bg-base)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '10px 14px', color: 'var(--text-primary)', fontSize: 13, fontFamily: 'var(--font-body)', width: '100%', outline: 'none' };
  var selectStyle = Object.assign({}, inputStyle, { appearance: 'none', cursor: 'pointer' });
  var textareaStyle = Object.assign({}, inputStyle, { minHeight: 80, resize: 'vertical' });
  var labelStyle = { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-muted)', marginBottom: 4, display: 'block' };
  var fieldStyle = { marginBottom: 14 };

  if (selectedChange && selectedChange._kind === 'proposal') {
    var p = selectedChange._proposal;
    return (
      <div className="tab-content">
        <Breadcrumb items={[{ label: 'Change Management', onClick: function() { setSelectedChange(null); } }, { label: 'AI Proposal AI-' + p.id }]} />
        <div className="section-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="changes-kind-badge changes-kind-ai">AI</span>
          <span>AI Proposal: AI-{p.id}</span>
        </div>
        {proposalToast && <div className={'admin-banner ' + (proposalToast.tone === 'success' ? 'success' : 'error')} style={{ fontSize: 12, marginBottom: 12 }}>{proposalToast.text}</div>}
        <div className="proposal-card proposal-card-sev-{p.severity || 'medium'}" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-lg)', padding: 24, marginBottom: 16 }}>
          <ProposalDetailPanel
            proposal={p}
            admin={isAdmin}
            busy={actionLoading}
            onApprove={function(pp) { handleProposalAction(pp, 'approve'); }}
            onReject={function(pp) { handleProposalAction(pp, 'reject'); }}
            dense={false}
          />
        </div>
      </div>
    );
  }
  if (selectedChange) {
    var ch = selectedChange;
    return (
      <div className="tab-content">
        <Breadcrumb items={[{ label: 'Change Management', onClick: function() { setSelectedChange(null); } }, { label: 'Request ' + ch.id }]} />
        <div className="section-title">Change Request: {ch.id}</div>
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-lg)', padding: 24, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 }}>{ch.title}</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <span style={badgeStyle(statusColors[ch.status] || '#64748b')}>{statusLabels[ch.status]}</span>
                <span style={badgeStyle(priorityColors[ch.priority] || '#64748b')}>{ch.priority}</span>
                <span style={badgeStyle(typeColors[ch.type] || '#64748b')}>{ch.type}</span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', padding: '3px 10px' }}>{ch.category}</span>
              </div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'right' }}><div>Submitted {timeAgo(ch.submittedAt)}</div><div>{new Date(ch.submittedAt).toLocaleString()}</div></div>
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: 16, whiteSpace: 'pre-wrap' }}>{ch.description}</div>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 12, color: 'var(--text-muted)' }}>
            <div><strong style={{ color: 'var(--text-secondary)' }}>Submitted by:</strong> {ch.submittedBy.name} ({ch.submittedBy.email})</div>
            {ch.reviewedBy && <div><strong style={{ color: 'var(--text-secondary)' }}>Reviewed by:</strong> {ch.reviewedBy.name}</div>}
            {ch.implementedBy && <div><strong style={{ color: 'var(--text-secondary)' }}>Implemented by:</strong> {ch.implementedBy}</div>}
            {ch.completedAt && <div><strong style={{ color: 'var(--text-secondary)' }}>Completed:</strong> {new Date(ch.completedAt).toLocaleString()}</div>}
          </div>
          {ch.reference && <div style={{ marginTop: 12, fontSize: 13, color: 'var(--text-secondary)' }}><strong>Reference: </strong>{ch.reference.match(/^https?:\/\//) ? <a href={ch.reference} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-blue)', textDecoration: 'none' }}>{ch.reference} ↗</a> : ch.reference}</div>}
          {ch.reviewNotes && <div style={{ marginTop: 12, padding: 12, background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', fontSize: 13, color: 'var(--text-secondary)' }}><strong>Review Notes:</strong> {ch.reviewNotes}</div>}
          {ch.completionNotes && <div style={{ marginTop: 8, padding: 12, background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', fontSize: 13, color: 'var(--text-secondary)' }}><strong>Completion Notes:</strong> {ch.completionNotes}</div>}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
          {ch.status === 'submitted' && isAdmin && <button className="insights-btn" disabled={actionLoading} onClick={function() { handleAction(ch.id, 'review'); }}>Mark Under Review</button>}
          {(ch.status === 'submitted' || ch.status === 'under_review') && isAdmin && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input style={Object.assign({}, inputStyle, { width: 160 })} placeholder="Reviewer name" value={reviewName} onChange={function(e) { setReviewName(e.target.value); }} />
              <input style={Object.assign({}, inputStyle, { width: 200 })} placeholder="Reviewer email (optional)" value={reviewEmail} onChange={function(e) { setReviewEmail(e.target.value); }} />
              <input style={Object.assign({}, inputStyle, { width: 220 })} placeholder="Notes (optional)" value={reviewNotes} onChange={function(e) { setReviewNotes(e.target.value); }} />
              <button className="insights-btn" style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e', borderColor: 'rgba(34,197,94,0.3)' }} disabled={actionLoading || !reviewName} onClick={function() { handleAction(ch.id, 'approve', { reviewedBy: { name: reviewName, email: reviewEmail }, reviewNotes: reviewNotes }); setReviewName(''); setReviewEmail(''); setReviewNotes(''); }}>Approve</button>
              <button className="insights-btn" style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', borderColor: 'rgba(239,68,68,0.3)' }} disabled={actionLoading || !reviewName} onClick={function() { handleAction(ch.id, 'deny', { reviewedBy: { name: reviewName, email: reviewEmail }, reviewNotes: reviewNotes || 'Denied' }); setReviewName(''); setReviewEmail(''); setReviewNotes(''); }}>Deny</button>
            </div>
          )}
          {ch.status === 'approved' && isAdmin && <button className="insights-btn" style={{ background: 'rgba(167,139,250,0.15)', color: '#a78bfa', borderColor: 'rgba(167,139,250,0.3)' }} disabled={actionLoading} onClick={function() { handleAction(ch.id, 'start', { implementedBy: 'CloudByte Group' }); }}>Start Implementation</button>}
          {ch.status === 'in_progress' && isAdmin && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input style={Object.assign({}, inputStyle, { width: 300 })} placeholder="Completion notes (optional)" value={completionNotes} onChange={function(e) { setCompletionNotes(e.target.value); }} />
              <button className="insights-btn" style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e', borderColor: 'rgba(34,197,94,0.3)' }} disabled={actionLoading} onClick={function() { handleAction(ch.id, 'complete', { completionNotes: completionNotes }); setCompletionNotes(''); }}>Mark Completed</button>
            </div>
          )}
          {!['completed', 'denied', 'cancelled'].includes(ch.status) && isAdmin && <button className="insights-btn" style={{ background: 'rgba(100,116,139,0.15)', color: '#64748b', borderColor: 'rgba(100,116,139,0.3)' }} disabled={actionLoading} onClick={function() { handleAction(ch.id, 'cancel'); }}>Cancel Request</button>}
        </div>
        <div className="section-title">Comments ({ch.comments.length})</div>
        {ch.comments.length > 0 && <div style={{ marginBottom: 16 }}>{ch.comments.map(function(c, i) { return (<div key={i} style={{ padding: '10px 14px', background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', marginBottom: 6 }}><div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}><span style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent-blue)' }}>{c.by}</span><span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{timeAgo(c.at)}</span></div><div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{c.text}</div></div>); })}</div>}
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <input style={Object.assign({}, inputStyle, { width: 160 })} placeholder="Your name" value={commentName} onChange={function(e) { setCommentName(e.target.value); }} />
          <input style={Object.assign({}, inputStyle, { flex: 1 })} placeholder="Add a comment..." value={commentText} onChange={function(e) { setCommentText(e.target.value); }} onKeyDown={function(e) { if (e.key === 'Enter' && commentName && commentText) { handleAction(ch.id, 'comment', { comment: { by: commentName, text: commentText } }); setCommentText(''); } }} />
          <button className="insights-btn" disabled={!commentName || !commentText || actionLoading} onClick={function() { handleAction(ch.id, 'comment', { comment: { by: commentName, text: commentText } }); setCommentText(''); }}>Add</button>
        </div>
      </div>
    );
  }

  if (showForm) {
    var sourceCtx = formData._sourceContext;
    return (
      <div className="tab-content">
        <Breadcrumb items={[{ label: 'Change Management', onClick: function() { setShowForm(false); } }, { label: 'New Request' }]} />
        <div className="section-title">Submit Change Request</div>
        {sourceCtx && sourceCtx.source === 'redteam' && (
          <div className="change-prefill-banner">
            <span className="change-prefill-badge">IMPORTED</span>
            <div className="change-prefill-body">
              <div className="change-prefill-title">Pre-filled from Aurelius Red Team</div>
              <div className="change-prefill-sub">Source: {sourceCtx.source_id} · Impact analysis auto-populated from observed simulation data</div>
            </div>
            {sourceCtx.impact_summary && (
              <div className="change-prefill-impact">
                <span><strong>{sourceCtx.impact_summary.users_affected}</strong> users</span>
                {sourceCtx.impact_summary.devices_affected > 0 && <span><strong>{sourceCtx.impact_summary.devices_affected}</strong> devices</span>}
                <span><strong>{sourceCtx.impact_summary.cost_estimate}</strong></span>
                <span><strong>{sourceCtx.impact_summary.timeline}</strong></span>
              </div>
            )}
          </div>
        )}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-lg)', padding: 28, maxWidth: 700 }}>
          <div style={{ display: 'flex', gap: 14 }}>
            <div style={Object.assign({}, fieldStyle, { flex: 1 })}><label style={labelStyle}>Your Name *</label><input style={inputStyle} value={formData.name} onChange={function(e) { setFormData(Object.assign({}, formData, { name: e.target.value })); }} placeholder="John Smith" /></div>
            <div style={Object.assign({}, fieldStyle, { flex: 1 })}><label style={labelStyle}>Your Email *</label><input style={inputStyle} value={formData.email} onChange={function(e) { setFormData(Object.assign({}, formData, { email: e.target.value })); }} placeholder="john@company.com" /></div>
          </div>
          <div style={fieldStyle}><label style={labelStyle}>Title *</label><input style={inputStyle} value={formData.title} onChange={function(e) { setFormData(Object.assign({}, formData, { title: e.target.value })); }} placeholder="Brief description of the change" /></div>
          <div style={fieldStyle}><label style={labelStyle}>Description *</label><textarea style={textareaStyle} value={formData.description} onChange={function(e) { setFormData(Object.assign({}, formData, { description: e.target.value })); }} placeholder="Detailed description..." /></div>
          <div style={fieldStyle}><label style={labelStyle}>Reference (optional)</label><input style={inputStyle} value={formData.reference || ''} onChange={function(e) { setFormData(Object.assign({}, formData, { reference: e.target.value })); }} placeholder="Jira issue, Asana link, or ticket number" /></div>
          <div style={{ display: 'flex', gap: 14 }}>
            <div style={Object.assign({}, fieldStyle, { flex: 1 })}><label style={labelStyle}>Type</label><select style={selectStyle} value={formData.type} onChange={function(e) { setFormData(Object.assign({}, formData, { type: e.target.value })); }}><option value="IT Change">IT Change</option><option value="Security Change">Security Change</option></select></div>
            <div style={Object.assign({}, fieldStyle, { flex: 1 })}><label style={labelStyle}>Category</label><select style={selectStyle} value={formData.category} onChange={function(e) { setFormData(Object.assign({}, formData, { category: e.target.value })); }}><option value="New User">New User</option><option value="Offboarding">Offboarding</option><option value="Software Install">Software Install</option><option value="Software Removal">Software Removal</option><option value="Hardware Request">Hardware Request</option><option value="Firewall Rule">Firewall Rule</option><option value="Access Change">Access Change</option><option value="Policy Update">Policy Update</option><option value="Security Exception">Security Exception</option><option value="Configuration Change">Configuration Change</option><option value="Network Change">Network Change</option><option value="Other">Other</option></select></div>
            <div style={Object.assign({}, fieldStyle, { flex: 1 })}><label style={labelStyle}>Priority</label><select style={selectStyle} value={formData.priority} onChange={function(e) { setFormData(Object.assign({}, formData, { priority: e.target.value })); }}><option value="Low">Low</option><option value="Medium">Medium</option><option value="High">High</option><option value="Critical">Critical</option></select></div>
          </div>
          <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
            <button className="qbr-generate-btn" style={{ flex: 'none', padding: '12px 32px' }} disabled={submitting || !formData.title || !formData.description || !formData.name || !formData.email} onClick={handleSubmit}>{submitting ? 'Submitting...' : 'Submit Change Request'}</button>
            <button className="insights-btn" onClick={function() { setShowForm(false); }}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  var openCount = summary.submitted + summary.under_review + summary.approved + summary.in_progress;
  return (
    <div className="tab-content">
      <TabDescription text="Formal change management workflow. All IT and security changes are submitted, reviewed, and tracked here for compliance audit readiness and operational accountability." />
      <ContentBand title="Change Management Flow" subtitle={'Live pipeline across all request stages — click any stage to filter' + (kindFilter !== 'all' ? ' · scope: ' + (kindFilter === 'changes' ? 'change requests only' : 'AI proposals only') : '')}>
        <ChangesFlowPipeline changes={pipelineItems} summary={summary} onStageClick={setStatusFilter} statusFilter={statusFilter} />
        <ChangesTerminalRow summary={summary} onStageClick={setStatusFilter} statusFilter={statusFilter} />
        <ChangesMetricTiles changes={pipelineItems} summary={summary} />
      </ContentBand>
      <div style={{ marginTop: 16, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button className="qbr-generate-btn" style={{ flex: 'none', padding: '10px 24px', fontSize: 12 }} onClick={function() { setShowForm(true); }}>+ New Change Request</button>
        {statusFilter && <button className="insights-btn" onClick={function() { setStatusFilter(null); }}>Clear Filter: {statusLabels[statusFilter]}</button>}
        {/* Kind filter pills — surface the new federation cleanly. AI count
            shown in the pill so the operator knows there's something to
            review even before clicking. */}
        <div className="changes-kind-pills">
          <button type="button" className={'changes-kind-pill' + (kindFilter === 'all' ? ' active' : '')} onClick={function() { setKindFilter('all'); }}>All</button>
          <button type="button" className={'changes-kind-pill' + (kindFilter === 'changes' ? ' active' : '')} onClick={function() { setKindFilter('changes'); }}>Change Requests</button>
          <button type="button" className={'changes-kind-pill changes-kind-pill-ai' + (kindFilter === 'proposals' ? ' active' : '')} onClick={function() { setKindFilter('proposals'); }}>
            AI Proposals
            {pendingProposalCount > 0 && <span className="changes-kind-pill-count">{pendingProposalCount}</span>}
          </button>
        </div>
      </div>
      {proposalToast && <div className={'admin-banner ' + (proposalToast.tone === 'success' ? 'success' : 'error')} style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}>{proposalToast.text}</div>}
      {items.length > 0 ? (
        <>
          <div className="section-title" style={{ marginTop: 24 }}>{statusFilter ? statusLabels[statusFilter] + ' Requests' : 'All Requests'} ({items.length})</div>
          <TableSearch value={chgSearch} onChange={setChgSearch} count={sortRows(filterRows(items, chgSearch, [function(c) { return c.id; }, function(c) { return c.title; }, function(c) { return c.submittedBy.name; }]), chgSort.sortField, chgSort.sortDir, {}).length} total={items.length} noun="requests" />
          <div className="posture-table-wrap">
            <table className="posture-table">
              <thead><tr><th style={{ width: 32 }}></th><SortTh field="id" current={chgSort.sortField} dir={chgSort.sortDir} onSort={chgSort.toggleSort}>ID</SortTh><SortTh field="title" current={chgSort.sortField} dir={chgSort.sortDir} onSort={chgSort.toggleSort}>Title</SortTh><SortTh field="type" current={chgSort.sortField} dir={chgSort.sortDir} onSort={chgSort.toggleSort}>Type</SortTh><SortTh field="priority" current={chgSort.sortField} dir={chgSort.sortDir} onSort={chgSort.toggleSort}>Priority</SortTh><SortTh field="status" current={chgSort.sortField} dir={chgSort.sortDir} onSort={chgSort.toggleSort}>Status</SortTh><th>Submitted By</th><SortTh field="submitted" current={chgSort.sortField} dir={chgSort.sortDir} onSort={chgSort.toggleSort}>Submitted</SortTh></tr></thead>
              <tbody>
                {sortRows(filterRows(items, chgSearch, [function(c) { return c.id; }, function(c) { return c.title; }, function(c) { return c.submittedBy.name; }]), chgSort.sortField, chgSort.sortDir, { id: function(c) { return c.id; }, title: function(c) { return c.title; }, type: function(c) { return c.type; }, priority: function(c) { return c.priority; }, status: function(c) { return c.status; }, submitted: function(c) { return new Date(c.submittedAt).getTime(); } }).map(function(ch) {
                  var isProposal = ch._kind === 'proposal';
                  return (
                    <tr key={ch.id} className={isProposal ? 'changes-row-proposal' : ''} style={{ cursor: 'pointer' }} onClick={function() { setSelectedChange(ch); }}>
                      <td>{isProposal ? <span className="changes-kind-badge changes-kind-ai" title="AI proposal — auto-drift or operator-initiated">AI</span> : <span className="changes-kind-badge changes-kind-chg" title="Change request">CHG</span>}</td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600 }}>{ch.id}</td>
                      <td className="posture-device-name" style={{ maxWidth: 280 }}><span className={'status-dot ' + (ch.status === 'submitted' ? 'status-dot-info' : ch.status === 'approved' || ch.status === 'completed' ? 'status-dot-good' : ch.status === 'rejected' ? 'status-dot-critical' : 'status-dot-warning')} />{ch.title}</td>
                      <td><span style={badgeStyle(typeColors[ch.type] || '#64748b')}>{ch.type}</span></td>
                      <td><span style={{ color: priorityColors[ch.priority] || '#94a3b8', fontWeight: 600, fontSize: 12 }}>{ch.priority}</span></td>
                      <td><span style={badgeStyle(statusColors[ch.status] || '#64748b')}>{isProposal ? proposalStatusLabel(ch.automationStatus) : statusLabels[ch.status]}</span></td>
                      <td style={{ fontSize: 12 }}>{ch.submittedBy.name}</td>
                      <td className="posture-date">{timeAgo(ch.submittedAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="empty-state" style={{ marginTop: 24 }}>{statusFilter ? 'No ' + statusLabels[statusFilter].toLowerCase() + ' requests.' : kindFilter === 'proposals' ? 'No AI proposals pending.' : 'No requests yet.'}</div>
      )}
    </div>
  );
}
// ── Budget Tab ────────────────────────────────────────────────────
// Wrapper: Budget parent gets a sub-tab toggle (Spend / Cost Savings).
// The original content is now BudgetSpendTab; the new CostSavingsTab
// surfaces leak-detection signals (license waste, contract renewals,
// cyber-insurance opportunities, top vendor spend).
function BudgetTab({ clientId }) {
  var [subTab, setSubTab] = useState('spend');
  var rc = getRestrictedClaims();
  return (
    <div className="tab-content compliance-with-subtabs">
      {rc.readOnly && (
        <div style={{ padding: '8px 14px', background: 'rgba(96,165,250,0.08)', border: '1px solid rgba(96,165,250,0.3)', borderRadius: 6, marginBottom: 12, fontSize: 12, color: '#cbd5e1', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14 }}>👁</span>
          <span><strong style={{ color: '#60a5fa' }}>Read-only view</strong>{rc.roleLabel ? ' · ' + rc.roleLabel : ''} — every edit affordance on this tab is disabled, and the server rejects any non-GET request. Spend data refreshes live from connected platforms.</span>
        </div>
      )}
      <div className="compliance-subtabs">
        <button type="button" className={'compliance-subtab' + (subTab === 'spend' ? ' active' : '')} onClick={function() { setSubTab('spend'); }}>
          <span className="compliance-subtab-icon">$</span>
          <span>Spend</span>
        </button>
        <button type="button" className={'compliance-subtab' + (subTab === 'savings' ? ' active' : '')} onClick={function() { setSubTab('savings'); }}>
          <span className="compliance-subtab-icon">↓</span>
          <span>Cost Savings</span>
        </button>
      </div>
      {subTab === 'spend' && <BudgetSpendTab clientId={clientId} />}
      {subTab === 'savings' && <CostSavingsTab clientId={clientId} />}
    </div>
  );
}

// ── Cost Savings sub-tab ─────────────────────────────────────────────
// Single-pane "where can we cut costs / what's renewing?" view. Aggregates:
//   • Microsoft 365 license waste (live from Graph)
//   • Pax8 top spend (renegotiation candidates)
//   • Atlassian billing snapshot
//   • Cyber-insurance premium-impact estimate
//   • Manually tracked contracts (CRUD'd by admin) with renewal alerts
function CostSavingsTab({ clientId }) {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState(null);
  var [editing, setEditing] = useState(null);     // { id?, ... } — open editor
  var [toast, setToast] = useState(null);
  // Permission gate — admin role AND not in client-portal preview URL.
  // !IS_CLIENT_VIEW alone leaks admin UI to client-role users on /.
  // Restricted users with read_only=true also force this to false
  // (defensive — restricted users are role='client' anyway, but
  // belt-and-suspenders here in case an admin gets the read_only flag).
  var isAdmin = isAdminRole() && !IS_CLIENT_VIEW && !isReadOnlyUser();

  function showToast(m, t) { setToast({ msg: m, type: t || 'info' }); setTimeout(function() { setToast(null); }, 3500); }

  function fetchData() {
    setLoading(true);
    authFetch(API + '/clients/' + clientId + '/cost-savings')
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setLoading(false);
        if (x.ok) setData(x.d);
        else setError(x.d.error || 'Cost savings calculation failed');
      })
      .catch(function() { setLoading(false); setError('Cost savings calculation failed'); });
  }

  useEffect(fetchData, [clientId]);

  function deleteContract(c) {
    if (!window.confirm('Delete contract for ' + c.vendor + '? This is logged in the audit trail and can be restored from the DB if needed.')) return;
    authFetch(API + '/admin/clients/' + clientId + '/contracts/' + c.id, { method: 'DELETE' })
      .then(function(r) { if (r.ok) { showToast('Contract deleted', 'ok'); fetchData(); } else { showToast('Delete failed', 'error'); } });
  }

  if (loading) return <div className="tab-content"><SkeletonLoader /></div>;
  if (error) return <div className="tab-content"><div className="empty-state" style={{ color: 'var(--accent-red)' }}>{error}</div></div>;
  if (!data) return <div className="tab-content"><div className="empty-state">No data.</div></div>;

  var s = data.signals || {};
  var fmt = function(n) { if (n == null) return '—'; return '$' + Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 }); };

  return (
    <div className="tab-content">
      <TabDescription text="Cost-savings opportunities surfaced from live data plus admin-tracked contracts. The opportunity number is the sum of unused license cost, projected cyber-insurance reduction (if controls allow), and renegotiation candidates ready for action." />

      {/* Hero — total annual opportunity */}
      <div className="savings-hero">
        <div className="savings-hero-main">
          <div className="savings-hero-eyebrow">Annual savings opportunity</div>
          <div className="savings-hero-value">{fmt(data.annualOpportunity)}<span className="savings-hero-unit">/year</span></div>
          <div className="savings-hero-context">
            Sum of license waste + projected cyber-insurance reduction.
            Pax8 / Atlassian / contract spend shown for renegotiation context.
          </div>
        </div>
        <div className="savings-hero-side">
          {s.licenses && s.licenses.annualWaste > 0 && (
            <div className="savings-hero-strip-item">
              <div className="savings-hero-strip-label">M365 unused licenses</div>
              <div className="savings-hero-strip-value">{fmt(s.licenses.annualWaste)}</div>
            </div>
          )}
          {s.cyberInsurance && s.cyberInsurance.estimatedAnnualSavings > 0 && (
            <div className="savings-hero-strip-item">
              <div className="savings-hero-strip-label">Cyber insurance reduction</div>
              <div className="savings-hero-strip-value">{fmt(s.cyberInsurance.estimatedAnnualSavings)}</div>
            </div>
          )}
        </div>
      </div>

      {/* M365 license waste detail */}
      {s.licenses && (
        <ContentBand title="Microsoft 365 License Waste" subtitle={s.licenses.error ? 'Error fetching' : (s.licenses.totalUnusedSeats || 0) + ' seats unused across ' + (s.licenses.rows ? s.licenses.rows.length : 0) + ' SKUs — ' + fmt(s.licenses.annualWaste) + '/year recoverable'} rightContent={<span className="live-badge">LIVE</span>}>
          {s.licenses.error
            ? <div className="empty-state" style={{ color: 'var(--accent-red)', padding: 24 }}>{s.licenses.error}</div>
            : !s.licenses.rows || s.licenses.rows.length === 0
              ? <div className="empty-state" style={{ padding: 24 }}>No license SKUs reported by this tenant.</div>
              : (
                <div className="savings-table">
                  <div className="savings-table-head">
                    <span>SKU</span>
                    <span style={{ textAlign: 'right' }}>Purchased</span>
                    <span style={{ textAlign: 'right' }}>Assigned</span>
                    <span style={{ textAlign: 'right' }}>Unused</span>
                    <span style={{ textAlign: 'right' }}>Utilization</span>
                    <span style={{ textAlign: 'right' }}>Annual waste</span>
                  </div>
                  {s.licenses.rows.map(function(r) {
                    var utilCls = r.utilizationPct >= 90 ? 'good' : r.utilizationPct >= 70 ? 'ok' : r.utilizationPct >= 50 ? 'warn' : 'bad';
                    return (
                      <div key={r.sku} className={'savings-table-row util-' + utilCls}>
                        <span><code>{r.sku}</code></span>
                        <span style={{ textAlign: 'right' }}>{r.purchased}</span>
                        <span style={{ textAlign: 'right' }}>{r.assigned}</span>
                        <span style={{ textAlign: 'right', fontWeight: r.unused > 0 ? 700 : 400, color: r.unused > 0 ? 'var(--accent-amber)' : 'inherit' }}>{r.unused}</span>
                        <span style={{ textAlign: 'right', color: utilCls === 'bad' ? '#ef4444' : utilCls === 'warn' ? '#f59e0b' : utilCls === 'good' ? '#22c55e' : 'inherit', fontWeight: 600 }}>{r.utilizationPct}%</span>
                        <span style={{ textAlign: 'right', fontWeight: 700, color: r.annualWaste > 0 ? 'var(--accent-amber)' : 'inherit' }}>{fmt(r.annualWaste)}</span>
                      </div>
                    );
                  })}
                </div>
              )
          }
        </ContentBand>
      )}

      {/* Cyber insurance summary card */}
      {s.cyberInsurance && (
        <ContentBand title="Cyber Insurance Renegotiation" subtitle={s.cyberInsurance.error ? s.cyberInsurance.error : (s.cyberInsurance.carrier || 'No carrier on file') + ' · current premium ' + fmt(s.cyberInsurance.currentPremium) + (s.cyberInsurance.renewalDate ? ' · renews ' + new Date(s.cyberInsurance.renewalDate).toLocaleDateString() : '')}>
          {!s.cyberInsurance.error && s.cyberInsurance.estimatedAnnualSavings > 0 ? (
            <div className="savings-cyber-callout">
              <div className="savings-cyber-savings">
                <div className="savings-cyber-label">Estimated savings at renewal</div>
                <div className="savings-cyber-value">{fmt(s.cyberInsurance.estimatedAnnualSavings)}<span className="savings-cyber-unit">/year</span></div>
                <div className="savings-cyber-pct">{s.cyberInsurance.impactPctLow}–{s.cyberInsurance.impactPctHigh}% range based on evidenced controls</div>
              </div>
              <div className="savings-cyber-cta">
                <div>Review the underwriting checklist and present evidence packs at renewal to capture this savings.</div>
                <div style={{ marginTop: 8, fontSize: 11, opacity: 0.7 }}>Open <strong>Compliance → Cyber Insurance</strong> for the full breakdown.</div>
              </div>
            </div>
          ) : (
            <div className="empty-state" style={{ padding: 20 }}>{s.cyberInsurance.error || 'No carrier or premium on file. Add it in Admin → Integrations → Edit.'}</div>
          )}
        </ContentBand>
      )}

      {/* Pax8 top spend */}
      {s.pax8 && !s.pax8.error && s.pax8.subscriptionCount > 0 && (
        <ContentBand title="Pax8 Top Spend (Renegotiation Candidates)" subtitle={s.pax8.subscriptionCount + ' active subscriptions · ' + fmt(s.pax8.totalAnnualCost) + '/year'} rightContent={<span className="live-badge">LIVE</span>}>
          <div className="savings-topspend">
            {s.pax8.topSpend.map(function(t, i) {
              return (
                <div key={t.id} className="savings-topspend-row">
                  <span className="savings-topspend-rank">#{i + 1}</span>
                  <span className="savings-topspend-product"><code>{t.productId}</code> × {t.quantity}</span>
                  <span className="savings-topspend-cost">{fmt(t.monthlyCost)}<span className="muted">/mo</span></span>
                </div>
              );
            })}
          </div>
        </ContentBand>
      )}

      {/* Contracts (manually tracked) */}
      <ContentBand
        title="Tracked Contracts"
        subtitle={s.contracts ? (s.contracts.count + ' contract' + (s.contracts.count === 1 ? '' : 's') + ' · ' + fmt(s.contracts.totalAnnualCost) + '/year' + (s.contracts.expiringSoonCount > 0 ? ' · ' + s.contracts.expiringSoonCount + ' renewing soon' : '')) : ''}
        rightContent={isAdmin && <button className="admin-btn primary sm" onClick={function() { setEditing({}); }}>+ New contract</button>}
      >
        {(!s.contracts || s.contracts.count === 0)
          ? <div className="empty-state" style={{ padding: 24 }}>{isAdmin ? 'No contracts tracked yet. Add ISP, hardware leases, professional services, or any non-Pax8 vendor agreement here so renewals show up in the Cost Savings view.' : 'No contracts on file yet. Your account team will populate this view.'}</div>
          : (
            <div className="savings-contracts">
              {s.contracts.contracts.map(function(c) {
                var statusCls = c.renewal_status;
                var statusLabel = statusCls === 'expired' ? Math.abs(c.days_to_expire) + 'd past expiry'
                  : statusCls === 'critical' ? 'Renews in ' + c.days_to_expire + 'd'
                  : statusCls === 'soon' ? 'Renews in ' + c.days_to_expire + 'd'
                  : statusCls === 'ok' ? c.days_to_expire + 'd to renewal'
                  : 'No expiry on file';
                return (
                  <div key={c.id} className={'savings-contract savings-contract-' + statusCls}>
                    <div className="savings-contract-main">
                      <div className="savings-contract-head">
                        <span className="savings-contract-vendor">{c.vendor}</span>
                        {c.product && <span className="savings-contract-product">— {c.product}</span>}
                        <span className={'savings-contract-type type-' + (c.contract_type || 'other')}>{c.contract_type || 'other'}</span>
                        {c.auto_renew && <span className="savings-contract-autorenew">Auto-renew</span>}
                      </div>
                      <div className="savings-contract-meta">
                        <span><strong>{fmt(c.monthly_cost)}</strong>/mo · <strong>{fmt(c.annual_cost)}</strong>/yr</span>
                        {c.expires_at && <span>Expires {new Date(c.expires_at).toLocaleDateString()}</span>}
                        {c.renewal_notice_days && <span>{c.renewal_notice_days}d notice required</span>}
                      </div>
                      {c.notes && <div className="savings-contract-notes">{c.notes}</div>}
                    </div>
                    <div className="savings-contract-side">
                      <span className={'savings-contract-status status-' + statusCls}>{statusLabel}</span>
                      {c.days_to_notice_deadline != null && c.days_to_notice_deadline >= 0 && c.days_to_notice_deadline < 30 && (
                        <span className="savings-contract-notice-warning">⚠ {c.days_to_notice_deadline}d to give cancel notice</span>
                      )}
                      {isAdmin && (
                        <div className="savings-contract-actions">
                          <button className="admin-btn sm" onClick={function() { setEditing(c); }}>Edit</button>
                          <button className="admin-btn sm" onClick={function() { deleteContract(c); }}>Delete</button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )
        }
      </ContentBand>

      {editing && isAdmin && (
        <ContractEditorModal
          contract={editing}
          clientId={clientId}
          onClose={function() { setEditing(null); }}
          onSaved={function() { setEditing(null); fetchData(); showToast('Contract saved', 'ok'); }}
          onError={function(m) { showToast(m || 'Save failed', 'error'); }}
        />
      )}
      {toast && <div className={'admin-toast ' + (toast.type || 'info')}>{toast.msg}</div>}

      <div className="cyber-disclaimer">
        Generated {new Date(data.generatedAt).toLocaleString()}. License-waste numbers reflect Microsoft Graph at the time of fetch; cyber-insurance projection is an estimated range based on industry-published underwriting factors. Treat as renewal-conversation guidance, not a binding quote.
      </div>
    </div>
  );
}

function ContractEditorModal({ contract, clientId, onClose, onSaved, onError }) {
  var isEdit = contract && contract.id;
  var [form, setForm] = useState({
    vendor: contract.vendor || '',
    product: contract.product || '',
    contract_type: contract.contract_type || 'saas',
    monthly_cost: contract.monthly_cost == null ? '' : String(contract.monthly_cost),
    starts_at: contract.starts_at ? String(contract.starts_at).slice(0, 10) : '',
    expires_at: contract.expires_at ? String(contract.expires_at).slice(0, 10) : '',
    auto_renew: !!contract.auto_renew,
    renewal_notice_days: contract.renewal_notice_days == null ? '' : String(contract.renewal_notice_days),
    notes: contract.notes || ''
  });
  var [saving, setSaving] = useState(false);

  function setField(name, value) { setForm(function(prev) { var next = Object.assign({}, prev); next[name] = value; return next; }); }

  function save() {
    if (!form.vendor.trim()) { onError('Vendor is required'); return; }
    setSaving(true);
    var url = isEdit
      ? API + '/admin/clients/' + clientId + '/contracts/' + contract.id
      : API + '/admin/clients/' + clientId + '/contracts';
    var method = isEdit ? 'PUT' : 'POST';
    authFetch(url, {
      method: method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(Object.assign({}, form, { monthly_cost: form.monthly_cost === '' ? 0 : Number(form.monthly_cost) }))
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) { setSaving(false); if (x.ok) onSaved(); else onError(x.d.error || 'Save failed'); })
      .catch(function() { setSaving(false); onError('Save failed'); });
  }

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={function(e) { e.stopPropagation(); }} style={{ maxWidth: 560 }}>
        <div className="admin-modal-header">{isEdit ? 'Edit contract' : 'New contract'}</div>
        <div className="admin-modal-body" style={{ maxHeight: '72vh', overflow: 'auto' }}>
          <div className="admin-form-grid two">
            <div className="admin-field"><label>Vendor</label>
              <input type="text" value={form.vendor} onChange={function(e) { setField('vendor', e.target.value); }} placeholder="e.g. Comcast Business" />
            </div>
            <div className="admin-field"><label>Product / line</label>
              <input type="text" value={form.product} onChange={function(e) { setField('product', e.target.value); }} placeholder="e.g. 500/500 fiber" />
            </div>
            <div className="admin-field"><label>Type</label>
              <select value={form.contract_type} onChange={function(e) { setField('contract_type', e.target.value); }}>
                <option value="isp">ISP / Connectivity</option>
                <option value="saas">SaaS / Software</option>
                <option value="hardware">Hardware / Lease</option>
                <option value="services">Professional Services</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="admin-field"><label>Monthly cost (USD)</label>
              <input type="text" inputMode="decimal" value={form.monthly_cost} onChange={function(e) { setField('monthly_cost', e.target.value); }} placeholder="500" />
            </div>
            <div className="admin-field"><label>Starts at</label>
              <input type="date" value={form.starts_at} onChange={function(e) { setField('starts_at', e.target.value); }} />
            </div>
            <div className="admin-field"><label>Expires at</label>
              <input type="date" value={form.expires_at} onChange={function(e) { setField('expires_at', e.target.value); }} />
            </div>
            <div className="admin-field"><label>Renewal notice (days)</label>
              <input type="text" inputMode="numeric" value={form.renewal_notice_days} onChange={function(e) { setField('renewal_notice_days', e.target.value); }} placeholder="60" />
              <div className="admin-field-hint">If client must give notice N days before expiry to cancel.</div>
            </div>
            <div className="admin-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 22 }}>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', textTransform: 'none', fontSize: 13, letterSpacing: 0, fontWeight: 400, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!form.auto_renew} onChange={function(e) { setField('auto_renew', e.target.checked); }} /> Auto-renews
              </label>
            </div>
            <div className="admin-field" style={{ gridColumn: '1 / -1' }}><label>Notes</label>
              <textarea className="admin-textarea" rows={3} value={form.notes} onChange={function(e) { setField('notes', e.target.value); }} placeholder="Account contact, MSA reference, renegotiation history…" />
            </div>
          </div>
        </div>
        <div className="admin-modal-footer">
          <button className="admin-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="admin-btn primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Azure Detailed Cost Report ────────────────────────────────────
// On-demand generator backed by /api/clients/:id/azure-cost-report.
// Sectioned into the dimensions Craig-from-GSI's support ticket
// asked for: monthly trend, by service / RG / resource / meter /
// region, plus a VM-centric pass with orphan callouts.
//
// Render strategy: collapsed by default (just a "Generate" button +
// month picker). Expands to a full inline report below the button
// with print + CSV-export affordances. Print stylesheet hides the
// rest of the dashboard so window.print() produces a clean PDF.
function AzureCostReportSection({ clientId }) {
  var [months, setMonths] = useState(6);
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(false);
  var [err, setErr] = useState(null);

  function generate() {
    setLoading(true); setErr(null); setData(null);
    authFetch(API + '/clients/' + clientId + '/azure-cost-report?months=' + months)
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, status: r.status, d: d }; }); })
      .then(function(x) {
        setLoading(false);
        if (x.ok) setData(x.d);
        else setErr((x.d && x.d.error) || ('Report failed (HTTP ' + x.status + ')'));
      })
      .catch(function() { setLoading(false); setErr('Report request failed'); });
  }

  function exportCsv() {
    if (!data) return;
    var lines = [];
    function addSection(title, rows, columns) {
      lines.push(''); lines.push(title); lines.push(columns.join(','));
      rows.forEach(function(r) {
        lines.push(columns.map(function(c) {
          var v = r[c];
          if (v == null) return '';
          v = String(v);
          if (v.indexOf(',') !== -1 || v.indexOf('"') !== -1) v = '"' + v.replace(/"/g, '""') + '"';
          return v;
        }).join(','));
      });
    }
    lines.push('Azure Cost Report');
    lines.push('Client,' + (data.client_name || data.client_id));
    lines.push('Subscription,' + data.subscription_id);
    lines.push('Date range,' + data.date_range.from + ' → ' + data.date_range.to + ' (' + data.date_range.months + ' months)');
    lines.push('Actual total,' + data.totals.actual_total);
    lines.push('Amortized total,' + data.totals.amortized_total);
    lines.push('Monthly average,' + data.totals.monthly_average_actual);
    addSection('Monthly trend', data.monthly_trend, ['month', 'cost']);
    addSection('By service', data.by_service, ['service', 'cost']);
    addSection('By resource group', data.by_resource_group, ['resource_group', 'cost']);
    addSection('By resource', data.by_resource, ['resource_name', 'resource_group', 'resource_type', 'cost', 'amortized_cost']);
    addSection('By meter', data.by_meter, ['category', 'subcategory', 'cost']);
    addSection('By region', data.by_region, ['region', 'cost']);
    addSection('VMs', data.vm_breakdown.vms, ['name', 'resource_group', 'location', 'size', 'os', 'vm_cost', 'os_disk_cost', 'data_disks_cost']);
    addSection('Orphaned disks', data.vm_breakdown.orphaned_disks, ['name', 'resource_group', 'location', 'size_gb', 'sku', 'cost']);
    addSection('Orphaned public IPs', data.vm_breakdown.orphaned_public_ips, ['name', 'resource_group', 'location', 'sku', 'allocation', 'cost']);
    addSection('Snapshots', data.vm_breakdown.snapshots, ['name', 'resource_group', 'location', 'size_gb', 'cost']);
    addSection('Recovery vaults', data.vm_breakdown.recovery_vaults, ['name', 'resource_group', 'location', 'sku', 'cost']);
    var blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url;
    a.download = 'azure-cost-report-' + clientId + '-' + months + 'mo.csv';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function fmtUsd(n) {
    if (n == null) return '—';
    return '$' + Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  function fmtDate(iso) {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString(); } catch (e) { return iso; }
  }

  return (
    <ContentBand
      title="Detailed Azure Cost Report"
      subtitle="Multi-dimensional cost breakdown for support-ticket-style requests (Subscription / RG / Resource / Meter / Region / SKU). Includes orphan resource callouts."
      rightContent={
        <div className="no-print" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>Months</label>
          <select value={months} onChange={function(e) { setMonths(parseInt(e.target.value, 10)); }} style={{ background: 'var(--bg-base)', border: '1px solid var(--border-default)', borderRadius: 6, padding: '4px 8px', fontSize: 12, color: 'var(--text-primary)' }}>
            <option value="1">1</option><option value="3">3</option><option value="6">6</option><option value="12">12</option>
          </select>
          <button className="insights-btn" disabled={loading} onClick={generate}>{loading ? 'Generating…' : (data ? 'Re-run' : 'Generate')}</button>
          {data && <button className="insights-btn" onClick={exportCsv}>Export CSV</button>}
          {data && <button className="qbr-generate-btn" style={{ padding: '6px 14px', fontSize: 12 }} onClick={function() { window.print(); }}>Print / PDF</button>}
        </div>
      }
    >
      {err && <div className="admin-banner error" style={{ fontSize: 12 }}>{err}</div>}
      {loading && <div className="muted" style={{ fontSize: 12, padding: 12 }}>Pulling 6 cost-management queries + ARM inventory in parallel — typically 5-15 seconds.</div>}
      {!data && !loading && !err && (
        <div className="muted" style={{ fontSize: 12, padding: 12 }}>
          Click <strong>Generate</strong> to pull the report. Behind the scenes: monthly trend, breakdown by service / resource group / per-resource / meter / region, amortized variant with reservations + savings plans, and an ARM sweep that calls out orphaned disks / unattached public IPs / snapshots / recovery vaults.
        </div>
      )}
      {data && (
        <div className="azure-cost-report">
          <div className="acr-header">
            <div className="acr-meta">
              <div><span className="acr-meta-label">Subscription</span><code>{data.subscription_id}</code></div>
              <div><span className="acr-meta-label">Date range</span>{fmtDate(data.date_range.from)} → {fmtDate(data.date_range.to)} ({data.date_range.months} mo)</div>
              <div><span className="acr-meta-label">Generated</span>{fmtDate(data.generated_at)}</div>
            </div>
            <div className="metric-tiles" style={{ marginTop: 12 }}>
              <div className="metric-tile">
                <div className="metric-tile-label">Total Actual</div>
                <div className="metric-tile-value">{fmtUsd(data.totals.actual_total)}</div>
                <div className="metric-tile-context">{data.date_range.months}-month window</div>
              </div>
              <div className="metric-tile">
                <div className="metric-tile-label">Amortized</div>
                <div className="metric-tile-value">{fmtUsd(data.totals.amortized_total)}</div>
                <div className="metric-tile-context">Incl. reservations + savings plans</div>
              </div>
              <div className="metric-tile">
                <div className="metric-tile-label">Monthly Avg</div>
                <div className="metric-tile-value">{fmtUsd(data.totals.monthly_average_actual)}</div>
                <div className="metric-tile-context">Across {data.totals.months_observed} observed month(s)</div>
              </div>
              <div className={'metric-tile' + (data.vm_breakdown.orphaned_disks_count > 0 ? ' metric-tile-alert' : '')}>
                <div className="metric-tile-label">Orphans</div>
                <div className="metric-tile-value">{data.vm_breakdown.orphaned_disks_count + data.vm_breakdown.orphaned_public_ips_count}</div>
                <div className="metric-tile-context">Disks + public IPs unattached</div>
              </div>
            </div>
          </div>

          {data.query_errors && data.query_errors.length > 0 && (
            <div className="admin-banner warn" style={{ fontSize: 11, marginTop: 12 }}>
              {data.query_errors.length} cost-management quer{data.query_errors.length === 1 ? 'y' : 'ies'} returned an error — report is partial. Details: {data.query_errors.map(function(e) { return e.query; }).join(', ')}.
            </div>
          )}

          {data.recommendations && data.recommendations.length > 0 && (
            <div className="acr-recs">
              <div className="acr-section-title">Recommendations</div>
              <ul>
                {data.recommendations.map(function(r, i) {
                  return (
                    <li key={i} className={'acr-rec acr-rec-' + r.severity}>
                      <span className={'acr-rec-sev acr-rec-sev-' + r.severity}>{r.severity}</span>
                      <strong>{r.title}</strong>
                      <div className="acr-rec-action">{r.action}</div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div className="acr-section">
            <div className="acr-section-title">Monthly trend</div>
            <table className="posture-table">
              <thead><tr><th>Month</th><th style={{ textAlign: 'right' }}>Cost</th></tr></thead>
              <tbody>
                {data.monthly_trend.map(function(r, i) {
                  return <tr key={i}><td>{r.month}</td><td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{fmtUsd(r.cost)}</td></tr>;
                })}
              </tbody>
            </table>
          </div>

          <div className="acr-section">
            <div className="acr-section-title">By service ({data.by_service.length})</div>
            <table className="posture-table">
              <thead><tr><th>Service</th><th style={{ textAlign: 'right' }}>Cost</th></tr></thead>
              <tbody>
                {data.by_service.slice(0, 25).map(function(r, i) {
                  return <tr key={i}><td>{r.service}</td><td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{fmtUsd(r.cost)}</td></tr>;
                })}
              </tbody>
            </table>
          </div>

          <div className="acr-section">
            <div className="acr-section-title">By resource group ({data.by_resource_group.length})</div>
            <table className="posture-table">
              <thead><tr><th>Resource Group</th><th style={{ textAlign: 'right' }}>Cost</th></tr></thead>
              <tbody>
                {data.by_resource_group.slice(0, 25).map(function(r, i) {
                  return <tr key={i}><td>{r.resource_group}</td><td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{fmtUsd(r.cost)}</td></tr>;
                })}
              </tbody>
            </table>
          </div>

          <div className="acr-section">
            <div className="acr-section-title">Top 50 resources by cost ({data.by_resource.length} total)</div>
            <div className="posture-table-wrap">
              <table className="posture-table">
                <thead><tr><th>Name</th><th>Type</th><th>Resource Group</th><th style={{ textAlign: 'right' }}>Actual</th><th style={{ textAlign: 'right' }}>Amortized</th></tr></thead>
                <tbody>
                  {data.by_resource.slice(0, 50).map(function(r, i) {
                    return (
                      <tr key={i}>
                        <td title={r.resource_id}>{r.resource_name}</td>
                        <td style={{ fontSize: 11, color: 'var(--text-muted)' }}>{r.resource_type || '—'}</td>
                        <td style={{ fontSize: 11 }}>{r.resource_group || '—'}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{fmtUsd(r.cost)}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>{r.amortized_cost != null ? fmtUsd(r.amortized_cost) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="acr-section">
            <div className="acr-section-title">By meter (category / subcategory)</div>
            <table className="posture-table">
              <thead><tr><th>Category</th><th>Subcategory</th><th style={{ textAlign: 'right' }}>Cost</th></tr></thead>
              <tbody>
                {data.by_meter.slice(0, 30).map(function(r, i) {
                  return <tr key={i}><td>{r.category}</td><td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.subcategory || '—'}</td><td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{fmtUsd(r.cost)}</td></tr>;
                })}
              </tbody>
            </table>
          </div>

          <div className="acr-section">
            <div className="acr-section-title">By region</div>
            <table className="posture-table">
              <thead><tr><th>Region</th><th style={{ textAlign: 'right' }}>Cost</th></tr></thead>
              <tbody>
                {data.by_region.map(function(r, i) {
                  return <tr key={i}><td>{r.region}</td><td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{fmtUsd(r.cost)}</td></tr>;
                })}
              </tbody>
            </table>
          </div>

          <div className="acr-section">
            <div className="acr-section-title">VM-related rollup</div>
            <div className="metric-tiles">
              <div className="metric-tile"><div className="metric-tile-label">Virtual Machines</div><div className="metric-tile-value">{fmtUsd(data.vm_related_summary.virtual_machines)}</div></div>
              <div className="metric-tile"><div className="metric-tile-label">Storage / Disks</div><div className="metric-tile-value">{fmtUsd(data.vm_related_summary.storage_disks)}</div></div>
              <div className="metric-tile"><div className="metric-tile-label">Networking</div><div className="metric-tile-value">{fmtUsd(data.vm_related_summary.networking)}</div></div>
              <div className="metric-tile"><div className="metric-tile-label">Bandwidth</div><div className="metric-tile-value">{fmtUsd(data.vm_related_summary.bandwidth)}</div></div>
              <div className="metric-tile"><div className="metric-tile-label">Backup</div><div className="metric-tile-value">{fmtUsd(data.vm_related_summary.backup)}</div></div>
              <div className="metric-tile"><div className="metric-tile-label">Log Analytics</div><div className="metric-tile-value">{fmtUsd(data.vm_related_summary.log_analytics)}</div></div>
              <div className="metric-tile"><div className="metric-tile-label">Azure Monitor</div><div className="metric-tile-value">{fmtUsd(data.vm_related_summary.azure_monitor)}</div></div>
              <div className="metric-tile"><div className="metric-tile-label">Defender for Cloud</div><div className="metric-tile-value">{fmtUsd(data.vm_related_summary.defender)}</div></div>
            </div>
          </div>

          {data.vm_breakdown.vms.length > 0 && (
            <div className="acr-section">
              <div className="acr-section-title">VMs ({data.vm_breakdown.vm_count})</div>
              <div className="posture-table-wrap">
                <table className="posture-table">
                  <thead><tr><th>Name</th><th>RG</th><th>Region</th><th>Size</th><th>OS</th><th style={{ textAlign: 'right' }}>VM Cost</th><th style={{ textAlign: 'right' }}>OS Disk</th><th style={{ textAlign: 'right' }}>Data Disks</th></tr></thead>
                  <tbody>
                    {data.vm_breakdown.vms.map(function(vm, i) {
                      return (
                        <tr key={i}>
                          <td>{vm.name}</td>
                          <td style={{ fontSize: 11 }}>{vm.resource_group}</td>
                          <td style={{ fontSize: 11 }}>{vm.location}</td>
                          <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{vm.size}</td>
                          <td style={{ fontSize: 11 }}>{vm.os}</td>
                          <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{fmtUsd(vm.vm_cost)}</td>
                          <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>{fmtUsd(vm.os_disk_cost)}</td>
                          <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>{fmtUsd(vm.data_disks_cost)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {data.vm_breakdown.orphaned_disks.length > 0 && (
            <div className="acr-section">
              <div className="acr-section-title acr-section-title-alert">⚠ Unattached disks ({data.vm_breakdown.orphaned_disks_count})</div>
              <table className="posture-table">
                <thead><tr><th>Name</th><th>RG</th><th>Size (GB)</th><th>SKU</th><th>Created</th><th style={{ textAlign: 'right' }}>Cost</th></tr></thead>
                <tbody>
                  {data.vm_breakdown.orphaned_disks.map(function(d, i) {
                    return <tr key={i}><td>{d.name}</td><td style={{ fontSize: 11 }}>{d.resource_group}</td><td style={{ textAlign: 'right' }}>{d.size_gb}</td><td style={{ fontSize: 11 }}>{d.sku} ({d.tier})</td><td style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fmtDate(d.created)}</td><td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{fmtUsd(d.cost)}</td></tr>;
                  })}
                </tbody>
              </table>
            </div>
          )}

          {data.vm_breakdown.orphaned_public_ips.length > 0 && (
            <div className="acr-section">
              <div className="acr-section-title acr-section-title-alert">⚠ Unattached public IPs ({data.vm_breakdown.orphaned_public_ips_count})</div>
              <table className="posture-table">
                <thead><tr><th>Name</th><th>RG</th><th>Region</th><th>SKU</th><th>Allocation</th><th style={{ textAlign: 'right' }}>Cost</th></tr></thead>
                <tbody>
                  {data.vm_breakdown.orphaned_public_ips.map(function(ip, i) {
                    return <tr key={i}><td>{ip.name}</td><td style={{ fontSize: 11 }}>{ip.resource_group}</td><td style={{ fontSize: 11 }}>{ip.location}</td><td style={{ fontSize: 11 }}>{ip.sku}</td><td style={{ fontSize: 11 }}>{ip.allocation}</td><td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{fmtUsd(ip.cost)}</td></tr>;
                  })}
                </tbody>
              </table>
            </div>
          )}

          {data.vm_breakdown.snapshots.length > 0 && (
            <div className="acr-section">
              <div className="acr-section-title">Snapshots ({data.vm_breakdown.snapshots_count})</div>
              <table className="posture-table">
                <thead><tr><th>Name</th><th>RG</th><th>Size (GB)</th><th>Created</th><th style={{ textAlign: 'right' }}>Cost</th></tr></thead>
                <tbody>
                  {data.vm_breakdown.snapshots.slice(0, 30).map(function(s, i) {
                    return <tr key={i}><td>{s.name}</td><td style={{ fontSize: 11 }}>{s.resource_group}</td><td style={{ textAlign: 'right' }}>{s.size_gb}</td><td style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fmtDate(s.created)}</td><td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{fmtUsd(s.cost)}</td></tr>;
                  })}
                </tbody>
              </table>
            </div>
          )}

          {data.vm_breakdown.recovery_vaults.length > 0 && (
            <div className="acr-section">
              <div className="acr-section-title">Recovery Services Vaults ({data.vm_breakdown.recovery_vaults.length})</div>
              <table className="posture-table">
                <thead><tr><th>Name</th><th>RG</th><th>Region</th><th>SKU</th><th style={{ textAlign: 'right' }}>Cost</th></tr></thead>
                <tbody>
                  {data.vm_breakdown.recovery_vaults.map(function(v, i) {
                    return <tr key={i}><td>{v.name}</td><td style={{ fontSize: 11 }}>{v.resource_group}</td><td style={{ fontSize: 11 }}>{v.location}</td><td style={{ fontSize: 11 }}>{v.sku}</td><td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{fmtUsd(v.cost)}</td></tr>;
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </ContentBand>
  );
}

function BudgetSpendTab({ clientId }) {
  const [pax8Data, setPax8Data] = useState(null);
  const [licensingData, setLicensingData] = useState(null);
  const [jiraData, setJiraData] = useState(null);
  const [vmData, setVmData] = useState(null);
  const [loading, setLoading] = useState(true);
  var paxSort = useTableSort('product', 'asc');
  var vmSort = useTableSort('name', 'asc');
  const [forecastDelta, setForecastDelta] = useState(0);
  const [costHistory, setCostHistory] = useState(null);

  useEffect(function() {
    setLoading(true); setPax8Data(null); setLicensingData(null); setJiraData(null); setVmData(null); setForecastDelta(0); setCostHistory(null);
    Promise.all([
      authFetch(API + '/clients/' + clientId + '/subscriptions').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
      authFetch(API + '/clients/' + clientId + '/licensing').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
      authFetch(API + '/clients/' + clientId + '/jira').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
      authFetch(API + '/clients/' + clientId + '/azure-vms').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
      authFetch(API + '/clients/' + clientId + '/billing-history').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
    ]).then(function(results) { setPax8Data(results[0]); setLicensingData(results[1]); setJiraData(results[2]); setVmData(results[3]); setCostHistory(results[4]); setLoading(false); });
  }, [clientId]);

  if (loading) return <div className="tab-content"><SkeletonLoader /></div>;

  var costSources = [];
  var totalMonthly = 0;
  var pax8Monthly = 0, pax8Subs = [], pax8PerUserCost = 0, pax8UserCount = 0;
  if (pax8Data && pax8Data.enabled) {
    pax8Monthly = pax8Data.summary.totalMonthlyCost;
    pax8Subs = pax8Data.subscriptions || [];
    totalMonthly += pax8Monthly;
    costSources.push({ name: 'Microsoft (via Pax8)', monthly: pax8Monthly, source: 'pax8', live: true });
    var userSubs = pax8Subs.filter(function(s) { return s.quantity > 0 && s.pricePerUnit > 0; });
    if (userSubs.length > 0) { pax8UserCount = Math.max.apply(null, userSubs.map(function(s) { return s.quantity; })); pax8PerUserCost = pax8Monthly / pax8UserCount; }
  }
  var atlassianMonthly = 0, atlassianProducts = [];
  if (jiraData && jiraData.billing) { atlassianMonthly = jiraData.billing.totalMonthlyCost; atlassianProducts = jiraData.billing.products || []; totalMonthly += atlassianMonthly; costSources.push({ name: 'Atlassian', monthly: atlassianMonthly, source: 'atlassian', live: false }); }
  var vmMonthly = 0, vms = [];
  if (vmData && vmData.enabled) { vmMonthly = vmData.costs ? vmData.costs.forecast : 0; vms = vmData.vms || []; totalMonthly += vmMonthly; costSources.push({ name: 'Azure Infrastructure', monthly: vmMonthly, source: 'azure', live: true }); }
  var totalAnnual = totalMonthly * 12;

  var forecastUsers = pax8UserCount + forecastDelta;
  if (forecastUsers < 1) forecastUsers = 1;
  var forecastPax8 = pax8PerUserCost * forecastUsers;
  var forecastAtlassian = atlassianMonthly;
  if (jiraData && jiraData.billing && jiraData.billing.totalLicensedUsers > 0) forecastAtlassian = (atlassianMonthly / jiraData.billing.totalLicensedUsers) * forecastUsers;
  var forecastTotal = forecastPax8 + forecastAtlassian + (vmMonthly);
  var forecastDiff = forecastTotal - totalMonthly;

  if (costSources.length === 0) return <div className="tab-content"><IntegrationEmptyState kind="billing" clientId={clientId} extraDetail="Spend rolls up from Pax8 (Microsoft + vendors), Azure subscription, and Atlassian. Connect any one to start surfacing costs." /></div>;

  var fInputStyle = { background: 'var(--bg-base)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '10px 14px', color: 'var(--text-primary)', fontSize: 14, fontFamily: 'var(--font-mono)', width: 80, textAlign: 'center', outline: 'none' };

  return (
    <div className="tab-content">
      <TabDescription text="Consolidated IT spend across Microsoft licensing (Pax8), Atlassian subscriptions, and Azure infrastructure. Use the forecasting tool below to model cost impact from headcount changes." />
      <DataFreshness live={true} />
      <ContentBand title="Cost Overview">
        <QuickStats items={[
          { label: 'Est. Monthly', value: '$' + Math.round(totalMonthly).toLocaleString() },
          { label: 'Est. Annual', value: '$' + Math.round(totalAnnual).toLocaleString() },
          { label: 'Cost Sources', value: costSources.length },
          pax8UserCount > 0 ? { label: 'Cost Per User', value: '$' + Math.round(pax8PerUserCost).toLocaleString() + '/mo' } : null,
        ].filter(Boolean)} />
      </ContentBand>
      {costHistory && costHistory.months && costHistory.months.length > 0 && (
        <ContentBand title="Monthly Cost Trend" subtitle={'Last ' + costHistory.months.length + ' month' + (costHistory.months.length > 1 ? 's' : '') + ' of billing snapshots'}>
          {(function() {
            var allMonths = costHistory.months.slice();
            var maxCost = Math.max.apply(null, allMonths.map(function(m) { return m.billing.totalMonthly || 0; }).concat([totalMonthly]));
            var monthLabels = { '01': 'Jan', '02': 'Feb', '03': 'Mar', '04': 'Apr', '05': 'May', '06': 'Jun', '07': 'Jul', '08': 'Aug', '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Dec' };
            var rows = allMonths.map(function(entry) {
              var monthNum = entry.month.split('-')[1];
              var label = monthLabels[monthNum] + ' ' + entry.month.split('-')[0];
              var pct = maxCost > 0 ? Math.round((entry.billing.totalMonthly / maxCost) * 100) : 0;
              return (
                <div key={entry.month} className="cost-trend-row">
                  <span className="cost-trend-label">{label}</span>
                  <div className="cost-trend-bar-wrap">
                    <div className="cost-trend-bar" style={{ width: pct + '%' }} />
                  </div>
                  <span className="cost-trend-value">{'$' + Math.round(entry.billing.totalMonthly).toLocaleString()}</span>
                  {entry.delta ? (
                    <span className={'cost-trend-delta' + (entry.delta.direction === 'up' ? ' cost-up' : entry.delta.direction === 'down' ? ' cost-down' : '')}>
                      {entry.delta.direction === 'up' ? '↑' : entry.delta.direction === 'down' ? '↓' : '→'} {entry.delta.direction !== 'flat' ? (entry.delta.amount >= 0 ? '+$' : '-$') + Math.abs(Math.round(entry.delta.amount)).toLocaleString() : '$0'}
                      {entry.delta.pct !== 0 && <span className="cost-trend-pct"> ({entry.delta.pct > 0 ? '+' : ''}{entry.delta.pct}%)</span>}
                    </span>
                  ) : <span className="cost-trend-delta cost-trend-delta-empty" />}
                </div>
              );
            });
            // "Current" row comparing live data vs last snapshot
            var currentPct = maxCost > 0 ? Math.round((totalMonthly / maxCost) * 100) : 100;
            var lastHistorical = allMonths[allMonths.length - 1].billing.totalMonthly;
            var diff = totalMonthly - lastHistorical;
            var pctDiff = lastHistorical > 0 ? Math.round((diff / lastHistorical) * 1000) / 10 : 0;
            rows.push(
              <div key="current" className="cost-trend-row cost-trend-current">
                <span className="cost-trend-label">Current</span>
                <div className="cost-trend-bar-wrap">
                  <div className="cost-trend-bar cost-trend-bar-current" style={{ width: currentPct + '%' }} />
                </div>
                <span className="cost-trend-value">{'$' + Math.round(totalMonthly).toLocaleString()}</span>
                {Math.abs(diff) >= 1 ? (
                  <span className={'cost-trend-delta' + (diff > 0 ? ' cost-up' : ' cost-down')}>
                    {diff > 0 ? '↑' : '↓'} {diff > 0 ? '+$' : '-$'}{Math.abs(Math.round(diff)).toLocaleString()}
                    {pctDiff !== 0 && <span className="cost-trend-pct"> ({pctDiff > 0 ? '+' : ''}{pctDiff}%)</span>}
                  </span>
                ) : <span className="cost-trend-delta cost-trend-delta-empty" />}
              </div>
            );
            return <div className="cost-trend-chart">{rows}</div>;
          })()}
          {costHistory.months.length < 2 && (
            <div className="cost-trend-note">Cost tracking started — monthly trends and deltas will appear as more snapshots are recorded.</div>
          )}
        </ContentBand>
      )}
      <ContentBand title="Spend by Vendor">
        <SegmentBar size="lg" segments={costSources.map(function(src) { return { label: src.name, value: src.monthly, color: src.source === 'pax8' ? '#5b9ef4' : src.source === 'atlassian' ? '#60a5fa' : '#a78bfa' }; })} />
        <MetricList>
          {costSources.map(function(src) {
            var pct = totalMonthly > 0 ? Math.round((src.monthly / totalMonthly) * 100) : 0;
            return <MetricListItem key={src.name} icon="$" iconColor={src.source === 'pax8' ? '#5b9ef4' : src.source === 'atlassian' ? '#60a5fa' : '#a78bfa'} label={src.name} sublabel={pct + '% of total'} value={'$' + Math.round(src.monthly).toLocaleString() + '/mo'} valueColor={src.source === 'pax8' ? '#5b9ef4' : '#60a5fa'} />;
          })}
        </MetricList>
      </ContentBand>
      {pax8Subs.length > 0 && (
        <CollapsibleSection title="Microsoft Subscriptions (Pax8)" defaultOpen={true} rightContent={<span className="live-badge">LIVE</span>}>
          <div className="posture-table-wrap">
            <table className="posture-table">
              <thead><tr><SortTh field="product" current={paxSort.sortField} dir={paxSort.sortDir} onSort={paxSort.toggleSort}>Product</SortTh><SortTh field="vendor" current={paxSort.sortField} dir={paxSort.sortDir} onSort={paxSort.toggleSort}>Vendor</SortTh><SortTh field="qty" current={paxSort.sortField} dir={paxSort.sortDir} onSort={paxSort.toggleSort}>Qty</SortTh><th>Unit Price</th><SortTh field="cost" current={paxSort.sortField} dir={paxSort.sortDir} onSort={paxSort.toggleSort}>Monthly Cost</SortTh><th>Term</th><th>Commitment</th></tr></thead>
              <tbody>
                {sortRows(pax8Subs, paxSort.sortField, paxSort.sortDir, { product: function(s) { return s.productName; }, vendor: function(s) { return s.vendorName; }, qty: function(s) { return s.quantity; }, cost: function(s) { return s.monthlyCost || 0; } }).map(function(s, i) { return (<tr key={s.id || i}><td className="posture-device-name" title={s.productName}>{s.productName}</td><td style={{ fontSize: 12 }}>{s.vendorName}</td><td>{s.quantity}</td><td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>${s.pricePerUnit ? s.pricePerUnit.toFixed(2) : '—'}</td><td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600 }}>${s.monthlyCost ? s.monthlyCost.toFixed(2) : '—'}</td><td style={{ fontSize: 11 }}>{s.billingTerm || '—'}</td><td style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.commitmentTerm || '—'}{s.commitmentEnd ? ' (ends ' + new Date(s.commitmentEnd).toLocaleDateString() + ')' : ''}</td></tr>); })}
                <tr style={{ fontWeight: 700 }}><td colSpan="4" style={{ textAlign: 'right', color: 'var(--text-muted)' }}>Total</td><td style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--accent-blue)' }}>${Math.round(pax8Monthly).toLocaleString()}</td><td colSpan="2"></td></tr>
              </tbody>
            </table>
          </div>
        </CollapsibleSection>
      )}
      {atlassianProducts.length > 0 && (
        <CollapsibleSection title="Atlassian Subscriptions" defaultOpen={true}>
          <div className="posture-table-wrap">
            <table className="posture-table">
              <thead><tr><th>Product</th><th>Plan</th><th>Users</th><th>Monthly Cost</th><th>Cost / User</th></tr></thead>
              <tbody>
                {atlassianProducts.map(function(p, i) { var perUser = p.users > 0 ? (p.monthlyCost / p.users) : 0; return (<tr key={i}><td className="posture-device-name">{p.name}</td><td><span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, backgroundColor: 'rgba(96,165,250,0.15)', color: '#60a5fa' }}>{p.plan}</span></td><td>{p.users || '—'}</td><td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{p.monthlyCost > 0 ? '$' + p.monthlyCost.toFixed(2) : '—'}</td><td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{perUser > 0 ? '$' + perUser.toFixed(2) : '—'}</td></tr>); })}
                <tr style={{ fontWeight: 700 }}><td colSpan="3" style={{ textAlign: 'right', color: 'var(--text-muted)' }}>Total</td><td style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--accent-blue)' }}>${Math.round(atlassianMonthly).toLocaleString()}</td><td></td></tr>
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)' }}>Atlassian billing based on admin portal snapshots.</div>
        </CollapsibleSection>
      )}
      {vmData && vmData.enabled ? (
        <ContentBand title="Azure Virtual Machines" rightContent={<span className="live-badge">LIVE</span>}>
          <SegmentBar segments={[
            { label: 'Running', value: vmData.summary.running || 0, color: '#22c55e' },
            { label: 'Stopped', value: vmData.summary.stopped || 0, color: '#64748b' },
          ]} />
          <QuickStats items={[
            { label: 'Total VMs', value: vmData.summary.total },
            { label: 'Running', value: vmData.summary.running },
            { label: 'MTD Actual', value: '$' + Math.round(vmData.summary.actualMonthlyCost).toLocaleString() },
            { label: 'Forecast', value: '$' + Math.round(vmData.summary.forecastMonthlyCost).toLocaleString() },
          ]} />
          {vms.length > 0 && (
            <div className="posture-table-wrap" style={{ marginTop: 12 }}>
              <table className="posture-table">
                <thead><tr><SortTh field="name" current={vmSort.sortField} dir={vmSort.sortDir} onSort={vmSort.toggleSort}>VM Name</SortTh><SortTh field="size" current={vmSort.sortField} dir={vmSort.sortDir} onSort={vmSort.toggleSort}>Size</SortTh><SortTh field="os" current={vmSort.sortField} dir={vmSort.sortDir} onSort={vmSort.toggleSort}>OS</SortTh><SortTh field="status" current={vmSort.sortField} dir={vmSort.sortDir} onSort={vmSort.toggleSort}>Status</SortTh><SortTh field="region" current={vmSort.sortField} dir={vmSort.sortDir} onSort={vmSort.toggleSort}>Region</SortTh><th>Resource Group</th><th>RG Cost (MTD)</th></tr></thead>
                <tbody>{sortRows(vms, vmSort.sortField, vmSort.sortDir, { name: function(v) { return v.name; }, size: function(v) { return v.size; }, os: function(v) { return v.os; }, status: function(v) { return v.status; }, region: function(v) { return v.location; } }).map(function(vm, i) { var isRunning = vm.status === 'VM running'; return (<tr key={i} className={!isRunning ? 'posture-row-offline' : ''}><td className="posture-device-name">{vm.name}</td><td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{vm.size}</td><td>{vm.os}</td><td><span className={'posture-status ' + (isRunning ? 'posture-online' : 'posture-offline')}>{isRunning ? 'Running' : vm.status}</span></td><td style={{ fontSize: 12 }}>{vm.location}</td><td style={{ fontSize: 12 }}>{vm.resourceGroup}</td><td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{vmData.costs && vmData.costs.byResourceGroup[vm.resourceGroup] ? '$' + vmData.costs.byResourceGroup[vm.resourceGroup].toFixed(2) : '—'}</td></tr>); })}</tbody>
              </table>
            </div>
          )}
          {vmData.costs && Object.keys(vmData.costs.byService).length > 0 && (
            <ContentBand title="Cost Breakdown by Service (MTD)">
              <QuickStats items={Object.entries(vmData.costs.byService).sort(function(a, b) { return b[1] - a[1]; }).map(function(entry) { return { label: entry[0], value: '$' + entry[1].toFixed(2) }; })} />
            </ContentBand>
          )}
        </ContentBand>
      ) : (
        <><div className="section-title" style={{ marginTop: 28 }}>Azure Virtual Machines</div><div className="empty-state">Azure VM monitoring is not configured for this client.</div></>
      )}

      {/* Detailed Azure cost report — admin-only. Answers the
          "give me costs broken down by every dimension for the last N
          months" support-ticket use case. Multi-query rollup against
          Cost Management + ARM resource sweep for orphans. */}
      {vmData && vmData.enabled && isAdminRole() && !IS_CLIENT_VIEW && (
        <AzureCostReportSection clientId={clientId} />
      )}

      {pax8UserCount > 0 && (
        <>
          <div className="section-title" style={{ marginTop: 28 }}>Cost Forecasting</div>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-lg)', padding: 24 }}>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>Adjust the user count to estimate how costs would change.</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>Current Users</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--text-primary)' }}>{pax8UserCount}</div>
              <div style={{ fontSize: 20, color: 'var(--text-muted)' }}>→</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>Forecast Users</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button className="insights-btn" style={{ fontSize: 16, padding: '4px 12px', fontWeight: 700 }} onClick={function() { setForecastDelta(forecastDelta - 5); }}>-5</button>
                <button className="insights-btn" style={{ fontSize: 16, padding: '4px 12px', fontWeight: 700 }} onClick={function() { setForecastDelta(forecastDelta - 1); }}>-1</button>
                <input style={fInputStyle} value={forecastUsers} onChange={function(e) { var v = parseInt(e.target.value) || 1; setForecastDelta(v - pax8UserCount); }} />
                <button className="insights-btn" style={{ fontSize: 16, padding: '4px 12px', fontWeight: 700 }} onClick={function() { setForecastDelta(forecastDelta + 1); }}>+1</button>
                <button className="insights-btn" style={{ fontSize: 16, padding: '4px 12px', fontWeight: 700 }} onClick={function() { setForecastDelta(forecastDelta + 5); }}>+5</button>
              </div>
              {forecastDelta !== 0 && <button className="insights-btn" onClick={function() { setForecastDelta(0); }}>Reset</button>}
            </div>
            <div className="stats-grid">
              <StatCard title="Forecast Monthly" value={'$' + Math.round(forecastTotal).toLocaleString()} accent={forecastDiff > 0 ? '#f59e0b' : forecastDiff < 0 ? '#22c55e' : '#60a5fa'} />
              <StatCard title="Forecast Annual" value={'$' + Math.round(forecastTotal * 12).toLocaleString()} accent="#a78bfa" />
              <StatCard title="Monthly Change" value={(forecastDiff >= 0 ? '+$' : '-$') + Math.abs(Math.round(forecastDiff)).toLocaleString()} accent={forecastDiff > 0 ? '#f59e0b' : forecastDiff < 0 ? '#22c55e' : '#64748b'} subtitle={forecastDelta > 0 ? '+' + forecastDelta + ' users' : forecastDelta < 0 ? forecastDelta + ' users' : 'no change'} />
              <StatCard title="Annual Change" value={(forecastDiff >= 0 ? '+$' : '-$') + Math.abs(Math.round(forecastDiff * 12)).toLocaleString()} accent={forecastDiff > 0 ? '#f59e0b' : forecastDiff < 0 ? '#22c55e' : '#64748b'} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Cyber Insurance tab ──────────────────────────────────────────────
// Maps the live CMMC control state to a consolidated cyber underwriting
// checklist + estimates premium impact. Reuses control snapshot from the
// CMMC tab — opening this tab without first opening CMMC will show
// 'unknown' for every item until the snapshot populates.

function _fmtCurrency(n) {
  if (n == null || isNaN(n)) return '—';
  return '$' + Number(n).toLocaleString();
}
function _ciAnswerCls(a) { return a === 'yes' ? 'yes' : a === 'no' ? 'no' : a === 'partial' ? 'partial' : 'unknown'; }
function _ciAnswerLabel(a) { return a === 'yes' ? 'Yes' : a === 'no' ? 'No' : a === 'partial' ? 'Partial' : 'Verify'; }

function CyberInsuranceItemRow({ item }) {
  var [open, setOpen] = useState(false);
  var hasSourceData = item.sourceControls && item.sourceControls.length > 0;
  return (
    <div className={'cyber-item cyber-item-' + _ciAnswerCls(item.answer) + ' weight-' + item.weight}>
      <button type="button" className="cyber-item-head" onClick={function() { setOpen(!open); }}>
        <span className={'cyber-answer cyber-answer-' + _ciAnswerCls(item.answer)}>
          <span className="cyber-answer-dot" />
          {_ciAnswerLabel(item.answer)}
        </span>
        <span className="cyber-item-question">{item.question}</span>
        {item.weight === 'critical' && <span className="cyber-weight-badge critical">Critical</span>}
        <span className="cyber-item-impact">{item.impact_label}</span>
        <span className="cyber-item-toggle">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="cyber-item-body">
          <div className="cyber-item-detail">{item.detail}</div>
          {hasSourceData && (
            <div className="cyber-item-sources">
              <div className="cyber-item-sources-label">Source controls:</div>
              {item.sourceControls.map(function(sc) {
                return (
                  <div key={sc.id} className={'cyber-source-control ' + sc.state}>
                    <code>{sc.id}</code>
                    <span className={'cmmc-state ' + sc.state}><span className="cmmc-state-dot" />{sc.state === 'pass' ? 'Evidenced' : sc.state === 'fail' ? 'Gap' : 'Verify'}</span>
                    <span className="cyber-source-reason">{sc.reason || '—'}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CyberInsuranceTab({ c, clientId }) {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState(null);
  var [filter, setFilter] = useState('all');     // all | gaps | critical

  useEffect(function() {
    setLoading(true);
    authFetch(API + '/clients/' + clientId + '/cyber-insurance')
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setLoading(false);
        if (x.ok) setData(x.d);
        else setError(x.d.error || 'Cyber insurance evaluation failed');
      })
      .catch(function() { setLoading(false); setError('Cyber insurance evaluation failed'); });
  }, [clientId]);

  if (loading) return <div className="tab-content"><SkeletonLoader /></div>;
  if (error) return <div className="tab-content"><div className="empty-state" style={{ color: 'var(--accent-red)' }}>{error}</div></div>;
  if (!data) return <div className="tab-content"><div className="empty-state">No cyber insurance data.</div></div>;

  var p = data.policy || {};
  var items = data.items || [];
  var visibleItems = items.slice();
  if (filter === 'gaps') visibleItems = visibleItems.filter(function(i) { return i.answer === 'no' || i.answer === 'unknown' || i.answer === 'partial'; });
  if (filter === 'critical') visibleItems = visibleItems.filter(function(i) { return i.weight === 'critical'; });
  // Critical first, then by answer (no/unknown first), then alphabetical
  visibleItems.sort(function(a, b) {
    var w = { critical: 0, high: 1, medium: 2, low: 3 };
    if (w[a.weight] !== w[b.weight]) return w[a.weight] - w[b.weight];
    var r = { no: 0, unknown: 1, partial: 2, yes: 3 };
    if (r[a.answer] !== r[b.answer]) return r[a.answer] - r[b.answer];
    return 0;
  });

  var renewLabel = data.daysToRenewal == null ? null
    : data.daysToRenewal === 0 ? 'Renews today'
    : data.daysToRenewal > 0 ? data.daysToRenewal + ' days to renewal'
    : Math.abs(data.daysToRenewal) + ' days past renewal';
  var renewClass = data.daysToRenewal == null ? '' : data.daysToRenewal < 0 ? 'overdue' : data.daysToRenewal < 60 ? 'soon' : 'ok';

  return (
    <div className="tab-content">
      <TabDescription text="Cyber insurance underwriting readiness — every question carriers ask (Coalition, At-Bay, Beazley, Travelers, Chubb), answered automatically from the live control state in your CMMC evaluation. Yes answers stack into a premium-reduction estimate based on industry-published ranges." />

      {data.hint && <div className="cyber-hint-banner">{data.hint}</div>}

      {/* Hero — policy + impact estimate */}
      <div className="cyber-hero">
        <div className="cyber-hero-policy">
          <div className="cyber-hero-eyebrow">Cyber Insurance Policy</div>
          {p.carrier
            ? <>
                <div className="cyber-hero-carrier">{p.carrier}</div>
                <div className="cyber-hero-policy-meta">
                  {p.policyNumber && <span>Policy <code>{p.policyNumber}</code></span>}
                  {p.coverageLimit && <span>Coverage limit {_fmtCurrency(p.coverageLimit)}</span>}
                  {p.deductible && <span>Deductible {_fmtCurrency(p.deductible)}</span>}
                </div>
                <div className="cyber-hero-premium">
                  <span className="cyber-hero-premium-label">Annual premium</span>
                  <span className="cyber-hero-premium-value">{_fmtCurrency(p.premiumAnnual)}</span>
                </div>
                {renewLabel && <div className={'cyber-hero-renewal cyber-hero-renewal-' + renewClass}>{renewLabel}{p.renewalDate ? ' (' + new Date(p.renewalDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) + ')' : ''}</div>}
                {p.notes && <div className="cyber-hero-notes">{p.notes}</div>}
              </>
            : <div className="cyber-hero-empty">No carrier on file yet. Add carrier, policy number, premium, and renewal date in <strong>Admin → Integrations → Edit</strong> for this client to enable savings projections.</div>
          }
        </div>

        <div className="cyber-hero-impact">
          <div className="cyber-hero-eyebrow">Estimated premium impact</div>
          {data.impact && (data.impact.pctLow > 0 || data.impact.pctHigh > 0) ? (
            <>
              <div className="cyber-hero-impact-pct">
                <span>{data.impact.pctLow}<span className="muted">–</span>{data.impact.pctHigh}</span>
                <span className="cyber-hero-impact-unit">%</span>
              </div>
              <div className="cyber-hero-impact-label">reduction available based on your evidenced controls</div>
              {data.dollarSavings && (
                <div className="cyber-hero-impact-dollars">
                  ≈ {_fmtCurrency(data.dollarSavings.low)} – {_fmtCurrency(data.dollarSavings.high)} / year
                </div>
              )}
              {data.impact.cappedNote && <div className="cyber-hero-impact-note">{data.impact.cappedNote}</div>}
            </>
          ) : (
            <div className="cyber-hero-impact-empty">Open the CMMC tab to populate the underwriting checklist with live control state.</div>
          )}
          <div className="cyber-tally">
            <span className="cyber-tally-pill yes"><strong>{data.tally.yes || 0}</strong> Yes</span>
            <span className="cyber-tally-pill partial"><strong>{data.tally.partial || 0}</strong> Partial</span>
            <span className="cyber-tally-pill no"><strong>{data.tally.no || 0}</strong> No</span>
            <span className="cyber-tally-pill unknown"><strong>{data.tally.unknown || 0}</strong> Verify</span>
          </div>
        </div>
      </div>

      {/* Eligibility risk banner */}
      {data.eligibilityRisks && data.eligibilityRisks.length > 0 && (
        <div className="cyber-eligibility-risk">
          <div className="cyber-eligibility-risk-title">⚠ Eligibility risk — {data.eligibilityRisks.length} critical control{data.eligibilityRisks.length === 1 ? '' : 's'} unresolved</div>
          <div className="cyber-eligibility-risk-body">Carriers may decline or materially increase rates without these. Resolve before next renewal:</div>
          <ul>
            {data.eligibilityRisks.map(function(r) {
              return <li key={r.id}>{r.question}</li>;
            })}
          </ul>
        </div>
      )}

      {/* Filter */}
      <ContentBand
        title="Underwriting Checklist"
        subtitle="Consolidated questions across major carriers — auto-answered from live control state"
        rightContent={
          <div className="cyber-filter">
            <button className={'cyber-filter-btn' + (filter === 'all' ? ' active' : '')} onClick={function() { setFilter('all'); }}>All ({items.length})</button>
            <button className={'cyber-filter-btn' + (filter === 'gaps' ? ' active' : '')} onClick={function() { setFilter('gaps'); }}>Gaps ({(data.tally.no||0) + (data.tally.partial||0) + (data.tally.unknown||0)})</button>
            <button className={'cyber-filter-btn' + (filter === 'critical' ? ' active' : '')} onClick={function() { setFilter('critical'); }}>Critical ({items.filter(function(i) { return i.weight === 'critical'; }).length})</button>
          </div>
        }
      >
        <div className="cyber-item-list">
          {visibleItems.map(function(i) { return <CyberInsuranceItemRow key={i.id} item={i} />; })}
          {visibleItems.length === 0 && <div className="empty-state" style={{ padding: 24 }}>No items match the current filter.</div>}
        </div>
      </ContentBand>

      <div className="cyber-disclaimer">
        Premium-impact ranges are sourced from carrier-published reports (Coalition Active Insurance, Marsh State of the Cyber Market, At-Bay Tech-Risk Benchmark, 2023–2025). Actual savings depend on the specific carrier's appetite, your industry, and prior claims history. This dashboard is a guide for renewal conversations — not a binding quote.
      </div>
    </div>
  );
}

// ── CMMC live-evaluation tab ─────────────────────────────────────────
// Replaces the static demo CMMC bars. Pulls real control state from
// /api/clients/:id/cmmc which evaluates every L1/L2 control against
// Aurelius's data sources (NinjaOne Gov + Entra + KeeperGov). Atomus
// owns the SSP — this is the operations + service-delivery view.
var CMMC_STAGE_LABELS = {
  gap_assessment:  'Gap Assessment',
  remediation:     'Remediation',
  pre_assessment:  'Pre-Assessment',
  audit_scheduled: 'C3PAO Audit Scheduled',
  certified:       'Certified',
  maintenance:     'Maintenance'
};
var CMMC_STAGES_ORDER = ['gap_assessment','remediation','pre_assessment','audit_scheduled','certified','maintenance'];

var CMMC_FAMILY_NAMES = {
  AC: 'Access Control', AT: 'Awareness & Training', AU: 'Audit & Accountability',
  CM: 'Configuration Management', IA: 'Identification & Authentication',
  IR: 'Incident Response', MA: 'Maintenance', MP: 'Media Protection',
  PS: 'Personnel Security', PE: 'Physical Protection', RA: 'Risk Assessment',
  SA: 'Security Assessment', SC: 'System & Communications Protection',
  SI: 'System & Information Integrity'
};

var CMMC_SOURCE_LABELS = {
  entra:           'Microsoft Entra',
  ninjaone_gov:    'NinjaOne Gov',
  keepergov:       'KeeperGov',
  meraki:          'Cisco Meraki',
  aegis:           'Atomus Aegis',
  atomus:          'Atomus',
  endpoint_policy: 'Endpoint Policy',
  cbg_compliance:  'CBG',
  aurelius:        'Aurelius',
  manual:          'Manual'
};

// Tooltip copy for source chips — explains where Aurelius pulls evidence
// from for that control. Surfaces on hover so a non-CMMC-expert client
// can read what "Microsoft Entra" or "NinjaOne Gov" actually means in
// the context of their compliance posture.
var CMMC_SOURCE_TOOLTIPS = {
  entra:           'Evidence pulled from Microsoft Graph against this client\'s Entra ID tenant — users, conditional access policies, sign-in logs, device compliance.',
  ninjaone_gov:    'Evidence pulled from NinjaOne (FedRAMP / Gov tenant) — endpoint inventory, patch status, Defender state, agent health.',
  keepergov:       'Evidence pulled from KeeperGov (FIPS-validated password vault) — credential storage hygiene, privileged-access controls.',
  meraki:          'Evidence pulled from Cisco Meraki — network device posture, firmware, segmentation policies.',
  aegis:           'Evidence owned by Atomus Aegis — the endpoint agent enforces this control. Verify state in the Atomus dashboard.',
  atomus:          'Owned by the Atomus compliance team — see the SSP narrative for implementation details.',
  endpoint_policy: 'Endpoint policy enforced by CloudByte Group via NinjaOne Gov + Microsoft Defender. State verified in the RMM and Defender Security portal.',
  cbg_compliance:  'Owned by the CloudByte Group compliance team — see the SSP narrative for implementation details.',
  aurelius:        'Evidence comes from Aurelius\'s own data — personnel annotations, POA&M records, control-state log. Computed from your annotations + the system-of-record tables Aurelius keeps for this campaign.',
  manual:          'Manual attestation required — process or policy control evaluated by your compliance officer or CBG\'s GRC analyst, not by an automated check.'
};

// Tooltip copy for state chips — explains what each state actually means
// in plain-English audit language a non-CMMC-expert can act on.
var CMMC_STATE_TOOLTIPS = {
  pass:    'Evidenced — Aurelius pulled live data from a connected source and the control requirement is met. This evidence is what the assessor will see.',
  fail:    'Gap — Aurelius pulled live data and the control requirement is NOT met. This counts against your SPRS score and needs remediation.',
  unknown: 'Verify — Aurelius cannot evaluate this control automatically (no API field, manual attestation, or endpoint-side policy). Click "Fix this" for the remediation runbook.'
};

function CMMCStateChip({ state }) {
  var cls = state === 'pass' ? 'cmmc-state pass' : state === 'fail' ? 'cmmc-state fail' : state === 'inherited' ? 'cmmc-state inherited' : 'cmmc-state unknown';
  var label = state === 'pass' ? 'Evidenced' : state === 'fail' ? 'Gap' : state === 'inherited' ? 'Inherited' : 'Verify';
  var tip = CMMC_STATE_TOOLTIPS[state] || (state === 'inherited' ? 'Inherited from the CUI enclave — implemented via the enclave authorization, pending confirmation against its Customer Responsibility Matrix.' : '');
  return <span className={cls} title={tip}><span className="cmmc-state-dot" />{label}</span>;
}

function CMMCStageStrip({ stage, daysToAssessment }) {
  var idx = CMMC_STAGES_ORDER.indexOf(stage);
  return (
    <div className="cmmc-stage-strip">
      {CMMC_STAGES_ORDER.map(function(s, i) {
        var state = i < idx ? 'complete' : i === idx ? 'active' : 'upcoming';
        return (
          <div key={s} className={'cmmc-stage cmmc-stage-' + state}>
            <div className="cmmc-stage-marker">
              <span className="cmmc-stage-dot" />
              {i < CMMC_STAGES_ORDER.length - 1 && <span className="cmmc-stage-line" />}
            </div>
            <div className="cmmc-stage-label">{CMMC_STAGE_LABELS[s]}</div>
            {state === 'active' && daysToAssessment != null && i === CMMC_STAGES_ORDER.indexOf('audit_scheduled') && (
              <div className="cmmc-stage-meta">{daysToAssessment > 0 ? 'In ' + daysToAssessment + 'd' : daysToAssessment === 0 ? 'Today' : Math.abs(daysToAssessment) + 'd ago'}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Collapsible legend at the top of the controls list. Decodes the chip
// vocabulary ("1pt", "Gap", "Microsoft Entra") for non-CMMC-experts —
// clients, executives, prospective customers — so they can read the page
// without having to ask CBG what each pill means. Program-lead-aware:
// CBG-led / hybrid clients see CBG terminology; Atomus-led clients see
// Atomus terminology.
function CMMCControlLegend({ programLead }) {
  var [open, setOpen] = useState(false);
  var isCbgLed = programLead === 'cbg' || programLead === 'hybrid';
  // Source-list adapts to program lead — Atomus-led clients see Aegis/Atomus
  // entries; CBG-led clients see Endpoint Policy / CBG entries instead.
  var sources = isCbgLed
    ? [
        { key: 'entra',           label: 'Microsoft Entra',  desc: 'Live evidence from Microsoft Graph against your Entra ID tenant. Users, conditional access, sign-in logs, device compliance.' },
        { key: 'ninjaone_gov',    label: 'NinjaOne Gov',     desc: 'Live evidence from NinjaOne (FedRAMP / Gov tenant). Endpoint inventory, patch status, Defender state, agent health.' },
        { key: 'keepergov',       label: 'KeeperGov',        desc: 'Live evidence from KeeperGov (FIPS-validated password vault). Credential storage, privileged-access controls.' },
        { key: 'endpoint_policy', label: 'Endpoint Policy',  desc: 'Endpoint policy enforced by CloudByte Group via NinjaOne Gov + Microsoft Defender. Verify policy state in the RMM.' },
        { key: 'cbg_compliance',  label: 'CBG',              desc: 'Owned by CloudByte Group\'s compliance team. Implementation details live in your SSP narrative.' },
        { key: 'aurelius',        label: 'Aurelius',         desc: 'Computed from Aurelius\'s own tables — personnel annotations, POA&M records, control-state log. The platform IS the system of record for these.' },
        { key: 'manual',          label: 'Manual',           desc: 'Manual attestation — process or policy control evaluated by your compliance officer or CBG\'s GRC analyst, not by an automated check.' }
      ]
    : [
        { key: 'entra',           label: 'Microsoft Entra',  desc: 'Live evidence from Microsoft Graph against your Entra ID tenant. Users, conditional access, sign-in logs, device compliance.' },
        { key: 'ninjaone_gov',    label: 'NinjaOne Gov',     desc: 'Live evidence from NinjaOne (FedRAMP / Gov tenant). Endpoint inventory, patch status, Defender state, agent health.' },
        { key: 'keepergov',       label: 'KeeperGov',        desc: 'Live evidence from KeeperGov (FIPS-validated password vault). Credential storage, privileged-access controls.' },
        { key: 'aegis',           label: 'Atomus Aegis',     desc: 'Endpoint policy enforced by Atomus Aegis. Verify state in the Atomus dashboard.' },
        { key: 'atomus',          label: 'Atomus',           desc: 'Owned by the Atomus compliance team. Implementation details live in the SSP narrative Atomus authors.' },
        { key: 'aurelius',        label: 'Aurelius',         desc: 'Computed from Aurelius\'s own tables — personnel annotations, POA&M records, control-state log. The platform IS the system of record for these.' },
        { key: 'manual',          label: 'Manual',           desc: 'Manual attestation — process or policy control evaluated by your compliance officer, not by an automated check.' }
      ];
  return (
    <div className={'cmmc-legend' + (open ? ' open' : '')}>
      <button type="button" className="cmmc-legend-toggle" onClick={function() { setOpen(!open); }} aria-expanded={open}>
        <span className="cmmc-legend-icon" aria-hidden="true">💡</span>
        <span className="cmmc-legend-label">What do these labels mean?</span>
        <span className="cmmc-legend-chevron">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="cmmc-legend-body">
          <section className="cmmc-legend-section">
            <h4>Score weight</h4>
            <p className="cmmc-legend-prose">DoD's SPRS scoring methodology starts every assessment at <strong>+110</strong> and deducts each unimplemented control's weight. Aurelius shows the weight as a pill on every control row.</p>
            <ul className="cmmc-legend-list">
              <li><span className="cmmc-weight cmmc-weight-5">5 pts</span> <span>High-impact controls — absence puts the network at substantial risk (e.g. MFA, boundary protection, malicious code defenses).</span></li>
              <li><span className="cmmc-weight cmmc-weight-3">3 pts</span> <span>Standard-impact controls — the default weight class for most of the 110.</span></li>
              <li><span className="cmmc-weight cmmc-weight-1">1 pt</span> <span>Lower-impact controls — administrative or hygiene-tier requirements.</span></li>
            </ul>
          </section>
          <section className="cmmc-legend-section">
            <h4>Status</h4>
            <p className="cmmc-legend-prose">Every control resolves to one of three states. Aurelius never guesses — if it can't pull evidence, the state is <em>Verify</em>, not a fabricated pass.</p>
            <ul className="cmmc-legend-list">
              <li><span className="cmmc-state pass"><span className="cmmc-state-dot" />Evidenced</span> <span>Live data from a connected source confirms the control requirement. This evidence is what your assessor will see.</span></li>
              <li><span className="cmmc-state unknown"><span className="cmmc-state-dot" />Verify</span> <span>Aurelius cannot evaluate this control automatically — manual attestation, endpoint-side policy, or no API field. Click <strong>Fix this</strong> for the runbook.</span></li>
              <li><span className="cmmc-state fail"><span className="cmmc-state-dot" />Gap</span> <span>Live data shows the control requirement is NOT met. Counts against your SPRS score until remediated.</span></li>
            </ul>
          </section>
          <section className="cmmc-legend-section">
            <h4>Evidence source</h4>
            <p className="cmmc-legend-prose">The pill on the right of each control names where Aurelius pulls evidence from. Hover any pill on a control row to see the same explanation in context.</p>
            <ul className="cmmc-legend-list cmmc-legend-sources">
              {sources.map(function(s) {
                return (
                  <li key={s.key}>
                    <span className="cmmc-control-source cmmc-legend-source-pill">{s.label}</span>
                    <span>{s.desc}</span>
                  </li>
                );
              })}
            </ul>
          </section>
          <section className="cmmc-legend-section cmmc-legend-foot">
            <p>
              <strong>SPRS</strong> = Supplier Performance Risk System, the DoD scoring board your prime contractor reads when awarding work.
              {' '}
              <strong>SSP</strong> = System Security Plan, the auditor-facing document {isCbgLed ? 'CloudByte Group' : 'Atomus'} authors and submits.
              {' '}
              <strong>POA&amp;M</strong> = Plan of Action and Milestones, the working list of gaps and how they{"'"}ll be closed.
            </p>
          </section>
        </div>
      )}
    </div>
  );
}

function CMMCFamilyTile({ family, onClick, active }) {
  var pct = family.total > 0 ? Math.round((family.pass / family.total) * 100) : 0;
  var hasFail = family.fail > 0;
  var cls = 'cmmc-family-tile' + (active ? ' active' : '') + (hasFail ? ' has-gap' : '') + (family.unknown === family.total ? ' unverified' : '');
  return (
    <button type="button" className={cls} onClick={onClick}>
      <div className="cmmc-family-code">{family.code}</div>
      <div className="cmmc-family-name">{family.name}</div>
      <div className="cmmc-family-stats">
        <span className="cmmc-family-pct">{pct}%</span>
        <span className="cmmc-family-counts">
          <span className="ok">{family.pass}</span> /
          <span className="warn">{family.unknown}</span> /
          <span className="bad">{family.fail}</span>
        </span>
      </div>
      <div className="cmmc-family-bar"><div className="cmmc-family-bar-fill" style={{ width: pct + '%' }} /></div>
    </button>
  );
}

// Humanize an evidence-object key for display:
//   staleCount         → "Stale count"
//   encryptionPercent  → "Encryption percent"
//   compliancePercent  → "Compliance %"
//   directoryAuditEvents → "Directory audit events"
function _humanizeEvidenceKey(k) {
  if (!k) return '';
  // Special-case the percent suffix so we display % rather than the literal word
  var pct = /Percent$/i.test(k);
  var base = k.replace(/Percent$/i, '').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ').trim().toLowerCase();
  base = base.replace(/^./, function(c) { return c.toUpperCase(); });
  return pct ? base + ' %' : base;
}

// Render one evidence value in a friendly inline form. Recurses for nested
// objects; trims long arrays. Returns a React node.
function _renderEvidenceValue(key, value, depth) {
  depth = depth || 0;
  if (value === null || value === undefined) return <span className="muted">—</span>;
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="muted">none</span>;
    var trim = value.length > 5 ? value.slice(0, 5) : value;
    var nodes = trim.map(function(v, i) { return <span key={i} className="cmmc-evidence-array-item">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>; });
    if (value.length > 5) nodes.push(<span key="more" className="muted"> + {value.length - 5} more</span>);
    return <span className="cmmc-evidence-array">{nodes}</span>;
  }
  if (typeof value === 'boolean') return <span className={value ? 'ok' : 'muted'}>{value ? 'Yes' : 'No'}</span>;
  if (typeof value === 'number') {
    if (/percent$/i.test(key)) return <span><strong>{value}</strong>%</span>;
    return <strong style={{ color: 'var(--text-primary)' }}>{value.toLocaleString()}</strong>;
  }
  if (typeof value === 'object') {
    if (depth >= 2) return <code style={{ fontSize: 10 }}>{JSON.stringify(value)}</code>;
    var keys = Object.keys(value);
    return (
      <span className="cmmc-evidence-nested">
        {keys.map(function(nk, i) {
          return (
            <span key={nk} className="cmmc-evidence-nested-pair">
              <span className="cmmc-evidence-nested-key">{_humanizeEvidenceKey(nk)}:</span>
              <span className="cmmc-evidence-nested-val">{_renderEvidenceValue(nk, value[nk], depth + 1)}</span>
              {i < keys.length - 1 ? ', ' : ''}
            </span>
          );
        })}
      </span>
    );
  }
  // String
  return <span>{String(value)}</span>;
}

// Friendly evidence panel. Replaces the raw "Data lineage / Evidence: { ... }"
// dump with a structured presentation:
//   • Source line: "Source: Microsoft Entra · checked 5m ago"
//   • Status reason (if present)
//   • Structured evidence rows (humanized keys, formatted values)
//   • "Show technical detail" toggle revealing API trace + raw JSON for
//     engineers / forensic deep-dive.
function CMMCEvidencePanel({ ctl, sourceLabel, agoLabel }) {
  var [tech, setTech] = useState(false);
  var hasEvidence = ctl.evidence && typeof ctl.evidence === 'object' && Object.keys(ctl.evidence).length > 0;
  var hasTech = !!(ctl.lineage || hasEvidence);
  return (
    <div className="cmmc-evidence-panel">
      <div className="cmmc-evidence-source-line">
        <span className="muted">Source:</span>{' '}
        <strong>{sourceLabel}</strong>
        {agoLabel && agoLabel !== '—' && <span className="muted"> · checked {agoLabel}</span>}
      </div>
      {ctl.reason && (
        <div className={'cmmc-evidence-status cmmc-evidence-status-' + ctl.state}>{ctl.reason}</div>
      )}
      {hasEvidence && (
        <div className="cmmc-evidence-rows">
          {Object.keys(ctl.evidence).map(function(k) {
            return (
              <div key={k} className="cmmc-evidence-row">
                <span className="cmmc-evidence-key">{_humanizeEvidenceKey(k)}</span>
                <span className="cmmc-evidence-val">{_renderEvidenceValue(k, ctl.evidence[k])}</span>
              </div>
            );
          })}
        </div>
      )}
      {hasTech && (
        <div className="cmmc-evidence-tech-wrap">
          <button type="button" className="cmmc-evidence-tech-toggle" onClick={function() { setTech(!tech); }}>
            {tech ? '▾' : '▸'} {tech ? 'Hide technical detail' : 'Show technical detail'}
            <span className="cmmc-evidence-tech-hint">{tech ? '' : '— raw API trace + JSON for engineers'}</span>
          </button>
          {tech && (
            <div className="cmmc-evidence-tech">
              {ctl.lineage && (
                <div className="cmmc-evidence-tech-row">
                  <strong>API trace</strong>
                  <code>{ctl.lineage}</code>
                </div>
              )}
              {hasEvidence && (
                <div className="cmmc-evidence-tech-row">
                  <strong>Raw evidence</strong>
                  <pre>{JSON.stringify(ctl.evidence, null, 2)}</pre>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Per-control 800-171A assessment objectives. Each objective is graded
// Met / Not Met / N/A / Not Assessed; a control is Met only when all of its
// objectives are Met or N/A. This is the assessor-grade view — the automated
// state chip above is only a machine signal for a subset of these.
var CMMC_OBJ_STATUS_LABEL = { met: 'Met', not_met: 'Not Met', na: 'N/A', not_assessed: 'Not assessed' };
function CMMCObjectives({ ctl, clientId, onGraded }) {
  var [objs, setObjs] = useState(ctl.objectives || []);
  var [busy, setBusy] = useState(null);
  var auditorView = useAuditorView();
  var canEdit = isAdminRole() && !IS_CLIENT_VIEW && !auditorView;
  useEffect(function() { setObjs(ctl.objectives || []); }, [ctl]); // resync when the parent refetches /cmmc
  if (!objs.length) return null;
  var summary = { met: 0, not_met: 0, na: 0, not_assessed: 0 };
  objs.forEach(function(o) { summary[o.status] = (summary[o.status] || 0) + 1; });
  function setStatus(o, status) {
    if (!canEdit || o.status === status) return;
    setBusy(o.label);
    setObjs(function(list) { return list.map(function(x) { return x.label === o.label ? Object.assign({}, x, { status: status }) : x; }); });
    authFetch(API + '/clients/' + clientId + '/cmmc/objectives/' + encodeURIComponent(o.label), {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: status })
    }).then(function(r) { return r.ok ? r.json() : r.json().then(function(e) { throw new Error(e.error || 'Failed'); }); })
      .then(function() { setBusy(null); if (onGraded) onGraded(); }) // reconcile control chip + hero SPRS
      .catch(function(e) { setBusy(null); setObjs(function(list) { return list.map(function(x) { return x.label === o.label ? Object.assign({}, x, { status: o.status }) : x; }); }); window.alert('Could not save: ' + e.message); });
  }
  var STATUSES = [['met', 'Met'], ['not_met', 'Not Met'], ['na', 'N/A'], ['not_assessed', '—']];
  return (
    <div className="cmmc-objectives">
      <div className="cmmc-objectives-head">
        <span className="cmmc-objectives-title">Assessment objectives · NIST SP 800-171A</span>
        <span className="cmmc-objectives-summary">{summary.met}/{objs.length} met{summary.not_met ? ' · ' + summary.not_met + ' not met' : ''}{summary.not_assessed ? ' · ' + summary.not_assessed + ' not assessed' : ''}</span>
      </div>
      {objs.map(function(o) {
        return (
          <div key={o.label} className={'cmmc-objective-row cmmc-objective-' + o.status}>
            <span className="cmmc-objective-label">[{o.id}]</span>
            <span className="cmmc-objective-text">{o.text}</span>
            {o.source && o.source.indexOf('check:') === 0 && o.service && (
              <span className="cmmc-obj-src cmmc-obj-src-auto" title={'Auto-graded from ' + o.service + ' telemetry. You can override with a manual determination.'}>{o.service}</span>
            )}
            {o.source === 'manual' && (
              <span className="cmmc-obj-src cmmc-obj-src-manual" title="Manually attested by your team.">Attested</span>
            )}
            {!o.source && (
              <span className="cmmc-obj-src cmmc-obj-src-none" title="No automated evidence covers this determination. Grade it and upload evidence.">Needs evidence</span>
            )}
            {canEdit ? (
              <span className="cmmc-objective-set">
                {STATUSES.map(function(s) { return <button key={s[0]} type="button" className={'cmmc-obj-btn cmmc-obj-btn-' + s[0] + (o.status === s[0] ? ' active' : '')} disabled={busy === o.label} title={CMMC_OBJ_STATUS_LABEL[s[0]]} onClick={function() { setStatus(o, s[0]); }}>{s[1]}</button>; })}
              </span>
            ) : <span className={'cmmc-obj-badge cmmc-obj-badge-' + o.status}>{CMMC_OBJ_STATUS_LABEL[o.status]}</span>}
          </div>
        );
      })}
    </div>
  );
}

function CMMCControlRow({ ctl, clientId, onRemediated, programLead }) {
  var [open, setOpen] = useState(false);
  var [remOpen, setRemOpen] = useState(false);
  var auditorView = useAuditorView();
  var src = CMMC_SOURCE_LABELS[ctl.evidence_source] || ctl.evidence_source;
  var srcTooltip = CMMC_SOURCE_TOOLTIPS[ctl.evidence_source] || ('Evidence source: ' + (src || 'unknown'));
  var lastChecked = ctl.lastChecked ? new Date(ctl.lastChecked) : null;
  var ago = lastChecked ? Math.round((Date.now() - lastChecked.getTime()) / 60000) : null;
  var agoLabel = ago == null ? '—' : ago < 1 ? 'just now' : ago < 60 ? ago + 'm ago' : ago < 1440 ? Math.round(ago/60) + 'h ago' : Math.round(ago/1440) + 'd ago';
  // In auditor view, hide the Fix-this affordance — the assessor reads
  // evidence; they don't trigger remediations. Permission also requires
  // admin role (catches client-role users on the main / URL).
  var canRemediate = isAdminRole() && !IS_CLIENT_VIEW && !auditorView && (ctl.state === 'fail' || ctl.state === 'unknown');
  // Score-weight tooltip — explains the SPRS impact in plain English.
  var weightTooltip = ctl.score_weight === 5
    ? 'High-impact control (5 pts). DoD SPRS methodology deducts -5 from your score for each unimplemented 5-pt control. Absence puts the network at substantial risk.'
    : ctl.score_weight === 1
      ? 'Lower-impact control (1 pt). DoD SPRS methodology deducts -1 from your score for each unimplemented 1-pt control.'
      : 'Standard-impact control (3 pts). DoD SPRS methodology deducts -3 from your score for each unimplemented 3-pt control.';
  // Verify-link is program-lead-aware: Atomus-led → Aegis dashboard, CBG-led → NinjaOne Gov + Defender.
  var isCbgLed = programLead === 'cbg' || programLead === 'hybrid';
  var verifyLinkLabel = isCbgLed
    ? '→ Verify in NinjaOne Gov / Defender'
    : '→ Verify in Atomus Aegis';
  var showVerifyLink = (ctl.evidence_source === 'aegis' || ctl.evidence_source === 'endpoint_policy');
  return (
    <div className={'cmmc-control cmmc-control-' + ctl.state}>
      <button type="button" className="cmmc-control-head" onClick={function() { setOpen(!open); }}>
        <span className="cmmc-control-id">{ctl.id}</span>
        <span className="cmmc-control-title">{ctl.title}</span>
        {ctl.score_weight != null && <span className={'cmmc-weight cmmc-weight-' + ctl.score_weight} title={weightTooltip}>{ctl.score_weight} pt{ctl.score_weight === 1 ? '' : 's'}</span>}
        <CMMCStateChip state={ctl.state} />
        {ctl.enclave && ctl.enclave.responsibility === 'shared' && <span className="cmmc-enclave-tag shared" title={'Shared responsibility with ' + (ctl.enclave.label || 'the enclave') + ' — the enclave covers part; your tenant still owns a part.'}>Shared · {ctl.enclave.label || 'Enclave'}</span>}
        {ctl.objectiveSummary && ctl.objectiveSummary.total > 0 && <span className={'cmmc-assess-chip cmmc-assess-' + (ctl.assessmentStatus || 'not_assessed')} title={'800-171A assessment: ' + ctl.objectiveSummary.met + ' of ' + ctl.objectiveSummary.total + ' objectives met'}>{ctl.objectiveSummary.met}/{ctl.objectiveSummary.total}</span>}
        <span className="cmmc-control-source" title={srcTooltip}>{src}</span>
        <span className="cmmc-control-toggle">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="cmmc-control-body">
          <div className="cmmc-control-statement">{ctl.statement}</div>
          {/* Friendly evidence panel — replaces the raw lineage / JSON dump
              with a source line, structured rows, and a collapsed
              "technical detail" section for engineers / forensics. */}
          <CMMCEvidencePanel ctl={ctl} sourceLabel={src} agoLabel={agoLabel} />
          {ctl.enclave && (ctl.enclave.exostarAction || ctl.enclave.customerAction) && (
            <div className={'cmmc-enclave-resp ' + ctl.enclave.responsibility}>
              <div className="cmmc-enclave-resp-h">
                {ctl.enclave.responsibility === 'inherited' ? 'Inherited from ' + (ctl.enclave.label || 'enclave') : 'Shared responsibility · ' + (ctl.enclave.label || 'enclave')}
                <span className="src">{ctl.enclave.label || 'Enclave'} SRM</span>
              </div>
              {ctl.enclave.exostarAction && <div className="cmmc-enclave-resp-row"><span className="who">{ctl.enclave.label || 'Enclave'} provides</span><span className="what">{ctl.enclave.exostarAction}</span></div>}
              {ctl.enclave.customerAction && <div className="cmmc-enclave-resp-row"><span className="who">You provide</span><span className="what">{ctl.enclave.customerAction}</span></div>}
            </div>
          )}
          <CMMCObjectives ctl={ctl} clientId={clientId} onGraded={onRemediated} />
          {/* Recent activity strip — pulls audit_log + drift_log entries scoped to
              this control. Gives the operator (and assessor) a per-control thread
              connecting state changes ← remediation runs ← POA&M lifecycle. */}
          <CMMCControlHistoryStrip clientId={clientId} controlId={ctl.id} />
          <div className="cmmc-control-foot">
            {ctl.rev3_id && <span className="cmmc-control-rev3" title="NIST 800-171 Rev 3 cross-reference (released May 2024). CMMC certification still uses Rev 2 IDs but the assessor catalog increasingly references Rev 3.">Rev 3 ref: {ctl.rev3_id}</span>}
            {showVerifyLink && <span className="cmmc-aegis-link" title={isCbgLed ? 'Endpoint policy state is enforced by NinjaOne Gov + Microsoft Defender. Verify the policy in the RMM and Defender Security portal.' : 'Endpoint policy state is enforced by Atomus Aegis. Verify in the Aegis dashboard.'}>{verifyLinkLabel}</span>}
            {canRemediate && (
              <button type="button" className="cmmc-fix-btn" onClick={function(e) { e.stopPropagation(); setRemOpen(true); }}>
                <span aria-hidden="true">⚡</span> Fix this
              </button>
            )}
          </div>
        </div>
      )}
      {remOpen && (
        <CMMCRemediationDrawer
          clientId={clientId}
          control={ctl}
          onClose={function() { setRemOpen(false); }}
          onRemediated={function() {
            setRemOpen(false);
            if (onRemediated) onRemediated(ctl.id);
          }}
        />
      )}
    </div>
  );
}

// Slide-out drawer with the remediation plan + execution affordances. Three modes
// surface here based on the plan's automation level:
//   • auto / approval_required → "Run remediation" button + per-target list
//   • manual_with_script       → step-by-step + copyable PowerShell
//   • manual                   → step-by-step prose, no script
// Universal "Open NinjaOne ticket" fallback at the bottom — admins can always
// delegate the work via a ticket, even for fully-automatable plans.
function CMMCRemediationDrawer({ clientId, control, onClose, onRemediated }) {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [executing, setExecuting] = useState(false);
  var [result, setResult] = useState(null);
  var [error, setError] = useState(null);
  var [copiedScriptIdx, setCopiedScriptIdx] = useState(null);
  var auditorView = useAuditorView();

  useEffect(function() {
    setLoading(true); setError(null); setResult(null);
    authFetch(API + '/clients/' + clientId + '/cmmc/remediations/' + control.id)
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setLoading(false);
        if (x.ok) setData(x.d);
        else setError(x.d.error || 'Failed to load remediation');
      })
      .catch(function() { setLoading(false); setError('Failed to load remediation'); });
  }, [clientId, control.id]);

  function copyScript(text, idx) {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(text).then(function() {
      setCopiedScriptIdx(idx);
      setTimeout(function() { setCopiedScriptIdx(null); }, 1500);
    });
  }

  function execute(plan, opts) {
    opts = opts || {};
    var isCritical = plan.severity === 'critical';
    var prompt;
    if (isCritical && !opts.approval_id) {
      prompt = 'Critical-severity remediation: ' + plan.title + '\n\n' +
               'Aurelius requires a SECOND admin to approve before this can run. ' +
               'Submit a request for review?';
    } else if (plan.automation === 'approval_required') {
      prompt = 'Confirm execution: ' + plan.title + '\n\nThis will run write operations against the client tenant. Continue?';
    }
    if (prompt && !window.confirm(prompt)) return;
    setExecuting(true); setError(null);
    var body = { confirm: true };
    if (opts.approval_id) body.approval_id = opts.approval_id;
    authFetch(API + '/clients/' + clientId + '/cmmc/remediations/' + plan.id + '/execute', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setExecuting(false);
        if (x.ok) {
          setResult(x.d);
          if (x.d.mode === 'executed' && onRemediated) {
            // Refresh parent after a short delay so the user sees the result first
            setTimeout(onRemediated, 1500);
          }
        } else {
          setError(x.d.error || 'Execution failed');
        }
      })
      .catch(function() { setExecuting(false); setError('Execution failed'); });
  }

  function openTicket(plan) {
    setExecuting(true); setError(null);
    authFetch(API + '/clients/' + clientId + '/cmmc/remediations/' + plan.id + '/ticket', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({})
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setExecuting(false);
        if (x.ok) setResult(x.d);
        else setError(x.d.error || 'Ticket creation failed');
      });
  }

  // Promote the failing control to a tracked POA&M record. Auto-fills weakness
  // title, description, severity, and remediation plan from the catalog +
  // current control state. Idempotent — if an open POA&M already exists for
  // this control, returns it rather than creating a duplicate.
  function promoteToPoam(plan) {
    setExecuting(true); setError(null);
    authFetch(API + '/clients/' + clientId + '/cmmc/poam/promote', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ control_id: plan.control_id })
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setExecuting(false);
        if (x.ok) {
          var msg = x.d.created ? 'POA&M created: ' + (x.d.item && x.d.item.poam_number) : 'Existing open POA&M: ' + (x.d.item && x.d.item.poam_number);
          setResult({ mode: 'poam_promoted', message: msg, item: x.d.item });
        } else {
          setError(x.d.error || 'POA&M promotion failed');
        }
      });
  }

  return (
    <>
      <div className="coverage-drawer-overlay" onClick={onClose} />
      <aside className="coverage-drawer" role="dialog" aria-label="Remediation Plan">
        <div className="coverage-drawer-head">
          <div>
            <div className="cmmc-rem-eyebrow">Remediation · NIST {control.id}</div>
            <div className="coverage-drawer-title">{control.title}</div>
            <div className="coverage-drawer-sub">{control.statement}</div>
          </div>
          <button type="button" className="coverage-drawer-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="coverage-drawer-body cmmc-rem-body">
          {loading && <div className="empty-state" style={{ padding: 24 }}>Loading remediation plan…</div>}
          {error && <div className="admin-banner error">{error}</div>}
          {data && data.plans && data.plans.length === 0 && (
            <div className="empty-state" style={{ padding: 24 }}>
              {data.message || 'No structured remediation plan defined for this control yet.'}
            </div>
          )}
          {data && data.plans && data.plans.map(function(plan) {
            var autoLabel = { auto: 'Aurelius can execute', approval_required: 'Aurelius can execute (admin approval)', manual_with_script: 'Manual — script provided', manual: 'Manual — process control' }[plan.automation] || plan.automation;
            var sevColor = { critical: '#ef4444', high: '#f59e0b', medium: '#60a5fa', low: 'var(--text-muted)' }[plan.severity] || 'var(--text-muted)';
            return (
              <div key={plan.id} className={'cmmc-rem-plan cmmc-rem-' + plan.automation}>
                <div className="cmmc-rem-plan-head">
                  <span className="cmmc-rem-severity" style={{ color: sevColor, borderColor: sevColor + '60' }}>{plan.severity}</span>
                  <span className="cmmc-rem-automation">{autoLabel}</span>
                </div>
                <div className="cmmc-rem-title">{plan.title}</div>
                <div className="cmmc-rem-desc">{plan.description}</div>

                {plan.diagnosis && plan.diagnosis.affected_objects && plan.diagnosis.affected_objects.length > 0 && (
                  <div className="cmmc-rem-affected">
                    <div className="cmmc-rem-section-label">Affected ({plan.diagnosis.affected_objects.length})</div>
                    {plan.diagnosis.affected_objects.map(function(obj, i) {
                      return (
                        <div key={i} className="cmmc-rem-affected-row">
                          <span className={'cmmc-rem-obj-kind cmmc-rem-obj-' + obj.kind}>{obj.kind}</span>
                          <span className="cmmc-rem-obj-label">{obj.label}</span>
                        </div>
                      );
                    })}
                    {plan.diagnosis.context && <div className="cmmc-rem-context">{plan.diagnosis.context}</div>}
                  </div>
                )}

                <div className="cmmc-rem-steps">
                  <div className="cmmc-rem-section-label">Runbook</div>
                  {plan.steps.map(function(s, i) {
                    return (
                      <div key={i} className="cmmc-rem-step">
                        <div className="cmmc-rem-step-head">
                          <span className="cmmc-rem-step-num">{i + 1}</span>
                          <span className={'cmmc-rem-step-kind cmmc-rem-kind-' + s.kind}>{s.kind.replace('_', ' ')}</span>
                          <span className="cmmc-rem-step-desc">{s.description}</span>
                        </div>
                        {s.ps_script && (
                          <div className="cmmc-rem-script">
                            <div className="cmmc-rem-script-head">
                              <span>PowerShell</span>
                              <button type="button" className="cmmc-rem-copy-btn" onClick={function() { copyScript(s.ps_script, plan.id + '-' + i); }}>
                                {copiedScriptIdx === plan.id + '-' + i ? 'Copied ✓' : 'Copy'}
                              </button>
                            </div>
                            <pre>{s.ps_script}</pre>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {plan.references && plan.references.length > 0 && (
                  <div className="cmmc-rem-refs">
                    <div className="cmmc-rem-section-label">References</div>
                    {plan.references.map(function(r, i) {
                      return <a key={i} href={r.url} target="_blank" rel="noopener noreferrer" className="cmmc-rem-ref">{r.label} ↗</a>;
                    })}
                  </div>
                )}

                <div className="cmmc-rem-actions">
                  {auditorView ? (
                    <div className="cmmc-rem-auditor-note">
                      <span aria-hidden="true">👁</span>
                      Read-only — exit auditor view to run remediations, open tickets, or promote to POA&amp;M.
                    </div>
                  ) : (
                    <>
                      {(plan.automation === 'auto' || plan.automation === 'approval_required') && (
                        <button type="button" className="admin-btn primary" onClick={function() { execute(plan); }} disabled={executing}>
                          {executing ? 'Running…' : (plan.automation === 'approval_required' ? '⚡ Run with admin approval' : '⚡ Run remediation')}
                        </button>
                      )}
                      {(plan.automation === 'manual' || plan.automation === 'manual_with_script') && (
                        <button type="button" className="admin-btn primary" onClick={function() { execute(plan); }} disabled={executing}>
                          Mark as viewed (audit log)
                        </button>
                      )}
                      <button type="button" className="admin-btn" onClick={function() { promoteToPoam(plan); }} disabled={executing}>
                        Promote to POA&amp;M
                      </button>
                      <button type="button" className="admin-btn" onClick={function() { openTicket(plan); }} disabled={executing}>
                        Open NinjaOne ticket
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}

          {result && (
            <div className={'cmmc-rem-result ' + (result.mode === 'executed' ? 'success' : result.mode === 'awaiting_approval' ? 'warn' : 'info')}>
              <div className="cmmc-rem-result-title">
                {result.mode === 'executed' ? '✓ Executed'
                  : result.mode === 'awaiting_approval' ? '⏳ Awaiting second-admin approval'
                  : result.mode === 'ticket_draft' ? 'Ticket draft'
                  : result.mode === 'runbook_only' ? 'Runbook acknowledged'
                  : 'Result'}
              </div>
              <div className="cmmc-rem-result-msg">{result.message}</div>
              {result.mode === 'awaiting_approval' && result.execution_id && (
                <div className="cmmc-rem-result-detail" style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Request ID #{result.execution_id} — visible to other admins under <strong>Pending Approvals</strong> at the top of the CMMC tab. Once approved, return here to run the operation.</div>
                </div>
              )}
              {result.executed && result.executed.length > 0 && (
                <div className="cmmc-rem-result-detail">
                  <strong>Executed against:</strong>
                  <ul>{result.executed.map(function(x, i) { return <li key={i}>{typeof x === 'string' ? x : JSON.stringify(x)}</li>; })}</ul>
                </div>
              )}
              {result.failed && result.failed.length > 0 && (
                <div className="cmmc-rem-result-detail" style={{ color: 'var(--accent-red)' }}>
                  <strong>Failed:</strong>
                  <ul>{result.failed.map(function(x, i) { return <li key={i}>{x.upn || x.id}: {x.error}</li>; })}</ul>
                </div>
              )}
              {/* Closure-of-loop verify panel — Aurelius re-ran the control's
                  verify check immediately after the remediation. Surfaces the
                  before/after state so the operator (and assessor) sees the
                  loop closed in one place. */}
              {result.verify && result.verify.verified && (
                <div className={'cmmc-rem-verify cmmc-rem-verify-' + (result.verify.transitioned && result.verify.new_state === 'pass' ? 'pass' : result.verify.transitioned && result.verify.new_state === 'fail' ? 'fail' : 'unchanged')}>
                  <div className="cmmc-rem-verify-title">
                    {result.verify.transitioned && result.verify.new_state === 'pass'
                      ? '✓ Re-verified — control now passes'
                      : result.verify.transitioned && result.verify.new_state === 'fail'
                        ? '⚠ Re-verified — still failing'
                        : '⏱ Re-verified — no state change observed yet'}
                  </div>
                  <div className="cmmc-rem-verify-detail">
                    <span className="cmmc-rem-verify-transition">
                      <span className={'cmmc-rem-verify-state ' + (result.verify.prev_state || 'unknown')}>{result.verify.prev_state || 'unrecorded'}</span>
                      <span className="cmmc-rem-verify-arrow">→</span>
                      <span className={'cmmc-rem-verify-state ' + result.verify.new_state}>{result.verify.new_state}</span>
                    </span>
                    {result.verify.reason && <span className="cmmc-rem-verify-reason">{result.verify.reason}</span>}
                  </div>
                  {!result.verify.transitioned && (
                    <div className="cmmc-rem-verify-hint">Upstream system (e.g. Microsoft Graph) may take a few seconds to surface the change. The drift log records the attempt; refresh the controls list in a moment to see the updated state.</div>
                  )}
                </div>
              )}
              {result.verify && !result.verify.verified && result.verify.error && (
                <div className="cmmc-rem-verify cmmc-rem-verify-error">
                  <div className="cmmc-rem-verify-title">⚠ Re-verification couldn't run</div>
                  <div className="cmmc-rem-verify-detail">{result.verify.error}</div>
                </div>
              )}
              {result.ticket && (
                <div className="cmmc-rem-result-detail">
                  <div className="cmmc-rem-section-label" style={{ marginTop: 8 }}>Ticket draft (paste into NinjaOne Gov)</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Target org: <code>{result.ticket.target_org}</code> · Subject: <code>{result.ticket.subject}</code> · Priority: {result.ticket.priority}</div>
                  <pre style={{ maxHeight: 200, overflow: 'auto' }}>{result.ticket.body}</pre>
                </div>
              )}
            </div>
          )}
        </div>
      </aside>
    </>
  );
}

// POA&M lifecycle section — first-class records for tracked weaknesses.
// Lists open / in_progress / deferred / closed items with status pills,
// target dates, and overdue indicators. Admin can edit, transition status,
// or create new items manually for findings outside Aurelius's automated
// evaluation (penetration tests, audit observations).
function CMMCPoamSection({ clientId }) {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [editing, setEditing] = useState(null);
  var [filter, setFilter] = useState('active');     // active | all | closed
  var [toast, setToast] = useState(null);
  var auditorView = useAuditorView();
  // In auditor view, drop edit / transition affordances — assessor reads
  // POA&M state, doesn't mutate it.
  var isAdmin = isAdminRole() && !IS_CLIENT_VIEW && !auditorView;

  function showToast(m, t) { setToast({ msg: m, type: t || 'info' }); setTimeout(function() { setToast(null); }, 3500); }

  var [generating, setGenerating] = useState(false);

  function fetchData() {
    setLoading(true);
    authFetch(API + '/clients/' + clientId + '/cmmc/poam')
      .then(function(r) { return r.ok ? r.json() : { items: [], summary: {} }; })
      .then(function(d) { setData(d); setLoading(false); })
      .catch(function() { setLoading(false); });
  }
  useEffect(fetchData, [clientId]);

  // Auto-generate POA&M items from every control currently assessed Not Met
  // that lacks an open item. Idempotent on the server (skips ones already
  // tracked), so this is safe to click repeatedly.
  function generatePoam() {
    setGenerating(true);
    authFetch(API + '/clients/' + clientId + '/cmmc/poam/generate', { method: 'POST' })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setGenerating(false);
        if (x.ok) {
          var msg = x.d.created > 0
            ? 'Generated ' + x.d.created + ' POA&M item' + (x.d.created === 1 ? '' : 's') + ' from open findings' + (x.d.skipped ? ' · ' + x.d.skipped + ' already tracked' : '')
            : (x.d.findings === 0 ? 'No not-met findings to generate from' : 'All ' + x.d.skipped + ' finding(s) already have open POA&M items');
          showToast(msg, x.d.created > 0 ? 'ok' : 'info');
          if (x.d.created > 0) fetchData();
        } else { showToast(x.d.error || 'Generation failed', 'error'); }
      })
      .catch(function() { setGenerating(false); showToast('Generation failed', 'error'); });
  }

  function transitionPoam(item, status) {
    var body = { status: status };
    if (status === 'closed') {
      var ev = window.prompt('Closure evidence — describe how this gap was remediated and where the evidence lives (link, ticket, file, etc.):');
      if (!ev) return;
      body.closure_evidence = ev;
    } else if (status === 'deferred') {
      var reason = window.prompt('Deferral rationale — why is this not being remediated this cycle?');
      if (!reason) return;
      body.deferred_reason = reason;
      var until = window.prompt('Revisit date (YYYY-MM-DD), optional:');
      if (until && /^\d{4}-\d{2}-\d{2}$/.test(until)) body.deferred_until = until;
    }
    authFetch(API + '/clients/' + clientId + '/cmmc/poam/' + item.id + '/transition', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        if (x.ok) { showToast(item.poam_number + ' → ' + status, 'ok'); fetchData(); }
        else { showToast(x.d.error || 'Transition failed', 'error'); }
      });
  }

  if (loading) return null;
  if (!data) return null;

  var items = data.items || [];
  var visibleItems = filter === 'active' ? items.filter(function(i) { return i.status === 'open' || i.status === 'in_progress'; })
                    : filter === 'closed' ? items.filter(function(i) { return i.status === 'closed'; })
                    : items;
  var summary = data.summary || {};

  function sevCls(s) { return s === 'critical' ? 'crit' : s === 'high' ? 'high' : s === 'medium' ? 'med' : 'low'; }
  function statusTone(s) { return s === 'open' ? 'warn' : s === 'in_progress' ? 'info' : s === 'closed' ? 'ok' : 'mut'; }
  return (
    <>
      <div className="cmmcw-vhead">
        <div>
          <div className="cmmcw-eye">Assessment</div>
          <h2 className="cmmcw-h1">Plan of Action &amp; Milestones</h2>
          <p className="cmmcw-sub">Auto-generated from every control assessed Not Met, pre-filled from its failing 800-171A objectives. Each item flows to a Work task and closes when its change item completes.</p>
        </div>
        <div className="cmmcw-acts">
          {isAdmin && <button type="button" className="cmmcw-btn" disabled={generating} onClick={generatePoam} title="Create POA&M items for every control assessed Not Met that isn't already tracked.">{generating ? 'Generating…' : 'Generate from findings'}</button>}
          {isAdmin && <button type="button" className="cmmcw-btn cmmcw-btn-pri" onClick={function() { setEditing({}); }}>+ New POA&amp;M</button>}
        </div>
      </div>
      <div className="cmmcw-stats">
        <div className="cmmcw-stat"><div className="h"><span>Open items</span></div><div className="n warn">{summary.open || 0}</div><div className="s">awaiting work</div></div>
        <div className="cmmcw-stat"><div className="h"><span>In progress</span></div><div className="n info">{summary.in_progress || 0}</div><div className="s">being remediated</div></div>
        <div className="cmmcw-stat"><div className="h"><span>Past target</span></div><div className={'n' + (summary.overdue > 0 ? ' bad' : '')}>{summary.overdue || 0}</div><div className="s">overdue</div></div>
        <div className="cmmcw-stat"><div className="h"><span>Closed</span></div><div className="n ok">{summary.closed || 0}</div><div className="s">remediated</div></div>
      </div>
      <div className="cmmcw-tbar">
        <div className="cmmcw-segs">
          {['active', 'all', 'closed'].map(function(f) {
            var count = f === 'active' ? ((summary.open || 0) + (summary.in_progress || 0)) : f === 'closed' ? (summary.closed || 0) : items.length;
            return <button key={f} type="button" className={'cmmcw-seg' + (filter === f ? ' on' : '')} onClick={function() { setFilter(f); }}>{f === 'active' ? 'Active' : f === 'all' ? 'All' : 'Closed'} <span className="c">{count}</span></button>;
          })}
        </div>
      </div>
      {visibleItems.length === 0 ? (
        <div className="cmmcw-empty">
          {items.length === 0
            ? (isAdmin ? 'No POA&M items yet. Click "Generate from findings" to open one for every control assessed Not Met, promote a single control from Fix-this, or add one manually.' : 'No POA&M items on file.')
            : 'No items match this filter.'}
        </div>
      ) : (
        <div className="cmmcw-card"><div className="cmmcw-twrap"><table className="cmmcw-table">
          <thead><tr><th>ID</th><th>Control</th><th>Weakness</th><th>Severity</th><th>Status</th><th>Target</th>{isAdmin && <th style={{ textAlign: 'right' }}>Actions</th>}</tr></thead>
          <tbody>
            {visibleItems.map(function(p) {
              var overdue = (p.status === 'open' || p.status === 'in_progress') && p.days_to_target != null && p.days_to_target < 0;
              var targetLabel = p.target_date ? (overdue ? Math.abs(p.days_to_target) + 'd overdue' : (p.days_to_target === 0 ? 'due today' : 'in ' + p.days_to_target + 'd')) : '';
              return (
                <tr key={p.id}>
                  <td className="mono" style={{ color: 'var(--cw-acc)', whiteSpace: 'nowrap' }}>{p.poam_number}</td>
                  <td>{p.control_id ? <span className="cmmcw-chip">{p.control_id}</span> : '—'}</td>
                  <td><span className="cmmcw-lead">{p.weakness_title}</span>{p.weakness_desc && <span className="cmmcw-cellsub">{p.weakness_desc}</span>}</td>
                  <td><span className={'cmmcw-sev ' + sevCls(p.severity)}><i />{p.severity}</span></td>
                  <td><span className={'cmmcw-dot ' + statusTone(p.status)}>{p.status.replace('_', ' ')}</span></td>
                  <td className="mono" style={{ whiteSpace: 'nowrap' }}>{p.target_date ? new Date(p.target_date).toLocaleDateString() : '—'}{p.target_date && <span className={'cmmcw-cellsub' + (overdue ? ' bad' : '')}>{targetLabel}</span>}</td>
                  {isAdmin && (
                    <td><div className="cmmcw-rowacts">
                      <button type="button" className="cmmcw-rowbtn" onClick={function() { setEditing(p); }}>Edit</button>
                      {p.status === 'open' && <button type="button" className="cmmcw-rowbtn" onClick={function() { transitionPoam(p, 'in_progress'); }}>Start</button>}
                      {p.status !== 'closed' && <button type="button" className="cmmcw-rowbtn" onClick={function() { transitionPoam(p, 'closed'); }}>Close</button>}
                      {(p.status === 'open' || p.status === 'in_progress') && <button type="button" className="cmmcw-rowbtn" onClick={function() { transitionPoam(p, 'deferred'); }}>Defer</button>}
                      {(p.status === 'closed' || p.status === 'deferred') && <button type="button" className="cmmcw-rowbtn" onClick={function() { transitionPoam(p, 'open'); }}>Reopen</button>}
                    </div></td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table></div></div>
      )}
      {editing && (
        <CMMCPoamEditor
          clientId={clientId}
          item={editing}
          onClose={function() { setEditing(null); }}
          onSaved={function(msg) { setEditing(null); fetchData(); showToast(msg || 'Saved', 'ok'); }}
          onError={function(msg) { showToast(msg || 'Save failed', 'error'); }}
        />
      )}
      {toast && <div className={'admin-toast ' + (toast.type || 'info')}>{toast.msg}</div>}
    </>
  );
}

function CMMCPoamEditor({ clientId, item, onClose, onSaved, onError }) {
  var isNew = !item || !item.id;
  var [form, setForm] = useState({
    weakness_title:  (item && item.weakness_title)  || '',
    weakness_desc:   (item && item.weakness_desc)   || '',
    severity:        (item && item.severity)        || 'medium',
    status:          (item && item.status)          || 'open',
    owner_name:      (item && item.owner_name)      || '',
    owner_email:     (item && item.owner_email)     || '',
    target_date:     item && item.target_date ? String(item.target_date).slice(0, 10) : '',
    remediation_plan: (item && item.remediation_plan) || '',
    notes:           (item && item.notes)           || '',
    control_id:      (item && item.control_id)      || ''
  });
  var [saving, setSaving] = useState(false);

  function setField(k, v) { setForm(function(prev) { var n = Object.assign({}, prev); n[k] = v; return n; }); }

  function save() {
    if (!form.weakness_title.trim()) { onError('Weakness title is required'); return; }
    setSaving(true);
    var url = isNew ? API + '/clients/' + clientId + '/cmmc/poam' : API + '/clients/' + clientId + '/cmmc/poam/' + item.id;
    var method = isNew ? 'POST' : 'PUT';
    authFetch(url, { method: method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) { setSaving(false); if (x.ok) onSaved(isNew ? 'POA&M created' : 'Saved'); else onError(x.d.error || 'Save failed'); });
  }

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={function(e) { e.stopPropagation(); }} style={{ maxWidth: 640 }}>
        <div className="admin-modal-header">{isNew ? 'New POA&M Item' : 'Edit ' + (item.poam_number || 'POA&M')}</div>
        <div className="admin-modal-body" style={{ maxHeight: '72vh', overflow: 'auto' }}>
          <div className="admin-form-grid two">
            <div className="admin-field" style={{ gridColumn: '1 / -1' }}><label>Weakness title</label>
              <input type="text" value={form.weakness_title} onChange={function(e) { setField('weakness_title', e.target.value); }} placeholder="e.g. MFA not enforced for all users" />
            </div>
            <div className="admin-field" style={{ gridColumn: '1 / -1' }}><label>Description</label>
              <textarea className="admin-textarea" rows={3} value={form.weakness_desc} onChange={function(e) { setField('weakness_desc', e.target.value); }} placeholder="Detailed description of the gap, scope, and impact" />
            </div>
            <div className="admin-field"><label>Severity</label>
              <select value={form.severity} onChange={function(e) { setField('severity', e.target.value); }}>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div className="admin-field"><label>Status</label>
              <select value={form.status} onChange={function(e) { setField('status', e.target.value); }}>
                <option value="open">Open</option>
                <option value="in_progress">In progress</option>
                <option value="closed">Closed</option>
                <option value="deferred">Deferred</option>
              </select>
            </div>
            <div className="admin-field"><label>Owner name</label>
              <input type="text" value={form.owner_name} onChange={function(e) { setField('owner_name', e.target.value); }} placeholder="e.g. Alex Voskidis" />
            </div>
            <div className="admin-field"><label>Owner email</label>
              <input type="text" value={form.owner_email} onChange={function(e) { setField('owner_email', e.target.value); }} placeholder="alex@cloudbytegroup.com" />
            </div>
            <div className="admin-field"><label>Target completion</label>
              <input type="date" value={form.target_date} onChange={function(e) { setField('target_date', e.target.value); }} />
            </div>
            <div className="admin-field"><label>Linked control (optional)</label>
              <input type="text" value={form.control_id} onChange={function(e) { setField('control_id', e.target.value); }} placeholder="e.g. 3.5.3" />
            </div>
            <div className="admin-field" style={{ gridColumn: '1 / -1' }}><label>Remediation plan</label>
              <textarea className="admin-textarea" rows={3} value={form.remediation_plan} onChange={function(e) { setField('remediation_plan', e.target.value); }} placeholder="What will be done to remediate, by whom, in what order" />
            </div>
            <div className="admin-field" style={{ gridColumn: '1 / -1' }}><label>Notes</label>
              <textarea className="admin-textarea" rows={2} value={form.notes} onChange={function(e) { setField('notes', e.target.value); }} placeholder="Internal notes, context, history" />
            </div>
          </div>
        </div>
        <div className="admin-modal-footer">
          <button className="admin-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="admin-btn primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : (isNew ? 'Create' : 'Save')}</button>
        </div>
      </div>
    </div>
  );
}

// SSP export affordance. Authenticated fetch → opens the resulting HTML in a
// new window (sibling pattern to the QBR export). The HTML carries print
// styles + a Print button so the admin reviews and saves to PDF for the
// C3PAO submission package.
function CMMCSspExport({ clientId }) {
  var [generating, setGenerating] = useState(false);
  var [error, setError] = useState(null);
  // Pop-up blocked fallback: store the generated HTML as a Blob URL the
  // user can click. Browsers don't block click-triggered anchors so this
  // path always works, even when window.open is suppressed.
  var [fallbackUrl, setFallbackUrl] = useState(null);

  function generate() {
    setGenerating(true); setError(null); setFallbackUrl(null);
    authFetch(API + '/clients/' + clientId + '/cmmc/ssp')
      .then(function(r) {
        if (!r.ok) return r.json().then(function(d) { throw new Error(d.error || 'SSP generation failed'); });
        return r.text();
      })
      .then(function(html) {
        setGenerating(false);
        // Try direct window.open first; if blocked, build a blob URL
        // anchor the user can click as a fallback.
        var w;
        try { w = window.open('', '_blank', 'noopener,noreferrer'); } catch (e) { w = null; }
        if (w) {
          w.document.open(); w.document.write(html); w.document.close();
          return;
        }
        // Fallback: blob URL → user clicks to open in a new tab. Browsers
        // honor click-triggered navigations even when window.open is blocked.
        var blob = new Blob([html], { type: 'text/html;charset=utf-8' });
        var url = URL.createObjectURL(blob);
        setFallbackUrl(url);
      })
      .catch(function(e) { setGenerating(false); setError(e.message || 'SSP generation failed'); });
  }

  // Clean up blob URLs when component unmounts or a new one is generated
  useEffect(function() {
    return function() { if (fallbackUrl) URL.revokeObjectURL(fallbackUrl); };
  }, [fallbackUrl]);

  return (
    <ContentBand
      title="System Security Plan (SSP)"
      subtitle="Auto-generate the SSP draft from current control state + the latest completed scoping report. Admin review required before submission."
      rightContent={<button className="admin-btn primary sm" onClick={generate} disabled={generating}>{generating ? 'Generating…' : '📄 Generate SSP draft'}</button>}
    >
      <div className="ssp-export-hint">
        <strong>What you get:</strong> a NIST 800-171A-structured HTML document covering Sections 1–5 (System Identification, Description, Control Implementation, POA&amp;M, Approval). Each control gets an auto-generated narrative summarizing what Aurelius can verify; failed controls auto-populate Section 4 POA&amp;M entries with their remediation plans. Print to PDF from the new window.
        <br /><br />
        <strong>Important:</strong> auto-generated narratives are a starting draft. Each control's prose should be reviewed and customized to your organization before submission to the C3PAO. The "Implementation Method" lines describe what we evidence; "How it's implemented for THIS organization" is your edit territory.
        <br /><br />
        <strong>Pop-up blocker note:</strong> the SSP opens in a new window. If your browser blocks it, the fallback link below appears — click it directly. To stop the prompt, allow pop-ups for <code>aurelius.cloudbytegroup.com</code> in your browser settings.
      </div>
      {fallbackUrl && (
        <div className="admin-banner info" style={{ marginTop: 10 }}>
          New window was blocked. <a href={fallbackUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-blue)', fontWeight: 700 }}>Click here to open the SSP draft</a>.
        </div>
      )}
      {error && <div className="admin-banner error" style={{ marginTop: 10 }}>{error}</div>}
    </ContentBand>
  );
}

// ── CMMC Approvals banner (Phase B.4 — dual-control gate) ─────
// Shows pending dual-approval requests for critical-severity
// remediations across all clients, plus the calling admin's own
// in-flight requests. Renders at the top of the CMMC tab when there
// are any active items; collapses to nothing otherwise.
//
// Two columns:
//   • Pending — requests from OTHER admins this admin can approve.
//     Approve / Cancel buttons. Self-approval is rejected at the API.
//   • Mine — own requests + their state (awaiting / approved / done).
//     If approved, prompts the requester to re-open the remediation
//     drawer and execute with the approval_id.
function CMMCApprovalsBanner() {
  var [pending, setPending] = useState([]);
  var [mine, setMine] = useState([]);
  var [loading, setLoading] = useState(true);
  var [busyId, setBusyId] = useState(null);
  var auditorView = useAuditorView();
  function refetch() {
    setLoading(true);
    Promise.all([
      authFetch(API + '/cmmc/approvals/pending').then(function(r) { return r.json(); }).catch(function() { return { approvals: [] }; }),
      authFetch(API + '/cmmc/approvals/mine').then(function(r) { return r.json(); }).catch(function() { return { approvals: [] }; })
    ]).then(function(both) {
      setPending(both[0].approvals || []);
      setMine((both[1].approvals || []).filter(function(a) { return ['awaiting_approval','approved','running'].indexOf(a.status) >= 0; }));
      setLoading(false);
    });
  }
  useEffect(refetch, []);  // eslint-disable-line react-hooks/exhaustive-deps
  function approve(id) {
    setBusyId(id);
    authFetch(API + '/cmmc/approvals/' + id + '/approve', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) { setBusyId(null); if (x.ok) refetch(); else alert(x.d.error || 'Approve failed'); })
      .catch(function() { setBusyId(null); alert('Approve failed'); });
  }
  function cancel(id) {
    if (!window.confirm('Cancel this approval request?')) return;
    setBusyId(id);
    authFetch(API + '/cmmc/approvals/' + id + '/cancel', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) { setBusyId(null); if (x.ok) refetch(); else alert(x.d.error || 'Cancel failed'); })
      .catch(function() { setBusyId(null); alert('Cancel failed'); });
  }
  if (loading) return null;
  if (pending.length === 0 && mine.length === 0) return null;
  return (
    <div className="cmmc-approvals-banner">
      <div className="cmmc-approvals-head">
        <span className="cmmc-approvals-icon" aria-hidden="true">🛡</span>
        <span className="cmmc-approvals-label">Pending Approvals</span>
        <span className="cmmc-approvals-count">{pending.length + mine.length}</span>
        <button type="button" className="admin-btn sm" style={{ marginLeft: 'auto' }} onClick={refetch}>Refresh</button>
      </div>
      <div className="cmmc-approvals-grid">
        {pending.length > 0 && (
          <div className="cmmc-approvals-col">
            <div className="cmmc-approvals-col-label">For your review ({pending.length})</div>
            {pending.map(function(a) {
              var planTitle = a.plan && a.plan.title ? a.plan.title : a.playbook;
              var ctlId = a.target && a.target.control_id ? a.target.control_id : '—';
              return (
                <div key={a.id} className="cmmc-approvals-row">
                  <div className="cmmc-approvals-row-main">
                    <strong>{planTitle}</strong>
                    <span className="cmmc-approvals-meta">
                      {a.client_id} · NIST {ctlId} · requested by {a.requested_by_email || 'admin'} · {new Date(a.requested_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="cmmc-approvals-actions">
                    {!auditorView && <button type="button" className="admin-btn sm primary" disabled={busyId === a.id} onClick={function() { approve(a.id); }}>Approve</button>}
                    {!auditorView && <button type="button" className="admin-btn sm" disabled={busyId === a.id} onClick={function() { cancel(a.id); }}>Cancel</button>}
                    {auditorView && <span className="cmmc-approvals-readyhint" style={{ background: 'transparent', borderColor: 'var(--border-default)', color: 'var(--text-muted)' }}>Read-only — exit auditor view to act</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {mine.length > 0 && (
          <div className="cmmc-approvals-col">
            <div className="cmmc-approvals-col-label">Your requests ({mine.length})</div>
            {mine.map(function(a) {
              var planTitle = a.plan && a.plan.title ? a.plan.title : a.playbook;
              var ctlId = a.target && a.target.control_id ? a.target.control_id : '—';
              return (
                <div key={a.id} className="cmmc-approvals-row">
                  <div className="cmmc-approvals-row-main">
                    <strong>{planTitle}</strong>
                    <span className="cmmc-approvals-meta">
                      {a.client_id} · NIST {ctlId} · status: <span className={'cmmc-approvals-status cmmc-approvals-status-' + a.status}>{a.status.replace('_',' ')}</span>
                      {a.approved_by_email && <span> · approved by {a.approved_by_email}</span>}
                    </span>
                  </div>
                  <div className="cmmc-approvals-actions">
                    {!auditorView && a.status === 'awaiting_approval' && <button type="button" className="admin-btn sm" disabled={busyId === a.id} onClick={function() { cancel(a.id); }}>Cancel</button>}
                    {a.status === 'approved' && <span className="cmmc-approvals-readyhint">Re-open the remediation drawer to execute with approval ID #{a.id}.</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── CMMC Inventories triple (Phase B.4) ─────────────────────────
// Three tabs in one ContentBand. Each renders a fixed-column table
// of audit-relevant inventory:
//   • Personnel — every Entra user × clearance / training / CUI access
//   • Privileged — every directory role assignment × review state
//   • Software — every observed/whitelisted product × approval status
//
// ── Workspace register (mock layout) — reusable Environment/Compliance
// surface: view header + stat cards + table, fed by an endpoint via a small
// config with an adapt() that maps API data to stats/columns/rows. ──────────
function cwDot(tone, text) { return <span className={'cmmcw-dot ' + tone}>{text}</span>; }
function cwChip(text) { return text ? <span className="cmmcw-chip">{text}</span> : <span style={{ color: 'var(--cw-faint)' }}>—</span>; }
function cwSev(level, text) { var m = { critical: 'crit', high: 'high', medium: 'med', low: 'low', none: 'low' }; return <span className={'cmmcw-sev ' + (m[level] || 'low')}><i />{text || level || '—'}</span>; }
function cwPerson(name, sub) { return <span>{'​'}<span className="cmmcw-lead">{name || '—'}</span>{sub && <span className="cmmcw-cellsub">{sub}</span>}</span>; }

// Flatten a rendered table cell (React node) to plain text — the generic
// fallback sort key for any register that doesn't supply an explicit `sort`
// array. Recurses through strings/numbers/arrays/elements and strips the
// zero-width space cwPerson uses as an anchor.
function cwCellText(node) {
  if (node == null || node === false || node === true) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(cwCellText).join(' ');
  if (node.props && node.props.children != null) return cwCellText(node.props.children);
  return '';
}
// Column comparator: numeric when both sides are numbers (or numeric strings),
// otherwise case-insensitive text. Blank/em-dash values sort last regardless.
function cwCmp(a, b) {
  var as = (typeof a === 'string') ? a.replace(/​/g, '').trim() : a;
  var bs = (typeof b === 'string') ? b.replace(/​/g, '').trim() : b;
  var ae = (as == null || as === '' || as === '—'), be = (bs == null || bs === '' || bs === '—');
  if (ae && be) return 0; if (ae) return 1; if (be) return -1;
  var na = (typeof as === 'number') ? as : (/^-?\d+(\.\d+)?$/.test(String(as)) ? parseFloat(as) : null);
  var nb = (typeof bs === 'number') ? bs : (/^-?\d+(\.\d+)?$/.test(String(bs)) ? parseFloat(bs) : null);
  if (na != null && nb != null) return na - nb;
  return String(as).toLowerCase().localeCompare(String(bs).toLowerCase(), undefined, { numeric: true });
}

// Per-register provenance — which of the client's integrations feed each
// surface. Keyed by endpoint. Maturing toward the Atomus model where every
// screen traces to a real source; 'Manual entry' marks org-maintained data.
var CMMCW_SOURCES = {
  '/posture': ['Intune', 'Microsoft Defender', 'NinjaOne Gov'],
  '/cmmc/inventory/software': ['NinjaOne Gov', 'Intune'],
  '/cmmc/inventory/personnel': ['Microsoft Graph', 'HR'],
  '/cmmc/inventory/privileged': ['Microsoft Graph', 'Duo'],
  '/network': ['Cisco Meraki'],
  '/cmmc/locations': ['Manual entry'],
  '/cmmc/external-systems': ['HigherGov', 'Manual entry'],
  '/changes': ['Aurelius change workflow'],
  '/cmmc/risk': ['Manual entry'],
  '/cmmc/baselines': ['Aurelius baseline'],
  '/cmmc/maintenance': ['Manual entry'],
  '/cmmc/subcontractors': ['SPRS', 'Manual entry'],
  '/cmmc/reviews-checklist': ['Manual entry'],
  '/cmmc/visitors': ['Verkada', 'Manual entry'],
  '/cmmc/vulnerabilities': ['Microsoft Defender', 'NinjaOne Gov'],
  '/cmmc/flaws': ['Manual entry']
};

function CmmcwRegister({ clientId, cfg, sources }) {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [q, setQ] = useState('');
  var [sortCol, setSortCol] = useState(null);   // column index being sorted, or null
  var [sortDir, setSortDir] = useState('asc');   // 'asc' | 'desc'
  useEffect(function() {
    var live = true;
    setLoading(true);
    authFetch(API + '/clients/' + clientId + cfg.endpoint)
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) { if (live) { setData(d); setLoading(false); } })
      .catch(function() { if (live) setLoading(false); });
    return function() { live = false; };
  }, [clientId, cfg.endpoint]);
  // reset the sort when the register (endpoint) changes
  useEffect(function() { setSortCol(null); setSortDir('asc'); }, [cfg.endpoint]);
  if (loading) return <div className="cmmcw-empty">Loading {cfg.title.toLowerCase()}…</div>;
  var v = cfg.adapt(data || {});
  var rows = v.rows || [];
  if (q) { var lq = q.toLowerCase(); rows = rows.filter(function(r) { return (r._s || '').toLowerCase().indexOf(lq) >= 0; }); }
  // Sort by the active column. Use the row's explicit `sort` value when the
  // register supplies one (semantic ordering for status/boolean/date columns),
  // else fall back to the rendered cell's text. Stable + non-mutating.
  function sortVal(r, col) {
    if (r.sort && r.sort[col] != null) return r.sort[col];
    return cwCellText(r.cells ? r.cells[col] : null);
  }
  if (sortCol != null) {
    rows = rows.slice().sort(function(a, b) {
      var c = cwCmp(sortVal(a, sortCol), sortVal(b, sortCol));
      return sortDir === 'desc' ? -c : c;
    });
  }
  function toggleSort(col) {
    if (sortCol === col) { setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); }
    else { setSortCol(col); setSortDir('asc'); }
  }
  return (
    <>
      <div className="cmmcw-vhead">
        <div><div className="cmmcw-eye">{cfg.eye}</div><h2 className="cmmcw-h1">{cfg.title}</h2><p className="cmmcw-sub">{cfg.sub}</p></div>
      </div>
      {(CMMCW_SOURCES[cfg.endpoint] || []).length > 0 && (
        <div className="cmmcw-srcline">
          <span className="k">Fed by</span>
          {(CMMCW_SOURCES[cfg.endpoint] || []).map(function(sname, i) {
            if (/manual/i.test(sname)) return <span key={i} className="cmmcw-chip">{sname}</span>;
            var connected = sources && (sname in sources) ? sources[sname] : null; // null = source label, connection unknown
            if (connected === false) return <span key={i} className="cmmcw-chip off" title="Not connected for this client"><span className="dotoff" />{sname}</span>;
            return <span key={i} className="cmmcw-chip accent" title={connected === true ? 'Connected' : 'Integration source'}>{connected === true && <span className="dotlive" />}{sname}</span>;
          })}
        </div>
      )}
      {v.stats && <div className="cmmcw-stats">{v.stats.map(function(s, i) { return <div className="cmmcw-stat" key={i}><div className="h"><span>{s[0]}</span></div><div className={'n ' + (s[2] || '')}>{s[1]}</div><div className="s">{s[3] || ' '}</div></div>; })}</div>}
      {cfg.searchable !== false && (
        <div className="cmmcw-tbar"><div className="cmmcw-search-wrap"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="m20 20-3-3" /></svg><input className="cmmcw-search-in" placeholder={cfg.searchPlaceholder || 'Search…'} value={q} onChange={function(e) { setQ(e.target.value); }} /></div></div>
      )}
      <div className="cmmcw-card"><div className="cmmcw-twrap"><table className="cmmcw-table">
        <thead><tr>{v.columns.map(function(c, i) {
          var active = sortCol === i;
          return (
            <th key={i} className={'cmmcw-th-sort' + (active ? ' on ' + sortDir : '')} onClick={function() { toggleSort(i); }} title="Sort by this column">
              <span className="lbl">{c}</span>
              <svg className="cmmcw-sort-caret" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
            </th>
          );
        })}</tr></thead>
        <tbody>
          {rows.map(function(r, ri) { return <tr key={r._k || ri}>{r.cells.map(function(c, ci) { return <td key={ci}>{c}</td>; })}</tr>; })}
          {rows.length === 0 && <tr><td colSpan={v.columns.length}><div className="cmmcw-empty">Nothing to show.</div></td></tr>}
        </tbody>
      </table></div>{v.footer && <div className="cmmcw-cfoot"><span className="mono">{v.footer}</span></div>}</div>
    </>
  );
}

// Config factory for the Environment registers (data via existing endpoints).
function cmmcwEnvCfg(key) {
  if (key === 'personnel') return {
    eye: 'Environment', title: 'Personnel', sub: 'Every identity scoped for CMMC — training, background-check and CUI-access state, driven from Entra ID and your HR source.',
    endpoint: '/cmmc/inventory/personnel', searchPlaceholder: 'Search by name, email, job title…',
    adapt: function(d) {
      var ps = d.personnel || [], s = d.summary || {};
      return {
        stats: [['People', ps.length, ''], ['CUI handlers', s.cui_access != null ? s.cui_access : ps.filter(function(p) { return p.cui_access; }).length, 'info'], ['Training overdue', s.training_overdue || 0, (s.training_overdue ? 'warn' : '')], ['MFA gaps', s.mfa_gaps || 0, (s.mfa_gaps ? 'bad' : '')]],
        columns: ['Person', 'Dept', 'CUI access', 'Training', 'Background', 'MFA'],
        rows: ps.map(function(p) {
          return { _k: p.id, _s: (p.display_name || '') + ' ' + (p.user_principal_name || '') + ' ' + (p.job_title || ''),
            // explicit sort keys aligned to columns; status columns rank by
            // attention-needed (higher = worse) so desc surfaces gaps first
            sort: [
              (p.display_name || '').toLowerCase(), (p.department || '').toLowerCase(),
              p.cui_access ? 1 : 0,
              p.training_overdue ? 1 : 0,
              p.background_overdue ? 1 : 0,
              p.mfa_gap ? 2 : (p.mfa_enabled ? 0 : 1)
            ],
            cells: [
            cwPerson(p.display_name, p.job_title), cwChip(p.department),
            p.cui_access ? cwDot('info', 'Yes') : cwDot('mut', 'No'),
            p.training_overdue ? cwDot('warn', 'Overdue') : cwDot('ok', 'Current'),
            p.background_overdue ? cwDot('bad', 'Missing') : cwDot('ok', 'Complete'),
            (p.mfa_gap) ? cwDot('bad', 'Gap') : cwDot('ok', p.mfa_enabled ? 'On' : '—')
          ] };
        }),
        footer: ps.length + ' personnel · synced from Microsoft Graph + HR'
      };
    }
  };
  if (key === 'privileged') return {
    eye: 'Environment', title: 'Privileged Access', sub: 'Elevated role assignments across every connected platform — the 3.1.5 / 3.5.3 least-privilege picture in one place.',
    endpoint: '/cmmc/inventory/privileged', searchPlaceholder: 'Search by name, email, role…',
    adapt: function(d) {
      var as = d.assignments || [], s = d.summary || {};
      return {
        stats: [['Assignments', as.length, ''], ['Permanent', s.permanent != null ? s.permanent : as.filter(function(a) { return a.assignment_type === 'permanent'; }).length, ''], ['Review overdue', s.review_overdue || 0, (s.review_overdue ? 'warn' : '')], ['PIM recommended', s.pim_recommended || 0, (s.pim_recommended ? 'info' : '')]],
        columns: ['Identity', 'Role', 'Type', 'Last review', 'Flags'],
        rows: as.map(function(a) {
          return { _k: a.id, _s: (a.principal_display_name || '') + ' ' + (a.principal_upn || '') + ' ' + (a.role_display_name || ''), cells: [
            cwPerson(a.principal_display_name, a.principal_upn), a.role_display_name, cwChip(a.assignment_type),
            a.review_overdue ? cwDot('bad', 'Overdue') : cwDot('ok', a.review_age_days != null ? a.review_age_days + 'd ago' : 'Current'),
            a.flag_count > 0 ? cwDot('warn', a.flag_count + ' flag' + (a.flag_count === 1 ? '' : 's')) : cwDot('ok', 'Clean')
          ] };
        }),
        footer: as.length + ' privileged assignments'
      };
    }
  };
  if (key === 'software') return {
    eye: 'Environment', title: 'Software', sub: 'Installed software discovered across managed endpoints — application-control evidence for the 3.4 family.',
    endpoint: '/cmmc/inventory/software', searchPlaceholder: 'Search software or publisher…',
    adapt: function(d) {
      var sw = d.software || [];
      var cnt = function(st) { return sw.filter(function(x) { return x.approval_status === st; }).length; };
      return {
        stats: [['Titles', sw.length, ''], ['Approved', cnt('approved'), 'ok'], ['Pending', cnt('pending'), (cnt('pending') ? 'warn' : '')], ['Restricted / blocked', cnt('restricted') + cnt('blocked'), ((cnt('restricted') + cnt('blocked')) ? 'bad' : '')]],
        columns: ['Software', 'Publisher', 'Devices', 'Risk', 'Status'],
        rows: sw.map(function(x) {
          var st = x.approval_status;
          var tone = st === 'approved' ? 'ok' : st === 'pending' ? 'warn' : st === 'blocked' ? 'bad' : st === 'restricted' ? 'warn' : 'mut';
          return { _k: x.id, _s: (x.software_name || '') + ' ' + (x.publisher || ''), cells: [
            cwPerson(x.software_name, x.version), x.publisher,
            <span className="mono">{x.devices != null ? x.devices : x.install_count}</span>,
            cwSev(x.risk_score, x.risk_score), cwDot(tone, st)
          ] };
        }),
        footer: sw.length + ' titles across the fleet'
      };
    }
  };
  if (key === 'devices') return {
    eye: 'Environment', title: 'Devices', sub: 'Every workstation and server in scope, with live health from Intune / Defender and NinjaOne Gov — no agent of ours required.',
    endpoint: '/posture', searchPlaceholder: 'Search by device or owner…',
    adapt: function(d) {
      var ds = d.devices || [], s = d.summary || {};
      return {
        stats: [['Devices', s.total != null ? s.total : ds.length, ''], ['Online', s.online != null ? s.online : ds.filter(function(x) { return !x.offline; }).length, 'ok'], ['Offline', s.offline != null ? s.offline : ds.filter(function(x) { return x.offline; }).length, (s.offline ? 'warn' : '')], ['Defender off', s.defenderDisabled || 0, (s.defenderDisabled ? 'bad' : '')]],
        columns: ['Device', 'Owner', 'Class', 'Status', 'Defender'],
        rows: ds.map(function(x) {
          var cls = (x.nodeClass || '');
          return { _k: x.id, _s: (x.name || '') + ' ' + (x.lastUser || ''), cells: [
            cwPerson(x.name, null), x.lastUser || '—',
            cwChip(cls.indexOf('MAC') >= 0 ? 'macOS' : cls.indexOf('LAPTOP') >= 0 ? 'Laptop' : cls.indexOf('SERVER') >= 0 ? 'Server' : 'Workstation'),
            x.offline ? cwDot('warn', 'Offline') : cwDot('ok', 'Online'),
            x.defenderDisabled ? cwDot('bad', 'Disabled') : cwDot('ok', x.av && x.av.state === 'ON' ? 'Healthy' : (x.av && x.av.state) || '—')
          ] };
        }),
        footer: (s.total != null ? s.total : ds.length) + ' devices · Intune + Defender + NinjaOne Gov'
      };
    }
  };
  if (key === 'network') return {
    eye: 'Environment · 3.13', title: 'Network Inventory', sub: 'Firewalls, switches and access points across every location, with firmware state and scope.',
    endpoint: '/network', searchPlaceholder: 'Search device, model, or site…',
    adapt: function(d) {
      var ds = d.devices || [], s = d.summary || {};
      var outdated = ds.filter(function(x) { return x.firmwareStatus && x.firmwareStatus !== 'upToDate'; }).length;
      return {
        stats: [['Devices', s.total != null ? s.total : ds.length, ''], ['Online', s.online != null ? s.online : ds.filter(function(x) { return x.status === 'online'; }).length, 'ok'], ['Sites', s.sites || 0, ''], ['Firmware outdated', outdated, (outdated ? 'warn' : '')]],
        columns: ['Device', 'Model', 'Site', 'IP', 'Status', 'Firmware'],
        rows: ds.map(function(x) {
          return { _k: x.id, _s: (x.name || '') + ' ' + (x.model || '') + ' ' + (x.site || ''), cells: [
            cwPerson(x.name, x.productLine), x.model, x.site,
            <span className="mono">{x.ip || '—'}</span>,
            x.status === 'online' ? cwDot('ok', 'Online') : cwDot('warn', x.status || 'offline'),
            x.firmwareStatus === 'upToDate' ? cwDot('ok', 'Current') : cwDot('warn', 'Outdated')
          ] };
        }),
        footer: ds.length + ' network devices · Domain 3.13'
      };
    }
  };
  if (key === 'locations') return {
    eye: 'Environment · 3.10', title: 'Locations', sub: 'Physical sites with their access controls and CUI classification for the physical-protection family.',
    endpoint: '/cmmc/locations', searchPlaceholder: 'Search locations or addresses…',
    adapt: function(d) {
      var it = d.items || [], s = d.summary || {};
      var yn = function(v) { return v ? cwDot('ok', 'Yes') : cwDot('bad', 'No'); };
      return {
        stats: [['Locations', it.length, ''], ['CUI scope', s.cui_scope || 0, 'info']],
        columns: ['Location', 'Address', 'Keys', 'Cabinets', 'Labels', 'Scope', 'Authorized'],
        rows: it.map(function(l) {
          return { _k: l.id, _s: (l.name || '') + ' ' + (l.address || ''), cells: [
            <span className="cmmcw-lead">{l.name}</span>, l.address, yn(l.physical_keys), yn(l.locked_cabinets), yn(l.physical_labels),
            cwChip(l.scope), l.authorized
          ] };
        }),
        footer: it.length + ' locations'
      };
    }
  };
  if (key === 'external') return {
    eye: 'Environment', title: 'External Systems', sub: 'Cloud and SaaS platforms in the boundary, with FCI/CUI classification and data flow — the Body of Evidence inventory.',
    endpoint: '/cmmc/external-systems', searchPlaceholder: 'Search systems…',
    adapt: function(d) {
      var it = d.items || [], s = d.summary || {};
      return {
        stats: [['Systems', it.length, ''], ['Store CUI', s.cui || 0, 'info'], ['With SLA', s.with_sla || 0, '']],
        columns: ['System', 'Purpose', 'FCI / CUI', 'Data flow', 'SLA'],
        rows: it.map(function(x) {
          return { _k: x.id, _s: (x.name || '') + ' ' + (x.purpose || ''), cells: [
            <span className="cmmcw-lead">{x.name}</span>, x.purpose, cwChip(x.classification),
            cwDot(x.classification === 'CUI' ? 'info' : 'mut', x.data_flow), x.sla ? cwDot('ok', 'Yes') : cwDot('mut', '—')
          ] };
        }),
        footer: it.length + ' external systems · DFARS 252.204-7012 body of evidence'
      };
    }
  };
  return { eye: 'Environment', title: key, sub: '', endpoint: '/cmmc/inventory/' + key, adapt: function() { return { columns: [], rows: [] }; } };
}

function cwDate(ts) { return ts ? new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—'; }

// Config factory for the Compliance registers.
function cmmcwCompCfg(key) {
  if (key === 'change') return {
    eye: 'Compliance', title: 'Change Management', sub: 'The 3.4.4 impact-analysis workflow — changes to the CUI environment, reviewed and approved before implementation.',
    endpoint: '/changes', searchPlaceholder: 'Search changes…',
    adapt: function(d) {
      var cs = d.changes || [], s = d.summary || {};
      var tone = function(st) { return st === 'completed' ? 'ok' : (st === 'approved' || st === 'in_progress') ? 'info' : st === 'denied' ? 'bad' : (st === 'submitted' || st === 'under_review') ? 'warn' : 'mut'; };
      return {
        stats: [['Changes', cs.length, ''], ['In progress', (s.in_progress || 0) + (s.approved || 0), 'info'], ['Awaiting review', (s.submitted || 0) + (s.under_review || 0), (((s.submitted || 0) + (s.under_review || 0)) ? 'warn' : '')], ['Completed', s.completed || 0, 'ok']],
        columns: ['ID', 'Title', 'Type', 'Priority', 'Status', 'Submitted'],
        rows: cs.map(function(c) {
          return { _k: c.id, _s: (c.id || '') + ' ' + (c.title || ''), cells: [
            <span className="mono" style={{ color: 'var(--cw-acc)', whiteSpace: 'nowrap' }}>{c.id}</span>,
            cwPerson(c.title, c.submittedBy && c.submittedBy.name), cwChip(c.category || c.type), cwChip(c.priority),
            cwDot(tone(c.status), (c.status || '').replace(/_/g, ' ')),
            <span className="mono" style={{ whiteSpace: 'nowrap' }}>{cwDate(c.submittedAt)}</span>
          ] };
        }),
        footer: cs.length + ' change records'
      };
    }
  };
  if (key === 'risk') return {
    eye: 'Compliance', title: 'Risk Register', sub: 'Tracked risks with likelihood, impact and treatment status for the 3.11 risk-assessment family.',
    endpoint: '/cmmc/risk', searchPlaceholder: 'Search risks…',
    adapt: function(d) {
      var it = d.items || [], s = d.summary || {};
      var sevOf = function(x) { return x === 'high' ? 'high' : x === 'moderate' ? 'med' : x === 'critical' ? 'crit' : 'low'; };
      var tone = function(st) { return st === 'implemented' ? 'ok' : st === 'in_progress' ? 'info' : 'warn'; };
      return {
        stats: [['Risks', it.length, ''], ['Open', s.open || 0, (s.open ? 'warn' : '')], ['In progress', s.in_progress || 0, 'info'], ['Implemented', s.implemented || 0, 'ok']],
        columns: ['Risk', 'Category', 'Likelihood', 'Impact', 'Owner', 'Status'],
        rows: it.map(function(r) {
          return { _k: r.id, _s: (r.description || '') + ' ' + (r.category || ''), cells: [
            <span className="cmmcw-lead">{r.description}</span>, cwChip(r.category),
            <span className={'cmmcw-sev ' + sevOf(r.likelihood)}><i />{r.likelihood}</span>,
            <span className={'cmmcw-sev ' + sevOf(r.impact)}><i />{r.impact}</span>,
            r.owner, cwDot(tone(r.status), (r.status || '').replace(/_/g, ' '))
          ] };
        }),
        footer: it.length + ' risks tracked'
      };
    }
  };
  if (key === 'visitors') return {
    eye: 'Compliance', title: 'Visitors', sub: 'Physical-access visitor log for the 3.10 escort-and-monitor requirement.',
    endpoint: '/cmmc/visitors', searchPlaceholder: 'Search visitors…',
    adapt: function(d) {
      var it = d.items || [], s = d.summary || {};
      var tone = function(st) { return st === 'on_site' ? 'ok' : st === 'invited' ? 'info' : 'mut'; };
      return {
        stats: [['Visitors', it.length, ''], ['On site', s.on_site || 0, 'ok'], ['Checked out', s.checked_out || 0, ''], ['Invited', s.invited || 0, 'info']],
        columns: ['Visitor', 'Email', 'Status', 'Purpose', 'Host', 'When'],
        rows: it.map(function(v) {
          return { _k: v.id, _s: (v.name || '') + ' ' + (v.email || ''), cells: [
            <span className="cmmcw-lead">{v.name}</span>, v.email,
            cwDot(tone(v.status), (v.status || '').replace(/_/g, ' ')), v.purpose, v.host,
            <span className="mono" style={{ whiteSpace: 'nowrap' }}>{cwDate(v.visit_at)}</span>
          ] };
        }),
        footer: it.length + ' visitor records'
      };
    }
  };
  if (key === 'baselines') return {
    eye: 'Compliance · 3.4', title: 'Baselines', sub: 'Configuration baselines the assessment measures against — the source of truth for the automated checks.',
    endpoint: '/cmmc/baselines', searchPlaceholder: 'Search baselines…',
    adapt: function(d) {
      var it = d.items || [];
      return {
        stats: [['Baselines', it.length, '']],
        columns: ['Baseline', 'Value', 'Scope', 'Updated'],
        rows: it.map(function(b) {
          return { _k: b.id, _s: (b.name || '') + ' ' + (b.value || ''), cells: [
            <span className="cmmcw-lead">{b.name}</span>, <span className="cmmcw-chip">{b.value}</span>, cwChip(b.scope),
            <span className="mono">{cwDate(b.updated_at)}</span>
          ] };
        }),
        footer: it.length + ' baselines'
      };
    }
  };
  if (key === 'maintenance') return {
    eye: 'Compliance · 3.7', title: 'Maintenance', sub: 'Maintenance activity on CUI assets, with approval and completion state.',
    endpoint: '/cmmc/maintenance', searchPlaceholder: 'Search maintenance…',
    adapt: function(d) {
      var it = d.items || [], s = d.summary || {};
      return {
        stats: [['Entries', it.length, ''], ['Pending', s.pending || 0, (s.pending ? 'warn' : '')], ['Complete', s.complete || 0, 'ok']],
        columns: ['Asset', 'Type', 'Scheduled', 'Completed', 'Assigned', 'Status'],
        rows: it.map(function(m) {
          return { _k: m.id, _s: (m.asset || '') + ' ' + (m.type || ''), cells: [
            <span className="mono" style={{ color: 'var(--cw-ink)', fontWeight: 560 }}>{m.asset}</span>, m.type,
            <span className="mono">{cwDate(m.scheduled_date)}</span>, <span className="mono">{m.completed_date ? cwDate(m.completed_date) : '—'}</span>,
            m.assigned, m.status === 'complete' ? cwDot('ok', 'Complete') : cwDot('warn', m.status)
          ] };
        }),
        footer: it.length + ' maintenance records'
      };
    }
  };
  if (key === 'subcontractors') return {
    eye: 'Compliance', title: 'Subcontractor Submissions', sub: 'DFARS 252.204-7012 flow-down — each subcontractor’s readiness and CUI access down the supply chain.',
    endpoint: '/cmmc/subcontractors', searchPlaceholder: 'Search subcontractors…',
    adapt: function(d) {
      var it = d.items || [], s = d.summary || {};
      var sevR = function(r) { return r === 'high' ? 'high' : r === 'moderate' ? 'med' : 'low'; };
      return {
        stats: [['Subcontractors', it.length, ''], ['CUI access', s.cui_access || 0, 'info'], ['Qualified', s.qualified || 0, 'ok'], ['Attention', s.attention || 0, (s.attention ? 'bad' : '')]],
        columns: ['Subcontractor', 'CAGE', 'Status', 'CUI', 'Risk'],
        rows: it.map(function(x) {
          return { _k: x.id, _s: (x.name || '') + ' ' + (x.cage || ''), cells: [
            <span className="cmmcw-lead">{x.name}</span>, <span className="mono">{x.cage}</span>,
            x.compliance_status === 'Qualified' ? cwDot('ok', 'Qualified') : cwDot('bad', 'Not ready'),
            x.cui_access ? cwDot('info', 'CUI access') : cwDot('mut', 'No CUI'),
            <span className={'cmmcw-sev ' + sevR(x.risk)}><i />{x.risk}</span>
          ] };
        }),
        footer: it.length + ' subcontractors'
      };
    }
  };
  if (key === 'reviews') return {
    eye: 'Compliance · 3.12', title: 'Quarterly Reviews', sub: 'The recurring review checklist — evidence the program is operated, not just documented.',
    endpoint: '/cmmc/reviews-checklist', searchPlaceholder: 'Search review items…',
    adapt: function(d) {
      var it = d.items || [];
      return {
        stats: [['Review items', it.length, ''], ['Quarter', (d.summary && d.summary.quarter) || '—', 'info']],
        columns: ['Quarter', 'Review item', 'Completed', 'By'],
        rows: it.map(function(r) {
          return { _k: r.id, _s: (r.item || ''), cells: [
            cwChip(r.quarter), <span className="cmmcw-lead">{r.item}</span>,
            cwDot('ok', cwDate(r.completed_date)), r.completed_by
          ] };
        }),
        footer: it.length + ' review items'
      };
    }
  };
  return { eye: 'Compliance', title: key, sub: '', endpoint: '/cmmc/' + key, adapt: function() { return { columns: [], rows: [] }; } };
}

// Config factory for the Security registers.
function cmmcwSecCfg(key) {
  if (key === 'vulnerabilities') return {
    eye: 'Security', title: 'Vulnerabilities', sub: 'CVE exposure across managed assets with remediation-SLA tracking, from Defender and NinjaOne Gov.',
    endpoint: '/cmmc/vulnerabilities', searchPlaceholder: 'Search CVE or summary…',
    adapt: function(d) {
      var it = d.items || [], s = d.summary || {};
      var sevC = function(x) { return x === 'critical' ? 'crit' : x === 'high' ? 'high' : x === 'medium' ? 'med' : 'low'; };
      return {
        stats: [['Findings', it.length, ''], ['Critical & High', s.critical_high || 0, 'bad'], ['Past SLA', s.past_sla || 0, (s.past_sla ? 'bad' : '')], ['Resolved (wk)', s.resolved_week || 0, 'ok']],
        columns: ['CVE', 'Summary', 'Severity', 'CVSS', 'Assets', 'Days open'],
        rows: it.map(function(v) {
          return { _k: v.id, _s: (v.cve || '') + ' ' + (v.summary || ''), cells: [
            <span className="mono" style={{ color: 'var(--cw-acc)', whiteSpace: 'nowrap' }}>{v.cve}</span>,
            <span className="cmmcw-lead">{v.summary}</span>,
            <span className={'cmmcw-sev ' + sevC(v.severity)}><i />{v.severity}</span>,
            <span className="mono">{v.cvss}</span>, <span className="mono">{v.assets}</span>, <span className="mono">{v.days_open}d</span>
          ] };
        }),
        footer: it.length + ' findings'
      };
    }
  };
  if (key === 'flaws') return {
    eye: 'Security · 3.14', title: 'Reported Flaws', sub: 'User-reported system flaws for the flaw-remediation family.',
    endpoint: '/cmmc/flaws', searchPlaceholder: 'Search flaws…',
    adapt: function(d) {
      var it = d.items || [], s = d.summary || {};
      var sevI = function(x) { return x === 'high' ? 'high' : x === 'moderate' ? 'med' : 'low'; };
      return {
        stats: [['Entries', it.length, ''], ['Open', s.open || 0, (s.open ? 'warn' : '')], ['High impact', s.high_impact || 0, (s.high_impact ? 'bad' : '')], ['Unassigned', s.unassigned || 0, '']],
        columns: ['Title', 'Category', 'Status', 'Impact', 'Assigned', 'Reported'],
        rows: it.map(function(f) {
          return { _k: f.id, _s: (f.title || ''), cells: [
            <span className="cmmcw-lead">{f.title}</span>, cwChip(f.category),
            f.status === 'open' ? cwDot('warn', 'Open') : cwDot('ok', 'Resolved'),
            <span className={'cmmcw-sev ' + sevI(f.impact)}><i />{f.impact}</span>,
            f.assigned || '—', <span className="mono">{cwDate(f.reported_at)}</span>
          ] };
        }),
        footer: it.length + ' reported flaws'
      };
    }
  };
  return { eye: 'Security', title: key, sub: '', endpoint: '/cmmc/' + key, adapt: function() { return { columns: [], rows: [] }; } };
}

// Integrations fabric — the mock's connector catalog: each connector mapped to
// the control family it feeds, its access scope, and FedRAMP status. Nearly all
// are APIs Aurelius already holds (no Aegis-style agent required).
function CmmcwIntegrations() {
  var INTS = [
    ['Microsoft Graph', 'MG', '#7c8cff', 'Connected', 'ok', 'ro', 'Identity & Entra ID visibility — users, roles, Conditional Access, sign-in logs.', '3.1 · 3.5 Access & Auth', true],
    ['Intune', 'IN', '#5bd0e8', 'Connected', 'ok', 'ro', 'Device compliance, encryption and configuration baselines.', '3.4 Configuration', true],
    ['Microsoft Defender', 'MD', '#43dca0', 'Connected', 'ok', 'ro', 'Endpoint threat protection, AV health and vulnerabilities.', '3.14 System Integrity', true],
    ['NinjaOne Gov', 'NG', '#8b93ff', 'Connected', 'ok', 'ro', 'RMM — patch state, software inventory, device health.', '3.4 · 3.11', true],
    ['Cloudflare', 'CF', '#f2b24a', 'Connected', 'ok', 'rw', 'Zero-trust network access and boundary protection.', '3.13 System & Comms', true],
    ['Duo', 'DU', '#43dca0', 'Connected', 'ok', 'rw', 'Multi-factor authentication on local devices.', '3.5 Identification', false],
    ['KeeperGov', 'KG', '#c39bf5', 'Connected', 'ok', 'ro', 'FIPS-validated secrets vault and key lifecycle.', '3.13 System & Comms', true],
    ['Cisco Meraki', 'ME', '#5bd0e8', 'Connected', 'ok', 'ro', 'Network device inventory — firewalls, switches, APs.', '3.13 System & Comms', false],
    ['HigherGov', 'HG', '#8b93ff', 'Connected', 'ok', 'ro', 'Contract data for compliance documentation.', '3.4 Configuration', false],
    ['Exostar', 'EX', '#c39bf5', 'Available', 'mut', 'ro', 'CUI enclave — inherited controls & shared-responsibility evidence.', 'Enclave · Inheritance', true],
    ['Checkr', 'CK', '#43dca0', 'Available', 'mut', 'ro', 'Personnel background checks and screening.', '3.9 Personnel Security', false],
    ['Verkada', 'VK', '#f4796b', 'Available', 'mut', 'ro', 'Physical security monitoring and access control.', '3.10 Physical', false],
    ['Rippling / HR', 'HR', '#f2b24a', 'Available', 'mut', 'ro', 'Sync employee data from your HRIS.', '3.9 Personnel Security', false],
    ['PreVeil', 'PV', '#8b93ff', 'Available', 'mut', 'ro', 'Alternate CUI enclave — inheritance evidence.', 'Enclave · Inheritance', true]
  ];
  var connected = INTS.filter(function(x) { return x[3] === 'Connected'; }).length;
  var fed = INTS.filter(function(x) { return x[8]; }).length;
  return (
    <>
      <div className="cmmcw-vhead"><div><div className="cmmcw-eye">Fabric</div><h2 className="cmmcw-h1">Integrations</h2><p className="cmmcw-sub">The evidence fabric. Each connector maps to the control family it feeds, its access scope, and FedRAMP status — nearly all are APIs Aurelius already holds.</p></div></div>
      <div className="cmmcw-stats">
        <div className="cmmcw-stat"><div className="h"><span>Connected</span></div><div className="n ok">{connected}</div><div className="s">feeding evidence</div></div>
        <div className="cmmcw-stat"><div className="h"><span>Available</span></div><div className="n info">{INTS.length - connected}</div><div className="s">ready to connect</div></div>
        <div className="cmmcw-stat"><div className="h"><span>FedRAMP</span></div><div className="n">{fed}</div><div className="s">authorized</div></div>
        <div className="cmmcw-stat"><div className="h"><span>Read-only</span></div><div className="n">{INTS.filter(function(x) { return x[5] === 'ro'; }).length}</div><div className="s">least privilege</div></div>
      </div>
      <div className="cmmcw-int-grid">
        {INTS.map(function(x) {
          return (
            <div className="cmmcw-int" key={x[0]}>
              <div className="top">
                <div className="logo" style={{ color: x[2], background: x[2] + '14' }}>{x[1]}</div>
                <div><div className="nm">{x[0]}</div><div className="st">{cwDot(x[4], x[3])}</div></div>
                <span className="rw"><span className={'cmmcw-chip ' + (x[5] === 'rw' ? 'rw' : 'ro')}>{x[5] === 'rw' ? 'Read & Write' : 'Read Only'}</span></span>
              </div>
              <div className="desc">{x[6]}</div>
              <div className="foot"><span className="dom">{x[7]}</span>{x[8] && <span className="cmmcw-chip fed">FedRAMP</span>}</div>
            </div>
          );
        })}
      </div>
    </>
  );
}

// Tab switching is local state — the section refetches per tab so a
// stale tab doesn't pin obsolete data. Summary chips at the top of
// each tab give the assessor a single-glance read on the inventory's
// health (e.g. "8 / 50 personnel CUI-cleared, 1 training overdue").
function CMMCInventoriesSection({ clientId }) {
  var [tab, setTab] = useState('personnel');
  return (
    <ContentBand
      title="Inventories"
      subtitle="Audit-defense inventories of personnel, privileged access, and software. Auto-populated from connected sources where observable; annotated for fields that aren't."
    >
      <div className="cmmc-inv-tabs">
        {[
          { id: 'personnel',  label: 'Personnel',         hint: '3.9.1 / 3.9.2' },
          { id: 'privileged', label: 'Privileged Access', hint: '3.1.5 / 3.1.6' },
          { id: 'software',   label: 'Software',          hint: '3.4.6 / 3.4.8' }
        ].map(function(t) {
          return (
            <button key={t.id} type="button"
              className={'cmmc-inv-tab' + (tab === t.id ? ' active' : '')}
              onClick={function() { setTab(t.id); }}>
              <span className="cmmc-inv-tab-label">{t.label}</span>
              <span className="cmmc-inv-tab-hint">{t.hint}</span>
            </button>
          );
        })}
      </div>
      {tab === 'personnel'  && <CMMCPersonnelInventory  clientId={clientId} />}
      {tab === 'privileged' && <CMMCPrivilegedInventory clientId={clientId} />}
      {tab === 'software'   && <CMMCSoftwareInventory   clientId={clientId} />}
    </ContentBand>
  );
}

// Summary-chip strip — re-used across all three inventory tabs. Each
// chip shows a count + label, and a `tone` ('ok' / 'warn' / 'bad' /
// 'muted') drives the color. Hover for the assessor-language tooltip.
function CMMCInvSummary({ chips }) {
  return (
    <div className="cmmc-inv-summary">
      {chips.map(function(c, i) {
        return (
          <div key={i} className={'cmmc-inv-chip cmmc-inv-chip-' + (c.tone || 'muted')} title={c.tip || ''}>
            <span className="cmmc-inv-chip-count">{c.count}</span>
            <span className="cmmc-inv-chip-label">{c.label}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Sort helpers shared by the three inventory tables ───────────
// Ordinal rankings — used so "Critical → Low" and "Approved → Blocked"
// sort by severity rather than alphabetical. asc = "least severe first".
var INV_RISK_RANK       = { critical: 4, high: 3, medium: 2, low: 1, none: 0 };
var INV_APPROVAL_RANK   = { blocked: 4, restricted: 3, pending: 2, approved: 1 };
var INV_CLEARANCE_RANK  = { ts_sci: 6, top_secret: 5, secret: 4, confidential: 3, public_trust: 2, none: 1 };
var INV_ASSIGNMENT_RANK = { permanent: 2, eligible: 1 };

// Generic comparator. Nulls always sink to the bottom regardless of dir.
// Numeric vs string detection happens per-call.
function _invCompare(a, b, dir) {
  var an = a == null, bn = b == null;
  if (an && bn) return 0;
  if (an) return 1;
  if (bn) return -1;
  if (typeof a === 'number' && typeof b === 'number') return dir === 'asc' ? a - b : b - a;
  var as = String(a).toLowerCase(), bs = String(b).toLowerCase();
  return dir === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
}

// Sortable column header — clickable button that toggles the table's
// sort key + direction. Renders a tiny up/down indicator when active.
function CMMCSortHeader({ label, sortKey, currentKey, currentDir, onSort, sortable }) {
  if (sortable === false) return <span>{label}</span>;
  var active = sortKey === currentKey;
  var arrow = active ? (currentDir === 'asc' ? '▲' : '▼') : '';
  return (
    <button type="button" className={'cmmc-inv-sort-btn' + (active ? ' active' : '')}
      onClick={function() { onSort(sortKey); }}
      title={active ? 'Sorted ' + (currentDir === 'asc' ? 'ascending' : 'descending') + ' — click to flip' : 'Click to sort by ' + label}>
      <span>{label}</span>
      {arrow && <span className="cmmc-inv-sort-arrow">{arrow}</span>}
    </button>
  );
}

// Hook-style sort state: returns {sortKey, sortDir, setSort}. Calling
// setSort with the same key flips dir; with a new key resets to asc.
function useInventorySort(initialKey, initialDir) {
  var [sortKey, setSortKey] = useState(initialKey);
  var [sortDir, setSortDir] = useState(initialDir || 'asc');
  function setSort(key) {
    if (key === sortKey) { setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); }
    else { setSortKey(key); setSortDir('asc'); }
  }
  return { sortKey: sortKey, sortDir: sortDir, setSort: setSort };
}

function CMMCPersonnelInventory({ clientId }) {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [err, setErr] = useState(null);
  var [search, setSearch] = useState('');
  var [filter, setFilter] = useState('all');
  var sort = useInventorySort('display_name', 'asc');
  function refetch() {
    setLoading(true); setErr(null);
    authFetch(API + '/clients/' + clientId + '/cmmc/inventory/personnel')
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) { setLoading(false); if (x.ok) setData(x.d); else setErr(x.d.error || 'Failed to load personnel inventory'); })
      .catch(function() { setLoading(false); setErr('Failed to load personnel inventory'); });
  }
  useEffect(refetch, [clientId]);  // eslint-disable-line react-hooks/exhaustive-deps
  if (loading) return <div className="empty-state" style={{ padding: 20 }}><SkeletonLoader /></div>;
  if (err) return <div className="admin-banner error" style={{ margin: 0 }}>{err}</div>;
  var rows = (data && data.personnel) || [];
  var summary = (data && data.summary) || { total: 0, cui_access: 0, cleared_secret_or_higher: 0, training_overdue: 0, mfa_gaps: 0, stale_accounts: 0, background_overdue: 0 };
  var lowerSearch = (search || '').toLowerCase().trim();
  var filtered = rows.filter(function(p) {
    if (filter === 'cui'      && !p.cui_access) return false;
    if (filter === 'training' && !p.training_overdue) return false;
    if (filter === 'mfa'      && !p.mfa_gap) return false;
    if (filter === 'stale'    && !p.stale_account) return false;
    if (filter === 'cleared'  && ['secret','top_secret','ts_sci'].indexOf(p.clearance_level) < 0) return false;
    if (!lowerSearch) return true;
    return (p.display_name || '').toLowerCase().includes(lowerSearch)
        || (p.user_principal_name || '').toLowerCase().includes(lowerSearch)
        || (p.job_title || '').toLowerCase().includes(lowerSearch)
        || (p.department || '').toLowerCase().includes(lowerSearch);
  });
  // Apply sort. Each key has a custom accessor for ordinal / nullable
  // semantics where lexicographic order would be wrong.
  function _sortVal(p, key) {
    switch (key) {
      case 'display_name':         return p.display_name;
      case 'job_title':            return p.job_title;
      case 'clearance':            return INV_CLEARANCE_RANK[p.clearance_level || 'none'] || 0;
      case 'cui_access':           return p.cui_access ? 1 : 0;
      case 'background_check_date': return p.background_check_date;
      case 'training_completed_at': return p.training_completed_at;
      case 'last_signin_days':     return p.last_signin_days != null ? p.last_signin_days : 999999;
      case 'flag_count':           return (p.mfa_gap?1:0) + (p.stale_account?1:0) + (p.background_overdue?1:0) + ((p.training_overdue && p.training_due_at)?1:0);
      default:                     return p[key];
    }
  }
  var sorted = filtered.slice().sort(function(a, b) { return _invCompare(_sortVal(a, sort.sortKey), _sortVal(b, sort.sortKey), sort.sortDir); });
  return (
    <div>
      <CMMCInvSummary chips={[
        { count: summary.total,                     label: 'people',        tone: 'muted', tip: 'Total user accounts in the Entra tenant.' },
        { count: summary.cui_access,                label: 'CUI access',    tone: 'ok',    tip: 'Personnel flagged as authorized to handle Controlled Unclassified Information.' },
        { count: summary.cleared_secret_or_higher,  label: 'cleared',       tone: 'ok',    tip: 'Personnel with Secret-tier or higher clearance.' },
        { count: summary.training_overdue,          label: 'training due',  tone: summary.training_overdue > 0 ? 'warn' : 'muted', tip: 'CUI handlers whose annual security awareness training has lapsed.' },
        { count: summary.mfa_gaps,                  label: 'MFA gaps',      tone: summary.mfa_gaps > 0 ? 'bad' : 'muted', tip: 'CUI handlers without MFA enforced — direct CMMC 3.5.3 violation.' },
        { count: summary.stale_accounts,            label: 'stale (90d)',   tone: summary.stale_accounts > 0 ? 'warn' : 'muted', tip: 'Accounts with no sign-in in 90+ days. Candidates for offboarding under 3.5.6.' },
        { count: summary.background_overdue,        label: 'BG check due',  tone: summary.background_overdue > 0 ? 'warn' : 'muted', tip: 'CUI-access personnel without a recorded background check date.' }
      ]} />
      <div className="cmmc-inv-toolbar">
        <input className="cmmc-inv-search" type="text" placeholder="Search by name, UPN, title, department…" value={search} onChange={function(e) { setSearch(e.target.value); }} />
        <div className="cmmc-inv-filter">
          {[['all','All'],['cui','CUI access'],['cleared','Cleared'],['training','Training due'],['mfa','MFA gaps'],['stale','Stale']].map(function(f) {
            return <button key={f[0]} type="button" className={'cmmc-inv-filter-btn' + (filter === f[0] ? ' active' : '')} onClick={function() { setFilter(f[0]); }}>{f[1]}</button>;
          })}
        </div>
        <div className="cmmc-inv-rowcount">{sorted.length} of {rows.length}</div>
      </div>
      <div className="cmmc-inv-table cmmc-inv-personnel">
        <div className="cmmc-inv-row cmmc-inv-head">
          <CMMCSortHeader label="Person"        sortKey="display_name"         currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} />
          <CMMCSortHeader label="Role"          sortKey="job_title"            currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} />
          <CMMCSortHeader label="Clearance"     sortKey="clearance"            currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} />
          <CMMCSortHeader label="CUI"           sortKey="cui_access"           currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} />
          <CMMCSortHeader label="BG check"      sortKey="background_check_date" currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} />
          <CMMCSortHeader label="Training"      sortKey="training_completed_at" currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} />
          <CMMCSortHeader label="Last sign-in"  sortKey="last_signin_days"     currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} />
          <CMMCSortHeader label="Flags"         sortKey="flag_count"           currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} />
        </div>
        {sorted.length === 0 && <div className="empty-state" style={{ padding: 18 }}>{lowerSearch || filter !== 'all' ? 'No personnel match the filters.' : 'No personnel data available.'}</div>}
        {sorted.map(function(p) {
          return (
            <div key={p.id} className={'cmmc-inv-row' + (p.cui_access ? ' is-cui' : '') + (p.mfa_gap ? ' has-mfa-gap' : '')}>
              <span className="cmmc-inv-person">
                <strong>{p.display_name}</strong>
                <span className="cmmc-inv-upn">{p.user_principal_name || '—'}</span>
              </span>
              <span className="cmmc-inv-role">
                {p.job_title || '—'}
                {p.department && <span className="cmmc-inv-dept">{p.department}</span>}
              </span>
              <span className={'cmmc-inv-clearance cmmc-clr-' + (p.clearance_level || 'none')}>{p.clearance_label || '—'}</span>
              <span className="cmmc-inv-cui">{p.cui_access ? <span className="cmmc-inv-yes" title="CUI handler">●</span> : <span className="cmmc-inv-no" title="No CUI access">○</span>}</span>
              <span className={'cmmc-inv-date' + (p.background_overdue ? ' overdue' : '')}>{p.background_check_date || (p.cui_access ? <span className="cmmc-inv-missing">missing</span> : '—')}</span>
              <span className={'cmmc-inv-date' + (p.training_overdue ? ' overdue' : '')}>{p.training_completed_at || '—'}{p.training_due_at && <div className="cmmc-inv-rel">{p.training_overdue ? 'overdue' : 'due ' + p.training_due_at}</div>}</span>
              <span className={'cmmc-inv-date' + (p.stale_account ? ' overdue' : '')}>{p.last_signin_days != null ? (p.last_signin_days === 0 ? 'today' : p.last_signin_days + 'd ago') : '—'}</span>
              <span className="cmmc-inv-flags">
                {p.mfa_gap && <span className="cmmc-inv-flag cmmc-inv-flag-bad" title="CUI handler without MFA — 3.5.3 violation">No MFA</span>}
                {p.stale_account && <span className="cmmc-inv-flag cmmc-inv-flag-warn" title={'No sign-in for ' + p.last_signin_days + ' days'}>Stale</span>}
                {p.background_overdue && <span className="cmmc-inv-flag cmmc-inv-flag-warn" title="CUI handler without recorded background check">No BG</span>}
                {p.training_overdue && p.training_due_at && <span className="cmmc-inv-flag cmmc-inv-flag-warn" title={'Training expired ' + p.training_due_at}>Training</span>}
                {!p.mfa_gap && !p.stale_account && !p.background_overdue && !(p.training_overdue && p.training_due_at) && <span className="cmmc-inv-flag cmmc-inv-flag-ok">Clean</span>}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CMMCPrivilegedInventory({ clientId }) {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [err, setErr] = useState(null);
  var [filter, setFilter] = useState('all');
  var sort = useInventorySort('flag_count', 'desc');
  function refetch() {
    setLoading(true); setErr(null);
    authFetch(API + '/clients/' + clientId + '/cmmc/inventory/privileged')
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) { setLoading(false); if (x.ok) setData(x.d); else setErr(x.d.error || 'Failed to load privileged access inventory'); })
      .catch(function() { setLoading(false); setErr('Failed to load privileged access inventory'); });
  }
  useEffect(refetch, [clientId]);  // eslint-disable-line react-hooks/exhaustive-deps
  if (loading) return <div className="empty-state" style={{ padding: 20 }}><SkeletonLoader /></div>;
  if (err) return <div className="admin-banner error" style={{ margin: 0 }}>{err}</div>;
  var rows = (data && data.assignments) || [];
  var summary = (data && data.summary) || { total: 0, permanent: 0, eligible: 0, review_overdue: 0, stale_holders: 0, justification_missing: 0, pim_recommended: 0, distinct_principals: 0 };
  var filtered = rows.filter(function(a) {
    if (filter === 'flags' && a.flag_count === 0) return false;
    if (filter === 'overdue' && !a.review_overdue) return false;
    if (filter === 'stale' && !a.stale_holder) return false;
    if (filter === 'permanent' && a.assignment_type !== 'permanent') return false;
    return true;
  });
  function _sortVal(a, key) {
    switch (key) {
      case 'principal':       return a.principal_display_name || a.principal_upn;
      case 'role':            return a.role_display_name;
      case 'assignment_type': return INV_ASSIGNMENT_RANK[a.assignment_type] || 0;
      case 'last_signin':     return a.last_signin_days != null ? a.last_signin_days : 999999;
      case 'last_reviewed':   return a.last_reviewed_at;
      case 'justification':   return a.justification_missing ? 0 : 1;
      case 'flag_count':      return a.flag_count || 0;
      default:                return a[key];
    }
  }
  var sorted = filtered.slice().sort(function(a, b) { return _invCompare(_sortVal(a, sort.sortKey), _sortVal(b, sort.sortKey), sort.sortDir); });
  return (
    <div>
      <CMMCInvSummary chips={[
        { count: summary.distinct_principals,    label: 'principals',         tone: 'muted', tip: 'Distinct users holding at least one privileged role.' },
        { count: summary.total,                  label: 'assignments',        tone: 'muted', tip: 'Total privileged role assignments.' },
        { count: summary.permanent,              label: 'permanent',          tone: 'muted', tip: 'Assignments without PIM eligibility — always-active.' },
        { count: summary.eligible,               label: 'PIM eligible',       tone: 'ok',    tip: 'Assignments behind PIM activation — least-privilege posture.' },
        { count: summary.pim_recommended,        label: 'PIM recommended',    tone: summary.pim_recommended > 0 ? 'warn' : 'muted', tip: 'Permanent assignments flagged for conversion to PIM-eligible.' },
        { count: summary.review_overdue,         label: 'review overdue',     tone: summary.review_overdue > 0 ? 'bad' : 'muted', tip: 'Assignments not reviewed within their access-review cycle (3.1.5/3.1.6).' },
        { count: summary.stale_holders,          label: 'stale holders',      tone: summary.stale_holders > 0 ? 'warn' : 'muted', tip: 'Holders with no sign-in in 90+ days — candidates for revocation.' },
        { count: summary.justification_missing,  label: 'no justification',   tone: summary.justification_missing > 0 ? 'warn' : 'muted', tip: 'Assignments without a recorded business justification.' }
      ]} />
      <div className="cmmc-inv-toolbar">
        <div className="cmmc-inv-filter">
          {[['all','All'],['flags','With flags'],['overdue','Review overdue'],['stale','Stale holder'],['permanent','Permanent only']].map(function(f) {
            return <button key={f[0]} type="button" className={'cmmc-inv-filter-btn' + (filter === f[0] ? ' active' : '')} onClick={function() { setFilter(f[0]); }}>{f[1]}</button>;
          })}
        </div>
        <div className="cmmc-inv-rowcount">{sorted.length} of {rows.length}</div>
      </div>
      <div className="cmmc-inv-table cmmc-inv-privileged">
        <div className="cmmc-inv-row cmmc-inv-head">
          <CMMCSortHeader label="Holder"        sortKey="principal"       currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} />
          <CMMCSortHeader label="Role"          sortKey="role"            currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} />
          <CMMCSortHeader label="Type"          sortKey="assignment_type" currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} />
          <CMMCSortHeader label="Last sign-in"  sortKey="last_signin"     currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} />
          <CMMCSortHeader label="Reviewed"      sortKey="last_reviewed"   currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} />
          <CMMCSortHeader label="Justification" sortKey="justification"   currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} />
          <CMMCSortHeader label="Flags"         sortKey="flag_count"      currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} />
        </div>
        {sorted.length === 0 && <div className="empty-state" style={{ padding: 18 }}>{filter !== 'all' ? 'No assignments match the filter.' : 'No privileged role assignments returned.'}</div>}
        {sorted.map(function(a) {
          return (
            <div key={a.id} className={'cmmc-inv-row' + (a.flag_count > 0 ? ' has-flags' : '') + (a.review_overdue ? ' review-overdue' : '')}>
              <span className="cmmc-inv-person">
                <strong>{a.principal_display_name || a.principal_upn || '—'}</strong>
                <span className="cmmc-inv-upn">{a.principal_upn || '—'}</span>
              </span>
              <span className="cmmc-inv-priv-role"><strong>{a.role_display_name}</strong></span>
              <span className={'cmmc-inv-asg-type cmmc-inv-asg-' + a.assignment_type}>{a.assignment_type}{a.requires_pim && a.assignment_type === 'permanent' && <span className="cmmc-inv-pim-flag" title="Recommended: convert to PIM-eligible">PIM rec.</span>}</span>
              <span className={'cmmc-inv-date' + (a.stale_holder ? ' overdue' : '')}>{a.last_signin_days != null ? (a.last_signin_days === 0 ? 'today' : a.last_signin_days + 'd ago') : <span className="cmmc-inv-missing">never</span>}</span>
              <span className={'cmmc-inv-date' + (a.review_overdue ? ' overdue' : '')}>{a.review_age_days != null ? a.review_age_days + 'd ago' : <span className="cmmc-inv-missing">never</span>}{a.next_review_due && <div className="cmmc-inv-rel">{a.review_overdue ? 'overdue' : 'next ' + a.next_review_due}</div>}</span>
              <span className="cmmc-inv-just">{a.justification_missing ? <span className="cmmc-inv-missing">missing</span> : <span title={a.justification}>{(a.justification || '').slice(0, 80)}{(a.justification || '').length > 80 ? '…' : ''}</span>}</span>
              <span className="cmmc-inv-flags">
                {a.review_overdue && <span className="cmmc-inv-flag cmmc-inv-flag-bad" title="Periodic access review past due">Review</span>}
                {a.stale_holder && <span className="cmmc-inv-flag cmmc-inv-flag-warn" title="Holder hasn't signed in in 90+ days">Stale</span>}
                {a.justification_missing && <span className="cmmc-inv-flag cmmc-inv-flag-warn" title="Business justification not recorded">Justify</span>}
                {a.flag_count === 0 && <span className="cmmc-inv-flag cmmc-inv-flag-ok">Clean</span>}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CMMCSoftwareInventory({ clientId }) {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [err, setErr] = useState(null);
  var [search, setSearch] = useState('');
  var [filter, setFilter] = useState('all');
  var sort = useInventorySort('install_count', 'desc');
  function refetch() {
    setLoading(true); setErr(null);
    authFetch(API + '/clients/' + clientId + '/cmmc/inventory/software')
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) { setLoading(false); if (x.ok) setData(x.d); else setErr(x.d.error || 'Failed to load software inventory'); })
      .catch(function() { setLoading(false); setErr('Failed to load software inventory'); });
  }
  useEffect(refetch, [clientId]);  // eslint-disable-line react-hooks/exhaustive-deps
  if (loading) return <div className="empty-state" style={{ padding: 20 }}><SkeletonLoader /></div>;
  if (err) return <div className="admin-banner error" style={{ margin: 0 }}>{err}</div>;
  var rows = (data && data.software) || [];
  var summary = (data && data.summary) || { total: 0, approved: 0, pending: 0, restricted: 0, blocked: 0, cui_handling: 0, high_risk: 0, recently_introduced: 0, distinct_publishers: 0, total_installs: 0 };
  var lowerSearch = (search || '').toLowerCase().trim();
  var filtered = rows.filter(function(s) {
    if (filter !== 'all' && filter !== 'cui' && filter !== 'risk' && filter !== 'recent' && s.approval_status !== filter) return false;
    if (filter === 'cui' && !s.cui_handling) return false;
    if (filter === 'risk' && ['critical','high'].indexOf(s.risk_score) < 0) return false;
    if (filter === 'recent' && !s.recently_introduced) return false;
    if (!lowerSearch) return true;
    return (s.software_name || '').toLowerCase().includes(lowerSearch) || (s.publisher || '').toLowerCase().includes(lowerSearch);
  });
  function _sortVal(s, key) {
    switch (key) {
      case 'software_name':   return s.software_name;
      case 'publisher':       return s.publisher;
      case 'install_count':   return s.install_count || 0;
      case 'risk_score':      return INV_RISK_RANK[s.risk_score] || 0;
      case 'cui_handling':    return s.cui_handling ? 1 : 0;
      case 'approval_status': return INV_APPROVAL_RANK[s.approval_status] || 0;
      case 'reviewed_at':     return s.reviewed_at;
      case 'first_seen':      return s.first_seen;
      default:                return s[key];
    }
  }
  var sorted = filtered.slice().sort(function(a, b) { return _invCompare(_sortVal(a, sort.sortKey), _sortVal(b, sort.sortKey), sort.sortDir); });
  return (
    <div>
      {data && data.note && <div className="cmmc-inv-note">{data.note}</div>}
      <CMMCInvSummary chips={[
        { count: summary.total,                label: 'titles',         tone: 'muted', tip: 'Distinct software titles in the inventory.' },
        { count: summary.total_installs,       label: 'installs',       tone: 'muted', tip: 'Total install instances across the fleet (sum of per-title counts).' },
        { count: summary.approved,             label: 'approved',       tone: 'ok',    tip: 'Titles cleared for use under the application-control policy.' },
        { count: summary.pending,              label: 'pending review', tone: summary.pending > 0 ? 'warn' : 'muted', tip: 'Titles awaiting CBG security-team review.' },
        { count: summary.restricted,           label: 'restricted',     tone: summary.restricted > 0 ? 'warn' : 'muted', tip: 'Titles approved with conditions (limited users, time-bound, or specific use cases).' },
        { count: summary.blocked,              label: 'blocked',        tone: summary.blocked > 0 ? 'bad' : 'muted', tip: 'Titles flagged for removal — auto-uninstall queued or POA&M opened.' },
        { count: summary.cui_handling,         label: 'CUI-handling',   tone: 'ok',    tip: 'Titles approved for use with CUI workflows.' },
        { count: summary.high_risk,            label: 'high risk',      tone: summary.high_risk > 0 ? 'warn' : 'muted', tip: 'Titles scored high or critical for risk (regardless of approval).' },
        { count: summary.recently_introduced,  label: 'new (30d)',      tone: summary.recently_introduced > 0 ? 'warn' : 'muted', tip: 'Titles first observed in the last 30 days — drift signal worth reviewing.' }
      ]} />
      <div className="cmmc-inv-toolbar">
        <input className="cmmc-inv-search" type="text" placeholder="Search by name or publisher…" value={search} onChange={function(e) { setSearch(e.target.value); }} />
        <div className="cmmc-inv-filter">
          {[['all','All'],['approved','Approved'],['pending','Pending'],['restricted','Restricted'],['blocked','Blocked'],['cui','CUI'],['risk','High risk'],['recent','New 30d']].map(function(f) {
            return <button key={f[0]} type="button" className={'cmmc-inv-filter-btn' + (filter === f[0] ? ' active' : '')} onClick={function() { setFilter(f[0]); }}>{f[1]}</button>;
          })}
        </div>
        <div className="cmmc-inv-rowcount">{sorted.length} of {rows.length}</div>
      </div>
      <div className="cmmc-inv-table cmmc-inv-software">
        <div className="cmmc-inv-row cmmc-inv-head">
          <CMMCSortHeader label="Title"      sortKey="software_name"   currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} />
          <CMMCSortHeader label="Publisher"  sortKey="publisher"       currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} />
          <CMMCSortHeader label="Installs"   sortKey="install_count"   currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} />
          <CMMCSortHeader label="Risk"       sortKey="risk_score"      currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} />
          <CMMCSortHeader label="CUI"        sortKey="cui_handling"    currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} />
          <CMMCSortHeader label="Approval"   sortKey="approval_status" currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} />
          <CMMCSortHeader label="Reviewed"   sortKey="reviewed_at"     currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} />
          <CMMCSortHeader label="First seen" sortKey="first_seen"      currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} />
        </div>
        {sorted.length === 0 && <div className="empty-state" style={{ padding: 18 }}>{lowerSearch || filter !== 'all' ? 'No titles match the filters.' : 'No software annotations on file. The whitelist starts empty until CBG operators add entries.'}</div>}
        {sorted.map(function(s) {
          return (
            <div key={s.id} className={'cmmc-inv-row cmmc-inv-sw-' + s.approval_status + (s.recently_introduced ? ' is-new' : '')}>
              <span className="cmmc-inv-sw-name"><strong>{s.software_name}</strong>{s.source_live && <span className="cmmc-inv-sw-live" title="Discovered live from NinjaOne Gov endpoint inventory">●</span>}{s.version && <span className="cmmc-inv-sw-ver">v{s.version}</span>}</span>
              <span className="cmmc-inv-sw-pub">{s.publisher || '—'}</span>
              <span className="cmmc-inv-sw-installs"><strong>{s.install_count}</strong> <span className="cmmc-inv-rel">{s.install_count === 1 ? 'device' : 'devices'}</span></span>
              <span className={'cmmc-inv-risk cmmc-risk-' + s.risk_score}>{s.risk_score}</span>
              <span className="cmmc-inv-cui">{s.cui_handling ? <span className="cmmc-inv-yes" title="Approved for CUI workflows">●</span> : <span className="cmmc-inv-no" title="Non-CUI">○</span>}</span>
              <span className={'cmmc-inv-approval cmmc-app-' + s.approval_status}>{s.approval_status}</span>
              <span className={'cmmc-inv-date' + (s.review_stale ? ' overdue' : '')}>{s.reviewed_at ? new Date(s.reviewed_at).toLocaleDateString() : <span className="cmmc-inv-missing">never</span>}</span>
              <span className="cmmc-inv-date">{s.first_seen ? new Date(s.first_seen).toLocaleDateString() : '—'}{s.recently_introduced && <div className="cmmc-inv-rel cmmc-inv-rel-new">new {s.first_seen_days}d</div>}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Scoping reports section — versioned system-boundary snapshots. Each report
// captures CUI assets, in-scope / out-of-scope systems, network segments,
// and data flows for a moment in time. CBG-led clients author + complete
// these as the SSP foundation; Atomus-led clients track them as service-
// delivery artifacts that flow into Atomus's SSP.
// ── Aurelius SPA Posture (Phase B.9) ────────────────────────────
// Documents Aurelius's role inside this client's CMMC campaign as a
// Security Protection Asset. Surfaces:
//   • SPA classification statement + peer examples
//   • Boundary statement (what we store / don't store / touch)
//   • Configuration evidence (HTTPS, MFA, audit retention, etc.)
//   • Platform attestations (SOC 2, FedRAMP) with status
//   • Token + scope inventory for THIS client
//   • Active freeze + freeze history
//   • Freeze controls (admin only)
function CMMCSpaPostureSection({ clientId }) {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [err, setErr] = useState(null);
  var [freezeOpen, setFreezeOpen] = useState(false);
  var [freezeReason, setFreezeReason] = useState('');
  var [freezeNotes, setFreezeNotes] = useState('');
  var [unfreezeReason, setUnfreezeReason] = useState('');
  var [busy, setBusy] = useState(false);
  var auditorView = useAuditorView();
  var canAct = isAdminRole() && !IS_CLIENT_VIEW && !auditorView;

  function refetch() {
    setLoading(true); setErr(null);
    authFetch(API + '/clients/' + clientId + '/cmmc/spa-posture')
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) { setLoading(false); if (x.ok) setData(x.d); else setErr(x.d.error || 'Failed to load SPA posture'); })
      .catch(function() { setLoading(false); setErr('Failed to load SPA posture'); });
  }
  useEffect(refetch, [clientId]);  // eslint-disable-line react-hooks/exhaustive-deps

  function startFreeze() {
    if (!freezeReason.trim()) { alert('A reason is required for the freeze (assessor-facing).'); return; }
    if (!window.confirm('Start a freeze window?\n\nAurelius will stop making live API calls against this client. Control state will be served from the cached snapshot until the freeze ends.\n\nReason: ' + freezeReason)) return;
    setBusy(true);
    authFetch(API + '/clients/' + clientId + '/cmmc/spa-freeze', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: freezeReason.trim(), notes: freezeNotes.trim() || null })
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) { setBusy(false); if (x.ok) { setFreezeOpen(false); setFreezeReason(''); setFreezeNotes(''); refetch(); window.location.reload(); } else { alert(x.d.error || 'Freeze failed'); } })
      .catch(function() { setBusy(false); alert('Freeze failed'); });
  }
  function endFreeze() {
    if (!window.confirm('End the active freeze and resume live API calls?')) return;
    setBusy(true);
    authFetch(API + '/clients/' + clientId + '/cmmc/spa-unfreeze', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ended_reason: unfreezeReason.trim() || null })
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) { setBusy(false); if (x.ok) { setUnfreezeReason(''); refetch(); window.location.reload(); } else { alert(x.d.error || 'Unfreeze failed'); } })
      .catch(function() { setBusy(false); alert('Unfreeze failed'); });
  }

  if (loading) return <div className="empty-state" style={{ padding: 20 }}><SkeletonLoader /></div>;
  if (err) return <div className="admin-banner error" style={{ margin: 0 }}>{err}</div>;
  if (!data) return null;

  var attestKey = function(k) {
    var labels = { soc2_type_ii: 'SOC 2 Type II', fedramp_tailored: 'FedRAMP Tailored Low', fedramp_moderate: 'FedRAMP Moderate', iso_27001: 'ISO 27001', cmmc_self_attestation: 'CMMC Self-Attestation' };
    return labels[k] || k;
  };

  function generateEvidencePack() {
    // Open the assessor evidence pack in a new window (HTML response).
    // Same pop-up-blocker fallback pattern as the SSP export — if the
    // window.open call returns null we surface a click-here link.
    var url = API + '/clients/' + clientId + '/cmmc/spa-evidence-pack';
    setBusy(true);
    authFetch(url)
      .then(function(r) { return r.text().then(function(html) { return { ok: r.ok, html: html }; }); })
      .then(function(x) {
        setBusy(false);
        if (!x.ok) { alert('Failed to generate evidence pack'); return; }
        var blob = new Blob([x.html], { type: 'text/html' });
        var blobUrl = URL.createObjectURL(blob);
        var win = window.open(blobUrl, '_blank');
        if (!win) {
          // Pop-up blocked — fall back to download
          var a = document.createElement('a');
          a.href = blobUrl;
          a.download = 'aurelius-spa-evidence-pack-' + clientId + '.html';
          document.body.appendChild(a); a.click(); document.body.removeChild(a);
        }
      })
      .catch(function() { setBusy(false); alert('Failed to generate evidence pack'); });
  }

  return (
    <div>
      <ContentBand
        title="Aurelius compliance posture"
        subtitle="What Aurelius is in this CMMC campaign, what it touches, what it stores, and how it's configured. Designed to be the artifact you hand a C3PAO."
        rightContent={canAct ? <button type="button" className="admin-btn primary" onClick={generateEvidencePack} disabled={busy}>{busy ? '…' : '⬇ Generate evidence pack'}</button> : null}
      >
        <div className="cmmc-spa-classification">
          <div className="cmmc-spa-class-eyebrow">Classification</div>
          <div className="cmmc-spa-class-category">{data.spaClassification.category}</div>
          <div className="cmmc-spa-class-rationale">{data.spaClassification.rationale}</div>
          <div className="cmmc-spa-class-meta">
            <span>Per: <strong>{data.spaClassification.scopingGuide}</strong></span>
            <span>Peer SPAs: {data.spaClassification.peerExamples.join(', ')}</span>
          </div>
        </div>
      </ContentBand>

      {/* Freeze banner / controls */}
      <ContentBand
        title="Freeze for assessment"
        subtitle="Disconnect Aurelius from live API calls during a C3PAO review. Aurelius continues to render last-known state without polling the client environment."
      >
        {data.freeze ? (
          <div className="cmmc-spa-freeze-active">
            <div className="cmmc-spa-freeze-active-head">
              <span className="cmmc-spa-freeze-icon" aria-hidden="true">❄</span>
              <div>
                <div className="cmmc-spa-freeze-title">Freeze active since {new Date(data.freeze.started_at).toLocaleString()}</div>
                <div className="cmmc-spa-freeze-meta">Started by {data.freeze.started_by_email || data.freeze.started_by_name || 'admin'}</div>
              </div>
            </div>
            <div className="cmmc-spa-freeze-reason"><strong>Reason:</strong> {data.freeze.reason}</div>
            {data.freeze.notes && <div className="cmmc-spa-freeze-notes"><strong>Notes:</strong> {data.freeze.notes}</div>}
            {canAct && (
              <div className="cmmc-spa-freeze-end">
                <input
                  type="text"
                  placeholder="End reason (optional, e.g. 'Assessment concluded — resuming continuous monitoring')"
                  value={unfreezeReason}
                  onChange={function(e) { setUnfreezeReason(e.target.value); }}
                />
                <button type="button" className="admin-btn primary" disabled={busy} onClick={endFreeze}>{busy ? '…' : 'End freeze'}</button>
              </div>
            )}
          </div>
        ) : (
          <div className="cmmc-spa-freeze-inactive">
            <div className="cmmc-spa-freeze-inactive-text">
              No active freeze. Aurelius is making continuous live API calls against this client's environment for control evaluation.
            </div>
            {canAct && (
              <div>
                {!freezeOpen ? (
                  <button type="button" className="admin-btn" onClick={function() { setFreezeOpen(true); }}>❄ Start freeze for assessment</button>
                ) : (
                  <div className="cmmc-spa-freeze-form">
                    <input
                      type="text"
                      placeholder="Reason (assessor-facing — e.g. 'C3PAO Phase 1 review week of 2026-06-15')"
                      value={freezeReason}
                      onChange={function(e) { setFreezeReason(e.target.value); }}
                      autoFocus
                    />
                    <textarea
                      rows={2}
                      placeholder="Internal notes (optional)"
                      value={freezeNotes}
                      onChange={function(e) { setFreezeNotes(e.target.value); }}
                    />
                    <div className="cmmc-spa-freeze-form-actions">
                      <button type="button" className="admin-btn" onClick={function() { setFreezeOpen(false); setFreezeReason(''); setFreezeNotes(''); }}>Cancel</button>
                      <button type="button" className="admin-btn primary" disabled={busy || !freezeReason.trim()} onClick={startFreeze}>{busy ? '…' : 'Start freeze'}</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {data.freezeHistory && data.freezeHistory.length > 0 && (
          <div className="cmmc-spa-freeze-history">
            <div className="cmmc-section-label" style={{ marginTop: 12, marginBottom: 6 }}>Freeze history ({data.freezeHistory.length})</div>
            {data.freezeHistory.map(function(h) {
              var durationMs = h.ended_at ? new Date(h.ended_at).getTime() - new Date(h.started_at).getTime() : null;
              var durationLabel = durationMs ? (durationMs < 86400000 ? Math.round(durationMs / 3600000) + 'h' : Math.round(durationMs / 86400000) + 'd') : 'active';
              return (
                <div key={h.id} className={'cmmc-spa-freeze-history-row ' + (h.ended_at ? 'ended' : 'active')}>
                  <span className="cmmc-spa-freeze-history-when">{new Date(h.started_at).toLocaleDateString()}</span>
                  <span className="cmmc-spa-freeze-history-duration">{durationLabel}</span>
                  <span className="cmmc-spa-freeze-history-reason">{h.reason || 'no reason recorded'}</span>
                  <span className="cmmc-spa-freeze-history-by">{h.started_by_email || h.started_by_name || 'admin'}</span>
                </div>
              );
            })}
          </div>
        )}
      </ContentBand>

      <ContentBand
        title="Boundary statement"
        subtitle="What Aurelius stores, what it doesn't, and what it touches in this client's environment."
      >
        <div className="cmmc-spa-boundary-note">{data.boundary.boundaryNote}</div>
        <div className="cmmc-spa-boundary-grid">
          <div className="cmmc-spa-boundary-col cmmc-spa-boundary-stores">
            <div className="cmmc-spa-boundary-head"><span className="cmmc-spa-boundary-icon">●</span> Aurelius stores</div>
            <ul>
              {data.boundary.stores.map(function(s, i) { return <li key={i}>{s}</li>; })}
            </ul>
          </div>
          <div className="cmmc-spa-boundary-col cmmc-spa-boundary-not">
            <div className="cmmc-spa-boundary-head"><span className="cmmc-spa-boundary-icon">○</span> Aurelius does NOT store</div>
            <ul>
              {data.boundary.doesNotStore.map(function(s, i) { return <li key={i}>{s}</li>; })}
            </ul>
          </div>
        </div>
        <div className="cmmc-spa-touches">
          <div className="cmmc-section-label" style={{ marginBottom: 8 }}>Live integrations against this client</div>
          {data.boundary.touches.length === 0 && <div className="empty-state" style={{ padding: 14 }}>No external integrations wired for this client yet.</div>}
          {data.boundary.touches.map(function(t, i) {
            return (
              <div key={i} className="cmmc-spa-touch-row">
                <div className="cmmc-spa-touch-system">{t.system}</div>
                <div className="cmmc-spa-touch-purpose">{t.purpose}</div>
                <div className="cmmc-spa-touch-scope">{t.scope}</div>
              </div>
            );
          })}
        </div>
      </ContentBand>

      <ContentBand
        title="Configuration evidence"
        subtitle="Aurelius's own posture as an SPA. Mirror these in your boundary documentation."
      >
        <div className="cmmc-spa-config-grid">
          {[
            ['HTTPS only',                       data.configuration.https_only],
            ['MFA required for admin',           data.configuration.mfa_admin],
            ['Append-only audit log',            data.configuration.audit_log_append_only],
            ['Audit retention',                  data.configuration.audit_log_retention_days + ' days'],
            ['Secrets encrypted at rest',        data.configuration.secrets_encrypted_at_rest],
            ['Secrets storage',                  data.configuration.secrets_storage],
            ['Session timeout',                  data.configuration.session_timeout_minutes + ' minutes'],
            ['Login rate limit',                 data.configuration.rate_limit_login],
            ['API rate limit',                   data.configuration.rate_limit_api],
            ['Data residency',                   data.configuration.data_residency],
            ['Hosting environment',              data.configuration.hosting_environment],
            ['Code repository',                  data.configuration.code_repository],
            ['Deploy pipeline',                  data.configuration.deploy_pipeline],
            ['Transport',                        data.configuration.transport],
            ['Two-admin principle on critical',  data.configuration.two_admin_principle, data.configuration.two_admin_principle_note]
          ].map(function(row, i) {
            var label = row[0]; var value = row[1]; var note = row[2];
            var displayValue = typeof value === 'boolean' ? (value ? '✓ Enabled' : '✗ Disabled') : value;
            return (
              <div key={i} className={'cmmc-spa-config-row' + (typeof value === 'boolean' && !value ? ' bad' : '')}>
                <span className="cmmc-spa-config-key">{label}</span>
                <span className="cmmc-spa-config-val">{displayValue}</span>
                {note && <span className="cmmc-spa-config-note">{note}</span>}
              </div>
            );
          })}
        </div>
      </ContentBand>

      <ContentBand
        title="Platform attestations"
        subtitle="Aurelius's own compliance trajectory. SPA assessor questions about the platform's hosting + auditing posture map here."
      >
        <div className="cmmc-spa-attest-grid">
          {Object.keys(data.attestations).map(function(k) {
            var a = data.attestations[k];
            var statusClass = 'cmmc-spa-attest-' + (a.status || 'planned');
            return (
              <div key={k} className={'cmmc-spa-attest-row ' + statusClass}>
                <div className="cmmc-spa-attest-name">{attestKey(k)}</div>
                <div className="cmmc-spa-attest-status">{(a.status || '').replace(/_/g, ' ')}</div>
                <div className="cmmc-spa-attest-target">{a.target ? 'Target: ' + a.target : '—'}</div>
                <div className="cmmc-spa-attest-notes">{a.notes}</div>
              </div>
            );
          })}
        </div>
      </ContentBand>

      <ContentBand
        title="Token + scope inventory"
        subtitle="Every credential Aurelius holds for this client. Designed to be assessor-safe (no secret material) but auditor-useful (clear access disclosure)."
      >
        {data.tokenInventory.length === 0 && <div className="empty-state" style={{ padding: 14 }}>No tokens held for this client.</div>}
        {data.tokenInventory.map(function(t, i) {
          return (
            <div key={i} className="cmmc-spa-token-row">
              <div className="cmmc-spa-token-head">
                <strong>{t.integration}</strong>
                <span className="cmmc-spa-token-grant">{t.grant_type}</span>
              </div>
              <div className="cmmc-spa-token-meta">
                {t.consenter && <span><strong>Consenter:</strong> {t.consenter}</span>}
                {t.consented_at && <span><strong>Granted:</strong> {t.consented_at}</span>}
                {t.last_used_label && <span><strong>Last used:</strong> {t.last_used_label}</span>}
              </div>
              {t.scopes && t.scopes.length > 0 && (
                <div className="cmmc-spa-token-scopes">
                  <div className="cmmc-section-label">Read scopes</div>
                  <div className="cmmc-spa-token-scope-list">{t.scopes.map(function(s, j) { return <code key={j}>{s}</code>; })}</div>
                </div>
              )}
              {t.writes && t.writes.length > 0 && (
                <div className="cmmc-spa-token-writes">
                  <div className="cmmc-section-label">Write capabilities</div>
                  <ul>{t.writes.map(function(s, j) { return <li key={j}>{s}</li>; })}</ul>
                </div>
              )}
            </div>
          );
        })}
      </ContentBand>
    </div>
  );
}

// ── CMMC Documentation catalog (Phase B.8) ─────────────────────
// Per-client status of every CMMC artifact a CBG-managed campaign
// needs in its compliance package: policies, SOPs, forms, training.
// Operators flip status as they author/review/approve. Artifacts with
// has_template=true can be generated as printable HTML directly.
var CMMC_DOC_STATUS_LABELS = {
  not_started: 'Not started',
  draft: 'Draft',
  in_review: 'In review',
  approved: 'Approved',
  superseded: 'Superseded',
  archived: 'Archived'
};
var CMMC_DOC_KIND_LABELS = {
  policy: 'Policy',
  sop: 'SOP',
  form: 'Form / Record',
  training: 'Training',
  ssp: 'SSP',
  plan: 'Plan'
};
// Sufficiency labels surface "policy as promise + receipts" — the badge
// tells the operator at a glance whether the live evidence in Aurelius
// actually backs up what the policy claims. Drives both the per-row pill
// and the summary chips. See backend/cmmc-doc-catalog.js#assessSufficiency
// for the scoring rules.
var CMMC_DOC_SUFFICIENCY_LABELS = {
  strong:   'Strong evidence',
  moderate: 'Moderate evidence',
  thin:     'Thin evidence',
  empty:    'No evidence',
  external: 'External evidence'
};
var CMMC_DOC_SUFFICIENCY_SHORT = {
  strong:   'Strong',
  moderate: 'Moderate',
  thin:     'Thin',
  empty:    'Missing',
  external: 'External'
};

// Friendly byte size — used by the attachments panel.
function fmtBytes(n) {
  if (n == null) return '';
  if (n < 1024) return n + ' B';
  if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
  return (n / 1024 / 1024).toFixed(1) + ' MB';
}

// CMMC document attachments panel — renders inline below an artifact row
// when expanded. Two modes:
//   - admin (canAct=true, !isDemo): list + upload + delete
//   - admin on demo client: list-only with a "demo fixtures" note
//   - auditor / client view: list-only (no upload, no delete)
//
// The panel re-fetches on mount + after any mutation. Upload uses
// multipart/form-data so multer on the backend can stream it. Download
// is a direct browser fetch of a 15-min signed Azure SAS URL — bypasses
// our backend bandwidth on the way out.
function CMMCDocAttachments({ clientId, artifactId, canAct, isDemo, onCountChange }) {
  var [list, setList] = useState(null);
  var [loading, setLoading] = useState(true);
  var [err, setErr] = useState(null);
  var [busy, setBusy] = useState(false);
  var [classification, setClassification] = useState('unmarked');
  var [notes, setNotes] = useState('');
  var fileInputRef = useRef(null);

  function refetch() {
    setLoading(true); setErr(null);
    authFetch(API + '/clients/' + clientId + '/cmmc/docs/' + encodeURIComponent(artifactId) + '/attachments')
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setLoading(false);
        if (x.ok) {
          setList(x.d);
          if (onCountChange) onCountChange(x.d.count || 0);
        } else {
          setErr(x.d.error || 'Failed to load attachments');
        }
      })
      .catch(function() { setLoading(false); setErr('Failed to load attachments'); });
  }
  useEffect(refetch, [clientId, artifactId]);  // eslint-disable-line react-hooks/exhaustive-deps

  function handleUpload(e) {
    e.preventDefault();
    var file = fileInputRef.current && fileInputRef.current.files && fileInputRef.current.files[0];
    if (!file) { setErr('Select a file first'); return; }
    setBusy(true); setErr(null);
    var fd = new FormData();
    fd.append('file', file);
    fd.append('classification', classification);
    if (notes.trim()) fd.append('notes', notes.trim());
    authFetch(API + '/clients/' + clientId + '/cmmc/docs/' + encodeURIComponent(artifactId) + '/attachments', {
      method: 'POST', body: fd
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusy(false);
        if (x.ok) {
          if (fileInputRef.current) fileInputRef.current.value = '';
          setNotes(''); setClassification('unmarked');
          refetch();
        } else {
          setErr(x.d.error || 'Upload failed');
        }
      })
      .catch(function() { setBusy(false); setErr('Upload failed'); });
  }

  function handleDelete(id) {
    if (!window.confirm('Remove this attachment from the assessor pack? It will be soft-deleted (recoverable for 7 days).')) return;
    setBusy(true);
    authFetch(API + '/clients/' + clientId + '/cmmc/docs/' + encodeURIComponent(artifactId) + '/attachments/' + id, { method: 'DELETE' })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) { setBusy(false); if (x.ok) refetch(); else alert(x.d.error || 'Delete failed'); })
      .catch(function() { setBusy(false); alert('Delete failed'); });
  }

  if (loading && !list) return <div className="cmmc-docs-attachments"><div className="cmmc-docs-attachments-loading">Loading attachments…</div></div>;

  var attachments = (list && list.attachments) || [];
  var storageConfigured = list ? (list.storage_configured !== false) : true;

  return (
    <div className="cmmc-docs-attachments">
      <div className="cmmc-docs-attachments-list">
        {attachments.length === 0 && (
          <div className="cmmc-docs-attachments-empty">
            No attachments on file yet.
            {canAct && !isDemo && storageConfigured && ' Use the form below to upload signed AUPs, training certs, screenshots, or tabletop notes.'}
          </div>
        )}
        {attachments.map(function(att) {
          var isDemoUrl = att.download_url === '#demo-no-download';
          var classLabel = att.classification === 'cui' ? 'CUI' : att.classification === 'fci' ? 'FCI' : 'Unmarked';
          return (
            <div key={att.id} className="cmmc-docs-attachment-row">
              <span className={'cmmc-docs-att-class cmmc-docs-att-class-' + att.classification} title={'Classification: ' + classLabel}>{classLabel}</span>
              <span className="cmmc-docs-att-name" title={att.filename}>{att.filename}</span>
              <span className="cmmc-docs-att-size">{fmtBytes(att.size_bytes)}</span>
              <span className="cmmc-docs-att-meta">
                {att.uploaded_by_email ? 'by ' + att.uploaded_by_email + ' · ' : ''}
                {att.uploaded_at ? new Date(att.uploaded_at).toLocaleDateString() : ''}
              </span>
              <div className="cmmc-docs-att-actions">
                {att.download_url && !isDemoUrl && (
                  <a href={att.download_url} target="_blank" rel="noopener noreferrer" className="admin-btn sm" title="Opens a 15-min signed download URL">⬇ Download</a>
                )}
                {isDemoUrl && (
                  <span className="cmmc-docs-att-demo-tag" title="Demo attachments are display-only — they don't link to a real blob.">demo · view-only</span>
                )}
                {canAct && !isDemo && (
                  <button type="button" className="admin-btn sm" disabled={busy} onClick={function() { handleDelete(att.id); }}>Delete</button>
                )}
              </div>
              {att.notes && <div className="cmmc-docs-att-notes">{att.notes}</div>}
            </div>
          );
        })}
      </div>

      {canAct && !isDemo && storageConfigured && (
        <form className="cmmc-docs-attachments-upload" onSubmit={handleUpload}>
          <div className="cmmc-docs-attachments-upload-row">
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,image/png,image/jpeg,image/gif,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,text/csv"
              disabled={busy}
            />
            <select value={classification} onChange={function(e) { setClassification(e.target.value); }} disabled={busy} title="Marking the classification helps later when migrating to GCC-H — CUI must move; unmarked can stay on commercial.">
              <option value="unmarked">Unmarked</option>
              <option value="cui">CUI</option>
              <option value="fci">FCI</option>
            </select>
            <button type="submit" className="admin-btn sm primary" disabled={busy}>{busy ? 'Uploading…' : 'Upload'}</button>
          </div>
          <input
            type="text"
            className="cmmc-docs-attachments-notes-input"
            placeholder="Optional notes (e.g. signed by Priya P., DocuSign envelope HEL-2026-PS-001)"
            value={notes}
            onChange={function(e) { setNotes(e.target.value); }}
            disabled={busy}
            maxLength={1000}
          />
          {err && <div className="cmmc-docs-attachments-err">{err}</div>}
          <div className="cmmc-docs-attachments-hint">
            Max 10 MB. PDF / Office docs / images / CSV / TXT. Files are stored in Azure Blob (commercial today; will migrate to GCC-H).
          </div>
        </form>
      )}

      {isDemo && canAct && (
        <div className="cmmc-docs-attachments-demo-note">
          Demo attachments are display-only fixtures. Add a real client to test the upload flow against Azure Blob.
        </div>
      )}
      {!storageConfigured && canAct && !isDemo && (
        <div className="cmmc-docs-attachments-err">Blob storage isn't configured for this environment. Set <code>AZURE_STORAGE_CONNECTION_STRING</code> in App Service config.</div>
      )}
    </div>
  );
}

// AI proposals card on the CMMC tab Overview. Visible affordance for
// the agentic loop: when an AI insight (e.g. on the Compliance tab)
// calls the propose_remediation tool, a row appears here with target
// scope, rationale, and Approve / Reject buttons. Approve flows through
// the existing _runRemediationOps engine. Reject just marks cancelled.
//
// Pattern 8 (visible-affordance) lives or dies on this card — without
// it, the AI's propose_remediation tool would be invisible to operators.
// ── IdentityPostureSection (Commit C) ──────────────────────────────
// Visible affordance for the universal ISPM layer. Renders all 8
// identity-posture findings with their state pills, severity, evidence,
// and an admin-only "Propose" button when a remediation playbook is
// registered. Backend GET endpoint defined in server.js around the
// /identity-posture/eval/run handler.
//
// Sort order: failing first (most urgent), then warn, pass, unknown.
// Within each tier, severity rules: critical → high → medium → low.
//
// has_pending_proposal disables the Propose button to prevent a 409
// from the duplicate-detection guard.
function IdentityPostureSection({ clientId }) {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [err, setErr] = useState(null);
  var [busyId, setBusyId] = useState(null);
  var [toast, setToast] = useState(null);
  var [refreshing, setRefreshing] = useState(false);
  var [drill, setDrill] = useState({}); // findingId -> { open, loading, data, err }
  var [revealTick, setRevealTick] = useState(0); // bumps on each (re)load to replay the scan animation
  var [execId, setExecId] = useState(null); // when set, opens the execution theater modal
  var admin = isAdminRole() && !IS_CLIENT_VIEW;

  function toggleDrill(id) {
    var cur = drill[id];
    if (cur && cur.open) { setDrill(function (d) { return Object.assign({}, d, { [id]: Object.assign({}, cur, { open: false }) }); }); return; }
    if (cur && cur.data) { setDrill(function (d) { return Object.assign({}, d, { [id]: Object.assign({}, cur, { open: true }) }); }); return; }
    setDrill(function (d) { return Object.assign({}, d, { [id]: { open: true, loading: true, data: null, err: null } }); });
    authFetch(API + '/clients/' + clientId + '/identity-posture/' + id + '/entities')
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, j: j }; }); })
      .then(function (x) {
        setDrill(function (d) { return Object.assign({}, d, { [id]: { open: true, loading: false, data: x.ok ? x.j : null, err: x.ok ? null : (x.j.error || 'Failed to load') } }); });
      })
      .catch(function () { setDrill(function (d) { return Object.assign({}, d, { [id]: { open: true, loading: false, data: null, err: 'Failed to load' } }); }); });
  }

  // Findings whose drifted entities can be remediated by a registered playbook
  // on the automation engine (per-entity). Extend as playbooks are added.
  var REM_PLAYBOOK = {
    isp_inactive_enabled_users: { playbook: 'disable-user', label: 'Plan disable' },
    isp_guest_review_overdue:   { playbook: 'disable-user', label: 'Plan disable' },
    isp_disabled_with_licenses: { playbook: 'reclaim-licenses', label: 'Plan reclaim' }
  };
  // Tenant-level (policy) remediations — one action deploys a report-only CA policy.
  var REM_POLICY = {
    isp_legacy_auth_blocked: { playbook: 'deploy-ca-policy', template: 'block-legacy-auth', label: 'Deploy block-legacy-auth policy (report-only) →' },
    isp_ca_baseline_present: { playbook: 'deploy-ca-policy', template: 'require-mfa-all',   label: 'Deploy MFA-baseline policy (report-only) →' }
  };

  // Kick off a DRY-RUN of the mapped playbook for one drifted entity. Dry-run
  // makes no tenant change and needs no consent — it previews the exact steps
  // as an execution the operator approves + applies from the Automation tab.
  function planFix(playbook, upn) {
    if (!window.confirm('Plan a "' + playbook + '" dry-run for ' + upn + '? Nothing changes yet — it queues a preview you approve in the Automation tab to apply.')) return;
    authFetch(API + '/admin/automation/executions', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ playbook: playbook, clientId: clientId, target: { userId: upn, email: upn, displayName: upn }, mode: 'dry_run' })
    })
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, j: j }; }); })
      .then(function (x) {
        if (x.ok) {
          var eid = x.j.id || x.j.execution_id;
          if (eid) setExecId(eid); // open the phased execution theater on the dry-run
          setToast({ tone: 'success', text: 'Dry-run planned for ' + upn + '. Watch it below; approve in the Automation tab to apply for real.' });
        } else setToast({ tone: 'error', text: (x.j && x.j.error) || 'Could not plan fix' });
        setTimeout(function () { setToast(null); }, 8000);
      })
      .catch(function () { setToast({ tone: 'error', text: 'Could not plan fix' }); });
  }

  // Tenant-level policy deploy (report-only CA policy). Same dry-run → theater
  // → approve-in-Automation flow, but the target is the tenant + a template.
  function deployPolicy(playbook, template, title) {
    if (!window.confirm('Plan a REPORT-ONLY "' + title + '" Conditional Access policy?\n\nReport-only logs impact and does NOT enforce — nobody can be locked out. It queues a dry-run; approve in the Automation tab to create it, review the report-only results in Entra, then flip to enforced.')) return;
    authFetch(API + '/admin/automation/executions', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ playbook: playbook, clientId: clientId, target: { tenant: clientId }, params: { template: template }, mode: 'dry_run' })
    })
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, j: j }; }); })
      .then(function (x) {
        if (x.ok) {
          var eid = x.j.id || x.j.execution_id;
          if (eid) setExecId(eid);
          setToast({ tone: 'success', text: 'Report-only policy planned. Watch it below; approve in the Automation tab to create it.' });
        } else setToast({ tone: 'error', text: (x.j && x.j.error) || 'Could not plan policy' });
        setTimeout(function () { setToast(null); }, 8000);
      })
      .catch(function () { setToast({ tone: 'error', text: 'Could not plan policy' }); });
  }

  function fetchPosture() {
    setLoading(true); setErr(null);
    authFetch(API + '/clients/' + clientId + '/identity-posture')
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setLoading(false);
        if (x.ok) { setData(x.d); setRevealTick(function(t) { return t + 1; }); }
        else setErr(x.d.error || 'Failed to load identity posture');
      })
      .catch(function() { setLoading(false); setErr('Failed to load identity posture'); });
  }

  function reEvaluate() {
    setRefreshing(true);
    authFetch(API + '/identity-posture/eval/run', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clientId: clientId })
    })
      .then(function(r) { return r.json(); })
      .then(function() { setRefreshing(false); fetchPosture(); })
      .catch(function() { setRefreshing(false); setToast({ tone: 'error', text: 'Re-evaluation failed' }); });
  }

  function propose(finding) {
    if (!window.confirm('Create AI proposal to remediate "' + finding.title + '" with playbook ' + finding.remediation_playbook + '? It will land on the Proposals card awaiting approval.')) return;
    setBusyId(finding.id);
    authFetch(API + '/clients/' + clientId + '/identity-posture/' + finding.id + '/propose', { method: 'POST' })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusyId(null);
        if (x.ok) setToast({ tone: 'success', text: 'Proposal #' + x.d.proposal_id + ' created — open the ◇ proposals bell in the header to approve.' });
        else setToast({ tone: x.d.proposal_id ? 'warn' : 'error', text: x.d.error || 'Proposal failed' });
        fetchPosture();
        setTimeout(function() { setToast(null); }, 7000);
      })
      .catch(function() { setBusyId(null); setToast({ tone: 'error', text: 'Proposal request failed' }); });
  }

  useEffect(fetchPosture, [clientId]);  // eslint-disable-line react-hooks/exhaustive-deps

  // 412 = Entra not wired. Hide entirely rather than dump an error.
  if (!loading && err && err.indexOf('Entra is not wired') === 0) return null;

  if (loading) {
    return (
      <ContentBand title="Identity Posture" subtitle="Continuous evaluator (15-min cadence)">
        <div className="muted" style={{ padding: 16, fontSize: 12 }}>Loading posture findings…</div>
      </ContentBand>
    );
  }
  if (err) {
    return (
      <ContentBand title="Identity Posture" subtitle="Continuous evaluator (15-min cadence)">
        <div className="admin-banner error" style={{ fontSize: 12 }}>{err}</div>
      </ContentBand>
    );
  }
  if (!data || !data.findings) return null;

  var sevWeight = { critical: 0, high: 1, medium: 2, low: 3 };
  var stateWeight = { fail: 0, warn: 1, unknown: 2, pass: 3 };
  var sortedFindings = data.findings.slice().sort(function(a, b) {
    var sa = stateWeight[a.state] != null ? stateWeight[a.state] : 9;
    var sb = stateWeight[b.state] != null ? stateWeight[b.state] : 9;
    if (sa !== sb) return sa - sb;
    var wa = sevWeight[a.severity] != null ? sevWeight[a.severity] : 9;
    var wb = sevWeight[b.severity] != null ? sevWeight[b.severity] : 9;
    return wa - wb;
  });

  var counts = data.counts || { pass: 0, warn: 0, fail: 0, unknown: 0 };
  var subtitle = (counts.fail || 0) + ' failing · ' + (counts.warn || 0) + ' warn · ' + (counts.pass || 0) + ' pass · ' + (counts.unknown || 0) + ' unknown';

  return (
    <>
    <ContentBand
      title="Identity Posture"
      subtitle={subtitle}
      rightContent={admin && (
        <button type="button" className="admin-btn sm" onClick={reEvaluate} disabled={refreshing}>
          {refreshing ? 'Re-evaluating…' : 'Re-evaluate now'}
        </button>
      )}
    >
      <div className="isp-section-blurb">Continuous identity posture · re-scans every 15 min · click a finding to review affected users/devices and fix in place.</div>
      {toast && <div className={'admin-banner ' + (toast.tone === 'success' ? 'success' : toast.tone === 'warn' ? 'warn' : 'error')} style={{ fontSize: 11, marginBottom: 8 }}>{toast.text}</div>}
      {(function() {
        var total = (counts.pass || 0) + (counts.warn || 0) + (counts.fail || 0) + (counts.unknown || 0);
        if (!total) return null;
        var order = [['fail', counts.fail || 0], ['warn', counts.warn || 0], ['unknown', counts.unknown || 0], ['pass', counts.pass || 0]];
        return (
          <>
            <div className="isp-meter" key={'m' + revealTick}>
              {order.map(function(seg, i) { return seg[1] > 0 ? <div key={seg[0]} className={'isp-meter-seg ' + seg[0]} style={{ width: (seg[1] / total * 100) + '%', animationDelay: (i * 110) + 'ms' }} /> : null; })}
            </div>
            <div className="isp-meter-legend">
              <span><b>{counts.fail || 0}</b> drifted</span>
              <span><b>{counts.warn || 0}</b> warn</span>
              <span><b>{counts.pass || 0}</b> adherent</span>
              <span><b>{counts.unknown || 0}</b> unknown</span>
            </div>
          </>
        );
      })()}
      <div className="isp-finding-list isp-scan-host" key={revealTick}>
        <span className="isp-scan-line" />
        {sortedFindings.map(function(f, idx) {
          var lastChecked = f.last_checked ? new Date(f.last_checked) : null;
          var minsAgo = lastChecked ? Math.max(0, Math.round((Date.now() - lastChecked.getTime()) / 60000)) : null;
          var freshness = lastChecked ? (minsAgo < 60 ? minsAgo + 'm ago' : Math.round(minsAgo / 60) + 'h ago') : 'never';
          var canPropose = admin && f.state === 'fail' && f.remediation_playbook && !f.has_pending_proposal;
          return (
            <div key={f.id} className={'isp-finding isp-reveal isp-finding-state-' + f.state + ' isp-finding-sev-' + f.severity} style={{ animationDelay: (idx * 55) + 'ms' }}>
              <div className="isp-finding-row1">
                <span className={'isp-state-pill isp-state-' + f.state}>{f.state}</span>
                <span className={'isp-sev-pill isp-sev-' + f.severity}>{f.severity}</span>
                <span className="isp-finding-title">{f.title}</span>
                <span className="isp-finding-freshness" title={lastChecked ? lastChecked.toLocaleString() : 'Not yet evaluated'}>{freshness}</span>
              </div>
              {f.reason && <div className="isp-finding-reason">{f.reason}</div>}
              {(function() {
                var e = f.evidence || {};
                var r = (e.inactive_count != null && e.enabled_count != null) ? { bad: e.inactive_count, total: e.enabled_count }
                  : (e.stale_count != null && e.guest_count != null) ? { bad: e.stale_count, total: e.guest_count }
                  : (e.unencrypted_count != null && e.managed_count != null) ? { bad: e.unencrypted_count, total: e.managed_count }
                  : (e.noncompliant_count != null && e.managed_count != null) ? { bad: e.noncompliant_count, total: e.managed_count }
                  : null;
                if (!r || !r.total) return null;
                var pct = Math.min(100, Math.round((r.bad / r.total) * 100));
                return (
                  <div className="isp-card-meter">
                    <div className="isp-card-meter-track"><span className="isp-card-meter-fill" style={{ width: pct + '%' }} /></div>
                    <span className="isp-card-meter-label">{r.bad} of {r.total} affected</span>
                  </div>
                );
              })()}
              <div className="isp-finding-row3">
                <span className="isp-finding-meta">{f.category}</span>
                <div className="isp-finding-actions">
                  {admin && f.state === 'fail' && f.has_pending_proposal && (
                    <span className="isp-pending-pill" title="A remediation is already queued in the Automation tab.">queued</span>
                  )}
                  <button type="button" className={'isp-fix-btn' + ((REM_PLAYBOOK[f.id] || REM_POLICY[f.id]) && f.state === 'fail' ? ' primary' : '')} onClick={function() { toggleDrill(f.id); }}>
                    {drill[f.id] && drill[f.id].open ? 'Close' : ((REM_PLAYBOOK[f.id] || REM_POLICY[f.id]) && f.state === 'fail' ? 'Review & fix →' : 'Inspect →')}
                  </button>
                </div>
              </div>
              {drill[f.id] && drill[f.id].open && (
                <div className="isp-drill">
                  {drill[f.id].loading && <div className="isp-drill-loading">Loading affected entities…</div>}
                  {drill[f.id].err && <div className="admin-banner error" style={{ fontSize: 11 }}>{drill[f.id].err}</div>}
                  {drill[f.id].data && (function() {
                    var dd = drill[f.id].data;
                    return (
                      <>
                        <div className="isp-drill-summary">
                          <span className="isp-drill-count drift">{dd.driftedCount} drifted</span>
                          <span className="isp-drill-count ok">{dd.adherentCount} adherent</span>
                          <span className="isp-drill-level">{dd.level}-level · {dd.total} total</span>
                          {dd.note && <span className="isp-drill-note">{dd.note}</span>}
                        </div>
                        {REM_POLICY[f.id] && f.state === 'fail' && (
                          <div className="isp-drill-policy">
                            <span className="isp-drill-policy-txt">One-click fix — deploys a <strong>report-only</strong> policy (logs impact, no enforcement, no lockout). Approve in Automation to create, then flip to enforced after reviewing.</span>
                            <button type="button" className="isp-fix-btn primary" onClick={function() { deployPolicy(REM_POLICY[f.id].playbook, REM_POLICY[f.id].template, f.title); }}>{REM_POLICY[f.id].label}</button>
                          </div>
                        )}
                        {!REM_PLAYBOOK[f.id] && !REM_POLICY[f.id] && f.state === 'fail' && (
                          <div className="isp-drill-manual">Guided remediation — action the affected items below in Microsoft Entra / Intune.</div>
                        )}
                        {dd.items && dd.items.length > 0 ? (
                          <div className="isp-drill-items">
                            {dd.items.map(function(it, i) {
                              return (
                                <div key={i} className={'isp-drill-item isp-reveal-sm' + (it.drift ? ' drift' : ' ok')} style={{ animationDelay: (i * 28) + 'ms' }}>
                                  <span className="isp-drill-dot" />
                                  <span className="isp-drill-name">{it.name}</span>
                                  <span className="isp-drill-sub">{it.sub}</span>
                                  {admin && it.drift && REM_PLAYBOOK[f.id] && (
                                    <button type="button" className="isp-drill-fix" title="Queue a dry-run of the fix in the automation engine (approve in the Automation tab to apply)" onClick={function() { planFix(REM_PLAYBOOK[f.id].playbook, it.name); }}>{REM_PLAYBOOK[f.id].label}</button>
                                  )}
                                </div>
                              );
                            })}
                            {dd.capped && <div className="isp-drill-capped">Showing first 300 — {dd.total} total.</div>}
                          </div>
                        ) : (
                          <div className="isp-drill-empty">No drifted entities — all {dd.total} adherent.</div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </ContentBand>
    {execId && <AutomationExecutionDetail id={execId} onClose={function() { setExecId(null); }} />}
    </>
  );
}

// Live view — streams the operator's browser as JPEG frames over SSE while a
// run is in flight. Consumed via fetch streaming (so the Bearer token rides a
// header, not the URL). The red click-box is drawn by the worker and shows up
// in the frames.
// Live browser — embeds Steel's interactive WebRTC session view. The admin can
// click and type directly in it (to sign into the tenant); once signed in the
// agent takes over and navigates. `url` is Steel's debugUrl?interactive=true.
function SteelLiveView({ url, signin }) {
  if (!url) {
    return (
      <div style={{ marginBottom: 14 }}>
        <div className="empty-state" style={{ background: 'var(--bg-card)' }}>Booting the cloud browser…</div>
      </div>
    );
  }
  return (
    <div className="agent-live-wrap">
      <div className="agent-live-caption">
        <span style={{ color: 'var(--accent-red)' }}>●</span> Live browser
        {signin
          ? <span className="agent-live-signin"> · sign in to the tenant in the view — the agent continues the moment you’re in</span>
          : <span style={{ marginLeft: 6, color: 'var(--text-muted)' }}> · the agent is driving — you can take over in the view anytime</span>}
      </div>
      <div className={'agent-live-frame' + (signin ? ' signin' : '')}>
        <iframe title="live agent browser" src={url} allow="clipboard-read; clipboard-write" />
      </div>
    </div>
  );
}

// Humanize a run step for the live feed. Browser steps (navigate/click/type)
// read alongside the plain-language "reasoning" step that precedes them.
function stepView(s) {
  var a = (s && s.action) || '';
  if (a === 'graph') return { cls: 'query', pill: 'graph', text: 'Microsoft Graph  →  ' + s.detail };
  if (a === 'think') return { cls: 'agent', pill: 'reasoning', text: s.detail };
  if (a === 'observe') return { cls: 'agent', pill: 'observe', text: s.detail };
  if (a === 'navigate') return { cls: 'query', pill: 'go to', text: s.detail };
  if (a === 'click') return { cls: 'query', pill: 'action', text: 'Click' };
  if (a === 'type_text') return { cls: 'query', pill: 'action', text: 'Type “' + (s.detail || '') + '”' };
  if (a === 'scroll') return { cls: 'query', pill: 'action', text: 'Scroll ' + (s.detail || '') };
  if (a === 'go_back') return { cls: 'query', pill: 'action', text: 'Go back' };
  if (a === 'error') return { cls: 'error', pill: 'error', text: s.detail };
  if (a === 'awaiting sign-in' || a === 'signed in') return { cls: 'agent', pill: 'auth', text: s.detail };
  return { cls: 'agent', pill: a || 'step', text: s.detail || '' };
}

// The open run: two-pane stage (live browser + independently scrolling
// activity feed) while live; answer + scrollable log when finished.
function AgentRunStage({ r, detail }) {
  var feedRef = React.useRef(null);
  var steps = detail.steps || [];
  var working = r.status === 'running' || r.status === 'claimed' || r.status === 'queued' || r.status === 'starting';
  var live = detail.engine !== 'graph' && (r.status === 'starting' || r.status === 'awaiting_signin' || r.status === 'running');
  var signin = r.status === 'awaiting_signin';

  // Keep the feed pinned to the newest step as it streams.
  useEffect(function () {
    var el = feedRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [steps.length, r.status]);

  var feed = (
    <div className="agent-feed" ref={feedRef}>
      {steps.map(function (s, i) {
        var v = stepView(s);
        return (
          <div key={i} className={'agent-step agent-step-' + v.cls}>
            <span className="agent-step-kind">{v.pill}</span>
            <span className="agent-step-text">{v.text}</span>
          </div>
        );
      })}
      {working && <div className="agent-step agent-step-working"><span className="agent-spinner" /> {r.status === 'starting' ? 'booting the browser…' : 'working…'}</div>}
      {!steps.length && !working && <div className="agent-feed-empty">No steps recorded.</div>}
    </div>
  );

  return (
    <div className="agent-run-detail">
      {signin && <div className="admin-banner info agent-signin-banner">Sign in to {r.clientName}’s tenant in the live browser (including MFA). The agent starts the task the moment you’re in.</div>}
      {r.status === 'done' && detail.summary && <div className="agent-answer">{detail.summary}</div>}
      {r.status === 'failed' && detail.error && <div className="admin-banner error" style={{ marginBottom: 12 }}>{detail.error}</div>}
      {live ? (
        <div className="agent-run-stage">
          <div className="agent-stage-live"><SteelLiveView url={detail.liveViewUrl} signin={signin} /></div>
          <div className="agent-stage-feed">
            <div className="agent-feed-label">Live activity</div>
            {feed}
          </div>
        </div>
      ) : (
        <div className="agent-feed-standalone">
          <div className="agent-feed-label">Activity log</div>
          {feed}
        </div>
      )}
    </div>
  );
}

// Agent panel — admin picks a commercial org + task, queues a console-operator
// run, and watches status + the evidence pack. A local worker (operator/worker.js)
// picks up queued runs, drives the browser, and posts screenshots back.
function AgentTab({ clientId, clientName }) {
  var [enabledOrgs, setEnabledOrgs] = useState([]);
  var [task, setTask] = useState('');
  var [submitting, setSubmitting] = useState(false);
  var [runs, setRuns] = useState([]);
  var [loaded, setLoaded] = useState(false);
  var [openId, setOpenId] = useState(null);
  var [detail, setDetail] = useState(null);
  var [banner, setBanner] = useState(null);
  var [engine, setEngine] = useState('graph'); // 'graph' (direct, reliable) | 'steel' (visual browser)
  var [showArchived, setShowArchived] = useState(false);
  var [counts, setCounts] = useState({ active: 0, archived: 0 });

  function when(x) { try { return x ? new Date(x).toLocaleString() : ''; } catch (e) { return x || ''; } }
  function statusText(s) { return s === 'awaiting_signin' ? 'sign in' : s; }

  // The set of orgs where agentic ops are enabled (backend allowlist).
  useEffect(function () {
    authFetch(API + '/agent/orgs').then(function (r) { return r.json(); })
      .then(function (d) { setEnabledOrgs((d && d.orgs) || []); })
      .catch(function () { setEnabledOrgs([]); });
  }, []);
  // Switching org in the top bar collapses any open run from the previous org.
  useEffect(function () { setOpenId(null); setDetail(null); setBanner(null); }, [clientId]);

  var enabled = enabledOrgs.some(function (o) { return o.id === clientId; });
  var enabledNames = enabledOrgs.map(function (o) { return o.name; });

  function fetchRuns() {
    return authFetch(API + '/agent/runs?archived=' + (showArchived ? '1' : '0')).then(function (r) { return r.json(); })
      .then(function (d) { setRuns((d && d.runs) || []); if (d && d.counts) setCounts(d.counts); setLoaded(true); })
      .catch(function () { setLoaded(true); });
  }
  useEffect(fetchRuns, [showArchived]);
  useEffect(function () {
    var active = runs.some(function (r) { return r.status === 'queued' || r.status === 'claimed' || r.status === 'starting' || r.status === 'running' || r.status === 'awaiting_signin'; });
    if (!active) return;
    var t = setInterval(function () {
      fetchRuns();
      if (openId) viewRun(openId, true);
    }, 1500);
    return function () { clearInterval(t); };
  }, [runs, openId, showArchived]);

  function runAgent() {
    if (!clientId || !task.trim()) return;
    setSubmitting(true);
    setBanner(null);
    authFetch(API + '/agent/runs', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ clientId: clientId, task: task.trim(), engine: engine }) })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, d: d }; }); })
      .then(function (x) {
        setSubmitting(false);
        if (x.ok) { setBanner(null); setTask(''); setOpenId(x.d.id); viewRun(x.d.id, true); fetchRuns(); }
        else { setBanner({ tone: 'error', text: (x.d && x.d.error) || 'Failed to start run' }); }
      })
      .catch(function () { setSubmitting(false); setBanner({ tone: 'error', text: 'Failed to queue run' }); });
  }

  function cancelRun(id) {
    authFetch(API + '/agent/runs/' + id + '/cancel', { method: 'POST' })
      .then(function () { fetchRuns(); if (openId) viewRun(openId, true); })
      .catch(function () {});
  }

  function archiveRun(id, archived) {
    authFetch(API + '/agent/runs/' + id + '/archive', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ archived: archived }) })
      .then(function () { if (openId === id) { setOpenId(null); setDetail(null); } fetchRuns(); })
      .catch(function () {});
  }

  function archiveFinished() {
    authFetch(API + '/agent/runs/archive-finished', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ clientId: clientId }) })
      .then(function () { fetchRuns(); })
      .catch(function () {});
  }

  function viewRun(id, silent) {
    if (!silent && openId === id) { setOpenId(null); setDetail(null); return; }
    setOpenId(id);
    if (!silent) setDetail(null);
    authFetch(API + '/agent/runs/' + id).then(function (r) { return r.json(); })
      .then(function (d) { setDetail(d); }).catch(function () {});
  }

  var myRuns = runs.filter(function (r) { return r.clientId === clientId || r.clientId === 'demo-sandbox'; });

  if (!enabled) {
    return (
      <div className="tab-content">
        <TabDescription text={'Agentic console operations — the agent signs into a tenant, navigates it, and captures evidence, all from the cloud.'} />
        <ContentBand title="Agent unavailable for this org">
          <div className="empty-state agent-disabled">
            <div className="agent-disabled-title">Agentic operations aren’t enabled for {clientName || 'this organization'} yet.</div>
            <p>Console automation is turned on for the commercial orgs we’ve onboarded so far{enabledNames.length ? ' — ' + enabledNames.join(', ') : ''}. Regulated (CMMC) tenants stay excluded until our FedRAMP path is in place.</p>
            <p className="agent-disabled-hint">Switch to an enabled org using the selector at the top of the portal to use the agent.</p>
          </div>
        </ContentBand>
      </div>
    );
  }

  return (
    <div className="tab-content">
      <TabDescription text={'Ask the agent about ' + (clientName || 'this org') + '’s Microsoft tenant (read-only). Direct reads it through Microsoft Graph and answers in seconds — the reliable default. Visual browser opens the real admin portal in a live view you sign into once and watch it navigate — best for seeing the work, though Microsoft’s portal can be slow in an automated browser.'} />

      <ContentBand title="Run a task" subtitle="Describe what the agent should do. Direct reads the tenant via Microsoft Graph (fast, reliable); Visual browser drives the real portal in a live view you can watch.">
        <div className="agent-form">
          <div className="agent-form-row">
            <div className="agent-field">
              <label>Mode</label>
              <div className="compliance-subtabs agent-mode">
                <button type="button" className="compliance-subtab active">Read-only</button>
                <button type="button" className="compliance-subtab" disabled title="Making changes from the portal is coming next">Act<span className="agent-soon">soon</span></button>
              </div>
            </div>
            <div className="agent-field">
              <label>Method</label>
              <div className="compliance-subtabs agent-mode">
                <button type="button" className={'compliance-subtab' + (engine === 'graph' ? ' active' : '')} onClick={function () { setEngine('graph'); }} title="Reads the tenant directly via Microsoft Graph — fast and reliable, no sign-in">Direct</button>
                <button type="button" className={'compliance-subtab' + (engine === 'steel' ? ' active' : '')} onClick={function () { setEngine('steel'); }} title="Drives the real admin portal in a live browser — sign in once and watch it navigate">Visual browser</button>
              </div>
            </div>
          </div>
          <div className="agent-field">
            <label>Task</label>
            <textarea className="agent-textarea" rows={3} value={task} placeholder="e.g. capture this org's conditional access policies and MFA coverage"
              onChange={function (e) { setTask(e.target.value); }} />
          </div>
          <div className="agent-actions">
            <button className="admin-btn primary" disabled={submitting || !task.trim()} onClick={runAgent}>
              {submitting ? 'Starting…' : 'Run agent'}
            </button>
            <span className="agent-hint">{engine === 'graph'
              ? 'Direct: reads ' + (clientName || 'the org') + '’s tenant via Microsoft Graph — fast and reliable, no sign-in. Best for getting answers.'
              : 'Visual browser: opens ' + (clientName || 'the org') + '’s real admin portal in a live browser — sign in once, then watch it navigate. Note: Microsoft’s portal can be slow/unreliable in an automated browser.'}</span>
          </div>
        </div>
        {banner && <div className={'admin-banner ' + banner.tone} style={{ marginTop: 14 }}>{banner.text}</div>}
      </ContentBand>

      <ContentBand title="Runs" rightContent={
        <div className="agent-runs-toolbar">
          <button className={'agent-view-tab' + (!showArchived ? ' active' : '')} onClick={function () { setShowArchived(false); }}>Active{counts.active ? ' (' + counts.active + ')' : ''}</button>
          <button className={'agent-view-tab' + (showArchived ? ' active' : '')} onClick={function () { setShowArchived(true); }}>Archived{counts.archived ? ' (' + counts.archived + ')' : ''}</button>
          {!showArchived && myRuns.length > 0 && <button className="tab-drill-link" onClick={archiveFinished}>Archive finished</button>}
          <button className="tab-drill-link" onClick={fetchRuns}>↻ Refresh</button>
        </div>
      }>
        {!loaded ? <SkeletonLoader />
          : myRuns.length === 0 ? <div className="empty-state">{showArchived ? 'No archived runs for ' + (clientName || 'this org') + '.' : 'No runs for ' + (clientName || 'this org') + ' yet — start one above.'}</div>
          : (
            <div className="agent-runs">
              {myRuns.map(function (r) {
                var open = openId === r.id;
                var finished = r.status === 'done' || r.status === 'failed' || r.status === 'cancelled';
                return (
                  <div key={r.id} className="agent-run-card">
                    <div className="agent-run-head">
                      <span className={'agent-status agent-status-' + r.status}>{statusText(r.status)}</span>
                      <span className="agent-run-client">{r.clientName}</span>
                      <span className="agent-run-task">{r.task}</span>
                      {(r.status === 'starting' || r.status === 'awaiting_signin' || r.status === 'running' || r.status === 'queued' || r.status === 'claimed') && (
                        <button className="tab-drill-link agent-stop-btn" onClick={function () { cancelRun(r.id); }}>Stop</button>
                      )}
                      <button className="tab-drill-link" onClick={function () { viewRun(r.id); }}>{open ? 'Hide' : 'View'}</button>
                      {showArchived
                        ? <button className="tab-drill-link" onClick={function () { archiveRun(r.id, false); }}>Restore</button>
                        : (finished && <button className="tab-drill-link" onClick={function () { archiveRun(r.id, true); }}>Archive</button>)}
                    </div>
                    <div className="agent-run-meta">{when(r.createdAt)} · {r.stepCount} steps · {r.evidenceCount} evidence{r.error ? (' · ' + r.error) : ''}</div>

                    {open && detail && detail.id === r.id && <AgentRunStage r={r} detail={detail} />}
                  </div>
                );
              })}
            </div>
          )}
      </ContentBand>
    </div>
  );
}

function CMMCProposalsCard({ clientId, refreshSignal }) {
  var [proposals, setProposals] = useState([]);
  var [loading, setLoading] = useState(true);
  var [err, setErr] = useState(null);
  var [busyId, setBusyId] = useState(null);
  var [toast, setToast] = useState(null);
  var admin = isAdminRole();

  function fetchProposals() {
    setLoading(true); setErr(null);
    authFetch(API + '/clients/' + clientId + '/cmmc/proposals')
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setLoading(false);
        if (x.ok) setProposals(x.d.proposals || []);
        else setErr(x.d.error || 'Failed to load proposals');
      })
      .catch(function() { setLoading(false); setErr('Failed to load proposals'); });
  }
  useEffect(fetchProposals, [clientId, refreshSignal]);  // eslint-disable-line react-hooks/exhaustive-deps

  function approve(proposal) {
    if (!window.confirm('Approve "' + proposal.title + '"? This will execute the playbook against ' + (proposal.target_count || 'auto-resolved') + ' target(s) immediately.')) return;
    setBusyId(proposal.id);
    authFetch(API + '/cmmc/proposals/' + proposal.id + '/approve', { method: 'POST' })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusyId(null);
        if (x.ok) {
          var execMode = x.d.mode || 'completed';
          var summary = execMode === 'executed' ? ('Executed: ' + (x.d.executed ? x.d.executed.length : 0) + ' affected, ' + (x.d.failed ? x.d.failed.length : 0) + ' failed') : (x.d.message || 'Approved');
          setToast({ tone: x.d.failed && x.d.failed.length > 0 ? 'warn' : 'success', text: summary });
        } else {
          setToast({ tone: 'error', text: x.d.error || 'Approval failed' });
        }
        fetchProposals();
        setTimeout(function() { setToast(null); }, 8000);
      })
      .catch(function() { setBusyId(null); setToast({ tone: 'error', text: 'Approval request failed' }); });
  }
  function reject(proposal) {
    var reason = window.prompt('Reject reason (optional):');
    if (reason === null) return;
    setBusyId(proposal.id);
    authFetch(API + '/cmmc/proposals/' + proposal.id + '/reject', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: reason })
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusyId(null);
        setToast({ tone: x.ok ? 'success' : 'error', text: x.ok ? 'Rejected.' : (x.d.error || 'Reject failed') });
        fetchProposals();
        setTimeout(function() { setToast(null); }, 4000);
      })
      .catch(function() { setBusyId(null); setToast({ tone: 'error', text: 'Reject request failed' }); });
  }

  // Hide entirely if no proposals + not loading + no error — keeps the
  // overview clean when the AI hasn't proposed anything (the most
  // common state initially).
  if (!loading && !err && proposals.length === 0) return null;

  return (
    <div className="cmmc-proposals-card">
      <div className="cmmc-proposals-head">
        <div>
          <div className="cmmc-proposals-eyebrow">AI Proposals · awaiting approval</div>
          <div className="cmmc-proposals-title">{proposals.length > 0 ? proposals.length + ' proposal' + (proposals.length === 1 ? '' : 's') : (loading ? 'Loading...' : 'None pending')}</div>
        </div>
        <button type="button" className="admin-btn sm" onClick={fetchProposals} disabled={loading}>↻</button>
      </div>
      {err && <div className="admin-banner error" style={{ fontSize: 11 }}>{err}</div>}
      {toast && <div className={'admin-banner ' + (toast.tone === 'success' ? 'success' : toast.tone === 'warn' ? 'warn' : 'error')} style={{ fontSize: 11 }}>{toast.text}</div>}
      <div className="cmmc-proposals-list">
        {proposals.map(function(p) {
          return (
            <div key={p.id} className={'cmmc-proposal cmmc-proposal-sev-' + (p.severity || 'medium')}>
              <div className="cmmc-proposal-row1">
                <span className={'cmmc-proposal-sev cmmc-proposal-sev-' + (p.severity || 'medium')}>{p.severity || 'medium'}</span>
                <span className="cmmc-proposal-control">{p.control_id || '—'}</span>
                <span className="cmmc-proposal-pill cmmc-proposal-pill-auto">{p.automation || 'manual'}</span>
                <span className="cmmc-proposal-when">{new Date(p.requested_at).toLocaleString()}</span>
              </div>
              <div className="cmmc-proposal-title">{p.title}</div>
              {p.rationale && <div className="cmmc-proposal-rationale">"{p.rationale}"</div>}
              {p.targets && p.targets.length > 0 && (
                <div className="cmmc-proposal-targets">
                  <strong>{p.targets.length}</strong> target{p.targets.length === 1 ? '' : 's'}: {p.targets.slice(0, 4).map(function(t) { return <code key={t} className="cmmc-proposal-target">{t}</code>; })}
                  {p.targets.length > 4 && <span className="muted"> +{p.targets.length - 4} more</span>}
                </div>
              )}
              {admin && (
                <div className="cmmc-proposal-actions">
                  <button type="button" className="admin-btn sm primary" disabled={busyId === p.id} onClick={function() { approve(p); }}>{busyId === p.id ? '...' : 'Approve'}</button>
                  <button type="button" className="admin-btn sm" disabled={busyId === p.id} onClick={function() { reject(p); }}>Reject</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CMMCDocsSection({ clientId }) {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [err, setErr] = useState(null);
  var [kindFilter, setKindFilter] = useState('all');
  var [statusFilter, setStatusFilter] = useState('all');
  // Sufficiency filter is the new "show me what an assessor would flag"
  // lens. 'attention' is a synthetic value that surfaces the
  // approved/in_review + thin/empty intersection — i.e. policies that
  // claim implementation but have no live receipts.
  var [sufficiencyFilter, setSufficiencyFilter] = useState('all');
  var [search, setSearch] = useState('');
  var [busyId, setBusyId] = useState(null);
  // Per-row attachment panel toggle. Single-open at a time keeps the
  // page tidy and avoids running multiple fetches in parallel.
  var [expandedAttachmentsId, setExpandedAttachmentsId] = useState(null);
  // Override map for live counts after upload/delete (so the chip
  // updates without a full /cmmc/docs refetch).
  var [attachmentCountOverrides, setAttachmentCountOverrides] = useState({});
  var auditorView = useAuditorView();
  var canAct = isAdminRole() && !IS_CLIENT_VIEW && !auditorView;
  var isDemo = clientId === 'demo';

  function refetch() {
    setLoading(true); setErr(null);
    authFetch(API + '/clients/' + clientId + '/cmmc/docs')
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) { setLoading(false); if (x.ok) setData(x.d); else setErr(x.d.error || 'Failed to load documentation catalog'); })
      .catch(function() { setLoading(false); setErr('Failed to load documentation catalog'); });
  }
  useEffect(refetch, [clientId]);  // eslint-disable-line react-hooks/exhaustive-deps

  function setStatus(artifactId, status) {
    setBusyId(artifactId);
    authFetch(API + '/clients/' + clientId + '/cmmc/docs/' + encodeURIComponent(artifactId) + '/status', {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: status })
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) { setBusyId(null); if (x.ok) refetch(); else alert(x.d.error || 'Status update failed'); })
      .catch(function() { setBusyId(null); alert('Status update failed'); });
  }

  function generate(artifactId) {
    var url = API + '/clients/' + clientId + '/cmmc/docs/' + encodeURIComponent(artifactId) + '/export';
    setBusyId(artifactId);
    authFetch(url)
      .then(function(r) { return r.text().then(function(html) { return { ok: r.ok, html: html }; }); })
      .then(function(x) {
        setBusyId(null);
        if (!x.ok) { alert('Generate failed'); return; }
        var blob = new Blob([x.html], { type: 'text/html' });
        var blobUrl = URL.createObjectURL(blob);
        var win = window.open(blobUrl, '_blank');
        if (!win) {
          var a = document.createElement('a');
          a.href = blobUrl; a.download = artifactId + '-' + clientId + '.html';
          document.body.appendChild(a); a.click(); document.body.removeChild(a);
        }
        refetch();
      })
      .catch(function() { setBusyId(null); alert('Generate failed'); });
  }

  function generatePack(includeMode) {
    setBusyId('__pack__');
    var url = API + '/clients/' + clientId + '/cmmc/docs/pack?include=' + encodeURIComponent(includeMode || 'tracked');
    authFetch(url)
      .then(function(r) { return r.text().then(function(html) { return { ok: r.ok, html: html }; }); })
      .then(function(x) {
        setBusyId(null);
        if (!x.ok) {
          // Try to surface the JSON error message
          try { var parsed = JSON.parse(x.html); alert(parsed.error || 'Pack generation failed'); }
          catch (e) { alert('Pack generation failed'); }
          return;
        }
        var blob = new Blob([x.html], { type: 'text/html' });
        var blobUrl = URL.createObjectURL(blob);
        var win = window.open(blobUrl, '_blank');
        if (!win) {
          var a = document.createElement('a');
          a.href = blobUrl; a.download = 'compliance-doc-pack-' + clientId + '.html';
          document.body.appendChild(a); a.click(); document.body.removeChild(a);
        }
      })
      .catch(function() { setBusyId(null); alert('Pack generation failed'); });
  }

  if (loading) return <div className="empty-state" style={{ padding: 20 }}><SkeletonLoader /></div>;
  if (err) return <div className="admin-banner error" style={{ margin: 0 }}>{err}</div>;
  if (!data) return null;

  var lowerSearch = (search || '').toLowerCase().trim();
  var filtered = data.artifacts.filter(function(a) {
    if (kindFilter !== 'all' && a.kind !== kindFilter) return false;
    if (statusFilter !== 'all' && a.status !== statusFilter) return false;
    if (sufficiencyFilter !== 'all') {
      var lvl = (a.evidence_sufficiency && a.evidence_sufficiency.level) || 'empty';
      if (sufficiencyFilter === 'attention') {
        var claimsImplemented = a.status === 'approved' || a.status === 'in_review';
        if (!claimsImplemented || (lvl !== 'thin' && lvl !== 'empty')) return false;
      } else if (lvl !== sufficiencyFilter) {
        return false;
      }
    }
    if (!lowerSearch) return true;
    return (a.title || '').toLowerCase().includes(lowerSearch)
        || (a.description || '').toLowerCase().includes(lowerSearch)
        || (a.applicable_controls || []).some(function(c) { return c.toLowerCase().includes(lowerSearch); });
  });

  var s = data.summary;
  return (
    <div>
      <ContentBand
        title="Compliance documentation"
        subtitle="The artifacts your assessor will review during the CMMC engagement. Status reflects per-client progress; Aurelius generates the printable draft for artifacts with a template."
        rightContent={canAct ? (
          <div style={{ display: 'inline-flex', gap: 6 }}>
            <button type="button" className="admin-btn sm" onClick={function() { generatePack('approved'); }} disabled={busyId === '__pack__'} title="Bundle every APPROVED artifact into one printable pack — what you hand the C3PAO.">{busyId === '__pack__' ? '…' : '⬇ Pack (approved)'}</button>
            <button type="button" className="admin-btn sm primary" onClick={function() { generatePack('tracked'); }} disabled={busyId === '__pack__'} title="Bundle every artifact in any state OTHER THAN not_started. Useful for internal review of the in-flight pack.">{busyId === '__pack__' ? '…' : '⬇ Pack (tracked)'}</button>
          </div>
        ) : null}
      >
        <CMMCInvSummary chips={[
          { count: s.total,        label: 'artifacts',      tone: 'muted', tip: 'Total artifacts in the CMMC L2 documentation pack catalog.' },
          { count: s.approved,     label: 'approved',       tone: 'ok',    tip: 'Artifacts with sign-off recorded.' },
          { count: s.in_review,    label: 'in review',      tone: s.in_review > 0 ? 'warn' : 'muted', tip: 'Awaiting reviewer or approver action.' },
          { count: s.draft,        label: 'draft',          tone: s.draft > 0 ? 'warn' : 'muted', tip: 'Authoring in progress.' },
          { count: s.not_started,  label: 'not started',    tone: s.not_started > 0 ? 'bad' : 'muted', tip: 'Catalog entries with no work begun. Aurelius surfaces these as gaps.' },
          { count: s.review_overdue, label: 'review overdue', tone: s.review_overdue > 0 ? 'bad' : 'muted', tip: 'Approved artifacts past their annual review date. CMMC requires annual policy review.' },
          { count: s.with_template, label: 'auto-generatable', tone: 'muted', tip: 'Artifacts where Aurelius produces a printable draft (more templates rolling out).' },
          { count: (s.needs_evidence_attention || 0), label: 'need evidence', tone: (s.needs_evidence_attention || 0) > 0 ? 'bad' : 'muted', tip: 'Approved or in-review artifacts whose live evidence in Aurelius is thin or missing — assessor red flags.' }
        ]} />
        {s.by_sufficiency && (
          <div className="cmmc-docs-suff-summary" title="Evidence-of-implementation profile across the catalog. The badge per artifact reflects the live data Aurelius can show an assessor today.">
            <span className="cmmc-docs-suff-summary-label">Evidence profile:</span>
            {[
              ['strong',   s.by_sufficiency.strong   || 0],
              ['moderate', s.by_sufficiency.moderate || 0],
              ['thin',     s.by_sufficiency.thin     || 0],
              ['empty',    s.by_sufficiency.empty    || 0],
              ['external', s.by_sufficiency.external || 0]
            ].map(function(pair) {
              return (
                <span key={pair[0]} className={'cmmc-docs-suff-mini cmmc-docs-suff-' + pair[0]} title={CMMC_DOC_SUFFICIENCY_LABELS[pair[0]] + ' across the catalog.'}>
                  <span className="cmmc-docs-suff-mini-count">{pair[1]}</span>
                  <span className="cmmc-docs-suff-mini-label">{CMMC_DOC_SUFFICIENCY_SHORT[pair[0]]}</span>
                </span>
              );
            })}
          </div>
        )}
        <div className="cmmc-inv-toolbar">
          <input className="cmmc-inv-search" type="text" placeholder="Search by title, description, or NIST control…" value={search} onChange={function(e) { setSearch(e.target.value); }} />
          <div className="cmmc-inv-filter">
            {[['all','All'],['policy','Policies'],['plan','Plans'],['sop','SOPs'],['form','Forms'],['training','Training']].map(function(f) {
              return <button key={f[0]} type="button" className={'cmmc-inv-filter-btn' + (kindFilter === f[0] ? ' active' : '')} onClick={function() { setKindFilter(f[0]); }}>{f[1]}</button>;
            })}
          </div>
          <div className="cmmc-inv-filter">
            {[['all','Any'],['approved','Approved'],['in_review','In review'],['draft','Draft'],['not_started','Not started']].map(function(f) {
              return <button key={f[0]} type="button" className={'cmmc-inv-filter-btn' + (statusFilter === f[0] ? ' active' : '')} onClick={function() { setStatusFilter(f[0]); }}>{f[1]}</button>;
            })}
          </div>
          <div className="cmmc-inv-filter cmmc-inv-filter-suff" title="Filter by how strong the live evidence is for each artifact's promised implementation.">
            {[['all','All evidence'],['attention','Needs attention'],['strong','Strong'],['moderate','Moderate'],['thin','Thin'],['empty','Missing'],['external','External']].map(function(f) {
              return <button key={f[0]} type="button" className={'cmmc-inv-filter-btn' + (sufficiencyFilter === f[0] ? ' active' : '') + (f[0] === 'attention' ? ' cmmc-docs-suff-attention-btn' : '')} onClick={function() { setSufficiencyFilter(f[0]); }}>{f[1]}</button>;
            })}
          </div>
          <div className="cmmc-inv-rowcount">{filtered.length} of {data.artifacts.length}</div>
        </div>

        <div className="cmmc-docs-list">
          {filtered.length === 0 && <div className="empty-state" style={{ padding: 20 }}>No artifacts match the current filters.</div>}
          {filtered.map(function(a) {
            return (
              <div key={a.id} className={'cmmc-docs-row cmmc-docs-status-' + a.status + (a.review_overdue ? ' review-overdue' : '')}>
                <div className="cmmc-docs-row-head">
                  <span className={'cmmc-docs-kind cmmc-docs-kind-' + a.kind}>{CMMC_DOC_KIND_LABELS[a.kind] || a.kind}</span>
                  <strong className="cmmc-docs-title">{a.title}</strong>
                  <span className={'cmmc-docs-status cmmc-docs-status-pill-' + a.status}>{CMMC_DOC_STATUS_LABELS[a.status] || a.status}</span>
                  {/* Evidence sufficiency badge — translates "policy approved" into
                      "are the receipts there?" The level + tooltip make the gap
                      visible to operators (and assessors in auditor view). */}
                  {a.evidence_sufficiency && (function() {
                    var lvl = a.evidence_sufficiency.level || 'empty';
                    var reasons = a.evidence_sufficiency.reasons || [];
                    var tip = CMMC_DOC_SUFFICIENCY_LABELS[lvl] + (reasons.length ? '\n\n' + reasons.join('\n') : '');
                    var claimsImplemented = a.status === 'approved' || a.status === 'in_review';
                    var attentionFlag = claimsImplemented && (lvl === 'thin' || lvl === 'empty');
                    return (
                      <span
                        className={'cmmc-docs-suff cmmc-docs-suff-' + lvl + (attentionFlag ? ' cmmc-docs-suff-attention' : '')}
                        title={tip}
                      >
                        <span className="cmmc-docs-suff-dot" aria-hidden="true">●</span>
                        <span className="cmmc-docs-suff-text">{CMMC_DOC_SUFFICIENCY_SHORT[lvl]}</span>
                        {attentionFlag && <span className="cmmc-docs-suff-warn" aria-hidden="true">⚠</span>}
                      </span>
                    );
                  })()}
                  {a.review_overdue && <span className="cmmc-docs-overdue">Review overdue</span>}
                </div>
                <div className="cmmc-docs-desc">{a.description}</div>
                <div className="cmmc-docs-meta">
                  {a.applicable_controls && a.applicable_controls.length > 0 && (
                    <span><strong>NIST 800-171:</strong> {a.applicable_controls.join(', ')}</span>
                  )}
                  {a.review_cadence_months && <span><strong>Review cadence:</strong> every {a.review_cadence_months} months</span>}
                  {a.pages_estimate && <span>~{a.pages_estimate} pages</span>}
                  {a.approved_at && <span><strong>Approved:</strong> {new Date(a.approved_at).toLocaleDateString()}{a.approved_by_email && ' by ' + a.approved_by_email}</span>}
                  {a.review_due && <span className={a.review_overdue ? 'cmmc-docs-review-due overdue' : 'cmmc-docs-review-due'}><strong>Review due:</strong> {a.review_due}</span>}
                </div>
                {a.notes && <div className="cmmc-docs-notes">{a.notes}</div>}
                <div className="cmmc-docs-actions">
                  {/* In auditor view: only the APPROVED finished product is viewable.
                      Drafting / generating new versions is an admin authoring action,
                      so the button is hidden + relabeled when an assessor is in the seat. */}
                  {a.has_template && canAct && (
                    <button type="button" className="admin-btn sm primary" disabled={busyId === a.id} onClick={function() { generate(a.id); }}>
                      ⬇ Generate draft
                    </button>
                  )}
                  {a.has_template && !canAct && a.status === 'approved' && (
                    <button type="button" className="admin-btn sm" disabled={busyId === a.id} onClick={function() { generate(a.id); }}>
                      ⬇ View approved
                    </button>
                  )}
                  {!a.has_template && (
                    <span className="cmmc-docs-no-template" title="Template not yet implemented in Aurelius. Authoring is manual today; status tracking still works.">Template coming soon</span>
                  )}
                  {/* Attachments affordance — visible to everyone (auditors
                      need to be able to open the panel and download the
                      receipts). Upload/delete are gated inside the panel. */}
                  {(function() {
                    var liveCount = attachmentCountOverrides[a.id] != null ? attachmentCountOverrides[a.id] : (a.attachments_count || 0);
                    var open = expandedAttachmentsId === a.id;
                    return (
                      <button
                        type="button"
                        className={'admin-btn sm cmmc-docs-attachments-toggle' + (open ? ' active' : '') + (liveCount > 0 ? ' has-attachments' : '')}
                        onClick={function() { setExpandedAttachmentsId(open ? null : a.id); }}
                        title={liveCount > 0 ? liveCount + ' attachment(s) on file' : 'No attachments yet — open to add'}
                      >
                        <span className="cmmc-docs-attachments-toggle-icon">📎</span>
                        <span className="cmmc-docs-attachments-toggle-label">{liveCount > 0 ? liveCount + (liveCount === 1 ? ' file' : ' files') : 'Attachments'}</span>
                      </button>
                    );
                  })()}
                  {canAct && (
                    <select className="cmmc-docs-status-select" value={a.status} disabled={busyId === a.id} onChange={function(e) { setStatus(a.id, e.target.value); }}>
                      <option value="not_started">Not started</option>
                      <option value="draft">Draft</option>
                      <option value="in_review">In review</option>
                      <option value="approved">Approved</option>
                      <option value="superseded">Superseded</option>
                      <option value="archived">Archived</option>
                    </select>
                  )}
                </div>
                {expandedAttachmentsId === a.id && (
                  <CMMCDocAttachments
                    clientId={clientId}
                    artifactId={a.id}
                    canAct={canAct}
                    isDemo={isDemo}
                    onCountChange={function(n) {
                      setAttachmentCountOverrides(function(prev) {
                        var next = Object.assign({}, prev);
                        next[a.id] = n;
                        return next;
                      });
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </ContentBand>
    </div>
  );
}

// ── Compliance Audit Trail (Phase B.6) ──────────────────────────
// Unified event timeline pulling from audit_log + cmmc_drift_log.
// This is the assessor-facing "show me everything that's happened on
// this campaign" surface. Filterable by event type + severity, with
// an expandable detail view per event for full metadata.
//
// Two consumers:
//   1. Section mode (no controlId)        — full client-wide trail at
//      the bottom of the CMMC tab. Default-collapsed; click to expand.
//   2. Per-control mode (controlId set)   — compact 5-row strip inside
//      an expanded control card, showing the recent activity for
//      THAT control. Loaded lazily when the row is opened.

// Event-type → category map for filter chips + tinted row markers.
var AUDIT_EVENT_CATEGORIES = {
  STATE_TRANSITION:                   { label: 'State change',  category: 'drift',       icon: '↻' },
  CMMC_POAM_CREATED:                  { label: 'POA&M created', category: 'poam',        icon: '◇' },
  CMMC_POAM_PROMOTED:                 { label: 'POA&M created', category: 'poam',        icon: '◇' },
  CMMC_POAM_UPDATED:                  { label: 'POA&M edited',  category: 'poam',        icon: '◇' },
  CMMC_POAM_OPEN:                     { label: 'POA&M reopened', category: 'poam',       icon: '◇' },
  CMMC_POAM_IN_PROGRESS:              { label: 'POA&M in progress', category: 'poam',    icon: '◇' },
  CMMC_POAM_CLOSED:                   { label: 'POA&M closed',  category: 'poam',        icon: '◇' },
  CMMC_POAM_DEFERRED:                 { label: 'POA&M deferred', category: 'poam',       icon: '◇' },
  CMMC_REMEDIATION_VIEWED:            { label: 'Runbook viewed', category: 'remediation', icon: '⚡' },
  CMMC_REMEDIATION_EXECUTED:          { label: 'Remediation ran', category: 'remediation', icon: '⚡' },
  CMMC_REMEDIATION_DEFERRED:          { label: 'Remediation deferred', category: 'remediation', icon: '⚡' },
  CMMC_REMEDIATION_FAILED:            { label: 'Remediation failed', category: 'remediation', icon: '⚡' },
  CMMC_REMEDIATION_AWAITING_APPROVAL: { label: 'Approval requested', category: 'approval', icon: '🛡' },
  CMMC_REMEDIATION_APPROVED:          { label: 'Approval granted', category: 'approval', icon: '🛡' },
  CMMC_REMEDIATION_APPROVAL_CANCELLED: { label: 'Approval cancelled', category: 'approval', icon: '🛡' },
  CMMC_REMEDIATION_TICKET_REQUESTED:  { label: 'NinjaOne ticket', category: 'ticket',    icon: '☷' },
  CMMC_VERIFY_AFTER_REMEDIATION:      { label: 'Closure verify',  category: 'remediation', icon: '✓' },
  CMMC_PERSONNEL_UPDATED:             { label: 'Personnel update', category: 'inventory', icon: '◉' },
  CMMC_PRIVILEGED_REVIEW_UPDATED:     { label: 'Privileged review', category: 'inventory', icon: '◉' },
  CMMC_SOFTWARE_ANNOTATED:            { label: 'Software annotation', category: 'inventory', icon: '◉' },
  CMMC_SOFTWARE_REMOVED:              { label: 'Software removed', category: 'inventory', icon: '◉' },
  CMMC_SCOPING_CREATED:               { label: 'Scoping report', category: 'scoping',    icon: '◐' },
  CMMC_SCOPING_COMPLETED:             { label: 'Scoping locked', category: 'scoping',    icon: '◐' },
  CMMC_SCOPING_CANCELLED:             { label: 'Scoping cancelled', category: 'scoping',  icon: '◐' }
};
var AUDIT_CATEGORY_TONES = {
  drift:       { color: '#a78bfa', bg: 'rgba(167,139,250,0.10)' },
  poam:        { color: '#60a5fa', bg: 'rgba(96,165,250,0.10)' },
  remediation: { color: '#f59e0b', bg: 'rgba(245,158,11,0.10)' },
  approval:    { color: '#ef4444', bg: 'rgba(239,68,68,0.10)' },
  ticket:      { color: '#22c55e', bg: 'rgba(34,197,94,0.10)' },
  inventory:   { color: '#38bdf8', bg: 'rgba(56,189,248,0.10)' },
  scoping:     { color: '#a78bfa', bg: 'rgba(167,139,250,0.10)' }
};

// Event-row component — one entry in either the section view or the
// per-control strip. Clickable to reveal raw metadata.
function CMMCAuditEventRow({ event, compact }) {
  var [open, setOpen] = useState(false);
  var cat = AUDIT_EVENT_CATEGORIES[event.event_type] || { label: event.event_type, category: 'drift', icon: '·' };
  var tone = AUDIT_CATEGORY_TONES[cat.category] || AUDIT_CATEGORY_TONES.drift;
  var when = new Date(event.timestamp);
  var nowMs = Date.now();
  var ageMs = nowMs - when.getTime();
  var ago;
  if (ageMs < 60000)            ago = 'just now';
  else if (ageMs < 3600000)     ago = Math.round(ageMs / 60000) + 'm ago';
  else if (ageMs < 86400000)    ago = Math.round(ageMs / 3600000) + 'h ago';
  else if (ageMs < 30 * 86400000) ago = Math.round(ageMs / 86400000) + 'd ago';
  else ago = when.toLocaleDateString();
  var operator = event.operator || {};
  var operatorLabel = operator.display_name || operator.email || 'system';
  return (
    <div className={'cmmc-audit-event' + (compact ? ' compact' : '') + (open ? ' open' : '') + (event.severity === 'WARN' ? ' warn' : '') + (event.severity === 'ERROR' ? ' error' : '')}>
      <button type="button" className="cmmc-audit-event-head" onClick={function() { setOpen(!open); }}>
        <span className="cmmc-audit-icon" style={{ color: tone.color, background: tone.bg }} aria-hidden="true">{cat.icon}</span>
        <span className="cmmc-audit-when" title={when.toLocaleString()}>{ago}</span>
        {!compact && <span className="cmmc-audit-cat" style={{ color: tone.color }}>{cat.label}</span>}
        {!compact && event.control_id && <span className="cmmc-audit-control">NIST {event.control_id}</span>}
        <span className="cmmc-audit-summary">{event.summary}</span>
        {!compact && <span className="cmmc-audit-operator">by <strong>{operatorLabel}</strong></span>}
        <span className="cmmc-audit-toggle">{open ? '▾' : '▸'}</span>
      </button>
      {open && event.metadata && Object.keys(event.metadata).length > 0 && (
        <div className="cmmc-audit-event-detail">
          <div className="cmmc-audit-detail-grid">
            <span><strong>When</strong></span><span>{when.toLocaleString()}</span>
            <span><strong>Event type</strong></span><span><code>{event.event_type}</code></span>
            <span><strong>Severity</strong></span><span>{event.severity}</span>
            {event.control_id && <><span><strong>Control</strong></span><span>NIST 800-171 {event.control_id}</span></>}
            <span><strong>Operator</strong></span><span>{operatorLabel}{operator.email ? ' (' + operator.email + ')' : ''}</span>
          </div>
          <details style={{ marginTop: 8 }}>
            <summary style={{ cursor: 'pointer', fontSize: 11, color: 'var(--text-muted)' }}>Raw metadata</summary>
            <pre className="cmmc-audit-meta-raw">{JSON.stringify(event.metadata, null, 2)}</pre>
          </details>
        </div>
      )}
    </div>
  );
}

// Main client-wide audit trail section. Default-collapsed because the
// list is verbose; admin toggles open. Filter chips narrow by category.
function CMMCAuditTrailSection({ clientId, embedded }) {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(false);
  var [err, setErr] = useState(null);
  // When `embedded` is true (i.e. rendered as its own workspace section),
  // start expanded and don't render the show/hide toggle. Otherwise the
  // section sits collapsed inside a parent until the operator clicks open.
  var [open, setOpen] = useState(!!embedded);
  var [search, setSearch] = useState('');
  var [categoryFilter, setCategoryFilter] = useState('all');
  var [windowDays, setWindowDays] = useState(90);
  function refetch() {
    setLoading(true); setErr(null);
    authFetch(API + '/clients/' + clientId + '/cmmc/audit-trail?days=' + windowDays + '&limit=500')
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) { setLoading(false); if (x.ok) setData(x.d); else setErr(x.d.error || 'Failed to load audit trail'); })
      .catch(function() { setLoading(false); setErr('Failed to load audit trail'); });
  }
  // Lazy-load on expand — and re-fetch when the window changes
  useEffect(function() {
    if (open && (!data || data.windowDays !== windowDays)) refetch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, windowDays]);

  var events = (data && data.events) || [];
  var lowerSearch = (search || '').toLowerCase().trim();
  var filtered = events.filter(function(e) {
    var cat = (AUDIT_EVENT_CATEGORIES[e.event_type] || {}).category || 'other';
    if (categoryFilter !== 'all' && cat !== categoryFilter) return false;
    if (!lowerSearch) return true;
    return (e.summary || '').toLowerCase().includes(lowerSearch)
        || (e.event_type || '').toLowerCase().includes(lowerSearch)
        || (e.control_id || '').toLowerCase().includes(lowerSearch)
        || (e.operator && (e.operator.display_name || '').toLowerCase().includes(lowerSearch))
        || (e.operator && (e.operator.email || '').toLowerCase().includes(lowerSearch));
  });

  var summary = (data && data.summary) || { auditEvents: 0, stateTransitions: 0 };

  return (
    <ContentBand
      title="Audit Trail"
      subtitle="Every CMMC-relevant event Aurelius has captured for this client — admin actions, state transitions, remediation lifecycle. Append-only, attributable, exportable."
      rightContent={
        embedded
          ? <button className="admin-btn sm" onClick={refetch} disabled={loading} title="Re-pull from server">{loading ? '…' : '↻ Refresh'}</button>
          : <button className="admin-btn sm" onClick={function() { setOpen(!open); }}>
              {open ? 'Hide' : 'Show'} {data ? '(' + data.total + ')' : ''}
            </button>
      }
    >
      {open && (
        <div>
          <div className="cmmc-audit-summary-strip">
            <div className="cmmc-audit-summary-chip"><strong>{summary.auditEvents}</strong> admin actions</div>
            <div className="cmmc-audit-summary-chip"><strong>{summary.stateTransitions}</strong> state transitions</div>
            <div style={{ flex: 1 }} />
            <label className="cmmc-audit-window-wrap">
              <span className="cmmc-audit-window-label">Window:</span>
              <select className="cmmc-audit-window" value={windowDays} onChange={function(e) { setWindowDays(parseInt(e.target.value, 10)); }}>
                <option value="30">30 days</option>
                <option value="90">90 days</option>
                <option value="180">180 days</option>
                <option value="365">1 year</option>
                <option value="1095">3 years</option>
              </select>
            </label>
          </div>
          <div className="cmmc-inv-toolbar">
            <input className="cmmc-inv-search" type="text" placeholder="Search events…" value={search} onChange={function(e) { setSearch(e.target.value); }} />
            <div className="cmmc-inv-filter">
              {[['all','All'],['drift','State'],['poam','POA&M'],['remediation','Remediation'],['approval','Approval'],['ticket','Tickets'],['inventory','Inventory'],['scoping','Scoping']].map(function(f) {
                return <button key={f[0]} type="button" className={'cmmc-inv-filter-btn' + (categoryFilter === f[0] ? ' active' : '')} onClick={function() { setCategoryFilter(f[0]); }}>{f[1]}</button>;
              })}
            </div>
            <div className="cmmc-inv-rowcount">{filtered.length} of {events.length}</div>
          </div>
          {err && <div className="admin-banner error">{err}</div>}
          {loading && !data && <div className="empty-state" style={{ padding: 24 }}>Loading…</div>}
          {data && (
            <div className="cmmc-audit-list">
              <div className="cmmc-audit-event cmmc-audit-event-header" aria-hidden="true">
                <div className="cmmc-audit-event-head">
                  <span className="cmmc-audit-icon-col-label" />
                  <span>When</span>
                  <span>Category</span>
                  <span>Control</span>
                  <span>Event</span>
                  <span>Operator</span>
                  <span />
                </div>
              </div>
              {filtered.length === 0 && <div className="empty-state" style={{ padding: 18 }}>{(search || categoryFilter !== 'all') ? 'No events match the filters.' : 'No CMMC events recorded in this window.'}</div>}
              {filtered.map(function(e) { return <CMMCAuditEventRow key={e.id} event={e} />; })}
            </div>
          )}
        </div>
      )}
    </ContentBand>
  );
}

// Per-control mini-history strip — rendered inside an expanded control
// card. Lazy-loads when the row is opened. Caps at 8 events.
function CMMCControlHistoryStrip({ clientId, controlId }) {
  var [events, setEvents] = useState(null);
  var [loading, setLoading] = useState(true);
  useEffect(function() {
    setLoading(true);
    authFetch(API + '/clients/' + clientId + '/cmmc/audit-trail?control_id=' + encodeURIComponent(controlId) + '&days=365&limit=8')
      .then(function(r) { return r.ok ? r.json() : { events: [] }; })
      .then(function(d) { setEvents((d && d.events) || []); setLoading(false); })
      .catch(function() { setEvents([]); setLoading(false); });
  }, [clientId, controlId]);
  if (loading) return <div className="cmmc-control-history-strip"><div className="cmmc-section-label">Recent activity</div><div className="empty-state" style={{ padding: 8, fontSize: 11 }}>Loading…</div></div>;
  if (!events || events.length === 0) return (
    <div className="cmmc-control-history-strip">
      <div className="cmmc-section-label">Recent activity</div>
      <div className="empty-state" style={{ padding: 8, fontSize: 11 }}>No recorded events for this control yet — actions taken from this drawer will populate here.</div>
    </div>
  );
  return (
    <div className="cmmc-control-history-strip">
      <div className="cmmc-section-label">Recent activity ({events.length})</div>
      {events.map(function(e) { return <CMMCAuditEventRow key={e.id} event={e} compact />; })}
    </div>
  );
}

function CMMCScopingSection({ clientId, isCbgLed }) {
  var [reports, setReports] = useState(null);
  var [loading, setLoading] = useState(true);
  var [editing, setEditing] = useState(null);   // {} for new, {id, ...} for edit
  var [toast, setToast] = useState(null);
  var auditorView = useAuditorView();
  var isAdmin = isAdminRole() && !IS_CLIENT_VIEW && !auditorView;

  function showToast(m, t) { setToast({ msg: m, type: t || 'info' }); setTimeout(function() { setToast(null); }, 3500); }

  function fetchReports() {
    setLoading(true);
    authFetch(API + '/clients/' + clientId + '/cmmc/scoping')
      .then(function(r) { return r.ok ? r.json() : { reports: [] }; })
      .then(function(d) { setReports(d.reports || []); setLoading(false); })
      .catch(function() { setLoading(false); });
  }
  useEffect(fetchReports, [clientId]);

  function transitionTo(report, status) {
    var label = status === 'completed' ? 'mark this report as Completed (it becomes immutable and citable in the SSP)' : 'cancel this draft (it stays for audit but is excluded from the SSP)';
    if (!window.confirm('Are you sure you want to ' + label + '?')) return;
    authFetch(API + '/clients/' + clientId + '/cmmc/scoping/' + report.id + '/transition', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: status })
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        if (x.ok) { showToast(status === 'completed' ? 'Report completed' : 'Report cancelled', 'ok'); fetchReports(); }
        else { showToast(x.d.error || 'Transition failed', 'error'); }
      });
  }

  if (loading) return null;       // section is non-blocking; skip while loading rather than skeleton-shifting the whole tab

  return (
    <ContentBand
      title="Scoping Reports"
      subtitle={isCbgLed
        ? 'Versioned system-boundary snapshots — the foundation for the SSP. Each report captures CUI assets, in-scope systems, network segments, and data flows as of its date.'
        : 'Versioned system-boundary snapshots. Atomus owns the canonical SSP; reports here document the operational boundary that flows into their submission.'
      }
      rightContent={isAdmin && <button className="admin-btn primary sm" onClick={function() { setEditing({}); }}>+ New Scoping Report</button>}
    >
      {(!reports || reports.length === 0) ? (
        <div className="empty-state" style={{ padding: 24 }}>
          {isAdmin ? 'No scoping reports yet. Click "+ New Scoping Report" to create the first version.' : 'No scoping reports on file yet.'}
        </div>
      ) : (
        <div className="scoping-list">
          <div className="scoping-list-head">
            <span>Version</span>
            <span>Name</span>
            <span>Status</span>
            <span>Created</span>
            <span>Last updated</span>
            <span style={{ textAlign: 'right' }}>Actions</span>
          </div>
          {reports.map(function(r) {
            var statusCls = 'scoping-status scoping-status-' + r.status;
            return (
              <div key={r.id} className="scoping-row">
                <span className="scoping-version">v{r.version}</span>
                <span className="scoping-name">{r.name}</span>
                <span className={statusCls}>{r.status}</span>
                <span className="scoping-date">{new Date(r.created_at).toLocaleDateString()}</span>
                <span className="scoping-date">{new Date(r.updated_at).toLocaleDateString()}</span>
                <span className="scoping-actions" style={{ textAlign: 'right' }}>
                  {r.status === 'draft' && isAdmin && (
                    <>
                      <button className="admin-btn sm" onClick={function() { setEditing(r); }}>Edit</button>
                      <button className="admin-btn sm" onClick={function() { transitionTo(r, 'completed'); }}>Complete</button>
                      <button className="admin-btn sm" onClick={function() { transitionTo(r, 'cancelled'); }}>Cancel</button>
                    </>
                  )}
                  {r.status !== 'draft' && (
                    <button className="admin-btn sm" onClick={function() { setEditing(r); }}>View</button>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}
      {editing && (
        <CMMCScopingEditor
          clientId={clientId}
          report={editing}
          isAdmin={isAdmin}
          onClose={function() { setEditing(null); }}
          onSaved={function(msg) { setEditing(null); fetchReports(); showToast(msg || 'Saved', 'ok'); }}
          onError={function(msg) { showToast(msg || 'Save failed', 'error'); }}
        />
      )}
      {toast && <div className={'admin-toast ' + (toast.type || 'info')}>{toast.msg}</div>}
    </ContentBand>
  );
}

function CMMCScopingEditor({ clientId, report, isAdmin, onClose, onSaved, onError }) {
  var isNew = !report || !report.id;
  var isReadOnly = !isNew && (report.status !== 'draft' || !isAdmin);
  var [loaded, setLoaded] = useState(isNew ? report : null);
  var [name, setName] = useState((report && report.name) || 'Scoping Report');
  var [content, setContent] = useState((report && report.content) || { cui_assets: [], in_scope_systems: [], out_of_scope_systems: [], network_segments: [], data_flows: [], notes: '' });
  var [saving, setSaving] = useState(false);

  useEffect(function() {
    // Load full content when opening an existing report (list view returns metadata only)
    if (isNew) return;
    authFetch(API + '/clients/' + clientId + '/cmmc/scoping/' + report.id)
      .then(function(r) { return r.json(); })
      .then(function(d) {
        if (d && d.report) {
          setLoaded(d.report);
          setName(d.report.name);
          setContent(d.report.content || content);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report && report.id]);

  function setSection(key, value) {
    setContent(function(prev) { var next = Object.assign({}, prev); next[key] = value; return next; });
  }

  function save() {
    if (!name.trim()) { onError('Name is required'); return; }
    setSaving(true);
    var body = { name: name.trim(), content: content };
    var url = isNew
      ? API + '/clients/' + clientId + '/cmmc/scoping'
      : API + '/clients/' + clientId + '/cmmc/scoping/' + report.id;
    authFetch(url, {
      method: isNew ? 'POST' : 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) { setSaving(false); if (x.ok) onSaved(isNew ? 'Created scoping report' : 'Saved'); else onError(x.d.error || 'Save failed'); })
      .catch(function() { setSaving(false); onError('Save failed'); });
  }

  // Each section is a list of free-form rows {name, description, ...} edited
  // as JSON-array-of-objects. For Phase A we keep this lean — a textarea per
  // section. Phase B will add structured row editors.
  function arrayEditor(label, key, hint) {
    return (
      <div className="admin-form-section" style={{ marginBottom: 12 }}>
        <div className="admin-form-section-title" style={{ marginBottom: 6 }}>{label}</div>
        <div className="admin-field-hint" style={{ marginBottom: 6 }}>{hint}</div>
        <textarea
          className="admin-textarea mono"
          rows={Math.min(10, Math.max(3, ((content[key] || []).length || 0) + 2))}
          value={JSON.stringify(content[key] || [], null, 2)}
          readOnly={isReadOnly}
          onChange={function(e) {
            try { var parsed = JSON.parse(e.target.value); if (Array.isArray(parsed)) setSection(key, parsed); }
            catch (err) { /* user mid-typing — don't blow up */ }
          }}
        />
      </div>
    );
  }

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={function(e) { e.stopPropagation(); }} style={{ maxWidth: 720 }}>
        <div className="admin-modal-header">
          {isNew ? 'New Scoping Report' : (isReadOnly ? 'Scoping Report v' + (loaded ? loaded.version : '?') + ' · ' + (loaded ? loaded.status : '') : 'Edit Scoping Report v' + (loaded ? loaded.version : '?'))}
        </div>
        <div className="admin-modal-body" style={{ maxHeight: '72vh', overflow: 'auto' }}>
          {isReadOnly && (
            <div className="admin-banner info" style={{ marginBottom: 14 }}>
              This report is {loaded && loaded.status} and immutable. Create a new version to make changes.
            </div>
          )}
          <div className="admin-field" style={{ marginBottom: 14 }}>
            <label>Report name</label>
            <input type="text" value={name} onChange={function(e) { setName(e.target.value); }} disabled={isReadOnly} />
          </div>

          {arrayEditor('CUI Assets', 'cui_assets',
            'Things that ARE CUI: documents, drawings, source code with controlled tech, tooling. Each: { name, description, location, owner }.')}
          {arrayEditor('In-Scope Systems', 'in_scope_systems',
            'Systems that touch CUI. Each: { name, description, location, role }.')}
          {arrayEditor('Out-of-Scope Systems', 'out_of_scope_systems',
            'Systems explicitly excluded from the boundary. Document why.')}
          {arrayEditor('Network Segments', 'network_segments',
            'CIDR / VLAN / segments. Each: { name, description, cidr, vlan, contains_cui }.')}
          {arrayEditor('Data Flows', 'data_flows',
            'How CUI moves between systems. Each: { from, to, description, contains_cui }.')}

          <div className="admin-form-section">
            <div className="admin-form-section-title">Notes</div>
            <textarea
              className="admin-textarea"
              rows={4}
              value={content.notes || ''}
              readOnly={isReadOnly}
              onChange={function(e) { setSection('notes', e.target.value); }}
              placeholder="Free-form notes about this scoping snapshot — significant changes since previous version, edge cases, exceptions, etc."
            />
          </div>
        </div>
        <div className="admin-modal-footer">
          <button className="admin-btn" onClick={onClose} disabled={saving}>{isReadOnly ? 'Close' : 'Cancel'}</button>
          {!isReadOnly && (
            <button className="admin-btn primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : (isNew ? 'Create draft' : 'Save changes')}</button>
          )}
        </div>
      </div>
    </div>
  );
}

// CMMC workspace section keys — each maps to a renderable view inside the
// CMMC tab. The order here is the order they render in the section nav.
var CMMC_SECTION_KEYS = ['overview', 'controls', 'poams', 'devices', 'software', 'personnel', 'privileged', 'network', 'locations', 'external', 'change', 'risk', 'baselines', 'maintenance', 'subcontractors', 'reviews', 'visitors', 'vulnerabilities', 'flaws', 'integrations', 'inventories', 'scoping', 'documents', 'ssp', 'audit', 'posture'];

// ── Auditor view mode (Phase B.6 c) ─────────────────────────────
// Read-only inspection mode. When active, every CMMC component hides
// its edit / mutate / execute affordances so the workspace reads as
// pure evidence presentation — what an external assessor would see.
// Today this is an admin-only preview toggle (sessionStorage-backed)
// for dry-running the assessor experience and demoing the platform.
// The future real-auditor flow (separate user role with its own auth
// path) will set the same context and inherit the same hiding.
var CMMCAuditorContext = React.createContext(false);
function useAuditorView() { return React.useContext(CMMCAuditorContext); }

function CMMCTab({ c, clientId, onExit, onOpenCyber }) {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState(null);
  var [activeFamily, setActiveFamily] = useState(null);
  // UX additions: search + per-family collapse in the Controls section to
  // tame the scrolling on families with 30+ controls (AC alone has 22).
  var [controlSearch, setControlSearch] = useState('');
  var [stateFilter, setStateFilter] = useState('all'); // all | fail | unknown | pass
  var [assessFilter, setAssessFilter] = useState('all'); // all | met | in_progress | not_met | not_assessed | inherited (800-171A assessment status)
  var [expandedFamilies, setExpandedFamilies] = useState({});  // { AC: true, ... }
  // Drift quick-glance is folded into the Audit Trail section now; the
  // separate showDrift toggle is removed.

  // Section state — drives which workspace view is rendered. Synced to
  // window.location.hash so deep-links + browser back work for free.
  // Hash format: '#cmmc/<section>'. Falls back to 'overview' if missing
  // or unrecognized.
  function _readSectionFromHash() {
    var h = (typeof window !== 'undefined' && window.location && window.location.hash) || '';
    var m = h.match(/^#cmmc\/([a-z]+)/);
    var k = m ? m[1] : 'overview';
    return CMMC_SECTION_KEYS.indexOf(k) >= 0 ? k : 'overview';
  }
  var [section, setSectionState] = useState(_readSectionFromHash());
  function setSection(key) {
    if (CMMC_SECTION_KEYS.indexOf(key) < 0) key = 'overview';
    setSectionState(key);
    try {
      if (typeof window !== 'undefined') {
        var newHash = '#cmmc/' + key;
        if (window.location.hash !== newHash) {
          window.history.replaceState(null, '', window.location.pathname + window.location.search + newHash);
        }
      }
    } catch (e) { /* hash update is non-critical */ }
  }
  useEffect(function() {
    function onHash() { setSectionState(_readSectionFromHash()); }
    if (typeof window !== 'undefined') window.addEventListener('hashchange', onHash);
    return function() { if (typeof window !== 'undefined') window.removeEventListener('hashchange', onHash); };
  }, []);

  // Lightweight POA&M summary fetch for the section nav badge — done
  // alongside the main /cmmc fetch so the "POA&Ms (5)" count appears
  // before the operator clicks into that section.
  var [poamSummary, setPoamSummary] = useState(null);

  // Auditor view toggle — admin-only preview of the read-only inspection
  // experience. sessionStorage-backed so a refresh keeps the mode
  // (deliberate: an admin who toggled in for a demo doesn't want to
  // re-enter on every page change). Hidden for client-role users since
  // their view is already scoped to read-only-ish via RBAC.
  var [auditorView, setAuditorViewState] = useState(function() {
    try { return sessionStorage.getItem('aurelius_cmmc_auditor_view') === '1'; } catch (e) { return false; }
  });
  function setAuditorView(v) {
    setAuditorViewState(!!v);
    try { sessionStorage.setItem('aurelius_cmmc_auditor_view', v ? '1' : '0'); } catch (e) { /* non-critical */ }
  }

  useEffect(function() {
    setLoading(true);
    setActiveFamily(null);
    authFetch(API + '/clients/' + clientId + '/cmmc')
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setLoading(false);
        if (x.ok) setData(x.d);
        else setError(x.d.error || 'CMMC evaluation failed');
      })
      .catch(function() { setLoading(false); setError('CMMC evaluation failed'); });
    authFetch(API + '/clients/' + clientId + '/cmmc/poam')
      .then(function(r) { return r.ok ? r.json() : { summary: null }; })
      .then(function(d) { setPoamSummary(d.summary || null); })
      .catch(function() { /* non-critical */ });
  }, [clientId]);

  if (loading) return <div className="tab-content"><SkeletonLoader /></div>;
  if (error) return <div className="tab-content"><div className="empty-state" style={{ color: 'var(--accent-red)' }}>{error}</div></div>;
  if (!data || !data.enabled) return <div className="tab-content"><div className="empty-state">CMMC is not enabled for this client.</div></div>;

  var camp = data.campaign || {};
  var totals = data.totals || { total: 0, pass: 0, fail: 0, unknown: 0, coveragePercent: 0 };
  var families = data.families || [];
  var allControls = data.controls || [];
  var visibleControls = activeFamily ? allControls.filter(function(c) { return c.family === activeFamily; }) : allControls;

  // Newest lastChecked timestamp across all controls. Surfaces the
  // continuous-evaluator activity so the operator sees freshness.
  // If staler than 30 min, the indicator turns amber (continuous eval
  // either disabled or stuck — worth checking the audit log).
  var newestEvalAt = (function() {
    var newest = null;
    allControls.forEach(function(c) {
      if (!c.lastChecked) return;
      var t = new Date(c.lastChecked).getTime();
      if (!newest || t > newest) newest = t;
    });
    return newest;
  })();
  var evalFreshnessLabel = (function() {
    if (!newestEvalAt) return null;
    var ageMs = Date.now() - newestEvalAt;
    if (ageMs < 60 * 1000) return 'just now';
    if (ageMs < 3600 * 1000) return Math.round(ageMs / 60000) + 'm ago';
    if (ageMs < 86400 * 1000) return Math.round(ageMs / 3600000) + 'h ago';
    return Math.round(ageMs / 86400000) + 'd ago';
  })();
  var evalFreshnessTone = newestEvalAt && (Date.now() - newestEvalAt) > 30 * 60 * 1000 ? 'warn' : 'ok';

  // Sort controls within selected family: gaps first, then unknowns, then pass
  visibleControls = visibleControls.slice().sort(function(a, b) {
    var rank = { fail: 0, unknown: 1, pass: 2 };
    if (rank[a.state] !== rank[b.state]) return rank[a.state] - rank[b.state];
    return a.id.localeCompare(b.id, undefined, { numeric: true });
  });

  // Program lead drives whether this is a CBG-led campaign (Aurelius is
  // system of record — full SSP, POA&M, scoping authorship) or an
  // Atomus-led campaign (Atomus owns the SSP, Aurelius is the service-
  // delivery view). Default 'atomus' for legacy CMMC clients.
  var programLead = (camp.programLead || 'atomus');
  var isCbgLed = programLead === 'cbg' || programLead === 'hybrid';
  var sprs = data.sprs || { score: 110, conservativeScore: 110, maxScore: 110, deductions: 0, unknownDeductionsIfStrict: 0 };
  // Strict (DoD-methodology) reading is the headline. DoD does NOT have
  // a "not yet evaluated" category — assessors treat absence-of-evidence
  // as not-implemented. Showing the lenient score (Unknown = neutral)
  // as primary was deceptive: a client with 15 of 17 controls unevaluated
  // would see 110/110 and assume they were fine. Now: strict is primary,
  // lenient is shown as the "ceiling if all unknowns verify."
  var primaryScore = (typeof sprs.conservativeScore === 'number') ? sprs.conservativeScore : sprs.score;
  var ceilingScore = sprs.score;
  var sprsPositive = primaryScore >= 88;
  var sprsCaution  = primaryScore >= 0 && primaryScore < 88;

  // Section nav definition. Each section is a renderable view inside the
  // CMMC workspace. SSP is conditionally hidden for atomus-led campaigns
  // (Atomus owns the canonical SSP) and for client-role users (authoring
  // tool, not a viewing surface). Counts surface in the nav where they
  // give the operator immediate signal without clicking.
  var poamCount = poamSummary ? poamSummary.total : null;
  var sectionDefs = [
    { key: 'overview',   label: 'Overview',    badge: null },
    { key: 'controls',   label: 'Controls',    badge: totals.fail > 0 ? { text: totals.fail + ' gap' + (totals.fail === 1 ? '' : 's'), tone: 'bad' } : null },
    { key: 'poams',      label: 'POA&Ms',      badge: poamCount != null ? { text: String(poamCount), tone: 'muted' } : null },
    { key: 'devices',    label: 'Devices',     badge: null },
    { key: 'personnel',  label: 'Personnel',   badge: null },
    { key: 'privileged', label: 'Privileged Access', badge: null },
    { key: 'software',   label: 'Software',    badge: null },
    { key: 'network',    label: 'Network Inventory', badge: null },
    { key: 'locations',  label: 'Locations',   badge: null },
    { key: 'external',   label: 'External Systems', badge: null },
    { key: 'change',     label: 'Change Management', badge: null },
    { key: 'risk',       label: 'Risk Register', badge: null },
    { key: 'baselines',  label: 'Baselines',   badge: null },
    { key: 'maintenance', label: 'Maintenance', badge: null },
    { key: 'subcontractors', label: 'Subcontractors', badge: null },
    { key: 'reviews',    label: 'Quarterly Reviews', badge: null },
    { key: 'visitors',   label: 'Visitors',    badge: null },
    { key: 'vulnerabilities', label: 'Vulnerabilities', badge: null },
    { key: 'flaws',      label: 'Reported Flaws', badge: null },
    { key: 'integrations', label: 'Integrations', badge: null },
    { key: 'inventories', label: 'Inventories', badge: null, hidden: true },
    { key: 'scoping',    label: 'Scoping',     badge: null },
    { key: 'documents',  label: 'Documentation', badge: null },
    { key: 'ssp',        label: 'SSP',         badge: null, hidden: !isCbgLed || IS_CLIENT_VIEW || !isAdminRole() },
    { key: 'audit',      label: 'Audit Trail', badge: null },
    { key: 'posture',    label: 'Aurelius Posture', badge: data.freeze ? { text: 'FROZEN', tone: 'warn' } : null }
  ].filter(function(s) { return !s.hidden; });

  // Defensive: if URL hash points to a hidden section (e.g. someone shares
  // a #cmmc/ssp link to a client-role user), drop back to overview.
  var visibleSection = sectionDefs.some(function(s) { return s.key === section; }) ? section : 'overview';

  // Focused-mode workspace chrome — brand, top bar (client + dual SPRS +
  // auditor toggle + exit) and the domain-grouped left rail. The rail maps
  // the existing workspace sections into the domains an assessor thinks in
  // (Command / Assessment / Environment / Compliance / Oversight); new
  // registers layer into these domains in later slices.
  function cwIcon(name) {
    var P = {
      grid: 'M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z',
      clipboard: 'M6 4h12v17H6zM9 4V3h6v1M9 10h6M9 14h6M9 18h4',
      flag: 'M4 21V4h13l-2 4 2 4H4',
      book: 'M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2zM4 19a2 2 0 0 1 2-2h13',
      layers: 'M12 3l9 5-9 5-9-5 9-5zM3 13l9 5 9-5M3 17l9 5 9-5',
      target: 'M12 3a9 9 0 1 0 9 9M12 8a4 4 0 1 0 4 4M12 12h9',
      file: 'M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8zM14 3v5h5M9 13h6M9 17h6',
      shield: 'M12 3l8 4v5c0 5-3.5 8-8 9-4.5-1-8-4-8-9V7zM9 12l2 2 4-4',
      history: 'M3 12a9 9 0 1 1 3 6.7M3 12V7M3 12h5',
      users: 'M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9.5 3.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7M22 21v-2a4 4 0 0 0-3-3.8',
      key: 'M15.5 3.5a5 5 0 1 0 3.2 8.8L21 14l-1 3 3 1-2 2-2-1-3 1 1-3-2.2-2.2A5 5 0 0 0 15.5 3.5z',
      app: 'M4 5a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1zM4 9h16M8 4.5v4.5',
      device: 'M3 5a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1zM8 20h8M12 16v4',
      net: 'M5 6a2.5 2.5 0 1 0 0-.01M19 6a2.5 2.5 0 1 0 0-.01M12 18a2.5 2.5 0 1 0 0-.01M5 8.5v3a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-3M12 13.5v2',
      plug: 'M9 3v6M15 3v6M6 9h12v3a6 6 0 0 1-12 0zM12 18v3',
      branch: 'M6 3v12M6 18a3 3 0 1 0 .01 0M18 6a3 3 0 1 0 .01 0M18 9v1c0 3-4 4-9 5',
      alert: 'M10.3 4 1.9 18a2 2 0 0 0 1.7 3h16.8a2 2 0 0 0 1.7-3L13.7 4a2 2 0 0 0-3.4 0zM12 9v4M12 17h.01',
      usercheck: 'M14 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M8 3.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7M16 11l2 2 4-4',
      scan: 'M4 8V5a1 1 0 0 1 1-1h3M16 4h3a1 1 0 0 1 1 1v3M20 16v3a1 1 0 0 1-1 1h-3M8 20H5a1 1 0 0 1-1-1v-3M4 12h16',
      bug: 'M8 6a4 4 0 0 1 8 0M7 8h10v6a5 5 0 0 1-10 0zM12 8v12M6 10H3M6 14H3.5M18 10h3M18 14h2.5M6.5 6 5 4M17.5 6 19 4',
      pin: 'M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11zM12 7.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5z',
      cloud: 'M17.5 19a4.5 4.5 0 0 0 .5-8.98A6 6 0 0 0 6.2 9.3 4 4 0 0 0 7 17M8 19h9.5',
      sliders: 'M4 6h10M18 6h2M4 12h2M10 12h10M4 18h8M16 18h4M16 4a2 2 0 1 0 0 4 2 2 0 0 0 0-4M8 10a2 2 0 1 0 0 4 2 2 0 0 0 0-4M14 16a2 2 0 1 0 0 4 2 2 0 0 0 0-4',
      wrench: 'M14.5 5.5a4 4 0 0 0-5.3 4.9L3 16.6 5.4 19l6.2-6.2a4 4 0 0 0 4.9-5.3l-2.4 2.4-2-2z',
      building: 'M4 21V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v17M16 8h3a1 1 0 0 1 1 1v12M8 7h2M8 11h2M8 15h2M13 7h1M13 11h1',
      refresh: 'M21 12a9 9 0 1 1-3-6.7M21 4v5h-5'
    };
    return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d={P[name] || P.grid} /></svg>;
  }
  function renderWorkspaceChrome() {
    var visByKey = {};
    sectionDefs.forEach(function(s) { visByKey[s.key] = s; });
    var domains = [
      ['Command', [['overview', 'Command Center', 'grid']]],
      ['Assessment', [['controls', 'Controls', 'clipboard'], ['poams', 'POA&M', 'flag'], ['ssp', 'System Security Plan', 'book']]],
      ['Environment', [['devices', 'Devices', 'device'], ['software', 'Software', 'app'], ['personnel', 'Personnel', 'users'], ['privileged', 'Privileged Access', 'key'], ['network', 'Network Inventory', 'net'], ['locations', 'Locations', 'pin'], ['external', 'External Systems', 'cloud']]],
      ['Compliance', [['baselines', 'Baselines', 'sliders'], ['change', 'Change Management', 'branch'], ['maintenance', 'Maintenance', 'wrench'], ['documents', 'Policies & Docs', 'file'], ['reviews', 'Quarterly Reviews', 'refresh'], ['risk', 'Risk Register', 'alert'], ['scoping', 'Scoping', 'target'], ['subcontractors', 'Subcontractors', 'building'], ['visitors', 'Visitors', 'usercheck'], ['posture', 'Aurelius Posture', 'shield']]],
      ['Security', [['vulnerabilities', 'Vulnerabilities', 'scan'], ['flaws', 'Reported Flaws', 'bug']]],
      ['Oversight', [['audit', 'Audit Trail', 'history']]],
      ['Fabric', [['integrations', 'Integrations', 'plug']]]
    ];
    var strictTone = primaryScore >= 88 ? 'pos' : primaryScore >= 0 ? 'mid' : 'bad';
    var clientName = (c && (c.name || c.company)) || clientId;
    return (
      <>
        <div className="cmmcw-brand">
          <div className="cmmcw-mark"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l8 4v5c0 5-3.5 8-8 9-4.5-1-8-4-8-9V7z" /></svg></div>
          <div className="cmmcw-wm">Aurelius<small>Compliance</small></div>
        </div>
        <div className="cmmcw-top">
          <div className="cmmcw-client">
            <span className="nm">{clientName}</span>
            <span className="chip">CMMC L{camp.targetLevel || 2}</span>
          </div>
          <div className="cmmcw-spacer" />
          <div className="cmmcw-sprs" title="Strict = DoD machine reading (unevaluated counts as a gap). Assessed = honest 800-171A objective reading. Ceiling = if every unevaluated control verifies.">
            <div className="g"><span className="k">SPRS · Strict</span><span className={'v ' + strictTone}>{primaryScore >= 0 ? '+' : ''}{primaryScore}</span></div>
            {data.assessment && data.assessment.total > 0 && typeof sprs.assessedScore === 'number' && (
              <><div className="sep" /><div className="g"><span className="k">Assessed</span><span className="v info">{sprs.assessedScore >= 0 ? '+' : ''}{sprs.assessedScore}</span></div></>
            )}
            <div className="sep" /><div className="g"><span className="k">Ceiling</span><span className="v pos">+{ceilingScore}</span></div>
          </div>
          {!IS_CLIENT_VIEW && isAdminRole() && (
            <button type="button" className={'cmmcw-toggle' + (auditorView ? ' on' : '')} onClick={function() { setAuditorView(!auditorView); }} aria-pressed={auditorView}
              title={auditorView ? 'In auditor view — click to restore edit affordances' : 'Preview the read-only assessor experience'}>
              {auditorView ? 'Auditor view ON' : 'Auditor view'}
            </button>
          )}
          <button type="button" className="cmmcw-exit" onClick={function() { if (typeof onExit === 'function') onExit(); }} title="Leave the compliance workspace">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" /></svg>
            Exit
          </button>
        </div>
        <nav className="cmmcw-rail" aria-label="CMMC workspace">
          {domains.map(function(dom) {
            var items = dom[1].filter(function(it) { return visByKey[it[0]]; });
            if (!items.length) return null;
            return (
              <div className="cmmcw-sec" key={dom[0]}>
                <div className="cmmcw-lbl">{dom[0]}<span className="ln" /></div>
                {items.map(function(it) {
                  var badge = visByKey[it[0]].badge;
                  return (
                    <button key={it[0]} type="button" className={'cmmcw-nav' + (visibleSection === it[0] ? ' on' : '')}
                      aria-current={visibleSection === it[0] ? 'page' : undefined} onClick={function() { setSection(it[0]); }}>
                      {cwIcon(it[2])}
                      <span className="lab">{it[1]}</span>
                      {badge && <span className={'cnt ' + (badge.tone === 'bad' ? 'bad' : badge.tone === 'warn' ? 'warn' : '')}>{badge.text}</span>}
                    </button>
                  );
                })}
              </div>
            );
          })}
          {typeof onOpenCyber === 'function' && (
            <div className="cmmcw-sec">
              <div className="cmmcw-lbl">Adjacent<span className="ln" /></div>
              <button type="button" className="cmmcw-nav" onClick={onOpenCyber} title="Cyber-insurance readiness for this client">
                {cwIcon('shield')}<span className="lab">Cyber Insurance</span>
              </button>
            </div>
          )}
        </nav>
      </>
    );
  }

  // Render the controls-list section. Pulled out of the JSX into a function
  // because it's a sizeable IIFE that clarifies a lot when it's named.
  function renderControlsSection() {
    var refetch = function() {
      setLoading(true);
      authFetch(API + '/clients/' + clientId + '/cmmc')
        .then(function(r) { return r.json(); })
        .then(function(d) { setData(d); setLoading(false); });
    };
    var lowerSearch = (controlSearch || '').toLowerCase().trim();
    function asOf(ctl) { return ctl.assessmentStatus || 'not_assessed'; }
    function isInherited(ctl) { return (ctl.objectives || []).some(function(o) { return o.source && String(o.source).indexOf('inherited') === 0; }); }
    var base = visibleControls;   // respects activeFamily scoping (set from the Command Center)
    var filtered = base.filter(function(ctl) {
      if (lowerSearch && !((ctl.id || '').toLowerCase().includes(lowerSearch) || (ctl.title || '').toLowerCase().includes(lowerSearch) || (ctl.statement || '').toLowerCase().includes(lowerSearch))) return false;
      if (assessFilter === 'all') return true;
      if (assessFilter === 'inherited') return isInherited(ctl);
      return asOf(ctl) === assessFilter;
    });
    var rank = { not_met: 0, in_progress: 1, not_assessed: 2, met: 3 };
    filtered = filtered.slice().sort(function(a, b) {
      var ra = rank[asOf(a)] != null ? rank[asOf(a)] : 2, rb = rank[asOf(b)] != null ? rank[asOf(b)] : 2;
      if (ra !== rb) return ra - rb;
      return a.id.localeCompare(b.id, undefined, { numeric: true });
    });
    var segs = [
      ['all', 'All', base.length],
      ['not_met', 'Not met', base.filter(function(c) { return asOf(c) === 'not_met'; }).length],
      ['in_progress', 'In progress', base.filter(function(c) { return asOf(c) === 'in_progress'; }).length],
      ['inherited', 'Inherited', base.filter(isInherited).length],
      ['met', 'Evidenced', base.filter(function(c) { return asOf(c) === 'met'; }).length],
      ['not_assessed', 'Not assessed', base.filter(function(c) { return asOf(c) === 'not_assessed'; }).length]
    ];
    return (
      <>
        <div className="cmmcw-vhead">
          <div>
            <div className="cmmcw-eye">Assessment · NIST SP 800-171A</div>
            <h2 className="cmmcw-h1">Controls</h2>
            <p className="cmmcw-sub">Every control assessed at the determination-statement level. A control is Met only when all of its objectives are Met, N/A, or inherited.{activeFamily ? ' Scoped to ' + activeFamily + '.' : ''}</p>
          </div>
          <div className="cmmcw-acts">
            {activeFamily && <button type="button" className="cmmcw-btn" onClick={function() { setActiveFamily(null); }}>Show all families</button>}
            {!IS_CLIENT_VIEW && isAdminRole() && <button type="button" className="cmmcw-btn" onClick={refetch}>Re-evaluate</button>}
          </div>
        </div>
        {data.enclave && data.enclave.vendor && (function() {
          var encLabel = data.enclave.label || data.enclave.vendor;
          var live = !!data.enclave.live;               // backend: operational AND model known
          var cov = data.enclave.coverage;              // { full, shared, total } from the vendor SRM
          var modelLbl = data.enclave.modelLabel;
          var tgt = data.enclave.target_date ? new Date(data.enclave.target_date + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : null;
          var needsModel = data.enclave.status === 'operational' && !data.enclave.model;
          return (
            <div className={'cmmcw-enclave' + (live ? '' : ' planned')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l9 5-9 5-9-5 9-5zM3 13l9 5 9-5M3 17l9 5 9-5" /></svg>
              {live ? (
                <span><b>CUI enclave: {encLabel}{modelLbl ? ' · ' + modelLbl : ''} · live.</b> Per {encLabel}'s Shared Responsibility Matrix{cov ? ', ' : ' '}
                  {cov && <><b>{cov.full}</b> control{cov.full === 1 ? '' : 's'} inherited and <b>{cov.shared}</b> shared</>}. Inherited controls are implemented by the enclave; shared controls remain your assessment (the split shows on each control).</span>
              ) : needsModel ? (
                <span><b>CUI enclave: {encLabel} · deployment model not set.</b> Set the enclave model (Customer-computer vs EMSD VDI) to apply {encLabel}'s Shared Responsibility Matrix — no coverage is credited until the model is a confirmed fact.</span>
              ) : (
                <span><b>CUI enclave: {encLabel} · planned{tgt ? ' (' + tgt + ')' : ''}.</b> {encLabel} isn’t integrated with this tenant yet, so its controls still reflect the <b>corporate-tenant assessment</b> — no inheritance credit. They switch to per-SRM <b>Inherited / Shared</b> once {encLabel} is operational and its model is confirmed.</span>
              )}
            </div>
          );
        })()}
        <div className="cmmcw-tbar">
          <div className="cmmcw-segs">
            {segs.map(function(s) {
              return <button key={s[0]} type="button" className={'cmmcw-seg' + (assessFilter === s[0] ? ' on' : '')} onClick={function() { setAssessFilter(s[0]); }}>{s[1]} <span className="c">{s[2]}</span></button>;
            })}
          </div>
          <div className="cmmcw-search-wrap">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="m20 20-3-3" /></svg>
            <input className="cmmcw-search-in" placeholder="Search controls…" value={controlSearch} onChange={function(e) { setControlSearch(e.target.value); }} />
          </div>
        </div>
        <div className="cmmcw-ctrl-list">
          {filtered.map(function(ctl) { return <CMMCControlRow key={ctl.id} ctl={ctl} clientId={clientId} onRemediated={refetch} programLead={programLead} />; })}
          {filtered.length === 0 && <div className="cmmcw-empty">No controls match this filter.</div>}
        </div>
      </>
    );
  }

  // Command Center — the workspace landing screen: dual-SPRS score hero,
  // family readiness, headline stats, and the continuous-eval activity feed.
  function renderOverviewSection() {
    function relTime(ts) {
      if (!ts) return '';
      var ms = Date.now() - new Date(ts).getTime();
      if (ms < 3600000) return Math.max(1, Math.round(ms / 60000)) + 'm';
      if (ms < 86400000) return Math.round(ms / 3600000) + 'h';
      return Math.round(ms / 86400000) + 'd';
    }
    var STAGE_LABEL = { gap_assessment: 'Gap assessment', remediation: 'Remediation', pre_assessment: 'Pre-assessment', assessment: 'Assessment', certified: 'Certified', maintenance: 'Maintenance' };
    function prettyStage(s) { return STAGE_LABEL[s] || (s ? String(s).replace(/_/g, ' ') : '—'); }
    var asmt = data.assessment;
    return (
      <>
        <div className="cmmcw-cc-grid">
          <div className="cmmcw-score">
            <div className="cmmcw-score-lab">CMMC Level {camp.targetLevel || 2} Campaign · SPRS (strict)</div>
            <div className={'cmmcw-score-big' + (primaryScore >= 88 ? ' pos' : primaryScore >= 0 ? '' : ' bad')} title="Strict DoD reading: every unevaluated control counts as not-implemented.">
              {primaryScore >= 0 ? '+' : ''}{primaryScore}<span> / 110</span>
            </div>
            <div className="cmmcw-score-row">
              <div className="m"><div className="k">Evidenced</div><div className="n ok">{totals.pass}</div></div>
              <div className="m"><div className="k">Gaps</div><div className="n bad">{totals.fail}</div></div>
              <div className="m"><div className="k">{isCbgLed ? 'Verify / manual' : 'Verify w/ Aegis'}</div><div className="n warn">{totals.unknown}</div></div>
              {totals.inherited > 0 && <div className="m"><div className="k">Inherited</div><div className="n inh">{totals.inherited}</div></div>}
              {asmt && asmt.total > 0 && typeof sprs.assessedScore === 'number' && (
                <div className="m"><div className="k">Assessed · 800-171A</div><div className="n info">{sprs.assessedScore >= 0 ? '+' : ''}{sprs.assessedScore}</div></div>
              )}
            </div>
            <div className="cmmcw-score-note">
              Strict counts every unevaluated control as not-implemented (DoD methodology). Ceiling <b style={{ color: 'var(--cw-ok)' }}>+{ceilingScore}</b> if all {totals.unknown} unevaluated controls verify.
              {asmt && asmt.total > 0 && <> The <b>assessed</b> score reflects only objectives graded Met ({asmt.met}/{asmt.total}) — it rises as evidence is attested and inherited controls are confirmed.</>}
            </div>
            <div className="cmmcw-meta">
              <div className="dotpill"><i style={isCbgLed ? {} : { background: '#a78bfa', boxShadow: '0 0 0 4px rgba(167,139,250,0.18)' }} /><span className="g"><span className="k">Managed by</span><span className="v">{isCbgLed ? 'CloudByte Group' : 'Atomus' + (camp.aegisActive ? ' · Aegis' : '')}</span></span></div>
              {camp.stage && <div className="g"><span className="k">Stage</span><span className="v">{prettyStage(camp.stage)}</span></div>}
              {camp.targetAssessmentDate && <div className="g"><span className="k">Target assessment</span><span className="v">{new Date(camp.targetAssessmentDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}{camp.daysToAssessment != null && camp.daysToAssessment > 0 ? ' · ' + camp.daysToAssessment + 'd' : ''}</span></div>}
              {camp.c3pao && <div className="g"><span className="k">C3PAO</span><span className="v">{camp.c3pao}</span></div>}
              {data.enclave && data.enclave.vendor && <div className="g"><span className="k">CUI enclave</span><span className="v" style={{ color: data.enclave.live ? 'var(--cw-ok)' : '#e0a54a' }}>{(data.enclave.label || data.enclave.vendor) + (data.enclave.live ? (data.enclave.coverage ? ' · ' + data.enclave.coverage.full + ' inherited / ' + data.enclave.coverage.shared + ' shared' : ' · live') : ' · planned')}</span></div>}
              {evalFreshnessLabel && <div className="g"><span className="k">Evaluated</span><span className="v" style={{ color: evalFreshnessTone === 'warn' ? 'var(--cw-warn)' : 'var(--cw-ok)' }}>{evalFreshnessLabel}</span></div>}
            </div>
          </div>

          <div className="cmmcw-card">
            <div className="cmmcw-card-h"><span className="t">Family readiness</span><span className="s">evidenced coverage</span></div>
            <div>
              {families.map(function(f) {
                var covered = f.pass + (f.inherited || 0);   // inherited = covered via the enclave
                var pct = f.total > 0 ? Math.round((covered / f.total) * 100) : 0;
                var col = pct >= 80 ? 'var(--cw-ok)' : pct >= 50 ? 'var(--cw-warn)' : 'var(--cw-bad)';
                return (
                  <button type="button" className="cmmcw-fam" key={f.code} onClick={function() { setActiveFamily(f.code); setSection('controls'); }}>
                    <span className="code">{f.code}</span>
                    <span className="nm">{f.name}<span className="sub">{f.total}</span></span>
                    <span className="r"><span className="bar"><i style={{ width: pct + '%', background: col }} /></span><span className="pct">{pct}%</span></span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="cmmcw-stats">
          <button type="button" className="cmmcw-stat" onClick={function() { setSection('controls'); }}>
            <div className="h"><span>Controls fully met</span></div>
            <div className="n">{asmt ? asmt.controlsMet : totals.pass}<small> / {totals.total}</small></div>
            <div className="s">800-171A assessor-grade</div>
          </button>
          <button type="button" className="cmmcw-stat" onClick={function() { setSection('controls'); }}>
            <div className="h"><span>Objectives graded</span></div>
            <div className="n info">{asmt ? asmt.met : 0}<small> / {asmt ? asmt.total : 0}</small></div>
            <div className="s">{asmt && asmt.total ? Math.round(asmt.met / asmt.total * 100) : 0}% assessed</div>
          </button>
          <button type="button" className="cmmcw-stat" onClick={function() { setSection('controls'); }}>
            <div className="h"><span>Open gaps</span></div>
            <div className="n bad">{totals.fail}</div>
            <div className="s">−{sprs.deductions} pts to SPRS</div>
          </button>
          {poamSummary && (
            <button type="button" className="cmmcw-stat" onClick={function() { setSection('poams'); }}>
              <div className="h"><span>Open POA&amp;M</span></div>
              <div className="n warn">{(poamSummary.open || 0) + (poamSummary.in_progress || 0)}</div>
              <div className="s">{poamSummary.overdue > 0 ? poamSummary.overdue + ' past target' : poamSummary.total + ' total'}</div>
            </button>
          )}
        </div>

        <div className="cmmcw-card">
          <div className="cmmcw-card-h"><span className="t">Recent activity</span><span className="s">continuous evaluation · drift</span></div>
          <div className="cmmcw-feed">
            {(data.drift && data.drift.length) ? data.drift.slice(0, 6).map(function(d) {
              var good = d.new_state === 'pass';
              return (
                <div className="it" key={d.id}>
                  <span className={'ic ' + (good ? 'ok' : d.new_state === 'fail' ? 'bad' : '')}>{cwIcon(good ? 'shield' : 'flag')}</span>
                  <span className="tx"><code>{d.control_id}</code> <span className="w">{d.prev_state} →</span> <b>{d.new_state}</b> <span className="w">— {d.reason}</span></span>
                  <time>{relTime(d.detected_at)}</time>
                </div>
              );
            }) : <div className="cmmcw-empty">No state changes recorded in the last 30 days.</div>}
          </div>
        </div>
      </>
    );
  }

  return createPortal(
    <CMMCAuditorContext.Provider value={auditorView}>
    <div className="cmmcw">
      {renderWorkspaceChrome()}
      <main className="cmmcw-main">

      {/* SPA freeze banner — surfaces whenever Aurelius is in read-only mode
          for this client (e.g. C3PAO assessment in progress). Persists
          across every section so the operator can never miss the state. */}
      {data.freeze && (
        <div className="cmmc-freeze-banner" role="status">
          <span className="cmmc-freeze-banner-icon" aria-hidden="true">❄</span>
          <div className="cmmc-freeze-banner-text">
            <div><strong>Aurelius is frozen for this client</strong> — no live API calls are being made against the client environment. Control state shown is the snapshot from the moment the freeze started.</div>
            <div className="cmmc-freeze-banner-meta">
              Reason: <em>"{data.freeze.reason}"</em>
              {data.freeze.started_by_email && <> · started by {data.freeze.started_by_email}</>}
              {' · '}{new Date(data.freeze.started_at).toLocaleString()}
            </div>
          </div>
          <button type="button" className="admin-btn sm" onClick={function() { setSection('posture'); }}>Review in Aurelius Posture →</button>
        </div>
      )}

      {/* Auditor view banner — visible whenever the toggle is on so the operator
          knows why edit affordances are missing. Click to exit. */}
      {auditorView && isAdminRole() && !IS_CLIENT_VIEW && (
        <div className="cmmc-auditor-banner" role="status">
          <span className="cmmc-auditor-banner-icon" aria-hidden="true">👁</span>
          <span className="cmmc-auditor-banner-text">
            <strong>Auditor view</strong> — edit affordances are hidden so the workspace reads as pure evidence presentation. This is what an external assessor would see when reviewing the campaign.
          </span>
          <button type="button" className="admin-btn sm" onClick={function() { setAuditorView(false); }}>Exit auditor view</button>
        </div>
      )}

      {/* Section router — only the active section renders to keep the DOM
          small and avoid offscreen API fetches. */}
      <div className={'cmmc-section-content' + (auditorView ? ' cmmc-auditor-mode' : '')}>
        {visibleSection === 'overview'    && renderOverviewSection()}
        {visibleSection === 'controls'    && renderControlsSection()}
        {visibleSection === 'poams'       && <CMMCPoamSection clientId={clientId} />}
        {visibleSection === 'devices'     && <CmmcwRegister clientId={clientId} sources={data.integrations} cfg={cmmcwEnvCfg('devices')} />}
        {visibleSection === 'personnel'   && <CmmcwRegister clientId={clientId} sources={data.integrations} cfg={cmmcwEnvCfg('personnel')} />}
        {visibleSection === 'privileged'  && <CmmcwRegister clientId={clientId} sources={data.integrations} cfg={cmmcwEnvCfg('privileged')} />}
        {visibleSection === 'software'    && <CmmcwRegister clientId={clientId} sources={data.integrations} cfg={cmmcwEnvCfg('software')} />}
        {visibleSection === 'network'     && <CmmcwRegister clientId={clientId} sources={data.integrations} cfg={cmmcwEnvCfg('network')} />}
        {visibleSection === 'locations'   && <CmmcwRegister clientId={clientId} sources={data.integrations} cfg={cmmcwEnvCfg('locations')} />}
        {visibleSection === 'external'    && <CmmcwRegister clientId={clientId} sources={data.integrations} cfg={cmmcwEnvCfg('external')} />}
        {visibleSection === 'change'      && <CmmcwRegister clientId={clientId} sources={data.integrations} cfg={cmmcwCompCfg('change')} />}
        {visibleSection === 'risk'        && <CmmcwRegister clientId={clientId} sources={data.integrations} cfg={cmmcwCompCfg('risk')} />}
        {visibleSection === 'baselines'   && <CmmcwRegister clientId={clientId} sources={data.integrations} cfg={cmmcwCompCfg('baselines')} />}
        {visibleSection === 'maintenance' && <CmmcwRegister clientId={clientId} sources={data.integrations} cfg={cmmcwCompCfg('maintenance')} />}
        {visibleSection === 'subcontractors' && <CmmcwRegister clientId={clientId} sources={data.integrations} cfg={cmmcwCompCfg('subcontractors')} />}
        {visibleSection === 'reviews'     && <CmmcwRegister clientId={clientId} sources={data.integrations} cfg={cmmcwCompCfg('reviews')} />}
        {visibleSection === 'visitors'    && <CmmcwRegister clientId={clientId} sources={data.integrations} cfg={cmmcwCompCfg('visitors')} />}
        {visibleSection === 'vulnerabilities' && <CmmcwRegister clientId={clientId} sources={data.integrations} cfg={cmmcwSecCfg('vulnerabilities')} />}
        {visibleSection === 'flaws'       && <CmmcwRegister clientId={clientId} sources={data.integrations} cfg={cmmcwSecCfg('flaws')} />}
        {visibleSection === 'integrations' && <CmmcwIntegrations />}
        {visibleSection === 'inventories' && <CMMCInventoriesSection clientId={clientId} />}
        {visibleSection === 'scoping'     && <CMMCScopingSection clientId={clientId} isCbgLed={isCbgLed} />}
        {visibleSection === 'documents'   && <CMMCDocsSection clientId={clientId} />}
        {visibleSection === 'ssp'         && isCbgLed && isAdminRole() && !IS_CLIENT_VIEW && <CMMCSspExport clientId={clientId} />}
        {visibleSection === 'audit'       && <CMMCAuditTrailSection clientId={clientId} embedded />}
        {visibleSection === 'posture'     && <CMMCSpaPostureSection clientId={clientId} />}
      </div>
      </main>
    </div>
    </CMMCAuditorContext.Provider>,
    document.body
  );
}

// Inline enablement form shown on the CMMC sub-tab when the client doesn't
// have CMMC turned on yet. Admin-only; clients see a contact-CBG message.
// On submit, PATCHes the client integration record to flip cmmc=true plus
// the campaign metadata, then triggers a soft reload so the CMMC workspace
// activates without a manual page refresh.
function CMMCEnableEmptyState({ clientId, clientName, onEnabled }) {
  var [form, setForm] = useState({
    target_level: 2,
    program_lead: 'cbg',
    target_assessment_date: '',
    c3pao: '',
    campaign_stage: 'gap_assessment'
  });
  var [saving, setSaving] = useState(false);
  var [err, setErr] = useState(null);
  // Permission gate — admin role AND not in client-portal preview URL.
  var isAdmin = isAdminRole() && !IS_CLIENT_VIEW;

  if (!isAdmin) {
    return (
      <div className="tab-content">
        <div className="empty-state" style={{ padding: 32 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>CMMC tracking is not enabled for this client</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>If you're pursuing a DoD contract that requires CMMC L1 or L2 certification, contact your CloudByte Group account team to begin a campaign. CBG will configure the integrations + program lead + target assessment date in this dashboard.</div>
        </div>
      </div>
    );
  }

  function submit(e) {
    if (e) e.preventDefault();
    if (!window.confirm('Enable CMMC campaign tracking for ' + clientName + '?\n\nThis will turn on continuous control evaluation, expose the full CMMC workspace (controls, POA&Ms, inventories, scoping, SSP, audit trail, posture), and start collecting compliance evidence.\n\nYou can adjust the campaign metadata in Admin → Integrations at any time.')) return;
    setSaving(true); setErr(null);
    var payload = {
      cmmc: true,
      cmmc_target_level: form.target_level,
      cmmc_program_lead: form.program_lead,
      cmmc_campaign_stage: form.campaign_stage,
      cmmc_target_assessment_date: form.target_assessment_date || null,
      cmmc_c3pao: form.c3pao.trim() || null,
      cmmc_aegis_active: form.program_lead === 'atomus'
    };
    authFetch(API + '/admin/client-integrations/' + clientId, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(Object.assign({}, payload, { changeNote: 'CMMC campaign enabled via Compliance tab' }))
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setSaving(false);
        if (x.ok) {
          if (onEnabled) onEnabled();
          // Soft reload — the App-level clientList caches c.cmmc per client,
          // and the CMMCTab fetches its own data on mount. A reload is the
          // simplest way to ensure every consumer picks up the new state.
          window.location.reload();
        } else {
          setErr(x.d.error || 'Failed to enable CMMC');
        }
      })
      .catch(function() { setSaving(false); setErr('Failed to enable CMMC'); });
  }

  return (
    <div className="tab-content">
      <TabDescription text={'Enable CMMC tracking when ' + clientName + ' is pursuing or maintaining DoD contract eligibility. Aurelius will continuously evaluate every NIST 800-171 R2 control against live data, surface gaps, generate POA&Ms + SSP drafts, and track the campaign through certification.'} />
      <ContentBand title="Enable CMMC Campaign" subtitle="Pivoting to CMMC for a government RFP, or onboarding an existing CMMC client? Set the basics here. Aurelius will start collecting evidence immediately.">
        <form onSubmit={submit} className="cmmc-enable-form">
          <div className="cmmc-enable-grid">
            <div className="admin-field">
              <label>Target level</label>
              <select value={form.target_level} onChange={function(e) { setForm(Object.assign({}, form, { target_level: parseInt(e.target.value, 10) })); }}>
                <option value="1">CMMC Level 1 (FCI handlers — 17 controls)</option>
                <option value="2">CMMC Level 2 (CUI handlers — 110 controls)</option>
              </select>
              <div className="admin-field-hint">Level 2 is the standard for clients touching DoD CUI. Level 1 covers FCI-only contracts.</div>
            </div>
            <div className="admin-field">
              <label>Program lead</label>
              <select value={form.program_lead} onChange={function(e) { setForm(Object.assign({}, form, { program_lead: e.target.value })); }}>
                <option value="cbg">CloudByte Group (full SSP authority)</option>
                <option value="atomus">Atomus partnership (Atomus owns SSP)</option>
                <option value="hybrid">Hybrid (CBG authors, Atomus reviews)</option>
              </select>
              <div className="admin-field-hint">Drives whether Aurelius generates the SSP + POA&M lifecycle, or surfaces them as a service-delivery view alongside Atomus's authority.</div>
            </div>
            <div className="admin-field">
              <label>Campaign stage</label>
              <select value={form.campaign_stage} onChange={function(e) { setForm(Object.assign({}, form, { campaign_stage: e.target.value })); }}>
                <option value="gap_assessment">Gap Assessment (just starting)</option>
                <option value="remediation">Remediation (working through gaps)</option>
                <option value="pre_assessment">Pre-Assessment (final prep)</option>
                <option value="audit_scheduled">Audit Scheduled (C3PAO booked)</option>
                <option value="certified">Certified (post-audit, maintenance)</option>
                <option value="maintenance">Maintenance (annual cycle)</option>
              </select>
            </div>
            <div className="admin-field">
              <label>Target assessment date</label>
              <input type="date" value={form.target_assessment_date} onChange={function(e) { setForm(Object.assign({}, form, { target_assessment_date: e.target.value })); }} />
              <div className="admin-field-hint">When does the C3PAO walk in? Optional. Drives the days-remaining countdown on the CMMC dashboard.</div>
            </div>
            <div className="admin-field" style={{ gridColumn: '1 / -1' }}>
              <label>C3PAO (assessor org)</label>
              <input type="text" value={form.c3pao} onChange={function(e) { setForm(Object.assign({}, form, { c3pao: e.target.value })); }} placeholder="e.g. Apex Cybersecurity" />
              <div className="admin-field-hint">Optional. Surfaces in the campaign hero + the SSP cover page. You can set this later.</div>
            </div>
          </div>

          <div className="cmmc-enable-info">
            <strong>What happens when you enable:</strong>
            <ul style={{ marginTop: 6, paddingLeft: 20, fontSize: 12, lineHeight: 1.55 }}>
              <li>Aurelius starts continuous evaluation against all {form.target_level === 1 ? '17 (Level 1)' : '110 (Level 2)'} NIST 800-171 R2 controls.</li>
              <li>The CMMC workspace exposes Controls, POA&Ms, Inventories (Personnel / Privileged / Software), Scoping reports, SSP export, Audit Trail, and Aurelius Posture sections.</li>
              <li>If the client's NinjaOne Gov + Entra + KeeperGov integrations are wired, evidence collection is immediate. If not, you'll see "verify with [stack]" placeholders until the integrations are configured.</li>
              <li>Cyber Insurance tracking remains available alongside CMMC (sub-tab on this same tab).</li>
              <li>You can change any field on this page later in <strong>Admin → Integrations → Edit Client</strong>.</li>
            </ul>
          </div>

          {err && <div className="admin-banner error" style={{ marginTop: 12 }}>{err}</div>}

          <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
            <button type="submit" className="admin-btn primary" disabled={saving}>{saving ? 'Enabling…' : '◆ Enable CMMC tracking'}</button>
          </div>
        </form>
      </ContentBand>
    </div>
  );
}

function ComplianceTab({ c, clientId, onExit }) {
  const [soc2, setSoc2] = useState(null);
  const [soc2Loading, setSoc2Loading] = useState(false);
  // Sub-tabs are ALWAYS available, regardless of CMMC enablement state.
  // Cyber Insurance is its own product — gating it on CMMC was a UX bug.
  // The CMMC sub-tab handles its own "not enabled yet" state with an
  // inline enable form so the operator can flip it on without leaving
  // the workspace they're standing in.
  var [subTab, setSubTab] = useState('cmmc');

  useEffect(function() {
    if (c.cmmc) return;
    setSoc2Loading(true);
    authFetch(API + '/clients/' + clientId + '/compliance')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(data) { setSoc2(data); setSoc2Loading(false); })
      .catch(function() { setSoc2Loading(false); });
  }, [clientId, c.cmmc]);

  // Sub-tab strip — always rendered.
  var subTabStrip = (
    <div className="compliance-subtabs">
      <button type="button" className={'compliance-subtab' + (subTab === 'cmmc' ? ' active' : '')} onClick={function() { setSubTab('cmmc'); }}>
        <span className="compliance-subtab-icon">◆</span>
        <span>CMMC Campaign{!c.cmmc && <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: 'rgba(245,158,11,0.15)', color: '#f59e0b', letterSpacing: '0.4px' }}>NOT ENABLED</span>}</span>
      </button>
      <button type="button" className={'compliance-subtab' + (subTab === 'cyber' ? ' active' : '')} onClick={function() { setSubTab('cyber'); }}>
        <span className="compliance-subtab-icon">◈</span>
        <span>Cyber Insurance</span>
      </button>
    </div>
  );

  // CMMC enabled — full workspace. Cyber Insurance always reachable.
  if (c.cmmc) {
    return (
      <div className="tab-content compliance-with-subtabs">
        {subTabStrip}
        {subTab === 'cmmc' && <CMMCTab c={c} clientId={clientId} onExit={onExit} onOpenCyber={function() { setSubTab('cyber'); }} />}
        {subTab === 'cyber' && <CyberInsuranceTab c={c} clientId={clientId} />}
      </div>
    );
  }

  // CMMC NOT enabled — sub-tab still visible, but the CMMC pane shows the
  // enablement form (admin) or a contact-CBG message (client).
  // Cyber Insurance + (legacy) SOC 2 view still reachable.
  if (subTab === 'cmmc') {
    return (
      <div className="tab-content compliance-with-subtabs">
        {subTabStrip}
        <CMMCEnableEmptyState clientId={clientId} clientName={c.name || clientId} />
      </div>
    );
  }
  if (subTab === 'cyber') {
    return (
      <div className="tab-content compliance-with-subtabs">
        {subTabStrip}
        <CyberInsuranceTab c={c} clientId={clientId} />
      </div>
    );
  }

  // (legacy SOC 2 demo path retained for backward compat — only reachable
  // if neither sub-tab is selected, which doesn't happen in practice)
  if (soc2Loading) return <div className="tab-content"><SkeletonLoader /></div>;
  if (!soc2) return <div className="tab-content"><div className="empty-state">Compliance tracking is not yet configured for this client.{c.industry && c.industry.indexOf('Healthcare') >= 0 && <p style={{ marginTop: 12, color: '#94a3b8' }}>HIPAA compliance module coming soon.</p>}</div></div>;

  var totalControls = soc2.criteria.reduce(function(s, cr) { return s + cr.controls_total; }, 0);
  var implementedControls = soc2.criteria.reduce(function(s, cr) { return s + cr.controls_implemented; }, 0);
  var totalEvidence = soc2.criteria.reduce(function(s, cr) { return s + cr.evidence_count; }, 0);
  var daysUntilAudit = soc2.target_audit_date ? Math.round((new Date(soc2.target_audit_date).getTime() - Date.now()) / 86400000) : null;

  return (
    <div className="tab-content">
      <TabDescription text={'Formal compliance readiness for ' + soc2.framework + '. Each Trust Services Criterion tracks implemented controls, in-progress work, and evidence collected for the audit period.'} />
      <DataFreshness live={true} />
      <ContentBand title={soc2.framework + ' Readiness'} subtitle={daysUntilAudit !== null ? (daysUntilAudit > 0 ? daysUntilAudit + ' days until target audit date' : 'Audit period active') : 'Active program'} rightContent={<span className="live-badge">LIVE</span>}>
        <div className="metric-tiles">
          <div className={'metric-tile ' + (soc2.readiness_pct >= 90 ? 'metric-tile-good' : soc2.readiness_pct < 70 ? 'metric-tile-alert' : '')}>
            <div className="metric-tile-label">Overall Readiness</div>
            <div className="metric-tile-value">{soc2.readiness_pct}<span className="metric-tile-unit">%</span></div>
            <div className="metric-tile-context">Weighted across all criteria</div>
          </div>
          <div className="metric-tile">
            <div className="metric-tile-label">Controls Implemented</div>
            <div className="metric-tile-value">{implementedControls}<span className="metric-tile-unit"> / {totalControls}</span></div>
            <div className="metric-tile-context">{totalControls - implementedControls} remaining</div>
          </div>
          <div className="metric-tile">
            <div className="metric-tile-label">Evidence Collected</div>
            <div className="metric-tile-value">{totalEvidence}</div>
            <div className="metric-tile-context">Audit-ready artifacts</div>
          </div>
          <div className={'metric-tile' + (soc2.upcoming_tasks && soc2.upcoming_tasks.length > 2 ? ' metric-tile-alert' : '')}>
            <div className="metric-tile-label">Open Tasks</div>
            <div className="metric-tile-value">{soc2.upcoming_tasks ? soc2.upcoming_tasks.length : 0}</div>
            <div className="metric-tile-context">Remediation and documentation</div>
          </div>
        </div>
      </ContentBand>

      <ContentBand title="Trust Services Criteria" subtitle="Each criterion tracked independently — readiness scores, controls, and evidence">
        <div className="soc2-criteria">
          {soc2.criteria.map(function(cr) {
            var barColor = cr.score >= 90 ? '#22c55e' : cr.score >= 75 ? '#60a5fa' : cr.score >= 60 ? '#f59e0b' : '#ef4444';
            return (
              <div key={cr.id} className="soc2-criterion">
                <div className="soc2-criterion-header">
                  <div className="soc2-criterion-id">{cr.id}</div>
                  <div className="soc2-criterion-name">{cr.name}</div>
                  <div className="soc2-criterion-score" style={{ color: barColor }}>{cr.score}%</div>
                </div>
                <div className="soc2-criterion-bar"><div className="soc2-criterion-bar-fill" style={{ width: cr.score + '%', background: barColor }} /></div>
                <div className="soc2-criterion-meta">
                  <span>{cr.controls_implemented}/{cr.controls_total} controls implemented</span>
                  {cr.controls_in_progress > 0 && <span className="soc2-criterion-pending">{cr.controls_in_progress} in progress</span>}
                  <span>{cr.evidence_count} evidence items</span>
                  <span>Last tested {Math.round((Date.now() - new Date(cr.last_tested).getTime()) / 86400000)}d ago</span>
                </div>
              </div>
            );
          })}
        </div>
      </ContentBand>

      <ContentBand title="Recent Evidence Collection" subtitle="Most recent audit artifacts — automated and manual">
        <MetricList>
          {soc2.recent_evidence.map(function(e, i) {
            var ageHours = Math.round((Date.now() - new Date(e.collected).getTime()) / 3600000);
            var ageLabel = ageHours < 24 ? ageHours + 'h ago' : Math.round(ageHours / 24) + 'd ago';
            return <MetricListItem key={i} icon={e.type === 'Automated collection' ? '◆' : '◇'} iconColor={e.type === 'Automated collection' ? '#22c55e' : '#60a5fa'} label={e.control} sublabel={e.source} value={ageLabel} trail={e.type} />;
          })}
        </MetricList>
      </ContentBand>

      {soc2.upcoming_tasks && soc2.upcoming_tasks.length > 0 && (
        <ContentBand title="Upcoming Compliance Tasks" subtitle="Remediation and documentation items in flight">
          <MetricList>
            {soc2.upcoming_tasks.map(function(t, i) {
              var due = new Date(t.due);
              var daysUntil = Math.round((due.getTime() - Date.now()) / 86400000);
              var dueLabel = daysUntil < 0 ? Math.abs(daysUntil) + 'd overdue' : 'Due in ' + daysUntil + 'd';
              var priorityColor = t.priority === 'High' ? '#ef4444' : t.priority === 'Medium' ? '#f59e0b' : '#64748b';
              return <MetricListItem key={i} icon="◈" iconColor={priorityColor} label={t.task} sublabel={'Owner: ' + t.owner} badge={t.priority} badgeColor={priorityColor} value={dueLabel} valueColor={daysUntil < 0 ? '#ef4444' : 'var(--text-primary)'} />;
            })}
          </MetricList>
        </ContentBand>
      )}
    </div>
  );
}
// ── Reviews Tab (QBR Meeting Recaps) ─────────────────────────────
function ReviewsTab({ clientId, isAdmin }) {
  var [reviews, setReviews] = useState([]);
  var [loading, setLoading] = useState(true);
  var [view, setView] = useState('list');
  var [activeReview, setActiveReview] = useState(null);
  var [editing, setEditing] = useState(false);
  var [projectData, setProjectData] = useState(null);
  var [ticketData, setTicketData] = useState(null);
  var [form, setForm] = useState(null);

  function fetchReviews() {
    setLoading(true);
    authFetch(API + '/clients/' + clientId + '/reviews')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(data) { if (data) setReviews(data.reviews || []); setLoading(false); })
      .catch(function() { setLoading(false); });
  }

  useEffect(function() {
    fetchReviews();
    setView('list'); setActiveReview(null); setEditing(false); setForm(null);
    // Fetch linkable projects and tickets for the form
    Promise.all([
      authFetch(API + '/clients/' + clientId + '/projects').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
      authFetch(API + '/clients/' + clientId + '/tickets').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
    ]).then(function(results) {
      setProjectData(results[0] && results[0].projects ? results[0].projects : []);
      setTicketData(results[1] && results[1].tickets ? results[1].tickets : []);
    });
  }, [clientId]);

  function newForm() {
    return {
      date: new Date().toISOString().split('T')[0],
      title: '',
      attendees: '',
      summary: '',
      topics: [{ title: '', notes: '' }],
      actionItems: [{ text: '', owner: '', status: 'open', dueDate: '', linkedProject: '', linkedTicket: '' }],
      challenges: '',
      nextSteps: '',
      nextReviewDate: '',
    };
  }

  function openCreate() { setForm(newForm()); setEditing(false); setView('form'); }
  function openEdit(review) {
    setForm({
      date: review.date || '',
      title: review.title || '',
      attendees: review.attendees || '',
      summary: review.summary || '',
      topics: review.topics && review.topics.length > 0 ? review.topics : [{ title: '', notes: '' }],
      actionItems: review.actionItems && review.actionItems.length > 0 ? review.actionItems.map(function(a) { return { id: a.id, text: a.text || '', owner: a.owner || '', status: a.status || 'open', dueDate: a.dueDate || '', linkedProject: a.linkedProject || '', linkedTicket: a.linkedTicket || '' }; }) : [{ text: '', owner: '', status: 'open', dueDate: '', linkedProject: '', linkedTicket: '' }],
      challenges: review.challenges || '',
      nextSteps: review.nextSteps || '',
      nextReviewDate: review.nextReviewDate || '',
    });
    setActiveReview(review);
    setEditing(true);
    setView('form');
  }

  function saveReview() {
    var url = editing ? API + '/clients/' + clientId + '/reviews/' + activeReview.id : API + '/clients/' + clientId + '/reviews';
    var method = editing ? 'PUT' : 'POST';
    var body = Object.assign({}, form);
    body.topics = body.topics.filter(function(t) { return t.title.trim(); });
    body.actionItems = body.actionItems.filter(function(a) { return a.text.trim(); });
    authFetch(url, { method: method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (data.success) { fetchReviews(); setView('detail'); setActiveReview(data.review); setForm(null); }
      }).catch(function() {});
  }

  function deleteReview(reviewId) {
    if (!window.confirm('Delete this review? This cannot be undone.')) return;
    authFetch(API + '/clients/' + clientId + '/reviews/' + reviewId, { method: 'DELETE' })
      .then(function(r) { return r.json(); })
      .then(function(data) { if (data.success) { fetchReviews(); setView('list'); setActiveReview(null); } });
  }

  function updateActionStatus(reviewId, aiId, newStatus) {
    var review = reviews.find(function(r) { return r.id === reviewId; });
    if (!review) return;
    var updatedItems = review.actionItems.map(function(a) { return a.id === aiId ? Object.assign({}, a, { status: newStatus }) : a; });
    authFetch(API + '/clients/' + clientId + '/reviews/' + reviewId, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ actionItems: updatedItems }) })
      .then(function(r) { return r.json(); })
      .then(function(data) { if (data.success) { fetchReviews(); if (activeReview && activeReview.id === reviewId) setActiveReview(data.review); } });
  }

  function setFormField(field, value) { setForm(Object.assign({}, form, { [field]: value })); }
  function setTopic(idx, field, value) { var t = form.topics.slice(); t[idx] = Object.assign({}, t[idx], { [field]: value }); setForm(Object.assign({}, form, { topics: t })); }
  function addTopic() { setForm(Object.assign({}, form, { topics: form.topics.concat([{ title: '', notes: '' }]) })); }
  function removeTopic(idx) { var t = form.topics.slice(); t.splice(idx, 1); setForm(Object.assign({}, form, { topics: t.length > 0 ? t : [{ title: '', notes: '' }] })); }
  function setAction(idx, field, value) { var a = form.actionItems.slice(); a[idx] = Object.assign({}, a[idx], { [field]: value }); setForm(Object.assign({}, form, { actionItems: a })); }
  function addAction() { setForm(Object.assign({}, form, { actionItems: form.actionItems.concat([{ text: '', owner: '', status: 'open', dueDate: '', linkedProject: '', linkedTicket: '' }]) })); }
  function removeAction(idx) { var a = form.actionItems.slice(); a.splice(idx, 1); setForm(Object.assign({}, form, { actionItems: a.length > 0 ? a : [{ text: '', owner: '', status: 'open', dueDate: '', linkedProject: '', linkedTicket: '' }] })); }

  // Helper to resolve linked names
  function getProjectName(gid) { if (!gid || !projectData) return null; var p = projectData.find(function(pr) { return pr.gid === gid; }); return p ? p.name : null; }
  function getTicketSummary(id) { if (!id || !ticketData) return null; var t = ticketData.find(function(tk) { return String(tk.id) === String(id); }); return t ? '#' + t.id + ' ' + t.summary : null; }

  var statusColors = { open: '#f59e0b', 'in-progress': '#60a5fa', completed: '#22c55e', cancelled: '#64748b' };
  var statusLabels = { open: 'Open', 'in-progress': 'In Progress', completed: 'Completed', cancelled: 'Cancelled' };

  if (loading) return <div className="tab-content"><SkeletonLoader /></div>;

  // ── LIST VIEW ──
  if (view === 'list') {
    var openActions = 0, totalActions = 0;
    reviews.forEach(function(r) { if (r.actionItems) { totalActions += r.actionItems.length; openActions += r.actionItems.filter(function(a) { return a.status === 'open' || a.status === 'in-progress'; }).length; } });

    return (
      <div className="tab-content">
        <TabDescription text="Meeting recaps from Quarterly Business Reviews and strategic check-ins. Track action items, review discussions, and monitor progress between meetings." />
        <DataFreshness live={false} />
        {reviews.length > 0 && <HealthVerdict score={totalActions > 0 ? Math.round(((totalActions - openActions) / totalActions) * 100) : 100} context={
          openActions === 0 ? 'All action items from previous reviews have been completed.' :
          openActions <= 3 ? openActions + ' action item(s) still open across all reviews. On track.' :
          openActions + ' open action items need attention. Review priorities with your account team.'
        } />}
        {reviews.length > 0 && (
          <KPIStrip items={[
            { label: 'Total Reviews', value: reviews.length },
            { label: 'Action Items', value: totalActions },
            { label: 'Open Items', value: openActions, accent: openActions > 0 ? '#f59e0b' : '#22c55e' },
            { label: 'Completed', value: totalActions - openActions, accent: '#22c55e' },
            { label: 'Next Review', value: reviews[0] && reviews[0].nextReviewDate ? new Date(reviews[0].nextReviewDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—' },
          ]} />
        )}
        {isAdmin && <div style={{ margin: '16px 0' }}><button className="insights-btn" onClick={openCreate}>+ New Review</button></div>}
        {reviews.length === 0 ? (
          <EmptyStateAction icon="📋" title="No Business Reviews Yet" text="Quarterly Business Review recaps will appear here with discussion topics, action items, and progress tracking. Your CloudByte account team will add meeting notes after each QBR." cta={isAdmin ? 'Create your first review above' : 'Your next QBR recap will appear here'} />
        ) : (
          <div className="reviews-list">
            {reviews.map(function(r) {
              var aiTotal = r.actionItems ? r.actionItems.length : 0;
              var aiDone = r.actionItems ? r.actionItems.filter(function(a) { return a.status === 'completed'; }).length : 0;
              var aiOpen = r.actionItems ? r.actionItems.filter(function(a) { return a.status === 'open' || a.status === 'in-progress'; }).length : 0;
              return (
                <div key={r.id} className="review-card" onClick={function() { setActiveReview(r); setView('detail'); }}>
                  <div className="review-card-header">
                    <div>
                      <div className="review-card-date">{new Date(r.date + 'T12:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
                      <div className="review-card-title">{r.title}</div>
                    </div>
                    {aiTotal > 0 && (
                      <div className="review-card-progress">
                        <div className="review-card-progress-bar"><div className="review-card-progress-fill" style={{ width: Math.round((aiDone / aiTotal) * 100) + '%' }} /></div>
                        <span className="review-card-progress-text">{aiDone}/{aiTotal} items done</span>
                        {aiOpen > 0 && <span className="review-card-open-badge">{aiOpen} open</span>}
                      </div>
                    )}
                  </div>
                  {r.summary && <div className="review-card-summary">{r.summary.length > 180 ? r.summary.slice(0, 180) + '...' : r.summary}</div>}
                  {r.attendees && <div className="review-card-attendees">Attendees: {r.attendees}</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ── DETAIL VIEW ──
  if (view === 'detail' && activeReview) {
    var r = activeReview;
    return (
      <div className="tab-content">
        <Breadcrumb items={[
          { label: 'Reviews', onClick: function() { setView('list'); setActiveReview(null); } },
          { label: r.title }
        ]} />
        <div className="review-detail-header">
          <div>
            <div className="review-detail-date">{new Date(r.date + 'T12:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
            <div className="review-detail-title">{r.title}</div>
            {r.attendees && <div className="review-detail-attendees">Attendees: {r.attendees}</div>}
          </div>
          {isAdmin && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="insights-btn" onClick={function() { openEdit(r); }}>Edit</button>
              <button className="insights-btn" style={{ color: '#ef4444' }} onClick={function() { deleteReview(r.id); }}>Delete</button>
            </div>
          )}
        </div>

        {r.summary && (
          <CollapsibleSection title="Summary" defaultOpen={true}>
            <div className="review-detail-text">{r.summary}</div>
          </CollapsibleSection>
        )}

        {r.topics && r.topics.length > 0 && (
          <CollapsibleSection title={'Topics Discussed (' + r.topics.length + ')'} defaultOpen={true}>
            {r.topics.map(function(t, i) {
              return (
                <div key={i} className="review-topic">
                  <div className="review-topic-title">{t.title}</div>
                  {t.notes && <div className="review-topic-notes">{t.notes}</div>}
                </div>
              );
            })}
          </CollapsibleSection>
        )}

        {r.actionItems && r.actionItems.length > 0 && (
          <CollapsibleSection title={'Action Items (' + r.actionItems.length + ')'} defaultOpen={true} rightContent={
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {r.actionItems.filter(function(a) { return a.status === 'completed'; }).length + '/' + r.actionItems.length + ' completed'}
            </span>
          }>
            <div className="review-actions-list">
              {r.actionItems.map(function(a) {
                var projName = getProjectName(a.linkedProject);
                var ticketName = getTicketSummary(a.linkedTicket);
                return (
                  <div key={a.id} className={'review-action-item review-action-' + a.status}>
                    <div className="review-action-top">
                      <span className={'status-dot status-dot-' + (a.status === 'completed' ? 'good' : a.status === 'open' ? 'warning' : a.status === 'in-progress' ? 'info' : 'muted')} />
                      <span className="review-action-text">{a.text}</span>
                      {isAdmin && (
                        <select className="review-action-status-select" value={a.status} onClick={function(e) { e.stopPropagation(); }} onChange={function(e) { updateActionStatus(r.id, a.id, e.target.value); }}>
                          <option value="open">Open</option>
                          <option value="in-progress">In Progress</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      )}
                      {!isAdmin && <span className="review-action-status-badge" style={{ color: statusColors[a.status], borderColor: statusColors[a.status] }}>{statusLabels[a.status]}</span>}
                    </div>
                    <div className="review-action-meta">
                      {a.owner && <span>Owner: {a.owner}</span>}
                      {a.dueDate && <span>Due: {new Date(a.dueDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
                      {projName && <span className="review-action-link" title={'Linked to project: ' + projName}>📁 {projName}</span>}
                      {ticketName && <span className="review-action-link" title={'Linked to ticket'}>🎫 {ticketName}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </CollapsibleSection>
        )}

        {r.challenges && (
          <CollapsibleSection title="Challenges & Blockers" defaultOpen={true}>
            <div className="review-detail-text">{r.challenges}</div>
          </CollapsibleSection>
        )}

        {r.nextSteps && (
          <CollapsibleSection title="Next Steps" defaultOpen={true}>
            <div className="review-detail-text">{r.nextSteps}</div>
          </CollapsibleSection>
        )}

        {r.nextReviewDate && (
          <div className="review-next-date">
            Next review scheduled: <strong>{new Date(r.nextReviewDate + 'T12:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</strong>
          </div>
        )}
      </div>
    );
  }

  // ── FORM VIEW (Create / Edit) ──
  if (view === 'form' && form) {
    return (
      <div className="tab-content">
        <Breadcrumb items={[
          { label: 'Reviews', onClick: function() { setView('list'); setForm(null); } },
          { label: editing ? 'Edit Review' : 'New Review' }
        ]} />
        <div className="review-form">
          <div className="review-form-row">
            <label className="review-form-label">Date *<input type="date" className="review-form-input" value={form.date} onChange={function(e) { setFormField('date', e.target.value); }} /></label>
            <label className="review-form-label">Title *<input type="text" className="review-form-input" placeholder="Q2 2026 Business Review" value={form.title} onChange={function(e) { setFormField('title', e.target.value); }} /></label>
          </div>
          <label className="review-form-label">Attendees<input type="text" className="review-form-input" placeholder="Alex (CloudByte), Jane (Client)" value={form.attendees} onChange={function(e) { setFormField('attendees', e.target.value); }} /></label>
          <label className="review-form-label">Summary<textarea className="review-form-textarea" rows={4} placeholder="High-level recap of the meeting..." value={form.summary} onChange={function(e) { setFormField('summary', e.target.value); }} /></label>

          <div className="review-form-section-title">Topics Discussed</div>
          {form.topics.map(function(t, i) {
            return (
              <div key={i} className="review-form-topic">
                <input type="text" className="review-form-input" placeholder="Topic title" value={t.title} onChange={function(e) { setTopic(i, 'title', e.target.value); }} />
                <textarea className="review-form-textarea" rows={2} placeholder="Notes / discussion points" value={t.notes} onChange={function(e) { setTopic(i, 'notes', e.target.value); }} />
                {form.topics.length > 1 && <button className="review-form-remove" onClick={function() { removeTopic(i); }}>✕</button>}
              </div>
            );
          })}
          <button className="review-form-add" onClick={addTopic}>+ Add Topic</button>

          <div className="review-form-section-title">Action Items</div>
          {form.actionItems.map(function(a, i) {
            return (
              <div key={i} className="review-form-action">
                <div className="review-form-row">
                  <label className="review-form-label" style={{ flex: 3 }}>Action *<input type="text" className="review-form-input" placeholder="What needs to be done" value={a.text} onChange={function(e) { setAction(i, 'text', e.target.value); }} /></label>
                  <label className="review-form-label" style={{ flex: 1 }}>Owner<input type="text" className="review-form-input" placeholder="CloudByte / Client" value={a.owner} onChange={function(e) { setAction(i, 'owner', e.target.value); }} /></label>
                  <label className="review-form-label" style={{ flex: 1 }}>Due Date<input type="date" className="review-form-input" value={a.dueDate} onChange={function(e) { setAction(i, 'dueDate', e.target.value); }} /></label>
                </div>
                <div className="review-form-row">
                  <label className="review-form-label" style={{ flex: 1 }}>Status
                    <select className="review-form-input" value={a.status} onChange={function(e) { setAction(i, 'status', e.target.value); }}>
                      <option value="open">Open</option><option value="in-progress">In Progress</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option>
                    </select>
                  </label>
                  <label className="review-form-label" style={{ flex: 1 }}>Link to Project
                    <select className="review-form-input" value={a.linkedProject || ''} onChange={function(e) { setAction(i, 'linkedProject', e.target.value || null); }}>
                      <option value="">— None —</option>
                      {projectData && projectData.map(function(p) { return <option key={p.gid} value={p.gid}>{p.name}</option>; })}
                    </select>
                  </label>
                  <label className="review-form-label" style={{ flex: 1 }}>Link to Ticket
                    <select className="review-form-input" value={a.linkedTicket || ''} onChange={function(e) { setAction(i, 'linkedTicket', e.target.value || null); }}>
                      <option value="">— None —</option>
                      {ticketData && ticketData.filter(function(tk) { return tk.statusParent !== 5000 && tk.statusParent !== 6000; }).slice(0, 50).map(function(tk) { return <option key={tk.id} value={tk.id}>#{tk.id} {tk.summary && tk.summary.length > 50 ? tk.summary.slice(0, 50) + '...' : tk.summary}</option>; })}
                    </select>
                  </label>
                </div>
                {form.actionItems.length > 1 && <button className="review-form-remove" onClick={function() { removeAction(i); }}>✕</button>}
              </div>
            );
          })}
          <button className="review-form-add" onClick={addAction}>+ Add Action Item</button>

          <label className="review-form-label">Challenges & Blockers<textarea className="review-form-textarea" rows={3} placeholder="Any issues discussed..." value={form.challenges} onChange={function(e) { setFormField('challenges', e.target.value); }} /></label>
          <label className="review-form-label">Next Steps<textarea className="review-form-textarea" rows={3} placeholder="Agreed next steps..." value={form.nextSteps} onChange={function(e) { setFormField('nextSteps', e.target.value); }} /></label>
          <label className="review-form-label">Next Review Date<input type="date" className="review-form-input" style={{ maxWidth: 200 }} value={form.nextReviewDate} onChange={function(e) { setFormField('nextReviewDate', e.target.value); }} /></label>

          <div className="review-form-buttons">
            <button className="review-form-save" onClick={saveReview} disabled={!form.title.trim() || !form.date}>{editing ? 'Update Review' : 'Save Review'}</button>
            <button className="review-form-cancel" onClick={function() { setView(editing ? 'detail' : 'list'); setForm(null); }}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

// ── QBR Report View ───────────────────────────────────────────────
var qbrLoadingStages = ['Collecting endpoint data...', 'Analyzing identity posture...', 'Reviewing threats...', 'Gathering budget data...', 'Generating report...'];
var gradeColors = { A: '#22c55e', B: '#5b9ef4', C: '#f59e0b', D: '#f97316', F: '#ef4444' };
var priorityLabels = { urgent: 'Needs Attention Now', soon: 'This Quarter', planned: 'On the Roadmap' };
var priorityColors = { urgent: 'var(--accent-red)', soon: 'var(--accent-amber)', planned: 'var(--accent-blue)' };

function QBRPanel({ isOpen, onClose, report, clientName, loading }) {
  var today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  var [stageIdx, setStageIdx] = useState(0);

  useEffect(function() {
    if (!isOpen) return;
    var handler = function(e) { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return function() { window.removeEventListener('keydown', handler); };
  }, [isOpen, onClose]);

  useEffect(function() {
    if (!loading) { setStageIdx(0); return; }
    var interval = setInterval(function() { setStageIdx(function(prev) { return prev < qbrLoadingStages.length - 1 ? prev + 1 : prev; }); }, 3000);
    return function() { clearInterval(interval); };
  }, [loading]);

  var handlePrint = function() {
    if (!report) return;
    var printWindow = window.open('', '_blank');
    var s = '';
    if (report.grade) s += '<div style="text-align:center;margin:20px 0 30px"><div style="display:inline-block;width:64px;height:64px;border-radius:50%;background:' + (gradeColors[report.grade] || '#5b9ef4') + ';color:#fff;font-size:32px;font-weight:800;line-height:64px">' + report.grade + '</div><div style="font-size:13px;color:#64748b;margin-top:6px">' + (report.grade_label || '') + '</div></div>';
    if (report.bottom_line) s += '<div style="background:#f0f4ff;border-left:4px solid #1e40af;padding:14px 18px;font-size:16px;font-weight:600;color:#1e293b;margin-bottom:28px;border-radius:0 8px 8px 0">' + report.bottom_line + '</div>';
    s += '<div class="qbr-print-section">Executive Summary</div><div class="qbr-print-text">' + (report.executive_summary || '') + '</div>';
    if (report.value_delivered && report.value_delivered.length > 0) { s += '<div class="qbr-print-section">Value Delivered</div><table style="width:100%;border-collapse:collapse;margin:12px 0"><tbody>'; report.value_delivered.forEach(function(v) { s += '<tr><td style="padding:10px 12px;border-bottom:1px solid #e2e8f0;font-weight:700;white-space:nowrap;vertical-align:top;color:#1e40af;width:140px">' + v.metric + '</td><td style="padding:10px 12px;border-bottom:1px solid #e2e8f0;font-size:14px">' + v.detail + '</td></tr>'; }); s += '</tbody></table>'; }
    if (report.metrics_snapshot) { s += '<div class="qbr-print-section">Key Metrics</div><table style="width:100%;border-collapse:collapse;margin:12px 0"><tbody>'; Object.entries(report.metrics_snapshot).forEach(function(pair) { s += '<tr><td style="padding:8px 12px;border-bottom:1px solid #e2e8f0;font-weight:600;color:#475569;width:180px">' + pair[0] + '</td><td style="padding:8px 12px;border-bottom:1px solid #e2e8f0;font-size:14px">' + pair[1] + '</td></tr>'; }); s += '</tbody></table>'; }
    if (report.attention_items && report.attention_items.length > 0) { s += '<div class="qbr-print-section">Items Needing Attention</div>'; report.attention_items.forEach(function(item) { var pc = item.priority === 'urgent' ? '#dc2626' : item.priority === 'soon' ? '#d97706' : '#2563eb'; s += '<div style="margin-bottom:14px"><span style="display:inline-block;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:' + pc + ';margin-bottom:3px">' + (priorityLabels[item.priority] || item.priority) + '</span><div style="font-weight:600;font-size:14px;margin-bottom:3px">' + item.title + '</div><div style="font-size:13px;color:#64748b">' + item.detail + '</div></div>'; }); }
    if (report.investments && report.investments.length > 0) { s += '<div class="qbr-print-section">Strategic Investments</div>'; report.investments.forEach(function(inv) { s += '<div style="margin-bottom:14px"><div style="font-weight:600;font-size:14px;margin-bottom:3px">' + inv.title + '</div><div style="font-size:13px;color:#64748b">' + inv.detail + '</div></div>'; }); }
    if (report.project_status) s += '<div class="qbr-print-section">Project Status</div><div class="qbr-print-text">' + report.project_status + '</div>';
    if (report.budget_analysis) s += '<div class="qbr-print-section">Budget & Licensing</div><div class="qbr-print-text">' + report.budget_analysis + '</div>';
    if (report.identity_posture) s += '<div class="qbr-print-section">Identity & Access</div><div class="qbr-print-text">' + report.identity_posture + '</div>';
    if (report.compliance_notes) s += '<div class="qbr-print-section">Compliance</div><div class="qbr-print-text">' + report.compliance_notes + '</div>';
    printWindow.document.write('<!DOCTYPE html><html><head><title>QBR - ' + clientName + '</title><style>body{font-family:"Segoe UI",sans-serif;color:#1e293b;padding:40px;max-width:800px;margin:0 auto;line-height:1.6}.qbr-print-header{border-bottom:3px solid #1e40af;padding-bottom:16px;margin-bottom:32px}.qbr-print-logo{font-size:24px;font-weight:700;letter-spacing:3px;color:#1e40af}.qbr-print-sub{font-size:12px;color:#64748b}.qbr-print-client{font-size:20px;font-weight:600;margin-top:8px}.qbr-print-date{font-size:13px;color:#64748b}.qbr-print-section{font-size:14px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#1e40af;margin:28px 0 12px;border-bottom:1px solid #e2e8f0;padding-bottom:6px}.qbr-print-text{font-size:14px;margin-bottom:12px;white-space:pre-line}.qbr-print-footer{margin-top:40px;padding-top:16px;border-top:1px solid #e2e8f0;font-size:11px;color:#94a3b8;text-align:center}@media print{body{padding:20px}}</style></head><body><div class="qbr-print-header"><div class="qbr-print-logo">AURELIUS</div><div class="qbr-print-sub">by CloudByte Group</div><div class="qbr-print-client">' + clientName + '</div><div class="qbr-print-date">Quarterly Business Review — ' + today + '</div></div>' + s + '<div class="qbr-print-footer">Prepared by CloudByte Group using Aurelius — ' + today + '</div></body></html>');
    printWindow.document.close();
    setTimeout(function() { printWindow.print(); }, 500);
  };

  if (!isOpen) return null;

  var gradeColor = report && report.grade ? (gradeColors[report.grade] || '#5b9ef4') : '#5b9ef4';

  return (
    <>
      <div className="qbr-overlay" onClick={onClose} />
      <div className="qbr-panel">
        <div className="qbr-header">
          <div>
            <div className="qbr-title">Quarterly Business Review</div>
            <div className="qbr-client-name">{clientName}</div>
            <div className="qbr-date">{today}</div>
          </div>
          <div className="qbr-actions">
            {report && <button className="qbr-print-btn" onClick={handlePrint}>Export / Print</button>}
            <button className="qbr-close-btn" onClick={onClose}>&#10005;</button>
          </div>
        </div>
        <div className="qbr-body">
          {loading && (
            <div className="qbr-loading">
              <div className="qbr-loading-spinner" />
              <div className="qbr-loading-stage">{qbrLoadingStages[stageIdx]}</div>
              <div className="qbr-loading-sub">This may take 15-30 seconds</div>
            </div>
          )}
          {!loading && report && (
            <>
              {report.grade && (
                <div className="qbr-grade-banner">
                  <div className="qbr-grade-circle" style={{ background: gradeColor }}>{report.grade}</div>
                  <div className="qbr-grade-info">
                    {report.grade_label && <div className="qbr-grade-label">{report.grade_label}</div>}
                    {report.bottom_line && <div className="qbr-bottom-line">{report.bottom_line}</div>}
                  </div>
                </div>
              )}
              {!report.grade && report.bottom_line && <div className="qbr-bottom-line-standalone">{report.bottom_line}</div>}

              <div className="qbr-section-title">Executive Summary</div>
              <p className="qbr-text">{report.executive_summary}</p>

              {report.value_delivered && report.value_delivered.length > 0 && (
                <>
                  <div className="qbr-section-title">Value Delivered by CloudByte</div>
                  <div className="qbr-value-grid">
                    {report.value_delivered.map(function(v, i) { return (
                      <div key={i} className="qbr-value-item">
                        <div className="qbr-value-metric">{v.metric}</div>
                        <div className="qbr-value-detail">{v.detail}</div>
                      </div>
                    ); })}
                  </div>
                </>
              )}

              {report.metrics_snapshot && (
                <>
                  <div className="qbr-section-title">Key Metrics</div>
                  <div className="qbr-metrics-grid">
                    {Object.entries(report.metrics_snapshot).map(function(pair, i) { return (
                      <div key={i} className="qbr-metric-row">
                        <span className="qbr-metric-label">{pair[0]}</span>
                        <span className="qbr-metric-value">{pair[1]}</span>
                      </div>
                    ); })}
                  </div>
                </>
              )}

              {report.attention_items && report.attention_items.length > 0 && (
                <>
                  <div className="qbr-section-title">Needs Your Attention</div>
                  <div className="qbr-attention-list">
                    {report.attention_items.map(function(item, i) { return (
                      <div key={i} className={'qbr-attention-item qbr-attention-' + item.priority}>
                        <div className="qbr-attention-priority" style={{ color: priorityColors[item.priority] || 'var(--accent-blue)' }}>{priorityLabels[item.priority] || item.priority}</div>
                        <div className="qbr-attention-title">{item.title}</div>
                        <div className="qbr-attention-detail">{item.detail}</div>
                      </div>
                    ); })}
                  </div>
                </>
              )}

              {report.investments && report.investments.length > 0 && (
                <>
                  <div className="qbr-section-title">Strategic Investments</div>
                  <div className="qbr-investments-list">
                    {report.investments.map(function(inv, i) { return (
                      <div key={i} className="qbr-investment-item">
                        <div className="qbr-investment-title">{inv.title}</div>
                        <div className="qbr-investment-detail">{inv.detail}</div>
                      </div>
                    ); })}
                  </div>
                </>
              )}

              {report.project_status && (<><div className="qbr-section-title">Project Status</div><p className="qbr-text">{report.project_status}</p></>)}
              {report.budget_analysis && (<><div className="qbr-section-title">Budget & Licensing</div><p className="qbr-text">{report.budget_analysis}</p></>)}
              {report.identity_posture && (<><div className="qbr-section-title">Identity & Access</div><p className="qbr-text">{report.identity_posture}</p></>)}
              {report.compliance_notes && (<><div className="qbr-section-title">Compliance</div><p className="qbr-text">{report.compliance_notes}</p></>)}
            </>
          )}
          {!loading && !report && <div className="qbr-empty">No report generated yet.</div>}
        </div>
        <div className="qbr-footer">Prepared by CloudByte Group using Aurelius</div>
      </div>
    </>
  );
}

// ── AI Insights Panel ─────────────────────────────────────────────
var tabInsightLabels = {
  overview: 'Overview Insights', posture: 'Device Insights', identity: 'Identity Insights',
  siem: 'Threat Insights', support: 'Support Insights', projects: 'Project Insights',
  devops: 'Engineering Insights', changes: 'Change Insights', budget: 'Cost Insights',
  network: 'Network Insights', compliance: 'Compliance Insights', security: 'Security Insights'
};
var aiButtonLabels = {
  overview: 'Analyze Posture', posture: 'Analyze Devices', identity: 'Analyze Identity',
  siem: 'Analyze Threats', support: 'Analyze Support', projects: 'Analyze Projects',
  devops: 'Analyze Engineering', changes: 'Analyze Changes', budget: 'Analyze Costs',
  network: 'Analyze Network', compliance: 'Analyze Compliance', security: 'Analyze Security'
};
var tabEndpoints = {
  posture: '/posture', identity: '/entra', siem: '/siem',
  support: '/tickets', projects: '/projects', devops: '/jira',
  changes: '/changes', budget: '/subscriptions', network: '/network'
};

function getTabRuleInsights(tab, c, tabData) {
  var rules = [];

  if (tab === 'overview' || tab === 'security') {
    if (c.endpoints && c.endpoints.critical > 3) rules.push({ level: 'critical', text: c.endpoints.critical + ' devices offline — CloudByte tracking for reconnection.' });
    else if (c.endpoints && c.endpoints.critical > 0) rules.push({ level: 'high', text: c.endpoints.critical + ' device(s) offline — monitoring for auto-recovery.' });
    if (c.threats && c.threats.active > 0) rules.push({ level: 'critical', text: 'Huntress flagged ' + c.threats.active + ' active threat(s) — CloudByte investigating.' });
    if (c.endpoints && c.endpoints.total > 0) { var healthPct = Math.round((c.endpoints.healthy / c.endpoints.total) * 100); if (healthPct < 80) rules.push({ level: 'high', text: 'Fleet health at ' + healthPct + '% — ' + (100 - healthPct) + '% of devices need attention.' }); }
    if (c.huntress_agents && c.endpoints && c.endpoints.total > 0) { var cov = Math.round((c.huntress_agents.total / c.endpoints.total) * 100); if (cov < 90) rules.push({ level: 'medium', text: 'EDR coverage at ' + cov + '% — ' + (100 - cov) + '% of fleet pending agent deployment.' }); }
    if (c.siemSummary && c.siemSummary.threats > 0) rules.push({ level: 'high', text: c.siemSummary.threats + ' SIEM threat finding(s) — under active review by CloudByte.' });
  }

  if (tab === 'posture' && tabData && tabData.summary) {
    var s = tabData.summary;
    if (s.total > 0 && s.offline > 0) { var offPct = Math.round((s.offline / s.total) * 100); if (offPct > 10) rules.push({ level: 'high', text: offPct + '% of fleet offline (' + s.offline + ' of ' + s.total + ') — reconnection in progress.' }); else if (s.offline > 0) rules.push({ level: 'medium', text: s.offline + ' device(s) offline — monitoring for auto-recovery.' }); }
    if (s.avUnprotected > 0) rules.push({ level: 'high', text: s.avUnprotected + ' device(s) missing antivirus — remediation queued.' });
    if (s.defenderDisabled > 0) rules.push({ level: 'medium', text: 'Defender disabled on ' + s.defenderDisabled + ' device(s) — policy enforcement pending.' });
    if (s.total > 0) { var staleDevs = tabData.devices ? tabData.devices.filter(function(d) { return d.offline && d.lastContact && (Date.now() / 1000 - d.lastContact) > 30 * 86400; }).length : 0; if (staleDevs > 0) rules.push({ level: 'medium', text: staleDevs + ' device(s) inactive 30+ days — inventory cleanup recommended.' }); }
  }

  if (tab === 'identity' && tabData && tabData.summary) {
    var us = tabData.summary.users; var ds = tabData.summary.devices; var ins = tabData.summary.intune;
    if (us) {
      var disabledWithLicense = tabData.users ? tabData.users.filter(function(u) { return !u.enabled && u.licensed; }).length : 0;
      if (disabledWithLicense > 0) rules.push({ level: 'medium', text: disabledWithLicense + ' disabled account(s) still consuming licenses — reclaim to reduce spend.' });
      if (us.total > 0 && us.guests > 0) { var guestPct = Math.round((us.guests / us.total) * 100); if (guestPct > 20) rules.push({ level: 'medium', text: 'Guest accounts at ' + guestPct + '% of directory (' + us.guests + ') — review for access hygiene.' }); }
    }
    if (ds && ds.total > 0) { var nonCompliant = ds.total - ds.compliant; if (nonCompliant > 0) rules.push({ level: 'high', text: nonCompliant + ' Entra device(s) out of compliance — conditional access may block users.' }); }
    if (ins && ins.total > 0) { var unencrypted = ins.total - ins.encrypted; if (unencrypted > 0) rules.push({ level: 'high', text: unencrypted + ' device(s) missing disk encryption — data at rest exposed.' }); if (ins.nonCompliant > 0) rules.push({ level: 'medium', text: ins.nonCompliant + ' Intune device(s) non-compliant — policy remediation in progress.' }); }
  }

  if (tab === 'siem' && tabData) {
    var sm = tabData.summary || tabData;
    if (sm.priority_1 > 0) rules.push({ level: 'critical', text: sm.priority_1 + ' P1 finding(s) flagged — highest severity, CloudByte investigating.' });
    if (sm.threats > 0) rules.push({ level: 'high', text: sm.threats + ' confirmed threat finding(s) — active response underway.' });
    if (sm.suspects > 0) rules.push({ level: 'medium', text: sm.suspects + ' suspect finding(s) being triaged by CloudByte.' });
    if (sm.total_open > 20) rules.push({ level: 'medium', text: sm.total_open + ' open findings in queue — prioritized by severity.' });
  }

  if (tab === 'support' && tabData && tabData.metrics) {
    var m = tabData.metrics;
    if (m.open > 20) rules.push({ level: 'high', text: m.open + ' open tickets — elevated volume, triaging by priority.' });
    var stalled = (m.waitingForSupport || 0) + (m.pending || 0);
    if (stalled > 0) rules.push({ level: 'high', text: stalled + ' ticket(s) stalled — CloudByte escalation in progress.' });
    if (m.waitingForUser > 3) rules.push({ level: 'medium', text: m.waitingForUser + ' ticket(s) awaiting your team\'s response.' });
  }

  if (tab === 'projects' && tabData && tabData.projects) {
    var totalOverdue = 0, totalTasks = 0, totalCompleted = 0;
    tabData.projects.forEach(function(p) { totalOverdue += (p.overdueTasks || 0); totalTasks += (p.totalTasks || 0); totalCompleted += (p.completedTasks || 0); });
    if (totalOverdue > 0) rules.push({ level: 'high', text: totalOverdue + ' task(s) past due across ' + tabData.projects.length + ' project(s) — review timeline.' });
    tabData.projects.forEach(function(p) { if (p.dueDate && new Date(p.dueDate) < new Date() && !p.completed && p.progress < 90) rules.push({ level: 'high', text: p.name + ' past due at ' + p.progress + '% — may need scope adjustment.' }); });
    if (totalTasks > 0 && totalCompleted / totalTasks < 0.5 && tabData.projects.some(function(p) { return p.dueDate && new Date(p.dueDate) < new Date(Date.now() + 30 * 86400000); })) rules.push({ level: 'medium', text: 'Completion below 50% with deadlines within 30 days — escalation may be needed.' });
  }

  if (tab === 'devops' && tabData) {
    if (tabData.totalOverdue > 0) rules.push({ level: 'high', text: tabData.totalOverdue + ' overdue issue(s) — sprint velocity at risk.' });
    if (tabData.totalOpen > 50) rules.push({ level: 'medium', text: tabData.totalOpen + ' issues in backlog — consider grooming to maintain velocity.' });
    if (tabData.priorityBreakdown) { var highP = (tabData.priorityBreakdown['Highest'] || 0) + (tabData.priorityBreakdown['High'] || 0); if (highP > 5) rules.push({ level: 'high', text: highP + ' high-priority issue(s) should be in active sprints.' }); }
  }

  if (tab === 'changes' && tabData && tabData.summary) {
    var cs = tabData.summary;
    var pendingReview = (cs.submitted || 0) + (cs.under_review || 0);
    if (pendingReview > 3) rules.push({ level: 'medium', text: pendingReview + ' change request(s) awaiting approval — review to keep pipeline moving.' });
    if (cs.total > 0 && cs.denied > 0) { var denyRate = Math.round((cs.denied / cs.total) * 100); if (denyRate > 20) rules.push({ level: 'medium', text: 'Denial rate at ' + denyRate + '% — submissions may need better scoping.' }); }
  }

  if (tab === 'budget' && tabData) {
    var bs = tabData.summary || tabData;
    if (bs.totalSubscriptions !== undefined && bs.totalMonthlyCost) rules.push({ level: 'low', text: bs.totalSubscriptions + ' active subscriptions at $' + bs.totalMonthlyCost.toLocaleString() + '/mo managed by CloudByte.' });
  }

  if (tab === 'network' && tabData && tabData.summary) {
    var ns = tabData.summary;
    if (ns.offline > 0) rules.push({ level: 'high', text: ns.offline + ' network device(s) offline — investigating connectivity.' });
    if (ns.firmware_outdated > 0) rules.push({ level: 'medium', text: ns.firmware_outdated + ' device(s) on outdated firmware — update window being scheduled.' });
    if (ns.total > 0 && ns.online === ns.total) rules.push({ level: 'good', text: 'All ' + ns.total + ' network devices healthy and online.' });
  }

  if (tab === 'compliance') {
    if (c.scores && c.scores.overall > 0 && c.scores.overall < 80) rules.push({ level: 'high', text: 'Security score at ' + c.scores.overall + '/100 — below 80 target, remediation plan active.' });
    if (c.scores && c.scores.endpoint > 0 && c.scores.endpoint < 80) rules.push({ level: 'medium', text: 'Endpoint score at ' + c.scores.endpoint + '/100 — contributing to overall gap.' });
    if (c.scores && c.scores.threat > 0 && c.scores.threat < 80) rules.push({ level: 'medium', text: 'Threat score at ' + c.scores.threat + '/100 — detection coverage needs improvement.' });
  }

  if (rules.length === 0) rules.push({ level: 'good', text: 'All systems operational — CloudByte is actively monitoring your environment.' });
  return rules;
}

var insightLevelLabels = { critical: 'Action Required', high: 'Needs Attention', medium: 'Monitoring', low: 'Informational', good: 'All Clear' };

function InsightsPanel({ c, clientName, activeTab, clientId, onGenerateQBR }) {
  const [aiInsights, setAiInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('rules');
  const [tabData, setTabData] = useState(null);
  const [tabDataLoading, setTabDataLoading] = useState(false);

  useEffect(function() {
    setAiInsights(null);
    setMode('rules');
    setTabData(null);
    var endpoint = tabEndpoints[activeTab];
    if (endpoint && clientId) {
      setTabDataLoading(true);
      authFetch(API + '/clients/' + clientId + endpoint)
        .then(function(r) { return r.ok ? r.json() : null; })
        .then(function(data) { setTabData(data); setTabDataLoading(false); })
        .catch(function() { setTabDataLoading(false); });
    }
  }, [activeTab, clientId]);

  useEffect(function() { setAiInsights(null); setMode('rules'); setTabData(null); }, [clientName]);

  var ruleInsights = getTabRuleInsights(activeTab, c, tabData);

  var fetchAIInsights = async function() {
    setLoading(true);
    try {
      var resp = await authFetch(API + '/insights/' + clientId, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tab: activeTab }) });
      if (resp.ok) { var data = await resp.json(); setAiInsights(data.insights); setMode('ai'); }
    } catch (e) { console.warn('AI insights failed:', e); }
    setLoading(false);
  };

  var insights = mode === 'ai' && aiInsights ? aiInsights : ruleInsights;
  var tabLabel = tabInsightLabels[activeTab] || 'Insights';
  var aiLabel = aiButtonLabels[activeTab] || 'Analyze';

  return (
    <div className="insights-panel">
      <div className="insights-header">
        <div className="insights-header-left">
          {mode === 'ai' && <div className="insights-ai-badge">AI</div>}
          <div className="insights-tab-label">{mode === 'ai' ? 'Aurelius AI' : tabLabel}</div>
        </div>
        <div className="insights-toggle">
          {mode === 'ai' ? (
            <button className="insights-btn" onClick={function() { setMode('rules'); }}>Back to Rules</button>
          ) : (CAN_USE_ADMIN_FEATURES && (
            <button className="insights-btn insights-btn-ai" onClick={fetchAIInsights} disabled={loading}>
              {loading ? 'Analyzing...' : aiLabel}
            </button>
          ))}
        </div>
      </div>
      {loading && (
        <div className="insights-loading">
          <div className="insights-loading-pulse" />
          <div className="insights-loading-text">Aurelius is analyzing {clientName}...</div>
        </div>
      )}
      {!loading && tabDataLoading && <div className="insights-loading-subtle">Loading data...</div>}
      {!loading && (
        <div className="insights-list">
          {insights.map(function(ins, i) {
            if (ins.level === 'good') return (
              <div key={i} className="insight-clear">
                <div className="insight-clear-icon">&#10003;</div>
                <div>
                  <div className="insight-clear-label">All Clear</div>
                  <div className="insight-clear-text">{ins.text}</div>
                </div>
              </div>
            );
            return (
              <div key={i} className={'insight-card insight-card-' + ins.level} style={{ animationDelay: (i * 0.06) + 's' }}>
                <div className={'insight-level-badge insight-level-' + ins.level}>{insightLevelLabels[ins.level] || ins.level}</div>
                <div className="insight-card-text">{ins.text}</div>
              </div>
            );
          })}
        </div>
      )}
      {CAN_USE_ADMIN_FEATURES && (
        <button className="qbr-generate-btn" onClick={onGenerateQBR}>
          <span className="qbr-btn-text">Generate QBR Report</span>
          <span className="qbr-btn-sub">Full executive business review</span>
        </button>
      )}
    </div>
  );
}

function ClientNotFound({ clientId }) {
  return (
    <div className="app"><header className="header"><div className="header-left"><div className="logo">AURELIUS</div><span className="logo-sub">by CloudByte Group</span></div></header>
      <div className="client-not-found"><div className="not-found-icon">404</div><div className="not-found-title">Client Not Found</div><div className="not-found-text">The client "{clientId}" does not exist.</div></div>
    </div>
  );
}

// ── NavRail ─────────────────────────────────────────────────────────
// Vertical primary navigation. Replaces the horizontal tab strip so the
// catalog can grow past the width of the screen (Linear / Vercel /
// Bloomberg-terminal style). Collapses to a 64px icon rail; preference
// persists in localStorage. Groups are semantic, in priority order, with
// small-caps eyebrows. Active state is a left accent bar + tinted
// background + bolder weight. Red Team gets a marquee accent treatment.
//
// Hover flyouts (collapsed mode only) show section eyebrow + full label +
// any sub-items. The 'children' field is empty today but the markup is
// in place so the moment a tab grows sub-views we don't need to
// re-architect — just add { id, label } objects to the children array.

// Custom SVG icon set — 14 glyphs, single-stroke, monochrome, drawn for
// recognition at 18px. Inline so they tree-shake cleanly with React.
function NavIcon(props) {
  var name = props.name;
  var common = { width: 18, height: 18, viewBox: '0 0 18 18', fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true };
  switch (name) {
    case 'overview':   return <svg {...common}><rect x="2.5" y="2.5" width="5.5" height="5.5" rx="1"/><rect x="10" y="2.5" width="5.5" height="5.5" rx="1"/><rect x="2.5" y="10" width="5.5" height="5.5" rx="1"/><rect x="10" y="10" width="5.5" height="5.5" rx="1"/></svg>;
    case 'redteam':    return <svg {...common}><circle cx="9" cy="9" r="6"/><line x1="9" y1="2" x2="9" y2="4.5"/><line x1="9" y1="13.5" x2="9" y2="16"/><line x1="2" y1="9" x2="4.5" y2="9"/><line x1="13.5" y1="9" x2="16" y2="9"/><circle cx="9" cy="9" r="1.4" fill="currentColor" stroke="none"/></svg>;
    case 'security':   return <svg {...common}><path d="M9 2 L15 4.5 V8.5 C15 12 12.5 14.5 9 16 C5.5 14.5 3 12 3 8.5 V4.5 Z"/></svg>;
    case 'devices':    return <svg {...common}><rect x="3" y="4" width="12" height="8" rx="1"/><line x1="2" y1="14.5" x2="16" y2="14.5"/><line x1="7.5" y1="14.5" x2="10.5" y2="14.5" strokeWidth="2.4"/></svg>;
    case 'identity':   return <svg {...common}><circle cx="9" cy="6.5" r="2.7"/><path d="M3 15.5 C3 12.5 5.5 10.5 9 10.5 C12.5 10.5 15 12.5 15 15.5"/></svg>;
    case 'credentials':return <svg {...common}><circle cx="6" cy="9" r="2.8"/><line x1="8.8" y1="9" x2="16" y2="9"/><line x1="13" y1="9" x2="13" y2="11.2"/><line x1="15.2" y1="9" x2="15.2" y2="12"/></svg>;
    case 'threats':    return <svg {...common}><path d="M2 9 C4 5.5 6 4.5 9 4.5 C12 4.5 14 5.5 16 9 C14 12.5 12 13.5 9 13.5 C6 13.5 4 12.5 2 9 Z"/><circle cx="9" cy="9" r="2"/></svg>;
    case 'network':    return <svg {...common}><circle cx="9" cy="9" r="6"/><line x1="3" y1="9" x2="15" y2="9"/><ellipse cx="9" cy="9" rx="2.5" ry="6"/></svg>;
    case 'engineering':return <svg {...common}><polyline points="6.5,3 3,9 6.5,15"/><polyline points="11.5,3 15,9 11.5,15"/></svg>;
    case 'support':    return <svg {...common}><path d="M3 11 V9 C3 5.7 5.7 3 9 3 C12.3 3 15 5.7 15 9 V11"/><rect x="2.5" y="10.5" width="3" height="4.5" rx="0.7"/><rect x="12.5" y="10.5" width="3" height="4.5" rx="0.7"/></svg>;
    case 'projects':   return <svg {...common}><rect x="2.5" y="3" width="3.4" height="12" rx="0.6"/><rect x="7.3" y="3" width="3.4" height="9" rx="0.6"/><rect x="12.1" y="3" width="3.4" height="6" rx="0.6"/></svg>;
    case 'changes':    return <svg {...common}><path d="M3 5 H12 L10 3"/><path d="M3 5 V8"/><path d="M15 13 H6 L8 15"/><path d="M15 13 V10"/></svg>;
    case 'reviews':    return <svg {...common}><circle cx="9" cy="9" r="6"/><path d="M5.8 9 L8 11.2 L12.2 7"/></svg>;
    case 'budget':     return <svg {...common}><circle cx="9" cy="9" r="6"/><path d="M9 5.2 V12.8"/><path d="M11 7 H8 C6.9 7 6.9 9 8 9 H10 C11.1 9 11.1 11 10 11 H7"/></svg>;
    case 'compliance': return <svg {...common}><rect x="3" y="3" width="12" height="12" rx="1.5"/><path d="M5.8 9 L8 11.2 L12.2 7"/></svg>;
    // Setup tab: stylized "1-2-3 checklist" — three small numbered marks
    // stacked, the first checked. Reads as "guided onboarding" without
    // overlapping the compliance check icon (12.2,7 path).
    case 'setup':      return <svg {...common}><circle cx="4.5" cy="5"  r="1.6"/><path d="M3.6 5 L4.4 5.8 L5.6 4.4"/><line x1="7.5"  y1="5"  x2="14.5" y2="5"/><circle cx="4.5" cy="9"  r="1.6"/><line x1="7.5"  y1="9"  x2="14.5" y2="9"/><circle cx="4.5" cy="13" r="1.6"/><line x1="7.5"  y1="13" x2="14.5" y2="13"/></svg>;
    case 'agent':      return <svg {...common}><rect x="4" y="6" width="10" height="8" rx="2"/><line x1="9" y1="3" x2="9" y2="6"/><circle cx="9" cy="2.6" r="0.9" fill="currentColor" stroke="none"/><circle cx="6.8" cy="10" r="1" fill="currentColor" stroke="none"/><circle cx="11.2" cy="10" r="1" fill="currentColor" stroke="none"/></svg>;
    case 'work':       return <svg {...common}><rect x="2.5" y="2.5" width="13" height="13" rx="2"/><path d="M5.2 6.4 L6.6 7.8 L9 5.2"/><line x1="10.6" y1="6.4" x2="13.4" y2="6.4"/><path d="M5.2 11.4 L6.6 12.8 L9 10.2"/><line x1="10.6" y1="11.4" x2="13.4" y2="11.4"/></svg>;
    case 'service':    return <svg {...common}><path d="M4 9.5 V8.4 A5 5 0 0 1 14 8.4 V9.5"/><rect x="2.4" y="9" width="2.7" height="4.4" rx="1.1"/><rect x="12.9" y="9" width="2.7" height="4.4" rx="1.1"/><path d="M14 13.4 V14 A2.2 2.2 0 0 1 11.8 16.2 H9.4"/></svg>;
    default:           return <svg {...common}><circle cx="9" cy="9" r="3"/></svg>;
  }
}

var NAV_RAIL_GROUPS = [
  // The Setup item is conditional — NavRail filters it out for client
  // viewers and on the demo client (where data flows even though config
  // fields are null). The badge surfaces missing-integration count.
  { items: [
    { id: 'setup',    label: 'Setup',    icon: 'setup',    children: [], adminOnly: true, hideOnDemo: true },
    { id: 'agent',    label: 'Agent',    icon: 'agent',    children: [], adminOnly: true },
    { id: 'overview', label: 'Overview', icon: 'overview', children: [] },
    // Work is CBG's own delivery workspace (Asana replacement) — internal,
    // client-independent, admin-only. Sits at the top of the rail as a
    // daily-driver, not under a client-scoped group.
    { id: 'work',     label: 'Work',     icon: 'work',     children: [], adminOnly: true },
    // Service is CBG's own PSA — service desk + time tracking (Halo
    // replacement). Internal, client-independent, admin-only, like Work.
    { id: 'service',  label: 'Service',  icon: 'service',  children: [], adminOnly: true }
  ] },
  { items: [{ id: 'redteam',  label: 'Red Team', icon: 'redteam',  accent: true, children: [] }] },
  { label: 'Security Posture', items: [
    { id: 'security', label: 'Security',          icon: 'security',    children: [] },
    { id: 'posture',  label: 'Devices',           icon: 'devices',     children: [] },
    { id: 'identity', label: 'Identity',          icon: 'identity',    children: [] },
    { id: 'keeper',   label: 'Credentials',       icon: 'credentials', children: [] },
    { id: 'siem',     label: 'Threat Monitoring', icon: 'threats',     children: [] }
  ]},
  { label: 'Operations', items: [
    { id: 'network', label: 'Network',     icon: 'network',     children: [] },
    { id: 'devops',  label: 'Engineering', icon: 'engineering', children: [] },
    { id: 'support', label: 'Support',     icon: 'support',     children: [] }
  ]},
  { label: 'Workstreams', items: [
    { id: 'projects', label: 'Projects', icon: 'projects', children: [] },
    { id: 'changes',  label: 'Changes',  icon: 'changes',  children: [] },
    { id: 'reviews',  label: 'Reviews',  icon: 'reviews',  children: [] }
  ]},
  { label: 'Business', items: [
    { id: 'budget',     label: 'Budget',     icon: 'budget',     children: [] },
    { id: 'compliance', label: 'Compliance', icon: 'compliance', children: [] }
  ]}
];

// Orgs where the Agent tab is enabled (mirrors the backend AGENT_ORGS allowlist;
// backend is authoritative — this only controls the greyed nav treatment).
var AGENT_ENABLED_ORG_IDS = ['gsi', 'metro', 'lynk'];

function NavRail({ activeTab, onTabChange, badgeData, collapsed, onToggleCollapse, isAdmin, isDemo, allowedTabs, activeClient }) {
  // Flyouts use viewport-fixed positioning via a portal because the rail's
  // own scroll container (overflow-y: auto) would otherwise clip them.
  // CSS can't allow vertical scroll + horizontal overflow simultaneously
  // in the same element — browsers coerce overflow-x to auto when y is auto.
  // The portal escapes that constraint cleanly.
  var [flyoutState, setFlyoutState] = useState(null);  // { item, group, top, left }
  var leaveTimerRef = useRef(null);

  function showFlyout(item, group, e) {
    if (!collapsed) return;
    if (leaveTimerRef.current) { clearTimeout(leaveTimerRef.current); leaveTimerRef.current = null; }
    var btn = e.currentTarget.closest('.nav-rail-item-wrap') || e.currentTarget;
    var rect = btn.getBoundingClientRect();
    setFlyoutState({
      item: item,
      group: group,
      top: rect.top - 6,
      left: rect.right + 8
    });
  }
  function scheduleHide() {
    if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
    leaveTimerRef.current = setTimeout(function() { setFlyoutState(null); leaveTimerRef.current = null; }, 120);
  }
  function cancelHide() {
    if (leaveTimerRef.current) { clearTimeout(leaveTimerRef.current); leaveTimerRef.current = null; }
  }

  // Hide flyouts when the rail expands (collapsed → expanded transition)
  useEffect(function() {
    if (!collapsed) setFlyoutState(null);
  }, [collapsed]);

  // Hide flyout when the active tab changes (clicking through it)
  useEffect(function() { setFlyoutState(null); }, [activeTab]);

  // Cleanup on unmount
  useEffect(function() {
    return function() { if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current); };
  }, []);

  return (
    <nav className={'nav-rail' + (collapsed ? ' nav-rail-collapsed' : '')} aria-label="Primary navigation">
      <div className="nav-rail-scroll">
        {NAV_RAIL_GROUPS.map(function(group, gi) {
          // Filter items by visibility rules per render context.
          var visibleItems = group.items.filter(function(it) {
            if (it.adminOnly && !isAdmin) return false;
            if (it.hideOnDemo && isDemo) return false;
            // Restricted-user allowlist (S1.14) — when allowedTabs is set,
            // hide anything not in the list. Overview is always shown if
            // explicitly allowed; Setup is hidden anyway via adminOnly.
            if (Array.isArray(allowedTabs) && allowedTabs.length > 0 && allowedTabs.indexOf(it.id) === -1) return false;
            return true;
          });
          if (visibleItems.length === 0) return null;
          return (
            <div key={gi} className="nav-rail-group">
              {group.label && !collapsed && <div className="nav-rail-eyebrow">{group.label}</div>}
              {group.label && collapsed && gi > 0 && <div className="nav-rail-divider" />}
              {visibleItems.map(function(item) {
                var active = activeTab === item.id;
                var badge = badgeData && badgeData[item.id];
                var hasChildren = Array.isArray(item.children) && item.children.length > 0;
                var muted = item.id === 'agent' && activeClient && AGENT_ENABLED_ORG_IDS.indexOf(activeClient) < 0;
                return (
                  <div
                    key={item.id}
                    className="nav-rail-item-wrap"
                    onMouseEnter={function(e) { showFlyout(item, group, e); }}
                    onMouseLeave={scheduleHide}
                    onFocus={function(e) { showFlyout(item, group, e); }}
                    onBlur={scheduleHide}
                  >
                    <button
                      className={'nav-rail-item' + (active ? ' active' : '') + (item.accent ? ' accent' : '') + (muted ? ' nav-rail-item-muted' : '')}
                      onClick={function() { onTabChange(item.id); }}
                      aria-current={active ? 'page' : undefined}
                      aria-haspopup={hasChildren ? 'menu' : undefined}
                      title={muted ? 'Agentic operations aren’t enabled for this org yet' : undefined}
                    >
                      <span className="nav-rail-icon"><NavIcon name={item.icon} /></span>
                      <span className="nav-rail-label">{item.label}</span>
                      {badge && badge.type === 'dot' && <span className="nav-rail-badge-dot" />}
                      {badge && badge.type === 'count' && badge.count > 0 && (
                        <span className="nav-rail-badge-count">{badge.count > 99 ? '99+' : badge.count}</span>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      <button className="nav-rail-collapse-btn" onClick={onToggleCollapse} title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
        <span>{collapsed ? '›' : '‹'}</span>
        {!collapsed && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600 }}>Collapse</span>}
      </button>

      {/* Portal-rendered flyout — escapes the rail's overflow:auto so it overlays
          the dashboard content cleanly. Only renders in collapsed mode. */}
      {collapsed && flyoutState && createPortal(
        <NavRailFlyout
          item={flyoutState.item}
          group={flyoutState.group}
          top={flyoutState.top}
          left={flyoutState.left}
          activeTab={activeTab}
          onPick={onTabChange}
          onMouseEnter={cancelHide}
          onMouseLeave={scheduleHide}
        />,
        document.body
      )}
    </nav>
  );
}

function NavRailFlyout({ item, group, top, left, activeTab, onPick, onMouseEnter, onMouseLeave }) {
  var hasChildren = Array.isArray(item.children) && item.children.length > 0;
  var active = activeTab === item.id;
  return (
    <div
      className="nav-rail-flyout nav-rail-flyout-portal"
      role="menu"
      aria-label={item.label + ' menu'}
      style={{ top: top + 'px', left: left + 'px' }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {group && group.label && <div className="nav-rail-flyout-eyebrow">{group.label}</div>}
      <button
        className={'nav-rail-flyout-title' + (active ? ' active' : '')}
        onClick={function() { onPick(item.id); }}
        role="menuitem"
      >
        {item.label}
      </button>
      {hasChildren && (
        <div className="nav-rail-flyout-children">
          {item.children.map(function(child) {
            return (
              <button
                key={child.id}
                className="nav-rail-flyout-child"
                onClick={function() { onPick(child.id || item.id); }}
                role="menuitem"
              >
                {child.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── InsightsDrawer + InsightsFab ────────────────────────────────────
// Replaces the persistent right-sidebar. Reclaims ~320px of content
// width while making the AI/insights surface still one click away.
// Same vocabulary as QBRPanel + ChangelogPanel — slide from right,
// backdrop, ESC closes.
function InsightsFab({ onClick, hasAi, hidden }) {
  if (hidden) return null;
  return (
    <button className="insights-fab" onClick={onClick} aria-label="Open insights">
      <span className="insights-fab-glyph">✦</span>
      <span className="insights-fab-text">Insights</span>
      {hasAi && <span className="insights-fab-ai">AI</span>}
    </button>
  );
}

function InsightsDrawer(props) {
  // ESC to close
  useEffect(function() {
    if (!props.isOpen) return;
    function onKey(e) { if (e.key === 'Escape') props.onClose(); }
    window.addEventListener('keydown', onKey);
    return function() { window.removeEventListener('keydown', onKey); };
  }, [props.isOpen, props.onClose]);

  return (
    <>
      <div className={'insights-drawer-overlay' + (props.isOpen ? ' open' : '')} onClick={props.onClose} aria-hidden="true" />
      <aside
        className={'insights-drawer' + (props.isOpen ? ' open' : '')}
        role="dialog"
        aria-modal={props.isOpen ? 'true' : undefined}
        aria-label="Insights"
        aria-hidden={!props.isOpen}
      >
        <div className="insights-drawer-header">
          <div className="insights-drawer-title-block">
            <span className="insights-drawer-eyebrow">{props.clientName}</span>
            <span className="insights-drawer-title">Insights</span>
          </div>
          <button className="insights-drawer-close" onClick={props.onClose} aria-label="Close insights">×</button>
        </div>
        <div className="insights-drawer-body">
          {props.isOpen && (
            <InsightsPanel
              c={props.c}
              clientName={props.clientName}
              activeTab={props.activeTab}
              clientId={props.clientId}
              onGenerateQBR={props.onGenerateQBR}
            />
          )}
        </div>
      </aside>
    </>
  );
}

// ── Admin Panel ──────────────────────────────────────────────────────
// Admin-only surface for managing users, browsing the audit log, and
// revoking active sessions. Gated by role='admin' on backend + frontend.

function fmtDateTime(s) {
  if (!s) return '—';
  var d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}
function fmtDateShort(s) {
  if (!s) return '—';
  var d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
}
function isUserLocked(u) {
  return u.locked_until && new Date(u.locked_until).getTime() > Date.now();
}

function AdminPanel({ clientList, refreshClientList }) {
  // If the admin entered admin mode via a "Connect [Vendor]" CTA from
  // the client view, the intent is in sessionStorage. Default the
  // active tab to 'integrations' so AdminIntegrationsTab mounts +
  // pops the intent + opens the right Edit modal in one go.
  var initialTab = (function() {
    try {
      var raw = sessionStorage.getItem('aurelius_admin_integrations_intent');
      if (raw) {
        var intent = JSON.parse(raw);
        if (intent && intent.clientId && (Date.now() - (intent.ts || 0)) <= 5 * 60 * 1000) return 'integrations';
      }
    } catch (e) {}
    return 'users';
  })();
  var [activeTab, setActiveTab] = useState(initialTab);
  var [stats, setStats] = useState(null);

  useEffect(function() {
    authFetch(API + '/admin/stats').then(function(r) { return r.ok ? r.json() : null; }).then(function(s) { setStats(s); }).catch(function() {});
  }, [activeTab]);

  return (
    <div className="admin-panel">
      <div className="admin-panel-header">
        <div>
          <div className="admin-panel-title">Administrator Panel</div>
          <div className="admin-panel-sub">Users · Audit · Sessions — actions here are logged permanently</div>
        </div>
        {stats && (
          <div className="admin-panel-stats">
            <div className="admin-stat"><div className="admin-stat-num">{stats.users_total}</div><div className="admin-stat-lbl">Users</div></div>
            <div className="admin-stat"><div className="admin-stat-num">{stats.users_enabled}</div><div className="admin-stat-lbl">Enabled</div></div>
            <div className="admin-stat"><div className="admin-stat-num">{stats.users_locked}</div><div className="admin-stat-lbl">Locked</div></div>
            <div className="admin-stat"><div className="admin-stat-num">{stats.audit_24h}</div><div className="admin-stat-lbl">Events / 24h</div></div>
            <div className="admin-stat"><div className="admin-stat-num">{stats.warn_24h}</div><div className="admin-stat-lbl">Warnings / 24h</div></div>
          </div>
        )}
      </div>
      <div className="admin-tabs">
        <button className={'admin-tab ' + (activeTab === 'users' ? 'active' : '')} onClick={function() { setActiveTab('users'); }}>Users</button>
        <button className={'admin-tab ' + (activeTab === 'integrations' ? 'active' : '')} onClick={function() { setActiveTab('integrations'); }}>Integrations</button>
        <button className={'admin-tab ' + (activeTab === 'permissions' ? 'active' : '')} onClick={function() { setActiveTab('permissions'); }}>Permissions</button>
        <button className={'admin-tab ' + (activeTab === 'assessments' ? 'active' : '')} onClick={function() { setActiveTab('assessments'); }}>Tenant Assessments</button>
        <button className={'admin-tab ' + (activeTab === 'automation' ? 'active' : '')} onClick={function() { setActiveTab('automation'); }}>Automation</button>
        <button className={'admin-tab ' + (activeTab === 'audit' ? 'active' : '')} onClick={function() { setActiveTab('audit'); }}>Audit Log</button>
        <button className={'admin-tab ' + (activeTab === 'sessions' ? 'active' : '')} onClick={function() { setActiveTab('sessions'); }}>Active Sessions</button>
      </div>
      <div className="admin-tab-content">
        {activeTab === 'users' && <AdminUsersTab clientList={clientList} />}
        {activeTab === 'integrations' && <AdminIntegrationsTab refreshClientList={refreshClientList} />}
        {activeTab === 'permissions' && <AdminPermissionsTab />}
        {activeTab === 'assessments' && <AdminAssessmentsTab />}
        {activeTab === 'automation' && <AdminAutomationTab clientList={clientList} />}
        {activeTab === 'audit' && <AdminAuditTab />}
        {activeTab === 'sessions' && <AdminSessionsTab />}
      </div>
    </div>
  );
}

function AdminUsersTab({ clientList }) {
  var [users, setUsers] = useState([]);
  var [loading, setLoading] = useState(true);
  var [filter, setFilter] = useState('');
  var [showDeleted, setShowDeleted] = useState(false);
  var [editingUser, setEditingUser] = useState(null);
  var [creatingUser, setCreatingUser] = useState(false);
  var [revealedPassword, setRevealedPassword] = useState(null);
  var [toast, setToast] = useState(null);

  function showToast(msg, type) {
    setToast({ msg: msg, type: type || 'info' });
    setTimeout(function() { setToast(null); }, 3500);
  }

  function fetchUsers() {
    setLoading(true);
    var qs = showDeleted ? '?includeDeleted=true' : '';
    authFetch(API + '/admin/users' + qs)
      .then(function(r) { return r.ok ? r.json() : []; })
      .then(function(data) { setUsers(data); setLoading(false); })
      .catch(function() { setLoading(false); showToast('Failed to load users', 'error'); });
  }

  useEffect(fetchUsers, [showDeleted]);

  function onResetPassword(u) {
    if (!window.confirm('Reset password for ' + u.email + '?\n\nThis will immediately invalidate their current password.')) return;
    authFetch(API + '/admin/users/' + u.id + '/reset-password', { method: 'POST' })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        if (!x.ok) { showToast(x.d.error || 'Reset failed', 'error'); return; }
        setRevealedPassword({ email: x.d.user.email, password: x.d.password, title: 'Password reset' });
        fetchUsers();
      })
      .catch(function() { showToast('Reset failed', 'error'); });
  }

  function onUnlock(u) {
    authFetch(API + '/admin/users/' + u.id + '/unlock', { method: 'POST' })
      .then(function(r) { if (r.ok) { showToast('Unlocked ' + u.email, 'ok'); fetchUsers(); } else { showToast('Unlock failed', 'error'); } })
      .catch(function() { showToast('Unlock failed', 'error'); });
  }

  function onToggleEnabled(u) {
    var verb = u.enabled ? 'disable' : 'enable';
    if (u.enabled && !window.confirm('Disable ' + u.email + '?\n\nThey will not be able to log in. Active sessions remain valid until they expire naturally.')) return;
    authFetch(API + '/admin/users/' + u.id + '/' + verb, { method: 'POST' })
      .then(function(r) { if (r.ok) { showToast((verb === 'disable' ? 'Disabled ' : 'Enabled ') + u.email, 'ok'); fetchUsers(); } else { showToast(verb + ' failed', 'error'); } })
      .catch(function() { showToast(verb + ' failed', 'error'); });
  }

  function onDelete(u) {
    if (!window.confirm('Delete ' + u.email + '?\n\nThe user will be soft-deleted — row preserved for audit integrity, all sessions revoked immediately, restorable from the "Show deleted" view. Hard delete requires DB admin.')) return;
    authFetch(API + '/admin/users/' + u.id + '/delete', { method: 'POST' })
      .then(function(r) { if (r.ok) { showToast('Deleted ' + u.email, 'ok'); fetchUsers(); } else { r.json().then(function(d) { showToast(d.error || 'Delete failed', 'error'); }); } })
      .catch(function() { showToast('Delete failed', 'error'); });
  }

  function onRestore(u) {
    authFetch(API + '/admin/users/' + u.id + '/restore', { method: 'POST' })
      .then(function(r) { if (r.ok) { showToast('Restored ' + u.email, 'ok'); fetchUsers(); } else { showToast('Restore failed', 'error'); } })
      .catch(function() { showToast('Restore failed', 'error'); });
  }

  var filtered = users.filter(function(u) {
    if (!filter) return true;
    var q = filter.toLowerCase();
    return (u.email || '').toLowerCase().includes(q)
        || (u.client_id || '').toLowerCase().includes(q)
        || (u.display_name || '').toLowerCase().includes(q)
        || (u.role || '').toLowerCase().includes(q)
        || (Array.isArray(u.tags) && u.tags.some(function(t) { return t.toLowerCase().includes(q); }));
  });

  return (
    <div className="admin-users-tab">
      <div className="admin-toolbar">
        <input className="admin-filter-input" placeholder="Filter by email, client, role, tag..." value={filter} onChange={function(e) { setFilter(e.target.value); }} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer', color: showDeleted ? '#f87171' : 'var(--text-muted)' }}>
          <input type="checkbox" checked={showDeleted} onChange={function(e) { setShowDeleted(e.target.checked); }} />
          Show deleted
        </label>
        <div style={{ flex: 1 }} />
        <button className="admin-btn primary" onClick={function() { setCreatingUser(true); }}>+ New User</button>
        <button className="admin-btn" onClick={fetchUsers} disabled={loading}>{loading ? 'Loading...' : 'Refresh'}</button>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Role</th>
              <th>Client</th>
              <th>Status</th>
              <th>Last Login</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="6" style={{ padding: 24, textAlign: 'center', opacity: 0.5 }}>Loading users...</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan="6" style={{ padding: 24, textAlign: 'center', opacity: 0.5 }}>No users match "{filter}"</td></tr>}
            {!loading && filtered.map(function(u) {
              var locked = isUserLocked(u);
              var isDeleted = !!u.deleted_at;
              return (
                <tr key={u.id} className={(isDeleted ? 'deleted-row ' : '') + (!u.enabled && !isDeleted ? 'disabled-row' : '')}>
                  <td>
                    <div style={{ fontWeight: 600, textDecoration: isDeleted ? 'line-through' : 'none' }}>{u.email}</div>
                    {u.display_name && <div style={{ fontSize: 11, opacity: 0.6 }}>{u.display_name}</div>}
                    {Array.isArray(u.tags) && u.tags.length > 0 && (
                      <div style={{ marginTop: 4, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {u.tags.map(function(t) { return <span key={t} className="admin-tag-chip">{t}</span>; })}
                      </div>
                    )}
                    {u.notes && <div style={{ marginTop: 4, fontSize: 10, opacity: 0.55, fontStyle: 'italic', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={u.notes}>{u.notes}</div>}
                  </td>
                  <td><span className={'admin-role-chip ' + u.role}>{u.role}</span></td>
                  <td>{u.client_id || <span style={{ opacity: 0.4 }}>—</span>}</td>
                  <td>
                    {isDeleted
                      ? <span className="admin-chip bad">Deleted {fmtDateShort(u.deleted_at)}</span>
                      : u.enabled
                        ? <span className="admin-chip ok">Active</span>
                        : <span className="admin-chip bad">Disabled</span>}
                    {locked && !isDeleted && <span className="admin-chip warn" style={{ marginLeft: 6 }}>Locked</span>}
                    {u.must_change_password && !isDeleted && <span className="admin-chip info" style={{ marginLeft: 6 }}>Must change PW</span>}
                    {u.failed_login_count > 0 && !locked && !isDeleted && <span className="admin-chip warn-soft" style={{ marginLeft: 6 }}>{u.failed_login_count}/5 failed</span>}
                    {u.entra_object_id && !isDeleted && <span className="admin-chip info" style={{ marginLeft: 6 }} title={'Entra oid ' + u.entra_object_id.slice(0, 8) + '…'}>SSO</span>}
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {u.last_login_at ? fmtDateTime(u.last_login_at) : <span style={{ opacity: 0.4 }}>Never</span>}
                    {u.last_login_ip && <div style={{ fontSize: 10, opacity: 0.5 }}>from {u.last_login_ip}</div>}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <div className="admin-row-actions">
                      {isDeleted ? (
                        <button className="admin-btn sm ok" onClick={function() { onRestore(u); }}>Restore</button>
                      ) : (
                        <>
                          <button className="admin-btn sm" onClick={function() { setEditingUser(u); }}>Edit</button>
                          <button className="admin-btn sm" onClick={function() { onResetPassword(u); }}>Reset PW</button>
                          {locked && <button className="admin-btn sm warn" onClick={function() { onUnlock(u); }}>Unlock</button>}
                          <button className={'admin-btn sm ' + (u.enabled ? 'danger' : 'ok')} onClick={function() { onToggleEnabled(u); }}>{u.enabled ? 'Disable' : 'Enable'}</button>
                          <button className="admin-btn sm danger" onClick={function() { onDelete(u); }}>Delete</button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {(creatingUser || editingUser) && (
        <AdminUserModal
          user={editingUser}
          clientList={clientList}
          onClose={function() { setEditingUser(null); setCreatingUser(false); }}
          onSaved={function(result) {
            if (result.password) setRevealedPassword({ email: result.user.email, password: result.password, title: 'User created' });
            fetchUsers();
            setEditingUser(null);
            setCreatingUser(false);
          }}
          onError={function(msg) { showToast(msg, 'error'); }}
        />
      )}
      {revealedPassword && <AdminPasswordReveal data={revealedPassword} onClose={function() { setRevealedPassword(null); }} />}
      {toast && <AdminToast {...toast} />}
    </div>
  );
}

function AdminUserModal({ user, clientList, onClose, onSaved, onError }) {
  var isEdit = !!user;
  var [form, setForm] = useState({
    email: user ? user.email : '',
    role: user ? user.role : 'client',
    clientId: user ? (user.client_id || '') : '',
    displayName: user ? (user.display_name || '') : '',
    enabled: user ? user.enabled : true,
    notes: user ? (user.notes || '') : '',
    tags: user && Array.isArray(user.tags) ? user.tags.slice() : [],
    // Restricted-user fields (S1.14). null/empty allowedTabs → no
    // per-tab restriction; the user gets full access per their role.
    allowedTabs: user && Array.isArray(user.allowed_tabs) ? user.allowed_tabs.slice() : [],
    readOnly: user ? !!user.read_only : false,
    roleLabel: user && user.role_label ? user.role_label : '',
  });
  var [tagInput, setTagInput] = useState('');
  var [emailInput, setEmailInput] = useState(user ? user.email : '');
  var [saving, setSaving] = useState(false);
  var [emailSaving, setEmailSaving] = useState(false);
  var [showAdvanced, setShowAdvanced] = useState(false);

  function addTagFromInput() {
    var v = tagInput.trim().toLowerCase().slice(0, 32);
    if (!v) return;
    if (form.tags.indexOf(v) >= 0) { setTagInput(''); return; }
    if (form.tags.length >= 12) { onError('Max 12 tags per user'); return; }
    setForm(Object.assign({}, form, { tags: form.tags.concat([v]) }));
    setTagInput('');
  }
  function removeTag(t) {
    setForm(Object.assign({}, form, { tags: form.tags.filter(function(x) { return x !== t; }) }));
  }

  function submit(e) {
    e.preventDefault();
    if (!form.email || !form.role) { onError('Email and role are required'); return; }
    if (form.role === 'client' && !form.clientId) { onError('clientId is required for role=client'); return; }
    setSaving(true);
    if (isEdit) {
      authFetch(API + '/admin/users/' + user.id, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role: form.role, clientId: form.clientId || null,
          displayName: form.displayName, enabled: form.enabled,
          notes: form.notes, tags: form.tags,
          allowedTabs: form.role === 'client' && form.allowedTabs.length > 0 ? form.allowedTabs : null,
          readOnly: form.readOnly,
          roleLabel: form.roleLabel || null
        })
      })
        .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
        .then(function(x) { setSaving(false); if (x.ok) onSaved({ user: x.d }); else onError(x.d.error || 'Update failed'); })
        .catch(function() { setSaving(false); onError('Update failed'); });
    } else {
      authFetch(API + '/admin/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: form.email, role: form.role, clientId: form.clientId || null,
          displayName: form.displayName, enabled: form.enabled,
          allowedTabs: form.role === 'client' && form.allowedTabs.length > 0 ? form.allowedTabs : null,
          readOnly: form.readOnly,
          roleLabel: form.roleLabel || null
        })
      })
        .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
        .then(function(x) {
          setSaving(false);
          if (x.ok) {
            // After creating the user, send a follow-up PATCH if tags/notes are set (create endpoint doesn't accept them)
            if (x.d && x.d.user && (form.notes || form.tags.length > 0)) {
              authFetch(API + '/admin/users/' + x.d.user.id, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ notes: form.notes, tags: form.tags })
              }).finally(function() { onSaved(x.d); });
            } else {
              onSaved(x.d);
            }
          } else onError(x.d.error || 'Create failed');
        })
        .catch(function() { setSaving(false); onError('Create failed'); });
    }
  }

  function saveEmail() {
    if (!emailInput || emailInput === user.email) return;
    if (!window.confirm('Change email from ' + user.email + ' to ' + emailInput + '?\n\nThis is audited as USER_EMAIL_CHANGED. The user will need to use the new email to log in next time.')) return;
    setEmailSaving(true);
    authFetch(API + '/admin/users/' + user.id + '/email', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: emailInput })
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) { setEmailSaving(false); if (x.ok) onSaved({ user: x.d }); else onError(x.d.error || 'Email change failed'); })
      .catch(function() { setEmailSaving(false); onError('Email change failed'); });
  }

  function unlinkSso() {
    if (!window.confirm('Remove the Microsoft SSO link for ' + user.email + '?\n\nThey will need to sign in via SSO again (first sign-in re-links by email).')) return;
    authFetch(API + '/admin/users/' + user.id + '/unlink-sso', { method: 'POST' })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) { if (x.ok) onSaved({ user: x.d }); else onError(x.d.error || 'Unlink failed'); })
      .catch(function() { onError('Unlink failed'); });
  }

  function revokeSessions() {
    if (!window.confirm('Revoke ALL active sessions for ' + user.email + '?\n\nThey will be logged out of every device immediately. This is a security action.')) return;
    authFetch(API + '/admin/users/' + user.id + '/revoke-sessions', { method: 'POST' })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) { if (x.ok) onSaved({ user: Object.assign({}, user, { __revoked: x.d.revoked }) }); else onError(x.d.error || 'Revoke failed'); })
      .catch(function() { onError('Revoke failed'); });
  }

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={function(e) { e.stopPropagation(); }} style={{ maxWidth: 520 }}>
        <form onSubmit={submit}>
          <div className="admin-modal-header">{isEdit ? 'Edit user' : 'New user'}</div>
          <div className="admin-modal-body">
            {/* Email — separate save flow on edit because it has its own audit event */}
            <div className="admin-field">
              <label>Email</label>
              {isEdit ? (
                <div style={{ display: 'flex', gap: 6 }}>
                  <input type="email" style={{ flex: 1 }} value={emailInput} onChange={function(e) { setEmailInput(e.target.value); }} />
                  <button type="button" className="admin-btn sm" onClick={saveEmail} disabled={emailSaving || !emailInput || emailInput === user.email}>{emailSaving ? 'Saving...' : 'Update'}</button>
                </div>
              ) : (
                <input type="email" required value={form.email} onChange={function(e) { setForm(Object.assign({}, form, { email: e.target.value })); }} />
              )}
              {isEdit && <div className="admin-field-hint">Change is audited as USER_EMAIL_CHANGED. Case-insensitive, must be unique across active users.</div>}
            </div>

            <div className="admin-field">
              <label>Display name</label>
              <input type="text" value={form.displayName} onChange={function(e) { setForm(Object.assign({}, form, { displayName: e.target.value })); }} placeholder="Optional — shown in UI" />
            </div>

            <div className="admin-field">
              <label>Role</label>
              <select value={form.role} onChange={function(e) { setForm(Object.assign({}, form, { role: e.target.value, clientId: e.target.value === 'admin' ? '' : form.clientId })); }}>
                <option value="client">client</option>
                <option value="admin">admin</option>
              </select>
            </div>

            {form.role === 'client' && (
              <div className="admin-field">
                <label>Client</label>
                <select value={form.clientId} onChange={function(e) { setForm(Object.assign({}, form, { clientId: e.target.value })); }}>
                  <option value="">— Select a client —</option>
                  {(clientList || []).map(function(c) { return <option key={c.id} value={c.id}>{c.id} — {c.name}</option>; })}
                </select>
                <div className="admin-field-hint">Scopes all queries from this user to the selected client.</div>
              </div>
            )}

            <div className="admin-field">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.enabled} onChange={function(e) { setForm(Object.assign({}, form, { enabled: e.target.checked })); }} />
                <span>Enabled</span>
              </label>
            </div>

            {/* Restricted-user controls (S1.14) — only meaningful for client role.
                Empty allowedTabs = full client access; populate to scope. */}
            {form.role === 'client' && (
              <div className="admin-field" style={{ background: 'rgba(96,165,250,0.04)', border: '1px solid rgba(96,165,250,0.2)', borderRadius: 6, padding: 12 }}>
                <label style={{ fontWeight: 700, color: '#60a5fa', fontSize: 11, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Restricted access (optional)</label>
                <div className="admin-field-hint" style={{ marginTop: 4 }}>Leave empty for full client access. Pick specific tabs to scope this user to only those.</div>

                <div style={{ marginTop: 10, marginBottom: 8 }}>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6 }}>Allowed tabs</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
                    {[
                      { id: 'overview', label: 'Overview' },
                      { id: 'redteam', label: 'Red Team' },
                      { id: 'security', label: 'Security' },
                      { id: 'posture', label: 'Devices' },
                      { id: 'identity', label: 'Identity' },
                      { id: 'keeper', label: 'Credentials' },
                      { id: 'siem', label: 'Threats' },
                      { id: 'network', label: 'Network' },
                      { id: 'devops', label: 'Engineering' },
                      { id: 'support', label: 'Support' },
                      { id: 'projects', label: 'Projects' },
                      { id: 'changes', label: 'Changes' },
                      { id: 'reviews', label: 'Reviews' },
                      { id: 'budget', label: 'Budget' },
                      { id: 'compliance', label: 'Compliance' }
                    ].map(function(t) {
                      var on = form.allowedTabs.indexOf(t.id) !== -1;
                      return (
                        <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: on ? '#fff' : '#cbd5e1', padding: '4px 8px', background: on ? 'rgba(96,165,250,0.15)' : 'rgba(255,255,255,0.02)', border: '1px solid ' + (on ? 'rgba(96,165,250,0.5)' : 'rgba(255,255,255,0.08)'), borderRadius: 4, cursor: 'pointer' }}>
                          <input type="checkbox" checked={on} onChange={function(e) {
                            var next = form.allowedTabs.slice();
                            if (e.target.checked) { if (next.indexOf(t.id) === -1) next.push(t.id); }
                            else { next = next.filter(function(x) { return x !== t.id; }); }
                            setForm(Object.assign({}, form, { allowedTabs: next }));
                          }} />
                          <span>{t.label}</span>
                        </label>
                      );
                    })}
                  </div>
                  {form.allowedTabs.length > 0 && (
                    <button type="button" className="admin-btn sm" style={{ marginTop: 6 }} onClick={function() { setForm(Object.assign({}, form, { allowedTabs: [] })); }}>Clear (= full access)</button>
                  )}
                </div>

                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 10 }}>
                  <input type="checkbox" checked={form.readOnly} onChange={function(e) { setForm(Object.assign({}, form, { readOnly: e.target.checked })); }} />
                  <span style={{ fontSize: 12 }}>Read-only (server blocks every non-GET on /api/clients/&lt;id&gt;/* · frontend hides edit affordances on aware tabs)</span>
                </label>

                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>Role label (cosmetic — appears in admin listings)</div>
                  <input type="text" value={form.roleLabel} onChange={function(e) { setForm(Object.assign({}, form, { roleLabel: e.target.value })); }} placeholder="e.g. GSI Budget Auditor" style={{ width: '100%', padding: 6, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, color: '#fff', fontSize: 12 }} />
                </div>
              </div>
            )}

            <div className="admin-field">
              <label>Tags</label>
              <div className="admin-tag-editor">
                {form.tags.map(function(t) {
                  return (
                    <span key={t} className="admin-tag-chip editable">
                      {t}
                      <button type="button" onClick={function() { removeTag(t); }} title="Remove tag" aria-label="Remove tag">×</button>
                    </span>
                  );
                })}
                <input
                  type="text"
                  className="admin-tag-input"
                  placeholder={form.tags.length === 0 ? 'vip, on-leave, test-account…' : 'Add tag'}
                  value={tagInput}
                  onChange={function(e) { setTagInput(e.target.value); }}
                  onKeyDown={function(e) {
                    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTagFromInput(); }
                    else if (e.key === 'Backspace' && !tagInput && form.tags.length > 0) { removeTag(form.tags[form.tags.length - 1]); }
                  }}
                  onBlur={addTagFromInput}
                />
              </div>
              <div className="admin-field-hint">Press Enter or comma to add. Lowercased, deduped. Max 12 tags, 32 chars each.</div>
            </div>

            <div className="admin-field">
              <label>Notes</label>
              <textarea
                rows={3}
                value={form.notes}
                onChange={function(e) { setForm(Object.assign({}, form, { notes: e.target.value })); }}
                placeholder="Admin-only notes. Not visible to the user."
                style={{ padding: '10px 12px', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, fontFamily: 'inherit', resize: 'vertical', minHeight: 60 }}
              />
              <div className="admin-field-hint">Free-form. Shown in the user row as a tooltip on hover.</div>
            </div>

            {!isEdit && <div className="admin-modal-hint">A random 20-character password will be generated and shown once on save. The user will be required to change it on first login.</div>}

            {/* Advanced section — only in edit mode */}
            {isEdit && (
              <>
                <button type="button" className="admin-advanced-toggle" onClick={function() { setShowAdvanced(!showAdvanced); }}>
                  {showAdvanced ? '▴' : '▾'} Advanced actions
                </button>
                {showAdvanced && (
                  <div className="admin-advanced">
                    <div className="admin-advanced-row">
                      <div className="admin-advanced-label">
                        <div style={{ fontWeight: 600 }}>Force logout</div>
                        <div className="admin-field-hint">Revoke every active JWT for this user right now. Use for suspected compromise.</div>
                      </div>
                      <button type="button" className="admin-btn sm warn" onClick={revokeSessions}>Revoke all sessions</button>
                    </div>
                    <div className="admin-advanced-row">
                      <div className="admin-advanced-label">
                        <div style={{ fontWeight: 600 }}>Microsoft SSO</div>
                        <div className="admin-field-hint">
                          {user.entra_object_id
                            ? <>Linked to Entra oid <code>{user.entra_object_id.slice(0, 8)}…{user.entra_object_id.slice(-4)}</code></>
                            : 'Not linked. Will link automatically on first SSO sign-in that matches this email.'}
                        </div>
                      </div>
                      <button type="button" className="admin-btn sm danger" onClick={unlinkSso} disabled={!user.entra_object_id}>Unlink SSO</button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="admin-modal-footer">
            <button type="button" className="admin-btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="admin-btn primary" disabled={saving}>{saving ? 'Saving...' : (isEdit ? 'Save changes' : 'Create user')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AdminPasswordReveal({ data, onClose }) {
  var [copied, setCopied] = useState(false);
  function copy() {
    try {
      navigator.clipboard.writeText(data.password).then(function() { setCopied(true); setTimeout(function() { setCopied(false); }, 2000); });
    } catch (e) { /* noop */ }
  }
  return (
    <div className="admin-modal-overlay">
      <div className="admin-modal" style={{ maxWidth: 520 }}>
        <div className="admin-modal-header" style={{ background: 'linear-gradient(135deg, rgba(91,158,244,0.15), rgba(120,80,255,0.15))' }}>{data.title}</div>
        <div className="admin-modal-body">
          <div style={{ marginBottom: 10, fontSize: 13 }}>The new password for <strong>{data.email}</strong> is shown once below. <strong>Save it now</strong> — it cannot be retrieved after closing this dialog.</div>
          <div className="admin-pw-display">
            <code>{data.password}</code>
            <button type="button" className="admin-btn sm primary" onClick={copy}>{copied ? 'Copied ✓' : 'Copy'}</button>
          </div>
          <div className="admin-field-hint" style={{ marginTop: 12 }}>Distribute via 1Password, Keeper, or encrypted email. The user must change it on first login (must_change_password = TRUE).</div>
        </div>
        <div className="admin-modal-footer">
          <button type="button" className="admin-btn primary" onClick={onClose}>I've saved it — close</button>
        </div>
      </div>
    </div>
  );
}

// AdminPermissionsTab — CMMC-grade disclosure of every credential
// Aurelius requires. Surfaces:
//   - Each app/cred Aurelius authenticates with (multi-tenant SSO,
//     per-tenant Graph, NinjaOne commercial/FedRAMP, etc.)
//   - Each Microsoft Graph scope, the endpoints it covers, the
//     features it backs, and the CMMC justification
//   - Scopes Aurelius DELIBERATELY DOES NOT REQUEST (BitlockerKey.Read.All,
//     write scopes, mailbox/file scopes) — useful for explaining "why
//     can't I see X data?" and answering scope-bloat questions on review.
//
// Read-only view. Drives the Edit Client modal's permissions footer.
function AdminPermissionsTab() {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [err, setErr] = useState(null);
  var [tab, setTab] = useState('apps'); // apps | scopes | not-requested

  useEffect(function() {
    setLoading(true); setErr(null);
    authFetch(API + '/admin/permissions-inventory')
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) { setLoading(false); if (x.ok) setData(x.d); else setErr(x.d.error || 'Failed to load inventory'); })
      .catch(function() { setLoading(false); setErr('Failed to load inventory'); });
  }, []);

  if (loading) return <div className="admin-tab-pane"><SkeletonLoader /></div>;
  if (err) return <div className="admin-tab-pane"><div className="admin-banner error">{err}</div></div>;
  if (!data) return null;

  return (
    <div className="admin-tab-pane permissions-tab">
      <div className="admin-banner info" style={{ marginBottom: 12 }}>
        <strong>What this is.</strong> Source-of-truth disclosure of every credential, scope, and API permission Aurelius asks for — mapped to the feature it backs. Built for CMMC-tier clients first: every requested permission must be justified by an actual function. Aspirational scopes are pruned, not granted.
      </div>

      <div className="permissions-subtabs">
        <button className={'admin-tab' + (tab === 'apps' ? ' active' : '')} onClick={function() { setTab('apps'); }}>Apps &amp; credentials ({data.apps.length})</button>
        <button className={'admin-tab' + (tab === 'scopes' ? ' active' : '')} onClick={function() { setTab('scopes'); }}>Microsoft Graph scopes ({data.graphScopes.length})</button>
        <button className={'admin-tab' + (tab === 'not-requested' ? ' active' : '')} onClick={function() { setTab('not-requested'); }}>Deliberately NOT requested ({data.graphScopesNotRequested.length})</button>
      </div>

      {tab === 'apps' && (
        <div className="permissions-list">
          {data.apps.map(function(app) {
            return (
              <div key={app.id} className="permissions-app-card">
                <div className="permissions-app-head">
                  <span className="permissions-app-label">{app.label}</span>
                  <span className="permissions-app-loc">{app.location}</span>
                </div>
                <div className="permissions-app-row"><strong>Purpose:</strong> {app.purpose}</div>
                <div className="permissions-app-row"><strong>Auth:</strong> {app.auth}</div>
                <div className="permissions-app-row permissions-app-cmmc"><strong>CMMC scoping:</strong> {app.cmmc_classification}</div>
                {app.setup && <div className="permissions-app-row permissions-app-setup"><strong>Setup:</strong> {app.setup}</div>}
                {app.aurelius_app_id_env && <div className="permissions-app-meta">env: <code>{app.aurelius_app_id_env}</code></div>}
                {app.aurelius_app_id_field && <div className="permissions-app-meta">per-client field: <code>{app.aurelius_app_id_field}</code></div>}
              </div>
            );
          })}
        </div>
      )}

      {tab === 'scopes' && (
        <div className="permissions-list">
          {data.graphScopes.map(function(s, i) {
            return (
              <div key={i} className={'permissions-scope-card permissions-scope-' + s.type}>
                <div className="permissions-scope-head">
                  <code className="permissions-scope-name">{s.permission}</code>
                  <span className={'permissions-scope-type permissions-scope-type-' + s.type}>{s.type}</span>
                  {s.write && <span className="permissions-scope-write" title="This scope grants write access">WRITE</span>}
                  <span className="permissions-scope-app">on <code>{s.app}</code></span>
                </div>
                <div className="permissions-scope-row"><strong>Endpoints called:</strong>
                  <ul className="permissions-scope-endpoints">{(s.endpoints || []).map(function(e, j) { return <li key={j}><code>{e}</code></li>; })}</ul>
                </div>
                <div className="permissions-scope-row"><strong>Features:</strong>
                  <ul className="permissions-scope-features">{(s.features || []).map(function(f, j) { return <li key={j}>{f}</li>; })}</ul>
                </div>
                <div className="permissions-scope-row permissions-scope-cmmc"><strong>CMMC justification:</strong> {s.cmmc_justification}</div>
              </div>
            );
          })}
        </div>
      )}

      {tab === 'not-requested' && (
        <div className="permissions-list">
          <div className="admin-banner success" style={{ marginBottom: 8, fontSize: 12 }}>
            These scopes are <strong>not in Aurelius&rsquo;s manifest</strong> and <strong>not requested at consent</strong>. If a client GA reports seeing one of these on a consent screen, the screen is for a different app (NinjaOne, Atomus Aegis, KeeperGov, etc.) — not Aurelius.
          </div>
          {data.graphScopesNotRequested.map(function(s, i) {
            return (
              <div key={i} className="permissions-not-requested-card">
                <code className="permissions-scope-name">{s.permission}</code>
                <div className="permissions-not-requested-reason">{s.reason}</div>
              </div>
            );
          })}
        </div>
      )}

      <div className="permissions-footer">
        Generated {new Date(data.generatedAt).toLocaleString()}. Inventory is declarative; runtime grant state is shown per-client in the integration health summary inside each client&rsquo;s Edit Integrations modal.
      </div>
    </div>
  );
}

function AdminAuditTab() {
  var [rows, setRows] = useState([]);
  var [total, setTotal] = useState(0);
  var [page, setPage] = useState(1);
  var [pageSize] = useState(50);
  var [loading, setLoading] = useState(true);
  var [meta, setMeta] = useState({ eventTypes: [], severities: [] });
  var [filters, setFilters] = useState({ email: '', eventType: '', severity: '', startDate: '', endDate: '', failuresOnly: false });
  var [expandedRow, setExpandedRow] = useState(null);

  function queryString(extras) {
    var params = Object.assign({}, filters, extras || {});
    var parts = [];
    for (var k in params) {
      if (params[k] === '' || params[k] === false || params[k] == null) continue;
      parts.push(encodeURIComponent(k) + '=' + encodeURIComponent(params[k]));
    }
    return parts.join('&');
  }

  function fetchRows() {
    setLoading(true);
    var qs = queryString({ page: page, pageSize: pageSize });
    authFetch(API + '/admin/audit?' + qs)
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(data) {
        if (data) { setRows(data.rows); setTotal(data.total); }
        setLoading(false);
      })
      .catch(function() { setLoading(false); });
  }

  useEffect(function() {
    authFetch(API + '/admin/audit/metadata').then(function(r) { return r.ok ? r.json() : null; }).then(function(m) { if (m) setMeta(m); }).catch(function() {});
  }, []);

  useEffect(fetchRows, [page, filters]);

  function updateFilter(k, v) { setFilters(Object.assign({}, filters, { [k]: v })); setPage(1); }

  function exportCsv() {
    var token = getToken();
    var qs = queryString({});
    // We can't easily stream with authFetch; use fetch with auth header and blob download
    fetch(API + '/admin/audit/export.csv?' + qs, { headers: { 'Authorization': 'Bearer ' + token } })
      .then(function(r) { return r.blob(); })
      .then(function(blob) {
        var url = URL.createObjectURL(blob);
        var a = document.createElement('a'); a.href = url; a.download = 'aurelius-audit-' + new Date().toISOString().slice(0, 10) + '.csv';
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
      });
  }

  var totalPages = Math.max(1, Math.ceil(total / pageSize));
  var severityClass = { INFO: 'info', WARN: 'warn', ERROR: 'bad' };

  return (
    <div className="admin-audit-tab">
      <div className="admin-toolbar">
        <input className="admin-filter-input" placeholder="Filter by email..." value={filters.email} onChange={function(e) { updateFilter('email', e.target.value); }} />
        <select className="admin-filter-select" value={filters.eventType} onChange={function(e) { updateFilter('eventType', e.target.value); }}>
          <option value="">All events</option>
          {meta.eventTypes.map(function(t) { return <option key={t} value={t}>{t}</option>; })}
        </select>
        <select className="admin-filter-select" value={filters.severity} onChange={function(e) { updateFilter('severity', e.target.value); }}>
          <option value="">All severities</option>
          {meta.severities.map(function(s) { return <option key={s} value={s}>{s}</option>; })}
        </select>
        <input type="date" className="admin-filter-select" value={filters.startDate} onChange={function(e) { updateFilter('startDate', e.target.value); }} title="From date" />
        <input type="date" className="admin-filter-select" value={filters.endDate} onChange={function(e) { updateFilter('endDate', e.target.value); }} title="To date" />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
          <input type="checkbox" checked={filters.failuresOnly} onChange={function(e) { updateFilter('failuresOnly', e.target.checked); }} />
          Failures only
        </label>
        <button className="admin-btn" onClick={function() { setFilters({ email: '', eventType: '', severity: '', startDate: '', endDate: '', failuresOnly: false }); setPage(1); }}>Clear</button>
        <button className="admin-btn primary" onClick={exportCsv}>Export CSV</button>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table dense">
          <thead>
            <tr>
              <th style={{ width: 150 }}>When</th>
              <th>Event</th>
              <th style={{ width: 80 }}>Severity</th>
              <th style={{ width: 60 }}>OK</th>
              <th>User</th>
              <th style={{ width: 120 }}>Client</th>
              <th style={{ width: 140 }}>IP</th>
              <th>Metadata</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="8" style={{ padding: 24, textAlign: 'center', opacity: 0.5 }}>Loading audit events...</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan="8" style={{ padding: 24, textAlign: 'center', opacity: 0.5 }}>No events match these filters.</td></tr>}
            {!loading && rows.map(function(r) {
              var isExpanded = expandedRow === r.id;
              return (
                <React.Fragment key={r.id}>
                  <tr className={'audit-row clickable' + (isExpanded ? ' expanded' : '')} onClick={function() { setExpandedRow(isExpanded ? null : r.id); }}>
                    <td style={{ fontSize: 11 }}>{fmtDateTime(r.created_at)}</td>
                    <td><strong>{r.event_type}</strong></td>
                    <td><span className={'admin-chip ' + (severityClass[r.severity] || 'info')}>{r.severity}</span></td>
                    <td>{r.success ? <span style={{ color: '#22c55e' }}>✓</span> : <span style={{ color: '#ef4444' }}>✗</span>}</td>
                    <td style={{ fontSize: 12 }}>{r.email || (r.user_id ? 'user#' + r.user_id : '—')}</td>
                    <td style={{ fontSize: 12 }}>{r.client_id || <span style={{ opacity: 0.4 }}>—</span>}</td>
                    <td style={{ fontSize: 11, fontFamily: 'monospace' }}>{r.ip || '—'}</td>
                    <td style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      {r.metadata ? Object.keys(r.metadata).slice(0, 3).map(function(k) { return k + '=' + String(r.metadata[k]).slice(0, 25); }).join(' · ') : '—'}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="audit-row-expanded">
                      <td colSpan="8">
                        <div className="audit-expand-grid">
                          <div><div className="audit-expand-label">User Agent</div><div className="audit-expand-val">{r.user_agent || '—'}</div></div>
                          <div><div className="audit-expand-label">Metadata</div><pre className="audit-expand-val mono">{r.metadata ? JSON.stringify(r.metadata, null, 2) : '—'}</pre></div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="admin-pager">
        <span>{total.toLocaleString()} event(s) · page {page} of {totalPages}</span>
        <div style={{ flex: 1 }} />
        <button className="admin-btn sm" disabled={page <= 1} onClick={function() { setPage(1); }}>« First</button>
        <button className="admin-btn sm" disabled={page <= 1} onClick={function() { setPage(page - 1); }}>‹ Prev</button>
        <button className="admin-btn sm" disabled={page >= totalPages} onClick={function() { setPage(page + 1); }}>Next ›</button>
        <button className="admin-btn sm" disabled={page >= totalPages} onClick={function() { setPage(totalPages); }}>Last »</button>
      </div>
    </div>
  );
}

// Integration chip state:
//   configured + credentialPresent  → green   "Live"
//   configured + !credentialPresent → red     "Broken"  (wired but server can't call)
//   !configured                     → neutral "Not wired"
//   partial                         → yellow  "Partial"  (some config fields set, some missing)
function integrationStatus(it) {
  if (!it) return { cls: 'neutral', label: 'Unknown' };
  if (it.partial) return { cls: 'partial', label: 'Partial' };
  if (!it.configured) return { cls: 'neutral', label: 'Not wired' };
  if (it.credentialPresent === false) return { cls: 'broken', label: 'Broken' };
  return { cls: 'live', label: 'Live' };
}

var INTEGRATION_LABELS = {
  ninjaone_commercial: 'NinjaOne',
  ninjaone_fedramp:    'NinjaOne FedRAMP',
  huntress:            'Huntress',
  entra:               'Microsoft Entra',
  azure:               'Azure Resources',
  blumira:             'Blumira',
  asana:               'Asana',
  jira:                'Jira',
  unifi:               'UniFi',
  meraki:              'Meraki'
};

// ── Asana Discovery Modal ───────────────────────────────────────────
// Solves the "engineer creates new project, nobody updates client-config.js"
// problem. Lists every project visible to CBG's Asana token, joined with
// the per-client GID map, so unmapped projects are immediately visible
// alongside hints for which client to map them to.
function AsanaDiscoveryModal({ clients, onClose, onAssigned, onError }) {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState(null);
  var [search, setSearch] = useState('');
  var [unmappedOnly, setUnmappedOnly] = useState(true);
  var [copiedGid, setCopiedGid] = useState(null);
  var [busyGid, setBusyGid] = useState(null);   // gid currently saving — disables that row's controls
  var clientList = clients || [];
  var clientNameById = {};
  clientList.forEach(function(c) { clientNameById[c.id] = c.name; });

  useEffect(function() {
    setLoading(true);
    authFetch(API + '/admin/asana/discover')
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setLoading(false);
        if (x.ok) setData(x.d);
        else setError(x.d.error || 'Discovery failed');
      })
      .catch(function() { setLoading(false); setError('Discovery failed'); });
  }, []);

  function copyGid(gid) {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(gid).then(function() {
        setCopiedGid(gid);
        setTimeout(function() { setCopiedGid(null); }, 1500);
      });
    }
  }

  // Optimistic assign — flip the local mappedTo[] immediately, then call the
  // server. On failure we revert and surface the error toast. This keeps
  // the UI snappy and makes batch-mapping a Asana workspace feel instant.
  function applyMappingChange(gid, clientId, action) {
    if (!gid || !clientId || (action !== 'add' && action !== 'remove')) return;
    setBusyGid(gid);
    setData(function(prev) {
      if (!prev) return prev;
      var clone = JSON.parse(JSON.stringify(prev));
      clone.workspaces.forEach(function(ws) {
        ws.projects.forEach(function(p) {
          if (p.gid !== gid) return;
          if (action === 'add' && p.mappedTo.indexOf(clientId) === -1) p.mappedTo.push(clientId);
          if (action === 'remove') p.mappedTo = p.mappedTo.filter(function(id) { return id !== clientId; });
        });
      });
      // Recompute summary counters so the header chips update
      var mapped = 0, unmapped = 0, total = 0;
      clone.workspaces.forEach(function(ws) {
        ws.projects.forEach(function(p) {
          total++;
          if (p.mappedTo.length > 0) mapped++; else unmapped++;
        });
      });
      clone.summary = Object.assign({}, clone.summary, { total: total, mapped: mapped, unmapped: unmapped });
      return clone;
    });
    authFetch(API + '/admin/asana/assign', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ gid: gid, clientId: clientId, action: action })
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusyGid(null);
        if (x.ok) {
          if (onAssigned) {
            var name = clientNameById[clientId] || clientId;
            onAssigned(action === 'add' ? 'Assigned to ' + name : 'Removed from ' + name);
          }
        } else {
          // Revert on server-side error — flip the change back
          setData(function(prev) {
            if (!prev) return prev;
            var clone = JSON.parse(JSON.stringify(prev));
            clone.workspaces.forEach(function(ws) {
              ws.projects.forEach(function(p) {
                if (p.gid !== gid) return;
                if (action === 'add') p.mappedTo = p.mappedTo.filter(function(id) { return id !== clientId; });
                if (action === 'remove' && p.mappedTo.indexOf(clientId) === -1) p.mappedTo.push(clientId);
              });
            });
            return clone;
          });
          if (onError) onError(x.d.error || 'Assign failed');
        }
      })
      .catch(function() {
        setBusyGid(null);
        if (onError) onError('Network error — change was reverted');
      });
  }

  // Flatten + filter projects across workspaces
  var allProjects = data ? data.workspaces.flatMap(function(ws) {
    return ws.projects.map(function(p) { return Object.assign({}, p, { workspaceName: ws.name, workspaceGid: ws.gid }); });
  }) : [];
  var filtered = allProjects.filter(function(p) {
    if (unmappedOnly && p.mappedTo.length > 0) return false;
    if (search) {
      var q = search.toLowerCase();
      if (!(p.name || '').toLowerCase().includes(q)
        && !(p.gid || '').includes(search)
        && !(p.team || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={function(e) { e.stopPropagation(); }} style={{ maxWidth: 760 }}>
        <div className="admin-modal-header">Asana Project Discovery</div>
        <div className="admin-modal-body" style={{ maxHeight: '72vh', overflow: 'auto' }}>
          <div className="admin-field-hint" style={{ marginBottom: 14 }}>
            Every project visible to CBG's Asana token, joined with the per-client mapping in the database. When an engineer creates a new project, it shows up here as <span className="admin-chip warn" style={{ fontSize: 10, padding: '1px 6px' }}>unmapped</span> — pick the client from the dropdown to assign it. The change persists immediately and the client's Projects tab reflects it on next load.
          </div>

          {loading && <div style={{ padding: 24, textAlign: 'center', opacity: 0.5 }}>Calling Asana…</div>}
          {error && <div style={{ padding: 12, color: 'var(--accent-red)' }}>{error}</div>}

          {data && (
            <>
              <div className="admin-advanced" style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12 }}>
                  <span><strong style={{ fontSize: 18 }}>{data.summary.total}</strong> projects total</span>
                  <span><strong style={{ fontSize: 18, color: 'var(--accent-green)' }}>{data.summary.mapped}</strong> mapped</span>
                  <span><strong style={{ fontSize: 18, color: 'var(--accent-amber)' }}>{data.summary.unmapped}</strong> unmapped</span>
                  {data.summary.orphans > 0 && <span><strong style={{ fontSize: 18, color: 'var(--accent-red)' }}>{data.summary.orphans}</strong> orphan GIDs</span>}
                </div>
              </div>

              {data.orphans && data.orphans.length > 0 && (
                <div className="admin-advanced" style={{ marginBottom: 14, borderColor: 'rgba(240,101,101,0.3)' }}>
                  <div style={{ fontWeight: 700, marginBottom: 6, color: 'var(--accent-red)' }}>Orphan GIDs in client-config.js</div>
                  <div className="admin-field-hint" style={{ marginBottom: 8 }}>
                    These GIDs are listed in client-config but Asana didn't return them — project deleted, archived, or out of token scope. Worth removing.
                  </div>
                  {data.orphans.map(function(o) {
                    return (
                      <div key={o.gid} style={{ fontSize: 11, fontFamily: 'JetBrains Mono, monospace', marginBottom: 4 }}>
                        <code>{o.gid}</code> mapped to <strong>{o.mappedTo.join(', ')}</strong>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="admin-toolbar" style={{ marginBottom: 12 }}>
                <input className="admin-filter-input" style={{ flex: 1 }} placeholder="Search by name, GID, or team…" value={search} onChange={function(e) { setSearch(e.target.value); }} />
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                  <input type="checkbox" checked={unmappedOnly} onChange={function(e) { setUnmappedOnly(e.target.checked); }} />
                  Unmapped only
                </label>
              </div>

              {filtered.length === 0 && (
                <div style={{ padding: 24, textAlign: 'center', opacity: 0.55 }}>
                  {unmappedOnly ? 'All projects are mapped — nothing to do here.' : 'No projects match.'}
                </div>
              )}

              {filtered.map(function(p) {
                var busy = busyGid === p.gid;
                return (
                  <div key={p.gid} style={{ padding: '12px 14px', marginBottom: 8, background: 'var(--bg-card)', border: '1px solid var(--border-default)', borderRadius: 8, position: 'relative', borderLeft: '3px solid ' + (p.mappedTo.length === 0 ? 'var(--accent-amber)' : 'var(--accent-green)'), opacity: busy ? 0.6 : 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>{p.name}</div>
                        <div style={{ fontSize: 11, opacity: 0.65, marginTop: 3 }}>
                          {p.workspaceName}
                          {p.team && ' · ' + p.team}
                          {p.owner && ' · owner ' + p.owner}
                        </div>
                        <div style={{ fontSize: 10, fontFamily: 'JetBrains Mono, monospace', marginTop: 4, opacity: 0.55 }}>
                          GID <code>{p.gid}</code>
                          <button
                            type="button"
                            onClick={function() { copyGid(p.gid); }}
                            style={{ marginLeft: 8, fontSize: 10, padding: '1px 8px', background: 'transparent', border: '1px solid var(--border-default)', borderRadius: 4, color: 'var(--text-muted)', cursor: 'pointer' }}
                            title="Copy GID to clipboard"
                          >{copiedGid === p.gid ? 'copied ✓' : 'copy'}</button>
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, minWidth: 200 }}>
                        {p.mappedTo.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'flex-end' }}>
                            {p.mappedTo.map(function(cid) {
                              var name = clientNameById[cid] || cid;
                              return (
                                <span key={cid} className="admin-chip ok" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }} title={'Mapped to ' + name}>
                                  {name}
                                  <button
                                    type="button"
                                    onClick={function() { applyMappingChange(p.gid, cid, 'remove'); }}
                                    disabled={busy}
                                    style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, marginLeft: 2, fontSize: 14, lineHeight: 1, opacity: 0.7 }}
                                    title={'Remove from ' + name}
                                    aria-label={'Remove from ' + name}
                                  >×</button>
                                </span>
                              );
                            })}
                          </div>
                        )}
                        {p.mappedTo.length === 0 && <span className="admin-chip warn">unmapped</span>}
                        <select
                          value=""
                          disabled={busy || clientList.length === 0}
                          onChange={function(e) {
                            var val = e.target.value;
                            if (!val) return;
                            applyMappingChange(p.gid, val, 'add');
                            e.target.value = '';
                          }}
                          style={{ fontSize: 11, padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border-default)', background: 'var(--bg-surface)', color: 'var(--text-primary)', cursor: busy ? 'wait' : 'pointer', minWidth: 180 }}
                          title="Assign this project to a client"
                        >
                          <option value="">{busy ? 'Saving…' : (p.mappedTo.length === 0 ? '+ Assign to client…' : '+ Also assign to…')}</option>
                          {clientList
                            .filter(function(c) { return p.mappedTo.indexOf(c.id) === -1; })
                            .map(function(c) {
                              return <option key={c.id} value={c.id}>{c.name}</option>;
                            })}
                        </select>
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
        <div className="admin-modal-footer">
          <button className="admin-btn primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// Generic vendor discovery modal — handles NinjaOne (commercial+fedramp),
// Huntress, and UniFi. The vendor config object describes:
//   • title — modal heading
//   • discoverPath — GET endpoint
//   • assignPath — POST endpoint
//   • mode — 'single' (one value per client) or 'array' (many per client)
//   • payloadShape(action, resourceId, clientId, label?) — builds POST body
//   • sections (single-mode only) — for vendors like NinjaOne with two
//     instances (commercial vs fedramp), each with its own resource list
//   • renderResource(r) — optional extra info per row (e.g. UniFi device count)
//   • idLabel — what to call the ID in the UI (e.g. "Org ID", "Host ID")
function VendorDiscoveryModal({ vendor, clients, onClose, onAssigned, onError }) {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState(null);
  var [search, setSearch] = useState('');
  var [unmappedOnly, setUnmappedOnly] = useState(true);
  var [busyId, setBusyId] = useState(null);
  var [labelDraft, setLabelDraft] = useState({});  // for UniFi: per-host label being edited
  var clientList = clients || [];
  var clientNameById = {};
  clientList.forEach(function(c) { clientNameById[c.id] = c.name; });

  useEffect(function() {
    setLoading(true);
    authFetch(API + vendor.discoverPath)
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setLoading(false);
        if (x.ok) setData(x.d);
        else setError(x.d.error || 'Discovery failed');
      })
      .catch(function() { setLoading(false); setError('Discovery failed'); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendor.discoverPath]);

  // For NinjaOne we render TWO sections (commercial + fedramp). For Huntress/UniFi
  // we render ONE section. Normalise to `sections[]` so the render path is uniform.
  function getSections() {
    if (!data) return [];
    if (vendor.sections) {
      // Multi-section vendor: data has shape { commercial: {...}, fedramp: {...} }
      return vendor.sections.map(function(s) { return Object.assign({ key: s.key, label: s.label, instance: s.instance }, data[s.key] || { resources: [], orphans: [], summary: { total: 0, mapped: 0, unmapped: 0, orphans: 0 } }); });
    }
    return [{ key: 'default', label: null, instance: vendor.instance, resources: data.resources || [], orphans: data.orphans || [], summary: data.summary || {} }];
  }

  function applyChange(section, resourceId, clientId, action, label) {
    var bid = section.key + ':' + resourceId;
    setBusyId(bid);
    // Optimistic update — flip mappedTo locally before the API call
    setData(function(prev) {
      if (!prev) return prev;
      var clone = JSON.parse(JSON.stringify(prev));
      var bucket = vendor.sections ? clone[section.key] : clone;
      if (!bucket) return clone;
      bucket.resources.forEach(function(r) {
        if (String(r.id) !== String(resourceId)) return;
        if (vendor.mode === 'array') {
          if (action === 'add' && r.mappedTo.indexOf(clientId) === -1) r.mappedTo.push(clientId);
          if (action === 'remove') r.mappedTo = r.mappedTo.filter(function(id) { return id !== clientId; });
        } else {
          // single-value: assigning to a client BOOTS any other client mapped to this resource (one-to-one)
          if (action === 'set') {
            // Remove this client from all other resources
            bucket.resources.forEach(function(r2) { r2.mappedTo = r2.mappedTo.filter(function(id) { return id !== clientId; }); });
            r.mappedTo = [clientId];
          } else if (action === 'clear') {
            r.mappedTo = r.mappedTo.filter(function(id) { return id !== clientId; });
          }
        }
      });
      // Recompute summary
      var mapped = bucket.resources.filter(function(r) { return r.mappedTo.length > 0; }).length;
      bucket.summary = Object.assign({}, bucket.summary, { mapped: mapped, unmapped: bucket.resources.length - mapped });
      return clone;
    });
    var body = vendor.payloadShape(action, resourceId, clientId, label, section.instance);
    authFetch(API + vendor.assignPath, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusyId(null);
        if (x.ok) {
          if (onAssigned) {
            var name = clientNameById[clientId] || clientId;
            onAssigned(action === 'add' || action === 'set' ? 'Assigned to ' + name : 'Removed from ' + name);
          }
        } else {
          // Best-effort revert: re-fetch from server (simpler than reverse-mutating)
          setError(x.d.error || 'Assign failed');
          if (onError) onError(x.d.error || 'Assign failed');
        }
      })
      .catch(function() { setBusyId(null); if (onError) onError('Network error'); });
  }

  function renderResourceRow(section, r) {
    var bid = section.key + ':' + r.id;
    var busy = busyId === bid;
    var stripeColor = r.mappedTo.length === 0 ? 'var(--accent-amber)' : 'var(--accent-green)';
    return (
      <div key={bid} style={{ padding: '12px 14px', marginBottom: 8, background: 'var(--bg-card)', border: '1px solid var(--border-default)', borderRadius: 8, borderLeft: '3px solid ' + stripeColor, opacity: busy ? 0.6 : 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 13 }}>{r.name}</div>
            {vendor.renderResource && (
              <div style={{ fontSize: 11, opacity: 0.65, marginTop: 3 }}>{vendor.renderResource(r)}</div>
            )}
            <div style={{ fontSize: 10, fontFamily: 'JetBrains Mono, monospace', marginTop: 4, opacity: 0.55 }}>
              {vendor.idLabel} <code>{r.id}</code>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, minWidth: 220 }}>
            {r.mappedTo.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'flex-end' }}>
                {r.mappedTo.map(function(cid) {
                  var name = clientNameById[cid] || cid;
                  return (
                    <span key={cid} className="admin-chip ok" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }} title={'Mapped to ' + name}>
                      {name}
                      <button
                        type="button"
                        onClick={function() { applyChange(section, r.id, cid, vendor.mode === 'array' ? 'remove' : 'clear'); }}
                        disabled={busy}
                        style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, marginLeft: 2, fontSize: 14, lineHeight: 1, opacity: 0.7 }}
                        title={'Remove from ' + name} aria-label={'Remove from ' + name}
                      >×</button>
                    </span>
                  );
                })}
              </div>
            )}
            {r.mappedTo.length === 0 && <span className="admin-chip warn">unmapped</span>}
            {vendor.mode === 'array' && (
              <input
                type="text" placeholder="Site label (optional)"
                value={labelDraft[bid] || ''}
                onChange={function(e) { setLabelDraft(Object.assign({}, labelDraft, { [bid]: e.target.value })); }}
                style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border-default)', background: 'var(--bg-surface)', color: 'var(--text-primary)', minWidth: 220 }}
              />
            )}
            <select
              value=""
              disabled={busy || clientList.length === 0}
              onChange={function(e) {
                var val = e.target.value;
                if (!val) return;
                var assignAction = vendor.mode === 'array' ? 'add' : 'set';
                applyChange(section, r.id, val, assignAction, labelDraft[bid]);
                e.target.value = '';
                setLabelDraft(Object.assign({}, labelDraft, { [bid]: '' }));
              }}
              style={{ fontSize: 11, padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border-default)', background: 'var(--bg-surface)', color: 'var(--text-primary)', cursor: busy ? 'wait' : 'pointer', minWidth: 220 }}
              title="Assign this resource to a client"
            >
              <option value="">{busy ? 'Saving…' : (vendor.mode === 'single' && r.mappedTo.length > 0 ? '↻ Reassign to…' : '+ Assign to client…')}</option>
              {clientList
                .filter(function(c) { return vendor.mode === 'array' ? r.mappedTo.indexOf(c.id) === -1 : true; })
                .map(function(c) { return <option key={c.id} value={c.id}>{c.name}</option>; })}
            </select>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-modal-overlay" onClick={onClose}>
        <div className="admin-modal" onClick={function(e) { e.stopPropagation(); }}>
          <div className="admin-modal-header">{vendor.title}</div>
          <div className="admin-modal-body" style={{ padding: 40, textAlign: 'center', opacity: 0.6 }}>Calling {vendor.title}…</div>
        </div>
      </div>
    );
  }

  var sections = getSections();
  // Apply search + unmapped filter to each section's resources
  var filteredSections = sections.map(function(s) {
    var filtered = s.resources.filter(function(r) {
      if (unmappedOnly && r.mappedTo.length > 0) return false;
      if (search) {
        var q = search.toLowerCase();
        if (!(r.name || '').toLowerCase().includes(q) && !String(r.id).includes(search)) return false;
      }
      return true;
    });
    return Object.assign({}, s, { filtered: filtered });
  });

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={function(e) { e.stopPropagation(); }} style={{ maxWidth: 760 }}>
        <div className="admin-modal-header">{vendor.title}</div>
        <div className="admin-modal-body" style={{ maxHeight: '72vh', overflow: 'auto' }}>
          <div className="admin-field-hint" style={{ marginBottom: 14 }}>
            {vendor.helpText}
          </div>

          {error && <div style={{ padding: 12, color: 'var(--accent-red)' }}>{error}</div>}

          <div className="admin-toolbar" style={{ marginBottom: 12 }}>
            <input className="admin-filter-input" style={{ flex: 1 }} placeholder="Search by name or ID…" value={search} onChange={function(e) { setSearch(e.target.value); }} />
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
              <input type="checkbox" checked={unmappedOnly} onChange={function(e) { setUnmappedOnly(e.target.checked); }} />
              Unmapped only
            </label>
          </div>

          {filteredSections.map(function(s) {
            return (
              <div key={s.key} style={{ marginBottom: 18 }}>
                {s.label && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid var(--border-default)' }}>
                    <strong style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.6 }}>{s.label}</strong>
                    <span style={{ fontSize: 11, opacity: 0.7 }}>
                      {s.summary.total} total · <span style={{ color: 'var(--accent-green)' }}>{s.summary.mapped}</span> mapped · <span style={{ color: 'var(--accent-amber)' }}>{s.summary.unmapped}</span> unmapped
                      {s.summary.orphans > 0 && <> · <span style={{ color: 'var(--accent-red)' }}>{s.summary.orphans}</span> orphan</>}
                    </span>
                  </div>
                )}
                {s.orphans && s.orphans.length > 0 && (
                  <div className="admin-advanced" style={{ marginBottom: 12, borderColor: 'rgba(240,101,101,0.3)' }}>
                    <div style={{ fontWeight: 700, marginBottom: 6, color: 'var(--accent-red)', fontSize: 12 }}>Orphan IDs in client config</div>
                    <div className="admin-field-hint" style={{ marginBottom: 8 }}>
                      These IDs are mapped to a client but missing from the vendor's response. Probably deleted, archived, or out of token scope.
                    </div>
                    {s.orphans.map(function(o) {
                      return (
                        <div key={o.id} style={{ fontSize: 11, fontFamily: 'JetBrains Mono, monospace', marginBottom: 4 }}>
                          <code>{o.id}</code> mapped to <strong>{o.mappedTo.join(', ')}</strong>
                        </div>
                      );
                    })}
                  </div>
                )}
                {s.filtered.length === 0 && (
                  <div style={{ padding: 24, textAlign: 'center', opacity: 0.55, fontSize: 12 }}>
                    {unmappedOnly ? 'All resources mapped — nothing to assign here.' : 'No resources match.'}
                  </div>
                )}
                {s.filtered.map(function(r) { return renderResourceRow(s, r); })}
              </div>
            );
          })}
        </div>
        <div className="admin-modal-footer">
          <button className="admin-btn primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// Editor modal for a single client's integration mappings. Loads the raw
// DB record + recent change history, lets the operator update structured
// fields (string/number/array/object), then PUTs the diff back.
//
// Field rendering is intentionally hand-written per integration rather than
// a generic schema-driven form — each integration has different shape
// expectations (UniFi has paired host_id ↔ site_label entries; Asana is a
// flat GID list; Atlassian billing is an array of objects). Hand-written
// gives clearer affordances and validation hints.
// ── Connect Entra panel (Phase B.6 e part 1) ───────────────────────
// Replaces the raw tenant_id text input with a flow-aware connection
// panel for the multi-tenant Aurelius SIGN-IN app (User.Read delegated).
// This is SSO ONLY — it lets client users authenticate against Aurelius
// using their Microsoft credentials.
//
// What this panel does NOT do: enable Graph data reads (users, devices,
// conditional access, sign-in logs, directory roles). Those reads run
// on a SEPARATE per-tenant Aurelius Graph app reg that the client GA
// must register in their own tenant + grant application permissions on
// + upload Aurelius's public cert to. That's a manual onboarding step
// done outside this panel — the graph_client_id field captures the app
// ID once the GA hands it back to CBG.
//
// States:
//   not_started      no tenant_id on file → "Connect" CTA
//   pending_consent  tenant_id saved, SSO consent not yet granted
//   consent_granted  callback received, /organization not yet hit
//   connected        verified — SSO toggle becomes available
//
// All four states are reachable through the same panel. The status
// pill + last-verified time are the visual anchor.
function ConnectEntraPanel({ clientId, form, setField }) {
  var [conn, setConn] = useState(null);
  var [loading, setLoading] = useState(true);
  var [busy, setBusy] = useState(null); // 'consent' | 'verify' | 'disconnect' | null
  var [consentUrl, setConsentUrl] = useState(null);
  var [consentExpiresAt, setConsentExpiresAt] = useState(null);
  var [tenantInput, setTenantInput] = useState('');
  var [err, setErr] = useState(null);
  var [verifyResult, setVerifyResult] = useState(null);

  function fetchStatus() {
    setLoading(true); setErr(null);
    authFetch(API + '/admin/clients/' + clientId + '/entra/connection')
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setLoading(false);
        if (x.ok) {
          setConn(x.d);
          if (x.d.tenant_id && (!tenantInput || tenantInput === '')) setTenantInput(x.d.tenant_id);
        } else {
          setErr(x.d.error || 'Failed to load Entra connection state');
        }
      })
      .catch(function() { setLoading(false); setErr('Failed to load Entra connection state'); });
  }
  useEffect(fetchStatus, [clientId]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Detect the post-consent redirect (?entra_consent=success&clientId=...)
  // and refetch + show a banner once the modal mounts.
  useEffect(function() {
    var url = new URL(window.location.href);
    var status = url.searchParams.get('entra_consent');
    var cid = url.searchParams.get('clientId');
    if (status && cid === clientId) {
      // Strip the params so a refresh doesn't re-trigger
      url.searchParams.delete('entra_consent'); url.searchParams.delete('clientId'); url.searchParams.delete('tenant'); url.searchParams.delete('reason');
      window.history.replaceState({}, '', url.pathname + (url.search ? '?' + url.searchParams.toString() : '') + url.hash);
      if (status === 'success') {
        setVerifyResult({ ok: true, message: 'Consent granted by GA. Aurelius is connected to this tenant.' });
      } else if (status === 'declined') {
        setErr('Consent was declined by the GA. They can re-attempt with a fresh consent URL.');
      } else {
        setErr('Consent flow ended in an error: ' + (url.searchParams.get('reason') || 'unknown'));
      }
      fetchStatus();
    }
  }, [clientId]);  // eslint-disable-line react-hooks/exhaustive-deps

  function handleSaveTenantThenConsent() {
    var t = (tenantInput || '').trim();
    if (!t) { setErr('Enter a tenant ID or domain first.'); return; }
    // Push the value into the Edit Client form so the parent's Save
    // captures it (and the consent flow has it on file).
    setField('tenant_id', t);
    handleGetConsentUrl(t);
  }

  function handleGetConsentUrl(tenantOverride) {
    setBusy('consent'); setErr(null); setConsentUrl(null);
    var qs = tenantOverride ? '?tenant=' + encodeURIComponent(tenantOverride) : '';
    authFetch(API + '/admin/clients/' + clientId + '/entra/consent-url' + qs)
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusy(null);
        if (x.ok) {
          setConsentUrl(x.d.url);
          setConsentExpiresAt(x.d.expires_at);
        } else {
          setErr(x.d.error || 'Failed to generate consent URL');
        }
      })
      .catch(function() { setBusy(null); setErr('Failed to generate consent URL'); });
  }

  function handleVerify() {
    setBusy('verify'); setErr(null); setVerifyResult(null);
    authFetch(API + '/admin/clients/' + clientId + '/entra/verify', { method: 'POST' })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusy(null);
        if (x.ok) {
          setVerifyResult({ ok: true, message: 'Connection verified. Tenant: ' + (x.d.tenant_name || 'unnamed') + (x.d.tenant_domain ? ' (' + x.d.tenant_domain + ')' : '') });
          fetchStatus();
        } else {
          setVerifyResult({ ok: false, message: x.d.error || 'Verification failed' });
          fetchStatus();
        }
      })
      .catch(function() { setBusy(null); setVerifyResult({ ok: false, message: 'Verify request failed' }); });
  }

  function handleDisconnect() {
    if (!window.confirm('Disconnect Aurelius SSO sign-in for this client?\n\nThis only clears the SSO consent + sign-in toggle. The per-tenant Graph app (graph_client_id) and tenant_id stay wired so Graph data reads continue to work. To fully sever Aurelius from this tenant, the client GA also needs to revoke consent on the Microsoft side.')) return;
    setBusy('disconnect'); setErr(null);
    authFetch(API + '/admin/clients/' + clientId + '/entra/disconnect', { method: 'POST' })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setBusy(null);
        if (x.ok) {
          // Clear ONLY the SSO toggle in the form — leave tenant_id +
          // graph_client_id intact since they back the per-tenant Graph
          // app reg (a different concern from SSO consent).
          setField('sso_enabled', false);
          setVerifyResult({ ok: true, message: 'SSO disconnected. tenant_id + graph_client_id preserved for Graph data reads.' });
          fetchStatus();
        } else {
          setErr(x.d.error || 'Disconnect failed');
        }
      })
      .catch(function() { setBusy(null); setErr('Disconnect failed'); });
  }

  function copyConsentUrl() {
    if (!consentUrl) return;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(consentUrl).then(function() {
        setVerifyResult({ ok: true, message: 'Consent URL copied to clipboard. Send it to the client&rsquo;s Global Administrator.' });
      }).catch(function() { /* fall through to manual */ });
    }
  }

  if (loading) return <div className="connect-entra"><div className="connect-entra-loading">Loading Entra connection state…</div></div>;
  if (!conn) return <div className="connect-entra"><div className="admin-banner error">{err || 'Failed to load Entra connection state'}</div></div>;

  var status = conn.status;
  var statusLabel =
    status === 'not_started'      ? 'Not connected' :
    status === 'pending_consent'  ? 'Awaiting GA consent' :
    status === 'consent_granted'  ? 'Consent granted, not yet verified' :
    status === 'connected'        ? 'Connected' : status;

  return (
    <div className="connect-entra">
      <div className="connect-entra-status-row">
        <span className={'connect-entra-status connect-entra-status-' + status}>
          <span className="connect-entra-status-dot" aria-hidden="true">●</span>
          <span className="connect-entra-status-label">{statusLabel}</span>
        </span>
        {conn.tenant_name && <span className="connect-entra-tenant-name">{conn.tenant_name}{conn.tenant_domain && <span className="connect-entra-tenant-domain"> · {conn.tenant_domain}</span>}</span>}
        {conn.last_verified_at && (
          <span className="connect-entra-last-verified" title={conn.last_verified_by_email ? 'Verified by ' + conn.last_verified_by_email : ''}>
            Last verified: {new Date(conn.last_verified_at).toLocaleString()}
          </span>
        )}
      </div>

      {conn.last_error && (
        <div className="admin-banner warn" style={{ marginTop: 8, fontSize: 11 }}>
          <strong>Last error</strong> ({conn.last_error_at ? new Date(conn.last_error_at).toLocaleString() : 'unknown time'}): <code style={{ wordBreak: 'break-word' }}>{conn.last_error}</code>
        </div>
      )}

      {(status === 'not_started' || status === 'pending_consent') && (
        <div className="connect-entra-input-row">
          <label className="connect-entra-input-label">
            Tenant ID or domain
          </label>
          <input
            type="text"
            value={tenantInput}
            onChange={function(e) { setTenantInput(e.target.value); }}
            placeholder="contoso.onmicrosoft.com or 11111111-2222-3333-4444-555555555555"
            disabled={busy != null}
          />
          <div className="connect-entra-input-hint">
            GUID is most reliable, but Microsoft accepts a domain too. Aurelius stores the GUID returned by Microsoft after the GA grants consent.
          </div>
        </div>
      )}

      <div className="connect-entra-actions">
        {(status === 'not_started' || status === 'pending_consent') && (
          <button
            type="button"
            className="admin-btn primary"
            disabled={busy != null || !tenantInput.trim()}
            onClick={function() { handleSaveTenantThenConsent(); }}
            title="Generate a 15-minute admin-consent URL for the tenant. Send it to a Global Administrator on that tenant."
          >
            {busy === 'consent' ? 'Generating…' : '1. Generate consent URL'}
          </button>
        )}
        {status === 'consent_granted' && (
          <button
            type="button"
            className="admin-btn primary"
            disabled={busy != null}
            onClick={handleVerify}
          >
            {busy === 'verify' ? 'Verifying…' : 'Verify connection'}
          </button>
        )}
        {status === 'connected' && (
          <button
            type="button"
            className="admin-btn"
            disabled={busy != null}
            onClick={handleVerify}
            title="Re-test the connection by calling Microsoft Graph /organization."
          >
            {busy === 'verify' ? 'Verifying…' : 'Re-verify'}
          </button>
        )}
        {(status === 'consent_granted' || status === 'connected' || status === 'pending_consent') && (
          <button
            type="button"
            className="admin-btn"
            disabled={busy != null}
            onClick={function() { handleGetConsentUrl(); }}
            title="Generate a fresh consent URL. Useful if the GA needs to re-grant after revoking, or the previous link expired."
          >
            {busy === 'consent' ? 'Generating…' : 'Resend consent link'}
          </button>
        )}
        {(status === 'connected' || status === 'consent_granted' || status === 'pending_consent') && (
          <button
            type="button"
            className="admin-btn"
            style={{ marginLeft: 'auto', color: '#ef4444' }}
            disabled={busy != null}
            onClick={handleDisconnect}
          >
            Disconnect
          </button>
        )}
      </div>

      {consentUrl && (
        <div className="connect-entra-consent-url">
          <div className="connect-entra-consent-url-head">
            <strong>2. Send this URL to a Global Administrator on the target tenant.</strong>
            <span className="connect-entra-consent-url-expiry">Expires {consentExpiresAt ? new Date(consentExpiresAt).toLocaleTimeString() : 'in 15 min'}</span>
          </div>
          <div className="connect-entra-consent-url-actions">
            <input type="text" value={consentUrl} readOnly onClick={function(e) { e.target.select(); }} />
            <button type="button" className="admin-btn sm" onClick={copyConsentUrl}>Copy</button>
            <a href={consentUrl} target="_blank" rel="noopener noreferrer" className="admin-btn sm primary">Open</a>
          </div>
          <div className="connect-entra-consent-url-hint">
            They must be signed in as a GA when they open it. After they grant, Microsoft redirects them back to Aurelius and this panel auto-verifies.
          </div>
        </div>
      )}

      {verifyResult && (
        <div className={'admin-banner ' + (verifyResult.ok ? 'success' : 'error')} style={{ marginTop: 10, fontSize: 11 }}>
          {verifyResult.message}
        </div>
      )}

      {err && (
        <div className="admin-banner error" style={{ marginTop: 10, fontSize: 11 }}>{err}</div>
      )}

      <div className="connect-entra-footnote">
        <strong>SSO sign-in only.</strong> This grants the multi-tenant Aurelius app ({conn.aurelius_app_client_id ? conn.aurelius_app_client_id.slice(0, 8) + '…' : 'not set'}) the <code>User.Read</code> delegated permission on this tenant — enough for client users to sign into the Aurelius portal with their Microsoft credentials.
        <div style={{ marginTop: 6 }}>
          Graph <em>data</em> reads (users, devices, conditional access, sign-ins, directory roles) run on a <strong>separate per-tenant app registration</strong> that the GA must create in their own tenant. See <code>docs/connect-entra-flow.md</code> for the full setup, or open the Permissions inventory below to see exactly which scopes each Aurelius feature needs.
        </div>
      </div>
    </div>
  );
}

// ConnectGraphAppPanel — companion to ConnectEntraPanel. Where the
// Entra panel handles SSO sign-in consent (multi-tenant app, User.Read
// delegated), THIS panel handles the per-CLIENT-tenant Graph app reg
// that backs the actual data reads (User.Read.All, Directory.Read.All,
// AuditLog.Read.All, Policy.Read.All, DeviceManagementManagedDevices.Read.All,
// RoleManagement.Read.Directory).
//
// The setup is genuinely manual on the client side because Aurelius
// has no privilege to create app regs on a foreign tenant — only a GA
// can. So this panel:
//   1. Shows the cert thumbprint + expiry from CBG's Key Vault
//   2. Lets the admin download the public cert (.cer) and the
//      parameterized setup script (PowerShell or Az CLI)
//   3. Captures the client_id the GA returns (graph_client_id field)
//   4. Verifies via a real Graph /users call against the per-tenant app
//   5. Surfaces the audit script post-setup so the GA can re-check
//      that the app reg's permissions match the canonical list
//      (catches over-provisioned scopes — e.g. a stale BitlockerKey
//      from earlier setup attempts)
function ConnectGraphAppPanel({ clientId, form, setField }) {
  var [certStatus, setCertStatus] = useState(null);
  var [verifying, setVerifying] = useState(false);
  var [verifyResult, setVerifyResult] = useState(null);
  var [busy, setBusy] = useState(null);
  // Audit paste-back state
  var [auditResult, setAuditResult] = useState(null);  // { has_audit, audit_at, summary, over_provisioned, canonical }
  var [auditPasteOpen, setAuditPasteOpen] = useState(false);
  var [auditPasteText, setAuditPasteText] = useState('');
  var [auditSubmitting, setAuditSubmitting] = useState(false);
  var [auditPasteErr, setAuditPasteErr] = useState(null);

  function fetchCertStatus() {
    authFetch(API + '/admin/graph-app/cert-status')
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) { if (x.ok) setCertStatus(x.d); else setCertStatus({ configured: false, error: x.d.error }); })
      .catch(function() { setCertStatus({ configured: false, error: 'Failed to load cert status' }); });
  }
  function fetchAuditResult() {
    authFetch(API + '/admin/clients/' + clientId + '/graph-app/audit-result')
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) { if (x.ok) setAuditResult(x.d); })
      .catch(function() { /* swallow — audit history is best-effort */ });
  }
  useEffect(fetchCertStatus, []);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(fetchAuditResult, [clientId]);  // eslint-disable-line react-hooks/exhaustive-deps

  function handleSubmitAuditPaste() {
    if (!auditPasteText.trim()) { setAuditPasteErr('Paste the audit script output first.'); return; }
    setAuditSubmitting(true); setAuditPasteErr(null);
    authFetch(API + '/admin/clients/' + clientId + '/graph-app/audit-paste', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ output: auditPasteText })
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setAuditSubmitting(false);
        if (x.ok) {
          setAuditPasteText('');
          setAuditPasteOpen(false);
          fetchAuditResult();
        } else {
          setAuditPasteErr(x.d.error || 'Audit submission failed');
        }
      })
      .catch(function() { setAuditSubmitting(false); setAuditPasteErr('Audit submission failed'); });
  }

  function handleVerify() {
    setVerifying(true); setVerifyResult(null);
    authFetch(API + '/admin/clients/' + clientId + '/graph-app/verify', { method: 'POST' })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setVerifying(false);
        if (x.ok) setVerifyResult({ ok: true, message: 'Graph reads verified — saw ' + (x.d.user_count_seen || 0) + ' user record(s) on test query.' });
        else setVerifyResult({ ok: false, message: x.d.error || 'Verify failed' });
      })
      .catch(function() { setVerifying(false); setVerifyResult({ ok: false, message: 'Verify request failed' }); });
  }

  function downloadFromUrl(path, suggestedName) {
    setBusy(path);
    authFetch(API + path)
      .then(function(r) { return r.text().then(function(body) { return { ok: r.ok, body: body, headers: r.headers }; }); })
      .then(function(x) {
        setBusy(null);
        if (!x.ok) {
          try { var err = JSON.parse(x.body); alert(err.error || 'Download failed'); }
          catch (e) { alert('Download failed (' + x.body.slice(0, 80) + ')'); }
          return;
        }
        var blob = new Blob([x.body], { type: 'application/octet-stream' });
        var url = URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = url; a.download = suggestedName;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        setTimeout(function() { URL.revokeObjectURL(url); }, 1500);
      })
      .catch(function() { setBusy(null); alert('Download failed'); });
  }

  // Status logic — derives a single state for the panel
  var step;
  if (!certStatus) step = 'loading';
  else if (!certStatus.configured) step = 'cert_not_provisioned';
  else if (!form.tenant_id) step = 'tenant_missing';
  else if (!form.graph_client_id) step = 'awaiting_app_id';
  else step = 'configured';

  if (step === 'loading') return <div className="connect-graph-app"><div className="connect-entra-loading">Loading Graph cert status…</div></div>;

  if (step === 'cert_not_provisioned') {
    return (
      <div className="connect-graph-app">
        <div className="admin-banner error" style={{ fontSize: 11 }}>
          <strong>Graph cert not provisioned.</strong> Run <code>node backend/scripts/generate-graph-cert.js</code> (one-time, CBG-side) to generate a self-signed cert pair and upload it to Key Vault. Until then, no client can be wired for Graph data reads.
          {certStatus.error && <div style={{ marginTop: 4, opacity: 0.8 }}>Detail: <code>{certStatus.error}</code></div>}
        </div>
      </div>
    );
  }

  if (step === 'tenant_missing') {
    return (
      <div className="connect-graph-app">
        <div className="admin-banner info" style={{ fontSize: 11 }}>
          Save a <strong>tenant_id</strong> via the Microsoft Entra connection panel above first. The Graph setup script needs it to scope to the right tenant.
        </div>
      </div>
    );
  }

  return (
    <div className="connect-graph-app">
      {/* Always-visible status bar */}
      <div className="connect-graph-app-cert-status">
        <span className="connect-graph-app-cert-thumb" title={'Cert source: ' + (certStatus.cached_source || certStatus.source_preference || 'unknown')}>
          <span className="connect-graph-app-cert-label">Cert</span>
          <code>{(certStatus.cached_thumbprint || '').slice(0, 16) + (certStatus.cached_thumbprint ? '…' : '?')}</code>
        </span>
        {certStatus.cached_not_after && (
          <span className="connect-graph-app-cert-expiry">expires {new Date(certStatus.cached_not_after).toLocaleDateString()}</span>
        )}
      </div>

      {step === 'awaiting_app_id' && (
        <div className="connect-graph-app-steps">
          <ol>
            <li>
              <strong>Download the public cert.</strong>
              <button type="button" className="admin-btn sm" disabled={busy === '/admin/graph-app/public-cert'} onClick={function() { downloadFromUrl('/admin/graph-app/public-cert', 'aurelius-graph-public.cer'); }}>
                {busy === '/admin/graph-app/public-cert' ? '…' : '⬇ aurelius-graph-public.cer'}
              </button>
              <span className="connect-graph-app-hint">Send to the GA. They&rsquo;ll upload it to the new app reg as a cert credential.</span>
            </li>
            <li>
              <strong>Send the GA the setup script.</strong>
              <div className="connect-graph-app-button-row">
                <button type="button" className="admin-btn sm primary" disabled={busy && busy.indexOf('setup-script') >= 0} onClick={function() { downloadFromUrl('/admin/clients/' + clientId + '/graph-app/setup-script.ps1', 'aurelius-graph-setup-' + clientId + '.ps1'); }}>
                  ⬇ PowerShell (.ps1)
                </button>
                <button type="button" className="admin-btn sm" disabled={busy && busy.indexOf('setup-script') >= 0} onClick={function() { downloadFromUrl('/admin/clients/' + clientId + '/graph-app/setup-script.sh', 'aurelius-graph-setup-' + clientId + '.sh'); }}>
                  ⬇ Az CLI (.sh)
                </button>
              </div>
              <span className="connect-graph-app-hint">Both are parameterized for this client&rsquo;s tenant + cert. The GA runs as a Global Administrator. The script registers the app, uploads the cert, adds the canonical scopes, grants admin consent, and prints the resulting Application (client) ID.</span>
            </li>
            <li>
              <strong>Paste the returned Application (client) ID below.</strong>
              <input
                type="text"
                value={form.graph_client_id || ''}
                onChange={function(e) { setField('graph_client_id', e.target.value); }}
                placeholder="GUID returned by the setup script"
                style={{ marginTop: 4 }}
                className="connect-entra-input-row"
              />
              <span className="connect-graph-app-hint">Save the modal. Then click Verify below to confirm the Graph reads work end-to-end.</span>
            </li>
          </ol>
        </div>
      )}

      {step === 'configured' && (
        <div className="connect-graph-app-configured">
          <div className="connect-graph-app-app-row">
            <span className="connect-graph-app-cert-label">App ID</span>
            <code>{form.graph_client_id}</code>
            <button type="button" className="admin-btn sm" onClick={handleVerify} disabled={verifying}>
              {verifying ? 'Verifying…' : 'Verify connection'}
            </button>
            <button type="button" className="admin-btn sm" onClick={function() { downloadFromUrl('/admin/clients/' + clientId + '/graph-app/audit-script.ps1', 'aurelius-graph-audit-' + clientId + '.ps1'); }} title="PowerShell script the GA runs to audit the per-tenant app's actual permissions vs Aurelius's canonical list. Surfaces over-provisioned scopes.">
              ⬇ Audit script
            </button>
            <button type="button" className="admin-btn sm" onClick={function() { setAuditPasteOpen(!auditPasteOpen); setAuditPasteErr(null); }} title="Paste the audit script output back here so Aurelius records the result + flags drift in the audit log.">
              {auditPasteOpen ? 'Cancel paste' : '📋 Paste audit result'}
            </button>
          </div>
          {verifyResult && (
            <div className={'admin-banner ' + (verifyResult.ok ? 'success' : 'error')} style={{ fontSize: 11 }}>
              {verifyResult.message}
            </div>
          )}

          {/* Last audit result — historical state from the most recent paste-back */}
          {auditResult && auditResult.has_audit && (
            <div className="connect-graph-app-audit-summary">
              <div className="connect-graph-app-audit-summary-head">
                <strong>Last audit:</strong> {new Date(auditResult.audit_at).toLocaleString()}
                <span className={'connect-graph-app-audit-pill connect-graph-app-audit-pill-' + ((auditResult.over_provisioned && auditResult.over_provisioned.length > 0) || (auditResult.summary && auditResult.summary.canonical_missing > 0) ? 'drift' : 'clean')}>
                  {((auditResult.over_provisioned && auditResult.over_provisioned.length > 0) || (auditResult.summary && auditResult.summary.canonical_missing > 0)) ? 'Drift detected' : 'Clean'}
                </span>
              </div>
              {auditResult.summary && (
                <div className="connect-graph-app-audit-stats">
                  <span>{auditResult.summary.canonical_granted}/{auditResult.summary.canonical_total} canonical scopes granted</span>
                  {auditResult.summary.canonical_missing > 0 && <span className="bad">· {auditResult.summary.canonical_missing} missing</span>}
                  {auditResult.summary.canonical_partial > 0 && <span className="warn">· {auditResult.summary.canonical_partial} partial</span>}
                  {auditResult.summary.over_provisioned_count > 0 && <span className="bad">· {auditResult.summary.over_provisioned_count} over-provisioned</span>}
                </div>
              )}
              {auditResult.canonical && auditResult.canonical.length > 0 && (
                <ul className="connect-graph-app-audit-list">
                  {auditResult.canonical.map(function(c) {
                    var icon = c.status === 'granted' ? '✓' : c.status === 'missing' ? '✗' : '⚠';
                    return <li key={c.permission} className={'connect-graph-app-audit-item connect-graph-app-audit-item-' + c.status}><span className="connect-graph-app-audit-icon">{icon}</span><code>{c.permission}</code><span className="connect-graph-app-audit-note">{c.status.replace(/_/g, ' ')}</span></li>;
                  })}
                </ul>
              )}
              {auditResult.over_provisioned && auditResult.over_provisioned.length > 0 && (
                <div className="connect-graph-app-audit-overprov">
                  <strong>Over-provisioned (recommend remove for least-privilege):</strong>
                  <ul>
                    {auditResult.over_provisioned.map(function(p) { return <li key={p}><code>{p}</code></li>; })}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Paste-back form */}
          {auditPasteOpen && (
            <div className="connect-graph-app-audit-paste">
              <label className="connect-graph-app-audit-paste-label">Paste audit script output</label>
              <textarea
                value={auditPasteText}
                onChange={function(e) { setAuditPasteText(e.target.value); }}
                placeholder="Paste the GA's PowerShell output here (entire stdout is fine — we extract the ✓ / ⚠ / ✗ lines)…"
                rows={10}
                disabled={auditSubmitting}
              />
              <div className="connect-graph-app-button-row">
                <button type="button" className="admin-btn sm primary" disabled={auditSubmitting || !auditPasteText.trim()} onClick={handleSubmitAuditPaste}>
                  {auditSubmitting ? 'Recording…' : 'Record audit'}
                </button>
                <span className="connect-graph-app-hint">Aurelius parses the lines, records drift in the audit log, and surfaces over-provisioned scopes for cleanup.</span>
              </div>
              {auditPasteErr && <div className="admin-banner error" style={{ fontSize: 11 }}>{auditPasteErr}</div>}
            </div>
          )}

          <div className="connect-graph-app-rotate">
            <details>
              <summary>Re-issue setup script (rotate / repair)</summary>
              <div className="connect-graph-app-button-row" style={{ marginTop: 6 }}>
                <button type="button" className="admin-btn sm" onClick={function() { downloadFromUrl('/admin/graph-app/public-cert', 'aurelius-graph-public.cer'); }}>⬇ Public cert</button>
                <button type="button" className="admin-btn sm" onClick={function() { downloadFromUrl('/admin/clients/' + clientId + '/graph-app/setup-script.ps1', 'aurelius-graph-setup-' + clientId + '.ps1'); }}>⬇ Setup .ps1</button>
                <button type="button" className="admin-btn sm" onClick={function() { downloadFromUrl('/admin/clients/' + clientId + '/graph-app/setup-script.sh', 'aurelius-graph-setup-' + clientId + '.sh'); }}>⬇ Setup .sh</button>
              </div>
            </details>
          </div>
        </div>
      )}

      <div className="connect-graph-app-footnote">
        <strong>Why a separate flow.</strong> Per-tenant Graph apps are a deliberate CMMC-friendly choice — each client tenant&rsquo;s GA controls their own app reg, sees the exact permissions before consenting, and can revoke without affecting other clients. The CBG-side cert is rotated centrally; the per-tenant app reg only changes when the canonical scope list does.
      </div>
    </div>
  );
}

function EditClientIntegrationsModal({ clientId, dbBacked, onClose, onSaved, onError }) {
  var [raw, setRaw] = useState(null);              // server's current state
  var [form, setForm] = useState(null);            // working copy
  var [history, setHistory] = useState([]);
  var [loading, setLoading] = useState(true);
  var [saving, setSaving] = useState(false);
  var [error, setError] = useState(null);
  var [changeNote, setChangeNote] = useState('');
  var [showHistory, setShowHistory] = useState(false);
  var [advancedJson, setAdvancedJson] = useState({});  // per-field JSON editor unlock for atlassian_billing etc.

  useEffect(function() {
    setLoading(true);
    authFetch(API + '/admin/client-integrations/' + clientId)
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setLoading(false);
        if (x.ok) {
          setRaw(x.d.data || {});
          setForm(JSON.parse(JSON.stringify(x.d.data || {})));
          setHistory(x.d.history || []);
        } else {
          setError(x.d.error || 'Failed to load');
        }
      })
      .catch(function() { setLoading(false); setError('Failed to load'); });
  }, [clientId]);

  function setField(name, value) {
    setForm(function(prev) { var next = Object.assign({}, prev); next[name] = value; return next; });
  }

  // Helper for array fields edited as newline-delimited text
  function arrToLines(arr) { return Array.isArray(arr) ? arr.join('\n') : ''; }
  function linesToArr(s) {
    return (s || '').split(/\r?\n/).map(function(x) { return x.trim(); }).filter(function(x) { return x.length > 0; });
  }

  // unifi_site_labels is an object of { hostId: label }. Edit it as paired rows.
  function getUnifiPairs() {
    var ids = (form && Array.isArray(form.unifi_host_ids)) ? form.unifi_host_ids : [];
    var labels = (form && form.unifi_site_labels) || {};
    return ids.map(function(id) { return { id: id, label: labels[id] || '' }; });
  }
  function updateUnifiPair(idx, key, value) {
    var pairs = getUnifiPairs();
    pairs[idx] = Object.assign({}, pairs[idx], (key === 'id' ? { id: value } : { label: value }));
    var newIds = pairs.map(function(p) { return p.id; });
    var newLabels = {};
    pairs.forEach(function(p) { if (p.id && p.label) newLabels[p.id] = p.label; });
    setForm(function(prev) { return Object.assign({}, prev, { unifi_host_ids: newIds, unifi_site_labels: newLabels }); });
  }
  function addUnifiPair() {
    setForm(function(prev) {
      var ids = Array.isArray(prev.unifi_host_ids) ? prev.unifi_host_ids.slice() : [];
      ids.push('');
      return Object.assign({}, prev, { unifi_host_ids: ids });
    });
  }
  function removeUnifiPair(idx) {
    var pairs = getUnifiPairs();
    pairs.splice(idx, 1);
    var newIds = pairs.map(function(p) { return p.id; });
    var newLabels = {};
    pairs.forEach(function(p) { if (p.id && p.label) newLabels[p.id] = p.label; });
    setForm(function(prev) { return Object.assign({}, prev, { unifi_host_ids: newIds, unifi_site_labels: newLabels }); });
  }

  // atlassian_billing — JSON-edit because the shape is rich and the only
  // current consumer (the Atlassian billing chart) just iterates the array.
  function getAtlassianJson() {
    if (advancedJson.atlassian_billing != null) return advancedJson.atlassian_billing;
    return form && Array.isArray(form.atlassian_billing) ? JSON.stringify(form.atlassian_billing, null, 2) : '[]';
  }
  function setAtlassianJson(s) {
    setAdvancedJson(function(prev) { return Object.assign({}, prev, { atlassian_billing: s }); });
    try {
      var parsed = JSON.parse(s);
      if (Array.isArray(parsed)) setField('atlassian_billing', parsed);
    } catch (e) { /* user is mid-typing — don't blow up */ }
  }

  // Validate before save — catches obvious typos that the server-side
  // sanitizer would otherwise silently coerce.
  function validate() {
    if (!form.name || !String(form.name).trim()) return 'Name is required.';
    if (form.ninjaone_org_id != null && form.ninjaone_org_id !== '' && !/^\d+$/.test(String(form.ninjaone_org_id))) {
      return 'NinjaOne org ID must be numeric.';
    }
    if (form.huntress_org_id != null && form.huntress_org_id !== '' && !/^\d+$/.test(String(form.huntress_org_id))) {
      return 'Huntress org ID must be numeric.';
    }
    if (advancedJson.atlassian_billing != null) {
      try { var v = JSON.parse(advancedJson.atlassian_billing); if (!Array.isArray(v)) return 'Atlassian billing must be a JSON array.'; }
      catch (e) { return 'Atlassian billing has invalid JSON: ' + e.message; }
    }
    return null;
  }

  function handleSave() {
    var err = validate();
    if (err) { setError(err); return; }
    setError(null);
    setSaving(true);
    var payload = Object.assign({}, form, { changeNote: changeNote || null });
    authFetch(API + '/admin/client-integrations/' + clientId, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setSaving(false);
        if (x.ok) onSaved('Saved ' + (x.d.data && x.d.data.name ? x.d.data.name : clientId));
        else { setError(x.d.error || 'Save failed'); onError(x.d.error || 'Save failed'); }
      })
      .catch(function(e) { setSaving(false); setError('Save failed: ' + e.message); onError('Save failed'); });
  }

  if (loading) {
    return (
      <div className="admin-modal-overlay" onClick={onClose}>
        <div className="admin-modal" onClick={function(e) { e.stopPropagation(); }}>
          <div className="admin-modal-header">Loading…</div>
          <div className="admin-modal-body" style={{ padding: 40, textAlign: 'center', opacity: 0.6 }}>Fetching {clientId}…</div>
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="admin-modal-overlay" onClick={onClose}>
        <div className="admin-modal" onClick={function(e) { e.stopPropagation(); }}>
          <div className="admin-modal-header">Failed to load</div>
          <div className="admin-modal-body" style={{ padding: 40, color: 'var(--accent-red)' }}>{error || 'Unknown error'}</div>
          <div className="admin-modal-footer"><button className="admin-btn" onClick={onClose}>Close</button></div>
        </div>
      </div>
    );
  }

  var unifiPairs = getUnifiPairs();

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={function(e) { e.stopPropagation(); }} style={{ maxWidth: 760 }}>
        <div className="admin-modal-header">
          Edit integration mappings — {raw && raw.name ? raw.name : clientId}
          <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.6, marginLeft: 8 }}>{clientId}</span>
        </div>
        <div className="admin-modal-body" style={{ maxHeight: '72vh', overflow: 'auto' }}>
          {!dbBacked && (
            <div className="admin-banner warn" style={{ marginBottom: 14 }}>
              DB unreachable — saving will fail. Reload from DB or fix the connection first.
            </div>
          )}

          {/* Integration coverage at a glance — visible before any
              field-by-field editing so the admin sees the shape of
              the world for this client up front. */}
          <IntegrationsHealthSummary form={form} clientName={raw && raw.name} />

          <div className="admin-form-section">
            <div className="admin-form-section-title">Identity</div>
            <div className="admin-form-grid two">
              <div className="admin-field"><label>Display name</label>
                <input type="text" value={form.name || ''} onChange={function(e) { setField('name', e.target.value); }} />
              </div>
              <div className="admin-field"><label>Type</label>
                <select value={form.type || ''} onChange={function(e) { setField('type', e.target.value); }}>
                  <option value="">—</option>
                  <option value="Commercial">Commercial</option>
                  <option value="Regulated (DIB)">Regulated (DIB)</option>
                  <option value="Regulated (Healthcare)">Regulated (Healthcare)</option>
                  <option value="Regulated (Legal)">Regulated (Legal)</option>
                </select>
              </div>
              <div className="admin-field"><label>Industry</label>
                <input type="text" value={form.industry || ''} onChange={function(e) { setField('industry', e.target.value); }} />
              </div>
              <div className="admin-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 18, marginTop: 22 }}>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', textTransform: 'none', fontSize: 13, letterSpacing: 0, fontWeight: 400, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!form.cmmc} onChange={function(e) { setField('cmmc', e.target.checked); }} /> CMMC
                </label>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', textTransform: 'none', fontSize: 13, letterSpacing: 0, fontWeight: 400, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!form.isDemo} onChange={function(e) { setField('isDemo', e.target.checked); }} /> Demo
                </label>
              </div>
            </div>
          </div>

          <div className="admin-form-section">
            <div className="admin-form-section-title">NinjaOne</div>
            <div className="admin-form-grid two">
              <div className="admin-field"><label>Commercial org ID (us2)</label>
                <input type="text" inputMode="numeric" value={form.ninjaone_org_id == null ? '' : form.ninjaone_org_id} onChange={function(e) { setField('ninjaone_org_id', e.target.value); }} placeholder="e.g. 7" />
              </div>
              <div className="admin-field"><label>FedRAMP org name (fed)</label>
                <input type="text" value={form.fedramp_org_name || ''} onChange={function(e) { setField('fedramp_org_name', e.target.value); }} />
                <div className="admin-field-hint">Org name on fed.ninjarmm.com (used for matching).</div>
              </div>
            </div>
          </div>

          <div className="admin-form-section">
            <div className="admin-form-section-title">Huntress</div>
            <div className="admin-field"><label>Huntress org ID</label>
              <input type="text" inputMode="numeric" value={form.huntress_org_id == null ? '' : form.huntress_org_id} onChange={function(e) { setField('huntress_org_id', e.target.value); }} placeholder="e.g. 506426" />
            </div>
          </div>

          <div className="admin-form-section">
            <div className="admin-form-section-title">Microsoft Entra connection</div>
            <ConnectEntraPanel clientId={clientId} form={form} setField={setField} />
            <div className="admin-form-grid two" style={{ marginTop: 14 }}>
              <div className="admin-field" style={{ gridColumn: '1 / -1' }}><label>Azure subscription_id</label>
                <input type="text" value={form.azure_subscription_id || ''} onChange={function(e) { setField('azure_subscription_id', e.target.value); }} placeholder="GUID" />
                <div className="admin-field-hint">For Azure Resource Manager API (cost data, VM control). Separate from Entra — this is RBAC on the subscription, not the directory.</div>
              </div>
              <div className="admin-field" style={{ gridColumn: '1 / -1' }}><label>Microsoft Sentinel workspace ID (CMMC tier)</label>
                <input type="text" value={form.sentinel_workspace_id || ''} onChange={function(e) { setField('sentinel_workspace_id', e.target.value); }} placeholder="Log Analytics workspace ID (GUID)" />
                <div className="admin-field-hint">Sentinel sits on the client&rsquo;s M365 GCC tenant. Wired through Atomus approval. Leave blank for commercial-tier clients (they use Blumira instead).</div>
              </div>
              <div className="admin-field" style={{ gridColumn: '1 / -1', flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 4 }}>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', textTransform: 'none', fontSize: 13, letterSpacing: 0, fontWeight: 400, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!form.sso_enabled} onChange={function(e) { setField('sso_enabled', e.target.checked); }} disabled={!form.tenant_id} />
                  <span>Enable Microsoft SSO at <code>/client/{clientId}</code></span>
                </label>
              </div>
              {form.sso_enabled && !form.tenant_id && (
                <div className="admin-field" style={{ gridColumn: '1 / -1', marginTop: -4 }}>
                  <div className="admin-banner warn">SSO requires the Entra connection above. Connect Entra first, then re-enable SSO.</div>
                </div>
              )}
              {form.sso_enabled && form.cmmc && (
                <div className="admin-field" style={{ gridColumn: '1 / -1', marginTop: -4 }}>
                  <div className="admin-banner warn">This is a CMMC client — confirm Atomus has approved the SSO path before exposing it. Until then, leave it off.</div>
                </div>
              )}
            </div>
          </div>

          <div className="admin-form-section">
            <div className="admin-form-section-title">Per-tenant Graph app (Graph data reads)</div>
            <ConnectGraphAppPanel clientId={clientId} form={form} setField={setField} />
          </div>

          <div className="admin-form-section">
            <div className="admin-form-section-title">Blumira (Commercial SIEM)</div>
            <div className="admin-field"><label>Account ID</label>
              <input type="text" value={form.blumira_account_id || ''} onChange={function(e) { setField('blumira_account_id', e.target.value); }} placeholder="GUID" />
            </div>
          </div>

          <div className="admin-form-section">
            <div className="admin-form-section-title">Asana</div>
            <div className="admin-field"><label>Project GIDs (one per line)</label>
              <textarea
                className="admin-textarea"
                rows={Math.max(3, (form.asana_project_gids || []).length + 1)}
                value={arrToLines(form.asana_project_gids)}
                onChange={function(e) { setField('asana_project_gids', linesToArr(e.target.value)); }}
                placeholder={'1209332459796027\n1210842111414768'}
              />
              <div className="admin-field-hint">
                {(form.asana_project_gids || []).length} GID{(form.asana_project_gids || []).length === 1 ? '' : 's'}.
                Use the "Discover Asana projects" button to find unmapped GIDs.
              </div>
            </div>
          </div>

          <div className="admin-form-section">
            <div className="admin-form-section-title">Jira</div>
            <div className="admin-form-grid two">
              <div className="admin-field"><label>Atlassian domain</label>
                <input type="text" value={form.jira_domain || ''} onChange={function(e) { setField('jira_domain', e.target.value); }} placeholder="acme.atlassian.net" />
              </div>
              <div className="admin-field"><label>Jira email (auth)</label>
                <input type="text" value={form.jira_email || ''} onChange={function(e) { setField('jira_email', e.target.value); }} placeholder="api@acme.com" />
              </div>
              <div className="admin-field" style={{ gridColumn: '1 / -1' }}><label>Project keys (one per line)</label>
                <textarea
                  className="admin-textarea"
                  rows={3}
                  value={arrToLines(form.jira_project_keys)}
                  onChange={function(e) { setField('jira_project_keys', linesToArr(e.target.value)); }}
                  placeholder={'DOP\nHBT\nHED'}
                />
              </div>
              <div className="admin-field" style={{ gridColumn: '1 / -1' }}>
                <label>Atlassian billing (JSON array of {'{ name, plan, users, monthlyCost }'})</label>
                <textarea
                  className="admin-textarea mono"
                  rows={6}
                  value={getAtlassianJson()}
                  onChange={function(e) { setAtlassianJson(e.target.value); }}
                  placeholder='[{"name":"Jira Software","plan":"Standard","users":14,"monthlyCost":133.41}]'
                />
              </div>
            </div>
          </div>

          <div className="admin-form-section">
            <div className="admin-form-section-title">Cisco Meraki</div>
            <div className="admin-field"><label>Organization ID</label>
              <input type="text" value={form.meraki_org_id || ''} onChange={function(e) { setField('meraki_org_id', e.target.value); }} placeholder="e.g. 549236" />
              <div className="admin-field-hint">Numeric ID from the Meraki dashboard URL (or use the Meraki Discovery button on the Integrations tab to pick it visually).</div>
            </div>
          </div>

          <div className="admin-form-section">
            <div className="admin-form-section-title">UniFi</div>
            <div className="admin-field-hint" style={{ marginBottom: 6 }}>
              Each row = one UniFi host_id paired with the site label that should appear in the UI.
            </div>
            {unifiPairs.length === 0 && <div style={{ fontSize: 11, opacity: 0.55, padding: '6px 0' }}>No UniFi hosts wired.</div>}
            {unifiPairs.map(function(p, idx) {
              return (
                <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                  <input type="text" style={{ flex: 2, fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }} value={p.id} onChange={function(e) { updateUnifiPair(idx, 'id', e.target.value); }} placeholder="host_id" />
                  <input type="text" style={{ flex: 1 }} value={p.label} onChange={function(e) { updateUnifiPair(idx, 'label', e.target.value); }} placeholder="Site label" />
                  <button className="admin-btn sm" type="button" onClick={function() { removeUnifiPair(idx); }} title="Remove this row">✕</button>
                </div>
              );
            })}
            <button className="admin-btn sm" type="button" onClick={addUnifiPair}>+ Add UniFi host</button>
          </div>

          <div className="admin-form-section">
            <div className="admin-form-section-title">Pax8</div>
            <div className="admin-field"><label>Company ID</label>
              <input type="text" value={form.pax8_company_id || ''} onChange={function(e) { setField('pax8_company_id', e.target.value); }} />
            </div>
          </div>

          <div className="admin-form-section">
            <div className="admin-form-section-title">Cyber Insurance</div>
            <div className="admin-form-grid two">
              <div className="admin-field"><label>Carrier</label>
                <input type="text" value={form.cyber_insurance_carrier || ''} onChange={function(e) { setField('cyber_insurance_carrier', e.target.value); }} placeholder="e.g. Coalition, At-Bay, Beazley" />
              </div>
              <div className="admin-field"><label>Policy number</label>
                <input type="text" value={form.cyber_insurance_policy_number || ''} onChange={function(e) { setField('cyber_insurance_policy_number', e.target.value); }} />
              </div>
              <div className="admin-field"><label>Annual premium (USD)</label>
                <input type="text" inputMode="numeric" value={form.cyber_insurance_premium_annual == null ? '' : form.cyber_insurance_premium_annual} onChange={function(e) { setField('cyber_insurance_premium_annual', e.target.value); }} placeholder="12500" />
              </div>
              <div className="admin-field"><label>Renewal date</label>
                <input type="date" value={form.cyber_insurance_renewal_date || ''} onChange={function(e) { setField('cyber_insurance_renewal_date', e.target.value); }} />
              </div>
              <div className="admin-field"><label>Deductible (USD)</label>
                <input type="text" inputMode="numeric" value={form.cyber_insurance_deductible == null ? '' : form.cyber_insurance_deductible} onChange={function(e) { setField('cyber_insurance_deductible', e.target.value); }} placeholder="2500" />
              </div>
              <div className="admin-field"><label>Coverage limit (USD)</label>
                <input type="text" inputMode="numeric" value={form.cyber_insurance_coverage_limit == null ? '' : form.cyber_insurance_coverage_limit} onChange={function(e) { setField('cyber_insurance_coverage_limit', e.target.value); }} placeholder="1000000" />
              </div>
              <div className="admin-field" style={{ gridColumn: '1 / -1' }}><label>Notes</label>
                <input type="text" value={form.cyber_insurance_notes || ''} onChange={function(e) { setField('cyber_insurance_notes', e.target.value); }} placeholder="Coverage exclusions, broker contact, etc." />
              </div>
            </div>
          </div>

          {/* CMMC campaign metadata — only relevant when client is flagged CMMC */}
          {form.cmmc && (
            <div className="admin-form-section">
              <div className="admin-form-section-title">CMMC Campaign</div>
              <div className="admin-form-grid two">
                <div className="admin-field"><label>Program lead</label>
                  <select value={form.cmmc_program_lead || 'atomus'} onChange={function(e) { setField('cmmc_program_lead', e.target.value); }}>
                    <option value="atomus">Atomus-led (default — Atomus owns SSP, Aegis covers endpoints)</option>
                    <option value="cbg">CBG-led (Aurelius is system of record — full SSP authority)</option>
                    <option value="hybrid">Hybrid (Aurelius authors, Atomus reviews / co-signs)</option>
                  </select>
                  <div className="admin-field-hint">Drives whether Aurelius surfaces Atomus partnership badging and links, or runs the campaign as the lead platform.</div>
                </div>
                <div className="admin-field"><label>Target level</label>
                  <select value={form.cmmc_target_level == null ? '' : String(form.cmmc_target_level)} onChange={function(e) { setField('cmmc_target_level', e.target.value); }}>
                    <option value="">—</option>
                    <option value="1">Level 1</option>
                    <option value="2">Level 2</option>
                  </select>
                </div>
                <div className="admin-field"><label>Campaign stage</label>
                  <select value={form.cmmc_campaign_stage || ''} onChange={function(e) { setField('cmmc_campaign_stage', e.target.value); }}>
                    <option value="">—</option>
                    <option value="gap_assessment">Gap Assessment</option>
                    <option value="remediation">Remediation</option>
                    <option value="pre_assessment">Pre-Assessment</option>
                    <option value="audit_scheduled">C3PAO Audit Scheduled</option>
                    <option value="certified">Certified</option>
                    <option value="maintenance">Maintenance</option>
                  </select>
                </div>
                <div className="admin-field"><label>Target assessment date</label>
                  <input type="date" value={form.cmmc_target_assessment_date || ''} onChange={function(e) { setField('cmmc_target_assessment_date', e.target.value); }} />
                </div>
                <div className="admin-field"><label>C3PAO (assessor)</label>
                  <input type="text" value={form.cmmc_c3pao || ''} onChange={function(e) { setField('cmmc_c3pao', e.target.value); }} placeholder="e.g. Apex Cybersecurity" />
                </div>
                <div className="admin-field" style={{ gridColumn: '1 / -1' }}><label>Atomus Aegis client dashboard URL</label>
                  <input type="text" value={form.cmmc_atomus_dashboard_url || ''} onChange={function(e) { setField('cmmc_atomus_dashboard_url', e.target.value); }} placeholder="https://app.atomuscyber.us/companies/..." />
                  <div className="admin-field-hint">Per-client deep-link into Atomus's dashboard. Surfaced on the CMMC tab.</div>
                </div>
                <div className="admin-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 22 }}>
                  <label style={{ display: 'flex', gap: 6, alignItems: 'center', textTransform: 'none', fontSize: 13, letterSpacing: 0, fontWeight: 400, cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!form.cmmc_aegis_active} onChange={function(e) { setField('cmmc_aegis_active', e.target.checked); }} /> Aegis agent active
                  </label>
                </div>
              </div>
            </div>
          )}

          <div className="admin-form-section">
            <div className="admin-form-section-title">Change note (optional)</div>
            <input type="text" className="admin-form-changenote" value={changeNote} onChange={function(e) { setChangeNote(e.target.value); }} placeholder="e.g. Added new BST Asana project — engineer onboarding" />
          </div>

          <div style={{ marginTop: 18 }}>
            <button className="admin-btn sm" type="button" onClick={function() { setShowHistory(!showHistory); }}>
              {showHistory ? 'Hide' : 'Show'} change history ({history.length})
            </button>
            {showHistory && (
              <div style={{ marginTop: 10, maxHeight: 220, overflow: 'auto', border: '1px solid var(--border-default)', borderRadius: 6, padding: 8, background: 'var(--bg-card)' }}>
                {history.length === 0 && <div style={{ opacity: 0.55, padding: 8 }}>No history yet — first edit will appear here.</div>}
                {history.map(function(h) {
                  return (
                    <div key={h.id} style={{ borderBottom: '1px solid var(--border-default)', padding: '8px 4px', fontSize: 11 }}>
                      <div style={{ fontWeight: 600 }}>{fmtDateTime(h.changed_at)} — {h.changed_by_email || 'system'}</div>
                      {h.change_note && <div style={{ fontStyle: 'italic', opacity: 0.7 }}>"{h.change_note}"</div>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {error && <div style={{ padding: 12, marginTop: 12, color: 'var(--accent-red)', fontSize: 12 }}>{error}</div>}
        </div>
        <div className="admin-modal-footer">
          <button className="admin-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="admin-btn primary" onClick={handleSave} disabled={saving || !dbBacked}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Bootstrap a brand-new client. Minimal payload (clientId + name + type +
// industry + cmmc); the rest of the integration IDs get filled in via the
// existing edit modal. clientId is auto-derived from name on first type
// (slugified), but the user can override at any point by editing it.
function AddClientModal({ existingIds, onClose, onCreated, onError }) {
  var [form, setForm] = useState({ clientId: '', name: '', type: 'Commercial', industry: '', cmmc: false });
  var [idTouched, setIdTouched] = useState(false);
  var [saving, setSaving] = useState(false);
  var [error, setError] = useState(null);

  function slugify(s) {
    return (s || '').toString().toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')      // strip non-alphanumeric (except spaces/hyphens)
      .replace(/\s+/g, '-')               // spaces -> single hyphen
      .replace(/-+/g, '-')                // collapse runs of hyphens
      .replace(/^-+|-+$/g, '')            // trim leading/trailing hyphen
      .slice(0, 30);
  }

  function setName(v) {
    setForm(function(prev) {
      var next = Object.assign({}, prev, { name: v });
      if (!idTouched) next.clientId = slugify(v);
      return next;
    });
  }

  function setClientId(v) {
    setIdTouched(true);
    setForm(function(prev) { return Object.assign({}, prev, { clientId: (v || '').toLowerCase() }); });
  }

  // Local validation for the ID field (mirrors backend regex)
  var idValid = /^[a-z0-9](?:[a-z0-9]|-(?!-))*[a-z0-9]$/.test(form.clientId) && form.clientId.length >= 2 && form.clientId.length <= 30;
  var idCollision = (existingIds || []).indexOf(form.clientId) >= 0;
  var canSubmit = !!form.name.trim() && idValid && !idCollision && !saving;

  function submit(e) {
    if (e) e.preventDefault();
    if (!canSubmit) return;
    setSaving(true); setError(null);
    authFetch(API + '/admin/clients', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        clientId: form.clientId,
        name: form.name.trim(),
        type: form.type || null,
        industry: form.industry.trim() || null,
        cmmc: !!form.cmmc
      })
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setSaving(false);
        if (x.ok) {
          onCreated(x.d.clientId, 'Client "' + x.d.data.name + '" created — fill in integration IDs next.');
        } else {
          setError(x.d.error || 'Create failed');
          if (onError) onError(x.d.error || 'Create failed');
        }
      })
      .catch(function() { setSaving(false); setError('Create failed'); });
  }

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={function(e) { e.stopPropagation(); }} style={{ maxWidth: 520 }}>
        <form onSubmit={submit}>
          <div className="admin-modal-header">New client</div>
          <div className="admin-modal-body">
            <div className="admin-field">
              <label>Client name</label>
              <input type="text" required autoFocus value={form.name} onChange={function(e) { setName(e.target.value); }} placeholder="e.g. Acme Defense Industries" />
              <div className="admin-field-hint">Display name shown across the dashboard. Free-form.</div>
            </div>

            <div className="admin-field">
              <label>Client ID</label>
              <input type="text" required value={form.clientId} onChange={function(e) { setClientId(e.target.value); }} placeholder="e.g. acme-defense" style={{ fontFamily: 'JetBrains Mono, monospace' }} />
              <div className="admin-field-hint">
                {idTouched
                  ? 'Lowercase, 2–30 chars, alphanumeric + hyphens (no leading/trailing/double hyphens). Used in URLs and audit logs — pick carefully.'
                  : 'Auto-generated from the name. Click here to override.'}
              </div>
              {form.clientId && !idValid && <div className="admin-banner error" style={{ margin: '6px 0 0', padding: '6px 10px', fontSize: 11 }}>ID format invalid.</div>}
              {idCollision && <div className="admin-banner error" style={{ margin: '6px 0 0', padding: '6px 10px', fontSize: 11 }}>"{form.clientId}" is already in use.</div>}
            </div>

            <div className="admin-field">
              <label>Type</label>
              <select value={form.type} onChange={function(e) { setForm(Object.assign({}, form, { type: e.target.value })); }}>
                <option value="Commercial">Commercial</option>
                <option value="Regulated (DIB)">Regulated (DIB)</option>
                <option value="Internal">Internal</option>
                <option value="Other">Other</option>
              </select>
              <div className="admin-field-hint">Drives default integrations + which sources Aurelius wires for this client.</div>
            </div>

            <div className="admin-field">
              <label>Industry</label>
              <input type="text" value={form.industry} onChange={function(e) { setForm(Object.assign({}, form, { industry: e.target.value })); }} placeholder="e.g. Defense Contractor, Aerospace, Manufacturing" />
            </div>

            <div className="admin-field">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.cmmc} onChange={function(e) { setForm(Object.assign({}, form, { cmmc: e.target.checked })); }} />
                <span>CMMC client</span>
              </label>
              <div className="admin-field-hint">Toggle if they're going through CMMC. You can configure target level / program lead in the next step.</div>
            </div>

            <div className="integration-readonly-hint" style={{ borderColor: 'rgba(91,158,244,0.32)', marginTop: 12 }}>
              <strong style={{ color: 'var(--accent-blue)' }}>Next steps after creation:</strong> open the Edit modal to fill NinjaOne / Huntress / Entra / etc. IDs, then create a client login under <em>Users → Add user</em> with role=client + this clientId.
            </div>

            {error && <div className="admin-banner error" style={{ marginTop: 10 }}>{error}</div>}
          </div>
          <div className="admin-modal-footer">
            <button type="button" className="admin-btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="admin-btn primary" disabled={!canSubmit}>{saving ? 'Creating…' : 'Create client'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AdminIntegrationsTab({ refreshClientList }) {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [filter, setFilter] = useState('');
  var [expandedClient, setExpandedClient] = useState(null);
  var [asanaDiscoverOpen, setAsanaDiscoverOpen] = useState(false);
  var [vendorOpen, setVendorOpen] = useState(null);          // {key} for the vendor modal currently open
  var [editingClient, setEditingClient] = useState(null);    // clientId being edited, or null
  var [addingClient, setAddingClient] = useState(false);     // open the AddClientModal
  var [reloading, setReloading] = useState(false);
  var [toast, setToast] = useState(null);
  var [intentBanner, setIntentBanner] = useState(null);

  // Deep-link consumer: an IntegrationEmptyState elsewhere in the app
  // can stash an intent (clientId + vendor + editAnchor) in
  // sessionStorage and bounce the user here. We pop the intent on
  // mount, auto-open the Edit modal for that client, and surface a
  // banner naming the integration the admin should configure.
  useEffect(function() {
    try {
      var raw = sessionStorage.getItem('aurelius_admin_integrations_intent');
      if (!raw) return;
      sessionStorage.removeItem('aurelius_admin_integrations_intent');
      var intent = JSON.parse(raw);
      // Only honor recent intents (5-minute TTL — guard against stale
      // session storage from a previous admin who didn't follow through)
      if (!intent || !intent.clientId || (Date.now() - (intent.ts || 0)) > 5 * 60 * 1000) return;
      setEditingClient(intent.clientId);
      setIntentBanner({ clientId: intent.clientId, vendor: intent.vendor, editAnchor: intent.editAnchor });
    } catch (e) { /* sessionStorage unavailable / parse failure — silent */ }
  }, []);

  // Vendor configs for the parameterized VendorDiscoveryModal
  var VENDOR_CONFIGS = {
    ninjaone: {
      title: 'NinjaOne Discovery',
      discoverPath: '/admin/ninjaone/discover',
      assignPath: '/admin/ninjaone/assign',
      mode: 'single',
      idLabel: 'Org ID',
      sections: [
        { key: 'commercial', label: 'Commercial (us2.ninjarmm.com)', instance: 'commercial' },
        { key: 'fedramp', label: 'FedRAMP (fed.ninjarmm.com)', instance: 'fedramp' }
      ],
      payloadShape: function(action, id, clientId, label, instance) { return { clientId: clientId, instance: instance, value: id, action: action }; },
      helpText: 'Every NinjaOne organization visible across both instances. Pick a client from the dropdown to map an org. Each client has at most one commercial org and one FedRAMP org.'
    },
    huntress: {
      title: 'Huntress Discovery',
      discoverPath: '/admin/huntress/discover',
      assignPath: '/admin/huntress/assign',
      mode: 'single',
      idLabel: 'Org ID',
      payloadShape: function(action, id, clientId) { return { clientId: clientId, value: id, action: action }; },
      helpText: 'All Huntress organizations visible to CBG. Each client maps to exactly one Huntress org.'
    },
    unifi: {
      title: 'UniFi Discovery',
      discoverPath: '/admin/unifi/discover',
      assignPath: '/admin/unifi/assign',
      mode: 'array',
      idLabel: 'Host ID',
      renderResource: function(r) { return (r.deviceCount || 0) + ' device' + (r.deviceCount === 1 ? '' : 's') + (r.ip ? ' · ' + r.ip : ''); },
      payloadShape: function(action, id, clientId, label) { return { clientId: clientId, hostId: id, action: action, label: label || null }; },
      helpText: 'Every UniFi controller / host CBG can see. Clients can have multiple hosts (multi-site). Add a site label so it shows up nicely on the network tab.'
    },
    meraki: {
      title: 'Meraki Discovery',
      discoverPath: '/admin/meraki/discover',
      assignPath: '/admin/meraki/assign',
      mode: 'single',
      idLabel: 'Org ID',
      renderResource: function(r) { return r.url ? r.url.replace(/^https?:\/\//, '') : ''; },
      payloadShape: function(action, id, clientId) { return { clientId: clientId, value: id, action: action }; },
      helpText: 'Every Cisco Meraki organization visible to CBG. Each client maps to one Meraki org — switching organizations later is a single click.'
    }
  };

  function showToast(msg, type) { setToast({ msg: msg, type: type || 'info' }); setTimeout(function() { setToast(null); }, 3500); }

  function fetchData() {
    setLoading(true);
    authFetch(API + '/admin/integrations')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) { setData(d); setLoading(false); })
      .catch(function() { setLoading(false); });
  }

  useEffect(fetchData, []);

  function handleReload() {
    setReloading(true);
    authFetch(API + '/admin/client-integrations/reload', { method: 'POST' })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setReloading(false);
        if (x.ok) { showToast('Reloaded ' + x.d.count + ' configs from ' + x.d.source.toUpperCase(), 'ok'); fetchData(); }
        else { showToast('Reload failed', 'error'); }
      })
      .catch(function() { setReloading(false); showToast('Reload failed', 'error'); });
  }

  // Lightweight refresh — re-fetches the integration view from the
  // existing in-memory config WITHOUT forcing the server to re-read
  // the DB. Use this after running an external setup script (e.g.
  // a client GA running the Graph-app PS1) — the credential state
  // changed but the in-memory config is unchanged, so a full Reload
  // from DB is overkill. Bound to its own button to keep the heavier
  // "Reload from DB" affordance distinct.
  var [refreshing, setRefreshing] = useState(false);
  function handleRefresh() {
    setRefreshing(true);
    fetchData();
    // Re-flag false after a short tick (fetchData is fire-and-forget)
    setTimeout(function() { setRefreshing(false); }, 600);
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', opacity: 0.6 }}>Loading integration wiring...</div>;
  if (!data) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--accent-red, #ef4444)' }}>Failed to load integrations.</div>;

  var filtered = data.clients.filter(function(c) {
    if (!filter) return true;
    var q = filter.toLowerCase();
    return c.name.toLowerCase().includes(q) || c.clientId.toLowerCase().includes(q) || (c.industry || '').toLowerCase().includes(q);
  });

  var src = data.configSource || { source: 'file', count: data.clients.length };
  var dbBacked = src.source === 'db';

  return (
    <div className="admin-integrations-tab">
      <div className="admin-toolbar">
        <input className="admin-filter-input" placeholder="Filter clients..." value={filter} onChange={function(e) { setFilter(e.target.value); }} />
        <div style={{ flex: 1 }} />
        <button className="admin-btn sm primary" onClick={function() { setAddingClient(true); }} disabled={!dbBacked} title={dbBacked ? 'Bootstrap a new client (clientId + name + type, then fill IDs)' : 'Available when DB-backed config is active'}>
          + Add client
        </button>
        <button className="admin-btn sm" onClick={handleRefresh} disabled={refreshing} title="Re-fetch the integration view. Use after running a setup script in a client tenant.">
          {refreshing ? '↻ Refreshing…' : '↻ Refresh'}
        </button>
        <button className="admin-btn sm" onClick={handleReload} disabled={reloading} title="Force server to re-read client_integrations from the database (heavier — use after a direct DB edit)">
          {reloading ? 'Reloading…' : 'Reload from DB'}
        </button>
        <div className="integration-legend">
          <span className="int-legend-chip live">Live</span>
          <span className="int-legend-chip broken">Broken</span>
          <span className="int-legend-chip partial">Partial</span>
          <span className="int-legend-chip neutral">Not wired</span>
        </div>
      </div>

      <div className="integration-readonly-hint" style={{ borderColor: dbBacked ? 'rgba(78,201,176,0.35)' : 'rgba(245,166,35,0.35)' }}>
        {dbBacked
          ? <><strong style={{ color: 'var(--accent-green)' }}>DB-backed.</strong> Edits in this tab persist immediately to <code>aurelius.client_integrations</code> — no commit, no redeploy. The file (<code>backend/client-config.js</code>) is kept as a bootstrap seed and emergency fallback.</>
          : <><strong style={{ color: 'var(--accent-amber)' }}>File-backed (fallback mode).</strong> The DB-backed table is empty or unreachable. Edits in this tab will fail until the migration script runs (<code>node scripts/migrate-client-config-to-db.js</code>).</>
        }
      </div>

      {intentBanner && (
        <div className="admin-banner info" style={{ marginTop: 8, marginBottom: 8 }}>
          <strong>Connect {intentBanner.vendor || 'integration'}</strong> for <code>{intentBanner.clientId}</code>. The Edit Integrations modal opened automatically — find the {intentBanner.vendor || 'relevant'} section and fill in the required field(s).
          <button type="button" className="admin-btn sm" style={{ marginLeft: 12 }} onClick={function() { setIntentBanner(null); }}>Dismiss</button>
        </div>
      )}

      <div className="integration-discover-bar">
        <span className="integration-discover-label">One-click vendor mapping:</span>
        <button className="integration-inline-action" onClick={function() { setAsanaDiscoverOpen(true); }}>Asana ›</button>
        <button className="integration-inline-action" onClick={function() { setVendorOpen('ninjaone'); }}>NinjaOne ›</button>
        <button className="integration-inline-action" onClick={function() { setVendorOpen('huntress'); }}>Huntress ›</button>
        <button className="integration-inline-action" onClick={function() { setVendorOpen('unifi'); }}>UniFi ›</button>
        <button className="integration-inline-action" onClick={function() { setVendorOpen('meraki'); }}>Meraki ›</button>
      </div>

      {asanaDiscoverOpen && (
        <AsanaDiscoveryModal
          clients={data.clients.map(function(c) { return { id: c.clientId, name: c.name }; })}
          onClose={function() { setAsanaDiscoverOpen(false); fetchData(); /* refresh wired counts after assignments */ }}
          onAssigned={function(msg) { showToast(msg, 'ok'); }}
          onError={function(msg) { showToast(msg, 'error'); }}
        />
      )}
      {vendorOpen && VENDOR_CONFIGS[vendorOpen] && (
        <VendorDiscoveryModal
          vendor={VENDOR_CONFIGS[vendorOpen]}
          clients={data.clients.map(function(c) { return { id: c.clientId, name: c.name }; })}
          onClose={function() { setVendorOpen(null); fetchData(); }}
          onAssigned={function(msg) { showToast(msg, 'ok'); }}
          onError={function(msg) { showToast(msg, 'error'); }}
        />
      )}
      {editingClient && (
        <EditClientIntegrationsModal
          clientId={editingClient}
          dbBacked={dbBacked}
          onClose={function() {
            setEditingClient(null);
            // Refresh after close — covers the case where the operator
            // ran an external setup script (e.g. Graph-app PS1 in the
            // client tenant) AFTER opening the modal. Modal contents
            // are stale; the parent view needs fresh data.
            fetchData();
          }}
          onSaved={function(msg) {
            showToast(msg || 'Saved', 'ok');
            setEditingClient(null);
            fetchData();
            // Refresh the App-level client list too — covers the case
            // where the operator renamed the client in the Edit modal.
            if (refreshClientList) refreshClientList();
          }}
          onError={function(msg) { showToast(msg || 'Save failed', 'error'); }}
        />
      )}
      {addingClient && (
        <AddClientModal
          existingIds={data.clients.map(function(c) { return c.clientId; })}
          onClose={function() { setAddingClient(false); }}
          onCreated={function(newId, msg) {
            showToast(msg || 'Created', 'ok');
            setAddingClient(false);
            // Refresh both the integrations-tab data AND the App-level
            // clientList so dropdowns elsewhere (Add User, Automation Run,
            // Consents) see the new client without a page reload.
            fetchData();
            if (refreshClientList) refreshClientList();
            setEditingClient(newId);
          }}
          onError={function(msg) { showToast(msg || 'Create failed', 'error'); }}
        />
      )}
      {toast && <div className={'admin-toast ' + (toast.type || 'info')}>{toast.msg}</div>}

      {/* Global integrations — not per-client */}
      <div className="integration-global-strip">
        <div className="integration-global-title">Global platforms</div>
        <div className="integration-global-chips">
          {Object.keys(data.globalIntegrations).map(function(k) {
            var g = data.globalIntegrations[k];
            var label = { pax8: 'Pax8', anthropic: 'Anthropic', keeper: 'Keeper' }[k] || k;
            return (
              <div key={k} className={'integration-global-chip ' + (g.credentialPresent ? 'live' : 'broken')}>
                <span className="int-global-dot" />
                <span>{label}</span>
                <span className="int-global-status">{g.credentialPresent ? 'credentials set' : 'credentials missing'}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="integration-cards-grid">
        {filtered.map(function(c) {
          var isOpen = expandedClient === c.clientId;
          return (
            <div key={c.clientId} className={'integration-card' + (isOpen ? ' expanded' : '')}>
              <div className="integration-card-header" onClick={function() { setExpandedClient(isOpen ? null : c.clientId); }}>
                <div className="integration-card-title-block">
                  <div className="integration-card-title">{c.name}</div>
                  <div className="integration-card-meta">
                    <span className="integration-card-key">{c.clientId}</span>
                    <span className="integration-card-sep">·</span>
                    <span>{c.type}</span>
                    <span className="integration-card-sep">·</span>
                    <span>{c.industry}</span>
                    {c.cmmc && <span className="admin-chip warn" style={{ marginLeft: 6 }}>CMMC</span>}
                    {c.isDemo && <span className="admin-chip info" style={{ marginLeft: 6 }}>Demo</span>}
                    {/* Tenant licensing chip — set by /graph-app/verify or
                        /licensing-probe. Shown only when probed. Red for
                        CMMC clients on Free (licensing blocks meaningful
                        CMMC posture); amber for non-CMMC on Free (just FYI);
                        green for P1/P2. */}
                    {c.integrations && c.integrations.entra && c.integrations.entra.licensing && c.integrations.entra.licensing.tier && (function() {
                      var lic = c.integrations.entra.licensing;
                      var cls, label, title;
                      if (lic.tier === 'p2') { cls = 'ok'; label = 'Entra P2'; title = 'Entra ID P2 detected — full posture coverage available.'; }
                      else if (lic.tier === 'p1') { cls = 'ok'; label = 'Entra P1'; title = 'Entra ID P1 detected — most CMMC identity controls evidence-able.'; }
                      else if (lic.tier === 'free') {
                        cls = c.cmmc ? 'bad' : 'warn';
                        label = c.cmmc ? 'Entra Free — CMMC blocked' : 'Entra Free';
                        title = c.cmmc
                          ? 'Tenant on Entra ID Free. CMMC controls requiring P1+ (3.5.6 inactive accounts, ID.5.* conditional access, sign-in-risk) cannot be evidenced. Upgrade to M365 Business Premium minimum.'
                          : 'Tenant on Entra ID Free. Custom CA + sign-in risk policies unavailable. Inactivity / signInActivity-based checks degrade to "unknown".';
                      }
                      else return null;
                      return <span className={'admin-chip ' + cls} style={{ marginLeft: 6 }} title={title}>{label}</span>;
                    })()}
                  </div>
                </div>
                <div className="integration-card-right">
                  <div className="integration-card-counts">
                    {/* Two distinct views the admin should be able to see at a glance:
                        • Vendor count — "X wired" out of the underlying vendor mappings
                          (ninjaone, huntress, entra, azure, asana, jira, unifi, meraki, …).
                          Server-side summary returns this directly.
                        • Workspace coverage — "Y connected" out of the conceptual-kinds list
                          (identity, endpoints, EDR, SIEM, network, projects, …), tier-filtered.
                          Derived from the per-vendor map here so the math matches what's
                          visible to the admin (we can't reuse getIntegrationStatus directly
                          because the admin-list row doesn't carry the raw config fields). */}
                    {(function() {
                      var v = c.integrations || {};
                      var totalVendors = Object.keys(v).length;
                      var isCmmc = !!c.cmmc;
                      // Map each conceptual area to the underlying vendor(s) that
                      // would make it "configured". Tier-filter via applicable flag.
                      var areas = [
                        { kind: 'identity',   ok: v.entra && v.entra.configured },
                        { kind: 'endpoints',  ok: (v.ninjaone_commercial && v.ninjaone_commercial.configured) || (v.ninjaone_fedramp && v.ninjaone_fedramp.configured) },
                        { kind: 'edr',        applicable: !isCmmc, ok: v.huntress && v.huntress.configured },
                        { kind: 'siem',       applicable: !isCmmc, ok: v.blumira && v.blumira.configured },
                        { kind: 'network',    ok: (v.unifi && v.unifi.configured) || (v.meraki && v.meraki.configured) },
                        { kind: 'cloud-cost', ok: v.azure && v.azure.configured },
                        { kind: 'projects',   ok: v.asana && v.asana.configured },
                        { kind: 'devops',     ok: v.jira && v.jira.configured }
                      ];
                      var applicable = areas.filter(function(a) { return a.applicable !== false; });
                      var conceptualConnected = applicable.filter(function(a) { return !!a.ok; }).length;
                      var conceptualTotal = applicable.length;
                      return (
                        <>
                          <span title={'Vendor-level: ' + c.summary.configured + ' of ' + totalVendors + ' underlying integrations set up (NinjaOne, Huntress, Entra, Azure, Asana, Jira, UniFi, Meraki, Blumira, …)'}>
                            <strong>{c.summary.configured}{totalVendors > 0 ? '/' + totalVendors : ''}</strong> vendors
                          </span>
                          {c.summary.broken > 0 && <span className="ic-broken" title="Vendor configured but server can't authenticate (missing or invalid credential)"><strong>{c.summary.broken}</strong> broken</span>}
                          {c.summary.partial > 0 && <span className="ic-partial"><strong>{c.summary.partial}</strong> partial</span>}
                          {conceptualTotal > 0 && (
                            <span className="ic-coverage" title={'Workspace coverage: ' + conceptualConnected + ' of ' + conceptualTotal + ' areas (identity, endpoints, EDR, SIEM, network, cloud-cost, projects, devops) — tier-filtered. CMMC clients drop the commercial EDR + SIEM rows here.'}>
                              <strong>{conceptualConnected}/{conceptualTotal}</strong> areas
                            </span>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  <div className="integration-card-user">
                    {c.hasUser ? (
                      c.userEnabled
                        ? <span className="admin-chip ok" title={c.userEmail}>User active</span>
                        : <span className="admin-chip bad" title={c.userEmail}>User disabled</span>
                    ) : <span className="admin-chip warn">No DB user</span>}
                  </div>
                  <button
                    className="admin-btn sm"
                    onClick={function(e) { e.stopPropagation(); setEditingClient(c.clientId); }}
                    title={dbBacked ? 'Edit integration mappings (DB-backed)' : 'DB unreachable — fix the connection first'}
                    disabled={!dbBacked}
                  >Edit</button>
                  <span className="integration-expand-icon">{isOpen ? '▴' : '▾'}</span>
                </div>
              </div>

              <div className="integration-grid">
                {Object.keys(c.integrations).map(function(k) {
                  var it = c.integrations[k];
                  var status = integrationStatus(it);
                  return (
                    <div key={k} className={'int-chip ' + status.cls} title={status.label}>
                      <span className="int-chip-label">{INTEGRATION_LABELS[k] || k}</span>
                      <span className="int-chip-status">{status.label}</span>
                    </div>
                  );
                })}
              </div>

              {isOpen && (
                <div className="integration-detail">
                  {c.hasUser && (
                    <div className="integration-detail-section">
                      <div className="integration-detail-label">DB User</div>
                      <div className="integration-detail-value">
                        <code>{c.userEmail}</code>
                        {' · id=' + c.userId}
                        {' · ' + (c.userEnabled ? 'enabled' : 'disabled')}
                        {c.userLastLogin && ' · last login ' + fmtDateTime(c.userLastLogin)}
                      </div>
                    </div>
                  )}
                  {Object.keys(c.integrations).map(function(k) {
                    var it = c.integrations[k];
                    if (!it.configured && !it.partial) return null;
                    var status = integrationStatus(it);
                    return (
                      <div key={k} className="integration-detail-section">
                        <div className="integration-detail-label">
                          {INTEGRATION_LABELS[k] || k}
                          <span className={'int-inline-chip ' + status.cls}>{status.label}</span>
                        </div>
                        <div className="integration-detail-value mono">
                          {renderIntegrationDetail(k, it)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && <div style={{ padding: 40, textAlign: 'center', opacity: 0.5 }}>No clients match "{filter}"</div>}
      </div>
    </div>
  );
}

function renderIntegrationDetail(key, it) {
  if (!it.credentialPresent) {
    var envHint = {
      ninjaone_commercial: 'NINJAONE_CLIENT_ID / NINJAONE_CLIENT_SECRET',
      ninjaone_fedramp:    'NINJAONE_FED_CLIENT_ID / NINJAONE_FED_CLIENT_SECRET',
      huntress:            'HUNTRESS_API_KEY / HUNTRESS_API_SECRET',
      entra:               'MS_CLIENT_ID / MS_CLIENT_SECRET',
      azure:               'MS_CLIENT_ID / MS_CLIENT_SECRET',
      blumira:             'BLUMIRA_CLIENT_ID / BLUMIRA_CLIENT_SECRET',
      asana:               'ASANA_ACCESS_TOKEN',
      jira:                'JIRA_API_TOKEN',
      unifi:               'UNIFI_API_KEY',
      meraki:              'MERAKI_API_KEY'
    }[key] || '';
    return (
      <div>
        {integrationValueLine(key, it)}
        <div style={{ marginTop: 6, color: 'var(--accent-red, #ef4444)', fontSize: 11 }}>
          Server credentials missing — set {envHint} in App Service config to activate.
        </div>
      </div>
    );
  }
  return integrationValueLine(key, it);
}

function integrationValueLine(key, it) {
  if (it.value == null && !it.projectCount && !it.hostCount) return <span style={{ opacity: 0.5 }}>Configured but no value captured</span>;
  if (key === 'entra' && typeof it.value === 'object' && it.value) {
    return <div>tenant_id: {it.value.tenant}<br/>graph_client_id: {it.value.graph_client || <span style={{ color: 'var(--accent-amber, #f5a623)' }}>not set (partial)</span>}</div>;
  }
  if (key === 'asana') return <div>{it.projectCount} project{it.projectCount === 1 ? '' : 's'} wired</div>;
  if (key === 'jira')  return <div>domain: {it.value}<br/>{it.projectCount} project key{it.projectCount === 1 ? '' : 's'}</div>;
  if (key === 'unifi') return <div>{it.hostCount} host{it.hostCount === 1 ? '' : 's'} — sites: {it.siteLabels && it.siteLabels.length ? it.siteLabels.join(', ') : '(no labels)'}</div>;
  return <div>{String(it.value)}</div>;
}

function AdminSessionsTab() {
  var [sessions, setSessions] = useState([]);
  var [loading, setLoading] = useState(true);
  var [toast, setToast] = useState(null);

  function showToast(msg, type) { setToast({ msg: msg, type: type || 'info' }); setTimeout(function() { setToast(null); }, 3500); }

  function fetchSessions() {
    setLoading(true);
    authFetch(API + '/admin/sessions')
      .then(function(r) { return r.ok ? r.json() : []; })
      .then(function(data) { setSessions(data); setLoading(false); })
      .catch(function() { setLoading(false); });
  }

  useEffect(fetchSessions, []);

  function revoke(s) {
    if (!window.confirm('Revoke session for ' + (s.email || ('user#' + s.user_id)) + '?\n\nThey will be logged out on their next API call.')) return;
    authFetch(API + '/admin/sessions/revoke', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ jti: s.jti, userId: s.user_id, reason: 'admin-revoke' })
    })
      .then(function(r) { if (r.ok) { showToast('Session revoked', 'ok'); fetchSessions(); } else { showToast('Revoke failed', 'error'); } })
      .catch(function() { showToast('Revoke failed', 'error'); });
  }

  return (
    <div className="admin-sessions-tab">
      <div className="admin-toolbar">
        <div style={{ fontSize: 12, opacity: 0.7 }}>
          Active sessions are inferred from the last 8 hours of LOGIN_SUCCESS events. A logged-out or expired session may still briefly appear here.
        </div>
        <div style={{ flex: 1 }} />
        <button className="admin-btn" onClick={fetchSessions} disabled={loading}>{loading ? 'Loading...' : 'Refresh'}</button>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Role</th>
              <th>Client</th>
              <th>Login time</th>
              <th>IP</th>
              <th>User agent</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="7" style={{ padding: 24, textAlign: 'center', opacity: 0.5 }}>Loading sessions...</td></tr>}
            {!loading && sessions.length === 0 && <tr><td colSpan="7" style={{ padding: 24, textAlign: 'center', opacity: 0.5 }}>No active sessions.</td></tr>}
            {!loading && sessions.map(function(s) {
              return (
                <tr key={s.jti}>
                  <td>{s.email || ('user#' + s.user_id)}</td>
                  <td><span className={'admin-role-chip ' + s.role}>{s.role}</span></td>
                  <td>{s.client_id || <span style={{ opacity: 0.4 }}>—</span>}</td>
                  <td style={{ fontSize: 12 }}>{fmtDateTime(s.login_at)}</td>
                  <td style={{ fontSize: 11, fontFamily: 'monospace' }}>{s.ip || '—'}</td>
                  <td style={{ fontSize: 11, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.user_agent}>{s.user_agent || '—'}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button className="admin-btn sm danger" onClick={function() { revoke(s); }}>Revoke</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {toast && <AdminToast {...toast} />}
    </div>
  );
}

function AdminToast({ msg, type }) {
  var bg = type === 'error' ? 'linear-gradient(135deg, rgba(239,68,68,0.95), rgba(220,38,38,0.95))'
         : type === 'ok'    ? 'linear-gradient(135deg, rgba(34,197,94,0.95), rgba(22,163,74,0.95))'
         :                    'linear-gradient(135deg, rgba(91,158,244,0.95), rgba(56,120,200,0.95))';
  return (
    <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999, padding: '12px 20px', background: bg, color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 600, boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>
      {msg}
    </div>
  );
}

// ── Tenant Assessments admin tab ────────────────────────────────────
function AdminAssessmentsTab() {
  var [assessments, setAssessments] = useState([]);
  var [providers, setProviders] = useState([]);
  var [loading, setLoading] = useState(true);
  var [creating, setCreating] = useState(false);
  var [detail, setDetail] = useState(null);        // selected assessment id
  var [newConsent, setNewConsent] = useState(null); // { url, name, provider } — show after create
  var [toast, setToast] = useState(null);

  function showToast(msg, type) { setToast({ msg: msg, type: type || 'info' }); setTimeout(function() { setToast(null); }, 3500); }

  function fetchAll() {
    setLoading(true);
    Promise.all([
      authFetch(API + '/admin/assessments').then(function(r) { return r.ok ? r.json() : []; }),
      authFetch(API + '/admin/assessments/providers').then(function(r) { return r.ok ? r.json() : []; })
    ]).then(function(results) {
      setAssessments(results[0] || []);
      setProviders(results[1] || []);
      setLoading(false);
    }).catch(function() { setLoading(false); showToast('Failed to load assessments', 'error'); });
  }
  useEffect(fetchAll, []);

  function onDelete(a) {
    if (!window.confirm('Delete assessment for ' + a.prospect_name + '?\n\nSoft delete — row preserved in DB.')) return;
    authFetch(API + '/admin/assessments/' + a.id, { method: 'DELETE' })
      .then(function(r) { if (r.ok) { showToast('Deleted', 'ok'); fetchAll(); if (detail === a.id) setDetail(null); } else { showToast('Delete failed', 'error'); } });
  }

  function downloadPdf(a) {
    var token = getToken();
    fetch(API + '/admin/assessments/' + a.id + '/report.pdf', { headers: { 'Authorization': 'Bearer ' + token } })
      .then(function(r) { if (!r.ok) throw new Error(); return r.blob(); })
      .then(function(blob) {
        var url = URL.createObjectURL(blob);
        var link = document.createElement('a');
        link.href = url;
        link.download = 'aurelius-assessment-' + (a.tenant_domain || a.prospect_name).replace(/[^a-z0-9]/gi, '_') + '.pdf';
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
        URL.revokeObjectURL(url);
      })
      .catch(function() { showToast('Report generation failed', 'error'); });
  }

  var statusClass = { pending_consent: 'warn', in_progress: 'info', completed: 'ok', failed: 'bad', expired: 'warn-soft' };
  var statusLabel = { pending_consent: 'Awaiting consent', in_progress: 'Collecting', completed: 'Complete', failed: 'Failed', expired: 'Expired' };

  return (
    <div className="admin-assessments-tab">
      <div className="admin-toolbar">
        <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          Send a prospect a read-only tenant assessment link. They consent → we inventory → we generate a migration-readiness PDF.
        </div>
        <div style={{ flex: 1 }} />
        <button className="admin-btn primary" onClick={function() { setCreating(true); }}>+ New Assessment</button>
        <button className="admin-btn" onClick={fetchAll} disabled={loading}>{loading ? 'Loading...' : 'Refresh'}</button>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Prospect</th>
              <th>Provider</th>
              <th>Status</th>
              <th>Tenant</th>
              <th>Users</th>
              <th>Tier</th>
              <th>Created</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="8" style={{ padding: 24, textAlign: 'center', opacity: 0.5 }}>Loading assessments…</td></tr>}
            {!loading && assessments.length === 0 && (
              <tr><td colSpan="8" style={{ padding: 36, textAlign: 'center' }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6, color: 'var(--text-primary)' }}>No assessments yet</div>
                <div style={{ fontSize: 12, opacity: 0.65 }}>Create one to send a prospect a consent link.</div>
              </td></tr>
            )}
            {!loading && assessments.map(function(a) {
              var inv = a.inventory || {};
              var users = inv.users || {};
              return (
                <tr key={a.id} style={{ cursor: 'pointer' }} onClick={function() { setDetail(a.id); }}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{a.prospect_name}</div>
                    <div style={{ fontSize: 11, opacity: 0.6 }}>{a.prospect_email}</div>
                  </td>
                  <td><span className="admin-role-chip">{a.provider === 'microsoft' ? 'M365' : 'Google'}</span></td>
                  <td><span className={'admin-chip ' + (statusClass[a.status] || 'info')}>{statusLabel[a.status] || a.status}</span></td>
                  <td style={{ fontSize: 12 }}>{a.tenant_domain || <span style={{ opacity: 0.4 }}>—</span>}</td>
                  <td style={{ fontSize: 12 }}>{users.total != null ? users.total.toLocaleString() : <span style={{ opacity: 0.4 }}>—</span>}</td>
                  <td>{inv.provider ? <AssessmentTierChip inventory={inv} /> : <span style={{ opacity: 0.4 }}>—</span>}</td>
                  <td style={{ fontSize: 12 }}>{fmtDateTime(a.created_at)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <div className="admin-row-actions">
                      {a.status === 'completed' && <button className="admin-btn sm primary" onClick={function(e) { e.stopPropagation(); downloadPdf(a); }}>PDF</button>}
                      <button className="admin-btn sm danger" onClick={function(e) { e.stopPropagation(); onDelete(a); }}>Delete</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {creating && (
        <NewAssessmentModal
          providers={providers}
          onClose={function() { setCreating(false); }}
          onCreated={function(result) { setCreating(false); setNewConsent(result); fetchAll(); }}
          onError={function(msg) { showToast(msg, 'error'); }}
        />
      )}
      {newConsent && <ConsentLinkReveal data={newConsent} onClose={function() { setNewConsent(null); }} />}
      {detail && <AssessmentDetailDrawer id={detail} onClose={function() { setDetail(null); fetchAll(); }} onDownload={downloadPdf} />}
      {toast && <AdminToast {...toast} />}
    </div>
  );
}

function AssessmentTierChip({ inventory }) {
  // Score client-side from the stored inventory — matches backend exactly since
  // complexity.js's logic is deterministic on the inventory shape.
  var pts = 0;
  var u = inventory.users || {}, m = inventory.mailboxes || {}, f = inventory.fileStorage || {}, c = inventory.collab || {}, l = inventory.licenses || {};
  var TB = 1024*1024*1024*1024, GB = 1024*1024*1024;
  if (u.total > 100) pts += 1;
  if (u.total > 500) pts += 2;
  if (u.total > 2000) pts += 3;
  if ((m.totalBytes||0) > 1*TB) pts += 2;
  if ((m.totalBytes||0) > 10*TB) pts += 3;
  if ((m.largeOver50GB||0) > 5) pts += 1;
  if ((f.totalBytes||0) > 1*TB) pts += 1;
  if ((f.totalBytes||0) > 10*TB) pts += 2;
  if ((f.largeUsersOver100GB||0) > 10) pts += 1;
  if ((c.sites||0) > 100) pts += 1;
  if ((c.teamsCount||0) > 50) pts += 1;
  if (l.totalAvailable && (l.wastedSeats/l.totalAvailable) > 0.2) pts += 1;
  var tier = pts <= 2 ? 'S' : pts <= 5 ? 'M' : pts <= 8 ? 'L' : 'XL';
  var colors = { S: '#22c55e', M: '#f59e0b', L: '#f97316', XL: '#ef4444' };
  return <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 6, background: colors[tier], color: '#fff', fontSize: 10, fontWeight: 800 }}>{tier}</span>;
}

function NewAssessmentModal({ providers, onClose, onCreated, onError }) {
  var [form, setForm] = useState({ provider: '', prospectName: '', prospectEmail: '', notes: '', expiryDays: 14 });
  var [saving, setSaving] = useState(false);

  // Auto-select first enabled provider on mount
  useEffect(function() {
    if (!form.provider) {
      var firstEnabled = providers.find(function(p) { return p.enabled; });
      if (firstEnabled) setForm(function(s) { return { ...s, provider: firstEnabled.name }; });
    }
  }, [providers]);

  function submit(e) {
    e.preventDefault();
    if (!form.provider || !form.prospectName || !form.prospectEmail) { onError('All fields required'); return; }
    setSaving(true);
    authFetch(API + '/admin/assessments', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form)
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setSaving(false);
        if (x.ok) onCreated({ url: x.d.consentUrl, name: form.prospectName, provider: form.provider });
        else onError(x.d.error || 'Create failed');
      })
      .catch(function() { setSaving(false); onError('Create failed'); });
  }

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={function(e) { e.stopPropagation(); }} style={{ maxWidth: 520 }}>
        <form onSubmit={submit}>
          <div className="admin-modal-header">New Tenant Assessment</div>
          <div className="admin-modal-body">
            <div className="admin-field">
              <label>Provider</label>
              <select value={form.provider} onChange={function(e) { setForm(Object.assign({}, form, { provider: e.target.value })); }}>
                {providers.map(function(p) {
                  return <option key={p.name} value={p.name} disabled={!p.enabled}>{p.displayName}{!p.enabled ? ' — ' + p.disabledReason : ''}</option>;
                })}
              </select>
            </div>
            <div className="admin-field">
              <label>Prospect name</label>
              <input type="text" value={form.prospectName} onChange={function(e) { setForm(Object.assign({}, form, { prospectName: e.target.value })); }} placeholder="Rodriguez Law Firm" required />
            </div>
            <div className="admin-field">
              <label>Prospect email (consent link recipient)</label>
              <input type="email" value={form.prospectEmail} onChange={function(e) { setForm(Object.assign({}, form, { prospectEmail: e.target.value })); }} placeholder="admin@rodriguezlaw.com" required />
              <div className="admin-field-hint">Pre-fills sign-in at the IdP. Must be a Global Admin / Super Admin to consent.</div>
            </div>
            <div className="admin-field">
              <label>Link expiry (days)</label>
              <input type="number" min="1" max="90" value={form.expiryDays} onChange={function(e) { setForm(Object.assign({}, form, { expiryDays: parseInt(e.target.value || '14', 10) })); }} />
            </div>
            <div className="admin-field">
              <label>Notes (internal)</label>
              <textarea rows={2} value={form.notes} onChange={function(e) { setForm(Object.assign({}, form, { notes: e.target.value })); }}
                placeholder="Context for the sales team"
                style={{ padding: '10px 12px', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, fontFamily: 'inherit' }} />
            </div>
            <div className="admin-modal-hint">On save, you'll get a consent link to send the prospect. Email delivery is deferred — copy & send via your preferred secure channel.</div>
          </div>
          <div className="admin-modal-footer">
            <button type="button" className="admin-btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="admin-btn primary" disabled={saving || !form.provider}>{saving ? 'Creating…' : 'Create assessment'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ConsentLinkReveal({ data, onClose }) {
  var [copied, setCopied] = useState(false);
  function copy() {
    try { navigator.clipboard.writeText(data.url).then(function() { setCopied(true); setTimeout(function() { setCopied(false); }, 2000); }); } catch (e) {}
  }
  return (
    <div className="admin-modal-overlay">
      <div className="admin-modal" style={{ maxWidth: 560 }}>
        <div className="admin-modal-header" style={{ background: 'linear-gradient(135deg, rgba(91,158,244,0.15), rgba(120,80,255,0.15))' }}>Consent link created</div>
        <div className="admin-modal-body">
          <div style={{ fontSize: 13 }}>Send this URL to <strong>{data.name}</strong> via your preferred channel. The link is token-authenticated and expires automatically.</div>
          <div className="admin-pw-display"><code>{data.url}</code><button type="button" className="admin-btn sm primary" onClick={copy}>{copied ? 'Copied ✓' : 'Copy'}</button></div>
          <div className="admin-field-hint">Remind them: a <strong>{data.provider === 'microsoft' ? 'Global Admin' : 'Super Admin'}</strong> must be the one to click it. Standard user accounts cannot consent to the read-only scopes we request.</div>
        </div>
        <div className="admin-modal-footer">
          <button type="button" className="admin-btn primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

function AssessmentDetailDrawer({ id, onClose, onDownload }) {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  useEffect(function() {
    authFetch(API + '/admin/assessments/' + id)
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) { setData(d); setLoading(false); })
      .catch(function() { setLoading(false); });
  }, [id]);

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={function(e) { e.stopPropagation(); }} style={{ maxWidth: 720 }}>
        <div className="admin-modal-header">Assessment detail</div>
        <div className="admin-modal-body" style={{ maxHeight: '70vh', overflow: 'auto' }}>
          {loading && <div style={{ padding: 24, textAlign: 'center', opacity: 0.5 }}>Loading…</div>}
          {!loading && data && (
            <>
              <div className="admin-advanced">
                <div><strong>{data.assessment.prospect_name}</strong> — {data.assessment.prospect_email}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {data.assessment.provider === 'microsoft' ? 'Microsoft 365' : 'Google Workspace'}
                  {data.assessment.tenant_domain ? ' · ' + data.assessment.tenant_domain : ''}
                  {' · status '}<strong>{data.assessment.status}</strong>
                </div>
                {data.assessment.notes && <div style={{ fontSize: 12, marginTop: 6, fontStyle: 'italic' }}>{data.assessment.notes}</div>}
                {data.assessment.error_message && <div style={{ fontSize: 12, marginTop: 6, color: '#f87171' }}>Error: {data.assessment.error_message}</div>}
              </div>

              {data.consentUrl && (
                <div className="admin-advanced" style={{ marginTop: 14 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Consent link (pending)</div>
                  <div className="admin-pw-display"><code style={{ fontSize: 11 }}>{data.consentUrl}</code>
                    <button className="admin-btn sm primary" onClick={function() { navigator.clipboard && navigator.clipboard.writeText(data.consentUrl); }}>Copy</button>
                  </div>
                </div>
              )}

              {data.complexity && (
                <div className="admin-advanced" style={{ marginTop: 14 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Complexity — {data.complexity.label} (Tier {data.complexity.tier})</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
                    {data.complexity.points} points · est. {data.complexity.estimatedHours} hours · {data.complexity.flags.length} flag(s)
                  </div>
                  {data.complexity.flags.map(function(f, i) {
                    return (
                      <div key={i} style={{ fontSize: 12, marginBottom: 4 }}>
                        <span className="admin-chip warn-soft" style={{ marginRight: 6 }}>+{f.points}</span>
                        {f.blurb}
                      </div>
                    );
                  })}
                </div>
              )}

              {data.assessment.inventory && (
                <div className="admin-advanced" style={{ marginTop: 14 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Inventory</div>
                  <pre style={{ fontSize: 11, fontFamily: 'JetBrains Mono, monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: 'var(--bg-base)', padding: 10, borderRadius: 6, maxHeight: 260, overflow: 'auto' }}>
                    {JSON.stringify(data.assessment.inventory, null, 2)}
                  </pre>
                </div>
              )}

              <div className="admin-advanced" style={{ marginTop: 14 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Timeline</div>
                {data.events.length === 0 && <div style={{ fontSize: 12, opacity: 0.5 }}>No events recorded.</div>}
                {data.events.map(function(ev) {
                  return (
                    <div key={ev.id} style={{ fontSize: 11, marginBottom: 4, display: 'flex', gap: 10 }}>
                      <span style={{ color: 'var(--text-muted)', fontFamily: 'JetBrains Mono, monospace' }}>{fmtDateTime(ev.created_at)}</span>
                      <span style={{ fontWeight: 600 }}>{ev.event_type}</span>
                      {ev.metadata && <span style={{ opacity: 0.7 }}>{JSON.stringify(ev.metadata).slice(0, 80)}</span>}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
        <div className="admin-modal-footer">
          {data && data.assessment.status === 'completed' && <button className="admin-btn primary" onClick={function() { onDownload(data.assessment); }}>Download PDF</button>}
          <button className="admin-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Admin Automation tab ────────────────────────────────────────────
// Tier-1 helpdesk playbook execution against consented client tenants.
// All routes are admin-gated; destructive playbooks require step-up
// re-auth and a second admin's approval.

function AdminAutomationTab({ clientList }) {
  var [section, setSection] = useState('run');  // 'run' | 'consents' | 'executions' | 'approvals'
  var [toast, setToast] = useState(null);
  function showToast(msg, type) { setToast({ msg: msg, type: type || 'info' }); setTimeout(function() { setToast(null); }, 3500); }

  // Process consent-callback redirect query string once
  useEffect(function() {
    var p = new URLSearchParams(window.location.search);
    if (p.get('automation_consent') === 'success') {
      showToast('Consent granted for ' + (p.get('client') || 'client'), 'ok');
      window.history.replaceState({}, '', window.location.pathname);
      setSection('consents');
    } else if (p.get('automation_error')) {
      showToast('Consent failed: ' + p.get('automation_error'), 'error');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  return (
    <div className="admin-automation-tab">
      <div className="admin-toolbar">
        <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          Tier-1 automation. Run predefined playbooks (offboarding, onboarding, etc.) against client tenants you've been consented to. Destructive actions require step-up re-auth and a second admin's approval.
        </div>
        <div style={{ flex: 1 }} />
        <div className="admin-tabs" style={{ borderBottom: 'none', margin: 0 }}>
          <button className={'admin-tab ' + (section === 'run' ? 'active' : '')} onClick={function() { setSection('run'); }}>Run a playbook</button>
          <button className={'admin-tab ' + (section === 'approvals' ? 'active' : '')} onClick={function() { setSection('approvals'); }}>Approvals</button>
          <button className={'admin-tab ' + (section === 'executions' ? 'active' : '')} onClick={function() { setSection('executions'); }}>Executions</button>
          <button className={'admin-tab ' + (section === 'consents' ? 'active' : '')} onClick={function() { setSection('consents'); }}>Consents</button>
        </div>
      </div>

      {section === 'run'        && <AutomationRunSection clientList={clientList} showToast={showToast} />}
      {section === 'consents'   && <AutomationConsentsSection clientList={clientList} showToast={showToast} />}
      {section === 'executions' && <AutomationExecutionsSection />}
      {section === 'approvals'  && <AutomationApprovalsSection showToast={showToast} />}
      {toast && <AdminToast {...toast} />}
    </div>
  );
}

function AutomationConsentsSection({ clientList, showToast }) {
  var [consents, setConsents] = useState([]);
  var [loading, setLoading] = useState(true);
  var [picking, setPicking] = useState(false);
  var [pickedClient, setPickedClient] = useState('');

  function fetchConsents() {
    setLoading(true);
    authFetch(API + '/admin/automation/consents')
      .then(function(r) { return r.ok ? r.json() : []; })
      .then(function(d) { setConsents(d); setLoading(false); })
      .catch(function() { setLoading(false); });
  }
  useEffect(fetchConsents, []);

  function startConsent() {
    if (!pickedClient) { showToast('Pick a client first', 'error'); return; }
    authFetch(API + '/admin/automation/consents/start', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clientId: pickedClient })
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        if (!x.ok) { showToast(x.d.error || 'Consent start failed', 'error'); return; }
        // Open Microsoft consent in a new window to preserve our admin session
        window.open(x.d.consentUrl, '_blank', 'noopener,noreferrer,width=720,height=820');
        setPicking(false); setPickedClient('');
      });
  }

  function setKillSwitch(c, on) {
    if (on && !window.confirm('Kill-switch ' + c.client_id + '? All automation against this tenant will be blocked until cleared.')) return;
    authFetch(API + '/admin/automation/consents/' + c.id, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kill_switched: on })
    }).then(function(r) { if (r.ok) fetchConsents(); else showToast('Failed', 'error'); });
  }
  function setEnabled(c, on) {
    authFetch(API + '/admin/automation/consents/' + c.id, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: on })
    }).then(function(r) { if (r.ok) fetchConsents(); else showToast('Failed', 'error'); });
  }
  function revoke(c) {
    if (!window.confirm('REVOKE consent for ' + c.client_id + '?\n\nThis drops all stored admin tokens for this client. Future automation requires re-consent. Proceed?')) return;
    authFetch(API + '/admin/automation/consents/' + c.id, { method: 'DELETE' })
      .then(function(r) { if (r.ok) { showToast('Revoked', 'ok'); fetchConsents(); } else showToast('Failed', 'error'); });
  }

  return (
    <div>
      <div className="admin-toolbar" style={{ marginTop: 14 }}>
        {!picking && <button className="admin-btn primary" onClick={function() { setPicking(true); }}>+ Request consent</button>}
        {picking && (
          <>
            <select value={pickedClient} onChange={function(e) { setPickedClient(e.target.value); }} className="admin-filter-select">
              <option value="">— Pick a client —</option>
              {(clientList || []).filter(function(c) { return c.id !== 'demo'; }).map(function(c) {
                return <option key={c.id} value={c.id}>{c.name}</option>;
              })}
            </select>
            <button className="admin-btn primary" onClick={startConsent}>Open Microsoft consent</button>
            <button className="admin-btn" onClick={function() { setPicking(false); setPickedClient(''); }}>Cancel</button>
          </>
        )}
        <div style={{ flex: 1 }} />
        <button className="admin-btn" onClick={fetchConsents} disabled={loading}>{loading ? 'Loading...' : 'Refresh'}</button>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Tenant</th>
              <th>Granted by</th>
              <th>State</th>
              <th>Consented</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="6" style={{ padding: 24, textAlign: 'center', opacity: 0.5 }}>Loading…</td></tr>}
            {!loading && consents.length === 0 && (
              <tr><td colSpan="6" style={{ padding: 36, textAlign: 'center' }}>
                <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--text-primary)' }}>No active consents</div>
                <div style={{ fontSize: 12, opacity: 0.65 }}>Request consent for a client to enable automation against their tenant.</div>
              </td></tr>
            )}
            {!loading && consents.map(function(c) {
              var state = c.revoked_at ? 'revoked' : c.kill_switched ? 'kill-switched' : !c.enabled ? 'disabled' : 'active';
              var stateColor = c.revoked_at ? 'bad' : c.kill_switched ? 'warn' : !c.enabled ? 'warn-soft' : 'ok';
              return (
                <tr key={c.id}>
                  <td><strong>{c.client_id}</strong></td>
                  <td style={{ fontSize: 12 }}>{c.entra_tenant_domain || c.entra_tenant_id}</td>
                  <td style={{ fontSize: 12 }}>{c.consenting_user || '—'}</td>
                  <td><span className={'admin-chip ' + stateColor}>{state}</span></td>
                  <td style={{ fontSize: 12 }}>{fmtDateTime(c.consented_at)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <div className="admin-row-actions">
                      {c.kill_switched
                        ? <button className="admin-btn sm ok" onClick={function() { setKillSwitch(c, false); }}>Clear kill-switch</button>
                        : <button className="admin-btn sm warn" onClick={function() { setKillSwitch(c, true); }}>Kill-switch</button>}
                      {c.enabled
                        ? <button className="admin-btn sm" onClick={function() { setEnabled(c, false); }}>Disable</button>
                        : <button className="admin-btn sm ok" onClick={function() { setEnabled(c, true); }}>Enable</button>}
                      <button className="admin-btn sm danger" onClick={function() { revoke(c); }}>Revoke</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AutomationRunSection({ clientList, showToast }) {
  var [playbooks, setPlaybooks] = useState([]);
  var [pickedPlaybook, setPickedPlaybook] = useState('');
  var [pickedClient, setPickedClient] = useState('');
  var [userQuery, setUserQuery] = useState('');
  var [matches, setMatches] = useState(null);
  var [target, setTarget] = useState(null);
  var [forwardTo, setForwardTo] = useState('');
  var [plan, setPlan] = useState(null);
  var [previewSteps, setPreviewSteps] = useState(null);   // step list for the checklist
  var [enabledSteps, setEnabledSteps] = useState({});      // { stepId: true }
  var [lookingUp, setLookingUp] = useState(false);
  var [planning, setPlanning] = useState(false);
  var [executing, setExecuting] = useState(false);
  var [stepUpFresh, setStepUpFresh] = useState(false);

  useEffect(function() {
    authFetch(API + '/admin/automation/playbooks')
      .then(function(r) { return r.ok ? r.json() : []; })
      .then(function(d) { setPlaybooks(d); if (d.length && !pickedPlaybook) setPickedPlaybook(d[0].name); });
  }, []);

  // When the target changes, fetch a preview plan so the admin can
  // choose which non-essential steps to include before running anything.
  useEffect(function() {
    if (!target || !pickedPlaybook || !pickedClient) { setPreviewSteps(null); return; }
    authFetch(API + '/admin/automation/plan', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        playbook: pickedPlaybook,
        clientId: pickedClient,
        target: { userId: target.id, email: target.userPrincipalName, displayName: target.displayName },
        params: forwardTo ? { forwardTo: forwardTo } : {}
      })
    })
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) {
        if (!d || !d.plan) return;
        setPreviewSteps(d.plan.plan);
        var initial = {};
        d.plan.plan.forEach(function(s) { initial[s.id] = true; }); // default: all enabled
        setEnabledSteps(initial);
      })
      .catch(function() {});
  }, [target, pickedPlaybook, pickedClient, forwardTo]);

  function toggleStep(id) {
    setEnabledSteps(function(prev) {
      var next = Object.assign({}, prev);
      next[id] = !next[id];
      return next;
    });
  }

  function lookupUser() {
    if (!pickedClient || !userQuery.trim()) return;
    setLookingUp(true);
    setMatches(null); setTarget(null);
    authFetch(API + '/admin/automation/lookup-user', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clientId: pickedClient, query: userQuery.trim() })
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        setLookingUp(false);
        if (!x.ok) { showToast(x.d.error || x.d.message || 'Lookup failed', 'error'); return; }
        setMatches(x.d.matches || []);
      })
      .catch(function() { setLookingUp(false); showToast('Lookup failed', 'error'); });
  }

  function buildPlan(mode) {
    if (!target) { showToast('Select a user first', 'error'); return; }
    setPlanning(mode === 'plan');
    setExecuting(mode === 'execute');
    var enabledStepsList = previewSteps ? previewSteps.filter(function(s) { return enabledSteps[s.id]; }).map(function(s) { return s.id; }) : null;
    authFetch(API + '/admin/automation/executions', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        playbook: pickedPlaybook,
        clientId: pickedClient,
        target: { userId: target.id, email: target.userPrincipalName, displayName: target.displayName },
        params: forwardTo ? { forwardTo: forwardTo } : {},
        enabledSteps: enabledStepsList,
        mode: mode === 'execute' ? 'execute' : 'dry_run'
      })
    })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, status: r.status, d: d }; }); })
      .then(function(x) {
        setPlanning(false); setExecuting(false);
        if (!x.ok) {
          if (x.d.error === 'STEP_UP_REQUIRED') {
            stepUp(function() { showToast('Step-up successful — try again', 'ok'); setStepUpFresh(true); });
            return;
          }
          showToast(x.d.error || 'Failed', 'error');
          return;
        }
        setPlan(x.d);
        var msg = mode === 'execute'
          ? (x.d.execution.status === 'awaiting_approval' ? 'Queued — awaiting second admin approval' : 'Execution started')
          : 'Dry run started — see Executions tab';
        showToast(msg, 'ok');
      })
      .catch(function() { setPlanning(false); setExecuting(false); showToast('Failed', 'error'); });
  }

  function stepUp(onSuccess) {
    if (!window.confirm('Re-authenticate to authorize this destructive action? Your step-up token is valid for 5 minutes.')) return;
    authFetch(API + '/admin/automation/stepup', { method: 'POST' })
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(x) {
        if (x.ok) { setStepUpFresh(true); if (onSuccess) onSuccess(); }
        else showToast(x.d.error || 'Step-up failed', 'error');
      });
  }

  var pb = playbooks.find(function(p) { return p.name === pickedPlaybook; });

  return (
    <div style={{ marginTop: 14 }}>
      <div className="admin-advanced">
        <h3 style={{ marginTop: 0, fontSize: 13, color: 'var(--text-primary)' }}>1. Pick a playbook + client</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="admin-field">
            <label>Playbook</label>
            <select value={pickedPlaybook} onChange={function(e) { setPickedPlaybook(e.target.value); setPlan(null); }}>
              {playbooks.map(function(p) { return <option key={p.name} value={p.name}>{p.displayName}</option>; })}
            </select>
            {pb && <div className="admin-field-hint">{pb.description}</div>}
            {pb && pb.requiresApproval && <span className="admin-chip warn" style={{ marginTop: 6, alignSelf: 'flex-start' }}>Requires second-admin approval</span>}
          </div>
          <div className="admin-field">
            <label>Client</label>
            <select value={pickedClient} onChange={function(e) { setPickedClient(e.target.value); setTarget(null); setMatches(null); setPlan(null); }}>
              <option value="">— Pick a consented client —</option>
              {(clientList || []).filter(function(c) { return c.id !== 'demo'; }).map(function(c) {
                return <option key={c.id} value={c.id}>{c.name}</option>;
              })}
            </select>
            <div className="admin-field-hint">Client must have granted Automation consent (see Consents tab).</div>
          </div>
        </div>
      </div>

      {pickedPlaybook === 'offboard-user' && pickedClient && (
        <div className="admin-advanced" style={{ marginTop: 12 }}>
          <h3 style={{ marginTop: 0, fontSize: 13, color: 'var(--text-primary)' }}>2. Find the user</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              className="admin-filter-input" style={{ flex: 1 }}
              placeholder="email, UPN, or display name"
              value={userQuery}
              onChange={function(e) { setUserQuery(e.target.value); }}
              onKeyDown={function(e) { if (e.key === 'Enter') lookupUser(); }}
            />
            <button className="admin-btn primary" onClick={lookupUser} disabled={!userQuery.trim() || lookingUp}>
              {lookingUp ? 'Looking up…' : 'Look up'}
            </button>
          </div>
          {matches && matches.length === 0 && <div className="admin-field-hint" style={{ marginTop: 8 }}>No match.</div>}
          {matches && matches.length > 0 && (
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {matches.map(function(u) {
                var selected = target && target.id === u.id;
                return (
                  <button key={u.id} className={'admin-btn ' + (selected ? 'primary' : '')} style={{ textAlign: 'left' }}
                    onClick={function() { setTarget(u); setPlan(null); }}>
                    <div style={{ fontWeight: 700 }}>{u.displayName}</div>
                    <div style={{ fontSize: 11, opacity: 0.8 }}>{u.userPrincipalName}{u.accountEnabled === false ? ' · disabled' : ''}{u.assignedLicenses && u.assignedLicenses.length ? ' · ' + u.assignedLicenses.length + ' license(s)' : ''}</div>
                  </button>
                );
              })}
            </div>
          )}

          {target && (
            <div className="admin-field" style={{ marginTop: 14 }}>
              <label>Forward mail to (optional)</label>
              <input type="email" placeholder="manager@client.com" value={forwardTo} onChange={function(e) { setForwardTo(e.target.value); }} />
            </div>
          )}
        </div>
      )}

      {target && previewSteps && (
        <div className="admin-advanced" style={{ marginTop: 12 }}>
          <h3 style={{ marginTop: 0, fontSize: 13, color: 'var(--text-primary)' }}>3. Pick the steps to run</h3>
          <div className="admin-field-hint" style={{ marginBottom: 10 }}>
            Essential snapshot steps always run (forensic data). Everything else is opt-in: uncheck what you don't need for this run. Useful when you're temporarily suspending vs fully offboarding, or when a client wants license-strip-only.
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <button type="button" className="admin-btn sm" onClick={function() {
              var all = {}; previewSteps.forEach(function(s) { all[s.id] = true; }); setEnabledSteps(all);
            }}>Select all</button>
            <button type="button" className="admin-btn sm" onClick={function() {
              var min = {}; previewSteps.forEach(function(s) { if (s.essential) min[s.id] = true; }); setEnabledSteps(min);
            }}>Essentials only</button>
          </div>
          {previewSteps.map(function(s, i) {
            var checked = !!enabledSteps[s.id];
            var locked = s.essential;
            return (
              <label key={s.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 10px', borderLeft: '3px solid ' + (s.essential ? '#16a34a' : s.destructive ? '#dc2626' : s.manual ? '#d97706' : '#5b9ef4'), marginBottom: 6, cursor: locked ? 'default' : 'pointer', opacity: locked || checked ? 1 : 0.55 }}>
                <input type="checkbox" checked={locked || checked} disabled={locked} onChange={function() { if (!locked) toggleStep(s.id); }} style={{ marginTop: 2 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    {i+1}. {s.label}
                    {s.essential && <span className="admin-chip ok" style={{ marginLeft: 8 }}>essential</span>}
                    {s.manual && <span className="admin-chip warn" style={{ marginLeft: 8 }}>manual</span>}
                    {s.destructive && <span className="admin-chip bad" style={{ marginLeft: 8 }}>destructive</span>}
                    {s.nonFatal && <span className="admin-chip warn-soft" style={{ marginLeft: 8 }}>non-fatal</span>}
                  </div>
                  <div style={{ fontSize: 11, opacity: 0.75, marginTop: 3 }}>{s.summary}</div>
                </div>
              </label>
            );
          })}
        </div>
      )}

      {target && (
        <div className="admin-advanced" style={{ marginTop: 12 }}>
          <h3 style={{ marginTop: 0, fontSize: 13, color: 'var(--text-primary)' }}>4. Run it</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="admin-btn primary" onClick={function() { buildPlan('plan'); }} disabled={planning}>
              {planning ? 'Running dry-run…' : 'Dry-run'}
            </button>
            {!stepUpFresh && pb && pb.category === 'destructive' && (
              <button className="admin-btn warn" onClick={function() { stepUp(); }}>Step-up re-auth</button>
            )}
            <button
              className="admin-btn danger"
              onClick={function() {
                var enabledNames = previewSteps
                  ? previewSteps.filter(function(s) { return enabledSteps[s.id] && !s.essential && !s.manual; }).map(function(s) { return s.label; })
                  : [];
                var summary = enabledNames.length === 0 ? 'snapshots only' : enabledNames.join(', ');
                if (!window.confirm('Execute "' + pb.displayName + '" against ' + target.displayName + '?\n\nWill run: ' + summary + '\n\nThis is a destructive action. ' + (pb.requiresApproval ? 'A second admin must approve before any Graph calls run.' : 'It will run immediately.'))) return;
                buildPlan('execute');
              }}
              disabled={executing || (pb && pb.category === 'destructive' && !stepUpFresh)}
              title={pb && pb.category === 'destructive' && !stepUpFresh ? 'Step-up re-auth required first' : ''}
            >
              {executing ? 'Submitting…' : 'Execute'}
            </button>
          </div>
          <div className="admin-field-hint" style={{ marginTop: 10 }}>
            Dry-run records the planned steps without making any Graph calls. Executions appear under the "Executions" tab.
            {pb && pb.requiresApproval && ' This playbook requires a second admin to approve before any actions run.'}
          </div>
        </div>
      )}
    </div>
  );
}

function AutomationExecutionsSection() {
  var [rows, setRows] = useState([]);
  var [loading, setLoading] = useState(true);
  var [detailId, setDetailId] = useState(null);
  function fetch_() {
    setLoading(true);
    authFetch(API + '/admin/automation/executions')
      .then(function(r) { return r.ok ? r.json() : []; })
      .then(function(d) { setRows(d); setLoading(false); })
      .catch(function() { setLoading(false); });
  }
  useEffect(fetch_, []);
  var statusClass = { queued: 'info', awaiting_approval: 'warn', running: 'info', completed: 'ok', failed: 'bad', cancelled: 'warn-soft', rolled_back: 'warn' };

  return (
    <div style={{ marginTop: 14 }}>
      <div className="admin-toolbar">
        <div style={{ flex: 1 }} />
        <button className="admin-btn" onClick={fetch_} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
      </div>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Requested</th>
              <th>Playbook</th>
              <th>Client</th>
              <th>Mode</th>
              <th>Status</th>
              <th>Target</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="6" style={{ padding: 24, textAlign: 'center', opacity: 0.5 }}>Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan="6" style={{ padding: 24, textAlign: 'center', opacity: 0.5 }}>No executions yet.</td></tr>}
            {!loading && rows.map(function(r) {
              var t = r.target || {};
              return (
                <tr key={r.id} style={{ cursor: 'pointer' }} onClick={function() { setDetailId(r.id); }}>
                  <td style={{ fontSize: 12 }}>{fmtDateTime(r.requested_at)}</td>
                  <td>{r.playbook}</td>
                  <td>{r.client_id}</td>
                  <td><span className="admin-chip info">{r.mode}</span></td>
                  <td><span className={'admin-chip ' + (statusClass[r.status] || 'info')}>{r.status.replace('_', ' ')}</span></td>
                  <td style={{ fontSize: 12 }}>{t.email || t.displayName || t.userId || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {detailId && <AutomationExecutionDetail id={detailId} onClose={function() { setDetailId(null); fetch_(); }} />}
    </div>
  );
}

function AutomationExecutionDetail({ id, onClose }) {
  var [data, setData] = useState(null);
  useEffect(function() {
    var cancelled = false;
    var timer = null;
    function load() {
      authFetch(API + '/admin/automation/executions/' + id)
        .then(function(r) { return r.ok ? r.json() : null; })
        .then(function(d) {
          if (cancelled) return;
          setData(d);
          var st = d && d.execution && d.execution.status;
          if (st === 'queued' || st === 'running' || st === 'awaiting_approval') timer = setTimeout(load, 1500);
        })
        .catch(function() {});
    }
    load();
    return function() { cancelled = true; if (timer) clearTimeout(timer); };
  }, [id]);

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={function(e) { e.stopPropagation(); }} style={{ maxWidth: 760 }}>
        <div className="admin-modal-header">Execution detail</div>
        <div className="admin-modal-body" style={{ maxHeight: '70vh', overflow: 'auto' }}>
          {!data && <div style={{ padding: 24, textAlign: 'center', opacity: 0.5 }}>Loading…</div>}
          {data && (
            <>
              <div className="admin-advanced">
                <div><strong>{data.execution.playbook}</strong> · {data.execution.mode}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{data.execution.client_id} · status <strong>{data.execution.status}</strong></div>
                <div style={{ fontSize: 11, marginTop: 6 }}><strong>Target:</strong> {JSON.stringify(data.execution.target)}</div>
                {data.execution.params && <div style={{ fontSize: 11 }}><strong>Params:</strong> {JSON.stringify(data.execution.params)}</div>}
                {data.execution.error_message && <div style={{ fontSize: 12, marginTop: 8, color: '#f87171' }}>Error: {data.execution.error_message}</div>}
              </div>
              <div className="admin-advanced" style={{ marginTop: 14 }}>
                {(function() {
                  var steps = data.steps || [];
                  var total = steps.length;
                  var done = steps.filter(function(s) { return s.status === 'succeeded' || s.status === 'skipped' || s.status === 'failed'; }).length;
                  var pct = total ? Math.round(done / total * 100) : 0;
                  var st = data.execution.status;
                  var active = st === 'queued' || st === 'running' || st === 'awaiting_approval';
                  var compact = function(o) { try { return JSON.stringify(o).replace(/"/g, '').slice(0, 90); } catch (e) { return ''; } };
                  return (
                    <>
                      <div className="exec-theater-head">
                        <span className="exec-theater-title">{active ? (st === 'awaiting_approval' ? 'Awaiting approval' : 'Executing…') : 'Run complete'}</span>
                        <span className="exec-theater-count">{done}/{total} steps</span>
                      </div>
                      <div className="exec-progress"><div className={'exec-progress-fill' + (active ? ' active' : '') + (st === 'failed' ? ' failed' : '')} style={{ width: pct + '%' }} /></div>
                      {total === 0 && <div className="exec-waiting"><span className="agent-spinner" /> {active ? 'waiting for steps…' : 'no step records'}</div>}
                      {steps.map(function(s, i) {
                        var running = s.status === 'running';
                        var chip = s.status === 'succeeded' ? 'ok' : s.status === 'failed' ? 'bad' : s.status === 'skipped' ? 'warn-soft' : 'info';
                        return (
                          <div key={s.id} className={'exec-step exec-step-' + s.status + (running ? ' running' : '')} style={{ animationDelay: (i * 45) + 'ms' }}>
                            <span className="exec-step-seq">{s.seq}</span>
                            <div className="exec-step-main">
                              <div className="exec-step-label">{s.step_label} <span className={'admin-chip ' + chip}>{s.status}</span></div>
                              {s.error_message && <div className="exec-step-err">{s.error_message}</div>}
                              {(s.before_state || s.after_state) && (
                                <div className="exec-step-ba">
                                  {s.before_state && <code className="exec-ba before">{compact(s.before_state)}</code>}
                                  {s.before_state && s.after_state && <span className="exec-ba-arrow">→</span>}
                                  {s.after_state && <code className="exec-ba after">{compact(s.after_state)}</code>}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </>
                  );
                })()}
              </div>
            </>
          )}
        </div>
        <div className="admin-modal-footer">
          <button className="admin-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function AutomationApprovalsSection({ showToast }) {
  var [rows, setRows] = useState([]);
  var [loading, setLoading] = useState(true);
  var [stepUpFresh, setStepUpFresh] = useState(false);

  function fetch_() {
    setLoading(true);
    authFetch(API + '/admin/automation/executions?status=awaiting_approval')
      .then(function(r) { return r.ok ? r.json() : []; })
      .then(function(d) { setRows(d); setLoading(false); });
  }
  useEffect(fetch_, []);

  function stepUp() {
    if (!window.confirm('Re-authenticate to approve a destructive execution?')) return;
    authFetch(API + '/admin/automation/stepup', { method: 'POST' })
      .then(function(r) { if (r.ok) { setStepUpFresh(true); showToast('Step-up successful', 'ok'); } else showToast('Step-up failed', 'error'); });
  }
  function approve(r) {
    if (!stepUpFresh) { stepUp(); return; }
    authFetch(API + '/admin/automation/executions/' + r.id + '/approve', { method: 'POST' })
      .then(function(resp) { return resp.json().then(function(d) { return { ok: resp.ok, d: d }; }); })
      .then(function(x) {
        if (!x.ok) {
          if (x.d.error === 'STEP_UP_REQUIRED') { setStepUpFresh(false); stepUp(); return; }
          showToast(x.d.error || 'Approve failed', 'error'); return;
        }
        showToast('Approved — execution running', 'ok');
        fetch_();
      });
  }
  function cancel(r) {
    if (!window.confirm('Cancel this pending execution?')) return;
    authFetch(API + '/admin/automation/executions/' + r.id + '/cancel', { method: 'POST' })
      .then(function(resp) { if (resp.ok) { showToast('Cancelled', 'ok'); fetch_(); } else showToast('Cancel failed', 'error'); });
  }

  return (
    <div style={{ marginTop: 14 }}>
      <div className="admin-toolbar">
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Executions awaiting a second-admin approval. You cannot approve your own request.</div>
        <div style={{ flex: 1 }} />
        {!stepUpFresh && <button className="admin-btn warn" onClick={stepUp}>Step-up re-auth</button>}
        <button className="admin-btn" onClick={fetch_} disabled={loading}>{loading ? '…' : 'Refresh'}</button>
      </div>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr><th>Requested</th><th>Playbook</th><th>Client</th><th>Target</th><th>By</th><th style={{ textAlign: 'right' }}>Actions</th></tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="6" style={{ padding: 24, textAlign: 'center', opacity: 0.5 }}>Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan="6" style={{ padding: 24, textAlign: 'center', opacity: 0.5 }}>No pending approvals.</td></tr>}
            {!loading && rows.map(function(r) {
              var t = r.target || {};
              return (
                <tr key={r.id}>
                  <td style={{ fontSize: 12 }}>{fmtDateTime(r.requested_at)}</td>
                  <td>{r.playbook}</td>
                  <td>{r.client_id}</td>
                  <td style={{ fontSize: 12 }}>{t.email || t.displayName || t.userId}</td>
                  <td style={{ fontSize: 12 }}>admin#{r.requested_by}</td>
                  <td style={{ textAlign: 'right' }}>
                    <div className="admin-row-actions">
                      <button className="admin-btn sm primary" onClick={function() { approve(r); }} disabled={!stepUpFresh}>Approve</button>
                      <button className="admin-btn sm" onClick={function() { cancel(r); }}>Cancel</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Prospect-facing tenant assessment landing page ──────────────────
// Rendered for /assess/<token> and /assess/error — no authentication
// required. Prospect clicks "Grant access" → backend redirects to their
// IdP → callback → they come back here with ?done=1 or ?error=...
function AssessmentLandingPage({ token }) {
  var [state, setState] = useState({ loading: true, data: null, error: null });
  var [params] = useState(new URLSearchParams(window.location.search));
  var done = params.get('done') === '1';
  var errorParam = params.get('error');

  useEffect(function() {
    if (!token) { setState({ loading: false, data: null, error: 'missing_token' }); return; }
    fetch(API + '/assess/by-token/' + encodeURIComponent(token))
      .then(function(r) { return r.json().then(function(d) { return { ok: r.ok, status: r.status, d: d }; }); })
      .then(function(x) {
        if (x.ok) setState({ loading: false, data: x.d, error: null });
        else setState({ loading: false, data: null, error: x.d && x.d.error ? x.d.error : ('http_' + x.status) });
      })
      .catch(function() { setState({ loading: false, data: null, error: 'network' }); });
  }, [token]);

  // Polling: if in_progress, refresh status every 20s
  useEffect(function() {
    if (!state.data || state.data.status !== 'in_progress') return;
    var iv = setInterval(function() {
      fetch(API + '/assess/by-token/' + encodeURIComponent(token))
        .then(function(r) { return r.ok ? r.json() : null; })
        .then(function(d) { if (d) setState(function(s) { return { ...s, data: d }; }); })
        .catch(function() {});
    }, 20000);
    return function() { clearInterval(iv); };
  }, [token, state.data && state.data.status]);

  useEffect(function() {
    document.documentElement.setAttribute('data-theme', 'light');
  }, []);

  // Error-page route (/assess/error or failed lookup)
  if (state.error || window.location.pathname === '/assess/error') {
    var reasonMap = {
      invalid: 'This link is invalid or has been revoked.',
      expired: 'This assessment link has expired.',
      provider_disabled: 'The assessment provider is temporarily unavailable.',
      state_invalid: 'Your session timed out during sign-in. Please open the link again.',
      unknown_provider: 'Unknown provider. Contact CloudByte Group.',
      consent_rejected: 'Consent was not granted.',
      start_failed: 'Something went wrong starting the flow. Please try again.',
      exchange_failed: 'Microsoft rejected the consent exchange. Please try again or contact CloudByte Group.',
      network: 'Could not reach the server. Please try again.',
      missing_token: 'No assessment token in the URL.',
      http_404: 'This link is not recognized.',
      http_410: 'This link has been revoked or expired.'
    };
    var reason = params.get('reason') || state.error || 'unknown';
    return (
      <AssessmentShell title="We hit a snag" variant="error">
        <p>{reasonMap[reason] || ('Error: ' + reason)}</p>
        <p className="assess-muted">If you believe this is a mistake, reach out to the CloudByte Group contact who sent you this link.</p>
      </AssessmentShell>
    );
  }

  if (state.loading) {
    return <AssessmentShell title="Loading…"><div className="assess-spinner" /></AssessmentShell>;
  }

  var d = state.data;

  // Success state — OAuth just completed
  if (done || d.status === 'in_progress' || d.status === 'completed') {
    var inProgress = d.status === 'in_progress' || done;
    return (
      <AssessmentShell title={inProgress ? 'Thank you — we\'re on it' : 'Assessment complete'} variant="success">
        <p>
          {inProgress
            ? 'CloudByte Group is now collecting read-only telemetry from your tenant. This typically takes 10–30 minutes depending on tenant size.'
            : 'The assessment has completed. CloudByte Group will reach out with the readiness report shortly.'}
        </p>
        <div className="assess-metadata">
          <div><span>Tenant</span><strong>{d.tenantDomain || d.prospectName}</strong></div>
          <div><span>Provider</span><strong>{d.providerDisplay}</strong></div>
          <div><span>Status</span><strong>{d.status.replace('_', ' ')}</strong></div>
        </div>
        <p className="assess-muted">You can close this tab. A CloudByte Group representative will be in touch.</p>
      </AssessmentShell>
    );
  }

  if (d.status === 'failed' || errorParam) {
    return (
      <AssessmentShell title="Assessment didn't complete" variant="error">
        <p>Something prevented the read-only inventory from completing. This usually happens when the signed-in account lacked the admin role required to read the tenant.</p>
        <div className="assess-metadata">
          <div><span>Tenant</span><strong>{d.prospectName}</strong></div>
          <div><span>Provider</span><strong>{d.providerDisplay}</strong></div>
        </div>
        <p className="assess-muted">Contact CloudByte Group to reissue a link. We can retry the flow from a Global Admin / Super Admin account.</p>
      </AssessmentShell>
    );
  }

  if (d.status === 'expired') {
    return (
      <AssessmentShell title="This link has expired" variant="error">
        <p>For security, assessment links expire after a fixed window. Reach out to your CloudByte Group contact for a fresh link.</p>
      </AssessmentShell>
    );
  }

  // pending_consent — the main consent UI
  var startUrl = API + '/assess/' + encodeURIComponent(token) + '/start';
  var scopesByProvider = {
    microsoft: [
      'Read-only user directory (names, accounts, license assignment, sign-in activity)',
      'Read-only group, Teams, and SharePoint site lists',
      'Read-only usage reports (mailbox, OneDrive, and site storage in aggregate)',
      'Read-only privileged role assignments'
    ],
    google: [
      'Read-only Workspace user directory',
      'Read-only group and shared drive lists',
      'Read-only usage reports (Gmail and Drive storage in aggregate)',
      'Read-only admin activity reports'
    ]
  };
  var scopes = scopesByProvider[d.provider] || [];

  return (
    <AssessmentShell title="Tenant Assessment for CloudByte Group" variant="default">
      <p>
        <strong>{d.prospectName}</strong>, CloudByte Group has prepared a read-only assessment of your{' '}
        <strong>{d.providerDisplay}</strong> tenant. The result is a migration-readiness report
        covering user counts, storage, licensing, groups, and complexity indicators — used to
        scope and price a potential migration engagement.
      </p>
      <div className="assess-section-heading">What we will read</div>
      <ul className="assess-scope-list">
        {scopes.map(function(s, i) { return <li key={i}>{s}</li>; })}
      </ul>
      <div className="assess-section-heading">What we will NOT read</div>
      <ul className="assess-scope-list negative">
        <li>Email message bodies, attachments, or subject lines</li>
        <li>File or document contents in OneDrive, SharePoint, or Drive</li>
        <li>Chat messages in Teams or Google Chat</li>
        <li>Any personally-identifying record beyond display name and work email</li>
      </ul>
      <div className="assess-callout">
        <strong>Admin required.</strong> You will need to sign in as a{' '}
        {d.provider === 'microsoft' ? 'Microsoft 365 Global Admin (or equivalent consent-capable role)' : 'Google Workspace Super Admin'}
        . A standard user account cannot grant these scopes.
      </div>
      <a className="assess-cta" href={startUrl}>
        Grant read-only access with {d.providerDisplay}
      </a>
      <p className="assess-muted">
        Link expires {d.expiresAt ? new Date(d.expiresAt).toLocaleDateString('en-US', { dateStyle: 'long' }) : 'in 14 days'}.
        Access token is short-lived and refresh tokens are not stored.
      </p>
    </AssessmentShell>
  );
}

function AssessmentShell({ title, children, variant }) {
  return (
    <div className={'assess-page' + (variant ? ' ' + variant : '')}>
      <div className="assess-card">
        <div className="assess-brand">
          <img src="/logo.png" alt="CloudByte Group" />
          <div>
            <div className="assess-brand-name">CLOUDBYTE GROUP</div>
            <div className="assess-brand-sub">Aurelius Tenant Assessment</div>
          </div>
        </div>
        <h1 className="assess-title">{title}</h1>
        <div className="assess-body">{children}</div>
        <div className="assess-footer">
          Questions? Reach your CloudByte Group contact, or email <a href="mailto:info@cloudbytegroup.com">info@cloudbytegroup.com</a>.
        </div>
      </div>
    </div>
  );
}

// ── User badge in header ─────────────────────────────────────────────
// Shows the currently signed-in user (avatar + display name + role chip).
// Click → dropdown with full email + Sign out. Replaces the bare Logout
// button so it's always obvious which account this session belongs to.
function UserBadge({ onLogout }) {
  var [open, setOpen] = useState(false);
  var role = sessionStorage.getItem('aurelius_role') || '';
  var clientId = sessionStorage.getItem('aurelius_clientId') || '';
  var email = sessionStorage.getItem('aurelius_email') || '';
  var displayName = sessionStorage.getItem('aurelius_displayName') || email || (clientId ? (clientId === 'demo' ? 'Demo' : clientId) : 'Signed in');

  // Compute initials from displayName or email
  var initials = (function() {
    var src = displayName || email;
    if (!src) return '?';
    var parts = src.split(/[\s.@-]+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return src.slice(0, 2).toUpperCase();
  })();

  var roleLabel = role === 'admin' ? 'Admin'
    : clientId === 'demo' ? 'Demo'
    : role === 'client' ? 'Client'
    : '';

  // Close dropdown on outside click
  useEffect(function() {
    if (!open) return;
    function onDoc(e) {
      if (!e.target.closest || !e.target.closest('.user-badge-wrap')) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return function() { document.removeEventListener('mousedown', onDoc); };
  }, [open]);

  return (
    <div className="user-badge-wrap" style={{ position: 'relative' }}>
      <button
        type="button"
        className="user-badge"
        onClick={function() { setOpen(!open); }}
        title={email + (roleLabel ? ' · ' + roleLabel : '')}
      >
        <span className="user-badge-avatar">{initials}</span>
        <span className="user-badge-text">
          <span className="user-badge-name">{displayName}</span>
          {roleLabel && <span className={'user-badge-role ' + role}>{roleLabel}</span>}
        </span>
        <span className="user-badge-caret">{open ? '▴' : '▾'}</span>
      </button>
      {open && (
        <div className="user-badge-menu" role="menu">
          <div className="user-badge-menu-header">
            <div className="user-badge-menu-name">{displayName}</div>
            {email && <div className="user-badge-menu-email">{email}</div>}
            <div className="user-badge-menu-meta">
              {roleLabel && <span className={'user-badge-role ' + role}>{roleLabel}</span>}
              {clientId && clientId !== 'demo' && <span className="user-badge-menu-client">· {clientId}</span>}
            </div>
          </div>
          <button type="button" className="user-badge-menu-item logout" onClick={function() { setOpen(false); onLogout(); }}>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────
function App() {
  // Top-level router: /assess/* → prospect landing, everything else → MSP app
  if (IS_ASSESS_VIEW) return <AssessmentLandingPage token={URL_ASSESS_TOKEN} />;
  return <AureliusApp />;
}

function AureliusApp() {
  const [clientList, setClientList] = useState([]);
  const [activeClient, setActiveClient] = useState(null);
  const [clientData, setClientData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  // Restricted users land on the first tab in their allowlist instead of
  // the default 'overview' (which they may not have access to).
  const [activeTab, setActiveTab] = useState(function() {
    var rc = getRestrictedClaims();
    if (rc.allowedTabs && rc.allowedTabs.length > 0 && rc.allowedTabs.indexOf('overview') === -1) {
      return rc.allowedTabs[0];
    }
    return 'overview';
  });
  const [clientNotFound, setClientNotFound] = useState(false);
  const [authenticated, setAuthenticated] = useState(!!getToken());
  const [coverageOpen, setCoverageOpen] = useState(false);
  const [theme, setTheme] = useState(sessionStorage.getItem('aurelius_theme') || 'dark');
  const [badgeData, setBadgeData] = useState({});
  const [qbrOpen, setQbrOpen] = useState(false);
  const [qbrReport, setQbrReport] = useState(null);
  const [qbrLoading, setQbrLoading] = useState(false);
  const [adminMode, setAdminMode] = useState(false);

  // Listener for "open admin Integrations for this client" intent
  // dispatched by gotoAdminIntegrationsForClient() helper. Flips into
  // admin mode in-app. Only honored for admin-role users (the
  // dispatching button is also admin-gated, but defense-in-depth).
  useEffect(function() {
    function handleGoto(e) {
      if (!isAdminRole()) return;
      window.__aurelius_admin_handled_intent = true;
      setAdminMode(true);
      // The intent payload is already in sessionStorage; AdminIntegrationsTab
      // pops it on mount and opens the right edit modal.
    }
    window.addEventListener('aurelius:goto-admin-integrations', handleGoto);
    return function() { window.removeEventListener('aurelius:goto-admin-integrations', handleGoto); };
  }, []);

  const [navCollapsed, setNavCollapsed] = useState(function() {
    try { return localStorage.getItem('aurelius_nav_collapsed') === '1'; } catch (e) { return false; }
  });
  const [insightsOpen, setInsightsOpen] = useState(false);
  function toggleNavCollapsed() {
    setNavCollapsed(function(prev) {
      var next = !prev;
      try { localStorage.setItem('aurelius_nav_collapsed', next ? '1' : '0'); } catch (e) {}
      return next;
    });
  }
  const isAdmin = sessionStorage.getItem('aurelius_role') === 'admin';

  var generateQBR = async function() {
    if (!activeClient) return;
    setQbrOpen(true);
    setQbrLoading(true);
    setQbrReport(null);
    try {
      var resp = await authFetch(API + '/qbr', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ clientId: activeClient }) });
      if (resp.ok) { var data = await resp.json(); setQbrReport(data.report); }
    } catch (e) { console.warn('QBR generation failed:', e); }
    setQbrLoading(false);
  };

  useEffect(function() {
    document.documentElement.setAttribute('data-theme', theme);
    sessionStorage.setItem('aurelius_theme', theme);
  }, [theme]);

  // Idle timeout: auto-logout after 15 minutes with 60s warning (NIST 800-171 3.1.10)
  var [idleWarning, setIdleWarning] = useState(false);
  var [changelogOpen, setChangelogOpen] = useState(false);
  var [mspVerifyOpen, setMspVerifyOpen] = useState(false);
  var [changelogNew, setChangelogNew] = useState(false);

  // AI Proposals drawer + count poller (Option B — TopBar surface).
  // Replaces the embedded proposals card on Overview/Identity. Card
  // still renders inside the Compliance tab as a contextual home for
  // CMMC operators reviewing controls + proposals together.
  var [proposalsOpen, setProposalsOpen] = useState(false);
  var [pendingProposalsCount, setPendingProposalsCount] = useState(0);

  // Check if there are new changelog entries
  useEffect(function() {
    if (!authenticated) return;
    authFetch(API + '/changelog').then(function(r) { return r.json(); }).then(function(data) {
      if (data[0]) {
        var seen = sessionStorage.getItem('aurelius_changelog_seen');
        if (seen !== data[0].version) setChangelogNew(true);
      }
    }).catch(function() {});
  }, [authenticated]);

  // Pending-proposals count poller. Runs only for admins on a real
  // (non-demo) client. 60s cadence is generous — auto-drift fires at
  // most every 15 min from the continuous evaluator. The drawer's own
  // 30s poller takes over when it's open.
  function refreshProposalsCount() {
    if (!activeClient || activeClient === 'demo' || !isAdminRole()) {
      setPendingProposalsCount(0);
      return;
    }
    authFetch(API + '/clients/' + activeClient + '/cmmc/proposals')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) { if (d) setPendingProposalsCount((d.proposals || []).length); })
      .catch(function() {});
  }
  useEffect(function() {
    if (!authenticated) return;
    refreshProposalsCount();
    var iv = setInterval(refreshProposalsCount, 60000);
    return function() { clearInterval(iv); };
  }, [authenticated, activeClient]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(function() {
    if (!authenticated) return;
    var IDLE_TIMEOUT = 14 * 60 * 1000;
    var WARNING_DURATION = 60 * 1000;
    var idleTimer = null;
    var logoutTimer = null;
    function resetTimer() {
      if (idleTimer) clearTimeout(idleTimer);
      if (logoutTimer) clearTimeout(logoutTimer);
      setIdleWarning(false);
      idleTimer = setTimeout(function() {
        setIdleWarning(true);
        logoutTimer = setTimeout(function() {
          var token = getToken();
          if (token) fetch(API + '/logout', { method: 'POST', headers: { 'Authorization': 'Bearer ' + token } }).catch(function() {});
          clearToken(); setAuthenticated(false); setClientData(null); setClientList([]);
        }, WARNING_DURATION);
      }, IDLE_TIMEOUT);
    }
    var events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(function(evt) { window.addEventListener(evt, resetTimer); });
    resetTimer();
    return function() { if (idleTimer) clearTimeout(idleTimer); if (logoutTimer) clearTimeout(logoutTimer); events.forEach(function(evt) { window.removeEventListener(evt, resetTimer); }); };
  }, [authenticated]);

  // Refreshable client-list loader. Extracted from the initial-load effect
  // so screens that mutate the client roster (Add Client, Edit Client name)
  // can re-pull the canonical list and propagate the change to every
  // dropdown that reads from `clientList`.
  var refreshClientList = useCallback(function() {
    if (!authenticated) return Promise.resolve([]);
    return authFetch(API + '/clients')
      .then(function(r) { return r.json(); })
      .then(function(list) {
        setClientList(list);
        return list;
      })
      .catch(function(e) { console.error('Failed to load clients:', e); return []; });
  }, [authenticated]);

  useEffect(function() {
    if (!authenticated) return;
    refreshClientList().then(function(list) {
      if (IS_CLIENT_VIEW) {
        var found = list.find(function(c) { return c.id === URL_CLIENT; });
        if (found) { setActiveClient(URL_CLIENT); } else { setClientNotFound(true); }
      } else if (list.length > 0 && !activeClient) {
        setActiveClient(list[0].id);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated]);

  var fetchClientData = useCallback(async function() {
    if (!activeClient) return;
    setLoading(true);
    try {
      var resp = await authFetch(API + '/clients/' + activeClient + '/data');
      if (resp.ok) {
        var data = await resp.json();
        try {
          var siemResp = await authFetch(API + '/clients/' + activeClient + '/siem');
          if (siemResp.ok) { var siemData = await siemResp.json(); if (siemData.enabled) data.siemSummary = siemData.summary; }
        } catch (e) {}
        setClientData(data);
        setLastRefresh(new Date());
      }
    } catch (e) { console.error('Failed to fetch client data:', e); }
    setLoading(false);
  }, [activeClient]);

  useEffect(function() {
    fetchClientData();
    var interval = setInterval(fetchClientData, 60000);
    return function() { clearInterval(interval); };
  }, [fetchClientData]);

  // First-load redirect: when an ADMIN (role=admin) lands on a brand-
  // new client with zero integrations configured on the default
  // overview tab, jump them to Setup. Only fires for admin role —
  // never for client-role users regardless of URL. clientData lacks
  // the actual integration field values (only carries `live` flags),
  // so we don't try to compute "anyConfigured" here; the SetupTab
  // itself does that against the safe-config fetch. If we're going
  // to redirect, it's only when the admin would benefit (no live
  // flags lit at all).
  useEffect(function() {
    if (!clientData) return;
    if (!isAdminRole()) return;
    if (activeClient === 'demo' || clientData.isDemo) return;
    if (activeTab !== 'overview') return;
    // Use clientData.live flags as the "anything configured?" signal —
    // they're the data we actually have on hand and they're what the
    // CoverageBadge already trusts.
    var live = clientData.live || {};
    var anyLive = Object.keys(live).some(function(k) { return !!live[k]; });
    if (!anyLive) setActiveTab('setup');
  }, [clientData]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Reverse guard: if a non-admin user somehow lands on activeTab='setup'
  // (hash URL, persisted session state, etc.), bounce them to overview.
  // SetupTab itself returns null for non-admins; this prevents the
  // empty tab pane.
  useEffect(function() {
    if (!isAdminRole() && (activeTab === 'setup' || activeTab === 'agent' || activeTab === 'work' || activeTab === 'service')) setActiveTab('overview');
  }, [activeTab]);

  // ── Badge data computation ──
  useEffect(function() {
    if (!activeClient || !clientData) return;
    var badges = {};

    // Security: red dot if active threats or SIEM threats
    if ((clientData.threats && clientData.threats.active > 0) || (clientData.siemSummary && clientData.siemSummary.threats > 0)) {
      badges.security = { type: 'dot' };
    }
    // SIEM: count of open findings
    if (clientData.siemSummary && clientData.siemSummary.total_open > 0) {
      badges.siem = { type: 'count', count: clientData.siemSummary.total_open };
    }
    // Setup: count of integrations not yet wired (admin role only —
    // NavRail also hides the tab itself for non-admins). Note that
    // clientData.live is what we have on hand; we count "not lit" live
    // flags rather than checking the actual config fields (those
    // aren't on clientData; only on the safe-config admin endpoint).
    if (isAdminRole() && !(clientData && clientData.isDemo) && activeClient !== 'demo') {
      var live = clientData.live || {};
      var applicable = getApplicableIntegrations(clientData);
      var notStartedCount = applicable.reduce(function(acc, def) {
        // Map each integration kind to its live flag(s). If none of the
        // flags is lit, count as not_started.
        var liveKeysByKind = {
          endpoints:        ['ninjaone', 'ninjaone_gov'],
          edr:              ['huntress'],
          identity:         ['entra'],
          'siem-commercial':['blumira'],
          'siem-cmmc':      ['sentinel'],
          network:          ['unifi', 'meraki'],
          projects:         ['asana'],
          devops:           ['jira'],
          'cloud-cost':     ['azure'],                  // backend exposes !!config.azure_subscription_id as live.azure
          billing:          ['pax8']
        };
        var keys = liveKeysByKind[def.kind] || [];
        var lit = keys.some(function(k) { return !!live[k]; });
        return acc + (lit ? 0 : 1);
      }, 0);
      if (notStartedCount > 0) badges.setup = { type: 'count', count: notStartedCount };
    }

    setBadgeData(badges);

    // Fetch additional badge counts
    Promise.all([
      authFetch(API + '/clients/' + activeClient + '/changes').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
      authFetch(API + '/clients/' + activeClient + '/tickets').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
      authFetch(API + '/clients/' + activeClient + '/projects').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }),
    ]).then(function(results) {
      var newBadges = Object.assign({}, badges);
      var openChanges = 0;
      if (results[0] && results[0].summary) {
        var s = results[0].summary;
        openChanges = s.submitted + s.under_review + s.approved + s.in_progress;
      }
      // Federate: AI proposals also count toward "needs action on Changes tab".
      // pendingProposalsCount is maintained by the App-level 60s poller.
      var totalChanges = openChanges + (pendingProposalsCount || 0);
      if (totalChanges > 0) newBadges.changes = { type: 'count', count: totalChanges };
      if (results[1] && results[1].metrics && results[1].metrics.open > 0) {
        newBadges.support = { type: 'count', count: results[1].metrics.open };
      }
      if (results[2] && results[2].projects) {
        var totalOverdue = results[2].projects.reduce(function(s, p) { return s + p.overdueTasks; }, 0);
        if (totalOverdue > 0) newBadges.projects = { type: 'dot' };
      }
      setBadgeData(newBadges);
    });
  }, [activeClient, clientData, pendingProposalsCount]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Keyboard navigation: Ctrl+← / Ctrl+→ to cycle tabs
  var allTabs = ['setup','overview','redteam','security','posture','identity','keeper','siem','network','devops','support','projects','changes','reviews','budget','compliance'];
  useEffect(function() {
    function handleKeyNav(e) {
      if (!e.ctrlKey || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;
      e.preventDefault();
      var idx = allTabs.indexOf(activeTab);
      if (idx === -1) return;
      if (e.key === 'ArrowRight') setActiveTab(allTabs[(idx + 1) % allTabs.length]);
      else setActiveTab(allTabs[(idx - 1 + allTabs.length) % allTabs.length]);
    }
    window.addEventListener('keydown', handleKeyNav);
    return function() { window.removeEventListener('keydown', handleKeyNav); };
  }, [activeTab]);

  var handleLogout = function() {
    var token = getToken();
    if (token) {
      fetch(API + '/logout', { method: 'POST', headers: { 'Authorization': 'Bearer ' + token } }).catch(function() {});
    }
    clearToken(); setAuthenticated(false); setClientData(null); setClientList([]);
    sessionStorage.removeItem('aurelius_demo_role');
  };

  if (!authenticated) {
    if (IS_CLIENT_VIEW) return <LoginPage clientId={URL_CLIENT} onLogin={function() { setAuthenticated(true); }} />;
    return <LoginPage onLogin={function() { setAuthenticated(true); }} />;
  }
  if (clientNotFound) return <ClientNotFound clientId={URL_CLIENT} />;

  var clientInfo = clientList.find(function(c) { return c.id === activeClient; }) || {};
  var live = clientData ? clientData.live : { ninjaone: false, huntress: false };

  var c = {
    name: clientData ? clientData.name : (clientInfo.name || ''),
    type: clientData ? clientData.type : (clientInfo.type || ''),
    industry: clientData ? clientData.industry : (clientInfo.industry || ''),
    cmmc: clientData ? clientData.cmmc : (clientInfo.cmmc || false),
    endpoints: clientData ? (clientData.endpoints || { total: 0, healthy: 0, warning: 0, critical: 0 }) : { total: 0, healthy: 0, warning: 0, critical: 0 },
    threats: clientData ? (clientData.threats || { active: 0, mitigated: 0, investigating: 0 }) : { active: 0, mitigated: 0, investigating: 0 },
    huntress_agents: clientData ? clientData.huntress_agents : null,
    patches: placeholders.patches, credentials: placeholders.credentials, siem: placeholders.siem,
    tickets: placeholders.tickets, projects: placeholders.projects, cmmc_domains: placeholders.cmmc_domains,
    scores: { overall: 0, endpoint: 0, threat: 0, credential: 0, siem: 0 },
    live: live,
  };

  // Score calculation — only compute when live data is actually available
  if (clientData && clientData.endpoints && c.endpoints.total > 0) {
    c.scores.endpoint = Math.round((c.endpoints.healthy / c.endpoints.total) * 100);
  }
  if (clientData && clientData.threats) {
    c.scores.threat = c.threats.active === 0 ? 95 : c.threats.active <= 2 ? 70 : 45;
  }
  c.scores.credential = 0;
  if (clientData && clientData.siemSummary) {
    var st = clientData.siemSummary.threats || 0;
    var so = clientData.siemSummary.total_open || 0;
    c.scores.siem = so === 0 ? 95 : st === 0 && so <= 5 ? 85 : st === 0 ? 75 : st <= 2 ? 60 : 40;
  }
  var activeScores = [c.scores.endpoint, c.scores.threat, c.scores.siem].filter(function(s) { return s > 0; });
  c.scores.overall = activeScores.length > 0 ? Math.round(activeScores.reduce(function(a, b) { return a + b; }, 0) / activeScores.length) : 0;

  // Tab catalog now lives in NAV_RAIL_GROUPS at module scope; this used to
  // hold tabGroups / tabLabel for the horizontal tab strip but that's been
  // replaced by the vertical NavRail.

  if (clientList.length === 0 && !clientNotFound) {
    return (<div className="app"><header className="header"><div className="header-left"><div className="logo">AURELIUS</div><span className="logo-sub">by CloudByte Group</span></div></header><div className="tab-content"><SkeletonLoader /></div></div>);
  }

  return (
    <div className="app">
      {idleWarning && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999, padding: '12px 20px', background: 'linear-gradient(135deg, rgba(240,101,101,0.95), rgba(220,60,60,0.95))', backdropFilter: 'blur(8px)', color: '#fff', fontSize: 14, fontWeight: 600, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
          <span>Session expiring in 60 seconds due to inactivity</span>
          <button onClick={function() { setIdleWarning(false); }} style={{ background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 6, color: '#fff', padding: '6px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Stay Signed In</button>
        </div>
      )}
      <header className="header">
        <div className="header-left"><div className="logo">AURELIUS</div><span className="logo-sub">{adminMode ? 'Administrator Panel' : 'by CloudByte Group'}</span></div>
        <div className="header-right">
          {!IS_CLIENT_VIEW && !adminMode && (
            <select className="client-select" value={activeClient || ''} onChange={function(e) { setActiveClient(e.target.value); setActiveTab('overview'); setQbrOpen(false); setQbrReport(null); }}>
              {clientList.map(function(cl) { return <option key={cl.id} value={cl.id}>{cl.name}</option>; })}
            </select>
          )}
          {isAdmin && !IS_CLIENT_VIEW && (
            <button className={'admin-toggle-btn' + (adminMode ? ' active' : '')} onClick={function() { setAdminMode(!adminMode); }} title={adminMode ? 'Back to dashboard' : 'Administrator panel'}>
              {adminMode ? '← Dashboard' : 'Admin'}
            </button>
          )}
          {isAdminRole() && !IS_CLIENT_VIEW && (
            <PsaTimerPill onOpen={function() { setAdminMode(false); setActiveTab('service'); }} />
          )}
          {isAdminRole() && !IS_CLIENT_VIEW && pendingProposalsCount > 0 && (
            <button
              className="theme-toggle proposals-bell"
              onClick={function() { setProposalsOpen(true); }}
              title={pendingProposalsCount + ' AI proposal' + (pendingProposalsCount === 1 ? '' : 's') + ' awaiting approval'}
              style={{ position: 'relative' }}
            >
              {'◇'}
              <span className="proposals-bell-badge">{pendingProposalsCount > 9 ? '9+' : pendingProposalsCount}</span>
            </button>
          )}
          {isAdminRole() && (
            <button className="theme-toggle" onClick={function() { setMspVerifyOpen(true); }} title="Verify yourself to a caller">
              {'⛨'}
            </button>
          )}
          <button className="theme-toggle" onClick={function() { setChangelogOpen(true); if (changelogNew) setChangelogNew(false); }} title="What's New" style={{ position: 'relative' }}>
            {'◈'}
            {changelogNew && <span style={{ position: 'absolute', top: 4, right: 4, width: 7, height: 7, borderRadius: '50%', background: '#5b9ef4', boxShadow: '0 0 6px rgba(91,158,244,0.6)' }} />}
          </button>
          <button className="theme-toggle" onClick={function() { setTheme(theme === 'dark' ? 'light' : 'dark'); }} title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>{theme === 'dark' ? '☀' : '☾'}</button>
          <UserBadge onLogout={handleLogout} />
        </div>
      </header>

      {adminMode && <AdminPanel clientList={clientList} refreshClientList={refreshClientList} />}

      {!adminMode && (
        <div className="layout">
          <NavRail
            activeTab={activeTab}
            onTabChange={setActiveTab}
            badgeData={badgeData}
            collapsed={navCollapsed}
            onToggleCollapse={toggleNavCollapsed}
            isAdmin={isAdminRole()}
            isDemo={activeClient === 'demo' || (clientData && clientData.isDemo)}
            allowedTabs={getRestrictedClaims().allowedTabs}
            activeClient={activeClient}
          />
          <main className="layout-content">
            <div className="client-banner">
              <div><span className="client-name">{c.name}</span><span className="client-type">{c.type} — {c.industry}</span></div>
              <div className="banner-right">
                <CoverageBadge c={c} live={live} onClick={function() { setCoverageOpen(true); }} />
                {/* Inline workstream pill — shows when AI proposals are pending so
                    the operator never has to remember to check the bell or the
                    Changes tab. Click goes straight to the proposals drawer. */}
                {isAdminRole() && !IS_CLIENT_VIEW && pendingProposalsCount > 0 && (
                  <button
                    type="button"
                    className="banner-proposals-pill"
                    onClick={function() { setProposalsOpen(true); }}
                    title="Open the proposals drawer to review and approve"
                  >
                    <span className="banner-proposals-glyph">◇</span>
                    <span className="banner-proposals-count">{pendingProposalsCount}</span>
                    <span className="banner-proposals-label">pending approval{pendingProposalsCount === 1 ? '' : 's'}</span>
                  </button>
                )}
                {lastRefresh && <span className="last-refresh">Updated {lastRefresh.toLocaleTimeString()}</span>}
                <button className="refresh-btn" onClick={fetchClientData}>Refresh</button>
              </div>
            </div>
            {coverageOpen && <CoverageDrawer c={c} live={live} onClose={function() { setCoverageOpen(false); }} />}
            <div className="layout-content-inner">
              {loading && !clientData ? (
                <div className="tab-content"><SkeletonLoader /></div>
              ) : (
                <>
                  {activeTab === 'setup' && <SetupTab c={c} clientId={activeClient} onNavigate={setActiveTab} />}
                  {activeTab === 'agent' && <AgentTab clientId={activeClient} clientName={(clientList.find(function (cl) { return cl.id === activeClient; }) || {}).name || activeClient} />}
                  {activeTab === 'work' && <WorkTab clientList={clientList} />}
                  {activeTab === 'service' && <ServiceTab clientList={clientList} onNavigate={setActiveTab} />}
                  {activeTab === 'overview' && <OverviewTab c={c} live={live} clientId={activeClient} onNavigate={setActiveTab} />}
                  {activeTab === 'security' && <SecurityTab clientId={activeClient} c={c} live={live} onNavigate={setActiveTab} />}
                  {activeTab === 'posture' && <PostureTab clientId={activeClient} live={live} />}
                  {activeTab === 'identity' && <IdentityTab clientId={activeClient} live={live} />}
                  {activeTab === 'keeper' && <KeeperTab clientId={activeClient} />}
                  {activeTab === 'redteam' && <RedTeamTab clientId={activeClient} onNavigate={setActiveTab} />}
                  {activeTab === 'siem' && <SIEMTab clientId={activeClient} c={c} />}
                  {activeTab === 'network' && <NetworkTab clientId={activeClient} />}
                  {activeTab === 'devops' && <DevOpsTab clientId={activeClient} onNavigate={setActiveTab} />}
                  {activeTab === 'support' && <SupportTab clientId={activeClient} live={live} />}
                  {activeTab === 'changes' && <ChangesTab clientId={activeClient} isAdmin={sessionStorage.getItem('aurelius_role') === 'admin'} />}
                  {activeTab === 'budget' && <BudgetTab clientId={activeClient} />}
                  {activeTab === 'projects' && <ProjectsTab clientId={activeClient} />}
                  {activeTab === 'reviews' && <ReviewsTab clientId={activeClient} isAdmin={sessionStorage.getItem('aurelius_role') === 'admin'} />}
                  {activeTab === 'compliance' && <ComplianceTab c={c} clientId={activeClient} onExit={function() { setActiveTab('security'); }} />}
                </>
              )}
            </div>
          </main>
        </div>
      )}
      <InsightsFab
        hidden={adminMode}
        hasAi={CAN_USE_ADMIN_FEATURES}
        onClick={function() { setInsightsOpen(true); }}
      />
      <InsightsDrawer
        isOpen={insightsOpen}
        onClose={function() { setInsightsOpen(false); }}
        c={c}
        clientName={c.name}
        activeTab={activeTab}
        clientId={activeClient}
        onGenerateQBR={function() { setInsightsOpen(false); generateQBR(); }}
      />
      <QBRPanel isOpen={qbrOpen} onClose={function() { setQbrOpen(false); }} report={qbrReport} clientName={c ? c.name : ''} loading={qbrLoading} />
      <ChangelogPanel isOpen={changelogOpen} onClose={function() { setChangelogOpen(false); }} />
      <MspVerifyDrawer isOpen={mspVerifyOpen} onClose={function() { setMspVerifyOpen(false); }} />
      <ProposalsDrawer
        isOpen={proposalsOpen}
        onClose={function() { setProposalsOpen(false); refreshProposalsCount(); }}
        clientId={activeClient}
        clientName={c ? c.name : null}
        onChange={refreshProposalsCount}
      />
    </div>
  );
}

export default App;